/* eslint-disable no-unused-vars */
/* eslint-disable no-unreachable */
import { useState, useEffect, useRef, useMemo, useCallback, Component } from "react";
import MonthlyReports from "./MonthlyReports";
import TeamReporting from "./TeamReporting";
import TeamReportingSettings from "./TeamReportingSettings";
import ItGlueUpload from "./itglue/ItGlueUpload";
import TimeReconcileTab from "./timeReconcile/TimeReconcileTab";
import ProposalsTab from "./proposals/ProposalsTab";
import TemplatesTab from "./proposals/TemplatesTab";
import ContractTemplatesTab from "./contracts/ContractTemplatesTab";
import { mergeContractTemplates, backfillAppendix3, APPENDIX3_MARKER } from "./contracts/contractModel";
import { getSeedContractContent } from "../api/contractsApi";
import { DEFAULT_PROPOSAL_TEMPLATES, mergeTemplates, makeProposalRecord } from "./proposals/templateDefaults";
import { makeQuoteRecord, formatQuoteNumber, matchesQuoteNumberSearch } from "./quotes/quoteRecord";
import { makeQuoteFromRoadmapItem } from "./quotes/roadmapQuote";
import { planQuoteOpen } from "./quotes/quoteBuilder";
import ClientTypeahead from "./common/ClientTypeahead";
import QuoteBuilderPage from "./quotes/QuoteBuilderPage";
import QuoteSendModal from "./quotes/QuoteSendModal";
import QuotePushModal from "./quotes/QuotePushModal";
/* ⚠ Named QuoteKanban, not QuoteBoard: on a case-insensitive filesystem
   "./quotes/QuoteBoard" resolves to the React-free quoteBoard.js first. */
import QuoteKanban from "./quotes/QuoteKanban";
import QuoteCard from "./quotes/QuoteCard";
/* The fulfilment axis (invoiced · paid · ordered · autotask) — see quoteBoard.js
   for why it is a second axis and not four more statuses. */
import { withFulfilment, toggleStep, setStepDone, autotaskPushPatch } from "./quotes/quoteBoard";
import { withLineItemDetail } from "./quotes/quoteLineDetail";
import { filterRegisterRows, isRegisterFilterActive } from "./settings/clientRegisterFilter";
/* Duplicate · whole-quote templates · start from a template — the clone, the
   three template builders and the allowlisted copy all live in quoteTemplates.js
   (React-free, tested). isSectionTemplate / templateItemCount moved there too. */
import QuoteTemplateSaveModal from "./quotes/QuoteTemplateSaveModal";
import QuoteStartModal from "./quotes/QuoteStartModal";
import {
  cloneSections, cloneLineItems, templateCommons, structureFromTemplate, isSectionTemplate, templateItemCount,
  buildWholeQuoteTemplate, buildSectionTemplate, buildItemsTemplate, duplicateQuote,
} from "./quotes/quoteTemplates";
import { quoteCompanyPatch } from "./common/companyLink";
// ⚠ THE SHARED quantity rule. computeLineSubtotals below feeds the totals panel,
// and quoteRenderer.js carries a ported copy for the document — a test pins the
// two together. Never restate the coercion locally.
import { quantityOf } from "./proposals/foundationalBlocks";
import { periodOf, MONTHLY } from "./quotes/quotePeriod";
import { isFreightSection } from "./quotes/quoteSectionOrder";
import {
  DEFAULT_QUOTE_TERMS_TEXT, withQuoteTerms, addClause, updateClause,
  removeClause, moveClause, restoreDefaultClause,
} from "./quotes/quoteTerms";
import { createQuoteForItem, getRoadmap } from "../api/roadmapApi";
import { setClientTier } from "../api/autotaskDataApi";
import MeetingRoadmapSection from "./reports/MeetingRoadmapSection";
import { freezeRoadmapForMeeting } from "./reports/meetingRoadmap";
import OpportunitiesTab from "./opportunities/OpportunitiesTab";
import { isOpen } from "./opportunities/opportunityPipeline";
import { getUnifiRegister, getAvailableSites, setClientUnifi, clearClientUnifi, syncUnifi } from "../api/unifiApi";
import { getIndustries, setIndustry as setClientIndustry, getFortigateConfig, getFortigateDevices, setFortigateConfig, getM365Config, startM365Consent, getM365Domains, saveM365Domains, linkM365Tenant, recheckM365 , setItGlueOrgId, lookupItGlueOrg, setReportIdentity, fetchDiscussPending, markDiscussAddressed } from "../api/monthlyReportsApi";

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');`;

const css = `
${FONTS}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  /* ⚠ THE APP HEADER'S HEIGHT, as a variable because things OUTSIDE this file
     have to line up with it — the Monthly Report preview offsets itself by
     exactly this. It was a bare 46px in .hdr, so any overlay wanting to clear
     the header had to hardcode the same guess and would drift the day the
     header changed. */
  --hdr-h:46px;
  --dt-primary:#202020;
  --dt-secondary:#0b80a5;
  --dt-secondary-dark:#096685;
  --dt-navy:#2a4365;
  --dt-accent:#838383;
  --dt-light:#f0f0f0;
  --bg:#f8f9fa;
  --s1:#ffffff;
  --s2:#f0f4f7;
  --s3:#e8eef2;
  /* The SECTION CARD fill on the Proposals and Quoting builder forms — a step
     darker than --s2 so each section reads as its own block against the white
     form (Craig, 2026-10-02). ONE token, tune it here; the monthly-report
     section cards use #DBE0E6 if these should ever match. Nested cards inside a
     section stay --s1 (white) so they read as nested, not blended. */
  --form-card:#EDF0F4;
  --border:#dde3e8;
  --border2:#c8d2da;
  --strat:#0b80a5;
  --strat-d:rgba(11,128,165,0.1);
  --strat-g:rgba(11,128,165,0.25);
  --tact:#2a4365;
  --tact-d:rgba(42,67,101,0.1);
  --tact-g:rgba(42,67,101,0.25);
  --ops:#27ae60;
  --ops-d:rgba(39,174,96,0.1);
  --ops-g:rgba(39,174,96,0.25);
  --warn:#e67e22;
  --warn-d:rgba(230,126,34,0.1);
  --danger:#e74c3c;
  --danger-d:rgba(231,76,60,0.1);
  --cal:#d35400;
  --cal-d:rgba(211,84,0,0.1);
  --cal-g:rgba(211,84,0,0.2);
  --fellow:#7f56d9;
  --fellow-d:rgba(127,86,217,0.1);
  --text:#202020;
  --text2:#4a5568;
  --text3:#838383;
  --muted:#c8d2da;
  --ff-head:'Inter',sans-serif;
  --ff-mono:'Inter',sans-serif;
  --shadow-sm:0 2px 4px rgba(0,0,0,0.08);
  --shadow-md:0 4px 6px rgba(0,0,0,0.08);
  --shadow-lg:0 10px 25px rgba(0,0,0,0.12);
  --shadow-xl:0 20px 40px rgba(0,0,0,0.15);
}
body{background:var(--bg);color:var(--text);font-family:var(--ff-head);font-size:13px;overflow:hidden;}
.app{width:100vw;height:100vh;display:flex;flex-direction:column;position:relative;overflow:hidden;}
.noise{display:none;}

/* HEADER — slim, no nav */
.hdr{position:relative;z-index:20;display:flex;align-items:center;justify-content:space-between;
  padding:0 16px;height:var(--hdr-h);border-bottom:1px solid rgba(255,255,255,0.1);
  background:linear-gradient(135deg,var(--dt-navy) 0%,var(--dt-secondary) 100%);
  box-shadow:var(--shadow-md);flex-shrink:0;}
.logo{font-family:var(--ff-head);font-weight:700;font-size:14px;letter-spacing:-0.3px;color:#ffffff;display:flex;align-items:center;gap:10px;}
.logo-mark{width:24px;height:24px;border-radius:6px;background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.3);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:white;}
.hdr-pills{display:flex;gap:6px;align-items:center;}
.pill{display:flex;align-items:center;gap:5px;padding:3px 9px;border-radius:20px;border:1px solid rgba(255,255,255,0.2);background:rgba(255,255,255,0.1);font-size:10px;color:rgba(255,255,255,0.7);letter-spacing:0.04em;white-space:nowrap;}
.pill.p-ops{border-color:rgba(39,174,96,0.5);color:#5dda8a;background:rgba(39,174,96,0.15);}
.pill.p-cal{border-color:rgba(211,84,0,0.5);color:#ff8c42;background:rgba(211,84,0,0.15);}
.pill.p-warn{border-color:rgba(230,126,34,0.5);color:#f0a05a;background:rgba(230,126,34,0.15);}
.pill.p-fellow{border-color:rgba(127,86,217,0.5);color:#b09fe8;background:rgba(127,86,217,0.15);}
.pill-dot{width:5px;height:5px;border-radius:50%;background:currentColor;}
.pill.p-ops .pill-dot,.pill.p-cal .pill-dot,.pill.p-fellow .pill-dot{animation:blink 1.4s ease-in-out infinite;}
@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
/* Legacy nav classes kept for any remaining refs */
.nav{display:none;}
.nav-btn{display:none;}
.nav-btn.on{}
.nav-btn.has-badge{}
.nav-badge{position:absolute;top:-3px;right:-3px;width:14px;height:14px;border-radius:50%;background:#e74c3c;color:white;font-size:8px;display:flex;align-items:center;justify-content:center;font-weight:700;}

/* SIDE NAV */
.sidenav{width:210px;flex-shrink:0;display:flex;flex-direction:column;background:linear-gradient(180deg,var(--dt-navy) 0%,#0d2845 100%);border-right:1px solid rgba(255,255,255,0.07);overflow-y:auto;overflow-x:hidden;z-index:10;}
.sidenav::-webkit-scrollbar{width:3px;}.sidenav::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px;}
.sn-group{border-bottom:1px solid rgba(255,255,255,0.06);}
.sn-group-hdr{display:flex;align-items:center;justify-content:space-between;padding:10px 14px 8px;cursor:pointer;user-select:none;transition:background .15s;}
.sn-group-hdr:hover{background:rgba(255,255,255,0.05);}
.sn-group-label{font-size:9px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:rgba(255,255,255,0.4);}
.sn-chevron{font-size:8px;color:rgba(255,255,255,0.3);transition:transform .2s;}
.sn-chevron.open{transform:rotate(180deg);}
.sn-items{overflow:hidden;transition:max-height .22s ease;}
.sn-item{display:flex;align-items:center;gap:9px;padding:7px 14px 7px 20px;cursor:pointer;border-left:2px solid transparent;transition:all .12s;position:relative;}
.sn-item:hover{background:rgba(255,255,255,0.07);border-left-color:rgba(255,255,255,0.2);}
.sn-item.sn-on{background:rgba(255,255,255,0.12);border-left-color:var(--tact);}
.sn-item-icon{font-size:13px;flex-shrink:0;opacity:0.8;}
.sn-item-label{font-size:11px;font-weight:500;color:rgba(255,255,255,0.7);flex:1;letter-spacing:0.01em;}
.sn-item.sn-on .sn-item-label{color:white;font-weight:600;}
.sn-badge{min-width:16px;height:16px;border-radius:8px;background:#e74c3c;color:white;font-size:8px;display:flex;align-items:center;justify-content:center;font-weight:700;padding:0 4px;}

/* LAYOUT */
.body{display:flex;flex:1;overflow:hidden;position:relative;z-index:1;}
.resize-handle{width:8px;flex-shrink:0;cursor:col-resize;position:relative;z-index:10;background:transparent;transition:background 0.15s;}
.resize-handle:hover{background:var(--strat);}
.resize-handle.dragging{background:var(--strat);}
.resize-handle::after{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:1px;height:40px;background:var(--border2);border-radius:1px;}
.resize-handle:hover::after,.resize-handle.dragging::after{background:var(--strat);}
.panel{display:flex;flex-direction:column;border-right:1px solid var(--border);overflow:hidden;flex-shrink:0;background:var(--s1);}
.panel:last-child{border-right:none;flex:1;}
.ph{display:flex;align-items:center;justify-content:space-between;padding:10px 14px 9px;border-bottom:1px solid var(--border);flex-shrink:0;background:var(--s1);}
.ph-title{font-family:var(--ff-head);font-weight:600;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);}
.ph-badge{font-size:10px;padding:2px 8px;border-radius:10px;background:var(--strat-d);color:var(--strat);border:1px solid rgba(11,128,165,0.2);font-weight:600;}
.scroll{overflow-y:auto;flex:1;}
/* CLIENT TYPEAHEAD */
.typeahead{position:relative;}
.typeahead-input{width:100%;}
.typeahead-dropdown{position:absolute;top:100%;left:0;right:0;z-index:200;background:white;border:1px solid var(--border);border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.12);max-height:200px;overflow-y:auto;margin-top:2px;}
.typeahead-item{padding:8px 12px;font-size:11px;cursor:pointer;display:flex;align-items:center;justify-content:space-between;gap:8px;border-bottom:1px solid var(--border);}
.typeahead-item:last-child{border-bottom:none;}
.typeahead-item:hover{background:var(--strat-d);}
.typeahead-item.ti-active{background:var(--strat-d);}
.typeahead-tier{font-size:9px;font-weight:600;padding:1px 5px;border-radius:3px;flex-shrink:0;}
.tt-Gold{background:#fef3c7;color:#92400e;}
.tt-Silver{background:#f3f4f6;color:#4b5563;}
.tt-Bronze{background:#fef2e8;color:#92400e;}
.tt-Copper{background:#fff7ed;color:#b45309;}
.tt-Adhoc{background:var(--s2);color:var(--text3);}
/* PLAN MY DAY */
.plan-inbox{width:300px;flex-shrink:0;display:flex;flex-direction:column;overflow:hidden;border-right:1px solid var(--border);background:white;}
.plan-source-section{border-bottom:1px solid var(--border);}
.plan-source-hdr{padding:8px 12px;font-size:9px;letter-spacing:0.12em;text-transform:uppercase;font-weight:600;display:flex;align-items:center;justify-content:space-between;cursor:pointer;user-select:none;}
.plan-source-hdr:hover{background:var(--s2);}
.plan-item{display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid var(--border);cursor:grab;transition:background 0.1s;user-select:none;}
.plan-item:hover{background:#f0f7fb;}
.plan-item.pi-used{opacity:0.35;cursor:default;pointer-events:none;}
.plan-item-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.plan-canvas{flex:1;display:flex;flex-direction:column;overflow:hidden;background:var(--bg);}
.plan-slot{display:flex;gap:0;min-height:24px;position:relative;align-items:flex-start;}
.plan-slot-time{width:48px;flex-shrink:0;font-size:10px;color:var(--text3);padding:4px 6px 0;font-weight:400;border-right:1px solid var(--border2);background:white;height:28px;box-sizing:border-box;}
.plan-slot-content{flex:1;position:relative;overflow:visible;}
.plan-block{display:flex;align-items:flex-start;gap:6px;padding:4px 8px 4px 6px;border-radius:4px;border:1px solid var(--border);background:white;cursor:grab;position:relative;box-shadow:var(--shadow-sm);width:100%;box-sizing:border-box;}
.plan-block:hover{box-shadow:var(--shadow-md);}
.plan-block.pb-fixed{cursor:default;}
.plan-slot.drop-target{background:var(--strat-d);}
.scroll::-webkit-scrollbar{width:4px;}.scroll::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px;}

/* LAYER BADGES */
.lb{display:inline-flex;align-items:center;gap:4px;font-size:9px;padding:2px 7px;border-radius:4px;letter-spacing:0.06em;font-weight:600;text-transform:uppercase;}
.lb-S{background:var(--strat-d);color:var(--strat);}
.lb-T{background:var(--tact-d);color:var(--tact);}
.lb-O{background:var(--ops-d);color:var(--ops);}
.lb-B{background:rgba(230,126,34,0.1);color:var(--warn);}
.lb-C{background:var(--cal-d);color:var(--cal);}

/* PRIORITY BADGES */
.pri{display:inline-flex;align-items:center;font-size:9px;padding:2px 7px;border-radius:4px;letter-spacing:0.06em;font-weight:600;text-transform:uppercase;}
.pri-high{background:rgba(231,76,60,0.1);color:#c0392b;}
.pri-med{background:rgba(230,126,34,0.1);color:var(--warn);}
.pri-low{background:var(--ops-d);color:var(--ops);}

/* DRAG & DROP BLOCKS */
.block{display:flex;gap:9px;padding:10px 14px;border-bottom:1px solid var(--border);cursor:grab;transition:background 0.15s,box-shadow 0.15s,opacity 0.15s;position:relative;user-select:none;background:var(--s1);}
.block:active{cursor:grabbing;}
.block:hover{background:#eef6fa;}
.block.sel{background:#e0f0f8;}
.block.sel::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;}
.block.sel.lS::before{background:var(--strat);}
.block.sel.lT::before{background:var(--tact);}
.block.sel.lO::before{background:var(--ops);}
.block.sel.lB::before{background:var(--warn);}
.block.sel.lC::before{background:var(--cal);}
.block.done{opacity:0.4;}
.block.is-prep{border-left:3px solid var(--cal);}
.block.is-todo{border-left:3px solid var(--fellow);}
.block.dragging{opacity:0.4;transform:scale(0.98);}
.block.drag-over{background:var(--strat-d);border-top:2px solid var(--strat);}
.drag-handle{color:var(--muted);font-size:12px;cursor:grab;padding:1px 3px;opacity:0;transition:opacity 0.1s;align-self:flex-start;margin-top:1px;}
.block:hover .drag-handle{opacity:1;}
.block-t{font-size:10px;color:var(--text3);min-width:34px;padding-top:1px;font-weight:500;}
.block-body{flex:1;min-width:0;}
.block-name{font-size:12px;color:var(--text);margin-bottom:5px;line-height:1.3;font-weight:500;}
.block.sel .block-name{color:var(--strat);font-weight:600;}
.block-meta{display:flex;gap:5px;align-items:center;flex-wrap:wrap;}
.block-dur{font-size:10px;color:var(--text3);}
.block-acts{display:flex;gap:3px;opacity:0;transition:opacity 0.1s;margin-top:1px;}
.block:hover .block-acts{opacity:1;}
.ico-btn{width:22px;height:22px;border-radius:4px;border:1px solid var(--border2);background:var(--s2);color:var(--text3);cursor:pointer;font-size:11px;display:flex;align-items:center;justify-content:center;transition:all 0.12s;}
.ico-btn:hover{background:var(--strat-d);color:var(--strat);border-color:rgba(11,128,165,0.3);}
.add-row{padding:10px 14px;border-bottom:1px solid var(--border);background:var(--s1);}
.add-btn{display:flex;align-items:center;gap:7px;width:100%;padding:8px 10px;border-radius:6px;border:1px dashed var(--border2);background:transparent;color:var(--text3);cursor:pointer;font-family:var(--ff-head);font-size:12px;font-weight:500;transition:all 0.3s ease;text-align:left;}
.add-btn:hover{border-color:var(--strat);color:var(--strat);background:var(--strat-d);}

/* OVERDUE BADGE */
.overdue-chip{display:inline-flex;align-items:center;gap:4px;font-size:9px;padding:2px 7px;border-radius:4px;background:var(--danger-d);color:var(--danger);letter-spacing:0.06em;font-weight:600;}

/* INPUTS */
.inp{width:100%;background:#f8f9fa;border:1px solid #ddd;border-radius:8px;padding:8px 12px;color:var(--text);font-family:var(--ff-head);font-size:12px;outline:none;transition:all 0.3s ease;margin-bottom:6px;}
.inp:focus{border-color:var(--strat);box-shadow:0 0 0 3px rgba(11,128,165,0.15);background:white;}
.inp::placeholder{color:var(--text3);}
select.inp{cursor:pointer;}
textarea.inp{resize:vertical;min-height:70px;line-height:1.6;}

/* QUOTE BUILDER — THE LINE-ITEM ROW

   (No backticks anywhere in this comment — it lives inside the css template
   literal, where one would end the string mid-stylesheet. CLAUDE.md records the
   same trap in the backend's QUOTE_CSS; it caught me here too.)

   ⚠ IT NEVER FITTED ON ONE LINE, AND THAT IS ARITHMETIC RATHER THAN TASTE.
   The old row was nine columns, eight of them fixed:
     54 + 90 + 60 + 90 + 90 + 90 + 90 + 32 = 596px, +20px padding = 616px
   needed BEFORE the description got a single pixel. The builder's left pane is
   minmax(300px, 35%), so on a 1523px screen it has ~533px — the description
   resolved to zero, the row overflowed, and overflow:hidden on the table
   clipped MARKUP and SUBTOTAL off the right edge. A usable ~200px description
   needed a viewport of about 2330px. Widening the columns cannot fix that:
   nine form controls do not fit in a third of a screen.

   So the row STACKS, always, rather than behind a breakpoint. A breakpoint set
   at 'laptop' would still leave it broken at 1920, where the description gets
   56px. The threshold that would actually work is ~2330px, which is not a
   device class — it is a number that says the single-row layout was never
   viable.

   ⛔ ONE DEFINITION, USED BY BOTH THE HEADER AND THE ROWS. The nine-value
   template used to appear twice in QuoteBuilderPage.jsx, hand-synced, so an
   edit to one silently misaligned the other. .qli-nums is now the single
   grid: change it and the header and every row move together. */

/* The item: a thumbnail rail, and everything else stacked beside it. The rail
   spans both tiers, which is what stops a line item reading as two or three
   loose strips once the checkbox row and supplier panel are under it. */
.qli{display:grid;grid-template-columns:54px minmax(0,1fr);gap:0;padding:6px 10px;align-items:start;}
.qli-thumb{display:flex;align-items:flex-start;justify-content:center;padding-top:2px;}
.qli-main{display:flex;flex-direction:column;gap:5px;min-width:0;}
/* Tier 1: what the thing IS. The description takes every pixel left over. */
.qli-top{display:grid;grid-template-columns:92px minmax(0,1fr);gap:6px;align-items:center;}
/* Tier 1b: what the customer should KNOW about it — li.detail, which prints
   under the description on the quote. Ten rows and a scrollbar here; in the
   output it is plain text at its natural height. .qli-main is a flex column so
   this is already full width; min-width:0 is the shrink-chain guard (a
   textarea's intrinsic width will otherwise push the column wide — the same
   trap as the start modal). It is NOT in FREIGHT_RELEVANT_FIELDS. */
.qli-detail{display:block;width:100%;min-width:0;box-sizing:border-box;margin-bottom:0;font-size:11px;line-height:1.4;padding:4px 6px;resize:vertical;overflow-y:auto;}
/* Tier 2: what it COSTS. Also the header strip, so the labels genuinely sit
   above the fields they name — the one alignment that has to hold, because
   unit COST and unit PRICE are one word apart and the margin depends on
   telling them apart. */
/* ⚠ FRACTIONS, NOT PIXELS, AND THAT IS MEASURED. A fixed-px version of this
   row (64/92/92/78/72/28) needs 456px and the pane offers 397 at 1366 — so it
   overflowed by 44px and the delete button was clipped, which is the SAME bug
   one tier down. minmax(0,Npx) tracks were the other candidate and collapse
   rather than shrink. Fractions cannot overflow at any width.
   The cap stops them becoming absurd: an unbounded 1fr gives a 170px quantity
   box on a 2560px monitor. Capped, the fields run 52-110px from a 1366 laptop
   to a 2560 monitor, and the description takes everything else. */
.qli-nums{display:grid;grid-template-columns:0.85fr 1.25fr 1.25fr 1fr 1.15fr 28px;gap:6px;align-items:center;max-width:540px;}
/* The header repeats the rail's 54px so its labels line up with the numbers
   rather than with the left edge of the card. */
/* ⚠ THE TRANSPARENT BORDER IS LOAD-BEARING. .qli-item carries a 3px left rail,
   which narrows everything inside it — but the header strip sits outside the
   item and kept the full width, so every label sat 3px right of the field it
   names and drifted further across the fr tracks. Measured: header 400px wide
   starting at x=82, row 397px starting at x=85. Matching the inset puts the
   drift at 0px at every width. */
.qli-hdr{display:grid;grid-template-columns:54px minmax(0,1fr);gap:0;padding:7px 10px;border-left:3px solid transparent;}
.qli-lab{font-size:9px;font-weight:700;color:var(--text2);text-transform:uppercase;letter-spacing:0.5px;text-align:right;}
/* Tier 3+: the checkbox row and the supplier panel, indented to the rail so
   the whole item reads as one block. */
.qli-sub{display:flex;align-items:center;gap:14px;padding:4px 10px 8px 64px;font-size:10px;color:var(--text3);background:var(--s2);border-top:1px dashed var(--border);flex-wrap:wrap;}
/* ⚠ 3px of type colour down the left edge. Not decoration: it is what holds
   three stacked tiers together as one item, and it encodes the type that used
   to be readable at a glance from the dropdown's own colour. */
.qli-item{border-bottom:1px solid var(--border);background:white;border-left:3px solid var(--border2);}

/* BUTTONS */
.btn{font-family:var(--ff-head);font-size:12px;font-weight:500;padding:7px 14px;border-radius:6px;border:1px solid var(--border2);background:white;color:var(--text2);cursor:pointer;transition:all 0.3s ease;display:inline-flex;align-items:center;gap:6px;box-shadow:var(--shadow-sm);}
.btn:hover{background:var(--s2);color:var(--text);box-shadow:var(--shadow-md);transform:translateY(-1px);}
.btn:disabled{opacity:0.45;cursor:not-allowed;transform:none;box-shadow:none;}
.btn-S{background:var(--strat-d);border-color:rgba(11,128,165,0.25);color:var(--strat);}
.btn-S:hover{background:rgba(11,128,165,0.18);border-color:var(--strat);}
/* .btn-strat NEVER HAD A RULE. 22 buttons across 10 files carried the class — every
   primary action in Quoting, Proposals and Opportunities — and all of them rendered as a
   plain white .btn. Defined once, here, as the strategic-teal member of this family
   (the same tint as .btn-S) so the intent every caller had is what they now get.
   Inline style objects on a few callers (the quote header's HDR_BTN) still win, by design. */
.btn-strat{background:var(--strat-d);border-color:rgba(11,128,165,0.25);color:var(--strat);font-weight:600;}
.btn-strat:hover{background:rgba(11,128,165,0.18);border-color:var(--strat);}
.btn-T{background:var(--tact-d);border-color:rgba(42,67,101,0.25);color:var(--tact);}
.btn-T:hover{background:rgba(42,67,101,0.18);}
.btn-O{background:var(--ops-d);border-color:rgba(39,174,96,0.25);color:var(--ops);}
.btn-O:hover{background:rgba(39,174,96,0.18);border-color:var(--ops);}
.btn-C{background:var(--cal-d);border-color:rgba(211,84,0,0.25);color:var(--cal);}
.btn-C:hover{background:rgba(211,84,0,0.18);}
.btn-fellow{background:var(--fellow-d);border-color:rgba(127,86,217,0.25);color:var(--fellow);}
.btn-fellow:hover{background:rgba(127,86,217,0.18);}
.btn-danger{background:var(--danger-d);border-color:rgba(231,76,60,0.25);color:var(--danger);}
.btn-warn{background:var(--warn-d);border-color:rgba(230,126,34,0.25);color:var(--warn);}

/* FOCUS AREA */
.focus-area{flex:1;display:flex;flex-direction:column;overflow-y:auto;overflow-x:hidden;background:white;}
.focus-area::-webkit-scrollbar{width:5px;}.focus-area::-webkit-scrollbar-thumb{background:var(--border2);border-radius:3px;}
.focus-hero{padding:20px 24px 18px;border-bottom:1px solid var(--border);flex-shrink:0;position:sticky;top:0;z-index:5;overflow:hidden;background:white;box-shadow:0 2px 8px rgba(0,0,0,0.06);}
.focus-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,var(--dt-navy),var(--dt-secondary));opacity:0;transition:opacity 0.4s;}
.fh-S::before,.fh-T::before,.fh-O::before,.fh-C::before{opacity:1!important;}
.focus-eyebrow{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:6px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-weight:500;}
.focus-title{font-family:var(--ff-head);font-weight:700;font-size:18px;letter-spacing:-0.3px;line-height:1.25;color:var(--text);margin-bottom:14px;min-height:44px;}
.ft-S{color:var(--strat)!important;}.ft-T{color:var(--tact)!important;}.ft-O{color:var(--ops)!important;}.ft-C{color:var(--cal)!important;}
.focus-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.timer{font-family:var(--ff-head);font-weight:700;font-size:38px;letter-spacing:-2px;min-width:88px;transition:color 0.3s;}
.tm-S{color:var(--strat);}
.tm-T{color:var(--tact);}
.tm-O{color:var(--ops);}
.tm-C{color:var(--cal);}
.tm-paused{color:var(--warn);}
.tm-idle{color:var(--muted);}
.prog-wrap{height:4px;background:var(--border);border-radius:2px;margin-top:14px;overflow:hidden;}
.prog-bar{height:100%;border-radius:2px;transition:width 1s linear;}
.pb-S{background:linear-gradient(90deg,var(--dt-navy),var(--dt-secondary));}
.pb-T{background:linear-gradient(90deg,var(--dt-navy),var(--tact));}
.pb-O{background:linear-gradient(90deg,var(--ops),#2ecc71);}
.pb-C{background:linear-gradient(90deg,var(--cal),#e67e22);}
.ai-section{padding:14px 24px;border-bottom:1px solid var(--border);flex-shrink:0;background:linear-gradient(135deg,rgba(11,128,165,0.04) 0%,rgba(42,67,101,0.03) 100%);}

/* FOCUS ACCORDION */
.fa-hdr{display:flex;align-items:center;gap:8px;cursor:pointer;user-select:none;padding:9px 22px;border-bottom:1px solid var(--border);flex-shrink:0;background:white;transition:background .12s;position:sticky;top:0;z-index:4;}
.fa-hdr:hover{background:var(--s2);}
.fa-label{font-size:9px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:var(--text3);flex:1;display:flex;align-items:center;gap:6px;}
.fa-label span{font-size:12px;}
.fa-chevron{font-size:9px;color:var(--text3);transition:transform .18s;flex-shrink:0;}
.fa-chevron.open{transform:rotate(90deg);}
.fa-body{border-bottom:1px solid var(--border);}
.ai-label{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--dt-secondary);margin-bottom:8px;display:flex;align-items:center;gap:7px;font-weight:600;}
.ai-text{font-size:12px;color:var(--text2);line-height:1.75;}
.ai-loading{display:flex;gap:4px;align-items:center;padding:3px 0;}
.ai-dot{width:5px;height:5px;border-radius:50%;background:var(--dt-secondary);}
.ai-dot:nth-child(1){animation:aiD 1.2s 0s ease-in-out infinite;}
.ai-dot:nth-child(2){animation:aiD 1.2s 0.2s ease-in-out infinite;}
.ai-dot:nth-child(3){animation:aiD 1.2s 0.4s ease-in-out infinite;}
@keyframes aiD{0%,100%{opacity:0.2;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}

/* MEETINGS STRIP */
.meetings-strip{padding:10px 24px 12px;border-bottom:1px solid var(--border);flex-shrink:0;background:white;}
.ms-label{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--text3);margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;font-weight:600;}
.ms-scroll{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;}
.ms-scroll::-webkit-scrollbar{height:3px;}.ms-scroll::-webkit-scrollbar-thumb{background:var(--border2);}
.meeting-chip{flex-shrink:0;padding:9px 12px;border-radius:8px;border:1px solid var(--border);background:white;min-width:155px;cursor:pointer;transition:all 0.3s ease;box-shadow:var(--shadow-sm);}
.meeting-chip:hover{background:var(--s2);transform:translateY(-2px);box-shadow:var(--shadow-md);}
.mc-vcio{border-left:3px solid var(--strat);}
.mc-mgd{border-left:3px solid var(--tact);}
.mc-date{font-size:9px;color:var(--text3);margin-bottom:3px;font-weight:500;}
.mc-title{font-size:11px;color:var(--text);margin-bottom:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:500;}
.mc-meta{display:flex;gap:5px;align-items:center;flex-wrap:wrap;}
.mc-days{font-size:9px;padding:2px 6px;border-radius:4px;font-weight:600;}
.days-ok{background:var(--ops-d);color:var(--ops);}
.days-warn{background:var(--warn-d);color:var(--warn);}
.days-urgent{background:var(--danger-d);color:var(--danger);}

/* CADENCE MINI */
.cadence-mini{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;padding:12px 24px;border-bottom:1px solid var(--border);flex-shrink:0;background:var(--bg);}
.cad-card{padding:12px 14px;border-radius:8px;border:1px solid var(--border);background:white;cursor:pointer;transition:all 0.3s ease;box-shadow:var(--shadow-sm);}
.cad-card:hover{transform:translateY(-2px);box-shadow:var(--shadow-md);}
.cad-card.c-S{border-top:3px solid var(--strat);}
.cad-card.c-T{border-top:3px solid var(--tact);}
.cad-card.c-O{border-top:3px solid var(--ops);}
.cc-head{font-size:9px;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:5px;font-weight:600;}
.cc-head.cS{color:var(--strat);}.cc-head.cT{color:var(--tact);}.cc-head.cO{color:var(--ops);}
.cc-prog{height:3px;background:var(--border);border-radius:2px;overflow:hidden;margin-bottom:8px;}
.cc-prog-b{height:100%;border-radius:2px;transition:width 0.4s ease;}
.cc-item{display:flex;align-items:flex-start;gap:5px;font-size:10px;color:var(--text3);padding:3px 0;border-bottom:1px solid var(--border);cursor:pointer;transition:color 0.15s;}
.cc-item:last-child{border-bottom:none;}
.cc-item:hover{color:var(--strat);}
.cc-item.done-i{text-decoration:line-through;opacity:0.4;}
.cc-ck{width:13px;height:13px;border-radius:3px;flex-shrink:0;border:1px solid var(--border2);display:flex;align-items:center;justify-content:center;font-size:8px;transition:all 0.12s;margin-top:1px;}
.cc-ck.ck-S{background:var(--strat);border-color:var(--strat);color:white;}
.cc-ck.ck-T{background:var(--tact);border-color:var(--tact);color:white;}
.cc-ck.ck-O{background:var(--ops);border-color:var(--ops);color:white;}

/* TO-DO ITEMS */
.todo-item{padding:12px 16px;border-bottom:1px solid var(--border);transition:background 0.15s;position:relative;background:white;}
.todo-item:hover{background:#f0f7fb;}
.todo-item.td-done{opacity:0.45;}
.todo-item.td-overdue{border-left:3px solid var(--danger);background:#fff5f5;}
.todo-item.td-today{border-left:3px solid var(--strat);background:#f0f7fb;}
.todo-item.td-expanded{background:#eef6fa;}
.td-top{display:flex;align-items:flex-start;gap:9px;cursor:pointer;}
.td-cb{width:17px;height:17px;border-radius:4px;border:2px solid var(--border2);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:10px;margin-top:1px;cursor:pointer;transition:all 0.2s ease;}
.td-cb:hover{border-color:var(--strat);}
.td-cb.checked{background:var(--strat);border-color:var(--strat);color:white;}
.td-body{flex:1;min-width:0;}
.td-title{font-size:12px;color:var(--text);margin-bottom:4px;line-height:1.35;}
.td-done .td-title{text-decoration:line-through;color:var(--text3);}
.td-meta{display:flex;gap:6px;align-items:center;flex-wrap:wrap;}
.td-date{font-size:10px;color:var(--text3);}
.td-date.overdue-text{color:var(--danger);}
.td-date.today-text{color:var(--fellow);}
.td-expand-btn{font-size:10px;color:var(--text3);cursor:pointer;padding:1px 4px;border-radius:3px;border:1px solid transparent;transition:all 0.12s;flex-shrink:0;margin-top:1px;}
.td-expand-btn:hover{border-color:var(--border2);color:var(--text2);}
.td-detail{margin-top:10px;padding-top:10px;border-top:1px solid var(--border);}
.td-notes-text{font-size:11px;color:var(--text3);line-height:1.65;margin-bottom:10px;white-space:pre-wrap;}
.td-actions{display:flex;gap:6px;flex-wrap:wrap;}
.td-source{font-size:9px;color:var(--text3);margin-bottom:6px;display:flex;align-items:center;gap:5px;}

/* MEETING NOTES TAB */
.notes-area{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.notes-top{padding:16px 22px;border-bottom:1px solid var(--border);flex-shrink:0;background:white;box-shadow:var(--shadow-sm);}
.notes-top-title{font-family:var(--ff-head);font-weight:700;font-size:16px;letter-spacing:-0.3px;color:var(--text);margin-bottom:4px;}
.notes-top-sub{font-size:11px;color:var(--text3);line-height:1.6;}
.notes-body{flex:1;display:flex;overflow:hidden;}
.notes-left{width:55%;border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;background:var(--bg);}
.notes-right{flex:1;display:flex;flex-direction:column;overflow:hidden;background:white;}
.notes-ph{padding:10px 16px 9px;border-bottom:1px solid var(--border);font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--text3);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;font-weight:600;background:var(--s2);}
.notes-textarea{flex:1;background:white;border:none;outline:none;color:var(--text);font-family:var(--ff-head);font-size:12px;padding:16px;resize:none;line-height:1.8;}
.notes-textarea::placeholder{color:var(--muted);}
.notes-actions{padding:10px 16px;border-top:1px solid var(--border);display:flex;gap:8px;flex-shrink:0;background:var(--s2);}

/* AI EXTRACTED TODOS */
.extracted-item{padding:12px 16px;border-bottom:1px solid var(--border);transition:background 0.15s;background:white;}
.extracted-item:hover{background:#f0f7fb;}
.ext-top{display:flex;align-items:flex-start;gap:9px;margin-bottom:8px;}
.ext-num{width:20px;height:20px;border-radius:5px;background:var(--strat-d);border:1px solid rgba(11,128,165,0.25);color:var(--strat);font-size:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-weight:700;}
.ext-title{font-size:12px;color:var(--text);line-height:1.35;flex:1;font-weight:500;}
.ext-meta{display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-bottom:9px;padding-left:29px;}
.ext-controls{display:flex;gap:6px;flex-wrap:wrap;padding-left:29px;}
.ext-date-inp{background:#f8f9fa;border:1px solid #ddd;border-radius:6px;padding:5px 9px;color:var(--text);font-family:var(--ff-head);font-size:11px;outline:none;transition:all 0.3s ease;}
.ext-date-inp:focus{border-color:var(--strat);box-shadow:0 0 0 2px rgba(11,128,165,0.1);}
select.ext-date-inp{cursor:pointer;}
.ext-accept-btn{font-size:11px;padding:5px 12px;border-radius:6px;border:1px solid rgba(11,128,165,0.25);background:var(--strat-d);color:var(--strat);cursor:pointer;font-family:var(--ff-head);font-weight:500;transition:all 0.3s ease;}
.ext-accept-btn:hover{background:rgba(11,128,165,0.18);transform:translateY(-1px);}
.ext-accepted{font-size:11px;color:var(--ops);display:flex;align-items:center;gap:4px;font-weight:500;}

/* FELLOW CONNECT */
.fellow-connect{padding:14px 20px;border-bottom:1px solid var(--border);background:white;flex-shrink:0;box-shadow:var(--shadow-sm);}
.fellow-row{display:flex;align-items:center;gap:12px;}
.fellow-logo{width:32px;height:32px;border-radius:8px;background:var(--fellow-d);border:1px solid rgba(127,86,217,0.2);display:flex;align-items:center;justify-content:center;font-size:16px;}
.fellow-info{flex:1;}
.fellow-name{font-size:13px;color:var(--text);font-weight:600;margin-bottom:2px;}
.fellow-sub{font-size:11px;color:var(--text3);}

/* RESCHEDULE MODAL */
.modal-overlay{position:fixed;inset:0;z-index:400;background:rgba(32,32,32,0.55);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);}
.modal{background:white;border:1px solid var(--border);border-radius:12px;padding:0;width:380px;box-shadow:var(--shadow-xl);overflow:hidden;}
.modal-title{font-family:var(--ff-head);font-weight:700;font-size:15px;color:white;margin-bottom:4px;padding:18px 22px 14px;background:linear-gradient(135deg,var(--dt-navy),var(--dt-secondary));}
.modal-sub{font-size:11px;color:rgba(255,255,255,0.75);margin-bottom:0;line-height:1.6;}
.modal-label{font-size:10px;color:var(--text3);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px;font-weight:600;}
.modal-acts{display:flex;gap:8px;margin-top:16px;}

/* ALERT / TOAST */
.alert-overlay{position:fixed;top:68px;right:18px;z-index:300;width:315px;background:white;border:1px solid var(--border);border-top:4px solid var(--danger);border-radius:10px;padding:16px;box-shadow:var(--shadow-xl);animation:alertIn 0.28s cubic-bezier(0.34,1.56,0.64,1);}
@keyframes alertIn{from{opacity:0;transform:translateX(16px) scale(0.96)}to{opacity:1;transform:translateX(0) scale(1)}}
.prep-toast{position:fixed;bottom:24px;right:24px;z-index:200;width:350px;background:white;border:1px solid var(--border);border-radius:12px;padding:18px;box-shadow:var(--shadow-xl);border-top:4px solid var(--dt-secondary);animation:toastIn 0.3s cubic-bezier(0.34,1.56,0.64,1);}
@keyframes toastIn{from{opacity:0;transform:translateY(12px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}

/* SETTINGS */
.settings-wrap{padding:20px;display:flex;flex-direction:column;gap:16px;overflow-y:auto;background:var(--bg);}
.sg{border:1px solid var(--border);border-radius:10px;overflow:visible;box-shadow:var(--shadow-sm);}
.sg-h{padding:10px 14px;background:linear-gradient(135deg,var(--dt-navy),var(--dt-secondary));border-bottom:1px solid var(--border);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.9);font-weight:600;}
.sg-b{padding:14px 16px;display:flex;flex-direction:column;gap:10px;background:white;overflow:hidden;border-radius:0 0 10px 10px;}
.t-row{display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-bottom:1px solid var(--border);background:white;}
.t-row:last-child{border-bottom:none;}
.t-lbl{font-size:12px;color:var(--text2);font-weight:500;}
.toggle{position:relative;width:36px;height:20px;cursor:pointer;}
.toggle input{opacity:0;width:0;height:0;}
.t-tr{position:absolute;inset:0;background:#ddd;border-radius:20px;transition:all 0.2s;}
.toggle input:checked~.t-tr{background:var(--strat);}
.t-th{position:absolute;top:3px;left:3px;width:14px;height:14px;border-radius:50%;background:white;transition:all 0.2s;box-shadow:0 1px 3px rgba(0,0,0,0.2);}
.toggle input:checked~.t-th{left:19px;}
.vip-r{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);}
.vip-r:last-child{border-bottom:none;}
.cli-r{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);}
.cli-r:last-child{border-bottom:none;}

/* EMAIL PANEL */
.email-panel{min-width:160px;max-width:400px;}
.filter-bar{padding:8px 14px;border-bottom:1px solid var(--border);font-size:10px;color:var(--text3);display:flex;align-items:center;gap:6px;flex-shrink:0;background:var(--s2);}
.filt-on{color:var(--ops);background:var(--ops-d);padding:2px 8px;border-radius:10px;border:1px solid rgba(39,174,96,0.3);font-size:9px;font-weight:600;}
.em-item{padding:9px 12px;border-bottom:1px solid var(--border);cursor:pointer;transition:background 0.15s;background:var(--s2);}
.em-item:hover{background:white;}
.em-urgent{border-left:3px solid var(--danger);background:rgba(231,76,60,0.05)!important;}
.em-vip{border-left:3px solid var(--strat);}
.em-blocked{opacity:0.3;pointer-events:none;}
.em-client{border-left:3px solid var(--tact);}
.e-from{font-size:11px;color:var(--text2);margin-bottom:2px;display:flex;align-items:center;gap:5px;flex-wrap:wrap;}
.e-subj{font-size:11px;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:4px;}
.e-ai{font-size:10px;color:var(--text3);line-height:1.5;margin-bottom:4px;}
.e-meta{display:flex;align-items:center;gap:5px;font-size:10px;color:var(--text3);}
.eb{font-size:8px;padding:1px 5px;border-radius:3px;letter-spacing:0.07em;font-weight:600;text-transform:uppercase;}
.eb-vip{background:var(--strat-d);color:var(--strat);}
.eb-urg{background:var(--danger-d);color:var(--danger);}
.eb-cli{background:var(--tact-d);color:var(--tact);}
.eb-blk{background:var(--s3);color:var(--text3);}
.eb-ai{background:rgba(96,165,250,0.08);color:#93c5fd;}

/* AUTOTASK TICKET MODAL */
.todo-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:1000;display:flex;align-items:center;justify-content:center;}
.todo-modal{background:white;border-radius:12px;width:480px;max-width:95vw;box-shadow:0 20px 60px rgba(0,0,0,0.2);display:flex;flex-direction:column;overflow:hidden;}
.todo-modal-hdr{padding:18px 20px 14px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;}
.todo-modal-body{padding:20px;display:flex;flex-direction:column;gap:14px;overflow-y:auto;max-height:70vh;}
.todo-modal-footer{padding:14px 20px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:8px;}
.todo-field{display:flex;flex-direction:column;gap:5px;}
.todo-field label{font-size:11px;font-weight:600;color:var(--text2);letter-spacing:0.02em;}
.at-overlay{position:fixed;inset:0;z-index:500;background:rgba(32,32,32,0.6);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);}
.em-detail-overlay{position:fixed;inset:0;z-index:400;background:rgba(32,32,32,0.55);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);}
.em-detail-modal{background:white;border:1px solid var(--border);border-radius:12px;width:680px;max-width:90vw;max-height:85vh;display:flex;flex-direction:column;box-shadow:var(--shadow-xl);}
.em-detail-hdr{padding:18px 22px 14px;border-bottom:1px solid var(--border);flex-shrink:0;background:linear-gradient(135deg,var(--dt-navy) 0%,var(--dt-secondary) 100%);border-radius:12px 12px 0 0;color:white;}
.em-detail-from{font-size:13px;font-weight:700;letter-spacing:-0.2px;margin-bottom:4px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.em-detail-subj{font-size:15px;font-weight:600;margin-bottom:6px;line-height:1.3;}
.em-detail-meta{font-size:11px;color:rgba(255,255,255,0.7);display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.em-detail-body{flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;padding:18px 22px;font-size:13px;line-height:1.6;color:var(--text);word-wrap:break-word;background:white;}
.em-detail-body::-webkit-scrollbar{width:6px;}
.em-detail-body::-webkit-scrollbar-thumb{background:var(--border2);border-radius:3px;}
.em-detail-body::-webkit-scrollbar-thumb:hover{background:var(--text3);}
.em-detail-body img{max-width:100%;height:auto;}
.em-detail-body a{color:var(--strat);}
.em-detail-ai{margin:0 22px 0;padding:10px 12px;background:var(--s2);border:1px solid var(--border);border-radius:8px;font-size:11px;color:var(--text2);flex-shrink:0;}
.em-detail-ai-label{font-size:9px;letter-spacing:0.12em;text-transform:uppercase;color:var(--strat);margin-bottom:4px;font-weight:600;}
.em-detail-actions{padding:12px 22px;border-top:1px solid var(--border);background:var(--bg);border-radius:0 0 12px 12px;display:flex;gap:8px;flex-shrink:0;flex-wrap:wrap;align-items:center;}
.em-detail-actions .btn{font-size:12px;padding:8px 14px;}
.em-detail-close{background:transparent;border:none;color:rgba(255,255,255,0.7);font-size:22px;cursor:pointer;padding:0;line-height:1;margin-left:auto;transition:color 0.2s;}
.em-detail-close:hover{color:white;}
.at-modal{background:white;border:1px solid var(--border);border-radius:12px;width:560px;max-height:88vh;display:flex;flex-direction:column;box-shadow:var(--shadow-xl);}
.at-modal-hdr{padding:0;border-bottom:1px solid var(--border);flex-shrink:0;background:linear-gradient(135deg,var(--dt-navy) 0%,var(--dt-secondary) 100%);border-radius:12px 12px 0 0;padding:18px 22px 16px;}
.at-modal-title{font-family:var(--ff-head);font-weight:700;font-size:16px;letter-spacing:-0.3px;color:white;margin-bottom:3px;display:flex;align-items:center;gap:10px;}
.at-modal-sub{font-size:10px;color:rgba(255,255,255,0.7);display:flex;align-items:center;gap:8px;}
.at-modal-body{overflow-y:auto;padding:18px 22px;flex:1;}
.at-modal-body::-webkit-scrollbar{width:4px;}.at-modal-body::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px;}
.at-modal-footer{padding:14px 22px;border-top:1px solid var(--border);display:flex;gap:8px;align-items:center;flex-shrink:0;background:var(--bg);border-radius:0 0 12px 12px;}
.at-field{margin-bottom:14px;}
.at-label{font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text3);margin-bottom:6px;display:flex;align-items:center;gap:6px;font-weight:600;}
.at-label-required{color:var(--danger);font-size:9px;}
.at-input{width:100%;background:#f8f9fa;border:1px solid #ddd;border-radius:8px;padding:9px 12px;color:var(--text);font-family:var(--ff-head);font-size:12px;outline:none;transition:all 0.3s ease;}
.at-input:focus{border-color:var(--strat);box-shadow:0 0 0 3px rgba(11,128,165,0.12);background:white;}
.at-input::placeholder{color:var(--text3);}
select.at-input{cursor:pointer;}
textarea.at-input{resize:vertical;min-height:90px;line-height:1.65;}
.at-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.at-row-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;}
.at-section-label{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--dt-secondary);margin:16px 0 10px;padding-bottom:6px;border-bottom:2px solid var(--strat-d);display:flex;align-items:center;gap:6px;font-weight:600;}
.at-contact-chip{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;background:var(--strat-d);border:1px solid rgba(11,128,165,0.2);margin-bottom:14px;}
.at-contact-chip.not-found{background:var(--s2);border-color:var(--border);}
.at-contact-name{font-size:12px;color:var(--strat);font-weight:600;}
.at-contact-name.not-found{color:var(--text3);}
.at-contact-email{font-size:10px;color:var(--text3);}
.at-lookup-spin{width:12px;height:12px;border-radius:50%;border:2px solid var(--border);border-top-color:var(--dt-secondary);animation:spin 0.7s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}
.at-custom-field{display:flex;gap:8px;margin-bottom:6px;align-items:center;}
.at-custom-field .at-input{flex:1;margin:0;}
.at-add-custom{display:flex;align-items:center;gap:7px;font-size:12px;color:var(--text3);cursor:pointer;padding:5px 0;border:none;background:transparent;font-family:var(--ff-head);font-weight:500;transition:color 0.2s;}
.at-add-custom:hover{color:var(--strat);}
.at-success-overlay{position:fixed;inset:0;z-index:600;background:rgba(32,32,32,0.6);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);}
.at-success-box{background:white;border:1px solid rgba(39,174,96,0.3);border-radius:12px;padding:36px 44px;text-align:center;box-shadow:var(--shadow-xl);}
.at-success-icon{font-size:44px;margin-bottom:14px;}
.at-success-title{font-family:var(--ff-head);font-weight:700;font-size:20px;color:var(--ops);margin-bottom:8px;letter-spacing:-0.3px;}
.at-success-sub{font-size:13px;color:var(--text3);line-height:1.6;}
.at-ticket-num{font-size:12px;background:var(--strat-d);color:var(--strat);border:1px solid rgba(11,128,165,0.25);padding:5px 14px;border-radius:6px;display:inline-block;margin-top:10px;letter-spacing:0.04em;font-weight:600;}
.em-ticket-btn{display:inline-flex;align-items:center;gap:3px;font-size:9px;padding:3px 8px;border-radius:4px;border:1px solid rgba(11,128,165,0.25);background:var(--strat-d);color:var(--strat);cursor:pointer;font-family:var(--ff-head);transition:all 0.2s ease;letter-spacing:0.03em;font-weight:600;}
.em-ticket-btn:hover{background:var(--strat);color:white;border-color:var(--strat);transform:translateY(-1px);}
.em-ticket-btn.ticketed{border-color:rgba(39,174,96,0.25);background:var(--ops-d);color:var(--ops);cursor:default;}

/* CONNECT BOX */
.connect-box{margin:14px;padding:20px;border-radius:10px;border:1px solid var(--border);background:white;text-align:center;box-shadow:var(--shadow-sm);}
.c-icon{font-size:28px;margin-bottom:10px;}
.c-title{font-family:var(--ff-head);font-weight:700;font-size:14px;color:var(--text);margin-bottom:6px;}
.c-desc{font-size:11px;color:var(--text3);line-height:1.65;margin-bottom:14px;}
code{background:var(--s3);padding:1px 4px;border-radius:3px;font-size:10px;}
.row-gap{display:flex;gap:6px;margin-bottom:6px;align-items:center;}

/* CADENCE FULL */
.cadence-full{padding:16px;display:flex;flex-direction:column;gap:0;background:var(--bg);height:100%;overflow:hidden;}
.cadence-cols{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;flex:1;overflow:hidden;min-height:0;}
.cadence-col{display:flex;flex-direction:column;overflow:hidden;border:1px solid var(--border);border-radius:10px;box-shadow:var(--shadow-sm);}
.cadence-col-body{flex:1;overflow-y:auto;background:white;}
.cad-section{border:1px solid var(--border);border-radius:10px;overflow:hidden;box-shadow:var(--shadow-sm);}
.cad-sh{padding:12px 16px;display:flex;align-items:center;justify-content:space-between;}
.cad-sh.hS{background:linear-gradient(135deg,var(--dt-navy),var(--strat));border-bottom:none;}
.cad-sh.hT{background:linear-gradient(135deg,var(--dt-navy),var(--tact));border-bottom:none;}
.cad-sh.hO{background:linear-gradient(135deg,var(--dt-secondary),var(--ops));border-bottom:none;}
.cad-st{font-family:var(--ff-head);font-weight:700;font-size:13px;color:white!important;}
.cad-st.tS,.cad-st.tT,.cad-st.tO{color:white!important;}
.cad-sub{font-size:10px;color:rgba(255,255,255,0.7);margin-top:2px;}
.cad-item{display:flex;align-items:flex-start;gap:9px;padding:10px 16px;border-bottom:1px solid var(--border);cursor:default;transition:background 0.15s;background:white;}
.cad-item:last-child{border-bottom:none;}
.cad-item:hover{background:rgba(0,0,0,0.01);}.cad-cb:hover{border-color:var(--tact);background:rgba(52,152,219,0.08);}
.cad-item.ci-done{opacity:0.45;}
.cad-cb{width:16px;height:16px;border-radius:4px;border:2px solid var(--border2);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;margin-top:1px;transition:all 0.2s;cursor:pointer;}
.cad-cb.cbS{background:var(--strat);border-color:var(--strat);color:white;}
.cad-cb.cbT{background:var(--tact);border-color:var(--tact);color:white;}
.cad-cb.cbO{background:var(--ops);border-color:var(--ops);color:white;}
.ci-title{font-size:12px;color:var(--text);margin-bottom:2px;font-weight:500;}
.cad-item.ci-done .ci-title{text-decoration:line-through;}
.ci-desc{font-size:11px;color:var(--text3);line-height:1.5;}

/* MEETING INTELLIGENCE PACK */
.mip-section{padding:12px 22px 14px;border-bottom:1px solid var(--border);flex-shrink:0;background:linear-gradient(135deg,rgba(42,67,101,0.04) 0%,rgba(11,128,165,0.03) 100%);}
.mip-hdr{display:flex;align-items:center;gap:8px;margin-bottom:0;}
.mip-label{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--tact);font-weight:700;display:flex;align-items:center;gap:6px;flex:1;}
.mip-snapshot{font-size:12px;color:var(--text2);line-height:1.75;margin-bottom:12px;padding:10px 13px;background:white;border-radius:7px;border:1px solid var(--border);border-left:3px solid var(--tact);}
.mip-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;}
.mip-card{background:white;border:1px solid var(--border);border-radius:8px;overflow:hidden;}
.mip-card-hdr{padding:7px 11px;font-size:10px;font-weight:700;color:var(--text2);border-bottom:1px solid var(--border);display:flex;align-items:center;gap:5px;background:var(--s2);}
.mip-card-body{padding:8px 11px;display:flex;flex-direction:column;gap:4px;}
.mip-bullet{font-size:11px;color:var(--text2);line-height:1.45;padding-left:10px;position:relative;}
.mip-bullet::before{content:"•";position:absolute;left:0;color:var(--text3);}
.mip-bullet.mip-ok::before{content:"✓";color:var(--ops);}
.mip-bullet.mip-warn::before{content:"⚠";color:var(--warn);}
.mip-bullet.mip-none{color:var(--text3);font-style:italic;padding-left:0;}
.mip-bullet.mip-none::before{content:"";}
.mip-tp-section{background:white;border:1px solid var(--border);border-radius:8px;overflow:hidden;}
.mip-tp-hdr{padding:7px 11px;font-size:10px;font-weight:700;color:var(--dt-navy);border-bottom:1px solid var(--border);display:flex;align-items:center;gap:5px;background:linear-gradient(135deg,rgba(42,67,101,0.07),rgba(11,128,165,0.05));}
.mip-tp-body{padding:8px 11px;display:flex;flex-direction:column;gap:6px;}
.mip-tp-item{font-size:11px;color:var(--text);line-height:1.5;display:flex;gap:8px;align-items:flex-start;}
.mip-tp-num{flex-shrink:0;font-weight:700;color:var(--tact);min-width:16px;}
.mip-edit-input{width:100%;border:1px solid var(--border);border-radius:5px;padding:4px 7px;font-family:var(--ff-head);font-size:11px;color:var(--text);background:white;outline:none;resize:none;line-height:1.45;}
.mip-edit-input:focus{border-color:var(--strat);box-shadow:0 0 0 2px var(--strat-d);}
.mip-edit-row{display:flex;align-items:flex-start;gap:5px;margin-bottom:5px;}
.mip-edit-add{display:flex;align-items:center;gap:5px;padding:4px 0;border:none;background:transparent;font-size:10px;color:var(--strat);cursor:pointer;font-family:var(--ff-head);}
.mip-commit-btn{background:linear-gradient(135deg,var(--dt-navy),var(--dt-secondary));color:white;border:none;border-radius:6px;padding:6px 14px;font-family:var(--ff-head);font-size:10px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;transition:opacity .15s;}
.mip-commit-btn:disabled{opacity:.45;cursor:not-allowed;}
.mip-meeting-url{display:flex;align-items:center;gap:7px;padding:8px 10px;background:linear-gradient(135deg,rgba(42,67,101,0.06),rgba(11,128,165,0.04));border:1px solid var(--tact-d);border-radius:7px;margin-top:10px;}

/* PREP FORM EDITOR MODAL */
.pe-overlay{position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,0.55);display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(3px);}
.pe-modal{background:white;border-radius:12px;width:100%;max-width:700px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:var(--shadow-xl);}
.pe-hdr{background:linear-gradient(135deg,var(--dt-navy),var(--dt-secondary));padding:18px 22px 14px;flex-shrink:0;border-radius:12px 12px 0 0;}
.pe-email-bar{padding:12px 22px;border-bottom:2px solid var(--border);background:var(--s2);flex-shrink:0;display:flex;align-items:center;gap:10px;}
.pe-body{flex:1;overflow-y:auto;padding:16px 22px;}
.pe-body::-webkit-scrollbar{width:4px;}.pe-body::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px;}
.pe-footer{padding:13px 22px;border-top:1px solid var(--border);display:flex;gap:8px;align-items:center;flex-shrink:0;background:var(--s2);}
.pe-sec{background:white;border:1px solid var(--border);border-radius:8px;overflow:hidden;margin-bottom:12px;box-shadow:var(--shadow-sm);}
.pe-sec-hdr{display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--s3);border-bottom:1px solid var(--border);}
.pe-sec-name{flex:1;border:none;outline:none;background:transparent;font-family:var(--ff-head);font-size:12px;font-weight:700;color:var(--text);letter-spacing:.01em;}
.pe-sec-name:focus{background:white;border-radius:4px;padding:2px 6px;box-shadow:0 0 0 2px var(--strat-d);}
.pe-move-btn{width:20px;height:20px;border:1px solid var(--border);border-radius:4px;background:var(--s2);color:var(--text3);cursor:pointer;font-size:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;line-height:1;transition:.1s;}
.pe-move-btn:hover:not(:disabled){background:var(--tact-d);color:var(--tact);border-color:rgba(11,128,165,0.3);}
.pe-move-btn:disabled{opacity:.25;cursor:default;}
.pe-q{display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid var(--s3);}
.pe-q:last-of-type{border-bottom:none;}
.pe-q-label{flex:1;border:none;outline:none;background:transparent;font-family:var(--ff-head);font-size:12px;color:var(--text);padding:2px 4px;border-radius:4px;}
.pe-q-label:focus{background:var(--strat-d);box-shadow:0 0 0 2px rgba(11,128,165,0.2);}
.pe-add-q{display:flex;gap:6px;align-items:center;padding:8px 12px;background:var(--s2);border-top:1px solid var(--border);}
.pe-new-sec{display:flex;gap:6px;align-items:center;padding:10px 0 4px;}
`;

// ─── Constants ─────────────────────────────────────────────────────────────

const LAYERS={S:{label:"Strategic"},T:{label:"Tactical"},O:{label:"Operational"},B:{label:"Break"},C:{label:"Meeting"}};
const PREP_CFG={
  qbr:    {layer:"S",days:5,label:"QBR",    tasks:["Roadmap update","Risk & vulnerability review","Budget forecast","Lifecycle planning"]},
  monthly:{layer:"T",days:3,label:"Monthly", tasks:["Monthly report (traffic light)","Action register update","Security & backup status"]},
  adhoc:  {layer:"O",days:1,label:"Ad-hoc",  tasks:["Meeting brief","Open action items review"]},
};

function subtractWD(date,days){const d=new Date(date);let n=0;while(n<days){d.setDate(d.getDate()-1);if(d.getDay()!==0&&d.getDay()!==6)n++;}return d;}
function addWD(date,days){const d=new Date(date);let n=0;while(n<days){d.setDate(d.getDate()+1);if(d.getDay()!==0&&d.getDay()!==6)n++;}return d;}
function daysUntil(date){const now=new Date();now.setHours(0,0,0,0);const d=new Date(date);d.setHours(0,0,0,0);return Math.round((d-now)/(864e5));}
function fmt(s){return`${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;}
function fmtD(d){return new Date(d).toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});}
function isoDate(d){const dt=new Date(d);return`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;}
// Normalise a string for fuzzy company matching — strip spaces, punctuation, lowercase
function normCo(s){return(s||"").toLowerCase().replace(/[\s\-_'&.,]/g,"");}




function makeDate(n){const d=new Date();d.setDate(d.getDate()+n);while(d.getDay()===0||d.getDay()===6)d.setDate(d.getDate()+1);return d;}
function todayISO(){const n=new Date();const p=new Date(n.getTime()+8*60*60*1000);return`${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;}
// How far back the "past" lists reach: Past Prep Sessions and Past Meetings.
// ONE definition so the two cannot drift — they were 30 days and unbounded
// respectively, and neither matched what the calendar actually held.
// ⚠️ Bounded by the server: graphService.getCalendarEvents fetches 6 months
// back. Reaching further needs that window widened FIRST, or this silently
// returns whatever happens to have been fetched.
const PAST_WINDOW_MONTHS=6;
function pastWindowStartISO(){const d=new Date();d.setMonth(d.getMonth()-PAST_WINDOW_MONTHS);return d.toISOString().slice(0,10);}

// ─── Mock Data ──────────────────────────────────────────────────────────────

const MEETINGS=[
  {id:"m1",title:"PG Hotels — Monthly IT Management Meeting",client:"PG Hotels",tier:"vcio",date:makeDate(3), type:"monthly"},
  {id:"m2",title:"CBH Group — QBR Q2 Strategy Session",      client:"CBH Group",tier:"vcio",date:makeDate(7), type:"qbr"},
  {id:"m3",title:"TVH Hotel — Monthly Check-in",             client:"TVH Hotel",tier:"vcio",date:makeDate(11),type:"monthly"},
  {id:"m4",title:"HR Hotel — Monthly Review",                client:"HR Hotel", tier:"mgd", date:makeDate(14),type:"monthly"},
];

const DEF_BLOCKS=[]; // Start empty — blocks come from Plan My Day

// ── Pre-built Pre-Meeting Form template — Hotel, organised by department ──────
// Seeds the "+ Hotel — By Department" button on the Templates page. Lives at
// module scope so it's a single source of truth and doesn't bloat component state.
const HOTEL_BY_DEPARTMENT_QUESTIONS=[
  // Front Office
  {id:"fo-q1",section:"Front Office",   type:"yesno",   label:"PMS is operating without disruption"},
  {id:"fo-q2",section:"Front Office",   type:"yesno",   label:"Front desk performance has been smooth (no significant slowness or downtime)"},
  {id:"fo-q3",section:"Front Office",   type:"yesno",   label:"Integrations (booking engine, channel manager) working correctly"},
  {id:"fo-q4",section:"Front Office",   type:"yesno",   label:"Reservations, check-in, and billing have been free of issues"},
  {id:"fo-q5",section:"Front Office",   type:"yesno",   label:"Phone system at reception functioning reliably"},
  {id:"fo-c", section:"Front Office",   type:"textarea",label:"Comments (Front Office)",optional:true},
  // Food & Beverage
  {id:"fb-q1",section:"Food & Beverage",type:"yesno",   label:"POS terminals functioning correctly across all outlets"},
  {id:"fb-q2",section:"Food & Beverage",type:"yesno",   label:"Transactions completing without delays or system freezes"},
  {id:"fb-q3",section:"Food & Beverage",type:"yesno",   label:"PMS room-charge integration working correctly"},
  {id:"fb-q4",section:"Food & Beverage",type:"yesno",   label:"Payment processing has been issue-free"},
  {id:"fb-q5",section:"Food & Beverage",type:"yesnoNA", label:"Kitchen Display Systems / kitchen printers operating reliably"},
  {id:"fb-c", section:"Food & Beverage",type:"textarea",label:"Comments (Food & Beverage)",optional:true},
  // Housekeeping
  {id:"hk-q1",section:"Housekeeping",   type:"yesno",   label:"Room-status updates syncing reliably to the PMS"},
  {id:"hk-q2",section:"Housekeeping",   type:"yesnoNA", label:"Handheld devices / tablets used by housekeeping are reliable"},
  {id:"hk-q3",section:"Housekeeping",   type:"yesno",   label:"In-room tech issues (TV, phone, climate) being logged and addressed quickly"},
  {id:"hk-c", section:"Housekeeping",   type:"textarea",label:"Comments (Housekeeping)",optional:true},
  // Maintenance
  {id:"mt-q1",section:"Maintenance",    type:"yesno",   label:"Network has been stable across the property (no outages)"},
  {id:"mt-q2",section:"Maintenance",    type:"yesno",   label:"Critical infrastructure operating normally (servers, firewall, switches)"},
  {id:"mt-q3",section:"Maintenance",    type:"yesno",   label:"Wi-Fi coverage adequate across all areas (rooms, lobby, back of house)"},
  {id:"mt-q4",section:"Maintenance",    type:"yesnoNA", label:"Access control systems functioning correctly"},
  {id:"mt-q5",section:"Maintenance",    type:"yesnoNA", label:"CCTV systems operating and accessible"},
  {id:"mt-c", section:"Maintenance",    type:"textarea",label:"Comments (Maintenance)",optional:true},
  // Sales
  {id:"sl-q1",section:"Sales",          type:"yesnoNA", label:"Conference / event Wi-Fi performed as expected for events held"},
  {id:"sl-q2",section:"Sales",          type:"yesnoNA", label:"Event AV systems (projectors, screens, audio) functioning when needed"},
  {id:"sl-q3",section:"Sales",          type:"yesno",   label:"Sales team had reliable access to email and CRM"},
  {id:"sl-c", section:"Sales",          type:"textarea",label:"Comments (Sales / Events)",optional:true},
  // Overall
  {id:"ov-q1",section:"Overall Feedback",type:"yesno",  label:"Guest Wi-Fi is meeting expectations for speed and reliability"},
  {id:"ov-q2",section:"Overall Feedback",type:"yesno",  label:"Satisfied with IT support response times this month"},
  {id:"ov-q3",section:"Overall Feedback",type:"yesno",  label:"Issues were resolved effectively"},
  {id:"ov-q4",section:"Overall Feedback",type:"yesno",  label:"All recurring or unresolved issues affecting staff productivity have been addressed"},
  {id:"ov-q5",section:"Overall Feedback",type:"yesno",  label:"IT systems supported revenue and guest satisfaction without disruption this month"},
  {id:"ov-q6",section:"Overall Feedback",type:"yesno",  label:"Security has been incident-free this month"},
  {id:"ov-q7",section:"Overall Feedback",type:"yesno",  label:"We have upcoming events, peak periods, or planned changes to discuss"},
  {id:"ov-r", section:"Overall Feedback",type:"rating", label:"Overall IT satisfaction this month",scale:5},
  {id:"ov-c", section:"Overall Feedback",type:"textarea",label:"Anything else you would like reviewed in the meeting?",optional:true},
];

const EMAILS=[
  {id:1,from:"Sarah Chen",    subj:"URGENT: Production server down",    time:"9:02",priority:"urgent",vip:true, aiSum:"Server down since 8:45am. ~200 users affected."},
  {id:2,from:"Marcus Webb",   subj:"Q2 roadmap — need your section",    time:"8:47",priority:"vip",   vip:true, aiSum:"Needs your roadmap input by EOD Friday."},
  {id:3,from:"Dale Purdy",    subj:"QBR rescheduled — Thu 2pm",         time:"8:15",priority:"client",vip:true, aiSum:"QBR moved to Thursday 2pm. Calendar update needed."},
  {id:4,from:"Jake Langridge",subj:"PG Hotels cable runs complete",     time:"8:10",priority:"normal",vip:false,aiSum:"Phase 1 cable complete at TVH. Awaiting sign-off."},
  {id:5,from:"GitHub",        subj:"PR #47: Agent builder API",         time:"7:55",priority:"normal",vip:false,aiSum:"Srijana opened PR on agent builder endpoint."},
  {id:6,from:"LinkedIn",      subj:"4 new connection requests",         time:"7:40",priority:"noise", vip:false,aiSum:null},
  {id:7,from:"AWS Billing",   subj:"Your monthly bill is ready",        time:"7:20",priority:"noise", vip:false,aiSum:null},
];

const CAD_DATA={
  S:{label:"Strategic",freq:"Quarterly",desc:"QBRs · Roadmaps · Budget · Business alignment",
    items:[{id:"s1",title:"PG Hotels QBR",desc:"12-month roadmap, risk review, lifecycle",client:"PG Hotels",done:false},{id:"s2",title:"CBH IT Roadmap update",desc:"Q3 priorities, infrastructure lifecycle",client:"CBH",done:false},{id:"s3",title:"TVH security posture review",desc:"Conditional access, MFA, vulnerability scan",client:"TVH",done:true},{id:"s4",title:"Budget forecast — vCIO clients",desc:"Device refresh, licensing, Azure costs",client:"All",done:false}]},
  T:{label:"Tactical",freq:"Monthly",desc:"Reports · IT Management meetings · Action tracking",
    items:[{id:"t1",title:"Monthly report — CBH",desc:"Traffic light, ticket themes, security, backups",client:"CBH",done:false},{id:"t2",title:"Monthly report — TVH",desc:"Traffic light, ticket themes, security, backups",client:"TVH",done:false},{id:"t3",title:"Monthly report — HR Hotel",desc:"Traffic light summary, patch status",client:"HR Hotel",done:true},{id:"t4",title:"IT management meeting — PG Hotels",desc:"Review month, priorities, action register",client:"PG Hotels",done:false}]},
  O:{label:"Operational",freq:"Weekly",desc:"Client health · Monitoring · Proactive fixes",
    items:[{id:"o1",title:"Client health check",desc:"Monitoring alerts, backup status, security events",client:"All",done:false},{id:"o2",title:"Open ticket review",desc:"Aging tickets, unassigned, recurring issues",client:"All",done:true},{id:"o3",title:"Patch status check",desc:"Outstanding patches across managed endpoints",client:"All",done:false},{id:"o4",title:"Proactive comms — vCIO clients",desc:"Brief status update email",client:"vCIO",done:false}]},
};

/* ⚠ THE CANONICAL TIER ENUM, mirrored from the backend's clientTierService.
   These exact strings key CADENCE_TEMPLATES below and the server's rank map, so
   the two lists must stay in lockstep — a tier present here but absent there
   (or vice versa) falls through to the Adhoc cadence silently. */
const CLIENT_TIERS = ["Gold", "Silver", "Bronze", "Copper", "Adhoc"];

// ── Tier-based cadence templates ─────────────────────────────────────────────
const CADENCE_TEMPLATES={
  Gold:[
    {layer:"S",title:"QBR — {client}",desc:"12-month roadmap, risk review, lifecycle planning, budget forecast",freqWeeks:13},
    {layer:"S",title:"Annual IT Strategy — {client}",desc:"3-year technology direction, compliance alignment, executive presentation",freqWeeks:52},
    {layer:"T",title:"Monthly Tech Catchup — {client}",desc:"Review open tickets, recurring issues, upcoming changes, action register",freqWeeks:4},
    {layer:"T",title:"IT Health Review — {client}",desc:"Traffic light report, patch status, backup status, security events",freqWeeks:4},
    {layer:"T",title:"Contract Review — {client}",desc:"Service delivery review, upcoming renewals, scope changes",freqWeeks:13},
    {layer:"O",title:"Weekly Ticket Review — {client}",desc:"Aging tickets, unassigned items, escalations, stale issues",freqWeeks:1},
    {layer:"O",title:"Maintenance Coordination — {client}",desc:"Patch windows, firmware updates, certificate renewals, change scheduling",freqWeeks:4},
  ],
  Silver:[
    {layer:"S",title:"Bi-Annual Review — {client}",desc:"6-month roadmap review, risk posture, budget planning"},
    {layer:"T",title:"Monthly Tech Catchup — {client}",desc:"Review open tickets, recurring issues, upcoming changes"},
    {layer:"T",title:"IT Health Review — {client}",desc:"Traffic light report, patch status, backup status"},
    {layer:"O",title:"Ticket Queue Review — {client}",desc:"Aging tickets, unassigned items, escalations"},
    {layer:"O",title:"Maintenance Coordination — {client}",desc:"Patch windows, firmware updates, change scheduling"},
  ],
  Bronze:[
    {layer:"T",title:"Bi-Monthly Catchup — {client}",desc:"Review open items, upcoming changes, service update"},
    {layer:"T",title:"IT Health Summary — {client}",desc:"Patch status, backup status, key alerts"},
    {layer:"O",title:"Ticket Review — {client}",desc:"Aging tickets, unassigned items, escalations"},
  ],
  Copper:[
    {layer:"T",title:"Quarterly Touchpoint — {client}",desc:"Service check-in, open items, upcoming needs"},
    {layer:"O",title:"Ticket Review — {client}",desc:"Open and aging tickets, any escalations"},
  ],
  Adhoc:[
    {layer:"O",title:"Account Check-in — {client}",desc:"Open tickets, any outstanding items"},
  ],
};

const CLIENTS=[
  {id:"c1",name:"PG Hotels (Dale Purdy)",tier:"vcio"},
  {id:"c2",name:"CBH Group",tier:"vcio"},
  {id:"c3",name:"TVH Hotel",tier:"vcio"},
  {id:"c4",name:"HR Hotel",tier:"mgd"},
  {id:"c5",name:"Misc client A",tier:"bf"},
];

// ─── Autotask Reference Data (mock — replaced by live API calls when credentials set) ──
const AT_COMPANIES=[
  {id:29682,name:"PG Hotels"},
  {id:29683,name:"CBH Group"},
  {id:29684,name:"TVH Hotel"},
  {id:29685,name:"HR Hotel"},
  {id:29686,name:"Datatech Solutions"},
];
const AT_CONTACTS=[
  {id:1001,companyId:29682,firstName:"Dale",lastName:"Purdy",email:"dale@pghotels.com.au"},
  {id:1002,companyId:29682,firstName:"Jake",lastName:"Langridge",email:"jake@pghotels.com.au"},
  {id:1003,companyId:29683,firstName:"Marcus",lastName:"Webb",email:"marcus@cbhgroup.com.au"},
  {id:1004,companyId:29684,firstName:"Sarah",lastName:"Chen",email:"sarah@clientco.com"},
  {id:1005,companyId:29685,firstName:"HR",lastName:"Admin",email:"admin@hrhotel.com.au"},
];
const AT_QUEUES=[
  {id:29674,name:"Service Desk"},
  {id:29675,name:"Network & Infrastructure"},
  {id:29676,name:"Security"},
  {id:29677,name:"Projects"},
  {id:29678,name:"Management"},
];
const AT_STATUSES=[
  {id:1,name:"New"},
  {id:5,name:"In Progress"},
  {id:8,name:"Waiting Customer"},
  {id:11,name:"Waiting Vendor"},
  {id:32,name:"Complete"},
];
const AT_PRIORITIES=[
  {id:1,name:"Critical"},
  {id:2,name:"High"},
  {id:3,name:"Medium"},
  {id:4,name:"Low"},
];
const AT_WORK_TYPES=[
  {id:12,name:"Break/Fix"},
  {id:13,name:"Managed Services"},
  {id:14,name:"Project Work"},
  {id:15,name:"Consulting"},
  {id:16,name:"Internal"},
];
/* ── CLIENT REGISTER COLUMNS — ONE definition, header AND rows ──────────────
   It was a 13-column px literal written out TWICE, once for the header and once
   per row. Two copies of a column template is a drift waiting to happen: edit
   one and the header silently stops lining up with the data under it.

   ⚠ minmax(min, fr), NOT fixed px. Fixed columns summed to ~1860px regardless
   of the window, so on a wide screen they clustered left with a dead band down
   the right, and on a narrow one they pushed the PAGE wider than the viewport.
   The fr weights let the columns fill the section; the minimums are what keeps
   the crowded cells usable — M365 (Connect + the reuse-tenant dropdown), IT Glue
   (org id + find) and Report ID (four stacked vendor fields) — while ID, WAN,
   Tier and Mgd stay narrow.

   ⚠ REGISTER_MIN_W IS THE SUM OF THE MINIMUMS PLUS THE GAPS, and it is what
   makes the two requirements reconcile: ABOVE this width the columns spread to
   fill 100% and nothing scrolls; BELOW it the register's own wrapper scrolls
   horizontally. Change a minimum and change this number with it.
     54+160+120+170+130+100+120+64+190+130+170+70+48 = 1526, + 12 gaps x 8 = 1622 */
const REGISTER_COLS = "minmax(54px,0.5fr) minmax(160px,1.7fr) minmax(120px,1.2fr) "
  + "minmax(170px,1.8fr) minmax(130px,1.3fr) minmax(100px,1fr) minmax(120px,1.2fr) "
  + "minmax(64px,0.6fr) minmax(190px,2fr) minmax(130px,1.3fr) minmax(170px,1.7fr) "
  + "minmax(70px,0.7fr) minmax(48px,0.45fr)";
const REGISTER_MIN_W = 1622;

const AT_CONTRACTS=[
  {id:500,companyId:29682,name:"PG Hotels — Managed Services"},
  {id:501,companyId:29683,name:"CBH Group — Managed Services"},
  {id:502,companyId:29684,name:"TVH Hotel — Managed Services"},
  {id:503,companyId:29685,name:"HR Hotel — Basic Support"},
];
const AT_RESOURCES=[
  {id:200,name:"Craig (You)"},
  {id:201,name:"Jake Langridge"},
  {id:202,name:"Srijana McCarthy"},
  {id:203,name:"Marcus Grigg"},
];



// ─── Main App ───────────────────────────────────────────────────────────────

// Error boundary — catches render crashes so a single panel crash doesn't blank the whole app
class PanelErrorBoundary extends Component {
  constructor(p){super(p);this.state={err:null};}
  static getDerivedStateFromError(e){return{err:e};}
  componentDidCatch(e,info){console.error("Panel render error:",e,info);}
  render(){
    if(this.state.err){
      return(
        <div style={{padding:24,textAlign:"center",color:"var(--danger)",fontSize:12,lineHeight:1.8}}>
          <div style={{fontSize:24,marginBottom:8}}>⚠️</div>
          <div style={{fontWeight:600,marginBottom:4}}>Something went wrong rendering this panel.</div>
          <div style={{color:"var(--text3)",marginBottom:12}}>{this.state.err?.message}</div>
          <button style={{fontSize:11,padding:"4px 12px",borderRadius:4,border:"1px solid var(--border)",cursor:"pointer",background:"white"}}
            onClick={()=>this.setState({err:null})}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App({
  userInfo,
  liveEmails=[],
  liveCalEvents=[],
  atReference=null,
  fellowConnected=false,
  onCreateTicket,
  onArchiveEmail,
  onDeleteEmail=null,
  onReplyEmail=null,
  onAddTicketNote=null,
  onFetchEmailBody=null,
  onGenerateBrief,
  onExtractTodos,
  onFetchChaseUp=null,
  onFetchClientHealth=null,
  onDlpFetch=null,
  onDlpSave=null,
  onDlpPreview=null,
  onLookupContact,
  onGetContracts,
  onGetContacts,
  onGetAssets,
  onGetFellowMeetings,
  onGetFellowNotes,
  onAnnotateFellowMeeting=null,
  onSaveState,
  onTriggerSync,
  onExportCsv,
  onGetSyncStatus,
  savedState=null,
  syncStatus=null,
  onLoadPlan=null,
  onSavePlan=null,
  onPlanDay=null,
  onLoadAtCompanies=null,
  onGetAutotaskData=null,
  atDashData=null,
  onRefreshAtSync=null,
  atSyncing=false,
  onRunSentiment=null,
  preloadedSentiment=null,
  savedClientDomainOverrides={},
  savedClientEmailOverrides={},
  savedMeetingRawNotes={},
  dattoData={sites:[],devices:[],alerts:[],syncStatus:null},
  onTriggerDattoSync=null,
  onRefreshDattoData=null,
  myItProcessData={summaries:[],clients:[],reviews:[],overdue:[],findings:[],recommendations:[],initiatives:[],meetings:[],syncStatus:null},
  onTriggerMyItProcessSync=null,
  onRefreshMyItProcessData=null,
  onSetMyItProcessMapping=null,
  onGetAuthToken=null,
  onSaveFellowToken=null,
  onGetReportToken=null,
  onLoadDayCanvas=null,
  onSaveDayCanvas=null,
  syncedCompanies=[],
  lastEmailRefresh=null,
  emailRefreshing=false,
  onRefreshEmails=null,
  onSignOut,
  onForceLogin,
}) {
  const [tab,setTab]=useState(()=>sessionStorage.getItem("msp-os-tab")||"plan");
  const [tierSaving,setTierSaving]=useState(null);   // cid mid-save
  /* Client Register filter (Settings) — Tier and/or Company Name, AND-combined,
     client-side only. Filters the rows already loaded; no fetch, no data change.
     The Tier filter matches the DISPLAYED tier (c._tier, patched in place on an
     override) — see settings/clientRegisterFilter.js. */
  const [registerTierFilter,setRegisterTierFilter]=useState("");
  const [registerNameFilter,setRegisterNameFilter]=useState("");
  const [mitpSyncing,setMitpSyncing]=useState(false);
  const [mitpMappingClient,setMitpMappingClient]=useState(null);
  const [mitpMappingSearch,setMitpMappingSearch]=useState("");
  // Sidebar accordion — auto-open the section containing the active tab
  const tabToSection=(t)=>({plan:"day",focus:"day",notes:"meetings",prepnotes:"meetings",meetingnotes:"meetings",postreport:"reporting",monthlyreports:"reporting",teamreporting:"reporting",todos:"meetings",calendar:"meetings",prepformtemplates:"prepforms",prepformassign:"prepforms",cadence:"cadence",quoting:"quoting",opportunities:"quoting",proposals:"quoting",proposaltemplates:"quoting",budgeting:"budgeting",autotask:"autotask",myitprocess:"autotask",settings:"settings"})[t]||"day";
  const [navOpen,setNavOpen]=useState({day:true,meetings:true,reporting:true,prepforms:true,cadence:true,quoting:true,autotask:true,settings:true});
  const toggleNav=(sec)=>setNavOpen(o=>({...o,[sec]:!o[sec]}));
  const goTab=(t)=>{setTab(t);setNavOpen(o=>({...o,[tabToSection(t)]:true}));};
  useEffect(()=>{ sessionStorage.setItem("msp-os-tab",tab); },[tab]);

  // ── Side-panel visibility — ALLOWLISTS, deliberately ──────────────────────
  // These were four separate hardcoded DENYlists ("show everywhere except…"),
  // which meant every new tab silently inherited both side panels. Proposals
  // shipped with a Focus rail and an Inbox squeezing it into a narrow middle
  // column purely by being new — nobody chose that.
  //
  // Allowlist instead: a new tab gets no side panels until it opts in. Add the
  // tab id here if it genuinely wants one.
  //
  // The panel and its resize handle MUST read the same flag. The old Focus
  // handle denylist was missing "monthlyreports" and "fellowticketconfig", so
  // a stray drag handle rendered on those tabs with no panel behind it.
  const FOCUS_PANEL_TABS = ["focus","myitprocess"];
  // "opportunities" is here on purpose: the Inbox is that tab's DRAG SOURCE —
  // an email dragged onto a card is how the conversation record gets built, so
  // the panel is functional there, not decorative. Quoting and Proposals stay
  // off it.
  const INBOX_PANEL_TABS = ["plan","focus","todos","budgeting","myitprocess","opportunities"];
  const showFocusPanel = FOCUS_PANEL_TABS.includes(tab);
  const showInboxPanel = INBOX_PANEL_TABS.includes(tab);


  // ── Auto-load Fellow meetings ──────────────────────────────────────────────
  // Load once on mount so Prep Notes and Meeting Notes past sections have data.
  // Also reload when switching to any of the three meeting-related tabs.
  useEffect(()=>{
    if(onGetFellowMeetings&&fellowConn&&fellowMeetings.length===0&&!fellowMeetingsLoading){
      loadFellowMeetings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]); // on mount only
  useEffect(()=>{
    if(["notes","prepnotes","meetingnotes"].includes(tab)&&onGetFellowMeetings&&fellowConn&&fellowMeetings.length===0&&!fellowMeetingsLoading){
      loadFellowMeetings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  // Fetch AT ticket picklists when the Fellow Ticket Settings tab opens (lazy — only once)
  useEffect(()=>{
    if(tab!=="fellowticketconfig") return;
    if(ticketPicklists) return; // already loaded
    const tok=window.__mspToken;
    const ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    fetch(`${ab}/api/tickets/picklists`,{headers:{Authorization:`Bearer ${tok}`}})
      .then(r=>r.json())
      .then(d=>{if(d&&!d.error)setTicketPicklists(d);})
      .catch(err=>console.warn("Failed to fetch ticket picklists",err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  // ── Backend pre-warm ──────────────────────────────────────────────────────
  // Azure App Service sleeps after inactivity — the first request takes 7-8s to cold-start.
  // Fire a cheap ping as soon as Focus tab becomes active so the server is warm
  // by the time the user clicks a block.
  const API_BASE = window.location.hostname==="localhost"
    ? "http://localhost:3001"
    : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
  const backendWarmedRef=useRef(false);
  useEffect(()=>{
    if(tab!=="focus"||backendWarmedRef.current) return;
    backendWarmedRef.current=true;
    // Lightweight ping — we don't need the result, just wake the server
    fetch(`${API_BASE}/api/meeting-prep/ping`,{headers:{Authorization:`Bearer ${window.__mspToken||""}`}})
      .catch(()=>{}); // ignore errors — this is best-effort only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);
  const prevTabRef=useRef("focus");
  // Panel widths — restored from localStorage
  const [planWidth,setPlanWidth]=useState(()=>{
    const saved=localStorage.getItem("msp-os-plan-width");
    return saved?parseInt(saved):232;
  });
  const [inboxWidth,setInboxWidth]=useState(()=>{
    const saved=localStorage.getItem("msp-os-inbox-width");
    return saved?parseInt(saved):220;
  });
  // Daily plan date navigation — all dates use Perth time (UTC+8) to avoid midnight UTC rollover
  const todayStr=()=>{const n=new Date();const p=new Date(n.getTime()+8*60*60*1000);return`${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;};
  const [planDate,setPlanDate]=useState(todayStr);
  const [planLoading,setPlanLoading]=useState(false);
  const planSaveTimer=useRef(null);

  const resizingRef=useRef(null); // {panel, startX, startWidth}
  const [isDragging,setIsDragging]=useState(false);
  // Load Autotask companies on mount for client autocomplete
  useEffect(()=>{
    if(!onLoadAtCompanies) return;
    onLoadAtCompanies().then(companies=>{
      if(companies?.length) setAtCompanies(companies);
    }).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // When background sync completes, parent pushes fresh companies via prop
  useEffect(()=>{
    if(syncedCompanies?.length){
      setAtCompanies(syncedCompanies);
    }
  },[syncedCompanies]);

  const planWidthRef=useRef(planWidth);
  const inboxWidthRef=useRef(inboxWidth);
  // Keep refs in sync with state
  planWidthRef.current=planWidth;
  inboxWidthRef.current=inboxWidth;

  // Only restore blocks if they were saved today (Perth UTC+8) — start fresh each new day
  const _perthNow=(()=>{const n=new Date();return new Date(n.getTime()+8*60*60*1000);})();
  const _todayStr=`${_perthNow.getUTCFullYear()}-${String(_perthNow.getUTCMonth()+1).padStart(2,"0")}-${String(_perthNow.getUTCDate()).padStart(2,"0")}`;
  // lastSaved comes from backend as UTC ISO string — convert to Perth date before comparing
  const _lastSavedPerthDate=(()=>{
    if(!savedState?.lastSaved) return null;
    const utc=new Date(savedState.lastSaved);
    const perth=new Date(utc.getTime()+8*60*60*1000);
    return `${perth.getUTCFullYear()}-${String(perth.getUTCMonth()+1).padStart(2,"0")}-${String(perth.getUTCDate()).padStart(2,"0")}`;
  })();
  const _savedToday=_lastSavedPerthDate===_todayStr;
  const [blocks,setBlocks]=useState(_savedToday&&savedState?.blocks?.length>0?savedState.blocks:DEF_BLOCKS);
  const [selId,setSelId]=useState(2);
  const [focusing,setFocusing]=useState(false);
  const [running,setRunning]=useState(false);
  const [secs,setSecs]=useState(0);
  const [total,setTotal]=useState(0);
  const prevSelIdRef=useRef(null); // tracks previous block id for timer stashing
  const [vips,setVips]=useState(savedState?.vips?.length>0?savedState.vips:["Sarah Chen","Marcus Webb","Dale Purdy"]);
  const [managedCompanyIds,setManagedCompanyIds]=useState(()=>new Set(savedState?.managedCompanyIds||[]));
  // Domain overrides: { [companyId]: customDomain } — persisted to blob, never overwritten by AT sync
  const [clientDomainOverrides,setClientDomainOverrides]=useState(()=>savedClientDomainOverrides||{});
  // Email overrides: { [companyId]: email } — persisted to blob, the address we send the prep form to
  const [clientEmailOverrides,setClientEmailOverrides]=useState(()=>savedClientEmailOverrides||{});
  const [atCompanies,setAtCompanies]=useState([]); // Loaded from AT cache on mount
  // The Client Register's rows after its Tier / Company Name filter (declared above with its state).
  const registerRows = filterRegisterRows(atCompanies, { tier: registerTierFilter, name: registerNameFilter });
  // Which Client Register email row is focused — gates the domain-mismatch
  // warning so it only shows on blur, not mid-typing.
  const [focusedEmailCid,setFocusedEmailCid]=useState(null);

  // ── UniFi mapping (Client Register "UniFi Site" column) ───────────────────
  // Deliberately NOT in saveSnapshot: the mapping is org-wide config that the
  // server-side sync reads straight from its own blob, so the API is the single
  // source of truth. Loaded lazily — available-sites hits the UniFi cloud API.
  const [unifiByCid,setUnifiByCid]=useState({});       // cid -> register entry
  const [unifiSites,setUnifiSites]=useState([]);       // pickable sites
  const [unifiMeta,setUnifiMeta]=useState({lastSyncAt:null,selfHostedConfigured:false});
  const [unifiLoaded,setUnifiLoaded]=useState(false);
  const [unifiErr,setUnifiErr]=useState(null);
  const [unifiSyncing,setUnifiSyncing]=useState(false);
  const [unifiSavingCid,setUnifiSavingCid]=useState(null);

  // ── Client industry (themes the report's "Did You Know?" panel) ───────────
  // Server-side like the UniFi mapping, NOT saveSnapshot: the report generator
  // runs server-side and must read it at generate time.
  const [industryByCid,setIndustryByCid]=useState({});
  const [industrySuggestions,setIndustrySuggestions]=useState([]);
  const saveIndustry=useCallback((cid,value)=>{
    const val=String(value||"").trim();
    if((industryByCid[cid]||"")===val) return;           // no-op on blur without an edit
    setIndustryByCid(prev=>({...prev,[cid]:val}));
    setClientIndustry(cid,val).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save industry"));
  },[industryByCid]);

  // ── Microsoft 365 connection (Client Register "M365" column) ──────────────
  // "Connect" = admin-consent the read-only multi-tenant app into the client's
  // tenant. Craig never types a tenant ID — Microsoft returns it on the callback,
  // bound to a signed single-use state, and the backend stores it.
  const [m365ByCid,setM365ByCid]=useState({});
  const [m365Busy,setM365Busy]=useState(null);
  const [m365Tenants,setM365Tenants]=useState([]);   // already-consented tenants, for shared-tenant reuse
  const [m365Pick,setM365Pick]=useState(null);   // {companyId,companyName,tenantName,domains[],chosen[]}
  const loadM365=useCallback(()=>{
    getM365Config().then(r=>{setM365ByCid(r?.data||{});setM365Tenants(r?.knownTenants||[]);}).catch(()=>{});
  },[]);

  const openDomainPicker=useCallback((cid,name)=>{
    setM365Busy(cid);
    getM365Domains(cid)
      .then(r=>setM365Pick({companyId:cid,companyName:name,tenantName:r.tenantName,domains:r.domains||[],chosen:r.ownedDomains||[]}))
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not read tenant domains"))
      .finally(()=>setM365Busy(null));
  },[]);

  const connectM365=useCallback((cid,name)=>{
    setM365Busy(cid);
    startM365Consent(cid).then(r=>{
      // Consent must happen in a real browser context signed in as THAT tenant's
      // admin; a popup keeps the app state intact behind it.
      const w=window.open(r.consentUrl,"m365consent","width=620,height=760");
      if(!w) window.location.href=r.consentUrl;   // popup blocked → top-level redirect
    }).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not start consent"))
      .finally(()=>setM365Busy(null));
  },[]);

  const reuseM365Tenant=useCallback((cid,name,tenantId)=>{
    setM365Busy(cid);
    linkM365Tenant(cid,tenantId)
      .then(()=>{loadM365();openDomainPicker(cid,name);})
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not link tenant"))
      .finally(()=>setM365Busy(null));
  },[loadM365,openDomainPicker]);

  // IT Glue org id — explicit beats resolve-by-name, which breaks silently when
  // the Autotask and IT Glue names differ (and a wrong org would rate the wrong
  // client's passwords).
  const saveItGlue=useCallback((cid,val)=>{
    const v=String(val||"").trim();
    setM365ByCid(prev=>({...prev,[cid]:{...(prev[cid]||{}),itGlueOrgId:v||null}}));
    setItGlueOrgId(cid,v).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save IT Glue org"));
  },[]);
  // Vendor report identity — the exact client string as the vendor prints it.
  // Matched EXACTLY, never fuzzily: Slipstream calls Kalgoorlie Plaza Hotel
  // "Plaza Kalgoorlie", which no substring rule could ever bridge because the
  // words are reordered. Blank means "match on the Autotask name", which is
  // right for most clients.
  const saveReportIdentity=useCallback((cid,field,val)=>{
    const v=String(val||"").trim();
    setM365ByCid(prev=>({...prev,[cid]:{...(prev[cid]||{}),[field]:v||null}}));
    setReportIdentity(cid,{[field]:v}).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save the report identity"));
  },[]);
  const findItGlue=useCallback((cid)=>{
    setM365Busy(cid);
    lookupItGlueOrg(cid)
      .then(r=>{saveItGlue(cid,r.orgId);setUnifiErr(null);})
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"No IT Glue organisation matched"))
      .finally(()=>setM365Busy(null));
  },[saveItGlue]);

  const recheckM365Conn=useCallback((cid)=>{
    setM365Busy(cid);
    recheckM365(cid).then(()=>loadM365())
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Still cannot reach that tenant"))
      .finally(()=>setM365Busy(null));
  },[loadM365]);

  // The consent callback bounces back to ?tab=settings&m365=... — read it once,
  // show the outcome, and open the domain checklist when the tenant has several.
  useEffect(()=>{
    const q=new URLSearchParams(window.location.search);
    const st=q.get("m365");
    if(!st) return;
    const cid=q.get("company");
    if(st==="connected"){
      loadM365();
      if(q.get("pick")==="1"&&cid){
        const nm=(atCompanies.find(c=>String(c.id)===cid)||{}).companyName||"this client";
        openDomainPicker(cid,nm);
      }
    } else if(st==="novalidate"){
      setUnifiErr("Consent recorded but Graph access isn't working yet ("+(q.get("reason")||"")+"). Use Re-check in a minute.");
      loadM365();
    } else {
      setUnifiErr("M365 connection failed: "+(q.get("reason")||"unknown"));
    }
    // strip the params so a refresh doesn't replay the banner
    window.history.replaceState({},"",window.location.pathname);
  },[atCompanies,loadM365,openDomainPicker]);

  // ── FortiGate mapping (Client Register "FortiGate" + "WAN" columns) ───────
  // Server-side blob like UniFi/industry — the report generator reads it at
  // generate time. The device list only SUGGESTS a client (★); the saved SN is
  // what the report uses.
  const [fgByCid,setFgByCid]=useState({});
  const [fgDevices,setFgDevices]=useState([]);
  const saveFortigate=useCallback((cid,sn,wan)=>{
    setFgByCid(prev=>({...prev,[cid]:{sn,wan:wan||null}}));
    setFortigateConfig(cid,{sn,wan}).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save FortiGate mapping"));
  },[]);

  const loadUnifiRegister=useCallback(()=>{
    return getUnifiRegister().then(r=>{
      const byCid={};(r.clients||[]).forEach(c=>{byCid[String(c.companyId)]=c;});
      setUnifiByCid(byCid);
      setUnifiMeta({lastSyncAt:r.lastSyncAt||null,selfHostedConfigured:!!r.selfHostedConfigured});
      setUnifiErr(null);
    }).catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not load UniFi mapping"));
  },[]);

  // Load once, the first time Settings is opened.
  useEffect(()=>{
    if(tab!=="settings"||unifiLoaded) return;
    setUnifiLoaded(true);
    loadUnifiRegister();
    getIndustries()
      .then(r=>{setIndustryByCid(r?.data||{});setIndustrySuggestions(r?.suggestions||[]);})
      .catch(()=>{});
    getFortigateConfig().then(r=>setFgByCid(r?.data||{})).catch(()=>{});
    getFortigateDevices().then(list=>setFgDevices(list||[])).catch(()=>{});
    loadM365();
    getAvailableSites()
      .then(list=>setUnifiSites(list))
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not load UniFi sites"));
  },[tab,unifiLoaded,loadUnifiRegister,loadM365]);

  // Assign / change / clear one client's site.
  const saveUnifiSite=useCallback((cid,companyName,siteId)=>{
    setUnifiSavingCid(cid);
    const done=()=>setUnifiSavingCid(prev=>prev===cid?null:prev);
    if(!siteId){
      clearClientUnifi(cid)
        .then(()=>setUnifiByCid(prev=>{const next={...prev};delete next[cid];return next;}))
        .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not clear mapping"))
        .finally(done);
      return;
    }
    const site=unifiSites.find(s=>s.siteId===siteId);
    if(!site){done();return;}
    const reportType=unifiByCid[cid]?.reportType||"guestwifi";
    // Send the picked site's friendly label — it becomes the client-facing
    // "UniFi site" shown in the report and archive.
    setClientUnifi(cid,{siteId:site.siteId,siteName:site.siteName,hostId:site.hostId,reportType,companyName,siteLabel:site.label||site.siteName})
      .then(saved=>setUnifiByCid(prev=>({...prev,[cid]:{...(prev[cid]||{}),...saved,companyId:cid,state:"unknown"}})))
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save mapping"))
      .finally(done);
  },[unifiSites,unifiByCid]);

  const saveUnifiReportType=useCallback((cid,companyName,reportType)=>{
    const cur=unifiByCid[cid];
    if(!cur?.siteId) return;
    setUnifiSavingCid(cid);
    setClientUnifi(cid,{siteId:cur.siteId,siteName:cur.siteName,hostId:cur.hostId,reportType,companyName})
      .then(saved=>setUnifiByCid(prev=>({...prev,[cid]:{...(prev[cid]||{}),...saved}})))
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save report type"))
      .finally(()=>setUnifiSavingCid(prev=>prev===cid?null:prev));
  },[unifiByCid]);

  // Sync runs in the background server-side, so re-read the register after a beat.
  const runUnifiSyncNow=useCallback(()=>{
    setUnifiSyncing(true);
    syncUnifi()
      .then(()=>new Promise(res=>setTimeout(res,12000)))
      .then(()=>loadUnifiRegister())
      .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Sync failed"))
      .finally(()=>setUnifiSyncing(false));
  },[loadUnifiRegister]);

  // ── Prep-form recipient domain guard ──────────────────────────────────────
  // A prep form sent to the wrong client's address (Kalgoorlie→Cottesloe, Jul-26)
  // is a cross-client leak with no server-side block. These helpers surface a
  // mismatch between a recipient email's domain and the company's known domain
  // (custom override, else Autotask webAddress) at the point of entry and send.
  const companyKnownDomain = (companyId) => {
    const id = String(companyId);
    const raw = clientDomainOverrides[id]
      || atCompanies.find(c => String(c.id) === id)?.webAddress
      || "";
    return raw.toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].trim();
  };
  const emailDomainOf = (email) => (String(email).split("@")[1] || "").toLowerCase().trim();
  // Returns true to proceed, false to abort. No known domain → always proceed.
  const confirmEmailDomain = (email, companyId, companyName) => {
    const cDom = companyKnownDomain(companyId);
    const eDom = emailDomainOf(email);
    if (!cDom || !eDom || eDom === cDom) return true;
    return window.confirm(
      `⚠ This email domain (@${eDom}) doesn't match ${companyName || "this company"}'s known domain (@${cDom}).\n\n`
      + `Are you sure you want to send to ${email}?`
    );
  };
  // clientDomains derived from Autotask companies cache
  // clientDomains: use manual override if set, otherwise derive from webAddress
  const clientDomains=atCompanies.map(c=>{
    const override=clientDomainOverrides[String(c.id)];
    const derived=c.webAddress
      ? c.webAddress.replace(/^https?:\/\//,"").replace(/^www\./,"").split("/")[0].toLowerCase()
      : "";
    const domain=override||derived;
    return { client:c.companyName, domain, tier:c._tier||"Adhoc", id:c.id };
  }).filter(c=>c.domain);
  const [newVip,setNewVip]=useState("");
  const [emailAlert,setEmailAlert]=useState(null);
  // Client "Discuss at our next catch-up" flags: {companyId: [{period,count,unread,sections}]}
  // and the set of client+period the user has marked covered this session.
  const [discussPending,setDiscussPending]=useState([]);
  const [discussCovered,setDiscussCovered]=useState({});
  const [chaseInboxItems,setChaseInboxItems]=useState([]); // inbox — needs action
  const [chaseSentItems,setChaseSentItems]=useState([]);   // sent — awaiting reply
  const [chaseLoading,setChaseLoading]=useState(false);
  const [chaseRan,setChaseRan]=useState(null);
  const [clientHealthData,setClientHealthData]=useState([]);  // per-client health results
  const [clientHealthLoading,setClientHealthLoading]=useState(false);
  const [clientHealthRan,setClientHealthRan]=useState(null);
  const [clientHealthExpanded,setClientHealthExpanded]=useState({});  // {companyId: bool}
  const [m365,setM365]=useState(liveEmails.length>0);
  const [calConn,setCalConn]=useState(liveCalEvents.length>0);
  const [meetings,setMeetings]=useState(liveCalEvents);
  // Always sync meetings AND calConn when liveCalEvents updates (including on first load)
  // calConn can't be initialised as true at mount because liveCalEvents is always [] then
  useEffect(()=>{
    setMeetings(liveCalEvents);
    if(liveCalEvents.length>0) setCalConn(true);
  },[liveCalEvents]);

  const [aiText,setAiText]=useState("");
  const [aiLoad,setAiLoad]=useState(false);
  // Focus page accordion — which sections are open (default all open except upcoming-meetings on Prep)
  const [focusAccordion,setFocusAccordion]=useState({"client-health":true,"email-chase":true,aibrief:true,"pre-meeting-form":true,"meeting-context":true,mip:true,"upcoming-meetings":false});
  const toggleFocusSection=(key)=>setFocusAccordion(s=>({...s,[key]:!s[key]}));
  const [meetingBrief,setMeetingBrief]=useState(null);
  const [meetingBriefLoading,setMeetingBriefLoading]=useState(false);
  const [meetingBriefExpanded,setMeetingBriefExpanded]=useState(false);
  const [meetingBriefBlockId,setMeetingBriefBlockId]=useState(null);
  const [meetingBriefStatus,setMeetingBriefStatus]=useState("");
  const [meetingBriefEditing,setMeetingBriefEditing]=useState(false);
  const [meetingBriefOriginal,setMeetingBriefOriginal]=useState(null);
  const [meetingBriefSaving,setMeetingBriefSaving]=useState(false);
  const [meetingPageUrl,setMeetingPageUrl]=useState(null);
  const [meetingCommitting,setMeetingCommitting]=useState(false);
  const [prepResponse,setPrepResponse]=useState(null);
  const [prepResponseLoading,setPrepResponseLoading]=useState(false);
  const [prepLinkInfo,setPrepLinkInfo]=useState(null);
  const [sendingPrepLink,setSendingPrepLink]=useState(false);
  const [sentForms,setSentForms]=useState(new Set()); // companyIds sent this session
  const [filters,setFilters]=useState({blockNoise:true,allowVip:true,allowUrgent:true,allowClient:true});
  const [adding,setAdding]=useState(false);
  const [nb,setNb]=useState(()=>({title:"",layer:"O",start:localStorage.getItem("msp-os-work-start")||"09:00",dur:60}));
  const [cadence,setCadence]=useState(()=>{
    // If savedState has full items (new format), use them directly
    if(savedState?.cadenceState){
      const hasFullItems=Object.values(savedState.cadenceState).some(c=>c.items?.some(i=>i.title));
      if(hasFullItems){
        // Restore full structure, merging labels/freq/desc from CAD_DATA
        const merged={};
        Object.entries(CAD_DATA).forEach(([key,cat])=>{
          const saved=savedState.cadenceState[key];
          merged[key]={...cat,items:saved?.items||[]};
        });
        return merged;
      }
    }
    // First load or old format — return CAD_DATA defaults
    return CAD_DATA;
  });
  const [prepToast,setPrepToast]=useState(null);
  const [dismissed,setDismissed]=useState(new Set());

  // Meeting notes & todos
  const [fellowConn,setFellowConn]=useState(fellowConnected);
  const [selectedFellowMeeting,setSelectedFellowMeeting]=useState(null);
  // Ref shadow of selectedFellowMeeting — used to detect stale fetches when
  // user clicks a different meeting while one is still loading. Without this,
  // a late response could overwrite the new selection's notes.
  const selectedFellowMeetingRef = useRef(null);
  useEffect(()=>{ selectedFellowMeetingRef.current = selectedFellowMeeting; }, [selectedFellowMeeting]);
  const [importedFellowMeetings,setImportedFellowMeetings]=useState(new Set(savedState?.importedFellowMeetings||[]));

  // ── Fellow → Autotask ticket settings ──────────────────────────────────────
  // Persisted to blob state. Defaults match the agreed field values; user can
  // override via the "Fellow Ticket Settings" page in Meetings & Notes sidebar.
  const [fellowTicketSettings,setFellowTicketSettings]=useState(()=>({
    issueType:    savedState?.fellowTicketSettings?.issueType    ?? "17",   // CIO / Consulting / Training
    subIssueType: savedState?.fellowTicketSettings?.subIssueType ?? "240",  // IT Strategy Design + Planning - Delivery
    source:       savedState?.fellowTicketSettings?.source       ?? "4",    // Email
    queueId:      savedState?.fellowTicketSettings?.queueId      ?? "29683378", // Administration
    workTypeId:   savedState?.fellowTicketSettings?.workTypeId   ?? "",     // blank = omit billing code
  }));
  // Picklist values fetched once from the backend for populating settings dropdowns
  const [ticketPicklists,setTicketPicklists]=useState(null);
  // Status of the AT ticket creation fired on Commit: null | "creating" | {ticketNumber} | {error}
  const [fellowTicketStatus,setFellowTicketStatus]=useState(null);
  const [notesMode,setNotesMode]=useState(null); // null | 'manual' | 'fellow'
  const [syncLoading,setSyncLoading]=useState(false);
  const [syncResults,setSyncResults]=useState(null);
  // Work day settings
  const [workStart,setWorkStart]=useState(()=>localStorage.getItem("msp-os-work-start")||"08:00");
  const [workEnd,setWorkEnd]=useState(()=>localStorage.getItem("msp-os-work-end")||"18:00");
  // Plan My Day state
  const [dayPlan,setDayPlan]=useState([]);// Always starts empty — built via AI Plan My Day
  // Load day canvas from blob when plan tab opens (once per day)
  const canvasLoadedRef=useRef(false);
  // Helper: convert Focus blocks → canvas format
  const blocksToCanvas=(bks)=>bks.map(b=>({
    id:`cp-${b.id}`,
    title:b.title,
    layer:b.layer||"O",
    startTime:b.start||workStart,
    duration:b.dur||30,
    sourceType:"block",
    sourceId:b.id,
    color:"var(--text3)",
    fixed:false,
    done:b.done||false,
  }));

  useEffect(()=>{
    if(tab!=="plan"||!onLoadDayCanvas) return;
    const todayStr=(()=>{const n=new Date();const p=new Date(n.getTime()+8*60*60*1000);return `${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;})();

    if(!canvasLoadedRef.current){
      // First visit: load today's canvas from blob only — never mirror old Focus blocks
      canvasLoadedRef.current=true;
      onLoadDayCanvas(todayStr).then(data=>{
        const validBlocks=(data?.blocks||[]).filter(b=>b&&b.title&&b.startTime);
        // Only restore if the canvas was saved today — don't carry over from previous days
        if(validBlocks.length>0){
          // Convert savedDate to Perth date before comparing
          const _savedDatePerth=(()=>{
            if(!data?.savedDate) return data?.savedDate||"";
            const utc=new Date(data.savedDate+"T00:00:00Z");
            // savedDate is already a YYYY-MM-DD string (set by frontend), so just use it directly
            return data.savedDate.slice(0,10);
          })();
          if(_savedDatePerth===todayStr){
            setDayPlan(validBlocks);
            // If Focus blocks are empty (e.g. after a page refresh), rebuild them from canvas
            // This ensures Focus doesn't go blank after a refresh
            setBlocks(prev=>{
              if(prev.length>0) return prev; // already have blocks — don't overwrite
              const restored=validBlocks.map(b=>({
                id:b.sourceId||b.id||`r-${Math.random().toString(36).slice(2)}`,
                title:b.title,
                layer:b.layer||"O",
                start:b.startTime,
                dur:b.duration||30,
                done:b.done||false,
                prepFor:b.prepFor||null,
                todoId:b.todoId||null,
                type:b.type,
                sourceType:b.sourceType,
              }));
              if(restored.length>0) saveSnapshot({blocks:restored});
              return restored;
            });
          }
          // If saved on a different day, leave canvas empty — Plan My Day will populate it
        }
      }).catch(()=>{}); // Canvas empty on error — that's fine
    } else {
      // Returning to Plan tab — sync done state from Focus blocks onto canvas items only
      if(blocks.length>0&&dayPlan.length>0){
        setDayPlan(prev=>prev.map(p=>{
          const match=blocks.find(b=>b.id===p.sourceId||b.title===p.title);
          return match?{...p,done:match.done}:p;
        }));
      }
      // Do NOT rebuild canvas from Focus blocks — Plan My Day is the source of truth
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  // Auto-save day canvas to blob 2 seconds after any change
  const canvasSaveTimer=useRef(null);
  useEffect(()=>{
    if(!onSaveDayCanvas||dayPlan.length===0) return;
    clearTimeout(canvasSaveTimer.current);
    canvasSaveTimer.current=setTimeout(()=>{
      const todayStr=(()=>{const n=new Date();const p=new Date(n.getTime()+8*60*60*1000);return `${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;})();
      onSaveDayCanvas(todayStr,{blocks:dayPlan,savedDate:todayStr}).catch(()=>{});
    },2000);
    return()=>clearTimeout(canvasSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dayPlan]);
  const [priorityFlags,setPriorityFlags]=useState({}); // {itemId: reason} // [{id,title,layer,duration,startTime,type,sourceId,sourceType,fixed}]
  const [aiPlanning,setAiPlanning]=useState(false);
  const [planConfirmed,setPlanConfirmed]=useState(false);
  const [dayPlanDate,setDayPlanDate]=useState(()=>{const n=new Date();const p=new Date(n.getTime()+8*60*60*1000);return`${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;});






  const [fellowKey,setFellowKey]=useState("");
  const [notesMeeting,setNotesMeeting]=useState("");
  const [notesClient,setNotesClient]=useState(""); // resolved company name (not meeting title)
  const [notesText,setNotesText]=useState("");
  const [extracting,setExtracting]=useState(false);
  const [extracted,setExtracted]=useState([]); // [{title,priority,suggestedDate,layer,client,notes,accepted}]
  const [selectedNoteLines,setSelectedNoteLines]=useState(new Set()); // indices of selected raw note lines
  const [deletedNoteLines,setDeletedNoteLines]=useState(new Set()); // indices of lines deleted for privacy
  const [editedNoteLines,setEditedNoteLines]=useState({}); // {lineIndex: editedText}
  const [editingNoteLine,setEditingNoteLine]=useState(null); // index currently being edited
  const [assignToMeeting,setAssignToMeeting]=useState(""); // calendar meeting id to assign extracted todos to
  // Set true when user clicks Commit without picking a meeting from the dropdown.
  // Triggers red highlight on the Assign-to-meeting field + blocks the commit
  // until they pick one. Cleared when the user actually selects a meeting.
  const [meetingAssignError,setMeetingAssignError]=useState(false);
  const [todos,setTodos]=useState(savedState?.todos?.length>0?savedState.todos:[]);
  const [meetingRawNotes,setMeetingRawNotes]=useState(savedMeetingRawNotes||savedState?.meetingRawNotes||{});
  const [expandedTodo,setExpandedTodo]=useState(null);
  const [rescheduleModal,setRescheduleModal]=useState(null); // {id, title}
  const [rescheduleDate,setRescheduleDate]=useState("");
  const [addTodoModal,setAddTodoModal]=useState(false);
  const [cadDueDateModal,setCadDueDateModal]=useState(null); // {layer,id,title,dueDate}
  const [newTodo,setNewTodo]=useState({title:"",notes:"",dueDate:"",priority:"med",layer:"O",client:""});
  // Prep form editor modal — legacy per-company editor (still works as escape hatch)
  const [prepSendModal,setPrepSendModal]=useState(null);
  // { companyId, companyName, meetingDate, meetingTitle, questions:[...], email, newQLabels:{[section]:''}, newSecName:'' }
  const [prepSendModalSending,setPrepSendModalSending]=useState(false);
  // Pre-Meeting Form templates — list of named question sets; each customer can be assigned to one template
  const [prepFormTemplates,setPrepFormTemplates]=useState(savedState?.prepFormTemplates||[]);
  // Map of companyId → templateId — which template each managed customer uses
  const [companyPrepTemplates,setCompanyPrepTemplates]=useState(savedState?.companyPrepTemplates||{});
  // Template currently being edited in the modal
  const [prepTemplateModal,setPrepTemplateModal]=useState(null);
  // { templateId, name, description, questions:[...], newQLabels:{[section]:''}, newSecName:'', isNew?, dirty? }
  const [prepTemplateModalSaving,setPrepTemplateModalSaving]=useState(false);

  // Autotask
  const [atCreds,setAtCreds]=useState({username:"",apiKey:"",zone:""});
  const [atConnected,setAtConnected]=useState(true); // Credentials stored in backend env vars
  const [atConnecting,setAtConnecting]=useState(false);
  const [atModal,setAtModal]=useState(null);       // {email} — open ticket form
  const [atTodoModal,setAtTodoModal]=useState(null);// {todo} — open ticket form from To-Do
  const reconcileTicketDoneRef=useRef(null); // Missing Time: called with {ticketId,ticketNumber} after a reconcile-sourced ticket is created
  // Per-item expand state for the Focus todo-batch checklist. Top-level (not in
  // the batch IIFE) because hooks can't live inside that render callback.
  const [batchItemExpanded,setBatchItemExpanded]=useState({});
  // Lazy-loaded per-company AT data for ticket modals (refreshed when company changes)
  const [atModalContacts,setAtModalContacts]=useState([]);
  const [atModalContracts,setAtModalContracts]=useState([]);
  const [atModalAssets,setAtModalAssets]=useState([]);
  const [atModalLoading,setAtModalLoading]=useState(false);
  const [atTicket,setAtTicket]=useState({});        // form field values
  const [atLookup,setAtLookup]=useState(null);      // {contact, company} from email lookup
  const [atLookingUp,setAtLookingUp]=useState(false);
  const [atSubmitting,setAtSubmitting]=useState(false);
  const [atSuccess,setAtSuccess]=useState(null);    // {ticketNum, emailId}
  const [atCustomFields,setAtCustomFields]=useState([{key:"",value:""}]);
  // The signed-in tech's usable Autotask roles (GET /api/timelog/my-roles) for
  // the To-Do ticket modal's time-entry Role picker. Loaded once, on first open.
  const [atMyRoles,setAtMyRoles]=useState(null);
  const [ticketedEmails,setTicketedEmails]=useState(()=>new Set(savedState?.ticketedEmails||[]));
  const [todoedEmails,setTodoedEmails]=useState(()=>new Set(savedState?.todoedEmails||[]));
  // Dismissed plan items — cleared each new day
  const _planDismissDate=savedState?.planDismissDate||"";
  const _todayD=todayISO();
  const [dismissedPlanItems,setDismissedPlanItems]=useState(()=>
    _planDismissDate===_todayD?new Set(savedState?.dismissedPlanItems||[]):new Set()
  );

  // ─────────── QUOTING STATE ───────────
  // Backlog: things that need a quote built (intake from emails / manual)
  const [quoteBacklog,setQuoteBacklog]=useState(savedState?.quoteBacklog||[]);
  // Quotes: actual quotes being built / tracked through pipeline
  const [quotes,setQuotes]=useState(savedState?.quotes||[]);
  // Reusable line-item bundles
  const [quoteTemplates,setQuoteTemplates]=useState(savedState?.quoteTemplates||[]);
  // UI state for quoting tab
  const [editingQuoteId,setEditingQuoteId]=useState(null); // when set, builder modal is open
  // Builder's current focus — which Option (by id) or "common" zone is active.
  // AI suggestions and template-applies land here. Set automatically by clicking
  // a zone or its tab.
  // Where the next AI suggestion / template / manual add lands.
  // "common" | {sectionId, optionId} | {sectionId, optionId:null} | null (auto-resolve)
  const [focusedLocation,setFocusedLocation]=useState(null);
  // Index of the section currently being dragged (HTML5 DnD reorder).
  const dragSectionIndexRef = useRef(null);
  // Pending freight recalcs, keyed by quote id. useRef so the timers survive
  // re-renders without causing them.
  const freightSyncTimerRef = useRef(new Map());
  // Lightbox state — image URL to show in fullscreen overlay
  const [lightboxImage,setLightboxImage]=useState(null);
  const [quoteFilter,setQuoteFilter]=useState("all"); // all|backlog|draft|sent|won|lost
  const [quoteNumberSearch,setQuoteNumberSearch]=useState("");
  // Board (kanban) or List — remembered per browser, like the panel widths.
  const [quoteView,setQuoteViewState]=useState(()=>localStorage.getItem("msp-os-quote-view")||"board");
  const setQuoteView=(v)=>{setQuoteViewState(v);localStorage.setItem("msp-os-quote-view",v);};
  const [quoteToast,setQuoteToast]=useState(null); // {kind:"ok"|"err",msg:string}
  // The send modal: the id of the quote about to go out, or null. `busy` while
  // the request is in flight so a double-click cannot send twice.
  const [quoteSendModal,setQuoteSendModal]=useState(null); // {quoteId, busy}
  // The Push-to-Autotask review modal: {quoteId, preview|null, busy, result|null}
  const [quotePushModal,setQuotePushModal]=useState(null);
  const [quoteTemplateModal,setQuoteTemplateModal]=useState(null); // {quoteId, choices} — the three-way "Save as template"
  const [quoteStartModal,setQuoteStartModal]=useState(null);       // {backlogItem} — blank · template · copy

  // ─────────── QUOTING CONFIGURATION & SUPPLIERS ───────────
  // Global default markup. Per-supplier priority brands live on each supplier.
  const [defaultMarkupPct,setDefaultMarkupPct]=useState(savedState?.defaultMarkupPct??25);
  /* THE DEFAULT terms text, editable in Settings so a payment term does not
     need a deploy. Each quote keeps its OWN COPY, seeded from this at
     creation — see quoteTerms.js for why that is not the obvious build. */
  const [quoteTermsDefault,setQuoteTermsDefault]=useState(savedState?.quoteTermsDefault ?? DEFAULT_QUOTE_TERMS_TEXT);

  // Freight defaults, shaped from Leader's published rate card. Every supplier
  // inherits these on first use and can be tuned per-supplier in Settings.
  // Read through supFreight() rather than directly — a supplier saved before
  // freight existed has no `freight` key at all.
  const DEFAULT_FREIGHT_CONFIG = {
    enabled: true,
    metroThresholdExGst: 1000,   // orders at/above this ship free to metro
    metroFlatRateExGst: 15,      // charged below the threshold
    interstateMinExGst: 20,      // reserved: v1 always assumes metro road freight
    // Reserved for air freight opt-in; v1 computes road only.
    airFreightTiers: [
      { maxKg: 1,  charge: 12 },
      { maxKg: 3,  charge: 15 },
      { maxKg: 5,  charge: 17.5 },
      { maxKg: 10, charge: 40 },
      { maxKg: 20, charge: 80 },
    ],
    // Bulky/dangerous goods that are charged freight regardless of order value.
    excludeKeywords: ["printer", "ups", "cabinet", "rack", "battery backup"],
    // Keyword -> kg. The backend matches longest-pattern-first, so a specific
    // "switch 48-port" beats a generic "switch".
    weightLookup: [
      { pattern: "laptop", weightKg: 2.5 },
      { pattern: "notebook", weightKg: 2.5 },
      { pattern: "mini-pc", weightKg: 3.0 },
      { pattern: "mini pc", weightKg: 3.0 },
      { pattern: "sff", weightKg: 5.0 },
      { pattern: "desktop", weightKg: 8.0 },
      { pattern: "workstation", weightKg: 12.0 },
      { pattern: "monitor 24", weightKg: 5.0 },
      { pattern: "monitor 27", weightKg: 7.0 },
      { pattern: "monitor 32", weightKg: 12.0 },
      { pattern: "switch 8-port", weightKg: 2.0 },
      { pattern: "switch 24-port", weightKg: 4.0 },
      { pattern: "switch 48-port", weightKg: 7.0 },
      { pattern: "access point", weightKg: 1.0 },
      { pattern: "camera", weightKg: 1.5 },
      { pattern: "doorbell", weightKg: 1.0 },
      { pattern: "controller", weightKg: 5.0 },
      { pattern: "nvr", weightKg: 5.0 },
      { pattern: "cable", weightKg: 0.3 },
      { pattern: "dongle", weightKg: 0.1 },
    ],
    defaultWeightKg: 2.0,
  };

  // Default suppliers shipped with the app — used when no saved state exists.
  // Each supplier is independently configurable; AI quote-suggest queries all
  // enabled suppliers and merges results.
  const DEFAULT_SUPPLIERS = [
    {
      id: "leader",
      name: "Leader Computers",
      parserType: "leader",
      enabled: true,
      priorityBrands: ["HP","Ubiquiti","Yealink"],
      uploadFormat: "csv", // csv | zip
      catalogMeta: null,   // {syncedAt, totalItems, priorityItems, perthStock?, fileName}
      // hasAutoRefreshSource: backend has a URL configured (in Key Vault) for this
      // supplier so we can pull a fresh CSV without a manual upload. Currently only
      // Leader has this; Dicker doesn't have a feed URL configured yet.
      hasAutoRefreshSource: true,
    },
    {
      id: "dicker",
      name: "Dicker Data",
      parserType: "dicker",
      enabled: true,
      priorityBrands: ["HP","HPE"],
      uploadFormat: "zip",
      catalogMeta: null,
      hasAutoRefreshSource: false,
    },
  ];

  // Migrate any legacy savedState shape into the suppliers array
  const migrateSuppliersFromLegacy = () => {
    // If savedState already has a suppliers array, use it — but always re-apply
    // hasAutoRefreshSource from defaults so the flag follows the latest deploy
    // rather than getting frozen at whatever was in saved state.
    if (Array.isArray(savedState?.suppliers) && savedState.suppliers.length > 0) {
      return savedState.suppliers.map(s => {
        const dflt = DEFAULT_SUPPLIERS.find(d => d.id === s.id);
        return dflt ? {...s, hasAutoRefreshSource: dflt.hasAutoRefreshSource} : s;
      });
    }
    // Otherwise build from legacy keys: priorityBrands (flat array) + leaderCatalogMeta
    const legacyBrands = Array.isArray(savedState?.priorityBrands) && savedState.priorityBrands.length > 0
      ? savedState.priorityBrands
      : ["HP","Ubiquiti","Yealink"];
    return DEFAULT_SUPPLIERS.map(s => ({
      ...s,
      // Use legacy brands for Leader (preserves prior behaviour); keep Dicker's defaults
      priorityBrands: s.id === "leader" ? legacyBrands : s.priorityBrands,
      catalogMeta: s.id === "leader" ? (savedState?.leaderCatalogMeta || null) : null,
    }));
  };

  const [suppliers,setSuppliers]=useState(migrateSuppliersFromLegacy);

  // Per-supplier upload UI state — keyed by supplier id
  // { [supplierId]: { uploading: bool, modalOpen: bool } }
  const [supplierUploadState,setSupplierUploadState]=useState({});

  // DLP (Data Loss Prevention) — settings, custom rules, and live preview
  // Rules and mode persist server-side via /api/dlp/rules. Loaded on mount.
  const [dlpMode,setDlpMode]=useState("tokenised");
  const [dlpRules,setDlpRules]=useState([]);
  const [dlpLoaded,setDlpLoaded]=useState(false);
  const [dlpSaving,setDlpSaving]=useState(false);
  const [dlpSavedAt,setDlpSavedAt]=useState(null);
  const [dlpPreviewIn,setDlpPreviewIn]=useState("");
  const [dlpPreviewOut,setDlpPreviewOut]=useState("");
  const [dlpPreviewStats,setDlpPreviewStats]=useState(null);
  const [dlpPreviewing,setDlpPreviewing]=useState(false);
  useEffect(()=>{
    if(!onDlpFetch||dlpLoaded)return;
    (async()=>{
      try{
        const r=await onDlpFetch();
        if(r){
          if(typeof r.mode==="string")setDlpMode(r.mode);
          if(Array.isArray(r.rules))setDlpRules(r.rules);
        }
      }catch(e){console.warn("DLP rules load failed",e?.message);}
      finally{setDlpLoaded(true);}
    })();
  },[onDlpFetch,dlpLoaded]);

  // ─────────── OPPORTUNITIES STATE ───────────
  // An opportunity represents an inbound enquiry that may eventually become a quote.
  // Shape: { id, createdAt, updatedAt, title, pipelineStatus, closedAt,
  //          companyId, companyName, estValue, notes,
  //          emails:[{id,from,fromEmail,subj,body,time,addedAt}] }
  //
  // ⚠️ DEAD FIELD — records created before the pipeline board also carry
  // `status: "open"`. It is inert: nothing reads it, new records do not set it,
  // and it is NOT kept in sync with pipelineStatus, so anything that read it
  // would see "open" on a closed deal. Superseded by pipelineStatus, which must
  // only ever be read through pipelineStatusOf()/isOpen() from
  // components/opportunities/opportunityPipeline.js — those handle the legacy
  // records that have no pipelineStatus at all. Left in place deliberately:
  // migrating it would rewrite every blob to delete a field nobody reads.
  const [opportunities,setOpportunities]=useState(savedState?.opportunities||[]);
  // ── Proposals tab state ──
  // Full shape documented in components/proposals/ProposalsTab.jsx. Persisted
  // in the state blob like quotes — "proposals" is in the backend allowlist
  // (src/routes/state.js). Without that entry every save silently drops them.
  const [proposals,setProposals]=useState(savedState?.proposals||[]);
  // Templates seed themselves on first load; the Bronze seed is taken from the
  // component's own defaults, so nothing changes until Craig edits one.
  // mergeTemplates, not a bare fallback: the seed only fires on an empty array,
  // so a tier that ships later (Gold) would stay a "coming-soon" placeholder in
  // every existing blob. Merging upgrades shipped placeholders while leaving
  // the user's edits to active templates untouched.
  const [proposalTemplates,setProposalTemplates]=useState(
    ()=>mergeTemplates(savedState?.proposalTemplates, DEFAULT_PROPOSAL_TEMPLATES()));
  // ── Contracts ──
  // The DocuSign template catalogue, and one record per envelope sent. Both
  // keys are in the backend allowlist (src/routes/state.js) — without that they
  // save and are silently dropped, exactly as proposalTemplates would be.
  // mergeContractTemplates for the same reason mergeTemplates exists: the seed
  // only fires on an empty array, so a template added to the seed later would
  // never reach a blob that already has some.
  const [contractTemplates,setContractTemplates]=useState(
    ()=>mergeContractTemplates(savedState?.contractTemplates));
  const [contractInstances,setContractInstances]=useState(savedState?.contractInstances||[]);
  /* Appendix 3's markup lives in the BACKEND seed, so unlike the field-schema
     backfill this one cannot be done synchronously in mergeContractTemplates —
     it needs the seed fetched. Done here, once, at the single place templates
     are loaded, so it reaches BOTH the Contract Templates tab and the send
     panel in Proposals; a user who never opens the templates tab would
     otherwise keep sending contracts with the appendix silently missing.
     Additive and idempotent — a template that already has the block is
     untouched, so this settles to a no-op after the first run. */
  useEffect(()=>{
    let cancelled=false;
    if(!contractTemplates.some(t=>String(t?.content||"").trim()
        && !String(t.content).includes(APPENDIX3_MARKER))) return undefined;
    getSeedContractContent()
      .then((seeds)=>{
        if(cancelled||!seeds?.length) return;
        setContractTemplates((prev)=>{
          const next=prev.map((t)=>backfillAppendix3(t,seeds));
          if(next.every((t,i)=>t===prev[i])) return prev;   // no write if nothing changed
          saveSnapshot({contractTemplates:next});
          return next;
        });
      })
      .catch(()=>{});      // a seed fetch failure leaves the templates as they are
    return ()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  // ── Budgeting tab state ──
  const [budSelCompany,setBudSelCompany]=useState(null);
  const [budSelFy,setBudSelFy]=useState("");
  const [budFyList,setBudFyList]=useState([]);
  const [budFyLoading,setBudFyLoading]=useState(false);
  const [budData,setBudData]=useState(null);
  const [budLoading,setBudLoading]=useState(false);
  const [budSaving,setBudSaving]=useState(false);
  const [budDirty,setBudDirty]=useState(false);
  const [budToast,setBudToast]=useState(null);
  const [budNewOpen,setBudNewOpen]=useState(false);
  const [budNewFy,setBudNewFy]=useState("");
  const [budRollOpen,setBudRollOpen]=useState(false);
  const [budRollFrom,setBudRollFrom]=useState("");
  const [budRollTo,setBudRollTo]=useState("");
  const [selectedOpportunityId,setSelectedOpportunityId]=useState(null);

  // AI Assistant chat modal state — for iterative quote building
  const [aiChatOpen,setAiChatOpen]=useState(false);
  const [aiChatQuoteId,setAiChatQuoteId]=useState(null);
  // messages: [{role: "user"|"assistant", content: string, suggestions?: [items], ts: number}]
  const [aiChatMessages,setAiChatMessages]=useState([]);
  const [aiChatInput,setAiChatInput]=useState("");
  const [aiChatLoading,setAiChatLoading]=useState(false);

  // Plan item detail modal
  const [planItemModal,setPlanItemModal]=useState(null);
  const [planSectionExpanded,setPlanSectionExpanded]=useState({
    overdue:true,
    cadence:true,
    meetings:true,
    emails:true,
    carryForward:false,
    upcoming:false,
    allTodos:false,
  });
  const [deletedEmails,setDeletedEmails]=useState(new Set());

  // Inbox email detail modal — opens when user clicks an email row
  const [emailDetailModal,setEmailDetailModal]=useState(null); // {email}
  const [emailBodyLoading,setEmailBodyLoading]=useState(false);
  const [emailBodyContent,setEmailBodyContent]=useState(null); // {html, contentType, fetched:bool}
  const [emailBodyError,setEmailBodyError]=useState(null);

  // Inbox reply modal state — plain reply without creating a ticket
  const [replyModal,setReplyModal]=useState(null); // {email}
  const [replyBody,setReplyBody]=useState("");
  const [replySending,setReplySending]=useState(false);
  // Ticket modal extension: reply text sent as ticket public note + email reply on submit
  const [ticketReplyBody,setTicketReplyBody]=useState("");
  const [ticketReplySend,setTicketReplySend]=useState(true);

  // Drag state
  const dragIndex=useRef(null);
  const dragOverIndex=useRef(null);

  const timerRef=useRef(null);
  const timerStashRef=useRef({}); // blockId → {secs, total} for paused timers
  const focusSeenEmailIdsRef=useRef(new Set()); // tracks email IDs known at focus-start
  const sel=blocks.find(b=>b.id===selId);

  // Derive the client name from a block title like "Weekly Ticket Review — Cottesloe Beach Hotel"
  const getWeeklyReportCompany=(title)=>{
    if(!title||!title.includes("Weekly Ticket Review — ")) return null;
    return title.replace("Weekly Ticket Review — ","").trim();
  };
  const openWeeklyReport=async(block)=>{
    const clientName=getWeeklyReportCompany(block.title);
    if(!clientName||!onGetReportToken) return;
    const company=atCompanies.find(c=>c.companyName===clientName);
    if(!company){ alert(`Company "${clientName}" not found in Autotask data.\nRun a sync first.`); return; }
    const token=await onGetReportToken();
    if(!token){ alert("Could not get auth token"); return; }
    const date=todayISO();
    const apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    const url=`/weekly-report.html?companyId=${company.id}&date=${date}&token=${encodeURIComponent(token)}&api=${encodeURIComponent(apiBase)}`;
    window.open(url,"_blank");
  };
  const lc=sel?.layer||"O";
  const apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

  // Timer stash — when selection changes while a timer is active:
  //   • stash remaining secs for the block we're leaving
  //   • pause the timer
  //   • if returning to a previously stashed block, restore its time (paused)
  //   • if arriving at a fresh block, reset to 0 (user must press Start)
  useEffect(()=>{
    const prevId = prevSelIdRef.current;
    const newId  = sel?.id;
    if(prevId===newId){ prevSelIdRef.current=newId; return; }

    // Stash current timer state for the block we're leaving
    if(prevId!=null && focusing){
      timerStashRef.current[prevId]={secs, total};
      setRunning(false); // pause — interval clears via running useEffect
    }

    // Restore stashed state for the block we're arriving at
    if(newId!=null && timerStashRef.current[newId]){
      const stashed=timerStashRef.current[newId];
      setSecs(stashed.secs);
      setTotal(stashed.total);
      // Stay focused but paused — user sees "Resume" not "Start"
    } else if(newId!=null && prevId!=null && focusing){
      // Fresh block while in focus mode — reset timer, user presses Start
      setSecs(sel?.dur ? sel.dur*60 : 0);
      setTotal(sel?.dur ? sel.dur*60 : 0);
      setFocusing(false); // drop out of focus mode for the new block
    }

    prevSelIdRef.current=newId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sel?.id]);

  // Clear meeting brief when block selection changes — but only if we haven't loaded it for this block
  useEffect(()=>{
    if(selId!==meetingBriefBlockId){
      setMeetingBrief(null);setMeetingBriefExpanded(false);setMeetingBriefBlockId(null);
      setMeetingBriefEditing(false);setMeetingBriefOriginal(null);
      // Always clear meetingPageUrl when switching blocks — prevents stale URL from previous block showing
      setMeetingPageUrl(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selId]);

  // Timer
  useEffect(()=>{
    if(running){timerRef.current=setInterval(()=>setSecs(s=>{if(s<=1){clearInterval(timerRef.current);setRunning(false);setFocusing(false);return 0;}return s-1;}),1000);}
    else clearInterval(timerRef.current);
    return()=>clearInterval(timerRef.current);
  },[running]);

  // When focus starts, snapshot which email IDs already exist so we only alert on new arrivals
  useEffect(()=>{
    if(focusing){
      focusSeenEmailIdsRef.current=new Set(liveEmails.map(e=>String(e.id)));
      setEmailAlert(null); // clear any stale alert from a previous session
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[focusing]);

  // Poll for fresh emails every 60s while focusing (only if refresh callback available)
  useEffect(()=>{
    if(!focusing||!onRefreshEmails)return;
    const iv=setInterval(()=>{try{onRefreshEmails();}catch{}},60000);
    return()=>clearInterval(iv);
  },[focusing,onRefreshEmails]);

  // Detect new VIP emails that arrive during a focus session
  useEffect(()=>{
    if(!focusing||emailAlert)return; // don't stack alerts
    const newVip=liveEmails.find(e=>{
      const eid=String(e.id);
      if(focusSeenEmailIdsRef.current.has(eid))return false; // already knew about this one
      // Match if the email is already flagged vip, OR sender name matches the VIP list
      return e.vip||vips.some(v=>(e.from||"").toLowerCase().includes(v.toLowerCase()));
    });
    if(newVip){
      focusSeenEmailIdsRef.current.add(String(newVip.id)); // don't alert on it again
      setEmailAlert(newVip);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[liveEmails]);

  // Sync today's todos into blocks
  useEffect(()=>{
    const today=todayISO();
    const todayTodos=todos.filter(t=>!t.done&&t.dueDate===today);
    setBlocks(prev=>{
      // Remove stale todo blocks whose todo no longer applies today
      const cleaned=prev.filter(b=>!b.todoId||todayTodos.some(t=>t.id===b.todoId));
      // Add missing todo blocks
      const existingTodoIds=new Set(cleaned.filter(b=>b.todoId).map(b=>b.todoId));
      const toAdd=todayTodos.filter(t=>!existingTodoIds.has(t.id)).map(t=>({
        id:`todo-block-${t.id}`,
        title:t.title,
        layer:t.layer||"T",
        start:"09:00",
        dur:60,
        done:false,
        prepFor:null,
        todoId:t.id,
      }));
      return toAdd.length>0?[...cleaned,...toAdd]:cleaned;
    });
  },[todos]);

  // Fellow — pull meetings from backend
  const [fellowMeetings,setFellowMeetings]=useState([]);
  const [fellowMeetingsLoading,setFellowMeetingsLoading]=useState(false);
  // True while a single Fellow meeting's notes are being fetched/auto-healed.
  // The backend may take several seconds when the cache is being healed (refetch
  // from Fellow + AI sections extraction). Without an explicit loading state
  // the panel showed an ambiguous "Loading notes..." placeholder even after
  // clicks failed, and users clicked again before the first load completed.
  const [fellowNotesLoading,setFellowNotesLoading]=useState(false);
  // Prep Notes tab
  const [selectedPrepBlock,setSelectedPrepBlock]=useState(null);
  const [prepNotesResponse,setPrepNotesResponse]=useState(null);
  const [prepNotesLinkInfo,setPrepNotesLinkInfo]=useState(null);
  const [prepNotesLoading,setPrepNotesLoading]=useState(false);
  const [prepNotesBrief,setPrepNotesBrief]=useState(null);
  const [prepNotesAccordion,setPrepNotesAccordion]=useState({past:false,upcoming:true});
  // Meeting Notes tab
  const [selectedMeetingBlock,setSelectedMeetingBlock]=useState(null);
  const [meetingNotesAccordion,setMeetingNotesAccordion]=useState({past:false,upcoming:true});
  const [sessionExpired,setSessionExpired]=useState(false); // kept for safety but no longer used by apiClient
  const [meetingNotesBrief,setMeetingNotesBrief]=useState(null);
  const [meetingNotesBriefLoading,setMeetingNotesBriefLoading]=useState(false);
  const [meetingNotesGenerating,setMeetingNotesGenerating]=useState(false);
  const [meetingNotesCommitting,setMeetingNotesCommitting]=useState(false);
  const [meetingNotesLinkInfo,setMeetingNotesLinkInfo]=useState(null);
  const [meetingNotesPageUrl,setMeetingNotesPageUrl]=useState(null);
  const [meetingNotesPrepResponse,setMeetingNotesPrepResponse]=useState(null);
  // Post Meeting Reporting state
  const [postReportMeeting,setPostReportMeeting]=useState(null);
  const [postReportBrief,setPostReportBrief]=useState(null);
  const [postReportPrep,setPostReportPrep]=useState(null);
  const [postReportLoading,setPostReportLoading]=useState(false);
  const [postReportGenerating,setPostReportGenerating]=useState(false);
  // Persisted: map of meetingId → {token, url, generatedAt} — survives page refresh
  const [meetingReportUrls,setMeetingReportUrls]=useState(()=>savedState?.meetingReportUrls||{});

  // Helper: save state snapshot via ref to avoid stale closure / TDZ issues
  const stateRef = useRef({});
  stateRef.current = { blocks, todos, cadence, vips, managedCompanyIds, clientDomainOverrides, clientEmailOverrides, importedFellowMeetings, clientDomains, ticketedEmails, todoedEmails, dismissedPlanItems, quoteBacklog, quotes, quoteTemplates, defaultMarkupPct, quoteTermsDefault, suppliers, opportunities, proposals, proposalTemplates, contractTemplates, contractInstances, meetingRawNotes, meetingReportUrls, prepFormTemplates, companyPrepTemplates, fellowTicketSettings, onSaveState };
  // Keep a stable ref to onGetAuthToken so the effect below doesn't need it in deps
  const onGetAuthTokenRef = useRef(onGetAuthToken);
  useEffect(()=>{ onGetAuthTokenRef.current = onGetAuthToken; },[onGetAuthToken]);

  // Refresh auth token in window.__mspToken.
  // Runs: on mount, on every block selection change, and every 10 minutes
  // so raw fetch() calls always have a valid (non-expired) token.
  const refreshMspToken = useCallback(()=>{
    if(typeof onGetAuthTokenRef.current==='function'){
      onGetAuthTokenRef.current().then(t=>{ if(t) window.__mspToken=t; }).catch(()=>{});
    }
  },[]);
  useEffect(()=>{
    refreshMspToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sel?.id]);
  useEffect(()=>{
    // Proactive refresh every 10 minutes — MSAL tokens expire after 1 hour
    // This prevents the block_iframe_reload error cascade from expired tokens
    const interval = setInterval(refreshMspToken, 10 * 60 * 1000);
    refreshMspToken(); // also refresh on mount
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Track whether savedState has been loaded — prevents saveSnapshot overwriting blob
  // with empty defaults before the initial state load completes
  const stateLoadedRef = useRef(!!savedState?.lastSaved);
  useEffect(()=>{
    if(savedState?.lastSaved) stateLoadedRef.current = true;
  },[savedState]);

  const saveSnapshot = (overrides={}) => {
    const s = stateRef.current;
    if(!s.onSaveState) return;
    // CRITICAL: do not save until we have confirmed savedState was loaded from blob.
    // Saving before load would overwrite real data with empty defaults.
    if(!stateLoadedRef.current) {
      console.warn("saveSnapshot blocked — state not yet loaded from blob");
      return;
    }
    const cad=overrides.cadence??s.cadence;
    // Save full item list — items are removed on completion, not just marked done
    const cadSave={};
    Object.entries(cad).forEach(([k,v])=>{cadSave[k]={items:v.items};});
    s.onSaveState({
      blocks:                 overrides.blocks                 ?? s.blocks,
      todos:                  overrides.todos                  ?? s.todos,
      cadenceState:           cadSave,
      vips:                   overrides.vips                   ?? s.vips,
      managedCompanyIds:      overrides.managedCompanyIds      ?? [...s.managedCompanyIds],
      clientDomainOverrides:  overrides.clientDomainOverrides  ?? s.clientDomainOverrides ?? {},
      clientEmailOverrides:   overrides.clientEmailOverrides   ?? s.clientEmailOverrides  ?? {},
      importedFellowMeetings: overrides.importedFellowMeetings ?? [...s.importedFellowMeetings],
      ticketedEmails: overrides.ticketedEmails ?? [...s.ticketedEmails],
      todoedEmails:    overrides.todoedEmails    ?? [...s.todoedEmails],
      dismissedPlanItems: overrides.dismissedPlanItems ?? [...s.dismissedPlanItems],
      planDismissDate: overrides.planDismissDate ?? todayISO(),
      quoteBacklog:    overrides.quoteBacklog    ?? s.quoteBacklog,
      quotes:          overrides.quotes          ?? s.quotes,
      quoteTemplates:  overrides.quoteTemplates  ?? s.quoteTemplates,
      defaultMarkupPct:  overrides.defaultMarkupPct  ?? s.defaultMarkupPct,
      quoteTermsDefault: overrides.quoteTermsDefault ?? s.quoteTermsDefault,
      suppliers:         overrides.suppliers         ?? s.suppliers,
      opportunities:     overrides.opportunities     ?? s.opportunities,
      proposals:         overrides.proposals         ?? s.proposals         ?? [],
      proposalTemplates: overrides.proposalTemplates ?? s.proposalTemplates ?? [],
      contractTemplates: overrides.contractTemplates ?? s.contractTemplates ?? [],
      contractInstances: overrides.contractInstances ?? s.contractInstances ?? [],
      meetingRawNotes:   overrides.meetingRawNotes   ?? s.meetingRawNotes ?? {},
      meetingReportUrls: overrides.meetingReportUrls ?? s.meetingReportUrls ?? {},
      prepFormTemplates:    overrides.prepFormTemplates    ?? s.prepFormTemplates    ?? [],
      companyPrepTemplates: overrides.companyPrepTemplates ?? s.companyPrepTemplates ?? {},
      fellowTicketSettings: overrides.fellowTicketSettings ?? s.fellowTicketSettings ?? {},
    });
  };

  // Priority flags — manually toggled by clicking items in Plan My Day inbox
  const detectPrioritiesRef = useRef(null);

  // Auto-inject today's calendar meetings into the day canvas when plan tab opens
  // Also heals existing blocks that have stale/wrong client names (e.g. display names instead of company names)
  const meetingsInjectedRef = useRef(false);
  useEffect(()=>{
    if(tab!=="plan") return;
    const todayStr=todayISO();
    const todayMtgs=meetings.filter(m=>{
      const d=m.localDateStr||String(m.date||"").slice(0,10);
      return d===todayStr && m.client && m.client!=="Internal";
    });
    if(!todayMtgs.length) return;

    setDayPlan(prev=>{
      let updated = prev;

      // Heal existing blocks — if a block's title doesn't match the live client name,
      // replace it with the correctly resolved one. Match by startTime + base title.
      const healed = updated.map(b=>{
        if(b.layer!=="C"||!b.startTime) return b;
        const baseTitle=(b.title||"").split("—")[0].split("-")[0].trim().toLowerCase();
        const liveMatch=todayMtgs.find(m=>{
          const mBase=(m.title||"").toLowerCase();
          return m.startTime===b.startTime && (
            mBase===baseTitle ||
            mBase.includes(baseTitle) ||
            baseTitle.includes(mBase)
          );
        }) || todayMtgs.find(m=>{
          // Also match prep blocks: 30min before meeting
          const mBase=(m.title||"").toLowerCase();
          return Math.abs(timeToMins(m.startTime)-timeToMins(b.startTime)-30)<=15 && (
            mBase===baseTitle || baseTitle.includes("prep") || b.type==="meeting-prep"
          );
        });
        if(!liveMatch) return b;
        const correctTitle = b.type==="meeting-prep"
          ? `Prep: ${liveMatch.title} — ${liveMatch.client}`
          : `${liveMatch.title} — ${liveMatch.client}`;
        if(b.title===correctTitle) return b; // already correct
        return {...b, title:correctTitle};
      });

      // Add any missing blocks
      if(!meetingsInjectedRef.current) {
        meetingsInjectedRef.current=true;
        const existingIds=new Set(healed.map(b=>b.sourceId));
        const toAdd=todayMtgs
          .filter(m=>!existingIds.has(`cal-${m.id}`))
          .map(m=>({
            id:`dp-cal-${m.id}`,
            title:m.client?`${m.title} — ${m.client}`:m.title,
            layer:"C",
            duration:m.duration||60,
            startTime:m.startTime||"09:00",
            type:"meeting",
            sourceId:`cal-${m.id}`,
            sourceType:"meeting",
            fixed:true,
            color:"var(--cal)",
            reason:"Today's calendar meeting",
            _iCalUID:m.iCalUID||m.id||null,
            _meetingDate:m.localDateStr||String(m.date||"").slice(0,10)||null,
          }));
        if(toAdd.length) {
          return [...healed,...toAdd].sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime));
        }
      }

      // Check if anything actually changed before triggering a re-render
      const changed=healed.some((b,i)=>b.title!==prev[i]?.title);
      return changed ? healed : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab,meetings]);

  // ── Pre-Meeting Form Template helpers ──────────────────────────────────────
  // Push a template's questions to all customers assigned to that template.
  // Used when a template's questions change so every assigned customer's stored
  // questions stay in sync. Best-effort; failures are alerted but don't block.
  const propagateTemplateToAssignedCompanies = async (templateId, questions) => {
    const assignedCompanyIds = Object.entries(companyPrepTemplates||{})
      .filter(([,tid])=>tid===templateId)
      .map(([cid])=>cid);
    if(!assignedCompanyIds.length) return {ok:0, fail:0};
    const _apiBase = window.location.hostname==="localhost"
      ? "http://localhost:3001"
      : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    let ok=0, fail=0;
    for(const cid of assignedCompanyIds){
      try{
        const r = await fetch(`${_apiBase}/api/meeting-prep/${cid}/questions`, {
          method:"PUT",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${window.__mspToken||""}`},
          body: JSON.stringify({questions})
        });
        if(r.ok) ok++; else fail++;
      } catch { fail++; }
    }
    return {ok, fail};
  };

  // Assign a customer to a template (or unassign if templateId is null).
  // Pushes the template's questions to the customer's backend store, or DELETEs
  // the customer's custom questions to fall back to backend defaults.
  const assignCompanyToTemplate = async (companyId, templateId) => {
    const cid = String(companyId);
    const _apiBase = window.location.hostname==="localhost"
      ? "http://localhost:3001"
      : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    try {
      if(templateId){
        const tpl = (prepFormTemplates||[]).find(t=>t.id===templateId);
        if(!tpl) throw new Error("Template not found");
        const r = await fetch(`${_apiBase}/api/meeting-prep/${cid}/questions`, {
          method:"PUT",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${window.__mspToken||""}`},
          body: JSON.stringify({questions: tpl.questions||[]})
        });
        if(!r.ok) throw new Error(`HTTP ${r.status}`);
      } else {
        // Unassign — delete custom questions so backend falls back to DEFAULT_QUESTIONS
        await fetch(`${_apiBase}/api/meeting-prep/${cid}/questions`, {
          method:"DELETE",
          headers:{Authorization:`Bearer ${window.__mspToken||""}`}
        });
      }
      setCompanyPrepTemplates(prev=>{
        const next = {...prev};
        if(templateId) next[cid] = templateId; else delete next[cid];
        saveSnapshot({companyPrepTemplates: next});
        return next;
      });
      return true;
    } catch(e){
      alert("Failed to update template assignment: "+e.message);
      return false;
    }
  };

  // Fetch the backend DEFAULT_QUESTIONS by hitting the questions endpoint for any
  // company id that we know has no custom questions — backend falls back to defaults.
  // Used to seed a "New from Defaults" template.
  const fetchDefaultPrepQuestions = async () => {
    const _apiBase = window.location.hostname==="localhost"
      ? "http://localhost:3001"
      : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    // Use a sentinel id that almost certainly has no stored questions
    const r = await fetch(`${_apiBase}/api/meeting-prep/__defaults__/questions`, {
      headers:{Authorization:`Bearer ${window.__mspToken||""}`}
    });
    if(!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    return d.questions || [];
  };

  // Load prep response + AI brief from an upcomingMeetingPreps item directly
  const loadPrepNoteDataForPrep=async(prep)=>{
    setSelectedPrepBlock(prep);
    setPrepNotesResponse(null);setPrepNotesLinkInfo(null);setPrepNotesBrief(null);
    setPrepNotesLoading(true);
    if(!prep){setPrepNotesLoading(false);return;}
    const titleClient=atCompanies.find(c=>normCo(c.companyName)===normCo(prep.client)||c.companyName.toLowerCase()===prep.client?.toLowerCase());
    // If not found by name, try attendee email domains (for past meetings where client name may not be extracted)
    let resolvedClient=titleClient;
    if(!resolvedClient&&prep.attendees?.length){
      for(const att of prep.attendees){
        const email=att.emailAddress?.address||att.email||"";
        const domain=email.split("@")[1]||"";
        if(!domain||domain==="datatech.net.au")continue;
        const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
        if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){resolvedClient=co;break;}}
      }
    }
    if(!resolvedClient){setPrepNotesLoading(false);return;}
    const companyId=String(resolvedClient.id);
    const meetingDate=prep.meetingDate;
    // prep.id is constructed as `past-{eventId}-{date}` or `prep-{eventId}-{date}` — strip prefix to get raw event id
    const rawPrepId=prep.id?.replace(/^past-/,"").replace(/^prep-/,"").replace(/-\d{4}-\d{2}-\d{2}$/,"");
    // Probe ALL plausible storage keys in parallel — send-link freezes whichever
    // of (iCalUID, meetingDate) into the token, so the storage key varies.
    // Trying just one or two misses the case where data lives under a different key.
    // Probe ALL plausible storage keys in parallel — send-link freezes whichever
    // of (iCalUID, meetingDate, "prep-"+id) into the token at send time. The
    // "prep-{eventId}" variant exists because earlier code paths sent the
    // prep card's React id (which has the "prep-" prefix) as meetingDate.
    // Trying just one or two misses the data when it lives under a different key.
    const candidateKeys=[
      rawPrepId,
      prep.iCalUID,
      meetingDate,
      rawPrepId?`prep-${rawPrepId}`:null,
      prep.iCalUID?`prep-${prep.iCalUID}`:null,
    ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
    const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    const fetchWithTimeout=(url,ms=12000)=>{
      const ctrl=new AbortController();
      const tid=setTimeout(()=>ctrl.abort(),ms);
      return fetch(url,{headers:{Authorization:`Bearer ${window.__mspToken||""}`},signal:ctrl.signal})
        .then(r=>r.json()).catch(()=>null).finally(()=>clearTimeout(tid));
    };
    // Fire all candidate keys against `path` and pick the first one with valid data
    const probeAllKeys=async(path,validator)=>{
      const results=await Promise.all(
        candidateKeys.map(k=>fetchWithTimeout(`${_apiBase}${path}?meeting=${encodeURIComponent(k)}`))
      );
      for(const r of results){if(validator(r))return r;}
      return results.find(r=>r!=null)||null;
    };
    const timeoutPromise=new Promise(resolve=>setTimeout(()=>resolve(null),15000));
    try{
      const result=await Promise.race([
        Promise.all([
          probeAllKeys(`/api/meeting-prep/${companyId}`,r=>r&&(r.hasResponse||r.response)),
          probeAllKeys(`/api/meeting-prep/${companyId}/link`,r=>r&&(r.formUrl||r.sentAt)),
          probeAllKeys(`/api/ai/meeting-brief/${companyId}`,r=>r&&(r.brief||r.snapshot||r.hasBrief)),
        ]),
        timeoutPromise.then(()=>[null,null,null]),
      ]);
      const [resp,link,savedBrief]=result||[null,null,null];
      setPrepNotesResponse(resp?.response||null);
      setPrepNotesLinkInfo({...(link||{}),meetingDate,meetingKey:candidateKeys[0]});
      const _b=savedBrief?.brief;if(_b){setPrepNotesBrief(typeof _b==="object"?_b:(typeof _b==="string"?_b:null));}
    }catch{/* swallow */}
    finally{setPrepNotesLoading(false);}
  };

  // Load prep response + AI brief for a selected prep block (Prep Notes tab)
  const loadPrepNoteData=async(block)=>{
    setSelectedPrepBlock(block);
    setPrepNotesResponse(null);setPrepNotesLinkInfo(null);setPrepNotesBrief(null);
    if(!block)return;
    const titleClient=resolveTitleClient(block);
    if(!titleClient){setPrepNotesLoading(false);return;}
    const companyId=String(titleClient.id);
    const clientMeetings=upcomingMeetingPreps.filter(p=>p.client?.toLowerCase()===titleClient.companyName.toLowerCase());
    const meeting=clientMeetings[0];
    if(!meeting){setPrepNotesLoading(false);return;}
    const meetingDate=meeting.meetingDate;
    // Probe all three plausible storage keys — send-link freezes whichever of
    // (iCalUID, meetingDate) was present at the time of sending. We can't know
    // which one the data is under without trying all three.
    // Probe all plausible storage keys — send-link freezes whichever of
    // (iCalUID, meetingDate, "prep-"+id) was in context at the time of sending.
    // We can't know which one the data is under without trying all of them.
    const candidateKeys=[
      meeting.id,
      meeting.iCalUID,
      meetingDate,
      meeting.id?`prep-${meeting.id}`:null,
      meeting.iCalUID?`prep-${meeting.iCalUID}`:null,
    ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
    const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    const hdr={headers:{Authorization:`Bearer ${window.__mspToken||""}`}};
    const probeAllKeys=async(path,validator)=>{
      const get=(key)=>fetch(`${_apiBase}${path}?meeting=${encodeURIComponent(key)}`,hdr).then(r=>r.json()).catch(()=>null);
      const results=await Promise.all(candidateKeys.map(get));
      for(const r of results){if(validator(r))return r;}
      return results.find(r=>r!=null)||null;
    };
    setPrepNotesLoading(true);
    Promise.all([
      probeAllKeys(`/api/meeting-prep/${companyId}`,r=>r&&(r.hasResponse||r.response)),
      probeAllKeys(`/api/meeting-prep/${companyId}/link`,r=>r&&(r.formUrl||r.sentAt)),
      probeAllKeys(`/api/ai/meeting-brief/${companyId}`,r=>r&&(r.brief||r.snapshot||r.hasBrief)),
    ]).then(([resp,link,savedBrief])=>{
      setPrepNotesResponse(resp?.response||null);
      setPrepNotesLinkInfo({...(link||{}),meetingDate,meetingKey:candidateKeys[0]});
      const _b=savedBrief?.brief; if(_b){setPrepNotesBrief(typeof _b==="object"?_b:(typeof _b==="string"?_b:null));}
      setPrepNotesLoading(false);
    }).catch(()=>setPrepNotesLoading(false));
  };

  // Load meeting brief + pre-meeting form for a past calendar meeting (Meeting Notes tab)
  const loadMeetingNotesBrief=async(calMeeting)=>{
    setSelectedMeetingBlock(calMeeting);
    setMeetingNotesBrief(null);setMeetingNotesPageUrl(null);setMeetingNotesPrepResponse(null);setMeetingNotesLinkInfo(null);
    setMeetingNotesBriefLoading(true);
    if(!calMeeting){setMeetingNotesBriefLoading(false);return;}
    const titleClient=atCompanies.find(c=>
      (calMeeting.client&&(normCo(c.companyName)===normCo(calMeeting.client)||c.companyName.toLowerCase()===calMeeting.client.toLowerCase()))||
      calMeeting.title?.toLowerCase().includes(c.companyName.toLowerCase())
    );
    // Also try attendee email domain matching (needed when clientDomainOverrides not set)
    let resolvedClient=titleClient;
    if(!resolvedClient&&calMeeting.attendees?.length){
      for(const att of calMeeting.attendees){
        const email=att.emailAddress?.address||att.email||"";
        const domain=email.split("@")[1]||"";
        if(!domain||domain==="datatech.net.au")continue;
        const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
        if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){resolvedClient=co;break;}}
      }
    }
    if(!resolvedClient){setMeetingNotesBriefLoading(false);return;}
    const companyId=String(resolvedClient.id);
    const ds=calMeeting._dateStr||calMeeting.localDateStr||(typeof calMeeting.date==="string"?calMeeting.date.slice(0,10):null);
    // Storage can be keyed by ANY of: event id, iCalUID, or date — depending on
    // when/how the form link was generated. The send-link endpoint takes whatever
    // was in `clientMeeting.iCalUID || clientMeeting.meetingDate` at the moment
    // of sending, so the storage key is "frozen" at send time. To find it
    // reliably, probe all three plausible keys in parallel and pick whichever
    // returns actual data.
    // Storage can be keyed by ANY of: event id, iCalUID, date, or "prep-"+id —
    // depending on when/how the form link was generated. The send-link endpoint
    // takes whatever was in `clientMeeting.iCalUID || clientMeeting.meetingDate`
    // or earlier code paths used the prep card's React id (which has a "prep-"
    // prefix). The storage key is "frozen" at send time. To find it reliably,
    // probe all five plausible variants in parallel.
    const candidateKeys=[
      calMeeting.id,
      calMeeting.iCalUID,
      ds,
      calMeeting.id?`prep-${calMeeting.id}`:null,
      calMeeting.iCalUID?`prep-${calMeeting.iCalUID}`:null,
    ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
    const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
    // Fetch with abort controller so we never hang indefinitely
    const fetchWithTimeout=(url,ms=12000)=>{
      const ctrl=new AbortController();
      const tid=setTimeout(()=>ctrl.abort(),ms);
      return fetch(url,{headers:{Authorization:`Bearer ${window.__mspToken||""}`},signal:ctrl.signal})
        .then(r=>r.json())
        .catch(()=>null)
        .finally(()=>clearTimeout(tid));
    };
    // Fire all candidate keys against `path` and pick the best result
    const probeAllKeys=async(path,validator)=>{
      const results=await Promise.all(
        candidateKeys.map(k=>fetchWithTimeout(`${_apiBase}${path}?meeting=${encodeURIComponent(k)}`))
      );
      // First result that actually has data wins
      for(const r of results){if(validator(r))return r;}
      // Otherwise return the first non-null response (or null if all failed)
      return results.find(r=>r!=null)||null;
    };
    // Overall 15s safety net — if all probes somehow hang, clear the spinner
    const timeoutPromise=new Promise(resolve=>setTimeout(()=>resolve(null),15000));
    try{
      const [briefData,prepData,linkData]=await Promise.race([
        Promise.all([
          probeAllKeys(`/api/ai/meeting-brief/${companyId}`,r=>r&&(r.brief||r.snapshot||r.hasBrief)),
          probeAllKeys(`/api/meeting-prep/${companyId}`,r=>r&&(r.hasResponse||r.response)),
          // Probe the /link endpoint too — this tells us whether a form was even
          // sent for this meeting. Distinguishes "no form sent" from "form sent
          // but client hasn't responded yet" so the UI can show the right message.
          probeAllKeys(`/api/meeting-prep/${companyId}/link`,r=>r&&(r.formUrl||r.sentAt)),
        ]),
        timeoutPromise.then(()=>[null,null,null]),
      ]);
      // Diagnostic: when the response is missing, log what we tried so the next
      // missing-response report can be debugged from browser DevTools instead
      // of having to live-probe via the Chrome extension.
      if (!prepData?.response) {
        console.log("[MeetingNotes] No prep response found", {
          companyId,
          companyName: resolvedClient?.companyName,
          candidateKeys: candidateKeys.map(k => String(k).slice(0, 60) + (String(k).length>60?"…":"")),
          linkSent: !!linkData?.sentAt,
          linkContactEmail: linkData?.contactEmail || null,
        });
      }
      const brief=briefData?.brief||null;
      setMeetingNotesBrief(brief);
      if(brief?.committedMeetingUrl)setMeetingNotesPageUrl(brief.committedMeetingUrl);
      setMeetingNotesPrepResponse(prepData?.response||null);
      setMeetingNotesLinkInfo(linkData||null);
    }catch{/* swallow */}
    finally{setMeetingNotesBriefLoading(false);}
  };

  const loadFellowMeetings=async(forceRefresh=false)=>{
    setFellowMeetingsLoading(true);
    try{
      const meetings=await onGetFellowMeetings(20,forceRefresh);
      // Enrich with preview and attendee count from the list
      const enriched=(meetings||[]).map(m=>({
        ...m,
        preview:m.summary||m.description||m.bodyPreview||"",
        attendeeCount:m.attendees?.length||m.attendee_count||0,
      }));
      setFellowMeetings(enriched);
    }catch(e){console.warn("Fellow meetings load failed",e);}
    finally{setFellowMeetingsLoading(false);}
  };

  const selectFellowMeeting=async(meeting, forceRefresh=false)=>{
    // Mark as reviewed in cache when opened
    if(!forceRefresh&&onAnnotateFellowMeeting&&meeting?.id){
      onAnnotateFellowMeeting(meeting.id,{reviewed:true,reviewedAt:new Date().toISOString()});
      setFellowMeetings(ms=>ms.map(fm=>fm.id===meeting.id?{...fm,annotations:{...(fm.annotations||{}),reviewed:true}}:fm));
    }
    setSelectedFellowMeeting(meeting);
    setNotesMode("fellow");
    setExtracted([]);
    setNotesText("");
    setSelectedNoteLines(new Set());
    setDeletedNoteLines(new Set());
    setEditedNoteLines({});
    setEditingNoteLine(null);
    setAssignToMeeting("");
    setMeetingAssignError(false); // clear any prior validation error when switching meetings
    // Pull full notes from backend — pass forceRefresh to bypass blob cache.
    // Mark loading immediately so the right-hand panel shows a spinner; backend
    // may take several seconds when the cache auto-heal needs to refetch from
    // Fellow + re-extract AI sections.
    if(onGetFellowNotes){
      setFellowNotesLoading(true);
      try{
        const result=await onGetFellowNotes(meeting.id, meeting.title, forceRefresh);
        // Guard against a race where the user clicked a DIFFERENT meeting while
        // this fetch was in flight — only apply the result if this is still
        // the selected meeting. Otherwise the late response would overwrite
        // the newer selection's notes.
        if(selectedFellowMeetingRef.current?.id !== meeting.id){
          return; // user moved on; discard
        }
        if(result?.plainText){
          // Use curated version (with user deletions/edits) if it exists
          const curatedKey="curated_"+meeting.id;
          const curated=meetingRawNotes[curatedKey];
          setNotesText(curated?.text||result.plainText);
          setNotesMeeting(result.title||meeting.title||"");
          // Resolve the actual client company name from attendee email domains
          let resolvedClient="";
          for(const att of (meeting.attendees||[])){
            const email=att.email||att.emailAddress?.address||"";
            const domain=email.split("@")[1]||"";
            if(!domain||domain==="datatech.net.au")continue;
            const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
            if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){resolvedClient=co.companyName;break;}}
            if(!resolvedClient&&atCompanies.length){
              const domBase=domain.replace(/\.(com\.au|com|net\.au|net|org\.au|org|au)$/,"").replace(/[.-]/g," ").toLowerCase();
              const fuzzy=atCompanies.find(c=>c.companyName.toLowerCase().replace(/[^a-z0-9]/g,"").includes(domBase.replace(/\s/g,"")));
              if(fuzzy){resolvedClient=fuzzy.companyName;break;}
            }
          }
          setNotesClient(resolvedClient);
          // Update the cached badge on the meeting card
          if(forceRefresh){
            setFellowMeetings(ms=>ms.map(fm=>fm.id===meeting.id?{...fm,_cachedAt:new Date().toISOString()}:fm));
          }
        }
      }catch(e){console.warn("Fellow notes load failed",e);}
      finally{
        // Only clear loading if this is still the active selection. If user
        // moved on, the newer selectFellowMeeting call has already set
        // fellowNotesLoading=true for its own fetch — we must not turn it off.
        if(selectedFellowMeetingRef.current?.id === meeting.id){
          setFellowNotesLoading(false);
        }
      }
    }
  };

  const extractTodosFromNotes=async()=>{
    if(!notesText.trim())return;
    const lines=notesText.split("\n");
    const processedLines=lines.map((l,i)=>deletedNoteLines.has(i)?null:(editedNoteLines[i]??l)).filter(l=>l!==null);

    // TICKED LINES → move directly into the right Action Items panel (no AI needed)
    if(selectedNoteLines.size>0){
      const tickedItems=[...selectedNoteLines]
        .filter(i=>!deletedNoteLines.has(i))
        .sort((a,b)=>a-b)
        .map(i=>editedNoteLines[i]??lines[i]??"")
        .filter(Boolean)
        .map((line,idx)=>({
          id:`ext-${Date.now()}-${idx}`,
          title:line.replace(/^[-*•>◆✓#]+\s*/,"").replace(/^#+\s*/,"").trim()||line,
          priority:"med",
          suggestedDate:new Date(Date.now()+3*24*60*60*1000).toISOString().slice(0,10),
          editedDate:new Date(Date.now()+3*24*60*60*1000).toISOString().slice(0,10),
          layer:"O",
          client:notesClient||notesMeeting||"",
          owner:"me",
          notes:"",
          selected:true,
        }));
      if(tickedItems.length) setExtracted(prev=>{
        // Merge with any existing extracted items (avoid duplicates by title)
        const existingTitles=new Set(prev.map(e=>e.title));
        const newItems=tickedItems.filter(t=>!existingTitles.has(t.title));
        return [...prev,...newItems];
      });
      setSelectedNoteLines(new Set()); // clear ticks after moving across
      return;
    }

    // NO LINES SELECTED → run AI to detect action items → put in right panel
    const textToExtract=processedLines.join("\n");
    if(!textToExtract.trim())return;
    setExtracting(true);setExtracted([]);
    const today=todayISO();
    const prompt=`You are an MSP project assistant. Extract all action items from these meeting notes.
Meeting context: ${notesMeeting||"client meeting"}
Today's date: ${today}

For each action item return a JSON array (no markdown, pure JSON) with objects:
{
  "title": "concise action title",
  "priority": "high"|"med"|"low",
  "suggestedDate": "YYYY-MM-DD (high=1-2 days, med=3-5 days, low=7-14 days)",
  "layer": "S"|"T"|"O",
  "client": "client name or Internal",
  "owner": "person's name if mentioned, or 'me' if assigned to you, or 'unknown'",
  "notes": "any extra context from the notes"
}

Include ALL action items regardless of who they are assigned to.

Notes:
${textToExtract.slice(0,8000)}`;
    try{
      let parsed=[];
      if(onExtractTodos){
        parsed=await onExtractTodos(textToExtract,notesMeeting)||[];
      } else {
        const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1500,messages:[{role:"user",content:prompt}]})});
        const data=await res.json();
        const text=data.content?.find(b=>b.type==="text")?.text||"[]";
        parsed=JSON.parse(text.replace(/```json|```/g,"").trim());
      }
      setExtracted((parsed||[]).map((item,i)=>({
        ...item,
        id:`ext-${Date.now()}-${i}`,
        selected:true,
        editedDate:item.suggestedDate,
      })));
    }catch(e){
      console.warn("Extraction failed",e);
      setExtracted([{id:"ext-err",title:"Extraction failed — try again",priority:"low",layer:"O",client:"",owner:"unknown",notes:"",selected:false,editedDate:today}]);
    }finally{setExtracting(false);}
  };

  const connectCal=()=>{
    // Connect calendar — use real liveCalEvents if available, never overwrite with mock data
    setCalConn(true);
    if(liveCalEvents.length>0){
      setMeetings(liveCalEvents); // real Graph calendar events
    }
    // Do not set mock MEETINGS — real calendar data comes via liveCalEvents prop
  };

  const acceptPrep=(pb,mid)=>{setBlocks(bs=>{const next=[...bs,...pb];saveSnapshot({blocks:next});return next;});setDismissed(d=>new Set([...d,mid]));setPrepToast(null);};
  const dismissPrep=(mid)=>{setDismissed(d=>new Set([...d,mid]));setPrepToast(null);};

  // ─── Autotask ────────────────────────────────────────────────────────────

  const connectAutotask=async()=>{
    setAtConnecting(true);
    // In production: validate creds against AT REST API /V1.0/Companies?search={}
    // Here we simulate a 1.2s auth check
    await new Promise(r=>setTimeout(r,1200));
    setAtConnected(true);setAtConnecting(false);
  };

  const openTicketModal=async(email,opts={})=>{
    if(!atConnected)return;
    const quickTicket=!!opts.quickTicket;
    // Resolve sensible defaults from live AT reference data when available.
    // Mock arrays (AT_WORK_TYPES/AT_RESOURCES) contain placeholder IDs that don't
    // exist in real Autotask — sending them causes ticket creation to 500.
    const firstActiveQueue=(atReference?.queues||[]).find(q=>q.isActive!==false&&q.isSystem!==true);
    // Default work type to "Remote Support" if present in the live billing codes
    const remoteSupport=(atReference?.workTypes||[]).find(w=>/^\s*remote\s+support\s*$/i.test(w.name||w.label||""));
    // Default ticket source to "Web Portal" if present in the live sources picklist
    const webPortal=(atReference?.sources||[]).find(s=>/^\s*web\s*portal\s*$/i.test(s.label||""));
    const defaultTicket={
      title:email.subj,
      description:`Email from: ${email.from}\nReceived: ${email.time}\n\n${email.aiSum||""}`,
      priority:email.priority==="urgent"?1:email.priority==="vip"?2:3,
      queueId:firstActiveQueue?+firstActiveQueue.value:"",
      statusId:1,
      dueDate:isoDate(new Date(Date.now()+86400000)),
      workTypeId:remoteSupport?.id||"",
      source:webPortal?+webPortal.value:"",
      contractId:"",
      resourceId:"",
      companyId:"",
      contactId:"",
      configurationItemId:"",
    };
    setAtTicket(defaultTicket);
    // Tag the modal payload with quickTicket so the modal/submit logic
    // can choose between the simple "create ticket only" flow (from inbox row)
    // and the full "create ticket + reply + delete email" flow (from detail modal)
    setAtModal({...email,quickTicket});
    setAtLookup(null);
    setAtCustomFields([{key:"",value:""}]);
    // Reset ticket-reply fields. Pre-fill a courteous template.
    setTicketReplyBody(`Hi ${(email.from||"").split(" ")[0]||"there"},\n\nThanks for getting in touch — I've logged this as a ticket and will be in touch shortly with an update.\n\nKind regards,\nCraig`);
    // Default reply ON for detail-modal flow, OFF for quick-ticket flow
    setTicketReplySend(!quickTicket);
    // Lookup contact by email
    setAtLookingUp(true);
    await new Promise(r=>setTimeout(r,700));
    const contact=AT_CONTACTS.find(c=>c.email===email.email||email.from.toLowerCase().includes(c.firstName.toLowerCase()));
    if(contact){
      const company=AT_COMPANIES.find(c=>c.id===contact.companyId);
      setAtLookup({contact,company});
      setAtTicket(t=>({...t,companyId:contact.companyId,contactId:contact.id,contractId:""}));
      // Lazy-load live contacts/contracts from Autotask for this company
      const {contracts:loadedContracts}=await loadCompanyContactsContracts(contact.companyId);
      if(loadedContracts.length){
        setAtTicket(t=>({...t,contractId:loadedContracts[0].id}));
      }
    }
    setAtLookingUp(false);
  };

  const submitTicket=async()=>{
    setAtSubmitting(true);
    try {
      if(onCreateTicket) {
        const result = await onCreateTicket({...atTicket,customFields:atCustomFields.filter(cf=>cf.key&&cf.value)});
        const ticketId=result?.ticket?.id||result?.ticket?.itemId;
        if(!ticketId){
          setAtSubmitting(false);
          alert("Ticket creation failed: Autotask did not return a ticket ID. Check the form fields and try again.");
          return;
        }
        const ticketNumber=result?.ticket?.ticketNumber||null;
        const ticketNum=ticketNumber||`T${ticketId}`;
        const isQuick=!!atModal?.quickTicket;
        // Optional: send reply email + add public note to ticket
        // Quick ticket flow (from inbox row) skips reply/delete entirely.
        const replyText=(ticketReplyBody||"").trim();
        if(!isQuick && ticketReplySend && replyText){
          // 1) Record the reply on the ticket as a note.
          //    ⚠ THIS DOES NOT REACH THE CLIENT. The customer gets the reply
          //    from the Graph send at step 2. publish 2 is "Internal Project
          //    Team" in the live picklist, not the client portal (verified
          //    2026-08-29); the comment here claimed the opposite.
          if(onAddTicketNote){
            try{
              await onAddTicketNote(ticketId,{
                title:`Email reply to ${atModal.from||"client"}`,
                description:replyText,
                publish:2, // value unchanged — only the wrong comment was fixed
                noteType:1,
              });
            }catch(err){console.warn("Add ticket note failed",err);}
          }
          // 2) Send the reply email back via Graph (threaded reply to the original message)
          if(onReplyEmail && atModal.graphId){
            try{
              const htmlBody=replyText.replace(/\n/g,"<br/>");
              await onReplyEmail(atModal.graphId,htmlBody);
            }catch(err){console.warn("Reply email send failed",err);}
          }
        }
        setAtSuccess({ticketNum,emailId:atModal.id,quickTicket:isQuick});
        setTicketedEmails(s=>new Set([...s,atModal.id]));
        // Quick ticket flow leaves the email in the inbox; full flow deletes it.
        if(!isQuick){
          try {
            if(onDeleteEmail && atModal.graphId){
              await onDeleteEmail(atModal.graphId);
            } else if(onArchiveEmail && atModal.graphId){
              // Fallback to archive if delete handler not wired up
              await onArchiveEmail(atModal.graphId);
            }
          } catch(e) { console.warn("Delete/archive email failed",e); }
          setDeletedEmails(s=>new Set([...s,atModal.id]));
        }
        setAtModal(null);
        setTicketReplyBody("");
        setTimeout(()=>setAtSuccess(null),3000);
      }
    } catch(e) {
      console.warn("Ticket creation failed",e);
      const msg=e?.response?.data?.message||e?.message||"Unknown error from Autotask";
      alert(`Ticket creation failed: ${msg}`);
    } finally {
      setAtSubmitting(false);
    }
  };

  const cancelTicket=()=>{
    setAtModal(null);
    setAtTodoModal(null);
    setAtSuccess(null);
    setAtLookup(null);
    setAtModalContacts([]);
    setAtModalContracts([]);
    setAtModalAssets([]);
    setAtModalLoading(false);
    setTicketReplyBody("");
  };

  // ── Inbox actions: delete + plain reply (no ticket) ───────────────────────
  const handleDeleteEmail=async(email)=>{
    if(!email)return;
    if(!window.confirm(`Move this email from ${email.from} to Deleted Items?\n\n"${email.subj||""}"`))return;
    // Close detail modal if this email is currently being viewed
    if(emailDetailModal&&emailDetailModal.email&&emailDetailModal.email.id===email.id){
      setEmailDetailModal(null);
    }
    // Optimistic UI: hide immediately
    setDeletedEmails(s=>new Set([...s,email.id]));
    try{
      if(onDeleteEmail && email.graphId){
        await onDeleteEmail(email.graphId);
      } else if(onArchiveEmail && email.graphId){
        // Fallback if delete handler not wired
        await onArchiveEmail(email.graphId);
      }
    }catch(err){
      console.warn("Delete email failed",err);
      // Roll back optimistic hide on failure
      setDeletedEmails(s=>{const n=new Set([...s]);n.delete(email.id);return n;});
      alert(`Couldn't delete email: ${err?.message||"Unknown error"}`);
    }
  };

  // Fetch full email body when the detail modal opens. The /api/emails list
  // returns only bodyPreview (Graph caps at ~255 chars) so we hit Graph
  // on-demand for the full HTML body when the user actually opens an email.
  useEffect(()=>{
    if(!emailDetailModal){
      setEmailBodyContent(null);
      setEmailBodyError(null);
      setEmailBodyLoading(false);
      return;
    }
    const email=emailDetailModal.email;
    if(!email||!email.graphId||!onFetchEmailBody){
      // No fetcher available or no Graph ID — modal will fall back to bodyPreview
      return;
    }
    let cancelled=false;
    setEmailBodyLoading(true);
    setEmailBodyError(null);
    setEmailBodyContent(null);
    (async()=>{
      try{
        const result=await onFetchEmailBody(email.graphId);
        if(cancelled)return;
        setEmailBodyContent({
          html:result?.body?.content||result?.content||result?.body||"",
          contentType:result?.body?.contentType||result?.contentType||"html",
          fetched:true,
        });
      }catch(err){
        if(cancelled)return;
        console.warn("Fetch email body failed",err);
        setEmailBodyError(err?.message||"Couldn't load the full email body.");
      }finally{
        if(!cancelled)setEmailBodyLoading(false);
      }
    })();
    return()=>{cancelled=true;};
  },[emailDetailModal,onFetchEmailBody]);

  const openReplyModal=(email)=>{
    if(!email)return;
    setReplyModal({email});
    setReplyBody(`Hi ${(email.from||"").split(" ")[0]||"there"},\n\n\n\nKind regards,\nCraig`);
  };

  const sendReplyEmail=async()=>{
    if(!replyModal||!replyBody.trim())return;
    setReplySending(true);
    try{
      if(!onReplyEmail||!replyModal.email.graphId){
        throw new Error("Reply not available — email has no Graph ID or reply handler not wired up.");
      }
      const htmlBody=replyBody.replace(/\n/g,"<br/>");
      await onReplyEmail(replyModal.email.graphId,htmlBody);
      setReplyModal(null);
      setReplyBody("");
    }catch(err){
      console.warn("Reply send failed",err);
      alert(`Couldn't send reply: ${err?.message||"Unknown error"}`);
    }finally{
      setReplySending(false);
    }
  };

  // Phase 2: Datto device hint. Given a Datto device's lastLoggedInUser (e.g.
  // "DOMAIN\\username") and an AT contact, score how likely this is their machine.
  // Returns 0 when no signal. Used to surface a soft hint — never auto-fills any field.
  const matchContactToDevice=(contact,device)=>{
    if(!contact||!device?.lastLoggedInUser) return 0;
    const norm=s=>(s||"").toLowerCase().replace(/[._\s-]/g,"");
    const userPart=norm((device.lastLoggedInUser.split("\\").pop())||device.lastLoggedInUser);
    if(!userPart) return 0;
    const first=norm(contact.firstName);
    const last=norm(contact.lastName);
    const email=(contact.emailAddress||"").toLowerCase();
    const emailLocal=norm(email.split("@")[0]);
    const full=first+last;
    // Strongest: email local-part exact match (e.g. reception@ → \reception)
    if(emailLocal&&emailLocal.length>=3&&userPart===emailLocal) return 100;
    // Firstname.lastname or firstnamelastname combined
    if(full&&full.length>=4&&userPart===full) return 95;
    // First+last both contained in user (handles "troy.scott" → "troyscott")
    if(first&&last&&first.length>=3&&last.length>=3&&userPart.includes(first)&&userPart.includes(last)) return 80;
    // First name only exact (riskier when first names are common)
    if(first&&first.length>=4&&userPart===first) return 50;
    // Last name only exact
    if(last&&last.length>=4&&userPart===last) return 45;
    return 0;
  };

  // Find the best-matching Datto device for a contact, scoped to that company's
  // Datto site (by name). Returns the device or null. Threshold 80+ to avoid
  // false positives from short common first names like "John" against shared
  // accounts like "JOHN" the local-admin alias.
  const findDeviceForContact=(contact,companyName)=>{
    if(!contact||!companyName) return null;
    const devices=dattoData?.devices?.data||(Array.isArray(dattoData?.devices)?dattoData.devices:[])||[];
    if(!devices.length) return null;
    // Match site name to company name (case-insensitive, trimmed)
    const cn=companyName.trim().toLowerCase();
    const siteDevices=devices.filter(d=>(d.siteName||"").trim().toLowerCase()===cn);
    if(!siteDevices.length) return null;
    let best={device:null,score:0};
    for(const d of siteDevices){
      const score=matchContactToDevice(contact,d);
      if(score>best.score) best={device:d,score};
      if(score>=100) break;
    }
    return best.score>=80?{...best,deviceCount:siteDevices.length}:null;
  };

  // ── Action item carryover helpers (MIP outstanding actions) ───────────────
  //
  // gatherPriorActionItems(companyId, companyName) — returns todos for this
  //   client from the last 90 days, shaped as the backend expects.
  // findPreviousMeetingDate(companyName) — most recent past meeting with this
  //   client from liveCalEvents, used as the "completed since last meeting" cutoff.
  // fetchTicketStatuses(ids) — bulk lookup current AT ticket status from backend.
  // autoCompleteTodosFromAT — sweeps todos at app load, marks them done if
  //   their AT ticket is now Complete (status 5 or 32).

  const _aiApiBase = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

  const fetchTicketStatuses = async (ticketIds) => {
    const ids = (ticketIds || []).filter(Boolean);
    if (!ids.length) return {};
    try {
      const r = await fetch(`${_aiApiBase}/api/ai/ticket-statuses`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${window.__mspToken || ""}` },
        body: JSON.stringify({ ticketIds: ids }),
      });
      if (!r.ok) return {};
      const d = await r.json();
      return d.statuses || {};
    } catch (e) {
      console.warn("fetchTicketStatuses failed", e);
      return {};
    }
  };

  const autoCompleteTodosFromAT = async () => {
    const candidates = todos.filter(t => !t.done && t.atTicketId);
    if (!candidates.length) return;
    const statuses = await fetchTicketStatuses(candidates.map(t => t.atTicketId));
    const updates = [];
    candidates.forEach(t => {
      const s = statuses[t.atTicketId];
      if (s?.isComplete) {
        updates.push({
          id: t.id,
          completedAt: s.completedDate || s.lastActivityDate || new Date().toISOString(),
          atStatus: s.statusName,
        });
      }
    });
    if (!updates.length) return;
    setTodos(prev => {
      const updMap = new Map(updates.map(u => [u.id, u]));
      const next = prev.map(t => {
        const u = updMap.get(t.id);
        if (!u) return t;
        return { ...t, done: true, completedAt: u.completedAt, autoCompletedFromAT: true, atStatus: u.atStatus };
      });
      saveSnapshot({ todos: next });
      return next;
    });
  };

  const gatherPriorActionItems = (companyId, companyName) => {
    if (!companyName) return [];
    const cutoffMs   = Date.now() - 90 * 24 * 60 * 60 * 1000;
    const cnLower    = (companyName || "").toLowerCase().trim();

    // Strict client matching: companyId equality FIRST, then exact-name equality,
    // then substring containment IN EITHER DIRECTION (with neither side trimmed
    // to a single word). The old logic split on space and matched any todo whose
    // client contained the first word — so "Hotel Rottnest" matched every todo
    // with "hotel" in the client name, surfacing other clients' action items
    // in the Outstanding/Completed carry-forward cards.
    const matches = (todos || []).filter(t => {
      const created = new Date(t.createdAt || t.dueDate || 0).getTime();
      if (created && created < cutoffMs) return false;
      if (companyId != null && t.companyId != null && String(t.companyId) === String(companyId)) return true;
      const tc = (t.client || "").toLowerCase().trim();
      if (!tc) return false;
      if (tc === cnLower) return true;
      // Allow whole-name substring containment (handles "Karratha International Hotel" ⊃ "Karratha International Hotel — Wellard Suite") but require BOTH sides be reasonably long so a single word can't bleed across clients.
      if (cnLower.length >= 8 && tc.length >= 8 && (tc.includes(cnLower) || cnLower.includes(tc))) return true;
      return false;
    });

    return matches.map(t => ({
      todoId:            t.id,
      title:             t.title || "",
      atTicketId:        t.atTicketId || null,
      atTicketNumber:    t.atTicketNumber || null,
      doneInMspOs:       !!t.done,
      completedAt:       t.completedAt || null,
      sourceMeetingDate: t.meetingDate || t.sourceMeetingDate || t.dueDate || null,
    }));
  };

  const findPreviousMeetingDate = (companyName) => {
    if (!companyName || !Array.isArray(liveCalEvents)) return null;
    const cnLower = (companyName || "").toLowerCase().trim();
    const nowMs   = Date.now();
    // Same strict approach as gatherPriorActionItems — full-name match against
    // the meeting title. No first-word fallback (which caused "Hotel Rottnest"
    // to pick up any past meeting with "hotel" in the title).
    const past = liveCalEvents
      .filter(e => {
        const t = new Date(e.start).getTime();
        if (!t || t >= nowMs) return false;
        const ttl = (e.title || e.subject || "").toLowerCase();
        if (!ttl) return false;
        return ttl.includes(cnLower) && cnLower.length >= 8;
      })
      .sort((a, b) => new Date(b.start) - new Date(a.start));
    return past[0]?.start || null;
  };

  // One-shot auto-complete sweep — run once after todos hydrate from blob.
  // Placed AFTER the helpers above so the const references resolve cleanly
  // regardless of how the production bundler reorganises declarations.
  const autoCompleteSweepRef = useRef(false);
  useEffect(() => {
    if (autoCompleteSweepRef.current) return;
    if (!todos || !todos.length) return;
    if (!window.__mspToken) return;
    autoCompleteSweepRef.current = true;
    autoCompleteTodosFromAT();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos.length]);

  // ── Todo-batch helpers (AI Plan My Day grouping feature) ───────────────────
  //
  // When the AI Plan My Day spots a contiguous free window and a backlog of
  // short todos, it bundles them into a single "todo-batch" block (sel.type
  // === "todo-batch"). The block carries a _batchedTodos array of the actual
  // todo IDs/titles. Focus view renders a checklist; ticking flips the
  // underlying todo's `done` flag in real state. Untouched batched todos
  // auto-reschedule to tomorrow at end of day.

  const tomorrowISO = () => {
    const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const p = new Date(t.getTime() + 8 * 60 * 60 * 1000);
    return `${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;
  };

  // Flip a single batched todo's done flag. Called from the Focus view checklist.
  const toggleBatchTodo = (todoId) => {
    setTodos(prev => {
      const next = prev.map(t => {
        if (t.id !== todoId) return t;
        const willBeDone = !t.done;
        return {
          ...t,
          done: willBeDone,
          completedAt: willBeDone ? new Date().toISOString() : null,
          // Clear any auto-rollover marker so a re-tick doesn't get re-bumped
          _batchRolledOver: willBeDone ? false : t._batchRolledOver,
        };
      });
      saveSnapshot({ todos: next });
      return next;
    });
  };

  // Sweep through today's plan looking for todo-batch blocks whose batched
  // todos remain undone. Bump their dueDate to tomorrow so they don't get
  // marked as overdue and so the next AI Plan My Day picks them up. Idempotent
  // — once a todo is marked _batchRolledOver, it won't roll over a second time.
  const rolloverIncompleteBatchedTodos = () => {
    const batches = (dayPlan || []).filter(b => b.type === "todo-batch" && Array.isArray(b._batchedTodos));
    if (!batches.length) return;
    const tomorrow = tomorrowISO();
    const idsToBump = new Set();
    batches.forEach(b => {
      b._batchedTodos.forEach(bt => {
        const t = todos.find(t => t.id === bt.todoId);
        if (t && !t.done && !t._batchRolledOver) idsToBump.add(bt.todoId);
      });
    });
    if (!idsToBump.size) return;
    setTodos(prev => {
      const next = prev.map(t => {
        if (!idsToBump.has(t.id)) return t;
        return { ...t, dueDate: tomorrow, _batchRolledOver: true };
      });
      saveSnapshot({ todos: next });
      return next;
    });
  };

  // Run the rollover automatically at end of day. Fires once per day after
  // 17:30 (end of work hours buffer). The "completed today" check prevents
  // a second sweep on subsequent renders within the same day.
  const rolloverSweepRef = useRef(null);
  useEffect(() => {
    const checkAndRollover = () => {
      const now = new Date();
      const perthNow = new Date(now.getTime() + 8 * 60 * 60 * 1000);
      const hours = perthNow.getUTCHours();
      const todayStr = todayISO();
      // Only after 17:30, only once per day
      if (hours < 17 || (hours === 17 && perthNow.getUTCMinutes() < 30)) return;
      if (rolloverSweepRef.current === todayStr) return;
      rolloverSweepRef.current = todayStr;
      rolloverIncompleteBatchedTodos();
    };
    checkAndRollover();
    const id = setInterval(checkAndRollover, 5 * 60 * 1000); // re-check every 5 min
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayPlan.length, todos.length]);

  // Lazy-load active contacts, active contracts, and configuration items (assets)
  // for a company from Autotask. Called when ticket modal opens with a matched
  // company, and on every company change. Returns the loaded (sorted) lists so
  // callers can auto-pick the first contract.
  const loadCompanyContactsContracts=async(companyId)=>{
    if(companyId==null||companyId===""){
      setAtModalContacts([]);
      setAtModalContracts([]);
      setAtModalAssets([]);
      return {contacts:[],contracts:[],assets:[]};
    }
    setAtModalLoading(true);
    try{
      const [contacts,contracts,assets]=await Promise.all([
        onGetContacts ? onGetContacts(companyId).catch(()=>[]) : Promise.resolve([]),
        onGetContracts ? onGetContracts(companyId).catch(()=>[]) : Promise.resolve([]),
        onGetAssets ? onGetAssets(companyId).catch(()=>[]) : Promise.resolve([]),
      ]);
      // Filter inactive contacts, sort alphabetically by lastName then firstName
      const sortedContacts=(contacts||[])
        .filter(c=>c.isActive!==false)
        .sort((a,b)=>(a.lastName||"").localeCompare(b.lastName||"")||(a.firstName||"").localeCompare(b.firstName||""));
      // Sort contracts alphabetically by contractName (with fallback to name)
      const sortedContracts=(contracts||[])
        .sort((a,b)=>(a.contractName||a.name||"").localeCompare(b.contractName||b.name||""));
      // Filter inactive assets, sort alphabetically by referenceTitle or referenceNumber
      const sortedAssets=(assets||[])
        .filter(a=>a.isActive!==false)
        .sort((a,b)=>(a.referenceTitle||a.referenceNumber||"").localeCompare(b.referenceTitle||b.referenceNumber||""));
      setAtModalContacts(sortedContacts);
      setAtModalContracts(sortedContracts);
      setAtModalAssets(sortedAssets);
      return {contacts:sortedContacts,contracts:sortedContracts,assets:sortedAssets};
    }catch(e){
      console.warn("Failed to load contacts/contracts/assets for company",companyId,e);
      setAtModalContacts([]);
      setAtModalContracts([]);
      setAtModalAssets([]);
      return {contacts:[],contracts:[],assets:[]};
    }finally{
      setAtModalLoading(false);
    }
  };

  // Open ticket modal from a To-Do item
  // `prefill` (Missing Time) is merged over the defaults below: title, description,
  // companyId, contactId, hoursWorked, timeEntryNotes and the explicit meeting
  // times (timeStartUtc/timeEndUtc/timeDateLocal) that submitTodoTicket strips.
  const openTicketFromTodo=async(todo,prefill=null)=>{
    if(!atConnected)return;
    // Resolve sensible defaults from live AT reference data when available.
    // Mock arrays (AT_WORK_TYPES/AT_RESOURCES) contain placeholder IDs that don't
    // exist in real Autotask — sending them causes ticket creation to 500.
    const firstActiveQueue=(atReference?.queues||[]).find(q=>q.isActive!==false&&q.isSystem!==true);
    // Default work type to "Remote Support" if present in the live billing codes
    const remoteSupport=(atReference?.workTypes||[]).find(w=>/^\s*remote\s+support\s*$/i.test(w.name||w.label||""));
    // Default ticket source to "Web Portal" if present in the live sources picklist
    const webPortal=(atReference?.sources||[]).find(s=>/^\s*web\s*portal\s*$/i.test(s.label||""));
    const defaultTicket={
      title:todo.title||"",
      description:todo.notes?`${todo.notes}\n\nFrom To-Do list${todo.client?` (${todo.client})`:""}`:
        `To-Do action item${todo.client?` for ${todo.client}`:""}`,
      priority:todo.priority==="high"?1:todo.priority==="med"?2:3,
      queueId:firstActiveQueue?+firstActiveQueue.value:"",
      statusId:1,
      dueDate:todo.dueDate||isoDate(new Date(Date.now()+86400000)),
      workTypeId:remoteSupport?.id||"",
      source:webPortal?+webPortal.value:"",
      contractId:"",
      resourceId:"",
      companyId:"",
      contactId:"",
      configurationItemId:"",
      // Optional time entry logged against the new ticket (POST /api/timelog/entry).
      // Pre-filled from a Focus batch item's planned minutes when opened from
      // there (_originalDurationMins); blank from the plain To-Do list = no entry.
      hoursWorked:todo._originalDurationMins?+(todo._originalDurationMins/60).toFixed(2):"",
      timeEntryNotes:"",   // what was done — goes on the time entry, not the ticket
      roleId:"",           // "" = the signed-in tech's default Autotask role
    };
    if(prefill)Object.assign(defaultTicket,prefill);
    // Try to pre-match company from client name. A reconcile prefill carries the
    // company ID and is NEVER name-matched.
    let match=null;
    if(prefill?.companyId){
      match=atCompanies.find(c=>String(c.id)===String(prefill.companyId))||null;
    }else if(todo.client&&atCompanies.length){
      match=atCompanies.find(c=>
        c.companyName.toLowerCase().includes(todo.client.toLowerCase())||
        todo.client.toLowerCase().includes(c.companyName.toLowerCase())
      );
      if(match){
        defaultTicket.companyId=match.id;
      }
    }
    setAtTicket(defaultTicket);
    setAtTodoModal(todo);
    setAtModal(null);
    setAtLookup(match||null);
    setAtCustomFields([{key:"",value:""}]);
    // Roles for the time-entry picker — fetched once, non-blocking, and a
    // failure just leaves the picker on "default role".
    if(atMyRoles===null){
      const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
      fetch(`${_ab}/api/timelog/my-roles`,{headers:{Authorization:`Bearer ${window.__mspToken||""}`}})
        .then(r=>r.ok?r.json():null)
        .then(d=>setAtMyRoles(Array.isArray(d?.roles)?d.roles:[]))
        .catch(()=>setAtMyRoles([]));
    }
    // Lazy-load contacts and contracts from live Autotask for the matched company
    if(match){
      const {contracts:loadedContracts}=await loadCompanyContactsContracts(match.id);
      if(loadedContracts.length){
        setAtTicket(t=>({...t,contractId:loadedContracts[0].id}));
      }
    }
  };

  // Submit ticket from To-Do
  const submitTodoTicket=async()=>{
    setAtSubmitting(true);
    try{
      if(onCreateTicket){
        // Time-entry-only fields stay out of the ticket payload. (The backend's
        // createTicket allowlists its fields anyway, so this is hygiene, not the
        // 2026-09-30 500 — that was an inactive Autotask role, fixed server-side.)
        const {hoursWorked:_hw,timeEntryNotes:_ten,roleId:_rid,timeStartUtc:_tsu,timeEndUtc:_teu,timeDateLocal:_tdl,...ticketPayload}=atTicket; // eslint-disable-line no-unused-vars
        const result=await onCreateTicket({...ticketPayload,customFields:atCustomFields.filter(cf=>cf.key&&cf.value)});
        const ticketId=result?.ticket?.id||result?.ticket?.itemId;
        if(!ticketId){
          setAtSubmitting(false);
          alert("Ticket creation failed: Autotask did not return a ticket ID. Check the form fields and try again.");
          return;
        }
        // AT-format ticket number (e.g. T20260511.0042). Backend fetches this via a
        // follow-up GET after POST. Falls back to T<id> if the lookup fails.
        const ticketNumber=result?.ticket?.ticketNumber||null;
        const ticketNum=ticketNumber||`T${ticketId}`;
        // Mark the originating To-Do as done and stamp it with the Autotask ticket
        // number so it moves to the Completed section at the bottom of the list.
        // Persists atTicketId (numeric, for future API calls) and atTicketNumber
        // (TYYYYMMDD.NNNN, for display and copy/paste).
        const todoId=atTodoModal?.id;
        if(todoId&&atTodoModal?._source!=="reconcile"){
          setTodos(ts=>{
            const next=ts.map(t=>t.id===todoId?{...t,done:true,atTicketId:ticketId,atTicketNumber:ticketNumber,atTicketNum:ticketNum,completedAt:new Date().toISOString()}:t);
            saveSnapshot({todos:next});
            return next;
          });
        }
        // Optional time entry against the new ticket. Uses the EXISTING
        // /api/timelog/entry route (the mobile time-logger's), which resolves the
        // Autotask resource from the signed-in user's email server-side — so the
        // hours are always logged as Craig, never as whoever "Assigned To" names.
        // Non-fatal: the ticket exists either way; a failure is surfaced on the
        // success card rather than swallowed.
        const hoursWorked=+atTicket.hoursWorked||0;
        let timeEntry=null;
        if(hoursWorked>0){
          try{
            const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
            // Start/end = now minus the hours → now, as UTC ISO without the Z,
            // the shape the mobile time-logger sends and createTimeEntry expects.
            // dateWorked is the LOCAL calendar date (Perth is UTC+8; the UTC date
            // is yesterday before 08:00).
            // Missing Time passes the meeting's own UTC start/end + local date; otherwise now-minus-hours.
            const _now=atTicket.timeEndUtc?new Date(atTicket.timeEndUtc):new Date();
            const _start=atTicket.timeStartUtc?new Date(atTicket.timeStartUtc):new Date(_now.getTime()-hoursWorked*3600000);
            const teRes=await fetch(`${_ab}/api/timelog/entry`,{
              method:"POST",
              headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
              body:JSON.stringify({
                ticketID:ticketId,
                hoursWorked,
                summaryNotes:(atTicket.timeEntryNotes||atTicket.title||atTodoModal?.title||"To-Do work").trim(),
                billingCodeID:atTicket.workTypeId||null,
                contractID:atTicket.contractId||null,
                roleID:atTicket.roleId||null,        // null ⇒ server resolves the default role
                startDateTime:_start.toISOString().slice(0,19),
                endDateTime:_now.toISOString().slice(0,19),
                dateWorked:atTicket.timeDateLocal||isoDate(_now),
              }),
            });
            const teBody=await teRes.json().catch(()=>({}));
            timeEntry=teRes.ok?{hours:hoursWorked}:{hours:hoursWorked,error:teBody?.error||`HTTP ${teRes.status}`};
          }catch(teErr){
            console.warn("Time entry logging failed (ticket still created)",teErr);
            timeEntry={hours:hoursWorked,error:teErr?.message||"network error"};
          }
        }
        setAtSuccess({ticketNum,todoId,isTodo:true,timeEntry});
        if(atTodoModal?._source==="reconcile"){const done=reconcileTicketDoneRef.current;reconcileTicketDoneRef.current=null;done?.({ticketId,ticketNumber});}
        setAtTodoModal(null);
        setTimeout(()=>setAtSuccess(null),3000);
      }
    }catch(e){
      console.warn("Todo ticket creation failed",e);
      const msg=e?.response?.data?.message||e?.message||"Unknown error from Autotask";
      alert(`Ticket creation failed: ${msg}`);
    }finally{
      setAtSubmitting(false);
    }
  };

  const startFocus=async()=>{
    if(!sel)return;
    // Clear any stash for this block — fresh start
    if(sel.id!=null) delete timerStashRef.current[sel.id];
    const s=sel.dur*60;setSecs(s);setTotal(s);setFocusing(true);setRunning(true);
    setAiLoad(true);setAiText("");
    // If this is a Check Emails block, kick off the chase-up analysis
    const isCheckEmailBlock=(sel.title||"").toLowerCase().includes("check email");
    if(isCheckEmailBlock&&onFetchChaseUp&&chaseRan!==sel.id){
      setChaseInboxItems([]);setChaseSentItems([]);setChaseLoading(true);
      onFetchChaseUp()
        .then(({inboxItems=[],sentItems=[]}={})=>{
          setChaseInboxItems(inboxItems);setChaseSentItems(sentItems);setChaseRan(sel.id);
        })
        .catch(()=>{setChaseInboxItems([]);setChaseSentItems([]);})
        .finally(()=>setChaseLoading(false));
    }
    // If this is a Client Health Check block, kick off per-client AI analysis
    const isClientHealthBlock=(sel.title||"").toLowerCase().includes("client health");
    if(isClientHealthBlock&&onFetchClientHealth&&clientHealthRan!==sel.id){
      setClientHealthData([]);setClientHealthLoading(true);setClientHealthExpanded({});
      const alerts=Array.isArray(dattoData?.alerts)?dattoData.alerts:[];
      const sites=Array.isArray(dattoData?.sites)?dattoData.sites:[];
      onFetchClientHealth({dattoAlerts:alerts,dattoSites:sites})
        .then(clients=>{setClientHealthData(clients||[]);setClientHealthRan(sel.id);})
        .catch(()=>setClientHealthData([]))
        .finally(()=>setClientHealthLoading(false));
    }
    const overdue=Object.values(cadence).flatMap(c=>c.items.filter(i=>!i.done).map(i=>i.title)).slice(0,4);
    const todayTodos=todos.filter(t=>!t.done&&t.dueDate===todayISO());
    const relM=sel.prepFor?meetings.find(m=>m.id===sel.prepFor):null;
    const relTodo=sel.todoId?todos.find(t=>t.id===sel.todoId):null;
    const prompt=`You are an AI operating system for an MSP technical lead (vCIO practice).
Focus: "${sel.title}" (${LAYERS[sel.layer].label} — ${sel.dur}min)
${relM?`Prep block for: ${relM.title} on ${fmtD(relM.date)} (${daysUntil(relM.date)} days).`:""}
${relTodo?`This is a meeting action item from notes: "${relTodo.notes||""}" Priority: ${relTodo.priority}.`:""}
Today's to-dos: ${todayTodos.map(t=>t.title).join(", ")||"none"}.
Pending cadence: ${overdue.join(", ")||"all clear"}.
2-3 sentence brief. Sharp, direct, MSP/vCIO context.`;
    try{
      const brief = onGenerateBrief ? await onGenerateBrief({currentTask:sel?.title,layerName:LAYERS[sel?.layer]?.label,duration:sel?.dur,relatedMeeting:relM?{title:relM.title,date:fmtD(relM.date),daysAway:daysUntil(relM.date)}:null,upcomingMeetings:meetings.slice(0,3).map(m=>`${m.client} ${m.type} in ${daysUntil(m.date)} days`),pendingCadence:overdue,todayTodos:todayTodos.map(t=>t.title),isPrepBlock:!!sel?.prepFor}) : null;
      setAiText(brief || "");
    }catch{setAiText(`${LAYERS[lc].label} layer active. ${relM?`Prepping for ${relM.client}.`:""} ${todayTodos.length} to-dos scheduled today.`);}
    finally{setAiLoad(false);}
  };

  const stopFocus=()=>{
    // Clear stash for current block when ending
    if(sel?.id!=null) delete timerStashRef.current[sel.id];
    setFocusing(false);setRunning(false);setSecs(0);setTotal(0);setEmailAlert(null);
  };

  // ─── Extract todos from notes ────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const extractTodos=async()=>{
    if(!notesText.trim())return;
    setExtracting(true);setExtracted([]);
    const today=todayISO();
    const prompt=`You are an MSP project assistant. Extract all action items from these meeting notes.
Meeting context: ${notesMeeting||"general client meeting"}
Today's date: ${today}

For each action item return a JSON array (no markdown, pure JSON) with objects:
{
  "title": "concise action title",
  "priority": "high"|"med"|"low",
  "suggestedDate": "YYYY-MM-DD (within next 14 days, high=1-2 days, med=3-5 days, low=7-14 days)",
  "layer": "S"|"T"|"O" (S=strategic/roadmap, T=tactical/reports/meetings, O=operational/technical),
  "client": "client name or Internal",
  "notes": "any extra context or detail from the notes"
}

Notes:
${notesText}`;
    try{
      const todos = onExtractTodos ? await onExtractTodos(notesText, notesMeeting) : [];
      setExtracted((todos||[]).map((item,i)=>({...item,id:`ext-${Date.now()}-${i}`,accepted:false,editedDate:item.suggestedDate})));
    }catch(e){
      setExtracted([{id:"ext-err",title:"Could not parse — check notes and retry",priority:"low",suggestedDate:today,layer:"O",client:"",notes:"",accepted:false,editedDate:today}]);
    }finally{setExtracting(false);}
  };

  const acceptExtracted=(item)=>{
    // Find the assigned meeting details if one is selected
    const assignedMtg=assignToMeeting?meetings.find(m=>m.id===assignToMeeting):null;
    const newTodo={
      id:`todo-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      title:item.title,
      priority:item.priority,
      dueDate:item.editedDate||item.suggestedDate,
      layer:item.layer,
      client:notesClient||item.client||assignedMtg?.client||"",
      notes:item.notes,
      done:false,
      source:notesMeeting||"Meeting notes",
      meetingRef:assignToMeeting||null,
      meetingTitle:assignedMtg?.title||selectedFellowMeeting?.title||notesMeeting||null,
      createdAt:new Date().toISOString(),
    };
    setTodos(t=>{const next=[...t,newTodo];saveSnapshot({todos:next});return next;});
    setExtracted(ex=>ex.map(e=>e.id===item.id?{...e,accepted:true}:e));
  };


  const toggleTodo=(id)=>setTodos(ts=>{const next=ts.map(t=>t.id===id?{...t,done:!t.done}:t);saveSnapshot({todos:next});return next;});

  const updateTodoNotes=(id,notes)=>setTodos(ts=>{const next=ts.map(t=>t.id===id?{...t,notes}:t);saveSnapshot({todos:next});return next;});
  const updateTodoPriority=(id,priority)=>setTodos(ts=>{const next=ts.map(t=>t.id===id?{...t,priority}:t);saveSnapshot({todos:next});return next;});
  const updateTodoDate=(id,dueDate)=>setTodos(ts=>{const next=ts.map(t=>t.id===id?{...t,dueDate}:t);saveSnapshot({todos:next});return next;});
  const deleteTodo=(id)=>{
    setTodos(ts=>{
      const nextTodos=ts.filter(t=>t.id!==id);
      setBlocks(bs=>{
        const nextBlocks=bs.filter(b=>b.todoId!==id);
        // Single save with both updated arrays — prevents race condition from two separate saves
        saveSnapshot({todos:nextTodos,blocks:nextBlocks});
        return nextBlocks;
      });
      return nextTodos;
    });
  };

  // ─────────── QUOTING HELPERS ───────────
  // Match an email address to an atCompanies entry using domain heuristics.
  const matchCompanyFromEmail = (emailAddr) => {
    if(!emailAddr) return null;
    const m = String(emailAddr).match(/@([^>\s]+)/);
    const domain = (m?m[1]:emailAddr).toLowerCase().trim();
    if(!domain) return null;
    // Try exact domain match against clientDomains, then company name word match
    const cd = clientDomains.find(c=>c.domain && domain.endsWith(c.domain));
    if(cd){
      const company = atCompanies.find(c=>String(c.id)===String(cd.id));
      return company ? {id:String(company.id), name:company.companyName} : null;
    }
    return null;
  };

  // ─────────── OPPORTUNITY HELPERS ───────────
  // Create a new opportunity from a dragged email. Returns the new opportunity.
  const createOpportunityFromEmail = (em) => {
    const matched = matchCompanyFromEmail(em.fromEmail||em.from);
    const opp = {
      id: `opp-${Date.now()}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      title: em.subj||"(no subject)",
      // Pipeline status is local to MSP OS — never written back to Autotask.
      // Deliberately NO `status` field: that is the dead pre-pipeline one (see
      // OPPORTUNITIES STATE). Setting it here would create a second status that
      // drifts out of sync the first time this deal is closed.
      pipelineStatus: "open",
      closedAt: null,
      companyId: matched?.id||"",
      companyName: matched?.name||"",
      estValue: 0,
      notes: "",
      emails: [{
        id: em.id||`em-${Date.now()}`,
        from: em.from||"",
        fromEmail: em.fromEmail||"",
        subj: em.subj||"",
        body: em.body||"",
        time: em.time||new Date().toISOString(),
        addedAt: Date.now(),
      }],
    };
    setOpportunities(prev=>{
      const next=[opp,...prev];
      saveSnapshot({opportunities:next});
      return next;
    });
    setSelectedOpportunityId(opp.id);
    return opp;
  };

  // Create a blank opportunity manually (no email)
  const createOpportunityManually = () => {
    const opp = {
      id: `opp-${Date.now()}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      title: "New opportunity",
      // No `status` field — see createOpportunityFromEmail above.
      pipelineStatus: "open",
      closedAt: null,
      companyId: "",
      companyName: "",
      estValue: 0,
      notes: "",
      emails: [],
    };
    setOpportunities(prev=>{
      const next=[opp,...prev];
      saveSnapshot({opportunities:next});
      return next;
    });
    setSelectedOpportunityId(opp.id);
  };

  // Append an email to an existing opportunity (drag onto an opp row)
  const addEmailToOpportunity = (oppId, em) => {
    const newEmail = {
      id: em.id||`em-${Date.now()}`,
      from: em.from||"",
      fromEmail: em.fromEmail||"",
      subj: em.subj||"",
      body: em.body||"",
      time: em.time||new Date().toISOString(),
      addedAt: Date.now(),
    };
    setOpportunities(prev=>{
      const next = prev.map(o=>{
        if(o.id!==oppId) return o;
        // De-dupe by source email id
        if(em.id && (o.emails||[]).some(e=>e.id===em.id)) return o;
        return {...o, emails:[...(o.emails||[]),newEmail], updatedAt:Date.now()};
      });
      saveSnapshot({opportunities:next});
      return next;
    });
  };

  // Patch an opportunity (title / company / estValue / notes / status)
  const updateOpportunity = (id, patch) => {
    setOpportunities(prev=>{
      const next = prev.map(o=>o.id===id?{...o,...patch,updatedAt:Date.now()}:o);
      saveSnapshot({opportunities:next});
      return next;
    });
  };

  const deleteOpportunity = (id) => {
    if(!window.confirm("Delete this opportunity? This cannot be undone.")) return;
    setOpportunities(prev=>{
      const next = prev.filter(o=>o.id!==id);
      saveSnapshot({opportunities:next});
      return next;
    });
    if(selectedOpportunityId===id) setSelectedOpportunityId(null);
  };

  // ── Create Proposals / Quotes FROM an opportunity ───────────────────────
  // The new record carries opportunityId, which is the whole point: the
  // opportunity's card turns orange the moment this lands, with no second step
  // to remember. Both handlers navigate to the record they just made — creating
  // something and staying put would look like nothing happened.

  // Requested proposal to open once the Proposals tab mounts. ProposalsTab owns
  // its own openId, so this is a request it consumes and clears rather than a
  // second copy of that state living up here.
  const [pendingProposalOpenId,setPendingProposalOpenId]=useState(null);
  const clearPendingProposalOpen = useCallback(()=>setPendingProposalOpenId(null),[]);

  const openProposal = (proposalId) => {
    setPendingProposalOpenId(proposalId);
    goTab("proposals");
  };

  const createProposalFromOpportunity = async (oppId) => {
    const opp = opportunities.find(o=>o.id===oppId);
    const templates = mergeTemplates(proposalTemplates);
    const active = templates.filter(t=>t.status==="active");
    if(!active.length){
      window.alert("No active proposal template — open the Proposal Templates tab first.");
      return;
    }
    // Bronze unless it is gone; a picker here would put a modal in front of a
    // one-click action, and the tier is changeable in the builder anyway.
    const template = active.find(t=>t.key==="bronze-sentinel") || active[0];
    const p = await makeProposalRecord(template, {
      salesRepName: userInfo?.name||userInfo?.email||"",
      extra: { opportunityId: oppId },
      // Pre-fill what the opportunity actually knows. customerName is the name
      // rendered across the whole document, so seeding it saves retyping.
      content: { customerName: opp?.companyName || "" },
    });
    setProposals(prev=>{
      const next=[...prev,p];
      saveSnapshot({proposals:next});
      return next;
    });
    openProposal(p.id);
  };

  const createQuoteFromOpportunity = async (oppId) => {
    const opp = opportunities.find(o=>o.id===oppId);
    // One factory for every quote — see quoteRecord.js. This was an
    // independent literal until the Q number needed to reach all three paths.
    const q = await makeQuoteRecord({
      termsDefault: quoteTermsDefault,
      opportunityId: oppId,
      companyId: opp?.companyId||"",
      companyName: opp?.companyName||"",
      title: opp?.title||"New quote",
    });
    setQuotes(prev=>{
      const next=[q,...prev];
      saveSnapshot({quotes:next});
      return next;
    });
    goTab("quoting");
    setEditingQuoteId(q.id);
  };

  const openQuote = (quoteId) => { goTab("quoting"); setEditingQuoteId(quoteId); };

  const removeEmailFromOpportunity = (oppId, emailId) => {
    setOpportunities(prev=>{
      const next = prev.map(o=>o.id===oppId
        ? {...o, emails:(o.emails||[]).filter(e=>e.id!==emailId), updatedAt:Date.now()}
        : o);
      saveSnapshot({opportunities:next});
      return next;
    });
  };
  // ─────────────────────────────────────────────

  // Add a backlog item from a dragged email
  const addQuoteRequestFromEmail = (em) => {
    const matched = matchCompanyFromEmail(em.fromEmail||em.from);
    const item = {
      id: `qbl-${Date.now()}`,
      createdAt: Date.now(),
      source: "email",
      sourceEmailId: em.id||null,
      subject: em.subj||"",
      body: em.body||"",
      from: em.from||"",
      fromEmail: em.fromEmail||"",
      companyId: matched?.id||"",
      companyName: matched?.name||"",
    };
    setQuoteBacklog(prev=>{
      // De-dupe by source email id
      if(em.id && prev.some(b=>b.sourceEmailId===em.id)){
        setQuoteToast({kind:"err",msg:"This email is already in the quote backlog"});
        setTimeout(()=>setQuoteToast(null),2500);
        return prev;
      }
      const next=[item,...prev];
      saveSnapshot({quoteBacklog:next});
      return next;
    });
    setQuoteToast({kind:"ok",msg:`Added to quote backlog${matched?` — matched ${matched.name}`:""}`});
    setTimeout(()=>setQuoteToast(null),2500);
  };

  // Add a manual backlog item (user fills it in from the builder)
  const addQuoteRequestManually = () => {
    const item = {
      id: `qbl-${Date.now()}`,
      createdAt: Date.now(),
      source: "manual",
      subject: "New quote request",
      body: "",
      from: "",
      fromEmail: "",
      companyId: "",
      companyName: "",
    };
    setQuoteBacklog(prev=>{
      const next=[item,...prev];
      saveSnapshot({quoteBacklog:next});
      return next;
    });
    // Ask how to start (blank · template · copy), then open the builder
    setTimeout(()=>startQuoteFromBacklog(item),50);
  };

  const deleteBacklogItem = (id) => {
    setQuoteBacklog(prev=>{
      const next = prev.filter(b=>b.id!==id);
      saveSnapshot({quoteBacklog:next});
      return next;
    });
  };

  /* Start a quote from a roadmap item (Reporting → Monthly Reports → Roadmap).
     ⚠ Craig-initiated and on demand — nothing about the roadmap creates quotes
     automatically. The mapping lives in quotes/roadmapQuote.js, which goes
     through the ONE quote factory rather than building a fourth literal. */
  const createQuoteFromRoadmapItem = async (item) => {
    const q = await makeQuoteFromRoadmapItem(item, {
      companyId: item.companyId || "",
      companyName: item.companyName || "",
    });

    /* ⛔ THE SERVER DECIDES, NOT US. The same action fires from the Meeting Notes
       section, and the button is clickable twice — so the roadmap blob holds the
       idempotency guard. We mint optimistically (the record is local and cheap),
       then ask the server to own it. If it says the item was ALREADY quoted we
       throw our record away rather than adding a second quote for the same work.
       ⚠ Only the server sends the email, and only on the first call. */
    let claim = null;
    try {
      claim = await createQuoteForItem(item.companyId, item.id, {
        quoteId: q.id,
        quoteNumber: q.quoteNumber,
        companyName: item.companyName || "",
      });
    } catch (e) {
      // A failed claim must not leave a quote nobody can trace back to an item.
      throw new Error(e?.response?.data?.error || "Could not record the quote against the roadmap item.");
    }
    if (claim?.alreadyQuoted) {
      const err = new Error("This item has already been quoted.");
      err.alreadyQuoted = true;
      err.linkedQuoteId = claim.linkedQuoteId;
      throw err;
    }

    setQuotes(prev => {
      const next = [q, ...prev];
      saveSnapshot({ quotes: next });
      return next;
    });
    /* ⚠ SURFACE IT, don't just create it. The builder modal is rendered INSIDE
       `{tab==="quoting" && …}`, so setting editingQuoteId from the Monthly
       Reports tab creates the quote and shows NOTHING — the button reads as
       broken. convertBacklogToQuote can skip this only because it is already on
       that tab. planQuoteOpen states the rule once, and is unit-tested, so a
       future entry point cannot quietly reintroduce the same bug. */
    const open = planQuoteOpen({ quoteId: q.id, currentTab: tab });
    setEditingQuoteId(open.editingQuoteId);
    if (open.tabChanged) setTab(open.tab);
    return q;
  };

  // Convert a backlog item into a draft quote and open the builder
  const convertBacklogToQuote = async (b, { sections = [], commonLineItems = [] } = {}) => {
    /* One factory for every quote — see quoteRecord.js. New quotes start
       structurally blank (no sections, no default option); the builder shows
       an empty state prompting "+ Section" or "🤖 AI assist". */
    const q = await makeQuoteRecord({
      termsDefault: quoteTermsDefault,
      backlogId: b.id,
      companyId: b.companyId||"",
      companyName: b.companyName||"",
      contactName: b.from||"",
      contactEmail: b.fromEmail||"",
      title: b.subject||"New quote",
      description: b.body||"",
      /* From a template: the structure the quote is CREATED with. Applied
         exactly once, here — not through the builder's additive apply. */
      sections,
      extra: { commonLineItems },
    });
    setQuotes(prev=>{
      const next=[q,...prev];
      saveSnapshot({quotes:next});
      return next;
    });
    // Remove from backlog
    setQuoteBacklog(prev=>{
      const next = prev.filter(x=>x.id!==b.id);
      saveSnapshot({quoteBacklog:next});
      return next;
    });
    setEditingQuoteId(q.id);
    return q;
  };

  // ─────────── START A QUOTE: blank · template · copy ───────────────────────
  /* The choice at creation. Templates already worked, but only from a dropdown
     inside a quote already created blank — by then you had committed to blank.
     Every path still goes through convertBacklogToQuote / duplicateQuote, i.e.
     through the ONE factory. */
  const startQuoteFromBacklog = (b) => setQuoteStartModal({backlogItem: b});
  const startQuoteChoice = async (how, refId) => {
    const b = quoteStartModal?.backlogItem;
    setQuoteStartModal(null);
    if(!b) return;
    try{
      if(how==="template"){
        const t = quoteTemplates.find(x=>x.id===refId);
        /* Applied exactly ONCE, as the structure the quote is created with —
           not via applyTemplate, which is additive by design and which looks
           the quote up in `quotes` state the new record is not yet in. */
        await convertBacklogToQuote(b, structureFromTemplate(t));
        return;
      }
      if(how==="copy"){
        const source = quotes.find(x=>x.id===refId);
        if(!source) return;
        /* The Part A copy, plus what the backlog item itself knows: it is the
           request being answered, so its client, contact and subject win. */
        const copy = await duplicateQuote(ensureMigrated(source), {
          termsDefault: quoteTermsDefault,
          overrides: {
            backlogId: b.id,
            ...(b.companyId ? {companyId: b.companyId, companyName: b.companyName||""} : {}),
            ...(b.from ? {contactName: b.from} : {}),
            ...(b.fromEmail ? {contactEmail: b.fromEmail} : {}),
            ...(b.subject && b.subject!=="New quote request" ? {title: b.subject} : {}),
            ...(b.body ? {description: b.body} : {}),
          },
        });
        setQuotes(prev=>{ const next=[copy,...prev]; saveSnapshot({quotes:next}); return next; });
        setQuoteBacklog(prev=>{ const next=prev.filter(x=>x.id!==b.id); saveSnapshot({quoteBacklog:next}); return next; });
        setEditingQuoteId(copy.id);
        return;
      }
      await convertBacklogToQuote(b);
    }catch(e){ console.error("Quote start failed", e); }
  };

  /* Duplicate a quote: an ALLOWLISTED copy through the ONE factory —
     quoteTemplates.duplicateQuote has the reasoning; never a spread of the
     source. Awaited, because the Q number comes from the server counter and
     the builder must not open before the record exists. No confirm: a draft is
     harmless and reversible (deleteQuote confirms because deleting is not). */
  const duplicateQuoteById = async (id) => {
    const source = quotes.find(x=>x.id===id);
    if(!source) return;
    try{
      const copy = await duplicateQuote(ensureMigrated(source), {termsDefault: quoteTermsDefault});
      setQuotes(prev=>{ const next=[copy,...prev]; saveSnapshot({quotes:next}); return next; });
      setEditingQuoteId(copy.id);
      setQuoteToast({kind:"ok",msg:`Duplicated as ${copy.quoteNumber!=null?formatQuoteNumber(copy.quoteNumber):"a new draft"}`});
      setTimeout(()=>setQuoteToast(null),2500);
    }catch(e){
      console.error("Duplicate failed", e);
      setQuoteToast({kind:"err",msg:"Couldn't duplicate that quote"});
      setTimeout(()=>setQuoteToast(null),2500);
    }
  };

  // ─────────── QUOTE MIGRATION (→ Sections > Options > Line Items) ────────────
  // Three historic shapes migrate forward into q.sections[]:
  //   1. flat q.lineItems          (pre-Options)  → one section, one option
  //   2. q.options[] + common      (Options era)  → one section wrapping the options
  //   3. q.sections[]              (current)      → untouched
  // One-way and lazy: runs when a quote is first read after this deploy. The
  // deprecated fields are set to undefined so JSON.stringify drops them on the
  // next saveSnapshot — that is what makes the migration stick.
  /* ⚠ SEPARATE FROM THE SHAPE MIGRATION, and it has to be. migrateQuoteShape
     early-returns on `Array.isArray(q.sections)`, which is true of nearly
     every stored quote — so terms seeded inside its branches would reach only
     the handful that still predate sections, and every current quote would
     render with no terms at all. The seeding is applied to the RESULT instead,
     whichever branch produced it.

     withQuoteTerms returns the SAME object when terms are already present, so
     this stays free: ensureMigrated runs on every read, and a fresh reference
     each time would give React a changed quote on every render and make the
     debounced save fire forever. */
  /* withFulfilment wraps it for exactly the same reason, and keeps the same
     same-reference contract. Never move it inside shapeMigrateQuote.
     withLineItemDetail (the per-line customer-facing `detail` backfill) is
     the third wrapper — same contract, same reason. */
  const migrateQuoteShape = (q) => withLineItemDetail(withFulfilment(withQuoteTerms(shapeMigrateQuote(q), quoteTermsDefault)));

  const shapeMigrateQuote = (q) => {
    if (Array.isArray(q.sections)) return q; // already migrated

    // Options era → wrap the whole option set in one default section.
    if (Array.isArray(q.options)) {
      return {
        ...q,
        sections: [{
          id: `sec-${Date.now()}-migrated`,
          label: "Quote Options",
          description: "",
          selectedOptionId: q.selectedOptionId || null,
          createdByAI: false,
          options: q.options,
        }],
        commonLineItems: q.commonLineItems || [],
        options: undefined,
        selectedOptionId: undefined,
      };
    }

    // Pre-Options era → flat lineItems become one section / one option.
    // Guard on length: a brand-new quote carries `lineItems: []` and must stay
    // structurally blank (per the blank-new-quote rule) rather than gaining an
    // empty "Default" option nobody asked for.
    if (Array.isArray(q.lineItems) && q.lineItems.length > 0) {
      return {
        ...q,
        sections: [{
          id: `sec-${Date.now()}-migrated`,
          label: "Quote Options",
          description: "",
          selectedOptionId: null,
          createdByAI: false,
          options: [{
            id: `opt-${Date.now()}-default`,
            label: "Default",
            description: "",
            lineItems: q.lineItems,
          }],
        }],
        commonLineItems: q.commonLineItems || [],
        lineItems: undefined,
      };
    }

    // Bare / brand-new quote — no structure at all.
    return {
      ...q,
      sections: [],
      commonLineItems: q.commonLineItems || [],
      lineItems: undefined,
    };
  };

  // Ensure a quote is migrated before mutating it. Returns the (possibly new) quote.
  /* ⚠ NO `sections` SHORT-CIRCUIT ANY MORE. It used to return early on an
     already-migrated quote, which would now skip the terms backfill for every
     quote in the system. migrateQuoteShape is cheap and idempotent, and it
     returns the same reference when there is nothing to do. */
  const ensureMigrated = (q) => (q ? migrateQuoteShape(q) : q);

  // Count options across every section — used by the header and backlog cards.
  const sumOptions = (sections) => (sections || []).reduce((n, s) => n + (s.options || []).length, 0);

  // ─────────── TOTALS (Options + Common aware) ───────────
  // Returns: { optionTotals: [{id,label,subtotal,totalCost,...}], commonSubtotal,
  //           commonTotalCost, total, totalCost, profit, marginPct, markupPct }
  //   - total = (selectedOption's subtotal OR if none, the first option's subtotal as preview) + commonSubtotal
  // Per-line subtotal also respects "optional + not ticked" — but in the builder
  // (this function) we count everything because the builder is for you, not the customer.
  // The customer-side render (stage 4) will apply the toggle logic.
  /* ⚠ TWO STREAMS, ONE PASS. `subtotal` / `totalCost` are still the sum of
     EVERY line — the number the backend's parity test pins. `onceOff` and
     `recurring` are the same money split by the line's period (quotePeriod.js
     — the SHARED rule, imported, never restated), each with a `count` of the
     lines that fed it, so a $0.00 monthly line still reads as monthly. */
  const computeLineSubtotals = (items) => {
    let subtotal = 0, totalCost = 0;
    const onceOff   = { subtotal: 0, totalCost: 0, count: 0 };
    const recurring = { subtotal: 0, totalCost: 0, count: 0 };
    (items || []).forEach(li => {
      /* ⚠ quantityOf, NOT `Number(li.quantity) || 0`. This function was the one
         caller the quantity fix missed, and it is the caller that feeds the
         totals PANEL — so the panel and the document preview beside it were
         pricing the same line two different ways:

             quantityOf("")            -> 1     what the document prints
             Number("") || 0           -> 0     what the panel showed

         And "" is not hypothetical: the builder writes it into the field the
         moment a quantity is cleared, mid-edit. Clearing a quantity made the two
         numbers on screen disagree — worse than the original bug, which was at
         least consistent between them.

         ⚠ It is deliberately the SHARED helper, imported rather than restated.
         quoteRenderer.js carries a ported copy for the server side and a test
         pins the two together; a third local coercion here is exactly how they
         drift apart again. */
      const qty   = quantityOf(li);
      const price = Number(li.unitPrice) || 0;
      const cost  = Number(li.unitCost)  || 0;
      subtotal  += qty * price;
      totalCost += qty * cost;
      const stream = periodOf(li) ? recurring : onceOff;
      stream.subtotal  += qty * price;
      stream.totalCost += qty * cost;
      stream.count     += 1;
    });
    return { subtotal, totalCost, onceOff, recurring };
  };

  /** A stream summed across sections. Pure addition, in document order. */
  const sumStreams = (parts) => parts.reduce((a, p) => ({
    subtotal:  a.subtotal  + (p ? p.subtotal  : 0),
    totalCost: a.totalCost + (p ? p.totalCost : 0),
    count:     a.count     + (p ? p.count     : 0),
  }), { subtotal: 0, totalCost: 0, count: 0 });
  const EMPTY_STREAM = () => ({ subtotal: 0, totalCost: 0, count: 0 });

  // Totals roll up Section → Option → Line Item. Each section contributes ONE
  // option's worth of money (the customer picks one per section); common items
  // always add on top.
  /* ⛔ `total` ADDS A MONTHLY CHARGE TO A ONCE-OFF ONE. It stays because it is
     the parity number the backend pins and because every quote without a
     Recurring line is unaffected. It is NOT a figure the panel may show once
     `hasRecurring` is true — $500 a month plus a $499 device is not $999 of
     anything. The panel shows `onceOff` and `recurring`, and so does the
     customer document; one rule, both sides. */
  const computeQuoteTotals = (qIn) => {
    const q = ensureMigrated(qIn);
    const common = computeLineSubtotals(q.commonLineItems);
    const { subtotal: commonSubtotal, totalCost: commonTotalCost } = common;

    const sectionTotals = (q.sections || []).map(s => {
      const optionTotals = (s.options || []).map(opt => {
        const { subtotal, totalCost, onceOff, recurring } = computeLineSubtotals(opt.lineItems);
        return {
          id: opt.id,
          label: opt.label || "(unnamed option)",
          subtotal,
          totalCost,
          onceOff,
          recurring,
          profit: subtotal - totalCost,
          marginPct: subtotal > 0 ? ((subtotal - totalCost) / subtotal) * 100 : 0,
          isSelected: s.selectedOptionId === opt.id,
        };
      });
      // No explicit pick yet → preview the first option so the builder shows a
      // working number instead of $0 while the quote is still being assembled.
      const previewOpt = s.selectedOptionId
        ? optionTotals.find(o => o.id === s.selectedOptionId)
        : optionTotals[0];
      return {
        id: s.id,
        label: s.label || "(unnamed section)",
        selectedOptionId: s.selectedOptionId || null,
        previewOptionId: previewOpt?.id || null,
        previewOptionLabel: previewOpt?.label || "",
        sectionSubtotal:  previewOpt?.subtotal  || 0,
        sectionTotalCost: previewOpt?.totalCost || 0,
        sectionOnceOff:   previewOpt?.onceOff   || EMPTY_STREAM(),
        sectionRecurring: previewOpt?.recurring || EMPTY_STREAM(),
        optionTotals,
      };
    });

    const subtotal  = sectionTotals.reduce((acc, s) => acc + s.sectionSubtotal,  0) + commonSubtotal;
    const totalCost = sectionTotals.reduce((acc, s) => acc + s.sectionTotalCost, 0) + commonTotalCost;
    const profit    = subtotal - totalCost;

    // True once every section has an explicit pick — lets the panel say
    // "Total (selected)" rather than "Total (preview)".
    const allSectionsSelected = sectionTotals.length > 0
      && sectionTotals.every(s => !!s.selectedOptionId);

    /* Each stream rolled up the same way the combined figure is: the counted
       option per section, plus common. Profit and margin per stream, because a
       margin on "$999" of mixed money is as meaningless as the $999. */
    const stream = (pick, commonPart, period) => {
      const all = sumStreams([...sectionTotals.map(pick), commonPart]);
      const p = all.subtotal - all.totalCost;
      return {
        period,
        subtotal: all.subtotal,
        totalCost: all.totalCost,
        profit: p,
        marginPct: all.subtotal > 0 ? (p / all.subtotal) * 100 : 0,
        markupPct: all.totalCost > 0 ? (p / all.totalCost) * 100 : 0,
        lineCount: all.count,
      };
    };
    const onceOff   = stream(s => s.sectionOnceOff,   common.onceOff,   null);
    const recurring = stream(s => s.sectionRecurring, common.recurring, MONTHLY);

    return {
      sectionTotals,
      allSectionsSelected,
      commonSubtotal,
      commonTotalCost,
      commonOnceOff: common.onceOff,
      commonRecurring: common.recurring,
      subtotal,
      total: subtotal,
      totalCost,
      profit,
      marginPct: subtotal > 0 ? (profit / subtotal) * 100 : 0,
      markupPct: totalCost > 0 ? (profit / totalCost) * 100 : 0,
      onceOff,
      recurring,
      hasRecurring: recurring.lineCount > 0,
      hasOnceOff: onceOff.lineCount > 0,
    };
  };

  // Patch a quote
  const updateQuote = (id, patch) => {
    setQuotes(prev=>{
      const next = prev.map(q=>q.id===id?{...ensureMigrated(q),...patch,updatedAt:Date.now()}:q);
      saveSnapshot({quotes:next});
      return next;
    });
  };

  // ─────────── TERMS & CONDITIONS ───────────
  /* One writer for every clause operation, in the updater form. Two
     sequential setQuotes calls computed from the same closure would discard
     the first — the ProposalsTab company-picker bug, recorded in CLAUDE.md.

     ⚠ The RULES live in quoteTerms.js, not here. removeClause refuses to drop
     the required clause and moveClause refuses to move it, so those are not
     merely disabled buttons: anything that reaches the array by another route
     still cannot break them. */
  const patchQuoteTerms = (id, fn) => {
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if (q.id!==id) return q;
        const m = ensureMigrated(q);
        return {...m, terms: fn(m.terms||[]), updatedAt:Date.now()};
      });
      saveSnapshot({quotes:next});
      return next;
    });
  };
  const addQuoteClause     = (id)            => patchQuoteTerms(id, t=>addClause(t,""));
  const updateQuoteClause  = (id, cid, text) => patchQuoteTerms(id, t=>updateClause(t,cid,text));
  const removeQuoteClause  = (id, cid)       => patchQuoteTerms(id, t=>removeClause(t,cid));
  const moveQuoteClause    = (id, cid, d)    => patchQuoteTerms(id, t=>moveClause(t,cid,d));
  /* Restores from what Settings says TODAY, which is the point of the button:
     the snapshot is the default behaviour, and this is the deliberate way
     back to current wording after a term was negotiated. */
  const restoreQuoteClause = (id)            => patchQuoteTerms(id, t=>restoreDefaultClause(t, quoteTermsDefault));

  // ─────────── SECTION-LEVEL OPS ───────────
  const newSectionId = () => `sec-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
  const newOptionId  = () => `opt-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;

  // Shared writer: map over one quote's sections and persist. Every section and
  // option mutation goes through here so saveSnapshot is never forgotten.
  const countSectionLineItems = (section) =>
    (section?.options || []).reduce((n,o) => n + (o.lineItems||[]).length, 0);

  const patchQuoteSections = (quoteId, fn) => {
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if(q.id!==quoteId) return q;
        const m = ensureMigrated(q);
        const sections = fn(m);
        if (sections === m.sections) return m; // no-op guard (e.g. move past an edge)
        return {...m, sections, updatedAt: Date.now()};
      });
      saveSnapshot({quotes:next});
      return next;
    });
  };

  const addSection = (quoteId, createdByAI = false) => {
    const sec = {
      id: newSectionId(),
      label: "New Section",
      description: "",
      selectedOptionId: null,
      createdByAI,
      options: [],
    };
    /* Above freight, which is always last (quoteSectionOrder.js). The builder
       and the document order at render time anyway; keeping the stored order
       tidy just means the blob reads the way the page does. */
    patchQuoteSections(quoteId, m => [
      ...m.sections.filter(s => !isFreightSection(s)), sec, ...m.sections.filter(isFreightSection),
    ]);
    // Focus the new section so the next AI/template add targets it.
    setFocusedLocation({sectionId: sec.id, optionId: null});
    return sec.id;
  };

  const updateSection = (quoteId, sectionId, patch) =>
    patchQuoteSections(quoteId, m => m.sections.map(s => s.id===sectionId ? {...s, ...patch} : s));

  const deleteSection = (quoteId, sectionId) => {
    patchQuoteSections(quoteId, m => m.sections.filter(s => s.id!==sectionId));
    if (focusedLocation?.sectionId === sectionId) setFocusedLocation(null);
  };

  // Arrow-button reorder (accessibility fallback for drag). direction: -1 | +1
  /* ⚠ BOTH MOVES OPERATE ON THE NON-FREIGHT LIST. Freight is pinned last
     (quoteSectionOrder.js — the document applies the same rule), so it is
     never moved and never moved past: the builder passes indices over the
     main sections, and freight is re-appended after them every time. */
  const moveSection = (quoteId, sectionId, direction) => {
    patchQuoteSections(quoteId, m => {
      const main = m.sections.filter(s => !isFreightSection(s));
      const freight = m.sections.filter(isFreightSection);
      const idx = main.findIndex(s => s.id===sectionId);
      const to  = idx + direction;
      if (idx < 0 || to < 0 || to >= main.length) return m.sections; // at an edge, or freight
      const next = [...main];
      [next[idx], next[to]] = [next[to], next[idx]];
      return [...next, ...freight];
    });
  };

  // Drag-and-drop reorder (primary path).
  const reorderSections = (quoteId, fromIdx, toIdx) => {
    patchQuoteSections(quoteId, m => {
      const main = m.sections.filter(s => !isFreightSection(s));
      const freight = m.sections.filter(isFreightSection);
      const n = main.length;
      if (fromIdx < 0 || fromIdx >= n || toIdx < 0 || toIdx >= n || fromIdx === toIdx) return m.sections;
      const next = [...main];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return [...next, ...freight];
    });
  };

  // Which option this section resolves to when the customer views the quote.
  const selectOption = (quoteId, sectionId, optionId) => {
    patchQuoteSections(quoteId, m => m.sections.map(s =>
      s.id===sectionId ? {...s, selectedOptionId: optionId} : s));
    // Freight is charged on the selected option, so picking a different one
    // can change the total.
    scheduleFreightSync(quoteId);
  };

  const confirmAndDeleteSection = (quoteId, sectionId) => {
    const quote = quotes.find(q => q.id===quoteId);
    if(!quote) return;
    const section = ensureMigrated(quote).sections.find(s => s.id===sectionId);
    if(!section) return;
    const optCount  = (section.options || []).length;
    const itemCount = countSectionLineItems(section);
    if (itemCount > 0 && !window.confirm(
      `Delete section "${section.label||"(unnamed)"}"? This removes ${itemCount} line item${itemCount===1?"":"s"} across ${optCount} option${optCount===1?"":"s"}.`
    )) return;
    deleteSection(quoteId, sectionId);
  };

  // ─────────── OPTION-LEVEL OPS (scoped to a section) ───────────
  const addOption = (quoteId, sectionId) => {
    const optId = newOptionId();
    patchQuoteSections(quoteId, m => m.sections.map(s => {
      if (s.id!==sectionId) return s;
      // Label new options A, B, C… by position within THIS section.
      const letter = String.fromCharCode(65 + (s.options||[]).length);
      const opt = {id: optId, label: `Option ${letter}`, description: "", lineItems: []};
      return {
        ...s,
        options: [...(s.options||[]), opt],
        // First option in a section becomes the selection by default.
        selectedOptionId: s.selectedOptionId || optId,
      };
    }));
    setFocusedLocation({sectionId, optionId: optId});
    return optId;
  };

  const updateOption = (quoteId, sectionId, optionId, patch) =>
    patchQuoteSections(quoteId, m => m.sections.map(s => s.id!==sectionId ? s : {
      ...s,
      options: (s.options||[]).map(o => o.id===optionId ? {...o, ...patch} : o),
    }));

  const deleteOption = (quoteId, sectionId, optionId) => {
    patchQuoteSections(quoteId, m => m.sections.map(s => {
      if (s.id!==sectionId) return s;
      const remaining = (s.options||[]).filter(o => o.id!==optionId);
      return {
        ...s,
        options: remaining,
        // Deleting the selected option promotes the next one rather than
        // leaving the section with no pick at all.
        selectedOptionId: s.selectedOptionId === optionId
          ? (remaining[0]?.id || null)
          : s.selectedOptionId,
      };
    }));
    if (focusedLocation?.sectionId === sectionId && focusedLocation?.optionId === optionId) {
      setFocusedLocation({sectionId, optionId: null});
    }
  };

  const confirmAndDeleteOption = (quoteId, sectionId, optionId) => {
    const quote = quotes.find(q => q.id===quoteId);
    if(!quote) return;
    const opt = ensureMigrated(quote).sections.find(s => s.id===sectionId)?.options.find(o => o.id===optionId);
    if(!opt) return;
    const n = (opt.lineItems||[]).length;
    if (n > 0 && !window.confirm(`Delete "${opt.label||"this option"}" and its ${n} line item${n===1?"":"s"}?`)) return;
    deleteOption(quoteId, sectionId, optionId);
  };

  // ─────────── LINE ITEM OPS (Options or Common) ───────────
  // location: "common" or an option id
  const makeNewLineItem = (type = "hardware") => ({
    id: `li-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
    type,
    description: "",
    /* CUSTOMER-FACING long-form detail, shown under the description on the PDF and
       the web quote. ⛔ NOT supplierNotes — that one is internal and never rendered.
       Backfilled on read by withLineItemDetail (quotes/quoteLineDetail.js). */
    detail: "",
    quantity: 1,
    unitCost: 0,
    unitPrice: 0,
    // Customer-side editability (no UI yet — stage 4)
    customerCanEditQty: false,
    customerCanToggle: false,
    // Supplier/ordering fields.
    // supplierId is the stable key the freight calculator groups by; `supplier`
    // stays the human-facing name. The backend falls back to matching the name
    // for items created before supplierId existed, but new items carry the id
    // so a supplier rename in Settings can't orphan them.
    supplierId: "",
    supplier: "",
    supplierSku: "",
    supplierUrl: "",
    supplierNotes: "",
    // Shipping weight, when the catalog knew it. null = let the backend resolve.
    weightKg: null,
    weightSource: null,
    // Image
    imageUrl: "",
  });

  // Apply `fn(items) => items` to whichever list `location` points at.
  // location: "common" | {sectionId, optionId}. Everything below is a thin
  // wrapper over this so the common/section branching lives in one place.
  const patchLineItemsAt = (quoteId, location, fn) => {
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if(q.id!==quoteId) return q;
        const m = ensureMigrated(q);
        if (location === "common") {
          return {...m, commonLineItems: fn(m.commonLineItems || []), updatedAt: Date.now()};
        }
        const {sectionId, optionId} = location || {};
        if (!sectionId || !optionId) return m; // unresolved focus — drop the write
        return {
          ...m,
          sections: (m.sections || []).map(s => s.id !== sectionId ? s : {
            ...s,
            options: (s.options || []).map(o => o.id !== optionId ? o : {
              ...o,
              lineItems: fn(o.lineItems || []),
            }),
          }),
          updatedAt: Date.now(),
        };
      });
      saveSnapshot({quotes:next});
      return next;
    });
  };

  // Fields that change what freight costs. Editing a description or a note
  // shouldn't fire a backend recompute that can reach the AI.
  const FREIGHT_RELEVANT_FIELDS = ["quantity", "unitCost", "sku", "supplierId", "supplier", "weightKg"];

  const addLineItem = (quoteId, location, type = "hardware") => {
    patchLineItemsAt(quoteId, location, items => [...items, makeNewLineItem(type)]);
    scheduleFreightSync(quoteId);
  };

  const updateLineItem = (quoteId, location, lineId, patch) => {
    patchLineItemsAt(quoteId, location, items =>
      items.map(li => li.id === lineId ? {...li, ...patch} : li));
    if (Object.keys(patch || {}).some(k => FREIGHT_RELEVANT_FIELDS.includes(k))) {
      scheduleFreightSync(quoteId);
    }
  };

  const deleteLineItem = (quoteId, location, lineId) => {
    patchLineItemsAt(quoteId, location, items => items.filter(li => li.id !== lineId));
    scheduleFreightSync(quoteId);
  };

  const deleteQuote = (id) => {
    if(!window.confirm("Delete this quote? This cannot be undone.")) return;
    setQuotes(prev=>{
      const next = prev.filter(q=>q.id!==id);
      saveSnapshot({quotes:next});
      return next;
    });
    setEditingQuoteId(null);
  };

  // Locations are now objects ({sectionId, optionId}) rather than bare option-id
  // strings, so identity comparison no longer works — every focus check goes
  // through this.
  const isSameLocation = (a, b) => {
    if (a === "common" || b === "common") return a === b;
    if (!a || !b) return false;
    return a.sectionId === b.sectionId && a.optionId === b.optionId;
  };

  // Helper — return the line items at a given location of a quote
  const getLineItemsAt = (q, location) => {
    const m = ensureMigrated(q);
    if (location === "common") return m.commonLineItems || [];
    const section = (m.sections || []).find(s => s.id === location?.sectionId);
    return section?.options?.find(o => o.id === location?.optionId)?.lineItems || [];
  };

  // Helper — total count of line items across every section/option + common
  const countAllLineItems = (q) => {
    const m = ensureMigrated(q);
    return (m.commonLineItems || []).length +
      (m.sections || []).reduce((sum, s) =>
        sum + (s.options || []).reduce((n, o) => n + (o.lineItems || []).length, 0), 0);
  };

  // Helper — every line item flattened, each tagged with the location that owns
  // it. Shape is {location, locationLabel, lineItem} — the "Replace existing
  // line…" dropdown in the AI chat renders straight off this.
  const getAllLineItemRefs = (q) => {
    const m = ensureMigrated(q);
    const refs = [];
    (m.commonLineItems || []).forEach(li =>
      refs.push({location: "common", locationLabel: "Common", lineItem: li}));
    (m.sections || []).forEach(s => {
      (s.options || []).forEach(o => {
        (o.lineItems || []).forEach(li => refs.push({
          location: {sectionId: s.id, optionId: o.id},
          locationLabel: `${s.label || "Section"} › ${o.label || "Option"}`,
          lineItem: li,
        }));
      });
    });
    return refs;
  };

  // Helper — where should the next AI/template/manual add land?
  // Prefers the explicit focus, but re-validates it against the current quote so
  // a deleted section/option can never strand writes at a dead location.
  const resolveLocation = (q) => {
    const m = ensureMigrated(q);
    if (focusedLocation === "common") return "common";
    if (focusedLocation?.sectionId && focusedLocation?.optionId) {
      const section = (m.sections || []).find(s => s.id === focusedLocation.sectionId);
      if (section?.options?.some(o => o.id === focusedLocation.optionId)) return focusedLocation;
    }
    // Fall back to the first real option anywhere in the quote, else Common.
    for (const s of (m.sections || [])) {
      if (s.options?.[0]) return {sectionId: s.id, optionId: s.options[0].id};
    }
    return "common";
  };

  /* "Save as template" — asks which of THREE things to save (whole quote ·
     this section · these items) in QuoteTemplateSaveModal, then
     saveQuoteTemplate builds it. A window.confirm could only express two, so
     the mode used to be inferred from where the cursor happened to be — and
     there was NO path that saved more than one section, however many the
     quote had. The template builders live in quoteTemplates.js. */
  const saveCurrentAsTemplate = (quote) => {
    const q = ensureMigrated(quote);
    const focusedSec = focusedLocation?.sectionId
      ? (q.sections||[]).find(s => s.id === focusedLocation.sectionId)
      : null;
    // Whole quote — every non-freight section, plus the common items.
    const realSections = (q.sections||[]).filter(s=>!isFreightSection(s));
    const wholeItems = realSections.reduce((n,s)=>n+countSectionLineItems(s),0);
    const commons = (q.commonLineItems||[]).length;
    const whole = (wholeItems + commons) > 0
      ? { sections: realSections.length, items: wholeItems, commons, freightExcluded: (q.sections||[]).some(isFreightSection) }
      : null;
    // This section — the focused section, when it has real structure (2+ options):
    // "Turret Cameras: G4 Pro | G5 Pro" is the reusable unit, not one option's items.
    const section = focusedSec && (focusedSec.options||[]).length >= 2
      ? { id: focusedSec.id, label: focusedSec.label, options: focusedSec.options.length, items: countSectionLineItems(focusedSec) }
      : null;
    // These items — whatever has focus. Same guard as before: a focused section
    // with no options must not resolve to some OTHER section's first option,
    // which would silently offer the wrong items.
    let items = null;
    if (!(focusedSec && (focusedSec.options||[]).length === 0)) {
      const location = resolveLocation(q);
      const list = getLineItemsAt(q, location);
      if (list.length) {
        const locLabel = location === "common"
          ? "Common"
          : getAllLineItemRefs(q).find(r => isSameLocation(r.location, location))?.locationLabel
            || (q.sections || []).find(s => s.id === location.sectionId)?.label
            || "Option";
        items = { location, label: locLabel, count: list.length };
      }
    }
    if (!whole && !section && !items) {
      setQuoteToast({kind:"err", msg:"This quote has no line items to save"});
      setTimeout(()=>setQuoteToast(null), 2500);
      return;
    }
    setQuoteTemplateModal({ quoteId: q.id, choices: { quoteTitle: quote.title || "Template", whole, section, items } });
  };

  const saveQuoteTemplate = (quoteId, mode, name) => {
    const quote = quotes.find(x=>x.id===quoteId);
    const choices = quoteTemplateModal?.choices;
    setQuoteTemplateModal(null);
    if(!quote || !choices || !name) return;
    const q = ensureMigrated(quote);
    let t = null, what = "";
    if (mode === "whole") {
      t = buildWholeQuoteTemplate(q, name);
      what = `whole quote, ${t.sections.length} section${t.sections.length===1?"":"s"}`;
    } else if (mode === "section" && choices.section) {
      const sec = (q.sections||[]).find(s=>s.id===choices.section.id);
      if (sec) { t = buildSectionTemplate(q, sec, name); what = `section "${sec.label}"`; }
    } else if (mode === "items" && choices.items) {
      t = buildItemsTemplate(q, getLineItemsAt(q, choices.items.location), name);
      what = `${t.lineItems.length} item${t.lineItems.length===1?"":"s"}`;
    }
    if(!t) return;
    setQuoteTemplates(prev=>{ const next=[t,...prev]; saveSnapshot({quoteTemplates:next}); return next; });
    setQuoteToast({kind:"ok",msg:`Saved template "${name}" — ${what}`});
    setTimeout(()=>setQuoteToast(null),2500);
  };

  // Template shapes ("flat" / "sections"), isSectionTemplate and
  // templateItemCount live in quotes/quoteTemplates.js and are imported above.

  const applyTemplate = (quoteId, templateId) => {
    const t = quoteTemplates.find(x=>x.id===templateId);
    if(!t) return;

    // Sectioned: append the tree with fresh ids at every level. Never touches
    // existing sections, so this is additive and non-destructive.
    if (isSectionTemplate(t)) {
      // THE clone — fresh ids at every level, selection resolved against the
      // clones — is quoteTemplates.cloneSections, shared with duplicateQuote.
      const cloned = cloneSections(t.sections);
      patchQuoteSections(quoteId, m => [...m.sections, ...cloned]);
      // Common items ride along the same way — APPENDED, never replacing, and
      // absent on every template saved before whole-quote templates existed.
      const commons = cloneLineItems(templateCommons(t));
      if (commons.length) patchLineItemsAt(quoteId, "common", items => [...items, ...commons]);
      setQuoteToast({kind:"ok",msg:`Added ${cloned.length} section${cloned.length===1?"":"s"} from "${t.name}"`});
      setTimeout(()=>setQuoteToast(null),2500);
      return;
    }

    const quote = quotes.find(x=>x.id===quoteId);
    if(!quote) return;
    const m = ensureMigrated(quote);
    let location = resolveLocation(m);
    // Focused a section that has no options yet → give it one to receive the
    // items, rather than letting them land in an unrelated section.
    const focusedSec = focusedLocation?.sectionId
      ? (m.sections||[]).find(s => s.id === focusedLocation.sectionId)
      : null;
    if (focusedSec && (focusedSec.options||[]).length === 0) {
      location = {sectionId: focusedSec.id, optionId: addOption(quoteId, focusedSec.id)};
    }
    // A blank quote has nowhere to put them — Common would silently swallow the
    // items where the user expects a section.
    if (location === "common" && (m.sections || []).length === 0
        && !window.confirm(`This quote has no sections yet. Add "${t.name}" (${t.lineItems.length} items) to Common items?`)) {
      return;
    }
    const newLis = t.lineItems.map(li=>({
      ...li,
      id: `li-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
    }));
    patchLineItemsAt(quoteId, location, items => [...items, ...newLis]);
    setQuoteToast({kind:"ok",msg:`Applied template "${t.name}"`});
    setTimeout(()=>setQuoteToast(null),2500);
  };

  const renameTemplate = (id) => {
    const t = quoteTemplates.find(x=>x.id===id);
    if(!t) return;
    const name = window.prompt("Template name:", t.name);
    if(!name || !name.trim() || name.trim()===t.name) return;
    setQuoteTemplates(prev=>{
      const next = prev.map(x=>x.id===id?{...x,name:name.trim()}:x);
      saveSnapshot({quoteTemplates:next});
      return next;
    });
  };

  const deleteTemplate = (id) => {
    if(!window.confirm("Delete this template?")) return;
    setQuoteTemplates(prev=>{
      const next = prev.filter(t=>t.id!==id);
      saveSnapshot({quoteTemplates:next});
      return next;
    });
  };

  // Backend actions — POST to the API. Returns parsed JSON on 2xx, null otherwise.
  // Toasts are surfaced for both errors and success, so callers don't need to.
  const callQuoteBackend = async (path, body, successMsg) => {
    try{
      const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
      const res = await fetch(`${_apiBaseUrl()}${path}`, {
        method: "POST",
        headers: {
          "Content-Type":"application/json",
          ...(token?{Authorization:`Bearer ${token}`}:{}),
        },
        body: JSON.stringify(body||{}),
      });
      if(res.status===404){
        setQuoteToast({kind:"err",msg:`Backend route not yet wired: ${path}`});
      } else if(!res.ok){
        const txt = await res.text().catch(()=>"");
        setQuoteToast({kind:"err",msg:`Failed (${res.status}): ${txt.slice(0,80)||"server error"}`});
      } else {
        const data = await res.json().catch(()=>({}));
        setQuoteToast({kind:"ok",msg:successMsg||"Done"});
        return data;
      }
    } catch(err){
      setQuoteToast({kind:"err",msg:`Network error: ${err.message}`});
    } finally {
      setTimeout(()=>setQuoteToast(null),3000);
    }
    return null;
  };

  // ─── Supplier catalog management (generic — Leader, Dicker, future) ────────
  const _apiBaseUrl = () => window.location.hostname==="localhost"
    ? "http://localhost:3001"
    : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

  // Update one supplier's properties (priorityBrands, catalogMeta, enabled flag, etc)
  const updateSupplier = (supplierId, patch) => {
    setSuppliers(prev => {
      const next = prev.map(s => s.id === supplierId ? {...s, ...patch} : s);
      saveSnapshot({suppliers: next});
      return next;
    });
  };

  // ── Freight ───────────────────────────────────────────────────────────────
  // Suppliers saved before freight existed have no `freight` key, so always
  // read through this rather than touching supplier.freight directly.
  const supFreight = (s) => ({...DEFAULT_FREIGHT_CONFIG, ...(s?.freight || {})});

  const updateSupplierFreight = (supplierId, patch) => {
    setSuppliers(prev => {
      const next = prev.map(s => s.id !== supplierId ? s
        : {...s, freight: {...supFreight(s), ...patch}});
      saveSnapshot({suppliers: next});
      return next;
    });
  };

  // Row editors for the two table-shaped config blocks. Each takes a mutator so
  // add/update/remove don't need three near-identical copies of the same
  // read-modify-write.
  const patchFreightList = (supplierId, key, fn) => {
    setSuppliers(prev => {
      const next = prev.map(s => s.id !== supplierId ? s
        : {...s, freight: {...supFreight(s), [key]: fn([...(supFreight(s)[key] || [])])}});
      saveSnapshot({suppliers: next});
      return next;
    });
  };
  const addAirTier        = (id) => patchFreightList(id, "airFreightTiers", r => [...r, {maxKg:0, charge:0}]);
  const updateAirTier     = (id, i, patch) => patchFreightList(id, "airFreightTiers", r => {r[i]={...r[i],...patch}; return r;});
  const removeAirTier     = (id, i) => patchFreightList(id, "airFreightTiers", r => r.filter((_,x)=>x!==i));
  const addWeightLookup   = (id) => patchFreightList(id, "weightLookup", r => [...r, {pattern:"", weightKg:0}]);
  const updateWeightLookup= (id, i, patch) => patchFreightList(id, "weightLookup", r => {r[i]={...r[i],...patch}; return r;});
  const removeWeightLookup= (id, i) => patchFreightList(id, "weightLookup", r => r.filter((_,x)=>x!==i));

  // Freight is modelled as an auto-calculated Section so it flows through the
  // same totals, PDF and customer-page code paths as everything else — no
  // parallel "freight" concept to keep in sync. Idempotent.
  const ensureFreightSection = (q) => {
    const m = ensureMigrated(q);
    const existing = (m.sections || []).find(s => s.autoCalculated);
    if (existing) {
      return m.freight ? m : {...m, freight: defaultFreightState(existing.id)};
    }
    const secId = `sec-freight-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    const optId = `opt-freight-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    return {
      ...m,
      sections: [...m.sections, {
        id: secId,
        label: "Freight & Delivery",
        description: "",
        selectedOptionId: optId,
        createdByAI: false,
        autoCalculated: true,
        visibleToCustomer: m.freight?.visibleToCustomer !== false,
        options: [{
          id: optId,
          label: "Standard delivery",
          description: "",
          lineItems: [{
            /* "freight", not "other": the dropdown now shows every live Type,
               and "other" was a value nobody could select or see. Freight is
               still identified by isFreightLineItem, not by type. */
            ...makeNewLineItem("freight"),
            id: `li-freight-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
            description: "Freight & Delivery",
            quantity: 1, unitCost: 0, unitPrice: 0,
            isFreightLineItem: true,
          }],
        }],
      }],
      freight: {...defaultFreightState(secId), ...(m.freight || {})},
    };
  };

  const defaultFreightState = (sectionId) => ({
    mode: "managed",
    autoSectionId: sectionId,
    visibleToCustomer: true,
    manualOverride: null,
    lastComputedAt: null,
    lastComputedBreakdown: null,
  });

  // Write a computed total into the freight section's line item.
  const applyFreightResult = (m, result) => ({
    ...m,
    sections: m.sections.map(s => !s.autoCalculated ? s : {
      ...s,
      visibleToCustomer: m.freight?.visibleToCustomer !== false,
      options: (s.options || []).map(o => ({
        ...o,
        lineItems: (o.lineItems || []).map(li => !li.isFreightLineItem ? li : {
          ...li,
          quantity: 1,
          unitCost:  result.total || 0,
          unitPrice: result.total || 0,   // freight is passed through at cost
          description: buildFreightDescription(result),
        }),
      })),
    }),
    freight: {
      ...(m.freight || {}),
      lastComputedAt: Date.now(),
      lastComputedBreakdown: result,
    },
    updatedAt: Date.now(),
  });

  const buildFreightDescription = (result) => {
    const per = result?.perSupplier || [];
    let text = "Freight & Delivery";
    if (per.length === 1)      text = `${per[0].supplierName} — ${per[0].breakdown}`;
    else if (per.length > 1)   text = `Combined freight from ${per.length} suppliers`;
    if (result?.warnings?.length > 0) text += " (includes items always charged freight)";
    return text;
  };

  const syncFreightForQuote = async (quoteId) => {
    const q = quotes.find(qq => qq.id === quoteId);
    if (!q) return;
    const m = ensureMigrated(q);
    if (m.freight?.mode === "manual") return;   // manual mode owns the number

    // Nothing to charge freight on yet — don't create the section (or call the
    // backend) until the quote actually has items.
    if (countAllLineItems(m) === 0) return;

    const withFreight = ensureFreightSection(m);
    try {
      const result = await callQuoteBackend(
        `/api/quotes/${quoteId}/freight/compute`,
        {quote: withFreight, suppliers},
        null,   // silent: this fires on every edit, a toast per keystroke is noise
      );
      if (!result) return;
      setQuotes(prev => {
        const next = prev.map(qq =>
          qq.id !== quoteId ? qq : applyFreightResult(ensureFreightSection(qq), result));
        saveSnapshot({quotes: next});
        return next;
      });
    } catch (err) {
      console.error("freight sync failed", err);
    }
  };

  // Debounced per quote — a burst of edits (typing a quantity) collapses into
  // one backend call, which matters because that call can hit the AI.
  const scheduleFreightSync = (quoteId) => {
    if (!quoteId) return;
    const timers = freightSyncTimerRef.current;
    if (timers.has(quoteId)) clearTimeout(timers.get(quoteId));
    timers.set(quoteId, setTimeout(() => {
      timers.delete(quoteId);
      syncFreightForQuote(quoteId);
    }, 600));
  };

  const switchToManualFreight = (quoteId) => {
    patchQuoteFreight(quoteId, f => ({...f, mode:"manual", manualOverride: f.manualOverride || {totalAmount:0, note:""}}));
  };
  const switchToManagedFreight = (quoteId) => {
    patchQuoteFreight(quoteId, f => ({...f, mode:"managed", manualOverride:null}));
    scheduleFreightSync(quoteId);
  };
  const setFreightVisibility = (quoteId, visible) => {
    patchQuoteFreight(quoteId, f => ({...f, visibleToCustomer: visible}), (m) => ({
      ...m,
      sections: m.sections.map(s => !s.autoCalculated ? s : {...s, visibleToCustomer: visible}),
    }));
  };
  const setManualFreight = (quoteId, amount, note) => {
    const amt = Number(amount) || 0;
    patchQuoteFreight(quoteId,
      f => ({...f, mode:"manual", manualOverride:{totalAmount: amt, note: note || ""}}),
      (m) => ({
        ...m,
        sections: m.sections.map(s => !s.autoCalculated ? s : {
          ...s,
          options: (s.options||[]).map(o => ({
            ...o,
            lineItems: (o.lineItems||[]).map(li => !li.isFreightLineItem ? li : {
              ...li, unitCost: amt, unitPrice: amt,
              description: note ? `Freight: ${note}` : "Freight & Delivery (manual)",
            }),
          })),
        }),
      }));
  };

  // Shared writer for the quote-level freight object. `alsoPatch` runs after the
  // freight patch so callers can touch sections in the same state write.
  const patchQuoteFreight = (quoteId, fn, alsoPatch) => {
    setQuotes(prev => {
      const next = prev.map(q => {
        if (q.id !== quoteId) return q;
        const m = ensureFreightSection(q);
        const patched = {...m, freight: fn(m.freight || {}), updatedAt: Date.now()};
        return alsoPatch ? alsoPatch(patched) : patched;
      });
      saveSnapshot({quotes: next});
      return next;
    });
  };

  // Patch the per-supplier UI state (uploading flag, modal open)
  const setSupUiState = (supplierId, patch) => {
    setSupplierUploadState(prev => ({
      ...prev,
      [supplierId]: {...(prev[supplierId] || {}), ...patch},
    }));
  };

  // Refresh catalog metadata from backend on mount — for every enabled supplier.
  // Best-effort: silently ignores 404 (backend route not wired yet) and network errors.
  const refreshSupplierCatalogInfo = async (supplier) => {
    try{
      const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
      const res = await fetch(`${_apiBaseUrl()}/api/wholesaler/${supplier.id}/info`, {
        headers: token?{Authorization:`Bearer ${token}`}:{},
      });
      if(res.status===404) return;
      if(!res.ok) return;
      const data = await res.json().catch(()=>null);
      if(data){
        updateSupplier(supplier.id, {catalogMeta: data});
      }
    }catch(_e){
      /* silent */
    }
  };

  // Generic file upload — accepts CSV or ZIP based on supplier.uploadFormat.
  // The frontend forwards the file as-is; the backend handles extraction & parsing.
  const uploadSupplierCatalog = async (supplier, file) => {
    if(!file) return;
    const expectedExt = supplier.uploadFormat === "zip" ? ".zip" : ".csv";
    if(!file.name.toLowerCase().endsWith(expectedExt)){
      setQuoteToast({kind:"err",msg:`Please pick a ${expectedExt} file for ${supplier.name}`});
      setTimeout(()=>setQuoteToast(null),2500);
      return;
    }
    setSupUiState(supplier.id, {uploading: true});
    try{
      const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("priorityBrands", JSON.stringify(supplier.priorityBrands || []));
      const res = await fetch(`${_apiBaseUrl()}/api/wholesaler/${supplier.id}/upload`, {
        method: "POST",
        headers: token?{Authorization:`Bearer ${token}`}:{},
        body: fd,
      });
      if(res.status===404){
        setQuoteToast({kind:"err",msg:`Backend route not yet wired: /api/wholesaler/${supplier.id}/upload`});
      } else if(!res.ok){
        const txt = await res.text().catch(()=>"");
        setQuoteToast({kind:"err",msg:`Upload failed (${res.status}): ${txt.slice(0,80)||"server error"}`});
      } else {
        const data = await res.json().catch(()=>({}));
        const meta = {
          syncedAt:      data.syncedAt      || Date.now(),
          totalItems:    data.totalItems    || 0,
          priorityItems: data.priorityItems || 0,
          // Leader-specific perthStock; harmless for others
          perthStock:    data.perthStock    || 0,
          fileName:      file.name,
        };
        updateSupplier(supplier.id, {catalogMeta: meta});
        // Build a sensible toast — include perth count only if it's >0 (Leader)
        const bits = [
          `${meta.totalItems.toLocaleString()} items`,
          `${meta.priorityItems.toLocaleString()} priority`,
        ];
        if (meta.perthStock > 0) bits.push(`${meta.perthStock.toLocaleString()} in Perth`);
        setQuoteToast({kind:"ok",msg:`${supplier.name}: ${bits.join(", ")}`});
        setSupUiState(supplier.id, {modalOpen: false});
      }
    }catch(err){
      setQuoteToast({kind:"err",msg:`Network error: ${err.message}`});
    }finally{
      setSupUiState(supplier.id, {uploading: false});
      setTimeout(()=>setQuoteToast(null),4000);
    }
  };

  // Pull a fresh catalog from the supplier's configured source URL (Key Vault).
  // Triggers the same backend code path as the scheduled cron — just on-demand.
  // Currently only Leader has a configured source URL.
  const refreshSupplierFromSource = async (supplier) => {
    if (!supplier.hasAutoRefreshSource) {
      setQuoteToast({kind:"err",msg:`${supplier.name} has no source URL configured`});
      setTimeout(()=>setQuoteToast(null),3000);
      return;
    }
    setSupUiState(supplier.id, {pullingFromSource: true});
    try{
      const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
      const res = await fetch(`${_apiBaseUrl()}/api/wholesaler/${supplier.id}/refresh-from-source`, {
        method: "POST",
        headers: token?{Authorization:`Bearer ${token}`}:{},
      });
      if(res.status===404){
        setQuoteToast({kind:"err",msg:`Backend route not yet wired: /api/wholesaler/${supplier.id}/refresh-from-source`});
      } else if(!res.ok){
        const txt = await res.text().catch(()=>"");
        setQuoteToast({kind:"err",msg:`Pull failed (${res.status}): ${txt.slice(0,100)||"server error"}`});
      } else {
        const data = await res.json().catch(()=>({}));
        if (data.skipped) {
          // Backend says "nothing changed" or "too soon since last run"
          setQuoteToast({kind:"ok",msg:`${supplier.name}: ${data.skipReason || "no changes since last pull"}`});
        } else {
          const meta = {
            syncedAt:      data.syncedAt      || Date.now(),
            totalItems:    data.totalItems    || 0,
            priorityItems: data.priorityItems || 0,
            perthStock:    data.perthStock    || 0,
            fileName:      data.fileName      || "(from source URL)",
          };
          updateSupplier(supplier.id, {catalogMeta: meta});
          const bits = [
            `${meta.totalItems.toLocaleString()} items`,
            `${meta.priorityItems.toLocaleString()} priority`,
          ];
          if (meta.perthStock > 0) bits.push(`${meta.perthStock.toLocaleString()} in Perth`);
          setQuoteToast({kind:"ok",msg:`${supplier.name} fresh pull: ${bits.join(", ")}`});
        }
      }
    }catch(err){
      setQuoteToast({kind:"err",msg:`Network error: ${err.message}`});
    }finally{
      setSupUiState(supplier.id, {pullingFromSource: false});
      setTimeout(()=>setQuoteToast(null),4000);
    }
  };

  // Refresh all enabled suppliers' catalog info on mount (best-effort).
  useEffect(()=>{
    suppliers.filter(s => s.enabled).forEach(s => refreshSupplierCatalogInfo(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Helpers — return the "freshest" or "stalest" supplier for display purposes
  const supplierAgeDays = (s) => s.catalogMeta?.syncedAt
    ? (Date.now() - s.catalogMeta.syncedAt) / (1000*60*60*24)
    : null;
  const isSupplierStale = (s) => {
    const d = supplierAgeDays(s);
    return d !== null && d > 7;
  };

  // Aggregate counts across all enabled suppliers
  const aggregateSupplierStats = () => {
    const enabled = suppliers.filter(s => s.enabled);
    let totalItems = 0, priorityItems = 0, suppliersWithCatalog = 0;
    enabled.forEach(s => {
      if (s.catalogMeta) {
        totalItems += (s.catalogMeta.totalItems || 0);
        priorityItems += (s.catalogMeta.priorityItems || 0);
        suppliersWithCatalog++;
      }
    });
    return { totalItems, priorityItems, suppliersWithCatalog, totalSuppliers: enabled.length };
  };

  // Union of all enabled suppliers' priorityBrands — used in the AI chat empty state
  const aggregatePriorityBrands = () => {
    const set = new Set();
    suppliers.filter(s => s.enabled).forEach(s => (s.priorityBrands || []).forEach(b => set.add(b)));
    return [...set];
  };
  // ──────────────────────────────────────────────────────────────────────────

  // ── AI Assistant chat — iterative quote building ──────────────────────────
  // Opens the chat modal for a given quote, seeded with a fresh empty conversation.
  const openAiChat = (quote) => {
    setAiChatQuoteId(quote.id);
    setAiChatMessages([]);
    setAiChatInput("");
    setAiChatLoading(false);
    setAiChatOpen(true);
  };

  const closeAiChat = () => {
    setAiChatOpen(false);
    setAiChatQuoteId(null);
    setAiChatMessages([]);
    setAiChatInput("");
  };

  // Send a user turn to the backend. The backend gets the FULL conversation
  // history plus the current quote's line items so Claude has context for
  // refinement requests like "swap the laptops for ones with 32GB RAM".
  const sendAiChatMessage = async () => {
    const text = aiChatInput.trim();
    if(!text || aiChatLoading) return;
    const quote = quotes.find(q=>q.id===aiChatQuoteId);
    if(!quote) return;

    // Optimistically append the user turn
    const userTurn = { role:"user", content:text, ts:Date.now() };
    const nextHistory = [...aiChatMessages, userTurn];
    setAiChatMessages(nextHistory);
    setAiChatInput("");
    setAiChatLoading(true);

    try{
      const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
      // Send only role+content for the backend (strip our local fields like ts/suggestions)
      const apiMessages = nextHistory.map(m => ({ role: m.role, content: m.content }));
      // Build the full Options + Common shape so Claude understands the structure
      const m = ensureMigrated(quote);
      const focusLoc = resolveLocation(m);
      const trimLi = (li) => ({
        id: li.id,
        sku: li.sku||"",
        description: li.description||"",
        manufacturer: li.manufacturer||"",
        quantity: li.quantity,
        unitCost: li.unitCost,
        unitPrice: li.unitPrice,
      });
      const res = await fetch(`${_apiBaseUrl()}/api/ai/quote-suggest`, {
        method: "POST",
        headers: {
          "Content-Type":"application/json",
          ...(token?{Authorization:`Bearer ${token}`}:{}),
        },
        body: JSON.stringify({
          messages: apiMessages,
          companyName: quote.companyName,
          // Sections > Options > Line Items — lets Claude route suggestions into
          // the right section, or propose new ones.
          sections: (m.sections || []).map(s => ({
            id: s.id,
            label: s.label,
            description: s.description || "",
            selectedOptionId: s.selectedOptionId || null,
            options: (s.options || []).map(o => ({
              id: o.id,
              label: o.label,
              description: o.description || "",
              lineItems: (o.lineItems || []).map(trimLi),
            })),
          })),
          commonLineItems: (m.commonLineItems || []).map(trimLi),
          focusedLocation: focusLoc,           // "common" | {sectionId, optionId}
          focusedLocationLabel: focusLoc === "common"
            ? "Common"
            : getAllLineItemRefs(m).find(r => isSameLocation(r.location, focusLoc))?.locationLabel
              || (()=>{ // no line items there yet — build the label from the tree
                  const s = (m.sections||[]).find(x => x.id === focusLoc.sectionId);
                  const o = s?.options?.find(x => x.id === focusLoc.optionId);
                  return s ? `${s.label||"Section"} › ${o?.label||"Option"}` : "Option";
                })(),
          // Backwards-compat: flat list for older backend builds
          currentLineItems: [
            ...(m.commonLineItems || []).map(trimLi),
            ...(m.sections || []).flatMap(s => (s.options || []).flatMap(o => (o.lineItems || []).map(trimLi))),
          ],
          // Generic suppliers config — backend queries all enabled suppliers
          suppliers: suppliers.filter(s => s.enabled).map(s => ({
            id: s.id,
            name: s.name,
            priorityBrands: s.priorityBrands || [],
          })),
          // Backwards-compat: union of all suppliers' priorityBrands for old backend builds
          priorityBrands: aggregatePriorityBrands(),
          defaultMarkupPct,
        }),
      });

      if(!res.ok){
        const txt = await res.text().catch(()=>"");
        const errMsg = `Failed (${res.status}): ${txt.slice(0,140)||"server error"}`;
        setAiChatMessages(prev=>[...prev,{role:"assistant",content:errMsg,suggestions:[],ts:Date.now(),isError:true}]);
        return;
      }

      const data = await res.json().catch(()=>({}));
      // Backend returns { reply: string, suggestions: [items] }
      // Each suggestion gets a unique chatItemId so we can track accept/skip state
      const suggestions = (data.suggestions||data.lineItems||[]).map((s,i)=>({
        ...s,
        chatItemId: `cs-${Date.now()}-${i}-${Math.random().toString(36).slice(2,5)}`,
        status: "pending", // pending | accepted | skipped
      }));
      const assistantTurn = {
        role:"assistant",
        content: data.reply || data.message || (suggestions.length===0 ? "I couldn't find matching items in your priority catalog." : ""),
        suggestions,
        ts: Date.now(),
      };
      setAiChatMessages(prev=>[...prev, assistantTurn]);
    } catch(err){
      setAiChatMessages(prev=>[...prev,{role:"assistant",content:`Network error: ${err.message}`,suggestions:[],ts:Date.now(),isError:true}]);
    } finally {
      setAiChatLoading(false);
    }
  };


  // Foundational catalog search. Reuses Quoting's /api/ai/quote-suggest — same
  // keyword extraction, stock preference and markup the quote assistant uses —
  // but one-shot: the proposals UI keeps no thread, so this returns a plain
  // suggestions array and raises no toast of its own.
  const handleFoundationalSearch = async (query, currentItems = []) => {
    const token = onGetAuthTokenRef.current ? await onGetAuthTokenRef.current() : null;
    const res = await fetch(`${_apiBaseUrl()}/api/ai/quote-suggest`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        messages: [{ role: "user", content: query }],
        sections: [],
        commonLineItems: [],
        // Lets the model see what the block already holds, so a second search
        // repeats itself less. Not a hard duplicate guard.
        currentLineItems: (currentItems || []).map(li => ({
          description: li.description, quantity: li.quantity, unitCost: li.unitCost,
          unitPrice: li.unitPrice, supplier: li.supplier, supplierSku: li.supplierSku,
        })),
        suppliers: suppliers.filter(s => s.enabled).map(s => ({
          id: s.id, name: s.name, priorityBrands: s.priorityBrands || [],
        })),
        priorityBrands: aggregatePriorityBrands(),
        defaultMarkupPct,
      }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Search failed (${res.status})${txt ? ": " + txt.slice(0, 80) : ""}`);
    }
    const data = await res.json().catch(() => ({}));
    return data.suggestions || data.lineItems || [];
  };

  // ── AI suggestion routing ──────────────────────────────────────────────────
  // Claude may tag each suggestion with where it belongs. Pure helpers: they
  // take an already-migrated quote and return a new one.

  // Append `newLi` into `sectionId`, choosing the option by label — falling back
  // to the section's selection, then its first option, then creating one.
  const routeIntoSection = (m, sectionId, targetOptionLabel, newLi) => ({
    ...m,
    sections: m.sections.map(s => {
      if (s.id !== sectionId) return s;
      const byLabel = targetOptionLabel
        ? (s.options||[]).find(o => (o.label||"").toLowerCase() === targetOptionLabel.toLowerCase())
        : null;
      const optId = byLabel?.id || s.selectedOptionId || s.options?.[0]?.id;

      if (!optId) { // section exists but has no options yet
        const id = newOptionId();
        return {
          ...s,
          selectedOptionId: id,
          options: [{id, label: targetOptionLabel || "Option A", description: "", lineItems: [newLi]}],
        };
      }
      // Label named an option we don't have → create it alongside the others.
      if (targetOptionLabel && !byLabel) {
        const id = newOptionId();
        return {
          ...s,
          options: [...(s.options||[]), {id, label: targetOptionLabel, description: "", lineItems: [newLi]}],
        };
      }
      return {
        ...s,
        options: s.options.map(o => o.id !== optId ? o : {...o, lineItems: [...(o.lineItems||[]), newLi]}),
      };
    }),
    updatedAt: Date.now(),
  });

  const addSuggestionToQuote = (m, sugg, newLi) => {
    // 1. Explicit section id from Claude, and it still exists.
    if (sugg.targetSectionId && m.sections.some(s => s.id === sugg.targetSectionId)) {
      return routeIntoSection(m, sugg.targetSectionId, sugg.targetOptionLabel, newLi);
    }

    // 2. A section LABEL — reuse an existing section with that name (AI-created
    //    or not, so a user rename doesn't cause a duplicate), else create one.
    if (sugg.targetSectionLabel) {
      const existing = m.sections.find(s =>
        (s.label||"").toLowerCase() === sugg.targetSectionLabel.toLowerCase());
      if (existing) return routeIntoSection(m, existing.id, sugg.targetOptionLabel, newLi);
      const secId = newSectionId(), optId = newOptionId();
      return {
        ...m,
        sections: [...m.sections, {
          id: secId,
          label: sugg.targetSectionLabel,
          description: "",
          selectedOptionId: optId,
          createdByAI: true,
          options: [{
            id: optId,
            label: sugg.targetOptionLabel || "Option A",
            description: "",
            lineItems: [newLi],
          }],
        }],
        updatedAt: Date.now(),
      };
    }

    // 3. No hints (or an older backend build) → current focus.
    // Check the raw focus first: a section the user just created has no options
    // yet, and resolveLocation would skip past it to some OTHER section's first
    // option. routeIntoSection creates the missing option in place instead.
    const focusedSec = focusedLocation?.sectionId
      ? m.sections.find(s => s.id === focusedLocation.sectionId)
      : null;
    if (focusedSec) {
      return routeIntoSection(m, focusedSec.id,
        focusedSec.options?.find(o => o.id === focusedLocation.optionId)?.label, newLi);
    }
    const loc = resolveLocation(m);
    if (loc === "common") {
      return {...m, commonLineItems: [...(m.commonLineItems||[]), newLi], updatedAt: Date.now()};
    }
    return routeIntoSection(m, loc.sectionId,
      m.sections.find(s => s.id === loc.sectionId)?.options
        ?.find(o => o.id === loc.optionId)?.label, newLi);
  };

  const lineItemFromSuggestion = (sugg) => ({
    ...makeNewLineItem(sugg.type || "hardware"),
    description: sugg.description||"",
    quantity: Number(sugg.quantity)||1,
    unitCost: Number(sugg.unitCost)||0,
    unitPrice: Number(sugg.unitPrice)||0,
    sku: sugg.sku||"",
    manufacturer: sugg.manufacturer||"",
    stockNote: sugg.stockNote||"",
    // Keep the id AND the name: the id is what freight groups by, the name is
    // what the user sees. Previously only the name survived, which left every
    // AI-added item invisible to the freight calculator.
    supplierId: sugg.supplierId || "",
    supplier: sugg.supplier
      || (sugg.supplierId ? (suppliers.find(s => s.id === sugg.supplierId)?.name || sugg.supplierId) : ""),
    supplierSku: sugg.supplierSku || sugg.sku || "",
    imageUrl: sugg.imageUrl || sugg.image || "",
    weightKg: Number(sugg.weightKg) > 0 ? Number(sugg.weightKg) : null,
    weightSource: Number(sugg.weightKg) > 0 ? "csv" : null,
  });

  // Accept a suggestion: route it per Claude's target hints, falling back to the
  // currently-focused location when no hints are present.
  const acceptAiSuggestion = (msgIdx, chatItemId) => {
    const msg = aiChatMessages[msgIdx];
    const sugg = msg?.suggestions?.find(s=>s.chatItemId===chatItemId);
    if(!sugg || sugg.status!=="pending") return;
    if(!aiChatQuoteId) return;
    const newLi = lineItemFromSuggestion(sugg);
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if(q.id!==aiChatQuoteId) return q;
        return addSuggestionToQuote(ensureMigrated(q), sugg, newLi);
      });
      saveSnapshot({quotes:next});
      return next;
    });
    setAiChatMessages(prev=>prev.map((m,i)=> i===msgIdx
      ? {...m, suggestions: m.suggestions.map(s=>s.chatItemId===chatItemId?{...s,status:"accepted"}:s)}
      : m));
    scheduleFreightSync(aiChatQuoteId);
  };

  // Accept every pending suggestion in one message, in a single state write.
  const acceptAllAiSuggestions = (msgIdx) => {
    const msg = aiChatMessages[msgIdx];
    const pending = (msg?.suggestions||[]).filter(s=>s.status==="pending");
    if(!pending.length || !aiChatQuoteId) return;
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if(q.id!==aiChatQuoteId) return q;
        // Fold each suggestion onto the ACCUMULATED quote, not the original.
        // Two items sharing a targetSectionLabel must land in the same section —
        // the second pass has to see the section the first one created, or the
        // label lookup misses and we get duplicate sections.
        return pending.reduce(
          (m, sugg) => addSuggestionToQuote(m, sugg, lineItemFromSuggestion(sugg)),
          ensureMigrated(q));
      });
      saveSnapshot({quotes:next});
      return next;
    });
    setAiChatMessages(prev=>prev.map((m,i)=> i===msgIdx
      ? {...m, suggestions: m.suggestions.map(s=>s.status==="pending"?{...s,status:"accepted"}:s)}
      : m));
    scheduleFreightSync(aiChatQuoteId);
  };

  // Replace an existing line item with a suggestion. lineId is searched across
  // all options and common items.
  const replaceWithAiSuggestion = (msgIdx, chatItemId, lineId) => {
    const msg = aiChatMessages[msgIdx];
    const sugg = msg?.suggestions?.find(s=>s.chatItemId===chatItemId);
    if(!sugg || sugg.status!=="pending") return;
    if(!aiChatQuoteId || !lineId) return;
    const patch = {
      type: sugg.type||"hardware",
      description: sugg.description||"",
      quantity: Number(sugg.quantity)||1,
      unitCost: Number(sugg.unitCost)||0,
      unitPrice: Number(sugg.unitPrice)||0,
      sku: sugg.sku||"",
      manufacturer: sugg.manufacturer||"",
      stockNote: sugg.stockNote||"",
      supplier: sugg.supplier
        || (sugg.supplierId ? (suppliers.find(s => s.id === sugg.supplierId)?.name || sugg.supplierId) : ""),
      supplierSku: sugg.supplierSku || sugg.sku || "",
      imageUrl: sugg.imageUrl || sugg.image || "",
    };
    setQuotes(prev=>{
      const next = prev.map(q=>{
        if(q.id!==aiChatQuoteId) return q;
        const m = ensureMigrated(q);
        // Try common items first
        if ((m.commonLineItems||[]).some(li => li.id===lineId)) {
          return {
            ...m,
            commonLineItems: m.commonLineItems.map(li => li.id===lineId ? {...li, ...patch} : li),
            updatedAt: Date.now(),
          };
        }
        // Otherwise scan every option in every section
        return {
          ...m,
          sections: (m.sections||[]).map(s => ({
            ...s,
            options: (s.options||[]).map(o => ({
              ...o,
              lineItems: (o.lineItems||[]).map(li => li.id===lineId ? {...li, ...patch} : li),
            })),
          })),
          updatedAt: Date.now(),
        };
      });
      saveSnapshot({quotes:next});
      return next;
    });
    setAiChatMessages(prev=>prev.map((m,i)=> i===msgIdx
      ? {...m, suggestions: m.suggestions.map(s=>s.chatItemId===chatItemId?{...s,status:"accepted",replacedLineId:lineId}:s)}
      : m));
  };

  const skipAiSuggestion = (msgIdx, chatItemId) => {
    setAiChatMessages(prev=>prev.map((m,i)=> i===msgIdx
      ? {...m, suggestions: m.suggestions.map(s=>s.chatItemId===chatItemId?{...s,status:"skipped"}:s)}
      : m));
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Always send the migrated shape — a legacy quote that was opened but never
  // edited still holds the old options[] in state (migration is lazy, and only
  // sticks on write), so the raw object would ship the pre-Sections shape.
  const generateQuotePdf = async (quote) => {
    const data = await callQuoteBackend(`/api/quotes/${quote.id}/pdf`, {quote: ensureMigrated(quote)}, "PDF generated");
    if(data?.pdfUrl){
      updateQuote(quote.id, {pdfUrl: data.pdfUrl});
    }
  };

  /* Opens the send modal (QuoteSendModal) in place of the old window.confirm.
     The guard stays IN FRONT of the modal: a quote with no address must refuse
     here rather than open a modal that cannot send. */
  const sendQuoteToClient = (quote) => {
    if(!quote.contactEmail){
      setQuoteToast({kind:"err",msg:"Add a client email address first"});
      setTimeout(()=>setQuoteToast(null),2500);
      return;
    }
    setQuoteSendModal({quoteId: quote.id, busy: false});
  };

  /* The modal's Send. `message` is Craig's covering text, possibly empty.
     ⚠ EMPTY MEANS EMPTY: no emailBodyText key is sent at all, so the request
     — and therefore the email — is exactly what every quote sent before the
     modal existed. A written message is sent AND kept on the quote record
     (inside `quotes`, which the state allowlist carries whole), so a resend
     opens with it. */
  const confirmSendQuote = async (message) => {
    const quoteId = quoteSendModal?.quoteId;
    const quote = quotes.find(q => q.id===quoteId);
    if(!quote || quoteSendModal?.busy) return;
    const text = String(message ?? "");
    const trimmed = text.trim();
    setQuoteSendModal({quoteId, busy: true});
    // Persist first, so the text survives even if the send itself fails and
    // Craig retries after fixing whatever went wrong.
    updateQuote(quoteId, {emailBodyText: trimmed ? text : ""});
    const payload = {
      quote: {...ensureMigrated(quote), emailBodyText: trimmed ? text : ""},
      ...(trimmed ? {emailBodyText: text} : {}),
    };
    const data = await callQuoteBackend(`/api/quotes/${quoteId}/send`, payload, "Quote sent to client");
    if(data?.sent){
      updateQuote(quoteId, {status:"sent", sentAt: Date.now()});
      setQuoteSendModal(null);
    } else {
      // Leave the modal open with the text intact; the toast says what failed.
      setQuoteSendModal({quoteId, busy: false});
    }
  };

  /* Opens the review modal (QuotePushModal) and fetches what it seeds from —
     the ticket title and a description built server-side from the ACCEPTED
     snapshot when there is one, else the selected options. The companyId
     guard stays IN FRONT: a quote naming a company that was never picked from
     Autotask cannot open a modal that could only push to a guess. */
  const pushQuoteToAutotask = async (quote) => {
    if(!quote.companyId){
      setQuoteToast({kind:"err",msg:"Pick the company from the list before pushing to Autotask — a typed name is not a link"});
      setTimeout(()=>setQuoteToast(null),3000);
      return;
    }
    setQuotePushModal({quoteId: quote.id, preview: null, busy: false, result: null});
    const data = await callQuoteBackend(`/api/quotes/${quote.id}/push-autotask/preview`, {quote: ensureMigrated(quote)}, "Ticket preview ready");
    if(!data){ setQuotePushModal(null); return; }   // the toast already said why
    setQuotePushModal(m => (m && m.quoteId===quote.id) ? {...m, preview: data} : m);
  };

  /* The modal's Push. Creates the ticket from what Craig finally pressed on,
     writes the ticket back onto the quote (so a second push is warned), and
     leaves the modal open on the result — including how many checklist items
     were verified on the ticket, or that they could not be. */
  /* issueType / subIssueType are the modal's two optional classifications,
     number-or-null; the route coerces again and drops a sub-issue that has no
     parent. Source (Web Portal) is set server-side, not here. */
  const confirmPushQuote = async ({title, description, checklist, force, issueType, subIssueType}) => {
    const m = quotePushModal;
    if(!m || m.busy) return;
    const quote = quotes.find(q => q.id===m.quoteId);
    if(!quote) return;
    setQuotePushModal({...m, busy: true});
    const data = await callQuoteBackend(`/api/quotes/${m.quoteId}/push-autotask`,
      {quote: ensureMigrated(quote), title, description, checklist: checklist||[], force: !!force,
       issueType: issueType ?? null, subIssueType: subIssueType ?? null}, "Pushed to Autotask");
    if(data?.autotaskTicketId){
      /* The patch — ticket fields AND the fulfilment.autotask tick — is built
         in quoteBoard.autotaskPushPatch: success only, never overwrites an
         existing stamp, keeps the other three steps, and stamps even on a
         not-yet-won quote (invisibly, until it is). Tested there. */
      updateQuote(m.quoteId, autotaskPushPatch(quote, data));
      setQuotePushModal({...m, busy: false, result: data});
    } else {
      setQuotePushModal({...m, busy: false});
    }
  };
  // ───────────────────────────────────────

  // ── Consolidate Operational cadence items ────────────────────────────────
  // Replaces all per-client O items (e.g. "Weekly Ticket Review — CBH Group")
  // with 4 consolidated "All" entries, spread Mon–Thu so they don't all land same day.
  const consolidateOperationalItems=()=>{
    const managedNames=new Set(atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).map(c=>c.companyName));
    if(managedNames.size===0){
      alert("No managed clients found. Go to Settings → Client Register and tick the Managed checkbox.");
      return;
    }

    // Helper: next occurrence of a specific weekday (0=Sun, 1=Mon … 5=Fri)
    const nextWeekday=(targetDay)=>{
      const d=new Date();d.setHours(0,0,0,0);
      const today=d.getDay();
      const diff=(targetDay-today+7)%7||7; // always next occurrence, never today
      d.setDate(d.getDate()+diff);
      return d.toISOString().slice(0,10);
    };

    let counter=Date.now();
    // All three consolidated ops items land on Friday — one review session covers the week
    const friday=nextWeekday(5);
    const consolidated=[
      {id:`cad-ops-chc-${counter++}`,title:"Client Health Check — All",
       desc:"Monitoring alerts, backup status, security events and patch status across all managed clients",
       client:"All",dueDate:friday,done:false,_consolidatedType:"client-health-check",_reportType:"client-health",_durationMins:60},
      {id:`cad-ops-otr-${counter++}`,title:"Open Ticket Review — All",
       desc:"Review open ticket counts, aging items and recurring issues across all client queues",
       client:"All",dueDate:friday,done:false,_consolidatedType:"open-ticket-review",_reportType:"open-tickets",_durationMins:60},
      {id:`cad-ops-psc-${counter++}`,title:"Patch Status Check — All",
       desc:"Outstanding patches across all managed endpoints",
       client:"All",dueDate:friday,done:false,_consolidatedType:"patch-status",_reportType:"patch-status",_durationMins:60},
    ];

    // Strip all per-client O items (any item where client is a managed company name),
    // also strip old single-client "All" ops items that are duplicates of our new consolidated set.
    const knownConsolidatedTitles=new Set([
      "Weekly Ticket Review — All","Open Ticket Review — All",
      "Client Health Check — All","Patch Status Check — All",
      "Client health check — All","Open ticket review — All","Patch status check — All", // legacy titles
    ]);
    const filteredO=(cadence.O?.items||[]).filter(i=>{
      if(managedNames.has(i.client)) return false;   // per-client → remove
      if(knownConsolidatedTitles.has(i.title)) return false; // old consolidated → remove
      return true; // keep anything else (manual items etc.)
    });

    const newO={...cadence.O,items:[...filteredO,...consolidated]};
    const updated={...cadence,O:newO};
    setCadence(updated);
    saveSnapshot({cadence:updated});
    alert(`Done! Removed per-client Operational items and added ${consolidated.length} consolidated entries spread Mon–Thu.`);
  };

  // Clear Strategic items and regenerate from managed clients only
  const resetStrategicItems=()=>{
    if(!atCompanies.length){ alert("No companies loaded. Run Autotask Data Sync first."); return; }
    const managed=atCompanies.filter(c=>managedCompanyIds.has(String(c.id)));
    if(!managed.length){
      alert("No managed clients selected.\n\nGo to Settings → Client Register and tick the Managed checkbox.");
      return;
    }
    const newSItems=[];
    let idCounter=Date.now();
    managed.forEach(c=>{
      const tier=c._tier||"Adhoc";
      const templates=(CADENCE_TEMPLATES[tier]||CADENCE_TEMPLATES.Adhoc).filter(t=>t.layer==="S");
      templates.forEach(tmpl=>{
        const d=new Date();
        d.setDate(d.getDate()+(tmpl.freqWeeks||13)*7);
        newSItems.push({
          id:`cad-${idCounter++}`,
          title:tmpl.title.replace("{client}",c.companyName),
          desc:tmpl.desc,
          client:c.companyName,
          tier,
          dueDate:d.toISOString().slice(0,10),
          done:false,
        });
      });
    });
    // Add the cross-client strategic item
    newSItems.push({id:`cad-${idCounter++}`,title:"Budget forecast — All vCIO clients",desc:"Device refresh pipeline, licensing renewals, Azure cost review",client:"All",dueDate:new Date(Date.now()+90*24*60*60*1000).toISOString().slice(0,10),done:false});
    const updated={...cadence,S:{...cadence.S,items:newSItems}};
    setCadence(updated);
    saveSnapshot({cadence:updated});
    alert(`Strategic layer reset — ${newSItems.length} items generated from ${managed.length} managed clients.`);
  };

  // Generate cadence items from atCompanies using tier templates
  const generateCadenceFromClients=()=>{
    if(!atCompanies.length){
      alert("No companies loaded. Run Autotask Data Sync first.");
      return;
    }
    const newItems={S:[],T:[],O:[]};
    let idCounter=Date.now();

    // Filter to clients ticked as Managed in the Client Register
    const managed=atCompanies.filter(c=>managedCompanyIds.has(String(c.id)));
    if(!managed.length){
      alert("No managed clients selected.\n\nGo to Settings → Client Register and tick the Managed checkbox for the clients you want in the Cadence Framework.");
      return;
    }

    managed.forEach(c=>{
      const tier=c._tier||"Adhoc";
      const templates=CADENCE_TEMPLATES[tier]||CADENCE_TEMPLATES.Adhoc;
      templates.forEach(tmpl=>{
        // Calculate next due date based on frequency
        const dueDate=(()=>{
          const d=new Date();
          d.setDate(d.getDate()+(tmpl.freqWeeks||4)*7);
          return d.toISOString().slice(0,10);
        })();
        const item={
          id:`cad-${idCounter++}`,
          title:tmpl.title.replace("{client}",c.companyName),
          desc:tmpl.desc,
          client:c.companyName,
          tier,
          dueDate,
          done:false,
        };
        if(newItems[tmpl.layer]) newItems[tmpl.layer].push(item);
      });
    });

    // Also add cross-client operational items
    newItems.O.push(
      {id:`cad-${idCounter++}`,title:"Client health check — All",desc:"Monitoring alerts, backup status, security events across all managed clients",client:"All",dueDate:new Date(Date.now()+7*24*60*60*1000).toISOString().slice(0,10),done:false},
      {id:`cad-${idCounter++}`,title:"Open ticket review — All",desc:"Aging tickets, unassigned items, recurring issues across all queues",client:"All",dueDate:new Date(Date.now()+7*24*60*60*1000).toISOString().slice(0,10),done:false},
      {id:`cad-${idCounter++}`,title:"Patch status check — All",desc:"Outstanding patches across all managed endpoints",client:"All",dueDate:new Date(Date.now()+7*24*60*60*1000).toISOString().slice(0,10),done:false},
    );
    newItems.S.push(
      {id:`cad-${idCounter++}`,title:"Budget forecast — All vCIO clients",desc:"Device refresh pipeline, licensing renewals, Azure cost review",client:"All",dueDate:new Date(Date.now()+90*24*60*60*1000).toISOString().slice(0,10),done:false},
    );

    const updated={
      S:{...cadence.S,items:newItems.S},
      T:{...cadence.T,items:newItems.T},
      O:{...cadence.O,items:newItems.O},
    };
    setCadence(updated);
    saveSnapshot({cadence:updated});
    alert(`Generated ${newItems.S.length} Strategic, ${newItems.T.length} Tactical, ${newItems.O.length} Operational items from ${managed.length} managed clients.`);
  };

  const openReschedule=(todo)=>{setRescheduleModal(todo);setRescheduleDate(isoDate(addWD(new Date(),1)));};
  const addManualTodo=()=>{
    if(!newTodo.title.trim()) return;
    const todo={
      id:`td-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      title:newTodo.title.trim(),
      notes:newTodo.notes.trim(),
      dueDate:newTodo.dueDate||"",
      priority:newTodo.priority||"med",
      layer:newTodo.layer||"O",
      client:newTodo.client.trim()||"",
      done:false,
      source:newTodo._sourceEmail?"Email":"Manual",
      createdAt:new Date().toISOString(),
    };
    // Single save with both updates to avoid race condition
    setTodos(ts=>{
      const nextTodos=[...ts,todo];
      if(newTodo._sourceEmail){
        setTodoedEmails(s=>{const nextEmails=new Set([...s,newTodo._sourceEmail]);saveSnapshot({todos:nextTodos,todoedEmails:[...nextEmails]});return nextEmails;});
      } else {
        saveSnapshot({todos:nextTodos});
      }
      return nextTodos;
    });
    setNewTodo({title:"",notes:"",dueDate:"",priority:"med",layer:"O",client:""});
    setAddTodoModal(false);
  };

  // Open todo modal pre-filled from an email
  // Next working day helper (skip weekends)
  const nextWorkingDay=()=>{
    const d=new Date();
    d.setDate(d.getDate()+1);
    while(d.getDay()===0||d.getDay()===6) d.setDate(d.getDate()+1);
    return d.toISOString().slice(0,10);
  };

  const dismissPlanItem=(itemId)=>{
    setDismissedPlanItems(s=>{
      const next=new Set([...s,itemId]);
      saveSnapshot({dismissedPlanItems:[...next],planDismissDate:todayISO()});
      return next;
    });
  };

  const openTodoFromEmail=(email,oneClick=false)=>{
    const domain=(email.from||"").split("@")[1]?.toLowerCase().replace(">","").trim()||"";
    const matched=clientDomains.find(cd=>cd.domain&&domain.endsWith(cd.domain));
    const client=matched?.client||"";
    const dueDate=nextWorkingDay();
    if(oneClick){
      const todo={
        id:`todo-${Date.now()}`,
        title:email.subj||"",
        notes:email.body||email.aiSum||"",
        dueDate,
        priority:email.priority==="urgent"?"high":"med",
        layer:"O",
        client,
        done:false,
        createdAt:new Date().toISOString(),
        _sourceEmail:email.id,
      };
      // Single combined save to prevent race condition between todos and todoedEmails
      setTodos(prev=>{
        const nextTodos=[...prev,todo];
        setTodoedEmails(s=>{const nextEmails=new Set([...s,email.id]);saveSnapshot({todos:nextTodos,todoedEmails:[...nextEmails]});return nextEmails;});
        return nextTodos;
      });
      return;
    }
    setNewTodo({title:email.subj||"",notes:email.body||email.aiSum||"",dueDate,priority:email.priority==="urgent"?"high":"med",layer:"O",client,_sourceEmail:email.id});
    setAddTodoModal(true);
  };

  const confirmReschedule=()=>{
    if(!rescheduleModal)return;
    updateTodoDate(rescheduleModal.id,rescheduleDate);
    setRescheduleModal(null);
  };

  // ─── Drag & drop ─────────────────────────────────────────────────────────
  const onDragStart=(e,index)=>{dragIndex.current=index;e.currentTarget.classList.add("dragging");};
  const onDragOver=(e,index)=>{e.preventDefault();dragOverIndex.current=index;};
  const onDrop=(e,index)=>{
    e.preventDefault();
    if(dragIndex.current===null||dragIndex.current===index)return;
    setBlocks(bs=>{
      const arr=[...bs];
      const [moved]=arr.splice(dragIndex.current,1);
      arr.splice(index,0,moved);
      saveSnapshot({blocks:arr});
      return arr;
    });
    dragIndex.current=null;dragOverIndex.current=null;
    document.querySelectorAll(".block.dragging").forEach(el=>el.classList.remove("dragging"));
  };
  const onDragEnd=(e)=>{e.currentTarget.classList.remove("dragging");};

    // ── Panel resize handlers ──────────────────────────────────────────────
  // ── Plan My Day helpers ────────────────────────────────────────────────
  const todayISO2=()=>todayISO();

  // ── Monthly meeting / cadence helpers ─────────────────────────────────────
  const addWorkingDays2=(date,days)=>{
    const d=new Date(date);
    let added=0;
    while(added<days){
      d.setDate(d.getDate()+1);
      if(d.getDay()!==0&&d.getDay()!==6) added++;
    }
    return d;
  };

  const subWorkingDays=(date,days)=>{
    const d=new Date(date);
    let removed=0;
    while(removed<days){
      d.setDate(d.getDate()-1);
      if(d.getDay()!==0&&d.getDay()!==6) removed++;
    }
    return d;
  };

  const clientFromAttendees=(attendees=[])=>{
    for(const a of attendees){
      const email=(a.emailAddress?.address||a.email||"").toLowerCase();
      const domain=email.split("@")[1]||"";
      // Skip Datatech own domain
      if(domain.includes("datatech")) continue;
      const match=clientDomains.find(cd=>domain===cd.domain||domain.endsWith("."+cd.domain));
      if(match) return match.client;
    }
    return null;
  };

  // Find upcoming "Monthly Tech Catchup" meetings and generate prep suggestions
  const monthlyMeetingPreps=()=>{
    // Return empty until calendar data is loaded — prevents spamming on every render
    if(!meetings||meetings.length===0) return [];
    const todayDate=new Date();todayDate.setHours(0,0,0,0);
    const in60=new Date(todayDate);in60.setDate(in60.getDate()+60); // look 60 days ahead
    const todayStr=todayDate.toISOString().slice(0,10);
    const preps=[];

    // Filter to monthly-type meetings from real calendar (type set by detectMeetingType)
    const monthlyMeetings=meetings.filter(m=>{
      if(!m.title) return false;
      return m.type==="monthly";
    });
    if(meetings.length>0&&monthlyMeetings.length===0){
      console.warn("[Tactical] meetings loaded but none are type=monthly. Total:",meetings.length,"types:",[...new Set(meetings.map(m=>m.type))]);
    }

    monthlyMeetings.forEach(m=>{
      // Use localDateStr (Perth date string) if available, fall back to date object
      const dateStr=m.localDateStr||(m.date instanceof Date
        ? `${m.date.getUTCFullYear()}-${String(m.date.getUTCMonth()+1).padStart(2,'0')}-${String(m.date.getUTCDate()).padStart(2,'0')}`
        : typeof m.date==="string"?m.date.slice(0,10):null);
      if(!dateStr) return;
      if(dateStr<todayStr||dateStr>in60.toISOString().slice(0,10)) return;

      // Client: prefer m.client (set by extractClientFromEvent), then try to resolve to a known AT company.
      // Title format often: "Monthly IT Management Meeting — CBH Group" or "CBH — Monthly Review"
      let client=m.client||"";
      const knownClients=atCompanies.map(c=>c.companyName);
      // Always attempt to resolve to a canonical AT company name:
      // 1. Exact case-insensitive match on current client value
      // 2. Current value is contained within a company name (or vice versa) — handles "Hotel Rottnest" vs "Hotel Rottnest Island"
      // 3. Scan meeting title for any known company name
      // 4. Fall back to whatever extractClientFromEvent gave us (or the title)
      const exactMatch=knownClients.find(n=>n.toLowerCase()===client.toLowerCase());
      const partialMatch=!exactMatch&&knownClients.find(n=>{
        const nl=n.toLowerCase(), cl=client.toLowerCase();
        return nl.includes(cl)||cl.includes(nl);
      });
      const titleMatch=!exactMatch&&!partialMatch&&knownClients.find(n=>
        m.title.toLowerCase().includes(n.toLowerCase())
      );
      if(exactMatch) client=exactMatch;
      else if(partialMatch) client=partialMatch;
      else if(titleMatch) client=titleMatch;
      else if(!client||client==="Internal"||client==="External") client=m.title||"Client";
      // else keep whatever extractClientFromEvent returned (may not match but better than nothing)

      // Calculate working day windows:
      // Prep (send form) = 5 working days before meeting
      // Pre-meeting (MIP + review) = 1 working day before meeting
      const meetingDateObj=new Date(dateStr+"T12:00:00");
      const prepDateObj=subWorkingDays(meetingDateObj,5);
      prepDateObj.setHours(0,0,0,0);
      const prepDateStr=prepDateObj.toISOString().slice(0,10);

      const preMeetingDateObj=subWorkingDays(meetingDateObj,1);
      preMeetingDateObj.setHours(0,0,0,0);
      const preMeetingDateStr=preMeetingDateObj.toISOString().slice(0,10);

      const daysTilMeeting=Math.round((new Date(dateStr+"T00:00:00")-todayDate)/(1000*60*60*24));

      preps.push({
        id:`prep-${m.id||m.title}-${dateStr}`,
        client,
        meetingDate:dateStr,
        prepDate:prepDateStr,
        preMeetingDate:preMeetingDateStr,
        meetingTitle:m.title,
        startTime:m.startTime||"09:00",
        duration:m.duration||60,
        daysUntil:daysTilMeeting,
        isDue:todayStr>=prepDateStr,
        isPreMeetingDue:todayStr>=preMeetingDateStr,
        attendees:m.attendees||[],
        iCalUID:m.iCalUID||m.id||null,
        graphEventId:m.id||null,
      });
    });

    // Sort ascending so the earliest occurrence comes first per client+title group
    const sorted=preps.sort((a,b)=>a.daysUntil-b.daysUntil);
    // Deduplicate: only keep the next upcoming event per client + meeting title.
    // e.g. if a client has Monthly Tech Catchup on Apr 28 AND May 26, only Apr 28 shows.
    // Once Apr 28 passes it drops off (dateStr<todayStr filter above), and May 26 becomes next.
    const seen=new Set();
    const deduped=sorted.filter(p=>{
      const key=`${p.client}||${p.meetingTitle.toLowerCase().trim()}`;
      if(seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return deduped;
  };

  // Auto-reset Tactical cadence items 7 days after their linked meeting.
  // Looks BACKWARDS at recently-passed meetings (last 90 days) — monthlyMeetingPreps()
  // only returns future meetings so can never trigger the reset condition.
  const checkCadenceAutoReset=()=>{
    if(!meetings||meetings.length===0) return;
    const today=new Date();today.setHours(0,0,0,0);
    const todayStr=today.toISOString().slice(0,10);
    const ago90=new Date(today);ago90.setDate(ago90.getDate()-90);
    const ago90Str=ago90.toISOString().slice(0,10);
    let changed=false;
    const newCadence={...cadence};

    // Find monthly meetings that have ALREADY PASSED (within last 90 days)
    const passedMonthlyMeetings=meetings.filter(m=>{
      if(m.type!=="monthly"||!m.title) return false;
      const dateStr=m.localDateStr||(m.date instanceof Date
        ?`${m.date.getUTCFullYear()}-${String(m.date.getUTCMonth()+1).padStart(2,'0')}-${String(m.date.getUTCDate()).padStart(2,'0')}`
        :typeof m.date==="string"?m.date.slice(0,10):null);
      return dateStr&&dateStr<todayStr&&dateStr>=ago90Str;
    });

    passedMonthlyMeetings.forEach(m=>{
      const dateStr=m.localDateStr||(m.date instanceof Date
        ?`${m.date.getUTCFullYear()}-${String(m.date.getUTCMonth()+1).padStart(2,'0')}-${String(m.date.getUTCDate()).padStart(2,'0')}`
        :typeof m.date==="string"?m.date.slice(0,10):null);
      if(!dateStr) return;

      const meetingDate=new Date(dateStr+"T12:00:00");
      const resetDate=new Date(meetingDate);
      resetDate.setDate(resetDate.getDate()+7);
      resetDate.setHours(0,0,0,0);
      if(today<resetDate) return; // 7-day grace period not yet elapsed

      // Resolve client name same way as monthlyMeetingPreps
      let client=m.client||"";
      const knownClients=atCompanies.map(c=>c.companyName);
      const exactMatch=knownClients.find(n=>n.toLowerCase()===client.toLowerCase());
      const partialMatch=!exactMatch&&knownClients.find(n=>{const nl=n.toLowerCase(),cl=client.toLowerCase();return nl.includes(cl)||cl.includes(nl);});
      const titleMatch=!exactMatch&&!partialMatch&&knownClients.find(n=>m.title.toLowerCase().includes(n.toLowerCase()));
      if(exactMatch) client=exactMatch;
      else if(partialMatch) client=partialMatch;
      else if(titleMatch) client=titleMatch;
      if(!client) return;

      // Clear done state on Tactical items for this client so they reset for the new cycle
      if(newCadence.T){
        const updatedItems=newCadence.T.items.map(item=>{
          if((item.client===client||item.id===`cal-mtg-prep-${client}`)&&item.done){
            changed=true;
            return{...item,done:false,doneAt:null};
          }
          return item;
        });
        newCadence.T={...newCadence.T,items:updatedItems};
      }
    });

    if(changed){
      setCadence(newCadence);
      saveSnapshot({cadence:newCadence});
    }
  };

  // Load meeting prep response when a prep OR meeting block is selected
  useEffect(()=>{
    if(!sel) { setPrepResponse(null); setPrepLinkInfo(null); setPrepResponseLoading(false); return; }
    const capturedSelId = sel.id; // capture now to avoid stale closure in async callbacks
    // Match prep blocks (start with "Prep:" or "Pre-meeting:") OR actual meeting blocks
    const isRelatedBlock = sel.title?.toLowerCase().startsWith("prep:") ||
                           sel.title?.toLowerCase().startsWith("pre-meeting:") ||
                           sel.title?.toLowerCase().includes("pre-meeting prep") ||
                           sel.title?.toLowerCase().includes("monthly tech catchup");
    if(!isRelatedBlock) { setPrepResponse(null); setPrepLinkInfo(null); setPrepResponseLoading(false); return; }

    // 1. Try to match company from the block title directly
    let titleClient = atCompanies.find(c =>
      sel.title?.toLowerCase().includes(c.companyName.toLowerCase()) ||
      normCo(sel.title||"").includes(normCo(c.companyName))
    );

    // 2. Fallback: cross-reference with the meetings array using startTime
    if(!titleClient && sel.startTime) {
      const matchedMeeting = meetings.find(m => {
        const mDate = m.localDateStr || String(m.date||"").slice(0,10);
        return mDate === todayISO() && m.startTime === sel.startTime && m.client;
      }) || meetings.find(m => {
        const mDate = m.localDateStr || String(m.date||"").slice(0,10);
        if(mDate !== todayISO() || !m.client) return false;
        const meetingMins = timeToMins(m.startTime||"09:00");
        const selMins = timeToMins(sel.startTime);
        return Math.abs(meetingMins - selMins - 30) <= 15;
      });
      if(matchedMeeting?.client) {
        titleClient = atCompanies.find(c =>
          matchedMeeting.client.toLowerCase() === c.companyName.toLowerCase() ||
          normCo(matchedMeeting.client) === normCo(c.companyName)
        );
      }
    }

    // If atCompanies hasn't loaded yet, bail gracefully — will retry when meetings/companies load
    if(!titleClient) { setPrepResponseLoading(false); return; }

    const companyId = String(titleClient.id);

    // ── Resolve meetingKey ──────────────────────────────────────────────────
    // Priority: use iCalUID/meetingDate attached directly to the block (Prep/Pre-meeting blocks)
    // These are set at block-creation time so we never need upcomingMeetingPreps for the key
    let meetingKey = sel._iCalUID || sel._meetingDate || null;
    let meetingDate = sel._meetingDate || null;

    // For calendar meeting blocks and old blocks without _iCalUID, fall back to upcomingMeetingPreps
    if(!meetingKey) {
      const clientMeetings = upcomingMeetingPreps.filter(p =>
        normCo(p.client||"") === normCo(titleClient.companyName) ||
        p.client?.toLowerCase() === titleClient.companyName.toLowerCase()
      );
      const meeting = clientMeetings[0];
      // If meetings haven't loaded yet, bail — useEffect will re-run when meetings.length changes
      if(!meeting) { setPrepResponseLoading(false); return; }
      meetingDate = meeting.meetingDate;
      meetingKey = meeting.iCalUID || meetingDate;
    }

    setPrepResponseLoading(true);
    // Ensure token is fresh before raw fetch calls — use Promise chain (useEffect is not async)
    const tokenPromise = typeof onGetAuthTokenRef.current==='function'
      ? onGetAuthTokenRef.current().then(t=>{ if(t) window.__mspToken=t; }).catch(()=>{})
      : Promise.resolve();
    const isPurePrepBlock2=sel.title?.toLowerCase().startsWith("prep:");
    const isMeetingBlock2=sel.type==="meeting"||(!sel.title?.toLowerCase().startsWith("prep:")&&!sel.title?.toLowerCase().startsWith("pre-meeting:"));
    if(isPurePrepBlock2){
      // Prep: blocks — only show form section (send link)
      setFocusAccordion(s=>({...s,"pre-meeting-form":true}));
    } else if(isMeetingBlock2){
      // Actual meeting blocks — show context + MIP
      setFocusAccordion(s=>({...s,"meeting-context":true,"mip":true}));
    } else {
      // Pre-meeting: blocks — show form responses + MIP
      setFocusAccordion(s=>({...s,"pre-meeting-form":true,"mip":true}));
    }

    const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

    // Probe ALL three plausible storage keys (event id, iCalUID, date) — the
    // send-link endpoint freezes whichever of (iCalUID, meetingDate) was in
    // context at send time. Trying just one or two misses the data when it
    // lives under a different key.
    // Chain after tokenPromise so the fresh token is in window.__mspToken before fetches start
    tokenPromise.then(()=>{
    const hdr2={headers:{Authorization:`Bearer ${window.__mspToken||""}`}};
    const get2=(path,key)=>fetch(`${_apiBase}${path}?meeting=${encodeURIComponent(key)}`,hdr2).then(r=>r.json()).catch(()=>null);
    const candidateKeys=[
      sel._iCalUID,
      meetingKey,
      meetingDate,
      sel._iCalUID?`prep-${sel._iCalUID}`:null,
      meetingKey?`prep-${meetingKey}`:null,
    ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
    const loadWithFallback = async (path) => {
      const has=(r)=>r&&(r.response||r.brief||r.formUrl||r.sentAt||r.snapshot||r.hasBrief);
      const results=await Promise.all(candidateKeys.map(k=>get2(path,k)));
      for(const r of results){if(has(r))return r;}
      return results.find(r=>r!=null)||null;
    };

    Promise.all([
      loadWithFallback(`/api/meeting-prep/${companyId}`),
      loadWithFallback(`/api/meeting-prep/${companyId}/link`),
      loadWithFallback(`/api/ai/meeting-brief/${companyId}`),
    ]).then(([resp, link, savedBrief]) => {
      setPrepResponse(resp?.response || null);
      setPrepLinkInfo({...(link||{}), meetingDate: meetingDate||meetingKey, meetingKey});
      if(savedBrief?.brief){
        setMeetingBrief(savedBrief.brief);
        setMeetingBriefExpanded(true);
        // Use captured ID to prevent stale-closure mismatch with clear useEffect
        setMeetingBriefBlockId(capturedSelId);
        if(savedBrief.brief?.committedMeetingUrl){
          const url = savedBrief.brief.committedMeetingUrl;
          if(typeof url === 'string' && url.startsWith('http')) setMeetingPageUrl(url);
        }
      } else {
        // No saved brief — ensure clean state (don't show stale committed button)
        setMeetingBriefBlockId(null);
      }
      setPrepResponseLoading(false);
    }).catch(() => setPrepResponseLoading(false));
    }); // end tokenPromise.then

  // Re-run when block changes OR when meetings/companies finish loading (fixes race condition)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sel?.id, meetings.length, atCompanies.length]);

  // Run auto-reset check on mount
  useEffect(()=>{
    if(meetings.length>0) checkCadenceAutoReset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[meetings]);

  // Roll consolidated O item due dates forward to next Friday when the previous one has passed.
  // Dep [] — runs once on every mount so stale/missing dates are always fixed immediately.
  useEffect(()=>{
    if(!cadence.O?.items?.length) return;
    const todayStr=new Date().toISOString().slice(0,10);

    const nextFriday=(()=>{
      const d=new Date(); d.setHours(0,0,0,0);
      const diff=(5-d.getDay()+7)%7||7;
      d.setDate(d.getDate()+diff);
      return d.toISOString().slice(0,10);
    })();

    // Match by title — catches both old-style ("Client health check") and new consolidated items.
    // Proactive comms has client "vCIO" so is unaffected.
    const ROLLING_TITLES=/(health check|ticket review|patch status)/i;
    let changed=false;
    const updatedItems=cadence.O.items.map(item=>{
      if(item.client!=="All") return item;
      if(!ROLLING_TITLES.test(item.title)) return item;
      if(!item.dueDate||item.dueDate<todayStr){
        changed=true;
        return{...item,dueDate:nextFriday,done:false};
      }
      return item;
    });

    if(changed){
      const newCadence={...cadence,O:{...cadence.O,items:updatedItems}};
      setCadence(newCadence);
      saveSnapshot({cadence:newCadence});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Auto-inject "📧 Send Pre-Meeting Forms" block — 2 working days before each monthly prep date
  useEffect(()=>{
    if(!meetings.length) return;
    const perthNow=new Date(new Date().getTime()+8*60*60*1000);
    const todayStr=`${perthNow.getUTCFullYear()}-${String(perthNow.getUTCMonth()+1).padStart(2,"0")}-${String(perthNow.getUTCDate()).padStart(2,"0")}`;
    const dueToday=meetings.filter(m=>{
      if(m.type!=="monthly") return false;
      const dateStr=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
      if(!dateStr) return false;
      const meetingDate=new Date(dateStr+"T12:00:00");
      const sendDate=subWorkingDays(meetingDate,5); // send forms = 5WD before meeting (same as Prep block)
      return sendDate.toISOString().slice(0,10)===todayStr;
    });
    if(!dueToday.length) return;
    setBlocks(prev=>{
      if(prev.some(b=>b.id==="send-forms-block")) return prev;
      const newBlock={id:"send-forms-block",title:"📧 Send Pre-Meeting Forms",layer:"T",start:workStart,dur:15,done:false,prepFor:null,todoId:null};
      const next=[newBlock,...prev];
      saveSnapshot({blocks:next});
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[meetings]);

  const timeToMins=(t)=>{const[h,m]=t.split(":").map(Number);return h*60+m;};
  const minsToTime=(m)=>{const h=Math.floor(m/60);const mn=m%60;return `${String(h).padStart(2,"0")}:${String(mn).padStart(2,"0")}`;};

  const planDayHours=()=>{
    const s=timeToMins(workStart);
    const e=timeToMins(workEnd);
    const slots=[];
    for(let t=s;t<e;t+=15){slots.push(minsToTime(t));}
    return slots;
  };

  // How many 15-min slots does a block span?
  const blockSlots=(dur)=>Math.max(1,Math.round((dur||30)/15));

  // Get all slots occupied by placed blocks (for overlap detection)
  const occupiedSlots=()=>{
    const occupied={};
    dayPlan.forEach(b=>{
      const start=timeToMins(b.startTime);
      const slots=blockSlots(b.duration);
      for(let i=0;i<slots;i++){
        const t=minsToTime(start+i*15);
        if(!occupied[t]) occupied[t]=[];
        occupied[t].push(b.id);
      }
    });
    return occupied;
  };

  const totalPlanMins=()=>dayPlan.reduce((n,b)=>n+(b.duration||30),0);

  const addToDayPlan=(item,startTime)=>{
    if(dayPlan.find(b=>b.sourceId===item.id)) return; // already added
    const newBlock={
      id:`dp-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      title:item.title,
      layer:item.layer||"O",
      duration:item.duration||30,
      startTime:startTime||workStart,
      type:item.type||"task",
      sourceId:item.id,
      sourceType:item.sourceType||"todo",
      fixed:item.fixed||false,
      color:item.color||null,
    };
    setDayPlan(prev=>{
      // Insert in time order
      const next=[...prev,newBlock].sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime));
      return next;
    });
  };

  const removeFromDayPlan=(blockId)=>{
    setDayPlan(prev=>prev.filter(b=>b.id!==blockId));
  };

  const updateDayPlanTime=(blockId,startTime)=>{
    setDayPlan(prev=>prev.map(b=>b.id===blockId?{...b,startTime}:b)
      .sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime)));
  };

  // Build source items for the inbox panel
  const planSourceItems=()=>{
    const today=todayISO2();
    const items={
      overdue:[],
      cadence:[],
      cadenceCompleted:[],
      meetings:[],
      emails:[],
      carryForward:[],
    };

    // Overdue todos
    todos.filter(t=>!t.done&&t.dueDate&&t.dueDate<today).forEach(t=>{
      items.overdue.push({id:t.id,title:t.title,layer:t.layer||"O",duration:30,sourceType:"todo",type:"todo",
        dueDate:t.dueDate||null,client:t.client||null,color:"var(--danger)"});
    });

    // Today todos
    todos.filter(t=>!t.done&&t.dueDate===today).forEach(t=>{
      items.overdue.push({id:`td-${t.id}`,title:t.title,layer:t.layer||"O",duration:30,sourceType:"todo",type:"todo",
        dueDate:t.dueDate||null,client:t.client||null,color:"var(--strat)"});
    });

    // Cadence items due within 3 WORKING days (skip weekends)
    const addWorkingDays=(date,days)=>{
      const d=new Date(date);let added=0;
      while(added<days){d.setDate(d.getDate()+1);const dow=d.getDay();if(dow!==0&&dow!==6)added++;}
      return d;
    };
    const cad3wdStr=addWorkingDays(new Date(),3).toISOString().slice(0,10);
    const todayForCad=todayISO();
    // Match the Cadence page filter: only show items for currently-managed clients.
    // Items with client="All" or "vCIO" are global obligations and always included.
    // If the user has no managed clients selected yet, fall through and show everything
    // (otherwise the section would be empty during initial setup).
    const managedNames=new Set(atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).map(c=>c.companyName));
    const isManagedCadenceItem=(i)=>{
      if(managedNames.size===0) return true; // pre-setup state — show all
      if(!i.client) return true; // unscoped item — always relevant
      if(i.client==="All"||i.client==="vCIO") return true;
      if(managedNames.has(i.client)) return true;
      // Fuzzy match guards against minor name discrepancies (matches Cadence page logic)
      const cl=i.client.toLowerCase();
      for(const n of managedNames){const nl=n.toLowerCase();if(nl.includes(cl)||cl.includes(nl)) return true;}
      return false;
    };
    Object.entries(cadence).forEach(([layer,cat])=>{
      cat.items.filter(i=>isManagedCadenceItem(i)&&(!i.dueDate||i.dueDate<=cad3wdStr)).forEach(i=>{
        // Consolidated O "All" items get 60-min duration and carry a managed client list for the modal
        const isConsolidated=layer==="O"&&i.client==="All"&&i._consolidatedType;
        const managedClientNames=isConsolidated
          ?atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).map(c=>({id:c.id,name:c.companyName,tier:c._tier||"Adhoc"}))
          :null;
        const item={id:`cad-${layer}-${i.id}`,title:i.title,layer,
          duration:isConsolidated?(i._durationMins||60):layer==="O"?15:layer==="T"?30:45,
          sourceType:"cadence",type:"cadence",
          dueDate:i.dueDate||null,
          dueToday:!i.dueDate||i.dueDate<=todayForCad,
          done:i.done||false,
          doneAt:i.doneAt||null,
          _cadLayer:layer,_cadId:i.id,
          color:layer==="S"?"var(--strat)":layer==="T"?"var(--tact)":"var(--ops)",
          ...(isConsolidated?{_consolidated:true,_consolidatedType:i._consolidatedType,_reportType:i._reportType||null,_clients:managedClientNames,desc:i.desc}:{})};
        if(i.done) items.cadenceCompleted.push(item);
        else items.cadence.push(item);
      });
    });
    // Sort completed by doneAt desc, keep last 10
    items.cadenceCompleted.sort((a,b)=>(b.doneAt||"").localeCompare(a.doneAt||"")).splice(10);

    // Monthly Tech Catchup prep items — surfaced in Cadence section
    // "Prep:" = 5 working days before meeting, 15 min, purpose: send the pre-meeting form
    monthlyMeetingPreps().filter(p=>p.isDue).forEach(p=>{
      const prepId=`cal-prep-${p.id}`;
      const alreadyDone=cadence.T?.items?.find(i=>i.id===prepId&&i.done);
      if(alreadyDone) return;
      items.cadence.push({
        id:prepId,
        title:`Prep: ${p.meetingTitle}${p.client?` — ${p.client}`:""}`,
        layer:"T",
        duration:15,
        sourceType:"cadence",
        type:"cadence-prep",
        dueDate:p.prepDate,
        dueToday:p.prepDate<=todayForCad,
        client:p.client||null,
        color:"var(--tact)",
        _meetingDate:p.meetingDate,
        _iCalUID:p.iCalUID||null,
      });
    });

    // "Pre-meeting:" = 1 working day before meeting, 30 min
    // Purpose: review form responses, build MIP, commit to meeting page
    monthlyMeetingPreps().filter(p=>p.isPreMeetingDue&&!p.isDue===false).forEach(p=>{
      // isDue means prep (5WD) is due. isPreMeetingDue means 1WD before. Show when 1WD window opens
      if(!p.isPreMeetingDue) return;
      const preMtgId=`cal-premtg-${p.id}`;
      items.meetings.push({
        id:preMtgId,
        title:`Pre-meeting: ${p.meetingTitle}${p.client?` — ${p.client}`:""}`,
        layer:"C",
        duration:30,
        sourceType:"meeting",
        type:"meeting-prep",
        fixed:false,
        dueDate:p.meetingDate,
        color:"var(--cal)",
        startTime:"08:30",
        _meetingTitle:p.meetingTitle,
        _iCalUID:p.iCalUID||null,
        client:p.client||null,
        _meetingDate:p.meetingDate,
      });
    });

    // Priority emails
    liveEmails.filter(e=>e.priority==="urgent"||e.vip).slice(0,5).forEach(e=>{
      items.emails.push({id:`em-${e.id}`,title:`Reply: ${e.subj}`,layer:"O",duration:30,sourceType:"email",type:"email",
        color:"var(--danger)"});
    });

    // Yesterday's carry-forwards (incomplete blocks from today's plan that are in blocks)
    blocks.filter(b=>!b.done).slice(0,5).forEach(b=>{
      items.carryForward.push({id:`cf-${b.id}`,title:b.title,layer:b.layer||"O",duration:b.dur||30,sourceType:"block",
        type:"carryforward",color:"var(--text3)"});
    });

    // Upcoming todos (due in next 7 days, not already in overdue section)
    const in7days=new Date();in7days.setDate(in7days.getDate()+7);
    const in7str=in7days.toISOString().slice(0,10);
    todos.filter(t=>!t.done&&t.dueDate&&t.dueDate>today&&t.dueDate<=in7str).forEach(t=>{
      items.upcoming=items.upcoming||[];
      items.upcoming.push({id:`up-${t.id}`,title:t.title,layer:t.layer||"O",duration:30,sourceType:"todo",type:"todo",
        dueDate:t.dueDate||null,client:t.client||null,color:"var(--ops)"});
    });

    // All other todos (no due date or far future)
    todos.filter(t=>!t.done&&(!t.dueDate||t.dueDate>in7str)).slice(0,15).forEach(t=>{
      items.allTodos=items.allTodos||[];
      items.allTodos.push({id:`at-${t.id}`,title:t.title,layer:t.layer||"O",duration:30,sourceType:"todo",type:"todo",
        client:t.client||null,color:"var(--text2)"});
    });

    // Mark dismissed items but keep them visible (rendered with strikethrough)
    const markDismissed=(arr)=>(arr||[]).map(i=>dismissedPlanItems.has(i.id)?{...i,dismissed:true}:i);
    return {
      overdue:markDismissed(items.overdue),
      cadence:markDismissed(items.cadence),
      cadenceCompleted:items.cadenceCompleted, // no dismissed logic — only ↺ reset shown
      meetings:markDismissed(items.meetings),
      emails:markDismissed(items.emails),
      carryForward:markDismissed(items.carryForward),
      upcoming:markDismissed(items.upcoming||[]),
      allTodos:markDismissed(items.allTodos||[]),
    };
  };

  const runAiDayPlan=async()=>{
    if(!onPlanDay && !onGenerateBrief)return;
    setAiPlanning(true);
    try{
      // ── Step 1: Refresh calendar from M365 before planning ──────────────────
      // This ensures rescheduled meetings are reflected in the plan.
      // Raw Graph events use a different shape than the transformed `meetings`
      // array — start.dateTime instead of startTime+date, no localDateStr/duration
      // — so we normalise them inline before stashing so the today-filter below
      // actually matches them.
      try{
        if(window.__mspToken){
          const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
          const calResp=await fetch(`${_ab}/api/calendar/events`,{headers:{Authorization:`Bearer ${window.__mspToken}`}});
          if(calResp.ok){
            const calData=await calResp.json();
            const evs=calData.events||calData;
            if(Array.isArray(evs)&&evs.length>0){
              // Use the SAME Perth-timezone conversion as MspOs.js
              // transformCalendarEvents. Graph sends "2026-05-28T01:30:00.0000000"
              // (UTC, 7 decimals, no Z). Naive `new Date(...).getHours()` is
              // timezone-dependent and was producing wrong startTime/localDateStr
              // (e.g. 01:30 instead of 09:30 Perth), which pushed meetings
              // off-screen and broke the today-filter. This matches MspOs.js exactly.
              const toDisplay=(raw)=>{
                if(!raw||typeof raw!=="string") return {timeStr:"09:00",dateStr:null,ms:null};
                const normalised=raw.replace(/(\.\d{3})\d+/,"$1").replace(/([+-]\d{2}:\d{2}|Z)?$/,"").concat("Z");
                const utcDt=new Date(normalised);
                if(isNaN(utcDt.getTime())) return {timeStr:"09:00",dateStr:null,ms:null};
                const perthMs=utcDt.getTime()+(8*60*60*1000);
                const p=new Date(perthMs);
                const hh=String(p.getUTCHours()).padStart(2,"0");
                const mm=String(p.getUTCMinutes()).padStart(2,"0");
                const dateStr=`${p.getUTCFullYear()}-${String(p.getUTCMonth()+1).padStart(2,"0")}-${String(p.getUTCDate()).padStart(2,"0")}`;
                return {timeStr:`${hh}:${mm}`,dateStr,ms:perthMs};
              };
              const normalised=evs.map(e=>{
                // Already-normalised events (from liveCalEvents) pass through.
                if(e.localDateStr&&e.startTime) return e;
                const rawStart=e.start?.dateTime||e.start||e.date||null;
                const rawEnd=e.end?.dateTime||e.end||null;
                if(!rawStart) return e;
                const s=toDisplay(typeof rawStart==="string"?rawStart:null);
                if(!s.dateStr) return e;
                const en=toDisplay(typeof rawEnd==="string"?rawEnd:null);
                let duration=60;
                if(en.ms&&s.ms) duration=Math.max(15,Math.round((en.ms-s.ms)/60000));
                return{
                  ...e,
                  startTime:s.timeStr,
                  localDateStr:s.dateStr,
                  duration,
                  title:e.subject||e.title||"Meeting",
                };
              });
              window.__freshCalEvents=normalised;
            }
          }
        }
      }catch(calErr){console.warn("Calendar refresh failed (non-fatal):",calErr.message);}

      const src=planSourceItems();
      // Separate today's todos from other items — they MUST be in the plan
      const todayTodoItems=src.overdue.filter(i=>i.sourceType==="todo"||i.type==="todo");
      // Items manually flagged as priority by the user
      const userPriorityItems=Object.keys(priorityFlags);
      const allSrcItems=[...src.overdue,...src.cadence,...src.meetings,...src.emails,...src.carryForward,...(src.upcoming||[]),...(src.allTodos||[])].filter(i=>!i.dismissed);
      const prioritisedItems=allSrcItems.filter(i=>userPriorityItems.includes(i.id));

      const allItems=[
        ...src.overdue.filter(i=>!i.dismissed).map(i=>({...i,urgency:"overdue/today - MUST INCLUDE"})),
        // User-marked priority items — MUST be included regardless of type
        ...prioritisedItems.filter(i=>!src.overdue.find(o=>o.id===i.id)).map(i=>({...i,urgency:"USER PRIORITY - MUST INCLUDE"})),
        // Only schedule cadence items due today — future ones are visible in inbox but not forced into plan
        ...src.cadence.filter(i=>i.dueToday&&!userPriorityItems.includes(i.id)).map(i=>({...i,urgency:"cadence due today"})),
        ...src.meetings.map(i=>({...i,urgency:"fixed meeting"})),
        ...src.emails.filter(i=>!userPriorityItems.includes(i.id)).map(i=>({...i,urgency:"priority email"})),
        ...src.carryForward.filter(i=>!userPriorityItems.includes(i.id)).map(i=>({...i,urgency:"carry forward - try to fit in"})),
        // Upcoming todos (next 7 days) - include if time allows
        ...(src.upcoming||[]).filter(i=>!userPriorityItems.includes(i.id)).map(i=>({...i,urgency:"upcoming todo - include if time allows"})),
      ];
      const workMins=timeToMins(workEnd)-timeToMins(workStart);
      const mustIncludeIds=[...todayTodoItems.map(i=>i.id),...prioritisedItems.map(i=>i.id)];

      // Pre-compute today's meetings injection BEFORE building the prompt
      // so we can tell the AI exactly which time slots are blocked
      const _perthNowPre=new Date(new Date().getTime()+(8*60*60*1000));
      const _todayStrPre=`${_perthNowPre.getUTCFullYear()}-${String(_perthNowPre.getUTCMonth()+1).padStart(2,"0")}-${String(_perthNowPre.getUTCDate()).padStart(2,"0")}`;
      const _filterToday=(arr)=>(arr||[]).filter(m=>{
        const ld=m.localDateStr||(()=>{
          try{
            const d=m.date instanceof Date?m.date:(m.date?new Date(m.date):(m.start?.dateTime?new Date(m.start.dateTime):null));
            if(!d||isNaN(d.getTime())) return null;
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          }catch{return null;}
        })();
        return ld===_todayStrPre;
      });
      // Try each source in priority order and use the FIRST that actually
      // contains today's meetings. This prevents a fresh-fetch that happens to
      // return no today-meetings (or mis-normalised ones) from masking the
      // already-correct liveCalEvents/meetings arrays — which was wiping the
      // calendar meetings from the plan.
      let _todayMtgsPre=_filterToday(window.__freshCalEvents);
      if(_todayMtgsPre.length===0) _todayMtgsPre=_filterToday(liveCalEvents);
      if(_todayMtgsPre.length===0) _todayMtgsPre=_filterToday(meetings);
      _todayMtgsPre=_todayMtgsPre.sort((a,b)=>timeToMins(a.startTime||"09:00")-timeToMins(b.startTime||"09:00"));

      // Helper: is this a Monthly Tech Catchup? Only those get an automatic
      // 15-minute prep block right before the meeting per the workflow.
      const isMtcMeeting=(m)=>/monthly tech catchup/i.test(m.title||m.subject||"");

      // Build pre-injected meetings for prompt context
      // Also add email check block at start of day. Use 15-minute prep window
      // (only for Monthly Tech Catchups) so the prompt matches what we actually
      // inject — otherwise the AI is misled about the size of the blocked window.
      const emailCheckBlock={title:"📧 Check Emails",startTime:workStart,duration:30};

      // ── Buffer windows — kept free for interruptions/unplanned work ─────
      // Morning buffer: 45 min, 2.5 hrs after work start (e.g. 10:30 if start=08:00)
      // Afternoon buffer: 45 min, 1.5 hrs before work end (e.g. 16:00 if end=17:30)
      const morningBufStart=minsToTime(timeToMins(workStart)+150); // +2h30m
      const afternoonBufStart=minsToTime(timeToMins(workEnd)-105); // -1h45m (45min buf + 1hr gap)
      const morningBuffer={title:"⏸ Morning Buffer (free)",startTime:morningBufStart,duration:45};
      const afternoonBuffer={title:"⏸ Afternoon Buffer (free)",startTime:afternoonBufStart,duration:45};

      // ── Afternoon email check at 13:00 if within work hours ─────────────
      const pmEmailMins=780; // 13:00
      const pmEmailInRange=pmEmailMins>=timeToMins(workStart)+90 && pmEmailMins+30<=timeToMins(workEnd);
      const pmEmailBlock=pmEmailInRange?{title:"📧 Check Emails (PM)",startTime:"13:00",duration:30}:null;

      const preInjected=[emailCheckBlock,morningBuffer,afternoonBuffer,...(pmEmailBlock?[pmEmailBlock]:[]),..._todayMtgsPre.flatMap(m=>{
        const ms=m.startTime||"09:00";
        const md=m.duration||60;
        const mm=timeToMins(ms);
        const blocks=[];
        if(isMtcMeeting(m)){
          const ps=minsToTime(Math.max(timeToMins(workStart),mm-15));
          blocks.push({title:`Prep: ${m.client?`${m.title} — ${m.client}`:m.title}`,startTime:ps,duration:15});
        }
        blocks.push({title:m.client?`${m.title} — ${m.client}`:m.title,startTime:ms,duration:md});
        return blocks;
      })];

      const occupiedForPrompt=preInjected.map(b=>{
        const endMins=timeToMins(b.startTime)+(b.duration||30);
        return `${b.startTime}-${minsToTime(endMins)} BLOCKED (${b.title})`;
      }).join("\n");

      const prompt=`You are an MSP vCIO daily planning assistant for Craig at Datatech Solutions.

Today is ${new Date().toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}.
Work hours: ${workStart} - ${workEnd} (${workMins} minutes available)
${occupiedForPrompt?`\nBLOCKED TIME SLOTS (do NOT schedule anything here — these are fixed and cannot be moved):\n${occupiedForPrompt}\n`:""}
Here are the items that need attention today:
${allItems.map((i,n)=>`${n+1}. [${i.urgency}] id:${i.id} ${i.title} (${i.duration}min, layer:${i.layer})`).join(" | ")}
${mustIncludeIds.length>0?`
MUST INCLUDE these items - they are due today and cannot be skipped: ${mustIncludeIds.join(", ")}`:""}

Create an optimised day plan. Return ONLY a JSON array, no markdown, no explanation:
[{
  "sourceId": "the id field from above",
  "title": "task title",
  "startTime": "HH:MM",
  "duration": minutes_as_number,
  "layer": "S|T|O|C",
  "reason": "one line why this is scheduled here"
}]

Special: TODO BATCHING (efficiency optimisation)
- If you find a contiguous free window of 60-180 minutes AND you have multiple short todo items (sourceType: todo, individually 30 min or less) that would naturally suit working through together, you SHOULD bundle them into ONE batched block.
- To create a batch, return ONE item with:
    "sourceId": "todo-batch",
    "title": "<a descriptive label you choose — e.g. 'Karratha catch-up tasks', 'Quick wins block', 'Hedland Hotel admin batch', whatever best describes the contents>",
    "startTime": "HH:MM",
    "duration": <total minutes for the batch — 60, 90, 120, 150, or 180>,
    "layer": "O",
    "reason": "Batched todos: <id1>, <id2>, <id3>, ...",
    "_batchedTodoIds": ["<todo id 1>","<todo id 2>","<todo id 3>", ...]
- The "_batchedTodoIds" array MUST list the id values (from the items list above) of every todo bundled into the batch. The post-processor uses these to render the checklist.
- Only batch items where urgency is [overdue/today - MUST INCLUDE], [USER PRIORITY - MUST INCLUDE], [carry forward - try to fit in], or [upcoming todo - include if time allows]. Never batch cadence-due-today items or fixed meetings.
- Items inside a batch count as "scheduled" — do NOT also schedule them individually elsewhere in the plan.
- Pick a title that gives Craig context: if all batched items are for the same client, name the client. If they're a theme (admin, follow-ups, code review), name the theme. Keep it under 50 chars.
- Batching is optional — if the day is fragmented or there aren't enough short todos, just schedule individually as usual.

Rules:
- CRITICAL: Do NOT schedule any item into a BLOCKED TIME SLOT listed above. These are fixed calendar meetings, automatic pre-meeting prep blocks, and the email check — they cannot be moved or replaced. The post-processor will REJECT any block you place inside a blocked slot, so just don't.
- CRITICAL: Do NOT include any of the BLOCKED items in your output. They are already scheduled — repeating them in your JSON will cause duplicates.
- CRITICAL: No two items can overlap in time. If item A ends at 10:30, item B must start at 10:30 or later.
- The first 30 minutes (${workStart}-${minsToTime(timeToMins(workStart)+30)}) is the email check — do NOT schedule anything else here.
- ${morningBufStart}-${minsToTime(timeToMins(morningBufStart)+45)} is a RESERVED MORNING BUFFER — leave this free for interruptions and unplanned work. Do not schedule anything here.
- ${afternoonBufStart}-${minsToTime(timeToMins(afternoonBufStart)+45)} is a RESERVED AFTERNOON BUFFER — leave this free for interruptions and unplanned work. Do not schedule anything here.
${pmEmailBlock?`- 13:00-13:30 is a RESERVED PM EMAIL CHECK — do NOT schedule anything here.`:""}- Calendar meetings (and their automatic pre-meeting prep blocks for Monthly Tech Catchups) are fixed and locked. Work around them.
- ALL items marked [overdue/today - MUST INCLUDE] MUST appear in the plan (either standalone or inside a batch)
- ALL items marked [USER PRIORITY - MUST INCLUDE] MUST appear in the plan (either standalone or inside a batch)
- ALL items marked [cadence due today] MUST appear in the plan as their own blocks (never batched)
- Items marked [upcoming todo - include if time allows] are optional — only include if there is room
- Items marked [carry forward - try to fit in] should be included if possible
- Do NOT include cadence items that are not in this list even if you know about them
- Group similar layer work together where possible
- Don't exceed available work hours
- Prioritise overdue items early in the day
- Strategic work in the morning when focus is highest
- Order: email check (fixed) → overdue → cadence due today → meetings (fixed) → carry forward → strategic → tactical → operational`;

      const result = onPlanDay ? await onPlanDay(prompt) : await onGenerateBrief({customPrompt:prompt,rawPrompt:true});
      if(result){
        const clean=result.replace(/```json|```/g,"").trim();
        let planned;
        // Helper: attempt to repair a truncated JSON array (AI hit max_tokens
        // mid-output). Find the last complete top-level object and close the array.
        const repairTruncatedArray=(s)=>{
          const start=s.indexOf("[");
          if(start<0) return null;
          // Walk forward tracking brace depth (ignoring braces inside strings),
          // recording the index just after each top-level object closes.
          let depth=0,inStr=false,esc=false,lastGoodEnd=-1;
          for(let i=start;i<s.length;i++){
            const ch=s[i];
            if(esc){esc=false;continue;}
            if(ch==="\\"){esc=true;continue;}
            if(ch==='"'){inStr=!inStr;continue;}
            if(inStr) continue;
            if(ch==="{") depth++;
            else if(ch==="}"){depth--;if(depth===0) lastGoodEnd=i;}
          }
          if(lastGoodEnd<0) return null;
          const repaired=s.slice(start,lastGoodEnd+1)+"]";
          try{return JSON.parse(repaired);}catch{return null;}
        };
        try{
          planned=JSON.parse(clean);
        }catch(parseErr){
          // The AI returned something that isn't directly parseable. Common cases:
          //  (a) extra prose around a valid array → extract the array
          //  (b) truncated output (hit max_tokens) → repair by closing the array
          //  (c) an actual API error string → surface a clear message
          const m=clean.match(/\[[\s\S]*\]/);
          if(m){
            try{planned=JSON.parse(m[0]);}catch{/* fall through to repair */}
          }
          if(!planned){
            planned=repairTruncatedArray(clean);
            if(planned){
              console.warn("AI day plan: response was truncated; recovered",planned.length,"complete blocks");
            }
          }
          if(!planned){
            console.error("AI day plan: response was not valid JSON",{rawResponse:clean.slice(0,500)});
            alert("AI Plan My Day couldn't read the response from the AI.\n\nThis usually means the AI service returned an error (e.g. out of credits or rate-limited) rather than a plan.\n\nThe response started with:\n"+clean.slice(0,200));
            setAiPlanning(false);
            return;
          }
        }
        if(!Array.isArray(planned)){
          console.error("AI day plan: parsed result is not an array",{planned});
          alert("AI Plan My Day got an unexpected response shape. Check the console for details.");
          setAiPlanning(false);
          return;
        }

        // Inject today's meetings as fixed blocks with correct time + duration
        // Today's date computed in pre-injection step above
        // Use pre-computed today's meetings (same data used for the prompt)
        const sortedTodayMtgs=_todayMtgsPre;
        const injectedMeetings=sortedTodayMtgs.flatMap((m,idx,arr)=>{
          // Extract startTime — from field, or derive from Date object
          const meetingStart=m.startTime||(m.date instanceof Date?m.date.toTimeString().slice(0,5):"09:00");
          const meetingDur=m.duration||60;
          const meetingMins=timeToMins(meetingStart);
          // Only Monthly Tech Catchups get an automatic prep block per workflow.
          // Other meetings (internal, ad-hoc) are injected without prep — Craig
          // can manually add prep tasks for those if needed.
          const wantsPrep=isMtcMeeting(m);
          // Check if previous meeting ends within 15 min of this one starting
          // (if so, no room for prep — skip it)
          const prevMeeting=idx>0?arr[idx-1]:null;
          let skipPrep=!wantsPrep;
          if(wantsPrep&&prevMeeting){
            const prevStart=prevMeeting.startTime||(prevMeeting.date instanceof Date?prevMeeting.date.toTimeString().slice(0,5):"09:00");
            const prevEnd=timeToMins(prevStart)+(prevMeeting.duration||60);
            const gap=meetingMins-prevEnd;
            if(gap<15) skipPrep=true; // Not enough gap for prep
          }
          const prepStart=minsToTime(Math.max(timeToMins(workStart),meetingMins-15));
          const blocks=[];
          if(!skipPrep){
            blocks.push({
              id:`dp-premtg-${m.id}`,
              title:`Prep: ${m.client?`${m.title} — ${m.client}`:m.title}`,
              layer:"C",
              duration:15,
              startTime:prepStart,
              type:"meeting-prep",
              sourceId:`premtg-cal-${m.id}`,
              sourceType:"meeting",
              fixed:true,
              color:"var(--cal)",
              reason:`Pre-meeting prep for ${m.client?`${m.title} — ${m.client}`:m.title}`,
              _iCalUID:m.iCalUID||m.id||null,
              _meetingDate:m.localDateStr||String(m.date||"").slice(0,10)||null,
            });
          }
          blocks.push({
            id:`dp-cal-${m.id}`,
            title:m.client?`${m.title} — ${m.client}`:m.title,
            layer:"C",
            duration:meetingDur,
            startTime:meetingStart,
            type:"meeting",
            sourceId:`cal-${m.id}`,
            sourceType:"meeting",
            fixed:true,
            color:"var(--cal)",
            reason:"Today's calendar meeting",
            _iCalUID:m.iCalUID||m.id||null,
            _meetingDate:m.localDateStr||String(m.date||"").slice(0,10)||null,
          });
          return blocks;
        });

        // Always reinject meetings fresh — don't carry over old fixed blocks
        const emailFixed={
          id:"dp-email-check",
          title:"📧 Check Emails",
          layer:"O",
          duration:30,
          startTime:workStart,
          type:"task",
          sourceId:"email-check-fixed",
          sourceType:"fixed",
          fixed:true,
          color:"var(--ops)",
          reason:"Daily email check — first 30 min of day",
        };
        // Buffer blocks — visual placeholders, not selectable work items
        const morningBufFixed={
          id:"dp-morning-buffer",
          title:"⏸ Free Time",
          layer:"O",
          duration:45,
          startTime:morningBufStart,
          type:"buffer",
          sourceId:"morning-buffer-fixed",
          sourceType:"fixed",
          fixed:true,
          color:"var(--border)",
          reason:"Morning buffer — reserved for interruptions and unplanned work",
        };
        const afternoonBufFixed={
          id:"dp-afternoon-buffer",
          title:"⏸ Free Time",
          layer:"O",
          duration:45,
          startTime:afternoonBufStart,
          type:"buffer",
          sourceId:"afternoon-buffer-fixed",
          sourceType:"fixed",
          fixed:true,
          color:"var(--border)",
          reason:"Afternoon buffer — reserved for interruptions and unplanned work",
        };
        // PM email check — injected if 13:00 is within work hours
        const pmEmailFixed=pmEmailBlock?{
          id:"dp-email-check-pm",
          title:"📧 Check Emails",
          layer:"O",
          duration:30,
          startTime:"13:00",
          type:"task",
          sourceId:"email-check-pm-fixed",
          sourceType:"fixed",
          fixed:true,
          color:"var(--ops)",
          reason:"Afternoon email check — 13:00",
        }:null;
        const fixedBlocks=[emailFixed,morningBufFixed,afternoonBufFixed,...(pmEmailFixed?[pmEmailFixed]:[]),...injectedMeetings];
        const fixedSourceIds=new Set(fixedBlocks.map(b=>b.sourceId));

        // Build set of fixed time ranges to prevent AI blocks overlapping them
        // Store as numbers (minutes) for correct numeric comparison in findFreeSlot
        const fixedRanges=fixedBlocks.map(b=>({
          start:timeToMins(b.startTime),
          end:timeToMins(b.startTime)+(b.duration||30),
        }));
        const overlapsFixed=(startMins,durMins)=>fixedRanges.some(r=>
          startMins<r.end && (startMins+durMins)>r.start
        );

        const aiBlocks=planned
          .filter(p=>!fixedSourceIds.has(p.sourceId))
          .map(p=>{
            // Detect a batched-todo block — AI returned sourceId === "todo-batch"
            // and an array of constituent todo IDs in _batchedTodoIds.
            const isBatch = p.sourceId === "todo-batch" && Array.isArray(p._batchedTodoIds) && p._batchedTodoIds.length > 0;
            const src=isBatch ? null : [...allItems].find(i=>i.id===p.sourceId);
            const startMins=timeToMins(p.startTime||workStart);
            const dur=p.duration||30;
            // If AI placed this block overlapping a fixed block, find next free slot
            // Find next free slot after fixed blocks
            const findFreeSlot=(start,durMins)=>{
              let s=start;
              for(let i=0;i<20;i++){
                let blocked=false;
                for(let j=0;j<fixedRanges.length;j++){
                  if(s<fixedRanges[j].end&&(s+durMins)>fixedRanges[j].start){
                    s=fixedRanges[j].end; // no extra gap — adjacent is fine
                    blocked=true;
                    break;
                  }
                }
                if(!blocked) break;
              }
              return s;
            };
            const finalStart=findFreeSlot(startMins,dur);

            if (isBatch) {
              // Hydrate the batch with title/client/duration from the underlying todos.
              // Skip any IDs that don't actually exist or aren't todos — defensive
              // against AI hallucinating an id that wasn't in the items list.
              const hydrated = p._batchedTodoIds
                .map(bid => {
                  // The id in allItems might be `td-X` (today todo wrapper) or X — try both
                  const item = allItems.find(i => i.id === bid)
                            || allItems.find(i => i.id === `td-${bid}`)
                            || allItems.find(i => i.sourceType === "todo" && (i.id === bid));
                  if (!item || (item.sourceType !== "todo" && item.type !== "todo")) return null;
                  // Resolve back to the actual todos[] entry for ticking later
                  const realTodoId = String(item.id).replace(/^td-/, "");
                  const realTodo = todos.find(t => t.id === realTodoId);
                  return {
                    todoId: realTodoId,
                    title: item.title || realTodo?.title || "Untitled",
                    client: item.client || realTodo?.client || null,
                    originalDuration: item.duration || 30,
                  };
                })
                .filter(Boolean);
              return {
                id: `dp-batch-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
                title: (p.title && p.title.length > 0) ? p.title : `Quick Tasks (${hydrated.length})`,
                layer: p.layer || "O",
                duration: dur,
                startTime: minsToTime(Math.min(finalStart, timeToMins(workEnd) - dur)),
                type: "todo-batch",
                sourceId: "todo-batch",
                sourceType: "todo",
                fixed: false,
                color: "var(--ops)",
                reason: p.reason || `Batched ${hydrated.length} todos`,
                _batchedTodos: hydrated,
              };
            }

            return{
              id:`dp-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
              title:p.title||src?.title||"Task",
              layer:p.layer||src?.layer||"O",
              duration:dur,
              startTime:minsToTime(Math.min(finalStart,timeToMins(workEnd)-dur)),
              type:src?.type||"task",
              sourceId:p.sourceId,
              sourceType:src?.sourceType||"todo",
              fixed:false,
              color:src?.color||null,
              reason:p.reason||"",
            };
          });

        // Make sure any todos absorbed into batches don't ALSO appear as
        // standalone "missed today todo" later in this function. The batch's
        // _batchedTodos array is the authoritative list of what's covered.
        const batchedTodoIds = new Set();
        aiBlocks.filter(b => b.type === "todo-batch").forEach(b => {
          (b._batchedTodos || []).forEach(bt => batchedTodoIds.add(bt.todoId));
        });

        // Remove standalone AI blocks whose sourceId resolves to a todo already
        // covered by a batch block — the AI sometimes schedules an item both as
        // a standalone block AND inside a batch, producing a visible duplicate.
        const aiBlocksDeduped = aiBlocks.filter(b => {
          if (b.type === "todo-batch") return true; // always keep the batch itself
          if (!b.sourceId) return true;
          const realId = String(b.sourceId).replace(/^td-/, "");
          return !batchedTodoIds.has(realId);
        });

        // Second pass: deconflict AI blocks against each other AND fixed blocks
        aiBlocksDeduped.sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime));
        for(let i=0;i<aiBlocksDeduped.length;i++){
          const cur=aiBlocksDeduped[i];
          let curStart=timeToMins(cur.startTime);
          const dur=cur.duration||30;
          // Check against all fixed ranges
          for(let attempt=0;attempt<30;attempt++){
            let blocked=false;
            // Check fixed blocks — must not touch (0 gap ok, but no overlap)
            for(let j=0;j<fixedRanges.length;j++){
              if(curStart<fixedRanges[j].end&&(curStart+dur)>fixedRanges[j].start){
                curStart=fixedRanges[j].end; // butt up against end, no extra gap
                blocked=true;
                break;
              }
            }
            // Check previous AI blocks — 0-minute gap (adjacent is fine)
            for(let j=0;j<i;j++){
              const prevStart=timeToMins(aiBlocksDeduped[j].startTime);
              const prevEnd=prevStart+(aiBlocksDeduped[j].duration||30);
              if(curStart<prevEnd&&(curStart+dur)>prevStart){
                curStart=prevEnd; // adjacent is fine
                blocked=true;
                break;
              }
            }
            if(!blocked) break;
          }
          cur.startTime=minsToTime(Math.min(curStart,timeToMins(workEnd)-dur));
        }

        // Force-add any today todos the AI missed — schedule them after last block.
        // Exclude ones already absorbed into a todo-batch (batchedTodoIds) so they
        // don't appear twice in the plan.
        const plannedSourceIds=new Set([...fixedBlocks,...aiBlocksDeduped].map(b=>b.sourceId));
        const missedTodayTodos=allItems
          .filter(i=>{
            if (!mustIncludeIds.includes(i.id)) return false;
            if (plannedSourceIds.has(i.id)) return false;
            // Strip td- prefix when checking against batchedTodoIds (the batch
            // stores real todo IDs without the "td-" wrapper)
            const realId = String(i.id).replace(/^td-/, "");
            if (batchedTodoIds.has(realId)) return false;
            return true;
          })
          .map(i=>({
            id:`dp-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
            title:i.title,
            layer:i.layer||"O",
            duration:i.duration||30,
            startTime:workStart, // will be pushed to end below
            type:i.type||"task",
            sourceId:i.id,
            sourceType:i.sourceType||"todo",
            fixed:false,
            color:i.color||null,
            reason:"Due today — auto-added",
          }));

        // Merge all blocks sorted by time
        const allBlocks=[...fixedBlocks,...aiBlocksDeduped,...missedTodayTodos]
          .sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime));

        // Push missed todos to after last scheduled block
        if(missedTodayTodos.length>0){
          let lastEnd=allBlocks.filter(b=>!missedTodayTodos.find(m=>m.id===b.id))
            .reduce((max,b)=>Math.max(max,timeToMins(b.startTime)+(b.duration||30)),timeToMins(workStart));
          missedTodayTodos.forEach(b=>{
            b.startTime=minsToTime(Math.min(lastEnd,timeToMins(workEnd)-b.duration));
            lastEnd+=b.duration+15;
          });
        }

        const newPlan=[...fixedBlocks,...aiBlocksDeduped,...missedTodayTodos]
          .sort((a,b)=>timeToMins(a.startTime)-timeToMins(b.startTime));
        setDayPlan(newPlan);
      }
    }catch(e){console.warn("AI day plan failed",e);}
    finally{setAiPlanning(false);}
  };

  const confirmDayPlan=()=>{
    // Convert canvas to Focus blocks — preserve done state if block already exists,
    // and pass through type + _batchedTodos so the Focus checklist for todo-batch
    // blocks can resolve its constituent todos on selection.
    const newBlocks=dayPlan.map((p,i)=>{
      // Try to find existing block to preserve done state
      const existing=blocks.find(b=>b.id===p.sourceId||b.title===p.title);
      return {
        id:existing?.id||`b-${Date.now()}-${i}`,
        title:p.title,
        layer:p.layer||"O",
        start:p.startTime,
        dur:p.duration,
        done:existing?.done||p.done||false,
        prepFor:null,
        todoId:p.sourceType==="todo"&&p.type!=="todo-batch"?p.sourceId?.replace("td-",""):null,
        ...(p.type?{type:p.type}:{}),
        ...(Array.isArray(p._batchedTodos)?{_batchedTodos:p._batchedTodos}:{}),
      };
    });
    setBlocks(newBlocks);
    saveSnapshot({blocks:newBlocks});
    // Update canvas blocks to reflect committed state (don't clear)
    setDayPlan(prev=>prev.map((p,i)=>({...p,sourceId:newBlocks[i]?.id||p.sourceId})));
    if(onSaveDayCanvas){
      const todayStr=(()=>{const n=new Date();const pp=new Date(n.getTime()+8*60*60*1000);return `${pp.getUTCFullYear()}-${String(pp.getUTCMonth()+1).padStart(2,"0")}-${String(pp.getUTCDate()).padStart(2,"0")}`;})();
      onSaveDayCanvas(todayStr,dayPlan).catch(()=>{});
    }
    setPlanConfirmed(true);
    setTab("focus");
  };

  const startResize=(panel,e)=>{
    e.preventDefault();
    e.stopPropagation();
    const startX=e.clientX;
    const startWidth=panel==="plan"?planWidthRef.current:inboxWidthRef.current;
    resizingRef.current={panel,startX,startWidth};

    // Use window-level events to capture mouse even outside the element
    const onMove=(ev)=>{
      ev.preventDefault();
      if(!resizingRef.current)return;
      const dx=ev.clientX-resizingRef.current.startX;
      if(panel==="plan"){
        const next=Math.max(160,Math.min(420,startWidth+dx));
        setPlanWidth(next);
      } else {
        const next=Math.max(160,Math.min(420,startWidth-dx));
        setInboxWidth(next);
      }
    };

    const onUp=(ev)=>{
      const dx=ev.clientX-startX;
      if(panel==="plan"){
        const next=Math.max(160,Math.min(420,startWidth+dx));
        setPlanWidth(next);
        localStorage.setItem("msp-os-plan-width",String(next));
      } else {
        const next=Math.max(160,Math.min(420,startWidth-dx));
        setInboxWidth(next);
        localStorage.setItem("msp-os-inbox-width",String(next));
      }
      resizingRef.current=null;
      setIsDragging(false);
      window.removeEventListener("mousemove",onMove);
      window.removeEventListener("mouseup",onUp);
    };

    setIsDragging(true);
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
  };

  // ── Plan date navigation ────────────────────────────────────────────────
  const fmtPlanDate=(d)=>{
    const date=new Date(d+"T12:00:00");
    return date.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",year:"numeric"});
  };

  const isToday=(d)=>d===todayStr();

  // ── Priority toggle — click an item to mark/unmark as priority ─────────────
  const togglePriority=(itemId)=>{
    setPriorityFlags(f=>{
      const next={...f};
      if(next[itemId]) delete next[itemId];
      else next[itemId]="Priority";
      return next;
    });
  };
  detectPrioritiesRef.current = null; // no auto-detection

  const navigatePlan=async(newDate)=>{
    // Save current plan first
    if(onSavePlan && blocks.length>0){
      await onSavePlan(planDate,blocks).catch(e=>console.warn("Plan save failed",e));
    }
    setPlanDate(newDate);
    setPlanLoading(true);
    try{
      const result=await onLoadPlan(newDate);
      if(result?.blocks){
        setBlocks(result.blocks);
      } else {
        // No plan for this date
        setBlocks([]);
      }
    }catch(e){
      console.warn("Plan load failed",e);
      setBlocks([]);
    }finally{
      setPlanLoading(false);
    }
  };

  const goBack=()=>{
    const d=new Date(planDate+"T12:00:00");
    d.setDate(d.getDate()-1);
    navigatePlan(d.toISOString().slice(0,10));
  };

  const goForward=()=>{
    const d=new Date(planDate+"T12:00:00");
    d.setDate(d.getDate()+1);
    navigatePlan(d.toISOString().slice(0,10));
  };

  const goToDate=(dateStr)=>{
    if(dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)){
      navigatePlan(dateStr);
    }
  };

  const copyFromYesterday=async()=>{
    const d=new Date(planDate+"T12:00:00");
    d.setDate(d.getDate()-1);
    const yesterday=d.toISOString().slice(0,10);
    try{
      const result=await onLoadPlan(yesterday);
      if(result?.blocks?.length){
        const incomplete=result.blocks.filter(b=>!b.done);
        setBlocks(incomplete);
        if(onSavePlan) onSavePlan(planDate,incomplete).catch(()=>{});
      }
    }catch(e){console.warn("Copy from yesterday failed",e);}
  };

  // Auto-save plan when blocks change (debounced)
  useEffect(()=>{
    if(!onSavePlan || planLoading) return;
    if(planSaveTimer.current) clearTimeout(planSaveTimer.current);
    planSaveTimer.current=setTimeout(()=>{
      onSavePlan(planDate,blocks).catch(e=>console.warn("Plan autosave failed",e));
    },2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[blocks,planDate]);

  const updateCadDueDate=(layer,id,dueDate)=>{
    setCadence(c=>{
      const next={...c,[layer]:{...c[layer],items:c[layer].items.map(i=>i.id===id?{...i,dueDate}:i)}};
      saveSnapshot({cadence:next});
      return next;
    });
    setCadDueDateModal(null);
  };

  const toggleCad=(layer,id)=>setCadence(c=>{
    const items=c[layer].items.map(i=>i.id===id?{...i,done:true,doneAt:new Date().toISOString().slice(0,10)}:i);
    const next={...c,[layer]:{...c[layer],items}};
    saveSnapshot({cadence:next});
    return next;
  });
  const unCad=(layer,id)=>setCadence(c=>{
    const items=c[layer].items.map(i=>i.id===id?{...i,done:false,doneAt:null}:i);
    const next={...c,[layer]:{...c[layer],items}};
    saveSnapshot({cadence:next});
    return next;
  });
  const addBlock=()=>{if(!nb.title.trim())return;setBlocks(bs=>{const next=[...bs,{...nb,id:Date.now(),done:false,prepFor:null,todoId:null}];saveSnapshot({blocks:next});return next;});setAdding(false);setNb({title:"",layer:"O",start:workStart,dur:60});};
  const addVip=()=>{if(newVip.trim()){setVips(v=>{const next=[...v,newVip.trim()];saveSnapshot({vips:next});return next;});setNewVip("");}};

  const prog=total>0?((total-secs)/total)*100:0;
  // Sidebar "Cadence N" badge — managed-clients only, same logic as Plan My
  // Day's Cadence Obligations and the Cadence page. Keeps all three counts
  // in sync so the badge is never higher than the actual visible list.
  const _pendingManagedNames=new Set(atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).map(c=>c.companyName));
  const _isPendingManaged=(i)=>{
    if(_pendingManagedNames.size===0) return true;
    if(!i.client||i.client==="All"||i.client==="vCIO") return true;
    if(_pendingManagedNames.has(i.client)) return true;
    const cl=i.client.toLowerCase();
    for(const n of _pendingManagedNames){const nl=n.toLowerCase();if(nl.includes(cl)||cl.includes(nl)) return true;}
    return false;
  };
  const pendingCount=Object.values(cadence).reduce((n,c)=>n+c.items.filter(i=>!i.done&&_isPendingManaged(i)).length,0);
  /* ── Client "Discuss at our next catch-up" flags ────────────────────────
     Loaded once per mount. Cheap (one call, listing only clients that have
     flags) and it must be present before the Prep Notes list paints, or the
     badge appears a beat late and reads as a flicker. */
  useEffect(()=>{
    let cancelled=false;
    fetchDiscussPending()
      .then(rows=>{if(!cancelled)setDiscussPending(rows||[]);})
      .catch(()=>{/* a missing badge must never break the tab */});
    return()=>{cancelled=true;};
  },[]);

  /* Resolve a prep's client to a companyId THE SAME WAY loadPrepNoteDataForPrep
     does. A second, looser resolution here would badge one hotel with another
     hotel's questions — the exact cross-client failure CLAUDE.md documents for
     first-word matching. If the name does not resolve, no badge: showing it
     against the wrong client is far worse than not showing it. */
  const prepCompanyId=(prep)=>{
    if(!prep?.client)return null;
    const c=atCompanies.find(x=>normCo(x.companyName)===normCo(prep.client)
      ||x.companyName?.toLowerCase()===prep.client?.toLowerCase());
    return c?c.id:null;
  };
  const discussForPrep=(prep)=>{
    const cid=prepCompanyId(prep);
    if(!cid)return null;
    return discussPending.find(d=>String(d.companyId)===String(cid))||null;
  };

  const upcomingMeetingPreps=monthlyMeetingPreps();
  const prepsDue=upcomingMeetingPreps.filter(p=>p.isDue).length;
  const prepsUpcoming=upcomingMeetingPreps.filter(p=>!p.isDue).length;

  // Resolve which AT company a selected block belongs to.
  // Tries title match first, then falls back to the meetings array by startTime
  // (handles blocks where client name wasn't appended, e.g. old AI-planned events)
  const resolveTitleClient=(block)=>{
    if(!block) return null;
    // 1. Title contains company name directly
    let match = atCompanies.find(c =>
      block.title?.toLowerCase().includes(c.companyName.toLowerCase()) ||
      normCo(block.title||"").includes(normCo(c.companyName))
    );
    if(match) return match;
    // 2. Look up by startTime in live meetings (uses attendee-email-resolved client)
    if(block.startTime) {
      const today = todayISO();
      const liveMeeting = meetings.find(m => {
        const mDate = m.localDateStr || String(m.date||"").slice(0,10);
        if(mDate !== today || !m.client) return false;
        // Exact startTime match (meeting block) OR 30min before (prep block)
        return m.startTime === block.startTime ||
               Math.abs(timeToMins(m.startTime) - timeToMins(block.startTime) - 30) <= 15;
      });
      if(liveMeeting?.client) {
        match = atCompanies.find(c =>
          liveMeeting.client.toLowerCase() === c.companyName.toLowerCase() ||
          normCo(liveMeeting.client) === normCo(c.companyName)
        );
      }
    }
    return match || null;
  };
  const sortedMeetings=[...meetings]
    .filter(m=>{
      if(!m.date) return false;
      const d=new Date(m.date);
      if(isNaN(d.getTime())) return false;
      const today=new Date();today.setHours(0,0,0,0);
      const limit=new Date(today);limit.setDate(limit.getDate()+7);
      return d>=today && d<=limit;
    })
    .sort((a,b)=>new Date(a.date)-new Date(b.date));
  const todayTodosCount=todos.filter(t=>!t.done&&t.dueDate===todayISO()).length;
  const overdueTodosCount=todos.filter(t=>!t.done&&t.dueDate<todayISO()).length;
  const pendingExtracted=extracted.filter(e=>!e.accepted).length;

  const emailSource = liveEmails.length > 0 ? liveEmails : EMAILS;
  const visibleEmails=emailSource.filter(e=>{
    if(!focusing)return true;
    if(e.priority==="urgent"&&filters.allowUrgent)return true;
    if(e.vip&&filters.allowVip)return true;
    if(e.priority==="client"&&filters.allowClient)return true;
    return !filters.blockNoise;
  });

  const priClass={high:"pri-high",med:"pri-med",low:"pri-low"};
  const todoDateClass=(t)=>{
    if(t.done)return "";
    if(t.dueDate<todayISO())return "overdue-text";
    if(t.dueDate===todayISO())return "today-text";
    return "";
  };
  const todoItemClass=(t)=>{
    if(t.done)return "td-done";
    if(t.dueDate<todayISO())return "td-overdue";
    if(t.dueDate===todayISO())return "td-today";
    return "";
  };

  const DASHBOARD_TECHS=["Craig Grant","Marcus Grigg","Dale Purdy","Srijana McCarthy"];
  const AT_COMPLETE_STATUS=5;

  // ── TechSummaryRow sub-component (needs useState — must be a proper component) ──
  const TechSummaryRow=({t,i})=>{
    const [expanded,setExpanded]=useState(false);
    const sc=t.score>=8?"#27ae60":t.score>=6?"#e67e22":"#e74c3c";
    const bg=t.score>=8?"rgba(39,174,96,0.05)":t.score>=6?"rgba(230,126,34,0.05)":"rgba(231,76,60,0.05)";
    const em=t.sentiment==="Positive"?"😊":t.sentiment==="Negative"?"😟":"😐";
    return(
      <div key={t.techName} style={{borderTop:i===0?"none":"1px solid var(--border)",background:bg,borderLeft:`4px solid ${sc}`}}>
        <div style={{padding:"8px 12px",display:"flex",alignItems:"center",gap:8,cursor:t.breakdown?.length>0?"pointer":"default"}}
          onClick={()=>t.breakdown?.length>0&&setExpanded(e=>!e)}>
          <span style={{fontSize:16,flexShrink:0}}>{em}</span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--text)"}}>{t.techName}</div>
            <div style={{fontSize:9,color:"var(--text3)"}}>{t.noteCount} notes · {t.ticketCount} tickets · {t.companies.length} client{t.companies.length!==1?"s":""}</div>
          </div>
          <div style={{flexShrink:0,textAlign:"right",marginRight:t.breakdown?.length>0?4:0}}>
            <div style={{fontSize:20,fontWeight:800,color:sc,lineHeight:1}}>{t.score.toFixed(1)}</div>
            <div style={{fontSize:8,color:"var(--text3)"}}>/10 avg</div>
          </div>
          {t.breakdown?.length>0&&(
            <div style={{fontSize:10,color:"var(--text3)",flexShrink:0}}>{expanded?"▲":"▼"}</div>
          )}
        </div>
        {expanded&&t.breakdown?.length>0&&(
          <div style={{borderTop:"1px solid var(--border)",background:"rgba(0,0,0,0.015)"}}>
            <div style={{padding:"4px 12px 2px",fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",borderBottom:"1px solid var(--border)",background:"white"}}>By Client</div>
            {t.breakdown.map((b,j)=>{
              const bsc=b.score>=8?"#27ae60":b.score>=6?"#e67e22":"#e74c3c";
              return(
                <div key={j} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"6px 12px",borderTop:j===0?"none":"1px solid var(--border)",background:"white"}}>
                  <div style={{width:3,borderRadius:2,alignSelf:"stretch",background:bsc,flexShrink:0,minHeight:20}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <span style={{fontSize:10,fontWeight:600,color:"var(--text)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.companyName}</span>
                      <span style={{fontSize:12,fontWeight:700,color:bsc,flexShrink:0}}>{b.score}<span style={{fontSize:8,fontWeight:400,color:"var(--text3)"}}>/10</span></span>
                    </div>
                    {b.summary&&<div style={{fontSize:9,color:"var(--text3)",lineHeight:1.3}}>{b.summary}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── AutotaskDashboard sub-component ──────────────────────────────────────────
  const AutotaskDashboard=({onGetData,onRefreshAtSync,atSyncing,atCompanies,onRunSentiment,preloadedData,dattoData={sites:[],devices:[],alerts:[]},onTriggerDattoSync,onRefreshDattoData})=>{
    const [rmmSyncing,setRmmSyncing]=useState(false);
    const [rmmExpanded,setRmmExpanded]=useState(false);
    const [tickets,setTickets]=useState([]);
    const [ticketNotes,setTicketNotes]=useState([]);
    const [resources,setResources]=useState([]);
    const [statuses,setStatuses]=useState([]);
    const [loading,setLoading]=useState(true);
    // Seed sentiment from prop at mount only — never re-seed from parent to avoid re-renders
    // Parent polling updates sentimentData, but that does NOT flow back down here after mount
    const [sentiment,setSentiment]=useState(()=>preloadedSentiment||null);
    const [sentimentLoading,setSentimentLoading]=useState(false);
    const [sentimentError,setSentimentError]=useState(null);
    const [coachingModal,setCoachingModal]=useState(null); // {techName, companyName, score, coaching}
    // One-time seed: if preloadedSentiment arrives slightly after mount (async load), catch it once
    const sentimentSeededRef=useRef(false);
    useEffect(()=>{
      if(preloadedSentiment?.results&&!sentimentSeededRef.current){
        sentimentSeededRef.current=true;
        setSentiment(preloadedSentiment);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[preloadedSentiment?.analysedAt]); // only re-seed if analysedAt changes (different build)

    // Use preloaded data if available — no fetch delay when switching to tab
    useEffect(()=>{
      if(preloadedData?.tickets?.length>0){
        setTickets(preloadedData.tickets);
        setTicketNotes(preloadedData.ticketNotes||[]);
        setResources(preloadedData.resources||[]);
        setStatuses(preloadedData.statuses||[]);
        setLoading(false);
      } else if(onGetData){
        // Fallback: fetch if preloaded data not ready yet
        setLoading(true);
        Promise.all([
          onGetData("tickets"),
          onGetData("ticketNotes"),
          onGetData("resources"),
          onGetData("ticketStatuses"),
        ]).then(([t,n,r,s])=>{
          setTickets(t||[]);
          setTicketNotes(n||[]);
          setResources(r||[]);
          setStatuses(s||[]);
        }).catch(()=>{}).finally(()=>setLoading(false));
      }
    },[onGetData,preloadedData]);

    const AT_BASE="https://ww14.autotask.net/Autotask/AutotaskExtend/ExecuteCommand.aspx?Code=OpenTicketDetail&TicketNumber=";

    const companyMap=useMemo(()=>{const m={};atCompanies.forEach(c=>{m[c.id]=c;});return m;},[atCompanies]);
    const resourceMap=useMemo(()=>{const m={};resources.forEach(r=>{m[r.id]=r;});return m;},[resources]);
    const statusMap=useMemo(()=>{const m={};statuses.forEach(s=>{m[s.value]=s.label;});return m;},[statuses]);

    const openTickets=useMemo(()=>tickets.filter(t=>Number(t.status)!==AT_COMPLETE_STATUS),[tickets]);

    // Section A: tickets with statusID = 19 (Customer Note Added)
    const CUSTOMER_NOTE_STATUS=19;
    const customerNotedTickets=useMemo(()=>{
      return tickets
        .filter(t=>Number(t.status)===CUSTOMER_NOTE_STATUS)
        .map(t=>{
          const company=companyMap[t.companyID];
          const tech=resourceMap[t.assignedResourceID];
          // Find the most recent customer note for the preview snippet
          const tNotes=ticketNotes
            .filter(n=>String(n.ticketID)===String(t.id)&&n.creatorType===2)
            .sort((a,b)=>new Date(b.createDateTime||b.createDate||0)-new Date(a.createDateTime||a.createDate||0));
          const latestNote=tNotes[0]||null;
          // Use ticket's last activity date for "how long ago"
          const lastActivity=t.lastActivityDate||t.lastModifiedDateTime||t.createDate;
          const hoursSince=Math.round((Date.now()-new Date(lastActivity||0).getTime())/3600000);
          return {ticket:t,note:latestNote,company,tech,hoursSince};
        })
        .sort((a,b)=>b.hoursSince-a.hoursSince);
    },[tickets,ticketNotes,companyMap,resourceMap]);

    // Section B: open tickets per tech
    const techCounts=useMemo(()=>DASHBOARD_TECHS.map(name=>{
      const res=resources.find(r=>`${r.firstName} ${r.lastName}`===name);
      const count=openTickets.filter(t=>t.assignedResourceID===res?.id).length;
      return {name,count,id:res?.id};
    }),[openTickets,resources]);

    // Section C: gold/silver unassigned
    const unassignedPremium=useMemo(()=>openTickets
      .filter(t=>{
        if(t.assignedResourceID) return false;
        const c=companyMap[t.companyID];
        return c&&(c._tier==="Gold"||c._tier==="Silver");
      })
      .map(t=>{
        const company=companyMap[t.companyID];
        const hoursSince=Math.round((Date.now()-new Date(t.createDate||t.createDateTime||Date.now()).getTime())/3600000);
        return {...t,company,hoursSince};
      })
      .sort((a,b)=>b.hoursSince-a.hoursSince)
    ,[openTickets,companyMap]);

    const maxTechCount=Math.max(...techCounts.map(t=>t.count),1);
    const fmtHours=(h)=>{if(h<1)return"<1h";if(h<24)return`${h}h`;const d=Math.floor(h/24);const r=h%24;return r>0?`${d}d ${r}h`:`${d}d`;};

    const runSentimentAnalysis=async()=>{
      if(!onRunSentiment) return;
      setSentimentLoading(true);
      setSentimentError(null);
      try{
        // GET — backend reads AT data from blob, filtered to managed companies
        const result=await onRunSentiment({days:30});
        if(result?.results) setSentiment(result);
        else if(result?.message) setSentimentError(result.message);
      }catch(err){
        setSentimentError(err.message);
      }finally{
        setSentimentLoading(false);
      }
    };
    const tierColor=(tier)=>tier==="Gold"?"var(--cal)":tier==="Silver"?"var(--text2)":"var(--ops)";
    const sHdr=(title,count)=>(
      <div style={{padding:"10px 14px 8px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:"white"}}>
        <div style={{fontWeight:600,fontSize:11,color:"var(--text)",letterSpacing:"0.01em"}}>{title}</div>
        {count!==undefined&&<div style={{fontSize:10,color:"var(--text3)",background:"var(--bg)",padding:"2px 7px",borderRadius:10,border:"1px solid var(--border)",fontWeight:600}}>{count}</div>}
      </div>
    );

    if(loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text3)",fontSize:12}}>Loading Autotask data...</div>;

    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>Autotask Dashboard</div>
          <div style={{fontSize:10,padding:"2px 8px",background:"var(--strat-d)",borderRadius:4,color:"var(--strat)",fontWeight:600}}>{openTickets.length} open</div>
          <div style={{fontSize:10,color:"var(--text3)"}}>{tickets.length} synced total</div>
          <div style={{marginLeft:"auto"}}>
            <button className="btn btn-S" style={{fontSize:10,padding:"4px 12px",display:"flex",alignItems:"center",gap:6}}
              onClick={async()=>{
                if(onRefreshAtSync){
                  await onRefreshAtSync();
                  // Reload data after sync
                  if(onGetData){
                    const [t,n,r,s]=await Promise.all([onGetData("tickets"),onGetData("ticketNotes"),onGetData("resources"),onGetData("ticketStatuses")]);
                    setTickets(t||[]);setTicketNotes(n||[]);setResources(r||[]);setStatuses(s||[]);
                  }
                }
              }}
              disabled={atSyncing}>
              {atSyncing?<><span style={{display:"inline-block",animation:"spin 1s linear infinite"}}>↻</span> Syncing...</>:<><span>↻</span> Refresh AT Data</>}
            </button>
          </div>
        </div>
        {/* ── 3-COLUMN DASHBOARD ── */}
        <div style={{flex:1,overflow:"hidden",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,padding:12,background:"var(--bg)"}}>

          {/* ═══════════ LEFT COLUMN — Customer Has Added a Note ═══════════ */}
          <div style={{display:"flex",flexDirection:"column",border:"1px solid var(--border)",borderRadius:8,background:"white",overflow:"hidden",minHeight:0}}>
            {sHdr("Customer Has Added a Note — Awaiting Response",customerNotedTickets.length)}
            <div style={{flex:1,overflowY:"auto"}}>
              {customerNotedTickets.length===0&&<div style={{padding:20,textAlign:"center",color:"var(--ops)",fontSize:11,fontWeight:600}}>✓ No tickets awaiting your response</div>}
              {customerNotedTickets.map(({ticket,note,company,tech,hoursSince})=>{
                const urg=hoursSince>48?"var(--danger)":hoursSince>24?"var(--cal)":"var(--ops)";
                const tier=company?._tier||"";
                return(
                  <div key={ticket.id} style={{padding:"9px 12px",borderBottom:"1px solid var(--border)",display:"flex",gap:8,alignItems:"flex-start",transition:"background 0.1s"}}
                    onMouseOver={e=>e.currentTarget.style.background="var(--bg)"}
                    onMouseOut={e=>e.currentTarget.style.background="white"}>
                    <div style={{flexShrink:0,width:4,borderRadius:2,alignSelf:"stretch",background:urg,minHeight:36}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                        <a href={`${AT_BASE}${ticket.ticketNumber}`} target="_blank" rel="noopener noreferrer"
                          style={{fontSize:11,fontWeight:700,color:"var(--strat)",textDecoration:"none",fontFamily:"monospace",letterSpacing:"0.02em"}}
                          title="Open in Autotask">
                          {ticket.ticketNumber}
                        </a>
                        {tier&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:tier==="Gold"?"rgba(211,84,0,0.12)":"rgba(74,85,104,0.1)",color:tierColor(tier),fontWeight:700}}>{tier}</span>}
                        <span style={{fontSize:10,color:"var(--text2)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:500}}>{company?.companyName||"Unknown"}</span>
                        <span style={{fontSize:11,fontWeight:700,color:urg,flexShrink:0}}>{fmtHours(hoursSince)}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text)",fontWeight:500,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ticket.title}</div>
                      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:10,color:"var(--text3)",background:"var(--bg)",padding:"1px 5px",borderRadius:3}}>{statusMap[ticket.status]||"Unknown"}</span>
                        {tech?<span style={{fontSize:10,color:"var(--text2)"}}>→ {tech.firstName} {tech.lastName}</span>:<span style={{fontSize:10,color:"var(--danger)",fontWeight:600}}>⚠ Unassigned</span>}
                        {note?.description&&<span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{note.description.replace(/<[^>]*>/g,"").slice(0,100)}"</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ═══════════ MIDDLE COLUMN — Open by Tech + Gold/Silver Unassigned ═══════════ */}
          <div style={{display:"flex",flexDirection:"column",gap:12,minHeight:0,overflow:"hidden"}}>

            {/* Open Tickets by Tech */}
            <div style={{display:"flex",flexDirection:"column",border:"1px solid var(--border)",borderRadius:8,background:"white",overflow:"hidden",flexShrink:0}}>
              {sHdr("Open Tickets by Technician")}
              <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:2}}>
                  <div style={{fontSize:28,fontWeight:800,color:"var(--text)",lineHeight:1}}>{openTickets.length}</div>
                  <div style={{fontSize:10,color:"var(--text3)"}}>total open</div>
                </div>
                {techCounts.map(({name,count})=>{
                  const pct=Math.round((count/maxTechCount)*100);
                  return(
                    <div key={name}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3,alignItems:"center"}}>
                        <span style={{fontSize:11,color:"var(--text2)",fontWeight:500}}>{name.split(" ")[0]}</span>
                        <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>{count}</span>
                      </div>
                      <div style={{height:7,background:"var(--bg)",borderRadius:4,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${pct}%`,background:"var(--strat)",borderRadius:4}}/>
                      </div>
                    </div>
                  );
                })}
                <div style={{paddingTop:8,borderTop:"1px solid var(--border)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3,alignItems:"center"}}>
                    <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Unassigned</span>
                    <span style={{fontSize:12,fontWeight:700,color:"var(--danger)"}}>{openTickets.filter(t=>!t.assignedResourceID).length}</span>
                  </div>
                  <div style={{height:7,background:"var(--bg)",borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.round((openTickets.filter(t=>!t.assignedResourceID).length/maxTechCount)*100)}%`,background:"var(--danger)",borderRadius:4}}/>
                  </div>
                </div>
              </div>
            </div>

            {/* Gold & Silver Unassigned */}
            <div style={{display:"flex",flexDirection:"column",border:"1px solid var(--border)",borderRadius:8,background:"white",overflow:"hidden",flex:1,minHeight:0}}>
              {sHdr("Gold & Silver — Unassigned",unassignedPremium.length)}
              <div style={{flex:1,overflowY:"auto"}}>
                {unassignedPremium.length===0&&<div style={{padding:14,textAlign:"center",color:"var(--ops)",fontSize:11,fontWeight:600}}>✓ All Gold & Silver tickets assigned</div>}
                {unassignedPremium.map(t=>{
                  const urg=t.hoursSince>24?"var(--danger)":"var(--cal)";
                  return(
                    <div key={t.id} style={{padding:"7px 12px",borderBottom:"1px solid var(--border)",display:"flex",gap:8,alignItems:"flex-start"}}>
                      <div style={{flexShrink:0,width:4,borderRadius:2,alignSelf:"stretch",background:tierColor(t.company?._tier),minHeight:28}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                          <a href={`${AT_BASE}${t.ticketNumber}`} target="_blank" rel="noopener noreferrer"
                            style={{fontSize:10,fontWeight:700,color:"var(--strat)",textDecoration:"none",fontFamily:"monospace"}}>
                            {t.ticketNumber}
                          </a>
                          <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:t.company?._tier==="Gold"?"rgba(211,84,0,0.12)":"rgba(74,85,104,0.1)",color:tierColor(t.company?._tier),fontWeight:700}}>{t.company?._tier}</span>
                          <span style={{fontSize:10,fontWeight:700,color:urg,marginLeft:"auto"}}>{fmtHours(t.hoursSince)}</span>
                        </div>
                        <div style={{fontSize:10,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:500}}>{t.title}</div>
                        <div style={{fontSize:10,color:"var(--text3)"}}>{t.company?.companyName}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>{/* end middle column */}

          {/* ═══════════ RIGHT COLUMN — Customer Sentiment Analysis ═══════════ */}
          <div style={{display:"flex",flexDirection:"column",border:"1px solid var(--border)",borderRadius:8,background:"white",overflow:"hidden",minHeight:0}}>
            <div style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,flexShrink:0,flexWrap:"wrap"}}>
              <span style={{fontWeight:700,fontSize:11,color:"var(--text)"}}>Customer Sentiment Analysis</span>
              <span style={{fontSize:10,color:"var(--text3)"}}>
                {sentiment?.partial ? "🔄 Building..." : "All active clients · last 30 days"}
                {sentiment?.results?.length > 0 && sentiment?.partial &&
                  <span style={{marginLeft:6,color:"var(--tact)"}}>({sentiment.results.length} so far)</span>}
              </span>
              {sentiment?.analysedAt&&<span style={{fontSize:9,color:"var(--text3)",marginLeft:"auto"}}>Updated {new Date(sentiment.analysedAt).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>}
            </div>
            <div style={{padding:"8px 14px 6px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              {sentimentError&&<span style={{fontSize:10,color:"var(--danger)",flex:1}}>{sentimentError}</span>}
              <button className="btn btn-S" style={{fontSize:10,padding:"4px 12px",marginLeft:sentimentError?0:"auto"}}
                onClick={runSentimentAnalysis}
                disabled={sentimentLoading||!onRunSentiment}>
                {sentimentLoading?"🔍 Analysing...":"🔍 Refresh Sentiment"}
              </button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"10px 10px 4px"}}>
              {!sentiment&&!sentimentLoading&&!sentimentError&&(
                <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11}}>
                  <div style={{fontSize:24,marginBottom:8}}>🔄</div>
                  Sentiment analysis is being built in the background.<br/>
                  <span style={{fontSize:10}}>Check back in a few minutes — data will appear automatically.</span>
                </div>
              )}
              {sentimentLoading&&!sentiment&&(
                <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11}}>🔍 Analysing customer sentiment…</div>
              )}
              {sentiment?.results?.length===0&&(
                <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11}}>No sentiment data found for the last 30 days</div>
              )}
              {sentiment?.results?.map(r=>{
                const emoji=r.sentiment==="Positive"?"😊":r.sentiment==="Negative"?"😟":"😐";
                const scoreColor=r.score>=8?"#27ae60":r.score>=6?"#e67e22":"#e74c3c";
                const bg=r.score>=8?"rgba(39,174,96,0.07)":r.score>=6?"rgba(230,126,34,0.07)":"rgba(231,76,60,0.07)";
                const techScore=(s)=>s>=8?"#27ae60":s>=6?"#e67e22":"#e74c3c";
                const techBg=(s)=>s>=8?"rgba(39,174,96,0.08)":s>=6?"rgba(230,126,34,0.08)":"rgba(231,76,60,0.08)";
                return(
                  <div key={r.companyId} style={{
                    border:`1px solid ${scoreColor}`,
                    borderLeft:`5px solid ${scoreColor}`,
                    borderRadius:8,
                    background:bg,
                    overflow:"hidden",
                    marginBottom:10,
                  }}>
                    {/* ── Company header ── */}
                    <div style={{padding:"11px 14px 8px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                        <span style={{fontSize:18}}>{emoji}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.companyName}</div>
                          <div style={{fontSize:9,color:r.tier==="Gold"?"var(--cal)":"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em"}}>{r.tier}</div>
                        </div>
                        <div style={{flexShrink:0,textAlign:"right"}}>
                          <div style={{fontSize:20,fontWeight:800,color:scoreColor,lineHeight:1}}>{r.score}</div>
                          <div style={{fontSize:8,color:"var(--text3)"}}>/10</div>
                        </div>
                      </div>
                      <div style={{fontSize:10,color:"var(--text2)",lineHeight:1.4,marginBottom:5}}>{r.summary}</div>
                      {r.flags?.length>0&&(
                        <div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:5}}>
                          {r.flags.map((f,i)=>{
                            const isBad=/(waiting|chase|escalat|urgent|overdue)/i.test(f);
                            return(
                              <span key={i} style={{fontSize:9,padding:"1px 5px",borderRadius:3,
                                background:isBad?"rgba(231,76,60,0.1)":"var(--bg)",
                                color:isBad?"var(--danger)":"var(--text3)",
                                border:"1px solid var(--border)"}}>
                                {f}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <div style={{fontSize:9,color:"var(--text3)"}}>{r.noteCount} notes · {r.ticketCount} tickets{r.chaseUps>0?` · ⚠ ${r.chaseUps} follow-up${r.chaseUps!==1?"s":""} detected`:""}</div>
                    </div>

                    {/* ── Per-tech breakdown ── */}
                    {r.techSentiment?.length>0&&(
                      <div style={{borderTop:`2px solid ${scoreColor}`}}>
                        <div style={{
                          padding:"6px 14px 5px",
                          fontSize:9,
                          fontWeight:700,
                          letterSpacing:"0.12em",
                          textTransform:"uppercase",
                          color:"var(--text3)",
                          background:"white",
                          borderBottom:"1px solid var(--border)",
                        }}>By Technician</div>
                        {r.techSentiment.map((ts,i)=>{
                          const tc=techScore(ts.score);
                          const tb=techBg(ts.score);
                          const te=ts.sentiment==="Positive"?"😊":ts.sentiment==="Negative"?"😟":"😐";
                          return(
                            <div key={i}
                              onClick={ts.coaching&&(ts.score||10)<7?()=>setCoachingModal({techName:ts.techName,companyName:r.companyName,score:ts.score,sentiment:ts.sentiment,coaching:ts.coaching}):undefined}
                              style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 14px",borderTop:i===0?"none":"1px solid var(--border)",background:"white",cursor:ts.coaching&&(ts.score||10)<7?"pointer":"default"}}>
                              <div style={{width:4,borderRadius:2,alignSelf:"stretch",background:tc,flexShrink:0,minHeight:28}}/>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                                  <span style={{fontSize:11}}>{te}</span>
                                  <span style={{fontSize:10,fontWeight:600,color:"var(--text)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ts.techName}</span>
                                  {ts.coaching&&(ts.score||10)<7&&<span style={{fontSize:8,background:"rgba(230,126,34,0.12)",color:"var(--cal)",padding:"1px 5px",borderRadius:3,fontWeight:600,flexShrink:0}}>coaching ▸</span>}
                                  <span style={{fontSize:12,fontWeight:800,color:tc,flexShrink:0}}>{ts.score}<span style={{fontSize:8,fontWeight:400,color:"var(--text3)"}}>/10</span></span>
                                </div>
                                <div style={{fontSize:9,color:"var(--text3)",lineHeight:1.3}}>{ts.summary}</div>
                                <div style={{fontSize:9,color:"var(--text3)",marginTop:2}}>{ts.noteCount} notes · {ts.ticketCount} ticket{ts.ticketCount!==1?"s":""}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Tech Summary Card ── */}
              {sentiment?.techSummary?.length>0&&(
                <div style={{
                  border:"2px solid var(--border)",
                  borderTop:"3px solid var(--strat)",
                  borderRadius:8,
                  background:"white",
                  overflow:"hidden",
                  marginTop:4,
                }}>
                  {/* Header */}
                  <div style={{padding:"10px 14px 8px",borderBottom:"1px solid var(--border)",background:"var(--strat-d)"}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--strat)",letterSpacing:"0.01em"}}>🧑‍💻 Technician Summary — All Clients</div>
                    <div style={{fontSize:9,color:"var(--text3)",marginTop:2}}>Averaged across all clients · last 30 days</div>
                  </div>
                  {/* Tech rows */}
                  {sentiment.techSummary.map((t,i)=>(
                    <TechSummaryRow key={t.techName} t={t} i={i}/>
                  ))}
                </div>
              )}
            </div>
          </div>{/* end right column */}

        </div>{/* end 3-column grid */}

        {/* ── Datto RMM Device Health ──────────────────────────────────── */}
        {(()=>{
          const rmmSites=dattoData?.sites||[];
          const rmmAlerts=dattoData?.alerts||[];
          const rmmDevices=dattoData?.devices||[];
          // rmmSyncing and rmmExpanded are declared at component level above
          if(!rmmSites.length&&!rmmSyncing) return(
            <div style={{margin:"12px 0 0",padding:"12px 16px",background:"rgba(11,128,165,0.04)",border:"1px dashed rgba(11,128,165,0.25)",borderRadius:8,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:18}}>🖥️</span>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--text)"}}>Datto RMM not configured</div>
                <div style={{fontSize:10,color:"var(--text3)"}}>Add DATTO-RMM-API-URL, DATTO-RMM-API-KEY and DATTO-RMM-API-SECRET to Key Vault, then run a sync. Only clients ticked as Managed in Client Register will be synced.</div>
              </div>
              {onTriggerDattoSync&&<button className="btn btn-S" style={{fontSize:10,padding:"4px 12px",flexShrink:0}}
                onClick={async()=>{setRmmSyncing(true);try{await onTriggerDattoSync?.();setTimeout(async()=>{await onRefreshDattoData?.();setRmmSyncing(false);},20000);}catch{setRmmSyncing(false);}}}
                disabled={rmmSyncing}>{rmmSyncing?"⏳ Syncing...":"🔄 Sync Datto RMM"}</button>}
            </div>
          );
          const totalAlerts=rmmAlerts.length;
          const critAlerts=rmmAlerts.filter(a=>(a.alertContext?.alertSeverity||a.severity||"").toLowerCase()==="critical"||a.priority===1).length;
          const totalDevices=rmmDevices.length;
          const offlineDevices=rmmDevices.filter(d=>!d.online).length;
          const avgPatch=rmmSites.filter(s=>s.patchPercent!=null).length>0
            ?Math.round(rmmSites.filter(s=>s.patchPercent!=null).reduce((a,s)=>a+s.patchPercent,0)/rmmSites.filter(s=>s.patchPercent!=null).length)
            :null;
          return(
            <div style={{margin:"12px 0 0",border:"1px solid var(--border)",borderRadius:8,background:"white",overflow:"hidden"}}>
              {/* RMM header */}
              <div style={{padding:"10px 16px",borderBottom:rmmExpanded?"1px solid var(--border)":"none",display:"flex",alignItems:"center",gap:10,background:"linear-gradient(135deg,#0b2240,#1a3a5c)",cursor:"pointer"}}
                onClick={()=>setRmmExpanded(e=>!e)}>
                <span style={{fontSize:14}}>🖥️</span>
                <div style={{flex:1}}>
                  <span style={{fontSize:12,fontWeight:700,color:"white",letterSpacing:"-0.2px"}}>Datto RMM Device Health</span>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.6)",marginLeft:10}}>{dattoData?.syncedAt?`Synced ${new Date(dattoData.syncedAt).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}`:"Not synced"}</span>
                </div>
                {/* Summary pills */}
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  {critAlerts>0&&<span style={{fontSize:10,fontWeight:700,background:"rgba(231,76,60,0.9)",color:"white",padding:"2px 8px",borderRadius:10}}>🔥 {critAlerts} critical</span>}
                  {totalAlerts>0&&<span style={{fontSize:10,fontWeight:700,background:"rgba(230,126,34,0.85)",color:"white",padding:"2px 8px",borderRadius:10}}>⚠ {totalAlerts} alerts</span>}
                  {offlineDevices>0&&<span style={{fontSize:10,fontWeight:700,background:"rgba(74,85,104,0.8)",color:"white",padding:"2px 8px",borderRadius:10}}>💤 {offlineDevices} offline</span>}
                  {avgPatch!=null&&<span style={{fontSize:10,fontWeight:700,background:avgPatch<80?"rgba(230,126,34,0.85)":"rgba(39,174,96,0.85)",color:"white",padding:"2px 8px",borderRadius:10}}>🩹 {avgPatch}% patched</span>}
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.6)"}}>{rmmExpanded?"▲":"▼"}</span>
                </div>
                <button className="btn" style={{fontSize:9,padding:"3px 10px",background:"rgba(255,255,255,0.1)",color:"white",border:"1px solid rgba(255,255,255,0.2)",flexShrink:0}}
                  onClick={async(e)=>{e.stopPropagation();setRmmSyncing(true);try{await onTriggerDattoSync?.();setTimeout(async()=>{await onRefreshDattoData?.();setRmmSyncing(false);},15000);}catch{setRmmSyncing(false);}}}
                  disabled={rmmSyncing}>{rmmSyncing?"⏳":"🔄"} Sync</button>
              </div>
              {rmmExpanded&&(
                <div style={{overflowX:"auto"}}>
                  {/* Per-site grid */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:1,background:"var(--border)"}}>
                    {rmmSites.sort((a,b)=>(b.alertCount||0)-(a.alertCount||0)).map(s=>{
                      const siteAlerts=rmmAlerts.filter(a=>a.alertSourceInfo?.siteUid===s.uid);
                      const critSite=siteAlerts.filter(a=>(a.alertContext?.alertSeverity||a.severity||"").toLowerCase()==="critical").length;
                      const patchColor=s.patchPercent==null?"var(--text3)":s.patchPercent>=90?"var(--ops)":s.patchPercent>=70?"var(--cal)":"var(--danger)";
                      const bg=critSite>0?"rgba(231,76,60,0.03)":s.alertCount>0?"rgba(230,126,34,0.02)":"white";
                      return(
                        <div key={s.uid} style={{background:bg,padding:"10px 14px"}}>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text)",marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={s.name}>{s.name}</div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                            <span style={{fontSize:9,background:"var(--bg)",padding:"1px 5px",borderRadius:3,color:"var(--text3)"}}>{s.deviceCount||0} devices</span>
                            {s.offlineCount>0&&<span style={{fontSize:9,background:"rgba(74,85,104,0.1)",padding:"1px 5px",borderRadius:3,color:"var(--text2)",fontWeight:600}}>💤 {s.offlineCount} offline</span>}
                            {s.alertCount>0&&<span style={{fontSize:9,background:critSite>0?"rgba(231,76,60,0.12)":"rgba(230,126,34,0.12)",padding:"1px 5px",borderRadius:3,color:critSite>0?"var(--danger)":"var(--cal)",fontWeight:600}}>{critSite>0?"🔥":"⚠"} {s.alertCount} alert{s.alertCount!==1?"s":""}</span>}
                            {s.patchPercent!=null&&<span style={{fontSize:9,background:"var(--bg)",padding:"1px 5px",borderRadius:3,color:patchColor,fontWeight:600}}>🩹 {s.patchPercent}%</span>}
                            {s.onlineCount>0&&<span style={{fontSize:9,background:"rgba(39,174,96,0.1)",padding:"1px 5px",borderRadius:3,color:"var(--ops)"}}>✓ {s.onlineCount} online</span>}
                          </div>
                          {/* Top alerts for this site */}
                          {siteAlerts.slice(0,2).map((a,i)=>(
                            <div key={i} style={{marginTop:4,fontSize:9,color:"var(--text3)",lineHeight:1.3,background:"rgba(231,76,60,0.04)",padding:"2px 6px",borderRadius:3,borderLeft:"2px solid var(--danger)"}}>
                              {(a.alertMessage||a.message||"Alert").slice(0,60)}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  {rmmSites.length===0&&<div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11}}>No Datto data — click Sync to load</div>}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Coaching Modal ───────────────────────────────────────────── */}
        {coachingModal&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
            onClick={e=>{if(e.target===e.currentTarget)setCoachingModal(null);}}>
            <div style={{background:"white",borderRadius:12,width:540,maxWidth:"95vw",maxHeight:"85vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,0.25)"}}>
              {/* Modal header */}
              <div style={{padding:"16px 20px 14px",borderBottom:"1px solid var(--border)",background:"linear-gradient(135deg,#fff7ed,#fef3c7)",flexShrink:0}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--cal)",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:4}}>🎓 Staff Coaching Notes</div>
                    <div style={{fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:2}}>{coachingModal.techName}</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>{coachingModal.companyName} · Score: <span style={{fontWeight:700,color:"var(--cal)"}}>{coachingModal.score}/10</span> · {coachingModal.sentiment}</div>
                  </div>
                  <button onClick={()=>setCoachingModal(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--text3)",padding:"0 4px",lineHeight:1}}>×</button>
                </div>
              </div>
              {/* Modal body */}
              <div style={{overflowY:"auto",padding:"18px 20px",display:"flex",flexDirection:"column",gap:16}}>
                {/* What went wrong */}
                {coachingModal.coaching.whatWentWrong?.length>0&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--danger)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:14}}>⚠️</span> What contributed to the low score
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {coachingModal.coaching.whatWentWrong.map((w,i)=>(
                        <div key={i} style={{padding:"7px 10px",background:"rgba(231,76,60,0.06)",border:"1px solid rgba(231,76,60,0.15)",borderRadius:6,fontSize:11,color:"var(--text2)",lineHeight:1.4}}>
                          {w}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Key moments */}
                {coachingModal.coaching.keyMoments?.length>0&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:14}}>💬</span> Key moments from the interaction
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {coachingModal.coaching.keyMoments.map((m,i)=>(
                        <div key={i} style={{padding:"7px 10px",background:"var(--bg)",borderLeft:"3px solid var(--border2)",borderRadius:"0 6px 6px 0",fontSize:11,color:"var(--text2)",lineHeight:1.4,fontStyle:"italic"}}>
                          {m}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Coaching tips */}
                {coachingModal.coaching.coachingTips?.length>0&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--strat)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:14}}>💡</span> Coaching tips for improvement
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {coachingModal.coaching.coachingTips.map((t,i)=>(
                        <div key={i} style={{padding:"7px 10px",background:"rgba(11,128,165,0.05)",border:"1px solid rgba(11,128,165,0.15)",borderRadius:6,fontSize:11,color:"var(--text2)",lineHeight:1.4,display:"flex",gap:8,alignItems:"flex-start"}}>
                          <span style={{color:"var(--strat)",fontWeight:700,flexShrink:0}}>{i+1}.</span>{t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Positives */}
                {coachingModal.coaching.positives?.length>0&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--ops)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:14}}>✅</span> What they did well
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {coachingModal.coaching.positives.map((p,i)=>(
                        <div key={i} style={{padding:"7px 10px",background:"rgba(39,174,96,0.06)",border:"1px solid rgba(39,174,96,0.15)",borderRadius:6,fontSize:11,color:"var(--text2)",lineHeight:1.4}}>
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)",flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--bg)"}}>
                <div style={{fontSize:10,color:"var(--text3)"}}>Generated from anonymised ticket notes · For internal coaching use only</div>
                <button className="btn" onClick={()=>setCoachingModal(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return(
    <>
      <style>{css}</style>
      <div className="app" style={{cursor:isDragging?"col-resize":undefined}}>

        <div className="noise"/>

        {/* ── PLAN ITEM DETAIL MODAL ── */}
          {planItemModal&&(
            <div className="modal-overlay" onClick={()=>setPlanItemModal(null)}>
              <div className="modal" style={{maxWidth:420,width:"90%"}} onClick={e=>e.stopPropagation()}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <div className={`lb lb-${planItemModal.layer}`} style={{fontSize:10}}>{LAYERS[planItemModal.layer]?.label||planItemModal.layer}</div>
                  {planItemModal.sourceType&&<div style={{fontSize:10,color:"var(--text3)",background:"var(--bg)",padding:"1px 6px",borderRadius:3,border:"1px solid var(--border)",textTransform:"capitalize"}}>{planItemModal.sourceType}</div>}
                  {planItemModal.dismissed&&<div style={{fontSize:10,color:"var(--danger)",background:"rgba(231,76,60,0.08)",padding:"1px 6px",borderRadius:3,border:"1px solid rgba(231,76,60,0.2)"}}>Dismissed</div>}
                  <button className="ico-btn" style={{marginLeft:"auto",fontSize:16}} onClick={()=>setPlanItemModal(null)}>×</button>
                </div>
                <div style={{fontWeight:600,fontSize:13,color:"var(--text)",marginBottom:8,lineHeight:1.4,textDecoration:planItemModal.dismissed?"line-through":"none"}}>{planItemModal.title}</div>
                {planItemModal.client&&<div style={{fontSize:11,color:"var(--text2)",marginBottom:6}}><span style={{color:"var(--text3)"}}>Client: </span>{planItemModal.client}</div>}
                {planItemModal.dueDate&&(
                  <div style={{fontSize:11,color:"var(--text2)",marginBottom:6}}>
                    <span style={{color:"var(--text3)"}}>Due: </span>
                    {(()=>{try{const d=new Date(planItemModal.dueDate+"T12:00:00");const today=new Date();today.setHours(0,0,0,0);const days=Math.ceil((d-today)/(1000*60*60*24));const fmt=d.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",year:"numeric"});const label=days<0?`Overdue by ${Math.abs(days)}d`:days===0?"Today":days===1?"Tomorrow":fmt;return<span style={{color:days<=0?"var(--danger)":days===1?"var(--ops)":"var(--text2)",fontWeight:days<=1?600:400}}>{label}</span>;}catch{return planItemModal.dueDate;}})()}
                  </div>
                )}
                {planItemModal.duration&&<div style={{fontSize:11,color:"var(--text2)",marginBottom:6}}><span style={{color:"var(--text3)"}}>Est. duration: </span>{planItemModal.duration} min</div>}
                {(planItemModal.desc||planItemModal.notes)&&(
                  <div style={{fontSize:11,color:"var(--text2)",marginTop:8,padding:"8px 10px",background:"var(--bg)",borderRadius:4,lineHeight:1.5,border:"1px solid var(--border)"}}>
                    {planItemModal.desc||planItemModal.notes}
                  </div>
                )}
                {/* Consolidated operational block — show per-client report links */}
                {planItemModal._consolidated&&planItemModal._clients?.length>0&&(()=>{
                  const reportType=planItemModal._reportType;
                  // Build report URL for a given client
                  const reportUrl=(clientId)=>{
                    const _apiBase=window.location.hostname==="localhost"
                      ?"http://localhost:3001"
                      :"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                    const tok=window.__mspToken||"";
                    const today=new Date().toISOString().slice(0,10);
                    if(reportType==="client-health")
                      return `/client-health.html?companyId=${clientId}&token=${encodeURIComponent(tok)}&api=${encodeURIComponent(_apiBase)}`;
                    if(reportType==="patch-status")
                      return `/patch-status.html?companyId=${clientId}&token=${encodeURIComponent(tok)}&api=${encodeURIComponent(_apiBase)}`;
                    if(reportType==="open-tickets")
                      return `/weekly-report.html?companyId=${clientId}&date=${today}&token=${encodeURIComponent(tok)}&api=${encodeURIComponent(_apiBase)}`;
                    return null;
                  };
                  const reportLabel=reportType==="client-health"?"Health Report":reportType==="patch-status"?"Patch Report":reportType==="open-tickets"?"Ticket Report":"Report";
                  return(
                    <div style={{marginTop:12}}>
                      <div style={{fontSize:10,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--text3)",marginBottom:6}}>
                        Managed Clients ({planItemModal._clients.length})
                        {reportType&&<span style={{marginLeft:6,color:"var(--ops)",fontWeight:400,textTransform:"none",letterSpacing:0}}>— click to open {reportLabel}</span>}
                      </div>
                      <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                        {planItemModal._clients.map(c=>{
                          const tc=c.tier==="Gold"?"#f59e0b":c.tier==="Silver"?"#64748b":c.tier==="Bronze"?"#b45309":c.tier==="Copper"?"#c2410c":"var(--text3)";
                          const url=reportUrl(c.id);
                          return(
                            <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",background:"var(--bg)",borderRadius:4,border:"1px solid var(--border)"}}>
                              <span style={{fontSize:9,fontWeight:600,color:tc,minWidth:46,textAlign:"center",background:`${tc}18`,padding:"1px 5px",borderRadius:3,border:`1px solid ${tc}30`}}>{c.tier}</span>
                              <span style={{flex:1,fontSize:11,color:"var(--text)",fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>
                              {url?(
                                <a href={url} target="_blank" rel="noopener noreferrer"
                                  style={{fontSize:9,padding:"2px 10px",borderRadius:3,background:"var(--ops)",color:"#fff",fontWeight:600,textDecoration:"none",flexShrink:0,border:"none",cursor:"pointer"}}
                                  title={`Open ${reportLabel} for ${c.name}`}>
                                  Open ↗
                                </a>
                              ):(
                                <button className="btn btn-S" style={{fontSize:9,padding:"2px 8px",flexShrink:0}}
                                  onClick={()=>{setTab("autotask");setPlanItemModal(null);}}
                                  title={`Review for ${c.name}`}>
                                  Review →
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
                  {planItemModal.dismissed?(
                    <button className="btn btn-S" style={{fontSize:11}}
                      onClick={()=>{setDismissedPlanItems(s=>{const next=new Set([...s]);next.delete(planItemModal.id);saveSnapshot({dismissedPlanItems:[...next],planDismissDate:todayISO()});return next;});setPlanItemModal(null);}}>
                      ↩ Restore to inbox
                    </button>
                  ):(
                    <button className="btn btn-S" style={{background:"rgba(231,76,60,0.08)",color:"var(--danger)",borderColor:"rgba(231,76,60,0.25)",fontSize:11}}
                      onClick={()=>{dismissPlanItem(planItemModal.id);setPlanItemModal(null);}}>
                      − Dismiss
                    </button>
                  )}
                  {!planItemModal.dismissed&&(
                    <button className="btn btn-S" style={{fontSize:11}}
                      onClick={()=>{addToDayPlan(planItemModal,null);setPlanItemModal(null);}}>
                      + Add to plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* CADENCE DUE DATE MODAL */}
          {cadDueDateModal&&(
            <div className="modal-overlay" onClick={e=>{if(e.target.className==="modal-overlay")setCadDueDateModal(null);}}>
              <div className="modal" style={{maxWidth:360}}>
                <div style={{background:"linear-gradient(135deg,var(--dt-navy),var(--tact))",padding:"16px 20px",borderRadius:"12px 12px 0 0"}}>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:14,color:"white"}}>Set Due Date</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.7)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cadDueDateModal.title}</div>
                </div>
                <div style={{padding:"18px 20px",display:"flex",flexDirection:"column",gap:12}}>
                  <input type="date" className="inp"
                    value={cadDueDateModal.dueDate}
                    onChange={e=>setCadDueDateModal(m=>({...m,dueDate:e.target.value}))}
                  />
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[["This week",7],["2 weeks",14],["1 month",30],["3 months",90]].map(([label,days])=>(
                      <button key={label} className="btn" style={{fontSize:10,padding:"3px 10px"}}
                        onClick={()=>{
                          const d=new Date();d.setDate(d.getDate()+days);
                          setCadDueDateModal(m=>({...m,dueDate:d.toISOString().slice(0,10)}));
                        }}>{label}</button>
                    ))}
                  </div>
                  <div className="modal-acts">
                    <button className="btn btn-S"
                      style={{flex:1,justifyContent:"center",background:"var(--tact)",color:"white",borderColor:"var(--tact)"}}
                      onClick={()=>updateCadDueDate(cadDueDateModal.layer,cadDueDateModal.id,cadDueDateModal.dueDate)}
                      disabled={!cadDueDateModal.dueDate}>Save Date</button>
                    <button className="btn" onClick={()=>setCadDueDateModal(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* RESCHEDULE MODAL */}
        {rescheduleModal&&(
          <div className="modal-overlay" onClick={e=>{if(e.target.className==="modal-overlay")setRescheduleModal(null);}}>
            <div className="modal">
              <div style={{background:"linear-gradient(135deg,var(--dt-navy),var(--dt-secondary))",padding:"18px 22px 16px",borderRadius:"12px 12px 0 0"}}>
                <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:15,color:"white",marginBottom:3}}>Reschedule Task</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Pick a new due date for this incomplete task.</div>
              </div>
              <div style={{padding:"18px 22px"}}>
                <div style={{fontSize:11,color:"var(--text)",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px",marginBottom:14,fontStyle:"italic"}}>"{rescheduleModal.title}"</div>
                <div style={{fontSize:10,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6,fontWeight:600}}>New due date</div>
                <input type="date" className="inp" value={rescheduleDate} onChange={e=>setRescheduleDate(e.target.value)} style={{marginBottom:0}}/>
                <div className="modal-acts">
                  <button className="btn btn-S" style={{flex:1,justifyContent:"center",background:"var(--strat)",color:"white",borderColor:"var(--strat)"}} onClick={confirmReschedule}>Reschedule</button>
                  <button className="btn" onClick={()=>setRescheduleModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PREP TOAST */}
        {prepToast&&(
          <div className="prep-toast">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
              <span style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:13,color:"var(--dt-secondary)"}}>📅 Prep Work Suggested</span>
              <button style={{width:24,height:24,borderRadius:"50%",border:"1px solid var(--border2)",background:"var(--s2)",color:"var(--text3)",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>dismissPrep(prepToast.meeting.id)}>×</button>
            </div>
            <div style={{fontSize:12,color:"var(--text)",fontWeight:500,marginBottom:3}}>{prepToast.meeting.title}</div>
            <div style={{fontSize:10,color:"var(--text3)",marginBottom:10}}>{fmtD(prepToast.meeting.date)} · {daysUntil(prepToast.meeting.date)} days · <span style={{color:"var(--warn)"}}>prep starts now</span></div>
            <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
              {prepToast.blocks.map(b=>(
                <div key={b.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",borderRadius:5,background:"var(--s3)",fontSize:10,color:"var(--text2)"}}>
                  <span className={`lb lb-${b.layer}`}>{LAYERS[b.layer].label}</span>
                  <span style={{flex:1}}>{b.title}</span>
                  <span style={{fontSize:10,color:"var(--text3)"}}>{b.dur}m</span>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:7}}>
              <button className="btn btn-C" style={{flex:1,justifyContent:"center"}} onClick={()=>acceptPrep(prepToast.blocks,prepToast.meeting.id)}>＋ Add to plan</button>
              <button className="btn" onClick={()=>dismissPrep(prepToast.meeting.id)}>Dismiss</button>
            </div>
          </div>
        )}

        {/* EMAIL INTERRUPT */}
        {emailAlert&&(
          <div className="alert-overlay">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
              <span style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:12,color:"var(--danger)",letterSpacing:"0.05em"}}>⚡ VIP Interrupt</span>
              <button style={{width:24,height:24,borderRadius:"50%",border:"1px solid var(--border2)",background:"var(--s2)",color:"var(--text3)",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setEmailAlert(null)}>×</button>
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginBottom:3,fontWeight:500}}>{emailAlert.from} · {emailAlert.time}</div>
            <div style={{fontSize:13,color:"var(--text)",fontWeight:600,marginBottom:7,lineHeight:1.35}}>{emailAlert.subj}</div>
            <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.65,marginBottom:12}}>{emailAlert.aiSum}</div>
            <div style={{display:"flex",gap:7}}>
              <button className="btn" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>setEmailAlert(null)}>Handle later</button>
              <button className="btn btn-danger" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>{stopFocus();setEmailAlert(null);}}>Break focus</button>
            </div>
          </div>
        )}

        {/* HEADER */}
        <header className="hdr">
          <div className="logo"><div className="logo-mark">M</div>MSP Operating System</div>
          {userInfo&&<div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginLeft:4}}>{userInfo.email}</div>}
          <div className="hdr-pills">
            <div className={`pill ${m365?"p-ops":""}`}><div className="pill-dot"/>{m365?"M365 live":"M365 offline"}</div>
            <div className={`pill ${calConn?"p-cal":""}`}><div className="pill-dot"/>{calConn?`${meetings.length} meetings`:"Cal offline"}</div>
            <div className={`pill ${fellowConn?"p-fellow":""}`}><div className="pill-dot"/>{fellowConn?"Fellow live":"Fellow offline"}</div>
            {focusing&&<div className="pill p-ops"><div className="pill-dot"/>{fmt(secs)}</div>}
            {pendingCount>0&&<div className="pill p-warn"><div className="pill-dot"/>{pendingCount} cadence</div>}
          </div>
        </header>

        <div className="body">

          {/* ── SIDE NAV ── */}
          {(()=>{
            const NAV_GROUPS=[
              {id:"day",    label:"Day Planning",     items:[
                {id:"plan",  icon:"✦", label:"Plan My Day"},
                {id:"focus", icon:"⚡", label:"Focus"},
              ]},
              {id:"meetings",label:"Meetings & Notes", items:[
                {id:"prepnotes",    icon:"📋", label:"Prep Notes"},
                {id:"meetingnotes", icon:"🗒️", label:"Meeting Notes"},
                {id:"notes",        icon:"📝", label:"Fellow Notes Import", badge:pendingExtracted>0?pendingExtracted:0},
                {id:"todos",        icon:"✅", label:"To-Do",               badge:overdueTodosCount>0?overdueTodosCount:0},
                {id:"calendar",     icon:"📅", label:"Calendar"},
                {id:"fellowticketconfig", icon:"⚙️", label:"Meetings & Notes Settings"},
              ]},
              {id:"reporting", label:"Reporting", items:[
                {id:"postreport",     icon:"📊", label:"Post Meeting Reporting"},
                {id:"monthlyreports", icon:"📈", label:"Monthly Reports"},
                {id:"teamreporting",  icon:"👥", label:"Team Reporting"},
              ]},
              {id:"prepforms", label:"Pre-Meeting Forms", items:[
                {id:"prepformtemplates", icon:"📝", label:"Templates"},
                {id:"prepformassign",    icon:"🔗", label:"Assign Customers"},
              ]},
              {id:"cadence", label:"Cadence",  items:[
                {id:"cadence", icon:"🔄", label:"Cadence", badge:(pendingCount+prepsDue)>0?(pendingCount+prepsDue):0},
              ]},
              {id:"quoting", label:"Quoting",  items:[
                // Opportunity first, then Quoting — the order follows the
                // actual workflow: a deal exists before it is quoted.
                {id:"opportunities", icon:"💡", label:"Opportunities", badge:opportunities.filter(isOpen).length||0},
                {id:"quoting", icon:"💰", label:"Quoting", badge:(quoteBacklog.length+quotes.filter(q=>q.status==="draft").length)>0?(quoteBacklog.length+quotes.filter(q=>q.status==="draft").length):0},
                {id:"proposals", icon:"📋", label:"Proposals", badge:proposals.filter(p=>p.status==="draft").length||0},
                {id:"proposaltemplates", icon:"🗂️", label:"Proposal Templates"},
                {id:"contracttemplates", icon:"📜", label:"Contract Templates"},
              ]},
              {id:"budgeting", label:"Budgeting",  items:[
                {id:"budgeting", icon:"📊", label:"Budgeting"},
              ]},
              {id:"autotask",label:"Autotask", items:[
                {id:"autotask",icon:"🎫", label:"Autotask"},
                {id:"myitprocess",icon:"📋", label:"myITprocess", badge:(myItProcessData?.overdue?.length||0)},
                {id:"itglueupload",icon:"📥", label:"IT Glue Upload"},
                {id:"timereconcile",icon:"⏱", label:"Missing Time"},
              ]},
              {id:"settings",label:"Settings", items:[
                {id:"settings",icon:"⚙️", label:"Settings"},
              ]},
            ];
            return(
              <div className="sidenav">
                {NAV_GROUPS.map(grp=>(
                  <div key={grp.id} className="sn-group">
                    <div className="sn-group-hdr" onClick={()=>toggleNav(grp.id)}>
                      <span className="sn-group-label">{grp.label}</span>
                      <span className={`sn-chevron ${navOpen[grp.id]?"open":""}`}>▼</span>
                    </div>
                    {navOpen[grp.id]&&(
                      <div className="sn-items">
                        {grp.items.map(item=>(
                          <div key={item.id} className={`sn-item ${tab===item.id?"sn-on":""}`}
                            onClick={()=>goTab(item.id)}>
                            <span className="sn-item-icon">{item.icon}</span>
                            <span className="sn-item-label">{item.label}</span>
                            {item.badge>0&&<span className="sn-badge">{item.badge}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{flex:1}}/>
                {/* Sign out at bottom */}
                <div style={{padding:"12px 14px",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
                  <button className="btn" style={{width:"100%",fontSize:10,padding:"6px 0",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.6)",borderRadius:6}}
                    onClick={onSignOut}>Sign out</button>
                </div>
              </div>
            );
          })()}

          {/* ── LEFT: TODAY'S PLAN (drag-drop) ── */}
          <div className="panel" style={{width:planWidth,flexShrink:0,display:showFocusPanel?"flex":"none",flexDirection:"column"}}>
            <div className="ph" style={{flexDirection:"column",height:"auto",padding:"0",gap:0}}>
              {/* Date navigation bar */}
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderBottom:"1px solid var(--border)",width:"100%"}}>
                <button className="ico-btn" onClick={goBack} title="Previous day" style={{flexShrink:0}}>‹</button>
                <input
                  type="date"
                  value={planDate}
                  onChange={e=>goToDate(e.target.value)}
                  style={{flex:1,background:"transparent",border:"none",outline:"none",fontSize:11,
                    color:isToday(planDate)?"var(--strat)":"var(--text)",fontFamily:"var(--ff-head)",
                    fontWeight:isToday(planDate)?600:400,cursor:"pointer",textAlign:"center"}}
                />
                <button className="ico-btn" onClick={goForward} title="Next day" style={{flexShrink:0}}>›</button>
              </div>
              {/* Date label + today badge */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 12px 5px",width:"100%"}}>
                <div style={{fontSize:10,fontWeight:600,color:isToday(planDate)?"var(--strat)":"var(--text)",letterSpacing:"0.02em"}}>
                  {isToday(planDate)?"Today — ":""}{fmtPlanDate(planDate)}
                </div>
                {planLoading&&<div style={{fontSize:9,color:"var(--text3)"}}>loading...</div>}
                {!isToday(planDate)&&<button className="ico-btn" onClick={()=>navigatePlan(todayStr())} title="Go to today" style={{fontSize:9,width:"auto",padding:"1px 6px"}}>Today</button>}
            </div>
              <div style={{display:"flex",gap:5,alignItems:"center"}}>
                {todayTodosCount>0&&<div className="ph-badge" style={{background:"var(--fellow-d)",color:"var(--fellow)",borderColor:"rgba(99,102,241,0.3)"}}>{todayTodosCount} to-dos</div>}
                <div className="ph-badge">{blocks.filter(b=>!b.done).length} blocks</div>
              </div>
            </div>
            <div className="scroll">
              {blocks.map((b,index)=>(
                <div
                  key={b.id}
                  draggable
                  onDragStart={e=>onDragStart(e,index)}
                  onDragOver={e=>onDragOver(e,index)}
                  onDrop={e=>onDrop(e,index)}
                  onDragEnd={onDragEnd}
                  className={`block ${b.id===selId?"sel":""} ${b.id===selId?`l${b.layer}`:""} ${b.done?"done":""} ${b.prepFor?"is-prep":""} ${b.todoId?"is-todo":""}`}
                  onClick={()=>!b.done&&setSelId(b.id)}
                >
                  <div className="drag-handle" title="Drag to reorder">⠿</div>
                  <div className="block-t" style={b.start&&(timeToMins(b.start)<timeToMins(workStart)||timeToMins(b.start)>=timeToMins(workEnd))?{color:"var(--cal)"}:{}}>{b.start}</div>
                  <div className="block-body">
                    <div className="block-name">{b.title}</div>
                    <div className="block-meta">
                      <span className={`lb lb-${b.layer}`}>{LAYERS[b.layer].label}</span>
                      <span className="block-dur">{b.dur}m</span>
                      {b.prepFor&&<span className="lb lb-C">prep</span>}
                      {b.todoId&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"var(--fellow-d)",color:"var(--fellow)"}}>to-do</span>}
                    </div>
                  </div>
                  <div className="block-acts">
                    {!b.done&&<button className="ico-btn" title="Done" onClick={e=>{e.stopPropagation();setBlocks(bs=>{const next=bs.map(x=>x.id===b.id?{...x,done:true}:x);saveSnapshot({blocks:next});return next;});}}>✓</button>}
                    {!b.done&&b.todoId&&<button className="ico-btn" title="Reschedule" onClick={e=>{e.stopPropagation();const td=todos.find(t=>t.id===b.todoId);if(td)openReschedule(td);}}>↻</button>}
                    <button className="ico-btn" title="Delete" onClick={e=>{e.stopPropagation();setBlocks(bs=>{const next=bs.filter(x=>x.id!==b.id);saveSnapshot({blocks:next});return next;});}}>×</button>
                  </div>
                </div>
              ))}
              {adding?(
                <div style={{padding:"9px 12px",borderBottom:"1px solid var(--border)"}}>
                  <input className="inp" placeholder="Block title..." value={nb.title} onChange={e=>setNb(n=>({...n,title:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addBlock()} autoFocus/>
                  <div className="row-gap">
                    <input type="time" className="inp" style={{flex:1,marginBottom:0}} value={nb.start} onChange={e=>setNb(n=>({...n,start:e.target.value}))}/>
                    <input type="number" className="inp" style={{width:52,marginBottom:0}} placeholder="min" value={nb.dur} onChange={e=>setNb(n=>({...n,dur:+e.target.value}))}/>
                  </div>
                  <select className="inp" value={nb.layer} onChange={e=>setNb(n=>({...n,layer:e.target.value}))}>
                    {Object.entries(LAYERS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <div style={{display:"flex",gap:6}}>
                    <button className={`btn btn-${nb.layer}`} onClick={addBlock}>Add</button>
                    <button className="btn" onClick={()=>setAdding(false)}>Cancel</button>
                  </div>
                </div>
              ):(
                <div className="add-row" style={{display:"flex",flexDirection:"column",gap:6}}>
                  <button className="add-btn" onClick={()=>setAdding(true)}>+ Add time block</button>
                  {blocks.length===0&&!isToday(planDate)&&onLoadPlan&&(
                    <button className="add-btn" style={{borderColor:"rgba(11,128,165,0.3)",color:"var(--strat)"}}
                      onClick={copyFromYesterday}>
                      ↑ Copy incomplete from yesterday
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Resize handle: Plan | Focus */}
          <div className="resize-handle" style={{display:showFocusPanel?"flex":"none"}} onMouseDown={(e)=>startResize("plan",e)} title="Drag to resize plan width"/>

          {/* ── PLAN MY DAY TAB ── */}
          {tab==="plan"&&(()=>{
            // Gate the tab behind a "data ready" flag — same pattern as Prep
            // Notes & Meeting Notes. Plan My Day pulls prep blocks from meetings
            // and resolves client names from atCompanies; both must be loaded
            // before the page renders to avoid the attendee-name → hotel-name flicker.
            const dataReady=meetings.length>0&&atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>
                  {meetings.length===0?"Loading calendar data…":"Loading company data…"}
                  <br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span>
                </div>
              </div>
            );
            return(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              {/* Header */}
              <div style={{padding:"12px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px",marginBottom:2}}>
                    ✦ Plan My Day
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>
                    {new Date().toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"})} · {workStart}–{workEnd} · {Math.floor((timeToMins(workEnd)-timeToMins(workStart))/60)}h available · {Math.round(totalPlanMins()/60*10)/10}h planned
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {dayPlan.length>0&&(
                    <button className="btn" style={{fontSize:11}} onClick={()=>setDayPlan([])}>✕ Clear plan</button>
                  )}
                  <button className="btn btn-fellow" style={{fontSize:12,padding:"8px 16px"}}
                    disabled={aiPlanning}
                    onClick={runAiDayPlan}>
                    {aiPlanning?(
                      <><div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/>Planning your day...</>
                    ):"✨ AI Plan my day"}
                  </button>
                  {dayPlan.length>0&&(
                    <button className="btn btn-S" style={{fontSize:12,padding:"8px 18px",background:"var(--strat)",color:"white",borderColor:"var(--strat)"}}
                      onClick={()=>{
                        if(window.confirm(`Confirm day plan with ${dayPlan.length} blocks? This will replace your current Today's Plan and switch to Focus mode.`)){
                          confirmDayPlan();
                        }
                      }}>
                      Start My Day →
                    </button>
                  )}
                </div>
              </div>

              <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                {/* LEFT: Source inbox */}
                <div className="plan-inbox">
                  <div style={{padding:"8px 12px 6px",borderBottom:"1px solid var(--border)",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",fontWeight:600,flexShrink:0}}>
                    Drag to plan →
                  </div>
                  <div className="scroll">
                    {(()=>{
                      const src=planSourceItems();
                      const usedIds=new Set(dayPlan.map(b=>b.sourceId));
                      // Also mark constituent todos of any batch block as used, so
                      // they don't show as available/draggable in the source panel.
                      dayPlan.forEach(b=>{
                        if(b.type==="todo-batch"&&Array.isArray(b._batchedTodos)){
                          b._batchedTodos.forEach(bt=>{
                            if(bt.todoId){
                              usedIds.add(bt.todoId);
                              usedIds.add(`td-${bt.todoId}`); // handles the td- prefix variant
                            }
                          });
                        }
                      });
                      const sections=[
                        {key:"overdue",label:"Overdue & Today",color:"var(--danger)",items:src.overdue},
                        {key:"cadence",label:"Cadence Obligations",color:"var(--tact)",items:src.cadence},
                        {key:"cadenceCompleted",label:"Recently Completed",color:"var(--ops)",items:src.cadenceCompleted||[],defaultClosed:true},
                        {key:"meetings",label:"Today's Meeting Prep",color:"var(--cal)",items:src.meetings},
                        {key:"emails",label:"Priority Emails",color:"var(--danger)",items:src.emails},
                        {key:"carryForward",label:"Carry Forward",color:"var(--text3)",items:src.carryForward},
                        {key:"upcoming",label:"Upcoming To-Dos (7 days)",color:"var(--ops)",items:src.upcoming||[]},
                        {key:"allTodos",label:"All To-Dos",color:"var(--text2)",items:src.allTodos||[]},
                      ];
                      return sections.filter(s=>s.items.length>0).map(s=>{
                        const isOpen=planSectionExpanded[s.key]!==(s.defaultClosed?true:false);
                        const remaining=s.items.filter(i=>!usedIds.has(i.id)).length;
                        const priorityCount=s.items.filter(i=>!usedIds.has(i.id)&&priorityFlags[i.id]).length;
                        return(
                        <div key={s.key} className="plan-source-section">
                          <div className="plan-source-hdr" style={{color:s.color,cursor:"pointer",userSelect:"none"}}
                            onClick={()=>setPlanSectionExpanded(prev=>({...prev,[s.key]:!isOpen}))}>
                            <span style={{display:"flex",alignItems:"center",gap:5}}>
                              <span style={{fontSize:10,color:"var(--text3)",transition:"transform 0.15s",display:"inline-block",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}>▶</span>
                              {s.label}
                            </span>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              {priorityCount>0&&(
                                <span style={{fontSize:9,background:"rgba(231,76,60,0.12)",color:"var(--danger)",fontWeight:600,padding:"1px 5px",borderRadius:3}}>
                                  ⚠ {priorityCount} priority
                                </span>
                              )}
                              {s.key==="cadenceCompleted"
                                ?<span style={{fontSize:10,color:"var(--ops)",fontWeight:400}}>✓ {s.items.length} done · click ↺ to reset</span>
                                :<span style={{fontSize:10,color:"var(--text3)",fontWeight:400}}>{remaining} remaining</span>
                              }
                            </div>
                          </div>
                          {isOpen&&s.items.map(item=>{
                            const used=usedIds.has(item.id);
                            return(
                              <div key={item.id}
                                className={`plan-item ${used?"pi-used":""}`}
                                draggable={!used&&!item.done}
                                title={item.done?"Completed — click ↺ to reset":priorityFlags[item.id]?"Click to remove priority flag":"Click to mark as priority"}
                                onDragStart={e=>{
                                  e.dataTransfer.setData("planItem",JSON.stringify(item));
                                }}
                                onClick={e=>{
                                  if(e.target.closest(".ico-btn")) return;
                                  setPlanItemModal(item);
                                }}
                                style={{
                                  cursor:used?"default":"pointer",
                                  opacity:item.dismissed||(s.key==="cadenceCompleted")?0.55:1,
                                  ...(s.key==="cadenceCompleted"?{background:"rgba(39,174,96,0.03)"}:{}),
                                  ...(item.dismissed&&s.key!=="cadenceCompleted"?{background:"rgba(0,0,0,0.02)"}:{}),
                                  ...(priorityFlags[item.id]&&!used&&!item.dismissed&&s.key!=="cadenceCompleted"?{background:"rgba(231,76,60,0.08)",borderLeft:"3px solid rgba(231,76,60,0.5)"}:{}),
                                }}>
                                <div className="plan-item-dot" style={{background:priorityFlags[item.id]&&!used?"var(--danger)":(item.color||"var(--text3)")}}/>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:11,color:s.key==="cadenceCompleted"?"var(--text3)":item.dismissed?"var(--text3)":priorityFlags[item.id]&&!used?"var(--danger)":"var(--text)",fontWeight:priorityFlags[item.id]&&!used&&s.key!=="cadenceCompleted"?600:500,lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textDecoration:item.dismissed||s.key==="cadenceCompleted"?"line-through":"none"}}>{item.title}</div>
                                  <div style={{fontSize:9,color:"var(--text3)",marginTop:1,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                                    {priorityFlags[item.id]&&!used&&<span style={{color:"var(--danger)",fontWeight:600}}>⚠</span>}
                                    <span>{item.duration}min</span>
                                    <span>·</span>
                                    <span className={`lb lb-${item.layer}`} style={{fontSize:8}}>{LAYERS[item.layer]?.label||item.layer}</span>
                                    {item.client&&(<><span>·</span><span style={{color:"var(--text2)",fontWeight:500,maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.client}</span></>)}
                                    {item.dueDate&&(()=>{
                                      try{
                                      const today=new Date();today.setHours(0,0,0,0);
                                      const due=new Date(item.dueDate+"T12:00:00");
                                      if(isNaN(due.getTime())) return null;
                                      const days=Math.ceil((due-today)/(1000*60*60*24));
                                      const fmt=due.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
                                      const isOverdue=days<0;
                                      const isToday=days===0;
                                      const isTomorrow=days===1;
                                      return(
                                        <>
                                          <span>·</span>
                                          <span style={{
                                            color:isOverdue?"var(--danger)":isToday?"var(--danger)":isTomorrow?"var(--ops)":"var(--text3)",
                                            fontWeight:isOverdue||isToday||isTomorrow?600:400,
                                          }}>
                                            📅 {isOverdue?`Overdue ${Math.abs(days)}d`:isToday?"Today":isTomorrow?"Tomorrow":fmt}
                                          </span>
                                        </>
                                      );
                                    }catch{return null;}
                                    })()}
                                  </div>
                                </div>
                                {!used&&(
                                  <div style={{display:"flex",gap:3,flexShrink:0}}>
                                    {/* cadenceCompleted section — only show ↺ reset */}
                                    {s.key==="cadenceCompleted"&&item._cadLayer&&(
                                      <button className="ico-btn" style={{fontSize:10,color:"var(--ops)",borderColor:"rgba(39,174,96,0.3)",background:"rgba(39,174,96,0.06)",padding:"0 6px",height:18}}
                                        title="Reset — mark as not done so it can be planned"
                                        onClick={e=>{e.stopPropagation();unCad(item._cadLayer,item._cadId);}}>
                                        ↺ Reset
                                      </button>
                                    )}
                                    {/* Regular items — normal + ! − buttons */}
                                    {s.key!=="cadenceCompleted"&&!item.dismissed&&<button className="ico-btn" style={{fontSize:14,fontWeight:300}}
                                      onClick={e=>{e.stopPropagation();addToDayPlan(item,null);}}
                                      title="Add to plan">+</button>}
                                    {s.key!=="cadenceCompleted"&&!item.dismissed&&<button className="ico-btn" style={{fontSize:12,fontWeight:700,
                                      color:priorityFlags[item.id]?"white":"var(--cal)",
                                      borderColor:priorityFlags[item.id]?"var(--cal)":"rgba(211,84,0,0.3)",
                                      background:priorityFlags[item.id]?"var(--cal)":"transparent"}}
                                      onClick={e=>{e.stopPropagation();togglePriority(item.id);}}
                                      title={priorityFlags[item.id]?"Remove priority":"Mark as priority"}>!</button>}
                                    {s.key!=="cadenceCompleted"&&<button className="ico-btn" style={{fontSize:14,fontWeight:300,
                                      color:item.dismissed?"var(--ops)":"var(--danger)",
                                      borderColor:item.dismissed?"rgba(39,174,96,0.4)":"rgba(231,76,60,0.3)",
                                      background:item.dismissed?"rgba(39,174,96,0.08)":"transparent"}}
                                      onClick={e=>{e.stopPropagation();
                                        if(item.dismissed){
                                          setDismissedPlanItems(s=>{const next=new Set([...s]);next.delete(item.id);saveSnapshot({dismissedPlanItems:[...next],planDismissDate:todayISO()});return next;});
                                        } else {
                                          dismissPlanItem(item.id);
                                        }
                                      }}
                                      title={item.dismissed?"Restore to inbox":"Dismiss"}>−</button>}
                                  </div>
                                )}
                                {used&&(
                                  <button className="ico-btn" style={{fontSize:9,color:"var(--ops)",flexShrink:0,padding:"0 4px",height:16,borderColor:"rgba(39,174,96,0.3)",background:"rgba(39,174,96,0.06)"}}
                                    title="Remove from plan — add again"
                                    onClick={e=>{e.stopPropagation();setDayPlan(p=>p.filter(b=>b.sourceId!==item.id));}}>
                                    ✓ ↺
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        );
                      });
                    })()}

                    {/* Empty state */}
                    {(()=>{
                      const src=planSourceItems();
                      const total=Object.values(src).reduce((n,a)=>n+a.length,0);
                      if(total===0) return(
                        <div style={{padding:"30px 16px",textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.7}}>
                          <div style={{fontSize:24,marginBottom:8}}>🎉</div>
                          Nothing urgent today!<br/>
                          Use AI to plan your day or add blocks manually.
                        </div>
                      );
                      return null;
                    })()}
                  </div>
                </div>

                {/* RIGHT: Time canvas */}
                <div className="plan-canvas">
                  <div style={{padding:"8px 16px 6px",borderBottom:"1px solid var(--border)",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",fontWeight:600,flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span>Day Canvas — {dayPlan.length} blocks</span>
                    {dayPlan.length>0&&<span style={{color:"var(--strat)",fontWeight:600}}>{Math.round(totalPlanMins()/60*10)/10}h of {(timeToMins(workEnd)-timeToMins(workStart))/60}h used</span>}
                  </div>
                  <div className="scroll" style={{padding:"0 0 20px 0"}}>
                    {/* AI reason banner if available */}
                    {dayPlan.some(b=>b.reason)&&(
                      <div style={{padding:"8px 16px",background:"rgba(127,86,217,0.06)",borderBottom:"1px solid var(--border)",fontSize:11,color:"var(--fellow)"}}>
                        ✨ AI has suggested a plan based on your priorities. Review and adjust as needed.
                      </div>
                    )}

                    {/* Time grid — 15min slots with spanning blocks */}
                    {(()=>{
                      const slots=planDayHours();
                      const occ=occupiedSlots();
                      // Track which slots are already rendered by a spanning block
                      const rendered=new Set();

                      // Build a map: slotTime -> blocks starting there
                      const blocksBySlot={};
                      dayPlan.forEach(b=>{
                        if(!blocksBySlot[b.startTime]) blocksBySlot[b.startTime]=[];
                        blocksBySlot[b.startTime].push(b);
                      });

                      return slots.map((slot,slotIdx)=>{
                        const isOccupied=!!occ[slot]&&!blocksBySlot[slot]?.length;
                        const blocksHere=blocksBySlot[slot]||[];

                        return(
                          <div key={slot}
                            className={`plan-slot${isOccupied?" ps-occupied":""}`}
                            style={{
                              height:28,
                              borderBottom:"1px solid var(--border)",
                              background:isOccupied?"rgba(0,0,0,0.01)":"transparent",
                            }}
                            onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="var(--strat-d)";}}
                            onDragLeave={e=>e.currentTarget.style.background=isOccupied?"rgba(0,0,0,0.01)":"transparent"}
                            onDrop={e=>{
                              e.currentTarget.style.background=isOccupied?"rgba(0,0,0,0.01)":"transparent";
                              const existing=e.dataTransfer.getData("planBlock");
                              if(existing){updateDayPlanTime(existing,slot);return;}
                              const item=e.dataTransfer.getData("planItem");
                              if(item){addToDayPlan(JSON.parse(item),slot);}
                            }}>
                            <div className="plan-slot-time">{slot}</div>
                            <div className="plan-slot-content">
                              {blocksHere.map(b=>{
                                const spans=blockSlots(b.duration);
                                const heightPx=spans*28-2;
                                return(
                                  <div key={b.id}
                                    className={`plan-block ${b.fixed?"pb-fixed":""}`}
                                    draggable={!b.fixed}
                                    onDragStart={e=>{
                                      e.stopPropagation();
                                      e.dataTransfer.setData("planBlock",b.id);
                                    }}
                                    style={{
                                      position:"absolute",
                                      top:0,
                                      left:0,
                                      right:0,
                                      height:heightPx,
                                      zIndex:2,
                                      borderLeft:`3px solid ${b.type==="buffer"?"var(--border)":b.color||"var(--strat)"}`,
                                      background:b.type==="buffer"
                                        ?"repeating-linear-gradient(45deg,#f8f9fa,#f8f9fa 4px,#ffffff 4px,#ffffff 8px)"
                                        :"white",
                                      opacity:b.type==="buffer"?0.75:1,
                                      overflow:"hidden",
                                      boxShadow:b.type==="buffer"?"none":"0 1px 4px rgba(0,0,0,0.1)",
                                      borderRadius:"0 4px 4px 0",
                                      padding:"3px 6px",
                                      display:"flex",
                                      flexDirection:"column",
                                      justifyContent:"flex-start",
                                    }}>
                                    <div style={{fontSize:spans===1?9:11,fontWeight:b.type==="buffer"?400:600,color:b.type==="buffer"?"var(--text3)":"var(--text)",lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:spans===1?"nowrap":"normal",fontStyle:b.type==="buffer"?"italic":"normal"}}>
                                      {b.type==="todo-batch"&&Array.isArray(b._batchedTodos)
                                        ?`☑ ${b.title} (${b._batchedTodos.filter(bt=>todos.find(t=>t.id===bt.todoId)?.done).length}/${b._batchedTodos.length})`
                                        :b.title}
                                    </div>
                                    {spans>=2&&(
                                      <div style={{display:"flex",gap:4,alignItems:"center",marginTop:1,flexWrap:"wrap"}}>
                                        <span style={{fontSize:9,color:"var(--text3)"}}>{b.duration}min</span>
                                        <span className={`lb lb-${b.type==="meeting-prep"?"C":b.layer==="C"?"T":b.layer}`} style={{fontSize:8}}>
                                          {b.type==="meeting-prep"?"Prep":b.layer==="C"?"Tactical":(LAYERS[b.layer]?.label||b.layer)}
                                        </span>
                                        {b.fixed&&<span style={{fontSize:9,color:"var(--cal)",fontWeight:600}}>📅</span>}
                                      </div>
                                    )}
                                    {(!b.fixed||b.type==="meeting-prep")&&(
                                      <button className="ico-btn"
                                        style={{position:"absolute",top:2,right:2,fontSize:10,width:16,height:16}}
                                        onClick={()=>removeFromDayPlan(b.id)}>×</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}

                    {/* Empty canvas prompt */}
                    {dayPlan.length===0&&(
                      <div style={{padding:"40px 24px",textAlign:"center",color:"var(--text3)",fontSize:12,lineHeight:1.8}}>
                        <div style={{fontSize:36,marginBottom:14}}>📋</div>
                        <div style={{fontFamily:"var(--ff-head)",fontWeight:600,fontSize:14,color:"var(--text)",marginBottom:8}}>Your day is empty</div>
                        Drag items from the left panel into time slots,<br/>
                        or click <strong>✨ AI Plan my day</strong> to get a suggested schedule.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })()}

          {/* ── FOCUS TAB ── */}
          {tab==="focus"&&(()=>{
            const dataReady=meetings.length>0&&atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>
                  {meetings.length===0?"Loading calendar data…":"Loading company data…"}
                  <br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span>
                </div>
              </div>
            );
            return(
            <div className="focus-area">
              <div className={`focus-hero ${focusing?`fh-${lc}`:""}`}>
                <div className="focus-eyebrow">
                  {focusing?"⚡ FOCUSING NOW":"SELECTED BLOCK"}
                  {sel&&<span className={`lb lb-${sel.type==="meeting-prep"?"C":sel.layer==="C"?"T":sel.layer}`}>
                    {sel.type==="meeting-prep"?"Prep layer":sel.layer==="C"&&sel.type==="meeting"?"Tactical layer":(LAYERS[sel.layer]?.label||sel.layer)+" layer"}
                  </span>}
                  {sel?.prepFor&&meetings.find(m=>m.id===sel.prepFor)&&(
                    <span style={{fontSize:9,color:"var(--cal)"}}>📅 prep · {meetings.find(m=>m.id===sel.prepFor)?.client} · {daysUntil(meetings.find(m=>m.id===sel.prepFor)?.date)}d</span>
                  )}
                  {sel?.todoId&&todos.find(t=>t.id===sel.todoId)&&(
                    <span style={{fontSize:9,color:"var(--fellow)"}}>📋 from: {todos.find(t=>t.id===sel.todoId)?.source}</span>
                  )}
                  {sel&&getWeeklyReportCompany(sel.title)&&(
                    <button
                      onClick={()=>openWeeklyReport(sel)}
                      style={{
                        marginLeft:"auto",fontSize:10,padding:"3px 10px",
                        background:"rgba(11,128,165,0.15)",color:"rgba(255,255,255,0.9)",
                        border:"1px solid rgba(11,128,165,0.5)",borderRadius:5,
                        cursor:"pointer",fontWeight:600,letterSpacing:"0.02em",
                        display:"flex",alignItems:"center",gap:5,
                      }}>
                      📊 View Weekly Report
                    </button>
                  )}
                </div>
                <div className={`focus-title ${focusing?`ft-${lc}`:""}`}>{sel?.title||"Select a block from today's plan"}</div>
                <div className="focus-row">
                  {focusing?(
                    <>
                      <div className={`timer ${running?`tm-${lc}`:"tm-paused"}`}>{fmt(secs)}</div>
                      <button className="btn" onClick={()=>setRunning(r=>!r)}>{running?"⏸ Pause":"▶ Resume"}</button>
                      <button className="btn btn-danger" onClick={stopFocus}>■ End</button>
                    </>
                  ):(
                    <>
                      {sel?.id!=null&&timerStashRef.current[sel.id]?(
                        // Returning to a paused block — show stashed time in paused colour
                        <>
                          <div className="timer tm-paused">{fmt(timerStashRef.current[sel.id].secs)}</div>
                          <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"rgba(230,126,34,0.12)",color:"var(--cal)",fontWeight:600,border:"1px solid rgba(230,126,34,0.3)"}}>⏸ Paused</span>
                        </>
                      ):(
                        <div className="timer tm-idle">{sel?`${sel.dur}:00`:"--:--"}</div>
                      )}
                      <button className={`btn btn-${sel?.layer||"O"}`} onClick={startFocus} disabled={!sel}>{sel?.id!=null&&timerStashRef.current[sel.id]?"⏵ Resume":"▶ Start focus"}</button>
                    </>
                  )}
                  {sel&&!focusing&&<span style={{fontSize:10,color:"var(--text3)"}}>{sel.type==="meeting-prep"?"Prep":sel.layer==="C"&&sel.type==="meeting"?"Tactical":(LAYERS[sel.layer]?.label||sel.layer)} · {sel.dur} min</span>}
                </div>
                {focusing&&total>0&&<div className="prog-wrap"><div className={`prog-bar pb-${lc}`} style={{width:`${prog}%`}}/></div>}
              </div>

              {/* ── Batched Todos Checklist (only when focused on a todo-batch) ── */}
              {sel?.type==="todo-batch"&&Array.isArray(sel._batchedTodos)&&sel._batchedTodos.length>0&&(()=>{
                // Resolve live state for each batched todo so the checklist
                // reflects ticks across re-renders. Items removed from `todos`
                // (e.g. deleted) just don't appear — defensive, not an error.
                const liveItems=sel._batchedTodos
                  .map(bt=>{const t=todos.find(x=>x.id===bt.todoId);return t?{...bt,done:!!t.done,client:t.client||bt.client,notes:t.notes||"",priority:t.priority||"",dueDate:t.dueDate||"",exists:true,_todo:t}:null;})
                  .filter(Boolean);
                const doneCount=liveItems.filter(i=>i.done).length;
                const totalCount=liveItems.length;
                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("batched-todos")}>
                      <div className="fa-label">
                        <span>☑</span> Tasks in this batch
                        <span style={{fontSize:9,fontWeight:400,color:"var(--text3)",textTransform:"none",letterSpacing:0,marginLeft:6}}>
                          {doneCount}/{totalCount} done
                        </span>
                      </div>
                      <span className={`fa-chevron ${focusAccordion["batched-todos"]!==false?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion["batched-todos"]!==false&&(
                      <div className="fa-body" style={{padding:"6px 14px 12px"}}>
                        <div style={{fontSize:10,color:"var(--text3)",marginBottom:8,lineHeight:1.5}}>
                          Work through these together. Tick each as you finish — anything still unchecked at end of day will roll over to tomorrow.
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {liveItems.map(item=>{
                            const isExpanded=!!batchItemExpanded[item.todoId];
                            const hasDetail=!!(item.notes||item.priority||item.dueDate);
                            const priorityLabel={high:"🔴 High",med:"🟡 Med",low:"⚪ Low"}[item.priority]||"";
                            return(
                              <div key={item.todoId} style={{borderRadius:6,border:`1px solid ${item.done?"rgba(39,174,96,0.25)":"var(--border)"}`,overflow:"hidden",background:item.done?"rgba(39,174,96,0.04)":"var(--s2)"}}>
                                {/* ── Main row ── */}
                                <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 10px"}}>
                                  <input
                                    type="checkbox"
                                    checked={item.done}
                                    onChange={()=>toggleBatchTodo(item.todoId)}
                                    style={{marginTop:3,flexShrink:0,width:16,height:16,cursor:"pointer"}}
                                  />
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:12,color:item.done?"var(--text3)":"var(--text)",textDecoration:item.done?"line-through":"none",lineHeight:1.4,wordBreak:"break-word"}}>
                                      {item.title}
                                    </div>
                                    {item.client&&(
                                      <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>
                                        {item.client}
                                      </div>
                                    )}
                                  </div>
                                  {/* Right-side controls */}
                                  <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                                    <span style={{fontSize:9,color:"var(--text3)"}}>{item.originalDuration}min</span>
                                    {/* Same look as the To-Do card's button: at 9px the bare emoji rendered as an unrecognisable yellow blob and Craig could not find it (2026-09-30). */}
                                    {atConnected&&!item.done&&(
                                      <button
                                        className="btn"
                                        style={{fontSize:10,padding:"3px 9px",lineHeight:1.4,whiteSpace:"nowrap",background:"var(--strat-d)",color:"var(--strat)",borderColor:"rgba(11,128,165,0.25)"}}
                                        onClick={e=>{e.stopPropagation();openTicketFromTodo({...item._todo,_originalDurationMins:item.originalDuration});}}
                                        title="Create Autotask ticket from this item (pre-fills hours from its planned duration)"
                                      >
                                        🎫 Ticket
                                      </button>
                                    )}
                                    {hasDetail&&(
                                      <button
                                        className="ico-btn"
                                        style={{fontSize:10,padding:"2px 5px",color:"var(--text3)",background:"none",border:"none",cursor:"pointer",lineHeight:1}}
                                        onClick={e=>{e.stopPropagation();setBatchItemExpanded(s=>({...s,[item.todoId]:!s[item.todoId]}));}}
                                        title={isExpanded?"Hide detail":"Show detail"}
                                      >
                                        {isExpanded?"▲":"▼"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {/* ── Expanded detail panel ── */}
                                {isExpanded&&hasDetail&&(
                                  <div style={{borderTop:"1px solid var(--border)",padding:"7px 10px 8px 36px",display:"flex",flexDirection:"column",gap:4,background:"var(--s1)"}}>
                                    {item.notes&&(
                                      <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5,whiteSpace:"pre-wrap"}}>
                                        {item.notes}
                                      </div>
                                    )}
                                    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:item.notes?4:0}}>
                                      {priorityLabel&&<span style={{fontSize:10,color:"var(--text3)"}}>{priorityLabel}</span>}
                                      {item.dueDate&&<span style={{fontSize:10,color:"var(--text3)"}}>📅 {item.dueDate}</span>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="fa-hdr" onClick={()=>toggleFocusSection("aibrief")}>
                <div className="fa-label"><span>◆</span> AI Operating Brief</div>
                <span className={`fa-chevron ${focusAccordion.aibrief?"open":""}`}>▶</span>
              </div>
              {focusAccordion.aibrief&&(
              <div className="ai-section" style={{borderBottom:"none"}}>
                {aiLoad?<div className="ai-loading"><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/></div>
                :aiText?<div className="ai-text">{aiText}</div>
                :<div className="ai-text" style={{color:"var(--text3)"}}>Start a focus session for a context-aware AI brief covering your layer, to-dos, prep status, and meeting schedule.</div>}
              </div>
              )}


              {/* ── Email Chase-Up (only when focused on Check Emails block) ── */}
              {(()=>{
                const isCheckEmailBlock=(sel?.title||"").toLowerCase().includes("check email");
                if(!isCheckEmailBlock) return null;
                const urgColor={high:"var(--danger)",med:"var(--cal)",low:"var(--text3)"};
                const urgBg={high:"rgba(231,76,60,0.08)",med:"rgba(230,126,34,0.07)",low:"rgba(74,85,104,0.05)"};
                const totalCount=chaseInboxItems.length+chaseSentItems.length;
                const rerun=(e)=>{
                  e.stopPropagation();
                  setChaseInboxItems([]);setChaseSentItems([]);setChaseLoading(true);
                  onFetchChaseUp()
                    .then(({inboxItems=[],sentItems=[]}={})=>{setChaseInboxItems(inboxItems);setChaseSentItems(sentItems);setChaseRan(sel?.id);})
                    .catch(()=>{setChaseInboxItems([]);setChaseSentItems([]);})
                    .finally(()=>setChaseLoading(false));
                };
                const renderChaseCard=(item,idx,listSetter,isSent)=>{
                  const syntheticEmail={
                    id:`chase-${isSent?"s":"i"}-${idx}`,
                    from:isSent?"Craig Grant":item.person,
                    fromEmail:"",subj:item.subject,
                    body:item.reason,bodyPreview:item.reason,aiSum:item.reason,
                    time:item.date,
                    // graphId enables full body fetch in the email detail modal
                    graphId:item.graphId||null,
                    priority:item.urgency==="high"?"urgent":item.urgency==="med"?"client":"low",vip:false,
                  };
                  const isTicketed=ticketedEmails.has(syntheticEmail.id);
                  return(
                    <div key={idx}
                      style={{background:urgBg[item.urgency||"low"],border:`1px solid ${urgColor[item.urgency||"low"]}33`,borderRadius:7,padding:"9px 12px",display:"flex",flexDirection:"column",gap:4,cursor:"pointer"}}
                      onClick={()=>setEmailDetailModal({email:syntheticEmail})}
                      title="Click to open">
                      <div style={{fontSize:12,fontWeight:600,color:"var(--text)",lineHeight:1.3}}>{item.subject}</div>
                      <div style={{fontSize:11,color:"var(--text2)",display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                        <span style={{fontWeight:500}}>{item.person}</span>
                        <span style={{color:"var(--text3)"}}>· {item.date}</span>
                        <span style={{padding:"1px 5px",borderRadius:4,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",background:urgBg[item.urgency||"low"],color:urgColor[item.urgency||"low"],border:`1px solid ${urgColor[item.urgency||"low"]}44`}}>{item.urgency}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text2)",fontStyle:"italic",lineHeight:1.4,borderTop:"1px solid var(--border)",paddingTop:4,marginTop:1}}>{item.reason}</div>
                      <div style={{display:"flex",gap:5,marginTop:2,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
                        {atConnected&&!isTicketed&&(
                          <button className="em-ticket-btn" style={{borderColor:"rgba(11,128,165,0.3)",background:"rgba(11,128,165,0.06)",color:"var(--strat)",fontWeight:600}} onClick={e=>{e.stopPropagation();openTicketModal(syntheticEmail,{quickTicket:true});}}>🎫 ticket</button>
                        )}
                        {isTicketed&&<span style={{fontSize:9,padding:"3px 8px",background:"rgba(39,174,96,0.1)",color:"var(--ops)",border:"1px solid rgba(39,174,96,0.3)",borderRadius:4,fontWeight:600}}>✓ Ticketed</span>}
                        <button className="em-ticket-btn" style={{borderColor:"rgba(74,85,104,0.2)",background:"rgba(74,85,104,0.04)",color:"var(--text2)",fontWeight:500}} onClick={e=>{e.stopPropagation();setEmailDetailModal({email:syntheticEmail});setReplyModal({email:syntheticEmail});}}>✏️ draft reply</button>
                        <button className="em-ticket-btn" style={{borderColor:"rgba(74,85,104,0.15)",background:"transparent",color:"var(--text3)"}} onClick={e=>{e.stopPropagation();listSetter(prev=>prev.filter((_,i)=>i!==idx));}}>✕ dismiss</button>
                      </div>
                    </div>
                  );
                };
                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("email-chase")}>
                      <div className="fa-label">
                        <span>🔍</span> Email Review
                        {chaseLoading&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9,color:"var(--text3)",textTransform:"none",letterSpacing:0,fontWeight:400}}><span className="ai-dot"/><span className="ai-dot"/><span className="ai-dot"/>Scanning 10 days…</span>}
                        {!chaseLoading&&totalCount>0&&<span style={{background:"rgba(231,76,60,0.1)",color:"var(--danger)",padding:"1px 7px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>{totalCount} item{totalCount!==1?"s":""}</span>}
                        {!chaseLoading&&chaseRan&&totalCount===0&&<span style={{background:"rgba(39,174,96,0.1)",color:"var(--ops)",padding:"1px 7px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>✓ All clear</span>}
                        {!chaseLoading&&onFetchChaseUp&&<button className="btn" style={{fontSize:9,padding:"2px 8px",marginLeft:6}} onClick={rerun}>↻ Re-run</button>}
                      </div>
                      <span className={`fa-chevron ${focusAccordion["email-chase"]!==false?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion["email-chase"]!==false&&(
                      <div className="fa-body">
                        {chaseLoading&&(
                          <div style={{padding:"18px 22px",display:"flex",alignItems:"center",gap:10,color:"var(--text3)",fontSize:12}}>
                            <div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/>
                            <span>Checking inbox, sent items and deleted folder across the last 10 days…</span>
                          </div>
                        )}
                        {!chaseLoading&&chaseRan&&totalCount===0&&(
                          <div style={{padding:"16px 22px",fontSize:12,color:"var(--text3)",fontStyle:"italic"}}>Nothing needs attention — all clear.</div>
                        )}
                        {!chaseLoading&&!chaseRan&&(
                          <div style={{padding:"16px 22px",fontSize:12,color:"var(--text3)"}}>Start the focus session to review your emails.</div>
                        )}
                        {!chaseLoading&&(chaseInboxItems.length>0||chaseSentItems.length>0)&&(
                          <div style={{padding:"8px 14px 12px",display:"flex",flexDirection:"column",gap:14}}>
                            {/* ── Inbox — needs your action ── */}
                            {chaseInboxItems.length>0&&(
                              <div>
                                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:"var(--text3)",marginBottom:7,display:"flex",alignItems:"center",gap:6}}>
                                  📥 Needs your response
                                  <span style={{fontWeight:400,fontSize:9,textTransform:"none",letterSpacing:0}}>received — {chaseInboxItems.length} item{chaseInboxItems.length!==1?"s":""}</span>
                                </div>
                                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                  {chaseInboxItems.map((item,idx)=>renderChaseCard(item,idx,setChaseInboxItems,false))}
                                </div>
                              </div>
                            )}
                            {/* ── Sent — awaiting reply ── */}
                            {chaseSentItems.length>0&&(
                              <div>
                                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:"var(--text3)",marginBottom:7,display:"flex",alignItems:"center",gap:6}}>
                                  📤 Awaiting reply
                                  <span style={{fontWeight:400,fontSize:9,textTransform:"none",letterSpacing:0}}>sent by you — no reply received</span>
                                </div>
                                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                  {chaseSentItems.map((item,idx)=>renderChaseCard(item,idx,setChaseSentItems,true))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Client Health Check (only when focused on that block) ── */}
              {(()=>{
                const isClientHealthBlock=(sel?.title||"").toLowerCase().includes("client health");
                if(!isClientHealthBlock) return null;
                const concernColor={high:"var(--danger)",medium:"var(--cal)",low:"var(--text3)",none:"var(--ops)"};
                const concernBg={high:"rgba(231,76,60,0.07)",medium:"rgba(230,126,34,0.06)",low:"rgba(74,85,104,0.04)",none:"rgba(39,174,96,0.04)"};
                const concernLabel={high:"⚠ Concern",medium:"⚡ Watch",low:"",none:"✓"};
                const totalClients=clientHealthData.length;
                const totalTickets=clientHealthData.reduce((s,c)=>s+c.ticketCount,0);
                const rerun=(e)=>{
                  e.stopPropagation();
                  setClientHealthData([]);setClientHealthLoading(true);setClientHealthExpanded({});
                  const alerts=Array.isArray(dattoData?.alerts)?dattoData.alerts:[];
                  const sites=Array.isArray(dattoData?.sites)?dattoData.sites:[];
                  onFetchClientHealth({dattoAlerts:alerts,dattoSites:sites})
                    .then(clients=>{setClientHealthData(clients||[]);setClientHealthRan(sel?.id);})
                    .catch(()=>setClientHealthData([]))
                    .finally(()=>setClientHealthLoading(false));
                };
                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("client-health")}>
                      <div className="fa-label">
                        <span>🏥</span> Client Health Check
                        {clientHealthLoading&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9,color:"var(--text3)",textTransform:"none",letterSpacing:0,fontWeight:400}}><span className="ai-dot"/><span className="ai-dot"/><span className="ai-dot"/>Analysing clients…</span>}
                        {!clientHealthLoading&&totalClients>0&&<span style={{background:"rgba(11,128,165,0.1)",color:"var(--tact)",padding:"1px 7px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>{totalClients} clients · {totalTickets} tickets</span>}
                        {!clientHealthLoading&&clientHealthRan&&totalClients===0&&<span style={{background:"rgba(39,174,96,0.1)",color:"var(--ops)",padding:"1px 7px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>✓ All clear</span>}
                        {!clientHealthLoading&&onFetchClientHealth&&<button className="btn" style={{fontSize:9,padding:"2px 8px",marginLeft:6}} onClick={rerun}>↻ Re-run</button>}
                      </div>
                      <span className={`fa-chevron ${focusAccordion["client-health"]!==false?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion["client-health"]!==false&&(
                      <div className="fa-body">
                        {clientHealthLoading&&(
                          <div style={{padding:"18px 22px",display:"flex",alignItems:"center",gap:10,color:"var(--text3)",fontSize:12}}>
                            <div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/>
                            <span>Fetching tickets, notes and time entries. Running AI analysis per client…</span>
                          </div>
                        )}
                        {!clientHealthLoading&&!clientHealthRan&&(
                          <div style={{padding:"16px 22px",fontSize:12,color:"var(--text3)"}}>Start the focus session to run client health analysis.</div>
                        )}
                        {!clientHealthLoading&&clientHealthRan&&totalClients===0&&(
                          <div style={{padding:"16px 22px",fontSize:12,color:"var(--text3)",fontStyle:"italic"}}>No recent ticket activity found for managed clients.</div>
                        )}
                        {!clientHealthLoading&&clientHealthData.length>0&&(
                          <div style={{padding:"6px 10px 12px",display:"flex",flexDirection:"column",gap:6}}>
                            {clientHealthData.map(client=>{
                              const isOpen=!!clientHealthExpanded[client.companyId];
                              const highConcern=client.tickets.some(t=>t.concern==="high");
                              const medConcern=!highConcern&&client.tickets.some(t=>t.concern==="medium");
                              const topConcern=highConcern?"high":medConcern?"medium":"none";
                              const clientSentiment=(preloadedSentiment?.results||[]).find(r=>String(r.companyId)===String(client.companyId));
                              return(
                                <div key={client.companyId} style={{border:`1px solid ${highConcern?"rgba(231,76,60,0.3)":medConcern?"rgba(230,126,34,0.25)":"var(--border)"}`,borderRadius:8,overflow:"hidden",background:"var(--white)"}}>
                                  {/* Client header row */}
                                  <div style={{padding:"9px 12px",display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:highConcern?"rgba(231,76,60,0.04)":medConcern?"rgba(230,126,34,0.03)":"var(--bg)"}}
                                    onClick={()=>setClientHealthExpanded(s=>({...s,[client.companyId]:!s[client.companyId]}))}>
                                    <span style={{fontSize:13}}>{isOpen?"▼":"▶"}</span>
                                    <div style={{flex:1,minWidth:0}}>
                                      <div style={{fontSize:13,fontWeight:700,color:"var(--text)",lineHeight:1.2}}>{client.companyName}</div>
                                      {client.overall&&<div style={{fontSize:11,color:"var(--text2)",marginTop:2,lineHeight:1.4,fontStyle:"italic"}}>{client.overall}</div>}
                                    </div>
                                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                                      {clientSentiment&&(
                                        <span style={{fontSize:11,fontWeight:700,color:clientSentiment.score>=7?"var(--ops)":clientSentiment.score>=5?"var(--cal)":"var(--danger)"}}>
                                          ●{clientSentiment.score}/10
                                        </span>
                                      )}
                                      <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"rgba(11,128,165,0.08)",color:"var(--tact)",border:"1px solid rgba(11,128,165,0.15)",fontWeight:600}}>
                                        {client.ticketCount} ticket{client.ticketCount!==1?"s":""}
                                      </span>
                                      {client.totalHours>0&&<span style={{fontSize:9,color:"var(--text3)"}}>{client.totalHours}h</span>}
                                      {client.dattoAlerts?.length>0&&(
                                        <span style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:"rgba(231,76,60,0.1)",color:"var(--danger)",border:"1px solid rgba(231,76,60,0.25)",fontWeight:600}}>⚠ {client.dattoAlerts.length} alert{client.dattoAlerts.length!==1?"s":""}</span>
                                      )}
                                      {topConcern!=="none"&&<span style={{fontSize:9,fontWeight:700,color:concernColor[topConcern]}}>{concernLabel[topConcern]}</span>}
                                    </div>
                                  </div>
                                  {/* Expanded ticket list */}
                                  {isOpen&&(
                                    <div style={{borderTop:"1px solid var(--border)",padding:"8px 12px",display:"flex",flexDirection:"column",gap:8}}>
                                      {/* Datto alerts if any */}
                                      {client.dattoAlerts?.length>0&&(
                                        <div style={{background:"rgba(231,76,60,0.05)",border:"1px solid rgba(231,76,60,0.2)",borderRadius:6,padding:"6px 10px"}}>
                                          <div style={{fontSize:10,fontWeight:700,color:"var(--danger)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>⚠ Datto RMM Alerts</div>
                                          {client.dattoAlerts.map((a,i)=>(
                                            <div key={i} style={{fontSize:11,color:"var(--text2)",padding:"2px 0",borderBottom:i<client.dattoAlerts.length-1?"1px solid rgba(231,76,60,0.1)":"none"}}>
                                              <strong>{a.alertSourceInfo?.deviceName||a.hostname||"Device"}</strong> — {a.alertMessage||a.message||"Alert"}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Tickets */}
                                      {client.tickets.map(ticket=>(
                                        <div key={ticket.id} style={{background:concernBg[ticket.concern||"none"],border:`1px solid ${concernColor[ticket.concern||"none"]}33`,borderRadius:6,padding:"8px 10px"}}>
                                          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:4}}>
                                            <div style={{flex:1,minWidth:0}}>
                                              <div style={{fontSize:12,fontWeight:600,color:"var(--text)",lineHeight:1.3}}>
                                                <span style={{fontFamily:"monospace",fontSize:10,color:"var(--text3)",marginRight:5}}>{ticket.ticketNumber}</span>
                                                {ticket.title}
                                                {ticket.assetFlag&&<span style={{marginLeft:6,fontSize:9,padding:"1px 5px",borderRadius:3,background:"rgba(230,126,34,0.12)",color:"var(--cal)",fontWeight:600,border:"1px solid rgba(230,126,34,0.3)"}}>🔄 Recurring asset</span>}
                                              </div>
                                              <div style={{fontSize:10,color:"var(--text3)",marginTop:2,display:"flex",flexWrap:"wrap",gap:6}}>
                                                {ticket.resourceNames.length>0&&<span>👤 {ticket.resourceNames.join(", ")}</span>}
                                                {ticket.totalHours>0&&<span>⏱ {ticket.totalHours}h</span>}
                                                {ticket.responseHours!==null&&<span>📨 {ticket.responseHours}h response</span>}
                                                {ticket.noteCount>0&&<span>💬 {ticket.noteCount} note{ticket.noteCount!==1?"s":""}</span>}
                                                <span style={{color:"var(--text3)"}}>{ticket.createDate}</span>
                                                {ticket.concern!=="none"&&<span style={{fontWeight:700,color:concernColor[ticket.concern]}}>{concernLabel[ticket.concern]}</span>}
                                              </div>
                                            </div>
                                          </div>
                                          {ticket.aiSummary&&(
                                            <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.55,borderTop:"1px solid var(--border)",paddingTop:5,fontStyle:"italic"}}>
                                              {ticket.aiSummary}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      {client.tickets.length===0&&<div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>No tickets this week.</div>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Upcoming Meetings (default closed, below AI Brief) ── */}
              {calConn&&sortedMeetings.length>0&&(
                <>
                  <div className="fa-hdr" onClick={()=>toggleFocusSection("upcoming-meetings")}>
                    <div className="fa-label"><span>📅</span> Upcoming Meetings <span style={{fontSize:9,fontWeight:400,color:"var(--text3)",textTransform:"none",letterSpacing:0}}>{sortedMeetings.length} upcoming</span></div>
                    <span className={`fa-chevron ${focusAccordion["upcoming-meetings"]?"open":""}`}>▶</span>
                  </div>
                  {focusAccordion["upcoming-meetings"]&&(
                    <div className="fa-body">
                      <div className="meetings-strip" style={{borderBottom:"none"}}>
                        <div className="ms-scroll">
                          {sortedMeetings.map(m=>{
                            const d=daysUntil(m.date);const cfg=PREP_CFG[m.type];
                            const prepAdded=blocks.some(b=>b.prepFor===m.id);
                            return(
                              <div key={m.id} className={`meeting-chip mc-${m.tier}`} onClick={()=>setTab("calendar")}>
                                <div className="mc-date">{fmtD(m.date)}</div>
                                <div className="mc-title">{m.title}</div>
                                <div className="mc-meta">
                                  <span className={`lb lb-${m.tier==="vcio"?"S":"T"}`}>{m.tier==="vcio"?"vCIO":"Mgd"}</span>
                                  <span className={`mc-days ${d<=2?"days-urgent":d<=cfg.days?"days-warn":"days-ok"}`}>{d===0?"TODAY":d===1?"Tomorrow":`${d} days`}</span>
                                  {d<=cfg.days&&!prepAdded&&<span style={{fontSize:9,color:"var(--warn)"}}>⚠ prep</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Client Pre-Meeting Responses ───────────────────── */}
              {(()=>{
                // isPurePrepBlock: "Prep: ..." only — just the form send step (5WD before meeting)
                // isPreMeetingBlock: "Pre-meeting: ..." — form review + MIP (1WD before / day of)
                const isPurePrepBlock=sel&&sel.title?.toLowerCase().startsWith("prep:");
                const isPreMeetingBlock=sel&&(sel.title?.toLowerCase().startsWith("pre-meeting:")||sel.title?.toLowerCase().includes("pre-meeting prep"));
                if(!isPurePrepBlock&&!isPreMeetingBlock) return null;
                const questions=prepResponse?.questions||[];
                const answers=prepResponse?.answers||{};
                const submittedAt=prepResponse?.submittedAt;
                const formUrl=prepLinkInfo?.formUrl;
                const sentAt=prepLinkInfo?.sentAt;
                const hasResponse=!!submittedAt;
                const titleClient=resolveTitleClient(sel);
                const clientMeeting=upcomingMeetingPreps.find(p=>titleClient&&(normCo(p.client||"")===normCo(titleClient.companyName)||p.client?.toLowerCase()===titleClient.companyName.toLowerCase()));
                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("pre-meeting-form")}>
                      <div className="fa-label">
                        <span>📋</span> Pre-Meeting Form
                        {prepResponseLoading&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9,color:"var(--text3)",textTransform:"none",letterSpacing:0,fontWeight:400}}><span className="ai-dot" style={{background:"var(--tact)"}}/><span className="ai-dot" style={{background:"var(--tact)"}}/><span className="ai-dot" style={{background:"var(--tact)"}}/>Loading…</span>}
                        {hasResponse&&<span style={{background:"rgba(39,174,96,0.12)",color:"var(--ops)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>✓ Response received</span>}
                        {!hasResponse&&sentAt&&<span style={{background:"rgba(230,126,34,0.1)",color:"var(--cal)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>📧 Sent · awaiting</span>}
                        {!hasResponse&&!sentAt&&formUrl&&<span style={{background:"rgba(74,85,104,0.1)",color:"var(--text2)",padding:"1px 6px",borderRadius:10,fontSize:9,textTransform:"none",letterSpacing:0}}>Link generated</span>}
                        {hasResponse&&isPreMeetingBlock&&(
                          <button className="btn btn-danger" style={{fontSize:9,padding:"1px 8px",marginLeft:"auto",fontWeight:600,textTransform:"none",letterSpacing:0}}
                            onClick={async(e)=>{
                              e.stopPropagation();
                              if(!window.confirm("Clear the submitted response? The client will be able to fill in the form again."))return;
                              const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                              try{
                                const meetingDate=clientMeeting?.iCalUID||clientMeeting?.meetingDate||prepLinkInfo?.meetingKey||prepLinkInfo?.meetingDate||"";
                                const r=await fetch(`${_ab}/api/meeting-prep/${titleClient.id}/response?meeting=${meetingDate}`,{method:"DELETE",headers:{Authorization:`Bearer ${window.__mspToken||""}`}});
                                if(r.ok){setPrepResponse(null);}
                                else{const raw=await r.text();alert("Reset failed: "+raw.slice(0,150));}
                              }catch(e){alert("Reset failed: "+e.message);}
                            }}>↺ Reset</button>
                        )}
                      </div>
                      <span className={`fa-chevron ${focusAccordion["pre-meeting-form"]?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion["pre-meeting-form"]&&(
                      <div className="fa-body">
                        <div style={{padding:"10px 22px 12px"}}>
                          {prepResponseLoading&&<div style={{fontSize:11,color:"var(--text3)",padding:"8px 0"}}>Loading…</div>}
                          {/* Send link — shown for both Prep: and Pre-meeting: blocks */}
                          {!prepResponseLoading&&!hasResponse&&(
                            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              {formUrl&&<div style={{fontSize:10,color:"var(--text3)",fontFamily:"monospace",background:"var(--bg)",padding:"3px 8px",borderRadius:4,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{formUrl}</div>}
                              {formUrl&&<button className="btn" style={{fontSize:9,padding:"3px 8px",flexShrink:0}} onClick={()=>navigator.clipboard.writeText(formUrl)}>📋 Copy</button>}
                              {titleClient&&clientMeeting&&(
                                <button className="btn btn-T" style={{fontSize:9,padding:"3px 10px",flexShrink:0}}
                                  disabled={sendingPrepLink}
                                  onClick={async()=>{
                                    setSendingPrepLink(true);
                                    const email=prompt(`Send pre-meeting form to email address for ${titleClient.companyName}:`,clientEmailOverrides[String(titleClient.id)]||"");
                                    if(!email){setSendingPrepLink(false);return;}
                                    if(!confirmEmailDomain(email,titleClient.id,titleClient.companyName)){setSendingPrepLink(false);return;}
                                    try{
                                      if(!window.__mspToken){alert("Auth token not ready — please wait a moment and try again.");setSendingPrepLink(false);return;}
                                      const _normKey=(k)=>typeof k==="string"?k.replace(/^prep-/,""):k;
                                      const _mk=_normKey(clientMeeting.iCalUID)||clientMeeting.meetingDate;
                                      const res=await fetch(`${apiBase}/api/meeting-prep/${titleClient.id}/send-link`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${window.__mspToken}`},body:JSON.stringify({meetingDate:_mk,meetingDisplayDate:clientMeeting.meetingDate,contactEmail:email,meetingTitle:clientMeeting.meetingTitle||"Monthly Tech Catchup"})});
                                      const raw=await res.text();let d={};try{d=JSON.parse(raw);}catch{}
                                      if(res.ok&&d.sent){setPrepLinkInfo({formUrl:d.formUrl,sentAt:new Date().toISOString()});alert("✅ Pre-meeting form sent to "+email);}
                                      else{alert(`Send failed (HTTP ${res.status}): ${d.error||d.message||raw.slice(0,200)||"No response body"}`);}
                                    }catch(e){alert("Error: "+e.message);}
                                    setSendingPrepLink(false);
                                  }}>{sendingPrepLink?"Sending…":"📧 Send to client"}</button>
                              )}
                            </div>
                          )}
                          {/* Full form response data — Pre-meeting: blocks only, not Prep: */}
                          {!prepResponseLoading&&hasResponse&&isPreMeetingBlock&&(
                            <div style={{display:"flex",flexDirection:"column",gap:8}}>
                              <div style={{fontSize:10,color:"var(--text3)"}}>Submitted {new Date(submittedAt).toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                              {(()=>{
                                const sectionMap={};const textareas=[];
                                questions.forEach(q=>{
                                  const ans=answers[q.id];
                                  if(ans===undefined||ans===null||ans==="")return;
                                  if(q.type==="textarea"){textareas.push({q,ans});return;}
                                  const sec=q.section||"General";
                                  if(!sectionMap[sec])sectionMap[sec]=[];
                                  sectionMap[sec].push({q,ans});
                                });
                                const sectionEntries=Object.entries(sectionMap);
                                if(sectionEntries.length===0&&textareas.length===0)return <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>No answers to display.</div>;
                                return(<>
                                  {sectionEntries.length>0&&(
                                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:7}}>
                                      {sectionEntries.map(([secName,items])=>(
                                        <div key={secName} style={{background:"white",border:"1px solid var(--border)",borderRadius:7,overflow:"hidden"}}>
                                          <div style={{padding:"5px 9px",background:"var(--tact-d)",borderBottom:"1px solid var(--border)",fontSize:9,fontWeight:700,color:"var(--tact)",letterSpacing:"0.04em",lineHeight:1.3}}>{secName}</div>
                                          <div style={{padding:"6px 9px",display:"flex",flexDirection:"column",gap:5}}>
                                            {items.map(({q,ans})=>(
                                              <div key={q.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6}}>
                                                <div style={{fontSize:10,color:"var(--text2)",lineHeight:1.3,flex:1}}>{q.label}</div>
                                                <div style={{flexShrink:0}}>
                                                  {q.type==="rating"
                                                    ?<div style={{fontSize:13,fontWeight:800,color:ans>=4?"var(--ops)":ans>=3?"var(--cal)":"var(--danger)",lineHeight:1}}>{ans}<span style={{fontSize:8,color:"var(--text3)",fontWeight:400}}>/{q.scale||5}</span></div>
                                                    :<div style={{fontSize:10,fontWeight:700,color:ans==="yes"?"var(--ops)":ans==="no"?"var(--danger)":"var(--text3)"}}>{ans==="yes"?"✓":ans==="no"?"✗":String(ans)}</div>
                                                  }
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {textareas.map(({q,ans})=>(
                                    <div key={q.id} style={{background:"var(--s2)",borderRadius:6,padding:"7px 10px",border:"1px solid var(--border)"}}>
                                      <div style={{fontSize:9,fontWeight:600,color:"var(--text3)",marginBottom:3}}>{q.label}</div>
                                      <div style={{fontSize:11,color:"var(--text)",lineHeight:1.55,whiteSpace:"pre-wrap"}}>{String(ans)}</div>
                                    </div>
                                  ))}
                                </>);
                              })()}
                            </div>
                          )}
                          {/* Prep: block with response received — show summary only, not full grid */}
                          {!prepResponseLoading&&hasResponse&&isPurePrepBlock&&(
                            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"rgba(39,174,96,0.07)",border:"1px solid rgba(39,174,96,0.2)",borderRadius:7}}>
                              <span style={{fontSize:16}}>✅</span>
                              <div>
                                <div style={{fontSize:12,fontWeight:600,color:"var(--ops)"}}>Form response received</div>
                                <div style={{fontSize:10,color:"var(--text3)"}}>Submitted {new Date(submittedAt).toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})} · Review full responses in your Pre-Meeting event</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Send Pre-Meeting Forms Block ── */}
              {sel?.id==="send-forms-block"&&(()=>{
                const perthNow=new Date(new Date().getTime()+8*60*60*1000);
                const todayStr=`${perthNow.getUTCFullYear()}-${String(perthNow.getUTCMonth()+1).padStart(2,"0")}-${String(perthNow.getUTCDate()).padStart(2,"0")}`;
                const dueClients=meetings.filter(m=>{
                  if(m.type!=="monthly") return false;
                  const dateStr=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                  if(!dateStr) return false;
                  const meetingDate=new Date(dateStr+"T12:00:00");
                  const prepDate=subWorkingDays(meetingDate,3);
                  const sendDate=subWorkingDays(prepDate,2);
                  return sendDate.toISOString().slice(0,10)===todayStr;
                }).map(m=>{
                  const dateStr=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                  const client=m.client||(()=>{const matched=atCompanies.find(c=>m.title?.toLowerCase().includes(c.companyName.toLowerCase()));return matched?.companyName||"";})();
                  const company=atCompanies.find(c=>c.companyName.toLowerCase()===client.toLowerCase()||m.title?.toLowerCase().includes(c.companyName.toLowerCase()));
                  const meetingDate=new Date(dateStr+"T12:00:00");
                  const prepDate=subWorkingDays(meetingDate,3);
                  return{id:m.id,iCalUID:m.iCalUID||m.id,title:m.title,client,company,meetingDate:dateStr,prepDate:prepDate.toISOString().slice(0,10),savedEmail:company?clientEmailOverrides[String(company.id)]||"":""};
                });
                return(
                  <div style={{padding:"14px 22px 16px",borderBottom:"1px solid var(--border)",flexShrink:0}}>
                    <div style={{fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--tact)",marginBottom:12,display:"flex",alignItems:"center",gap:6,fontWeight:700}}>
                      <span>📧</span> Pre-Meeting Forms to Send
                      <span style={{fontSize:9,fontWeight:400,color:"var(--text3)",textTransform:"none",letterSpacing:0}}>· send now so clients can respond before your prep session</span>
                    </div>
                    {dueClients.length===0
                      ?<div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>No forms due today.</div>
                      :<div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {dueClients.map(p=>{
                          const isSent=sentForms.has(p.company?String(p.company.id):""+p.id);
                          return(
                            <div key={p.id} style={{background:"white",border:`1px solid ${isSent?"rgba(39,174,96,0.3)":"var(--border)"}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,boxShadow:"var(--shadow-sm)"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontWeight:600,fontSize:13,color:"var(--text)",marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.client||p.title}</div>
                                <div style={{fontSize:10,color:"var(--text3)",display:"flex",gap:10}}>
                                  <span>Meeting: <strong style={{color:"var(--text2)"}}>{fmtD(p.meetingDate)}</strong></span>
                                  <span>Prep: {fmtD(p.prepDate)}</span>
                                  {p.savedEmail&&<span style={{color:"var(--tact)"}}>📧 {p.savedEmail}</span>}
                                  {!p.savedEmail&&<span style={{color:"var(--warn)"}}>⚠ No email set — will prompt</span>}
                                </div>
                              </div>
                              {isSent
                                ?<div style={{fontSize:11,fontWeight:600,color:"var(--ops)",flexShrink:0}}>✓ Sent</div>
                                :<button className="btn btn-T" style={{fontSize:10,padding:"6px 14px",flexShrink:0}}
                                  onClick={async()=>{
                                    if(!p.company){alert("Company not found in Autotask — run a sync first.");return;}
                                    const email=prompt(`Send pre-meeting form to email address for ${p.client}:`,p.savedEmail||"");
                                    if(!email) return;
                                    if(!confirmEmailDomain(email,p.company.id,p.client)) return;
                                    if(!window.__mspToken){alert("Auth token not ready — try again in a moment.");return;}
                                    try{
                                      const _normKey=(k)=>typeof k==="string"?k.replace(/^prep-/,""):k;
                                      const _mk=_normKey(p.iCalUID)||p.meetingDate;
                                      const r=await fetch(`${apiBase}/api/meeting-prep/${p.company.id}/send-link`,{
                                        method:"POST",
                                        headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken}`},
                                        body:JSON.stringify({meetingDate:_mk,meetingDisplayDate:p.meetingDate,contactEmail:email,meetingTitle:p.title||"Monthly Tech Catchup"})
                                      });
                                      const raw=await r.text();let d={};try{d=JSON.parse(raw);}catch{}
                                      if(r.ok&&d.sent){setSentForms(s=>new Set([...s,String(p.company.id)]));}
                                      else{alert("Send failed (HTTP "+r.status+"): "+(d.error||raw.slice(0,150)||"No response"));}
                                    }catch(e){alert("Error: "+e.message);}
                                  }}>
                                  📧 Send Form
                                </button>
                              }
                            </div>
                          );
                        })}
                        {dueClients.length>0&&dueClients.every(p=>sentForms.has(p.company?String(p.company.id):""+p.id))&&(
                          <div style={{fontSize:11,color:"var(--ops)",fontWeight:600,textAlign:"center",padding:"8px 0"}}>
                            ✓ All forms sent — mark this block as done
                          </div>
                        )}
                      </div>
                    }
                  </div>
                );
              })()}

              {/* ── Meeting Block — Pre-Meeting Context (read-only) ── */}
              {(()=>{
                const isPrepBlock=sel&&(sel.title?.toLowerCase().startsWith("prep:")||sel.title?.toLowerCase().startsWith("pre-meeting:")||sel.title?.toLowerCase().includes("pre-meeting prep"));
                const isMeetingBlock=sel&&!isPrepBlock&&(sel.title?.toLowerCase().includes("monthly tech catchup")||sel.title?.toLowerCase().includes("tech catchup")||sel.sourceType==="meeting");
                if(!isMeetingBlock||!prepResponse?.submittedAt) return null;
                const questions=prepResponse?.questions||[];
                const answers=prepResponse?.answers||{};
                const sectionMap={};const textareas=[];
                questions.forEach(q=>{
                  const ans=answers[q.id];
                  if(ans===undefined||ans===null||ans==="")return;
                  if(q.type==="textarea"){textareas.push({q,ans});return;}
                  const sec=q.section||"General";
                  if(!sectionMap[sec])sectionMap[sec]=[];
                  sectionMap[sec].push({q,ans});
                });
                const sectionEntries=Object.entries(sectionMap);
                if(sectionEntries.length===0&&textareas.length===0)return null;
                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("meeting-context")}>
                      <div className="fa-label"><span>📋</span> Pre-Meeting Responses
                        {prepResponseLoading&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:9,color:"var(--text3)",textTransform:"none",letterSpacing:0,fontWeight:400}}><span className="ai-dot" style={{background:"var(--tact)"}}/><span className="ai-dot" style={{background:"var(--tact)"}}/><span className="ai-dot" style={{background:"var(--tact)"}}/>Loading…</span>}
                        <span style={{background:"rgba(39,174,96,0.1)",color:"var(--ops)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>✓ Received {new Date(prepResponse.submittedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}</span>
                      </div>
                      <span className={`fa-chevron ${focusAccordion["meeting-context"]?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion["meeting-context"]&&(
                      <div className="fa-body">
                        <div style={{padding:"10px 22px 12px"}}>
                          {sectionEntries.length>0&&(
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:7,marginBottom:textareas.length?8:0}}>
                              {sectionEntries.map(([secName,items])=>(
                                <div key={secName} style={{background:"white",border:"1px solid var(--border)",borderRadius:7,overflow:"hidden"}}>
                                  <div style={{padding:"4px 9px",background:"var(--tact-d)",borderBottom:"1px solid var(--border)",fontSize:9,fontWeight:700,color:"var(--tact)",letterSpacing:"0.04em"}}>{secName}</div>
                                  <div style={{padding:"6px 9px",display:"flex",flexDirection:"column",gap:4}}>
                                    {items.map(({q,ans})=>(
                                      <div key={q.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6}}>
                                        <div style={{fontSize:10,color:"var(--text2)",lineHeight:1.3,flex:1}}>{q.label}</div>
                                        <div style={{flexShrink:0,fontSize:10,fontWeight:700,color:ans==="yes"?"var(--ops)":ans==="no"?"var(--danger)":"var(--text3)"}}>{ans==="yes"?"✓":ans==="no"?"✗":String(ans)}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {textareas.map(({q,ans})=>(
                            <div key={q.id} style={{background:"var(--s2)",borderRadius:6,padding:"6px 10px",border:"1px solid var(--border)",marginBottom:5}}>
                              <div style={{fontSize:9,fontWeight:600,color:"var(--text3)",marginBottom:2}}>{q.label}</div>
                              <div style={{fontSize:11,color:"var(--text)",lineHeight:1.5,whiteSpace:"pre-wrap"}}>{String(ans)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Meeting Intelligence Pack ── */}
              {(()=>{
                // MIP only shows for Pre-meeting: blocks and actual meeting blocks
                // Prep: blocks (5WD out, just send form) do NOT get MIP
                const isPrepBlock=sel&&sel.title?.toLowerCase().startsWith("prep:");
                if(isPrepBlock) return null;
                const isPreMeetingBlock=sel&&(sel.title?.toLowerCase().startsWith("pre-meeting:")||sel.title?.toLowerCase().includes("pre-meeting prep"));
                const isMeetingBlock=sel&&!isPreMeetingBlock&&(sel.title?.toLowerCase().includes("monthly tech catchup")||sel.title?.toLowerCase().includes("tech catchup")||sel.sourceType==="meeting");
                if(!isPreMeetingBlock&&!isMeetingBlock)return null;
                const titleClient=resolveTitleClient(sel);
                if(!titleClient)return null;
                const clientMeeting=upcomingMeetingPreps.find(p=>titleClient&&(normCo(p.client||"")===normCo(titleClient.companyName)||p.client?.toLowerCase()===titleClient.companyName.toLowerCase()));
                const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

                const generateBriefPack=async(existingUrl=null)=>{
                  setMeetingBriefLoading(true);setMeetingBriefExpanded(true);setMeetingBriefBlockId(selId);
                  // Clear stale committed URL on fresh generate (not on refresh-with-existing-url)
                  if(!existingUrl) setMeetingPageUrl(null);
                  setMeetingBriefStatus("Gathering Autotask ticket data…");
                  const statusSteps=[[1400,"Reading pre-meeting form responses…"],[2800,"Checking Datto RMM…"],[4200,"Reviewing past meeting notes…"],[5600,"Analysing sentiment…"],[7000,"Generating talking points…"]];
                  const statusTimers=statusSteps.map(([ms,msg])=>setTimeout(()=>setMeetingBriefStatus(msg),ms));
                  const clientCadence=Object.entries(cadence).flatMap(([layer,cat])=>(Array.isArray(cat.items)?cat.items:[]).filter(i=>i.client&&i.client.toLowerCase().includes(titleClient.companyName.toLowerCase().split(" ")[0])).map(i=>({layer,title:i.title,done:i.done,dueDate:i.dueDate||null})));
                  const _sites=Array.isArray(dattoData?.sites)?dattoData.sites:[];
                  const _alerts=Array.isArray(dattoData?.alerts)?dattoData.alerts:[];
                  const clientSites=_sites.filter(s=>titleClient.companyName.toLowerCase().split(" ").some(w=>w.length>3&&(s.name||"").toLowerCase().includes(w)));
                  const clientAlerts=_alerts.filter(a=>clientSites.some(s=>a.alertSourceInfo?.siteUid===s.uid));
                  const dattoSummary=clientSites.length>0?{siteCount:clientSites.length,deviceCount:clientSites.reduce((n,s)=>n+(s.deviceCount||0),0),offlineCount:clientSites.reduce((n,s)=>n+(s.offlineCount||0),0),alertCount:clientAlerts.length,criticalCount:clientAlerts.filter(a=>(a.alertContext?.alertSeverity||"").toLowerCase()==="critical").length}:null;
                  const clientFellow=fellowMeetings.filter(m=>m.title?.toLowerCase().includes(titleClient.companyName.toLowerCase().split(" ")[0])).slice(0,3).map(m=>({title:m.title,date:m.date,preview:m.preview||""}));
                  const prepFormData=prepResponse?{questions:prepResponse.questions||[],answers:prepResponse.answers||{},submittedAt:prepResponse.submittedAt}:null;
                  const clientSentiment=(preloadedSentiment?.results||[]).find(r=>String(r.companyId)===String(titleClient.id)||(r.companyName||"").toLowerCase()===titleClient.companyName.toLowerCase());
                  const sentimentData=clientSentiment?{score:clientSentiment.score,sentiment:clientSentiment.sentiment,summary:clientSentiment.summary}:null;
                  try{
                    const r=await fetch(`${_apiBase}/api/ai/meeting-brief`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},body:JSON.stringify({companyId:String(titleClient.id),companyName:titleClient.companyName,meetingDate:clientMeeting?.iCalUID||clientMeeting?.meetingDate||"",meetingTitle:sel.title,cadenceItems:clientCadence,dattoSummary,prepFormData,fellowNotes:clientFellow,sentimentData})});
                    const raw=await r.text();let d={};try{d=JSON.parse(raw);}catch{}
                    if(r.ok&&d.snapshot){
                      // Guard: only use existingUrl if it's a real http URL string
                      const safeExistingUrl = typeof existingUrl==="string"&&existingUrl.startsWith("http") ? existingUrl : null;
                      /* ⛔ FREEZE THE ROADMAP AT GENERATION. The Meeting Notes
                         section is a historical record of what was discussed, so
                         the item SET and every descriptive field are captured
                         now — items added later belong to a later meeting.
                         ⚠ Quote-state is deliberately NOT frozen (see
                         meetingRoadmap.js); it is read live at view time, or the
                         Create Quote button would appear to do nothing.
                         ⚠ Best-effort: a client with no roadmap, or a failed
                         fetch, must not block the brief — the section simply
                         does not render. */
                      let roadmapSnapshot=null;
                      try{
                        roadmapSnapshot=freezeRoadmapForMeeting(await getRoadmap(titleClient.id));
                      }catch(_e){ roadmapSnapshot=null; }
                      const briefWithUrl={
                        ...d,
                        ...(roadmapSnapshot?{roadmapSnapshot}:{}),
                        // Add display metadata — meeting.html needs these to render the header
                        companyName: titleClient.companyName,
                        meetingTitle: sel.title,
                        meetingDate: clientMeeting?.meetingDate||"", // actual date string for display (not iCalUID)
                        contactEmail: prepLinkInfo?.contactEmail || clientEmailOverrides?.[String(titleClient.id)] || "",
                        ...(safeExistingUrl?{committedMeetingUrl:safeExistingUrl}:{})
                      };
                      setMeetingBrief(briefWithUrl);
                      if(safeExistingUrl) setMeetingPageUrl(safeExistingUrl);
                      // Auto-save to blob immediately after generation so brief persists across navigation
                      const saveKey=encodeURIComponent(prepLinkInfo?.meetingKey||prepLinkInfo?.meetingDate||clientMeeting?.iCalUID||clientMeeting?.meetingDate||sel?._iCalUID||sel?._meetingDate||"");
                      if(saveKey&&saveKey!==encodeURIComponent("")){
                        fetch(`${_apiBase}/api/ai/meeting-brief/${titleClient.id}?meeting=${saveKey}`,{
                          method:"PUT",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                          body:JSON.stringify(briefWithUrl)
                        }).catch(()=>{});
                      }
                    }
                    else{alert("Brief failed (HTTP "+r.status+"): "+(d.error||raw.slice(0,150)));setMeetingBriefExpanded(false);}
                  }catch(e){alert("Brief failed: "+e.message);setMeetingBriefExpanded(false);}
                  finally{statusTimers.forEach(t=>clearTimeout(t));setMeetingBriefLoading(false);setMeetingBriefStatus("");}
                };

                const renderBullets=(items=[])=>items.map((item,i)=>{
                  const isNone=/no data available/i.test(item);
                  const isWarn=item.startsWith("⚠")||/offline|alert|critical|overdue/i.test(item);
                  const isOk=item.startsWith("✓");
                  return <div key={i} className={`mip-bullet${isNone?" mip-none":isWarn?" mip-warn":isOk?" mip-ok":""}`}>{item.replace(/^[✓✗⚠]\s*/,"")}</div>;
                });

                return(
                  <>
                    <div className="fa-hdr" onClick={()=>toggleFocusSection("mip")}>
                      <div className="fa-label"><span>✨</span> Meeting Intelligence Pack
                        {meetingBrief?.generatedAt&&<span style={{fontSize:9,fontWeight:400,color:"var(--ops)",textTransform:"none",letterSpacing:0}}>· saved {new Date(meetingBrief.generatedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>}
                      </div>
                      <span className={`fa-chevron ${focusAccordion.mip?"open":""}`}>▶</span>
                    </div>
                    {focusAccordion.mip&&(
                  <div className="mip-section" style={{borderBottom:"none"}}>
                    <div className="mip-hdr">
                      <div className="mip-label" style={{visibility:"hidden",height:0,overflow:"hidden"}}>placeholder</div>
                      {meetingBrief&&!meetingBriefLoading&&!meetingBriefEditing&&(
                        <button className="btn" style={{fontSize:9,padding:"3px 9px"}} onClick={()=>{
                          const savedUrl=meetingBrief?.committedMeetingUrl||meetingPageUrl;
                          setMeetingBrief(null);
                          // Keep the committed URL — refresh just regenerates the content
                          generateBriefPack(savedUrl);
                        }}>↻ Refresh</button>
                      )}
                      {meetingBrief&&meetingBriefExpanded&&!meetingBriefLoading&&!meetingBriefEditing&&(
                        <button className="btn" style={{fontSize:9,padding:"3px 9px",background:"var(--strat-d)",color:"var(--strat)",borderColor:"rgba(11,128,165,0.25)"}}
                          onClick={()=>{setMeetingBriefOriginal(JSON.parse(JSON.stringify(meetingBrief)));setMeetingBriefEditing(true);}}>✎ Edit</button>
                      )}
                      {meetingBriefExpanded&&meetingBrief&&!meetingBriefEditing&&(
                        <button className="ico-btn" style={{fontSize:11}} onClick={()=>setMeetingBriefExpanded(false)}>▲</button>
                      )}
                      {!meetingBriefExpanded&&!meetingBriefLoading&&(
                        <button className="btn btn-T" style={{fontSize:10,padding:"4px 12px"}} onClick={meetingBrief?()=>setMeetingBriefExpanded(true):generateBriefPack}>
                          {meetingBrief?"▼ Show":"✨ Generate"}
                        </button>
                      )}
                      {meetingBriefLoading&&(
                        <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:"var(--tact)"}}>
                          <div className="ai-dot" style={{background:"var(--tact)"}}/><div className="ai-dot" style={{background:"var(--tact)"}}/><div className="ai-dot" style={{background:"var(--tact)"}}/>
                          {meetingBriefStatus||"Generating…"}
                        </div>
                      )}
                    </div>

                    {meetingBriefExpanded&&meetingBrief&&!meetingBriefLoading&&(()=>{
                      const updBullet=(section,idx,val)=>setMeetingBrief(b=>({...b,[section]:b[section].map((v,i)=>i===idx?val:v)}));
                      const delBullet=(section,idx)=>setMeetingBrief(b=>({...b,[section]:b[section].filter((_,i)=>i!==idx)}));
                      const addBullet=(section)=>setMeetingBrief(b=>({...b,[section]:[...(b[section]||[]),"New item"]}));
                      const renderEditBullets=(section)=>(
                        <div>{(meetingBrief[section]||[]).map((item,idx)=>(
                          <div key={idx} className="mip-edit-row">
                            <textarea className="mip-edit-input" value={item} rows={1} onChange={e=>updBullet(section,idx,e.target.value)} onInput={e=>{e.target.style.height="auto";e.target.style.height=e.target.scrollHeight+"px";}}/>
                            <button className="ico-btn" style={{flexShrink:0,marginTop:1}} onClick={()=>delBullet(section,idx)}>×</button>
                          </div>
                        ))}<button className="mip-edit-add" onClick={()=>addBullet(section)}>＋ Add</button></div>
                      );
                      const saveBrief=async()=>{
                        setMeetingBriefSaving(true);
                        const saveKey=encodeURIComponent(prepLinkInfo?.meetingKey||prepLinkInfo?.meetingDate||clientMeeting?.iCalUID||clientMeeting?.meetingDate||sel?._iCalUID||sel?._meetingDate||"");
                        try{
                          const r=await fetch(`${_apiBase}/api/ai/meeting-brief/${titleClient.id}?meeting=${saveKey}`,{method:"PUT",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},body:JSON.stringify(meetingBrief)});
                          if(r.ok){setMeetingBriefEditing(false);setMeetingBriefOriginal(null);}
                          else{const raw=await r.text();alert("Save failed: "+raw.slice(0,150));}
                        }catch(e){alert("Save failed: "+e.message);}
                        finally{setMeetingBriefSaving(false);}
                      };
                      const commitMeeting=async()=>{
                        setMeetingCommitting(true);
                        const resolvedKey = prepLinkInfo?.meetingKey || prepLinkInfo?.meetingDate ||
                          clientMeeting?.iCalUID || clientMeeting?.meetingDate ||
                          sel?._iCalUID || sel?._meetingDate || "";
                        // Guard: resolvedKey must be a non-empty string, not an object
                        const safeKey = typeof resolvedKey==="string" ? resolvedKey : String(resolvedKey||"");
                        if(!safeKey){
                          alert("Cannot commit — meeting key not resolved. Please reselect this block.");
                          setMeetingCommitting(false); return;
                        }
                        const meetingKey=encodeURIComponent(safeKey);
                        // Safe serialise brief — strip any non-JSON-serialisable values (e.g. window refs)
                        let briefBody;
                        try{
                          briefBody=JSON.stringify({brief:meetingBrief});
                        }catch{
                          // Brief has circular/non-serialisable values — pick only known-safe fields
                          const safeBrief={
                            snapshot:meetingBrief?.snapshot||"",
                            openIssues:meetingBrief?.openIssues||[],
                            clientFeedback:meetingBrief?.clientFeedback||[],
                            infrastructure:meetingBrief?.infrastructure||[],
                            outstandingActions:meetingBrief?.outstandingActions||[],
                            sentiment:meetingBrief?.sentiment||null,
                            talkingPoints:meetingBrief?.talkingPoints||[],
                            generatedAt:meetingBrief?.generatedAt||"",
                          };
                          briefBody=JSON.stringify({brief:safeBrief});
                        }
                        try{
                          const r=await fetch(`${_apiBase}/api/ai/meeting-brief/${titleClient.id}/commit?meeting=${meetingKey}`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},body:briefBody});
                          const raw=await r.text();let d={};try{d=JSON.parse(raw);}catch{}
                          // Guard: meetingUrl must be a real http URL string
                          const safeUrl = typeof d.meetingUrl==="string"&&d.meetingUrl.startsWith("http") ? d.meetingUrl : null;
                          if(r.ok&&safeUrl){
                            setMeetingPageUrl(safeUrl);
                            const updatedBrief={...meetingBrief,committedMeetingUrl:safeUrl,committedAt:d.committedAt||new Date().toISOString()};
                            setMeetingBrief(updatedBrief);
                            await fetch(`${_apiBase}/api/ai/meeting-brief/${titleClient.id}?meeting=${meetingKey}`,{
                              method:"PUT",
                              headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                              body:JSON.stringify(updatedBrief)
                            }).catch(()=>{});
                          }
                          else{alert("Commit failed: "+(d.error||raw.slice(0,150)));}
                        }catch(e){alert("Commit failed: "+e.message);}
                        finally{setMeetingCommitting(false);}
                      };
                      return(
                        <div style={{marginTop:12}}>
                          {meetingBriefEditing
                            ?<textarea className="mip-edit-input" style={{marginBottom:10,minHeight:64}} value={meetingBrief.snapshot||""} onChange={e=>setMeetingBrief(b=>({...b,snapshot:e.target.value}))}/>
                            :<div className="mip-snapshot">{meetingBrief.snapshot}</div>
                          }
                          <div className="mip-grid">
                            {[{key:"openIssues",hdr:"🎫 Open Issues (Autotask)"},{key:"clientFeedback",hdr:"📋 Client Feedback (Form)"},{key:"infrastructure",hdr:"🖥️ Infrastructure (Datto)"},{key:"outstandingActions",hdr:"✅ Outstanding Actions (Fellow)"}].map(({key,hdr})=>(
                              <div key={key} className="mip-card">
                                <div className="mip-card-hdr">{hdr}</div>
                                <div className="mip-card-body">{meetingBriefEditing?renderEditBullets(key):renderBullets(meetingBrief[key]||[])}</div>
                              </div>
                            ))}
                            {meetingBrief.sentiment&&(
                              <div className="mip-card" style={{gridColumn:"1/-1"}}>
                                <div className="mip-card-hdr">😊 Customer Sentiment
                                  {meetingBrief.sentiment.score!=null&&<span style={{marginLeft:"auto",fontSize:13,fontWeight:800,color:meetingBrief.sentiment.score>=7?"var(--ops)":meetingBrief.sentiment.score>=5?"var(--cal)":"var(--danger)"}}>{meetingBrief.sentiment.score}<span style={{fontSize:9,fontWeight:400,color:"var(--text3)"}}>/10</span></span>}
                                </div>
                                <div className="mip-card-body">
                                  {meetingBrief.sentiment.summary&&<div className="mip-bullet" style={{paddingLeft:0,marginBottom:4}}>{meetingBrief.sentiment.summary}</div>}
                                  {renderBullets(meetingBrief.sentiment.bullets||[])}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="mip-tp-section">
                            <div className="mip-tp-hdr">🎯 Talking Points</div>
                            <div className="mip-tp-body">
                              {meetingBriefEditing?(
                                <div>{(meetingBrief.talkingPoints||[]).map((tp,i)=>(
                                  <div key={i} className="mip-edit-row" style={{alignItems:"flex-start"}}>
                                    <div className="mip-tp-num" style={{paddingTop:5}}>{i+1}.</div>
                                    <textarea className="mip-edit-input" style={{flex:1}} value={tp.replace(/^\d+\.\s*/,"")} rows={1} onChange={e=>setMeetingBrief(b=>({...b,talkingPoints:b.talkingPoints.map((v,j)=>j===i?e.target.value:v)}))} onInput={e=>{e.target.style.height="auto";e.target.style.height=e.target.scrollHeight+"px";}}/>
                                    <button className="ico-btn" style={{flexShrink:0,marginTop:2}} onClick={()=>setMeetingBrief(b=>({...b,talkingPoints:b.talkingPoints.filter((_,j)=>j!==i)}))}>×</button>
                                  </div>
                                ))}<button className="mip-edit-add" onClick={()=>setMeetingBrief(b=>({...b,talkingPoints:[...(b.talkingPoints||[]),"New talking point"]}))}>＋ Add</button></div>
                              ):(meetingBrief.talkingPoints||[]).map((tp,i)=>(
                                <div key={i} className="mip-tp-item"><div className="mip-tp-num">{i+1}.</div><div>{tp.replace(/^\d+\.\s*/,"")}</div></div>
                              ))}
                            </div>
                          </div>
                          {/* ── RECOMMENDATIONS & ROADMAP — below Talking Points ──
                              ⚠ Frozen rows from when the notes were generated;
                              only quote-state is live. The Create Quote button is
                              INTERNAL and never reaches the client-facing
                              meeting page, which renders the same rows read-only. */}
                          <MeetingRoadmapSection
                            snapshot={meetingBrief.roadmapSnapshot}
                            companyId={titleClient?.id}
                            internal
                            onCreateQuote={async (item)=>{
                              try{
                                await createQuoteFromRoadmapItem({...item, companyId:titleClient?.id, companyName:titleClient?.companyName||titleClient?.name||""});
                              }catch(e){
                                /* The server's guard fired — already quoted, so nothing
                                   was created and no second email went out. Correct, not
                                   an error; the reload shows the badge. */
                                if(!e.alreadyQuoted) alert(e.message||"Could not create the quote.");
                              }
                            }}
                          />
                          {meetingBriefEditing?(
                            <div style={{display:"flex",gap:8,marginTop:12,padding:"10px 0",borderTop:"1px solid var(--border)"}}>
                              <button className="btn btn-S" style={{background:"var(--strat)",color:"white",borderColor:"var(--strat)",fontSize:11}} disabled={meetingBriefSaving} onClick={saveBrief}>{meetingBriefSaving?"Saving…":"✓ Save"}</button>
                              <button className="btn" style={{fontSize:11}} onClick={()=>{setMeetingBrief(meetingBriefOriginal);setMeetingBriefEditing(false);setMeetingBriefOriginal(null);}}>Cancel</button>
                            </div>
                          ):(
                            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:12,padding:"10px 0",borderTop:"1px solid var(--border)"}}>
                              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                                {meetingPageUrl?(
                                  <button className="mip-commit-btn" style={{background:"linear-gradient(135deg,var(--ops),#219653)",cursor:"default",opacity:0.9}} disabled>
                                    ✓ Committed to Meeting
                                  </button>
                                ):(
                                  <button className="mip-commit-btn" disabled={meetingCommitting} onClick={commitMeeting}>
                                    {meetingCommitting?"Committing…":"🚀 Commit to Meeting Page"}
                                  </button>
                                )}
                                {/* Re-commit button always available to update after edits */}
                                {meetingPageUrl&&!meetingCommitting&&(
                                  <button className="btn" style={{fontSize:9,padding:"3px 9px"}} onClick={commitMeeting}>↻ Re-commit</button>
                                )}
                              </div>
                              {meetingPageUrl&&(
                                <div className="mip-meeting-url">
                                  <span style={{fontSize:10,fontWeight:600,color:"var(--ops)"}}>✓ Meeting page ready</span>
                                  <a href={meetingPageUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"var(--strat)",fontWeight:600,textDecoration:"none",marginLeft:4}}>Open Meeting Page →</a>
                                  <button className="btn" style={{fontSize:9,padding:"2px 7px",marginLeft:"auto"}} onClick={()=>navigator.clipboard.writeText(meetingPageUrl)}>📋 Copy</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                  </>
                );
              })()}

            </div>
            );
          })()}

          {/* ── PREP NOTES TAB ── */}
          {tab==="prepnotes"&&(()=>{
            const todayStr=todayISO();
            // Upcoming/Future: today and beyond. A meeting that is today still
            // benefits from prep, so it lives in the Upcoming accordion and
            // qualifies for "Due Within 7 Days" (daysUntil===0).
            const allFuturePreps=[...upcomingMeetingPreps].filter(p=>p.meetingDate>=todayStr).sort((a,b)=>a.daysUntil-b.daysUntil);
            const urgentPreps=allFuturePreps.filter(p=>p.daysUntil<=7);
            const laterPreps=allFuturePreps.filter(p=>p.daysUntil>7);
            // Gate the whole tab behind a "data ready" flag. We need BOTH the
            // calendar (meetings) AND the Autotask company cache (atCompanies)
            // loaded before rendering — otherwise the prep cards briefly show
            // the attendee's display name from the Graph event, then re-render
            // to the canonical hotel name once company resolution can run.
            // Showing a spinner avoids that flicker.
            const dataReady=meetings.length>0&&atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>
                  {meetings.length===0?"Loading calendar data…":"Loading company data…"}
                  <br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span>
                </div>
              </div>
            );
            // Past: monthly calendar meetings from the last 6 months, EXCLUDING
            // today (today lives in Upcoming so a meeting can't be in both lists)
            const pastCutoff=pastWindowStartISO();
            const pastPreps=[...meetings]
              .filter(m=>m.type==="monthly"&&(()=>{
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                if(!ds)return false;
                return ds<todayStr&&ds>=pastCutoff;
              })())
              .map(m=>{
                const dateStr=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                let client=m.client||"";
                // 1. Exact/partial match on existing client string
                if(client){
                  const exactMatch=atCompanies.find(c=>c.companyName.toLowerCase()===client.toLowerCase());
                  const partialMatch=!exactMatch&&atCompanies.find(c=>{const nl=c.companyName.toLowerCase(),cl=client.toLowerCase();return nl.includes(cl)||cl.includes(nl);});
                  if(exactMatch)client=exactMatch.companyName;
                  else if(partialMatch)client=partialMatch.companyName;
                }
                // 2. Company name in event title/subject
                if(!client){const titleMatch=atCompanies.find(c=>m.title?.toLowerCase().includes(c.companyName.toLowerCase()));if(titleMatch)client=titleMatch.companyName;}
                // 3. Attendee email domains → clientDomainOverrides (Graph API format: attendees[].emailAddress.address)
                if(!client&&m.attendees?.length){
                  for(const att of m.attendees){
                    const email=att.emailAddress?.address||att.email||"";
                    const domain=email.split("@")[1]||"";
                    if(!domain||domain==="datatech.net.au")continue;
                    const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                    if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){client=co.companyName;break;}}
                  }
                }
                const daysAgo=Math.ceil((new Date(todayStr+"T00:00:00")-new Date(dateStr+"T12:00:00"))/(1000*60*60*24));
                return{id:`past-${m.id||m.title}-${dateStr}`,client,meetingTitle:m.title||"Monthly Meeting",meetingDate:dateStr,startTime:m.startTime||"",daysAgo,iCalUID:m.iCalUID||m.id||null,isPast:true};
              })
              .sort((a,b)=>b.meetingDate.localeCompare(a.meetingDate));
            const sp=selectedPrepBlock;
            const questions=prepNotesResponse?.questions||[];
            const answers=prepNotesResponse?.answers||{};
            const submittedAt=prepNotesResponse?.submittedAt;
            const hasPrepBlockToday=(p)=>blocks.some(b=>(b.type==="meeting-prep"||b.prepFor)&&(b.title?.toLowerCase().includes(p.client.toLowerCase())||b.prepFor===p.iCalUID));
            // Shared right-panel detail renderer
            const renderDetail=()=>{
              if(!sp)return(
                <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:"var(--text3)",fontSize:12,lineHeight:1.8,padding:40,textAlign:"center"}}>
                  <div style={{fontSize:32,marginBottom:12}}>📋</div>
                  Select a meeting on the left<br/>to review the pre-meeting form, AI brief,<br/>and prep notes.
                </div>
              );
              // Show full-panel spinner while loading — prevents stale data flash
              if(prepNotesLoading)return(
                <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40}}>
                  <div style={{width:36,height:36,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--tact)",animation:"lspin 0.7s linear infinite"}}/>
                  <div style={{fontSize:12,color:"var(--text3)"}}>Loading prep data for {sp.client}…</div>
                </div>
              );
              const isPast=sp.isPast||sp.daysUntil<0;
              return(
                <div className="scroll" style={{padding:"18px 22px"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:4,gap:12}}>
                    <div style={{fontWeight:700,fontSize:15,color:"var(--text)"}}>{sp.client}</div>
                    <span style={{fontSize:10,padding:"3px 10px",borderRadius:12,
                      background:isPast?"var(--ops-d)":sp.daysUntil<=2?"var(--danger-d)":sp.daysUntil<=5?"var(--warn-d)":"var(--strat-d)",
                      color:isPast?"var(--ops)":sp.daysUntil<=2?"var(--danger)":sp.daysUntil<=5?"var(--warn)":"var(--strat)",
                      fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>
                      {isPast?`${sp.daysAgo||Math.abs(sp.daysUntil||0)}d ago`:sp.daysUntil===0?"TODAY":`${sp.daysUntil}d until meeting`}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>{sp.meetingTitle}</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:4,display:"flex",gap:12,flexWrap:"wrap"}}>
                    <span>📅 {new Date(sp.meetingDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"})}</span>
                    {sp.startTime&&<span>⏰ {sp.startTime}</span>}
                  </div>
                  {!isPast&&(
                    <div style={{fontSize:11,color:sp.isDue?"var(--warn)":"var(--text3)",marginBottom:12,fontWeight:sp.isDue?600:400}}>
                      {sp.isDue?"⚠ Prep due — ":"📋 Prep date: "}{new Date(sp.prepDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"})}
                    </div>
                  )}
                  {!isPast&&(!hasPrepBlockToday(sp)?(
                    <button className="btn btn-T" style={{marginBottom:12,fontSize:11,padding:"7px 14px"}}
                      onClick={()=>{
                        const newBlock={id:Date.now(),title:`Prep: ${sp.client} — ${sp.meetingTitle}`,layer:"T",start:workStart,dur:30,done:false,prepFor:sp.iCalUID||sp.id,todoId:null,type:"meeting-prep",sourceType:"meeting"};
                        setBlocks(bs=>{const next=[...bs,newBlock];saveSnapshot({blocks:next});return next;});
                      }}>+ Add prep block to today's plan</button>
                  ):(
                    <div style={{marginBottom:12,fontSize:11,color:"var(--ops)",fontWeight:600}}>✓ Prep block already in today's plan</div>
                  ))}
                  {/* ── Send Pre-Meeting Form ── */}
                  {(()=>{
                    const titleClient=atCompanies.find(c=>normCo(c.companyName)===normCo(sp.client)||c.companyName.toLowerCase()===sp.client.toLowerCase());
                    if(!titleClient)return null;
                    const savedEmail=clientEmailOverrides[String(titleClient.id)]||"";
                    const hasResponse=!!prepNotesResponse?.submittedAt;
                    // Compute whether the response actually has any meaningful answers,
                    // not just empty strings from un-filled textareas. Used to decide
                    // between "✓ Response received" vs "⚠ Submitted (empty)" badges.
                    const hasMeaningfulAnswers=hasResponse&&Object.values(prepNotesResponse?.answers||{}).some(v=>v!==null&&v!==undefined&&v!=="");
                    const formUrl=prepNotesLinkInfo?.formUrl;
                    const sentAt=prepNotesLinkInfo?.sentAt;
                    const isSentThisSession=sentForms.has(String(titleClient.id));
                    return(
                      <div style={{background:"white",borderRadius:8,padding:"12px 14px",marginBottom:12,border:"1px solid var(--border)"}}>
                        <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--tact)",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                          📋 Pre-Meeting Form
                          {hasResponse&&hasMeaningfulAnswers&&<span style={{background:"rgba(39,174,96,0.12)",color:"var(--ops)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>✓ Response received</span>}
                          {hasResponse&&!hasMeaningfulAnswers&&<span style={{background:"rgba(230,126,34,0.12)",color:"var(--cal)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>⚠ Submitted empty</span>}
                          {!hasResponse&&(sentAt||isSentThisSession)&&<span style={{background:"rgba(230,126,34,0.1)",color:"var(--cal)",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:600,textTransform:"none",letterSpacing:0}}>📧 Sent · awaiting</span>}
                        </div>
                        {!prepNotesLoading&&!hasResponse&&(
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                            {formUrl&&<div style={{fontSize:10,color:"var(--text3)",fontFamily:"monospace",background:"var(--bg)",padding:"3px 8px",borderRadius:4,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{formUrl}</div>}
                            {formUrl&&<button className="btn" style={{fontSize:9,padding:"3px 8px",flexShrink:0}} onClick={()=>navigator.clipboard.writeText(formUrl)}>📋 Copy</button>}
                            <button className="btn btn-T" style={{fontSize:10,padding:"5px 12px",flexShrink:0}}
                              disabled={sendingPrepLink}
                              onClick={async()=>{
                                setSendingPrepLink(true);
                                if(typeof onGetAuthTokenRef.current==='function'){try{const t=await onGetAuthTokenRef.current();if(t)window.__mspToken=t;}catch{}}
                                const email=prompt(`Send pre-meeting form to email address for ${sp.client}:`,savedEmail||"");
                                if(!email){setSendingPrepLink(false);return;}
                                if(!confirmEmailDomain(email,titleClient.id,sp.client)){setSendingPrepLink(false);return;}
                                try{
                                  if(!window.__mspToken){alert("Auth token not ready — please wait a moment and try again.");setSendingPrepLink(false);return;}
                                  const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                                  const _normKey=(k)=>typeof k==="string"?k.replace(/^prep-/,""):k;
                                  const _mk=_normKey(sp.iCalUID)||sp.meetingDate;
                                  const res=await fetch(`${_apiBase}/api/meeting-prep/${titleClient.id}/send-link`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${window.__mspToken}`},body:JSON.stringify({meetingDate:_mk,meetingDisplayDate:sp.meetingDate,contactEmail:email,meetingTitle:sp.meetingTitle||"Monthly Tech Catchup"})});
                                  const raw=await res.text();let d={};try{d=JSON.parse(raw);}catch{}
                                  if(res.ok&&d.sent){
                                    setPrepNotesLinkInfo(l=>({...(l||{}),formUrl:d.formUrl,sentAt:new Date().toISOString()}));
                                    setSentForms(s=>new Set([...s,String(titleClient.id)]));
                                    alert("✅ Pre-meeting form sent to "+email);
                                  }else{alert(`Send failed (HTTP ${res.status}): ${d.error||d.message||raw.slice(0,200)||"No response body"}`);}
                                }catch(e){alert("Error: "+e.message);}
                                setSendingPrepLink(false);
                              }}>
                              {sendingPrepLink?"Sending…":`📧 Send to ${savedEmail||"client"}`}
                            </button>
                          </div>
                        )}
                        {!prepNotesLoading&&hasResponse&&hasMeaningfulAnswers&&(
                          <div style={{fontSize:11,color:"var(--ops)"}}>✓ Client has already submitted a response — visible below.</div>
                        )}
                        {!prepNotesLoading&&hasResponse&&!hasMeaningfulAnswers&&(
                          <div style={{fontSize:11,color:"var(--cal)"}}>⚠ Form was submitted but no questions were answered — see below.</div>
                        )}
                        {prepNotesLoading&&<div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Loading…</div>}
                      </div>
                    );
                  })()}
                  <div style={{borderTop:"1px solid var(--border)",marginBottom:16}}/>
                  {prepNotesLoading&&<div style={{display:"flex",gap:8,alignItems:"center",color:"var(--tact)",fontSize:11,marginBottom:16}}><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/> Loading prep data…</div>}
                  {!prepNotesLoading&&prepNotesBrief&&(
                    <div style={{background:"var(--tact-d)",borderRadius:8,padding:"12px 16px",marginBottom:16,border:"1px solid var(--tact-g)"}}>
                      <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--tact)",marginBottom:8}}>◆ AI Meeting Brief</div>
                      {/* brief may be a plain string (legacy) or a full MIP object */}
                      {typeof prepNotesBrief==="string"&&(
                        <div style={{fontSize:12,color:"var(--text)",lineHeight:1.65,whiteSpace:"pre-wrap"}}>{prepNotesBrief}</div>
                      )}
                      {typeof prepNotesBrief==="object"&&(
                        <>
                          {prepNotesBrief.snapshot&&<div style={{fontSize:12,color:"var(--text)",lineHeight:1.65,whiteSpace:"pre-wrap",marginBottom:8}}>{prepNotesBrief.snapshot}</div>}
                          {(prepNotesBrief.talkingPoints||[]).length>0&&(
                            <div style={{marginTop:8}}>
                              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--tact)",marginBottom:5}}>🎯 Talking Points</div>
                              {(prepNotesBrief.talkingPoints||[]).map((tp,i)=>(
                                <div key={i} style={{fontSize:11,color:"var(--text)",lineHeight:1.5,marginBottom:3}}>
                                  <span style={{color:"var(--tact)",fontWeight:700,marginRight:5}}>{i+1}.</span>{tp.replace(/^\d+\.\s*/,"")}
                                </div>
                              ))}
                            </div>
                          )}
                          {prepNotesBrief.generatedAt&&<div style={{fontSize:9,color:"var(--text3)",marginTop:8}}>Generated {new Date(prepNotesBrief.generatedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>}
                        </>
                      )}
                    </div>
                  )}
                  {!prepNotesLoading&&!submittedAt&&(
                    <div style={{padding:"14px 0",fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>
                      {isPast?"No pre-meeting form response found for this past meeting.":"No pre-meeting form response yet."}{prepNotesLinkInfo?.sentAt?" Form was sent — awaiting response.":""}
                    </div>
                  )}
                  {!prepNotesLoading&&submittedAt&&(()=>{
                    // Group all answered questions by section — comments (textareas) go inside their card
                    const sectionMap={};
                    questions.forEach(q=>{
                      const ans=answers[q.id];
                      if(ans===undefined||ans===null||ans==="")return;
                      const sec=q.section||"General";
                      if(!sectionMap[sec])sectionMap[sec]=[];
                      sectionMap[sec].push({q,ans});
                    });
                    const sectionEntries=Object.entries(sectionMap);
                    return(
                      <>
                        <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--strat)",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                          ✅ Pre-Meeting Form Response
                          <span style={{fontSize:9,fontWeight:400,color:"var(--text3)",letterSpacing:0,textTransform:"none"}}>
                            Submitted {new Date(submittedAt).toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                          </span>
                        </div>
                        {sectionEntries.length===0&&(
                          <div style={{background:"rgba(230,126,34,0.06)",border:"1px solid rgba(230,126,34,0.2)",borderRadius:8,padding:"12px 14px",marginBottom:12,fontSize:11,color:"var(--text2)",lineHeight:1.5}}>
                            <div style={{fontWeight:600,color:"var(--cal)",marginBottom:4}}>⚠ Form submitted with no answers</div>
                            The client clicked submit but didn't fill in any questions. You may want to follow up before the meeting to gather their feedback.
                          </div>
                        )}
                        {sectionEntries.length>0&&(
                          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:12}}>
                            {sectionEntries.map(([secName,items])=>(
                              <div key={secName} style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border)"}}>
                                <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--strat)",marginBottom:7,borderBottom:"1px solid var(--border)",paddingBottom:5}}>{secName}</div>
                                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                  {items.map(({q,ans})=>{
                                    const isTextarea=q.type==="textarea"||q.type==="text";
                                    const isRating=q.type==="rating";
                                    const color=isRating?(ans>=4?"var(--ops)":ans>=3?"var(--cal)":"var(--danger)"):ans==="yes"?"var(--ops)":ans==="no"?"var(--danger)":"var(--text)";
                                    if(isTextarea){
                                      return(
                                        <div key={q.id} style={{marginTop:4,paddingTop:6,borderTop:"1px solid var(--border)"}}>
                                          <div style={{fontSize:9,color:"var(--text3)",lineHeight:1.3,marginBottom:3}}>{q.label}</div>
                                          <div style={{fontSize:11,color:"var(--text)",lineHeight:1.5,whiteSpace:"pre-wrap"}}>{String(ans)}</div>
                                        </div>
                                      );
                                    }
                                    return(
                                      <div key={q.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6}}>
                                        <div style={{fontSize:10,color:"var(--text2)",lineHeight:1.3,flex:1}}>{q.label}</div>
                                        <div style={{flexShrink:0,fontWeight:700,color,fontSize:isRating?13:11}}>
                                          {isRating?<>{ans}<span style={{fontSize:8,color:"var(--text3)",fontWeight:400}}>/{q.scale||5}</span></>:ans==="yes"?"✓ Yes":ans==="no"?"✗ No":String(ans)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            };
            return(
              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
                <div style={{padding:"13px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)",letterSpacing:"-0.3px",marginBottom:3}}>Prep Notes</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>Past prep sessions · upcoming meetings · click to review</div>
                  </div>
                  <div style={{fontSize:10,color:"var(--text3)",textAlign:"right"}}>
                    {urgentPreps.length>0&&<div style={{fontWeight:600,color:"var(--warn)"}}>⚠ {urgentPreps.length} prep{urgentPreps.length!==1?"s":""} due within 7 days</div>}
                    {discussPending.filter(d=>d.unread).length>0&&(
                      <div style={{fontWeight:600,color:"#0b6a8a"}}>
                        🔔 {discussPending.filter(d=>d.unread).length} client{discussPending.filter(d=>d.unread).length!==1?"s":""} flagged report sections
                      </div>)}
                    <div>{pastPreps.length} past · {allFuturePreps.length} upcoming</div>
                  </div>
                </div>
                <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                  {/* LEFT: accordion list */}
                  <div style={{width:310,borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden",background:"white",flexShrink:0}}>
                    <div className="scroll">

                      {/* ── UPCOMING ACCORDION ── */}
                      <div>
                        <div onClick={()=>setPrepNotesAccordion(a=>({...a,upcoming:!a.upcoming}))}
                          style={{padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:"var(--s2)",userSelect:"none"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--s3)"}
                          onMouseLeave={e=>e.currentTarget.style.background="var(--s2)"}>
                          <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:urgentPreps.length>0?"var(--warn)":"var(--text2)"}}>
                            {urgentPreps.length>0?"⚠ ":"📅 "}Upcoming Prep <span style={{fontWeight:400,color:"var(--text3)"}}>{allFuturePreps.length}</span>
                          </span>
                          <span style={{fontSize:9,color:"var(--text3)",transform:prepNotesAccordion.upcoming?"rotate(180deg)":"none",display:"inline-block",transition:"transform 0.18s"}}>▼</span>
                        </div>
                        {prepNotesAccordion.upcoming&&(
                          allFuturePreps.length===0?(
                            <div style={{padding:"14px",fontSize:11,color:"var(--text3)",fontStyle:"italic",textAlign:"center"}}>No upcoming monthly meetings found.</div>
                          ):(
                            <>
                              {urgentPreps.length>0&&<div style={{padding:"5px 14px",fontSize:9,color:"var(--warn)",fontWeight:700,letterSpacing:"0.06em",background:"rgba(230,126,34,0.06)",borderBottom:"1px solid var(--border)"}}>DUE WITHIN 7 DAYS</div>}
                              {urgentPreps.map(p=>{
                                const isSelected=sp?.id===p.id;
                                const urgencyColor=p.daysUntil<=2?"var(--danger)":p.daysUntil<=5?"var(--warn)":"var(--cal)";
                                return(
                                  <div key={p.id} onClick={()=>loadPrepNoteDataForPrep(p)}
                                    style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",cursor:"pointer",
                                      background:isSelected?"#eef6fa":"rgba(230,126,34,0.03)",
                                      borderLeft:isSelected?"3px solid var(--strat)":`3px solid ${urgencyColor}`,
                                      transition:"background 0.1s"}}>
                                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6,marginBottom:3}}>
                                      <div style={{fontSize:12,fontWeight:600,color:"var(--text)",lineHeight:1.3,flex:1}}>
                                        {p.client}
                                        {(()=>{const d=discussForPrep(p);if(!d)return null;return(
                                          <span title={`Client flagged: ${d.sections.join(", ")}`}
                                            style={{display:"inline-block",marginLeft:6,fontSize:8.5,fontWeight:700,padding:"1px 5px",borderRadius:3,
                                              background:d.unread?"#16b0e8":"#e3f2f9",color:d.unread?"#fff":"#0b6a8a",whiteSpace:"nowrap"}}>
                                            🔔 {d.count}
                                          </span>);})()}
                                      </div>
                                      <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:p.daysUntil<=2?"var(--danger-d)":"var(--warn-d)",color:urgencyColor,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>
                                        {p.daysUntil===0?"TODAY":`${p.daysUntil}d`}
                                      </span>
                                    </div>
                                    <div style={{fontSize:10,color:"var(--text3)",marginBottom:2}}>{p.meetingTitle}</div>
                                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                                      <span style={{fontSize:10,color:"var(--text3)"}}>📅 {new Date(p.meetingDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})}</span>
                                      {!hasPrepBlockToday(p)&&<button className="btn btn-T" style={{fontSize:9,padding:"1px 7px",marginLeft:"auto"}} onClick={e=>{e.stopPropagation();const nb={id:Date.now(),title:`Prep: ${p.client} — ${p.meetingTitle}`,layer:"T",start:"09:00",dur:30,done:false,prepFor:p.iCalUID||p.id,todoId:null,type:"meeting-prep",sourceType:"meeting"};setBlocks(bs=>{const next=[...bs,nb];saveSnapshot({blocks:next});return next;});}}>+ Plan</button>}
                                      {hasPrepBlockToday(p)&&<span style={{fontSize:9,color:"var(--ops)",fontWeight:600,marginLeft:"auto"}}>✓ Planned</span>}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )
                        )}
                      </div>

                      {/* ── PAST ACCORDION ── */}
                      <div style={{borderBottom:"1px solid var(--border)"}}>
                        <div onClick={()=>setPrepNotesAccordion(a=>({...a,past:!a.past}))}
                          style={{padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:"var(--s2)",userSelect:"none"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--s3)"}
                          onMouseLeave={e=>e.currentTarget.style.background="var(--s2)"}>
                          <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--text2)"}}>
                            ✅ Past Prep Sessions <span style={{fontWeight:400,color:"var(--text3)"}}>{pastPreps.length}</span>
                          </span>
                          <span style={{fontSize:9,color:"var(--text3)",transform:prepNotesAccordion.past?"rotate(180deg)":"none",display:"inline-block",transition:"transform 0.18s"}}>▼</span>
                        </div>
                        {prepNotesAccordion.past&&(
                          pastPreps.length===0?(
                            <div style={{padding:"14px",fontSize:11,color:"var(--text3)",fontStyle:"italic",textAlign:"center"}}>No past monthly meetings in the last 6 months.</div>
                          ):(
                            pastPreps.map(p=>{
                              const isSelected=sp?.id===p.id;
                              return(
                                <div key={p.id} onClick={()=>loadPrepNoteDataForPrep(p)}
                                  style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",cursor:"pointer",
                                    background:isSelected?"#eef6fa":"white",
                                    borderLeft:isSelected?"3px solid var(--strat)":"3px solid var(--ops)",
                                    transition:"background 0.1s"}}>
                                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6,marginBottom:3}}>
                                    <div style={{fontSize:12,fontWeight:500,color:"var(--text)",lineHeight:1.3,flex:1}}>{p.client}</div>
                                    <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:"var(--ops-d)",color:"var(--ops)",fontWeight:600,flexShrink:0,whiteSpace:"nowrap"}}>{p.daysAgo}d ago</span>
                                  </div>
                                  <div style={{fontSize:10,color:"var(--text3)",marginBottom:2}}>{p.meetingTitle}</div>
                                  <div style={{fontSize:10,color:"var(--text3)"}}>📅 {new Date(p.meetingDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})}</div>
                                </div>
                              );
                            })
                          )
                        )}
                      </div>

                    </div>
                  </div>
                  {/* RIGHT: shared detail panel */}
                  <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                    <PanelErrorBoundary>
                      {renderDetail()}
                    </PanelErrorBoundary>
                  </div>
                </div>
              </div>
            );
          })()}




          {/* ── MEETING NOTES (POST-MEETING) TAB ── */}
          {tab==="meetingnotes"&&(()=>{
            const todayStr=todayISO();
            const cutoff=pastWindowStartISO();

            // Same gate as Prep Notes — wait for both calendar and AT company
            // cache so meeting cards render with resolved client names from the
            // first paint, not the attendee display name that flips a moment later.
            const dataReady=meetings.length>0&&atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>
                  {meetings.length===0?"Loading calendar data…":"Loading company data…"}
                  <br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span>
                </div>
              </div>
            );

            // Helper: resolve client name from a calendar meeting (same logic as Prep Notes)
            const resolveClient=(m)=>{
              let client=m.client||"";
              // 1. Exact/partial match on existing client string
              if(client){
                const ex=atCompanies.find(c=>c.companyName.toLowerCase()===client.toLowerCase());
                const pt=!ex&&atCompanies.find(c=>{const n=c.companyName.toLowerCase(),l=client.toLowerCase();return n.includes(l)||l.includes(n);});
                if(ex)return ex.companyName;
                if(pt)return pt.companyName;
              }
              // 2. Company name in event title
              if(!client){const tm=atCompanies.find(c=>m.title?.toLowerCase().includes(c.companyName.toLowerCase()));if(tm)return tm.companyName;}
              // 3. Attendee email domains → clientDomainOverrides
              for(const att of (m.attendees||[])){
                const email=att.emailAddress?.address||att.email||"";
                const domain=email.split("@")[1]||"";
                if(!domain||domain==="datatech.net.au")continue;
                const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co)return co.companyName;}
              }
              return client;
            };

            // Title filter: Meeting Notes only ever shows monthly client catchups,
            // not internal/ad-hoc meetings. Match case-insensitively and as a
            // substring so variants like "KIH — Monthly Tech Catchup" still
            // qualify. Applied to both past and future lists below.
            const isMonthlyTechCatchup=(m)=>{
              const t=(m.title||m.subject||"").toLowerCase();
              return t.includes("monthly tech catchup");
            };

            // Past: calendar meetings from the last 6 months (see pastWindowStartISO)
            const pastMeetings=[...meetings]
              .filter(m=>{
                if(!isMonthlyTechCatchup(m)) return false;
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                return ds&&ds<todayStr&&ds>=cutoff;
              })
              .map(m=>{
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                const daysAgo=Math.ceil((new Date(todayStr+"T00:00:00")-new Date(ds+"T12:00:00"))/(1000*60*60*24));
                return{...m,_dateStr:ds,_daysAgo:daysAgo,client:resolveClient(m)};
              })
              .sort((a,b)=>b._dateStr.localeCompare(a._dateStr));

            // Upcoming: calendar meetings from today forward
            const futureMeetings=[...meetings]
              .filter(m=>{
                if(!isMonthlyTechCatchup(m)) return false;
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                return ds&&ds>=todayStr;
              })
              .map(m=>{
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                const daysAgo=Math.round((new Date(todayStr+"T00:00:00")-new Date(ds+"T00:00:00"))/(1000*60*60*24));
                return{...m,_dateStr:ds,_daysAgo:daysAgo,client:resolveClient(m)};
              })
              .sort((a,b)=>a._dateStr.localeCompare(b._dateStr));
            const sm=selectedMeetingBlock;
            const brief=meetingNotesBrief;
            const renderBullets=(items=[])=>items.map((item,i)=>{
              const isNone=/no data available/i.test(item);
              const isWarn=item.startsWith("⚠")||/offline|alert|critical|overdue/i.test(item);
              const isOk=item.startsWith("✓");
              return <div key={i} className={`mip-bullet${isNone?" mip-none":isWarn?" mip-warn":isOk?" mip-ok":""}`}>{item.replace(/^[✓✗⚠]\s*/,"")}</div>;
            });
            const renderCard=(m)=>{
              const isSelected=!!(sm?.id&&sm.id===m.id);
              const isPast=m._daysAgo>0;
              const isToday=m._daysAgo===0;
              const badgeLabel=isPast?`${m._daysAgo}d ago`:isToday?"Today":`in ${-m._daysAgo}d`;
              const badgeBg=isPast?"var(--ops-d)":isToday?"var(--strat-d)":"var(--cal-d)";
              const badgeColor=isPast?"var(--ops)":isToday?"var(--strat)":"var(--cal)";
              const borderColor=isPast?"var(--ops)":isToday?"var(--strat)":"var(--cal)";
              return(
                <div key={m.id||m._dateStr+m.title} onClick={()=>loadMeetingNotesBrief(m)}
                  style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",cursor:"pointer",
                    background:isSelected?"#eef6fa":"white",
                    borderLeft:isSelected?"3px solid var(--tact)":`3px solid ${borderColor}`,
                    transition:"background 0.1s"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6,marginBottom:3}}>
                    <div style={{fontSize:12,fontWeight:500,color:"var(--text)",lineHeight:1.3,flex:1}}>{m.client||m.title}</div>
                    <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:badgeBg,color:badgeColor,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>{badgeLabel}</span>
                  </div>
                  <div style={{fontSize:10,color:"var(--text3)",marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.title}</div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{fontSize:10,color:"var(--text3)"}}>📅 {new Date(m._dateStr+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})}</span>
                    {m.startTime&&<span style={{fontSize:10,color:"var(--text3)"}}>⏰ {m.startTime}</span>}
                  </div>
                </div>
              );
            };
            return(
              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
                <div style={{padding:"13px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)",letterSpacing:"-0.3px",marginBottom:3}}>Meeting Notes</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>Past · today · upcoming meetings · MIP · action items</div>
                  </div>
                  <div style={{fontSize:10,color:"var(--text3)",textAlign:"right"}}>
                    <div>{pastMeetings.length} past · {futureMeetings.length} today/upcoming</div>
                  </div>
                </div>
                <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                  <div style={{width:310,borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden",background:"white",flexShrink:0}}>
                    <div className="scroll">
                      {pastMeetings.length===0&&futureMeetings.length===0&&(
                        <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.8}}>
                          <div style={{fontSize:22,marginBottom:6}}>📅</div>
                          No meetings found.<br/>Connect your calendar in Settings.
                        </div>
                      )}
                      {/* ── TODAY & UPCOMING accordion — first and open by default ── */}
                      {futureMeetings.length>0&&(
                        <div>
                          <div onClick={()=>setMeetingNotesAccordion(a=>({...a,upcoming:!a.upcoming}))}
                            style={{padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:"var(--s2)",userSelect:"none"}}
                            onMouseEnter={e=>e.currentTarget.style.background="var(--s3)"}
                            onMouseLeave={e=>e.currentTarget.style.background="var(--s2)"}>
                            <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--strat)"}}>
                              📅 Today & Upcoming <span style={{fontWeight:400,color:"var(--text3)"}}>{futureMeetings.length}</span>
                            </span>
                            <span style={{fontSize:9,color:"var(--text3)",transform:meetingNotesAccordion.upcoming?"rotate(180deg)":"none",display:"inline-block",transition:"transform 0.18s"}}>▼</span>
                          </div>
                          {meetingNotesAccordion.upcoming&&futureMeetings.map(m=>renderCard(m))}
                        </div>
                      )}
                      {/* ── PAST MEETINGS accordion — second and closed by default ── */}
                      <div style={{borderBottom:"1px solid var(--border)"}}>
                        <div onClick={()=>setMeetingNotesAccordion(a=>({...a,past:!a.past}))}
                          style={{padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",background:"var(--s2)",userSelect:"none"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--s3)"}
                          onMouseLeave={e=>e.currentTarget.style.background="var(--s2)"}>
                          <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--text2)"}}>
                            ✅ Past Meetings <span style={{fontWeight:400,color:"var(--text3)"}}>{pastMeetings.length}</span>
                          </span>
                          <span style={{fontSize:9,color:"var(--text3)",transform:meetingNotesAccordion.past?"rotate(180deg)":"none",display:"inline-block",transition:"transform 0.18s"}}>▼</span>
                        </div>
                        {meetingNotesAccordion.past&&(
                          pastMeetings.length===0
                            ?<div style={{padding:"14px",fontSize:11,color:"var(--text3)",fontStyle:"italic",textAlign:"center"}}>No past meetings in the last 6 months.</div>
                            :pastMeetings.map(m=>renderCard(m))
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                    <PanelErrorBoundary>
                    {!sm&&(
                      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:"var(--text3)",fontSize:12,lineHeight:1.8,padding:40,textAlign:"center"}}>
                        <div style={{fontSize:32,marginBottom:12}}>🗒️</div>
                        Select a past meeting on the left<br/>to review the Meeting Intelligence Pack,<br/>action items, and meeting page.
                      </div>
                    )}
                    {sm&&meetingNotesBriefLoading&&(
                      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40}}>
                        <div style={{width:36,height:36,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--tact)",animation:"lspin 0.7s linear infinite"}}/>
                        <div style={{fontSize:12,color:"var(--text3)"}}>Loading meeting data…</div>
                      </div>
                    )}
                    {sm&&!meetingNotesBriefLoading&&(
                      <div className="scroll" style={{padding:"18px 22px"}}>
                        {/* Header */}
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:4,gap:12}}>
                          <div style={{fontWeight:700,fontSize:15,color:"var(--text)"}}>{sm.client||sm.title}</div>
                          <span style={{fontSize:10,padding:"3px 10px",borderRadius:12,background:"var(--ops-d)",color:"var(--ops)",fontWeight:700,flexShrink:0}}>{sm._daysAgo===0?"Today":`${sm._daysAgo}d ago`}</span>
                        </div>
                        <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>{sm.title}</div>
                        <div style={{fontSize:11,color:"var(--text3)",marginBottom:14,display:"flex",gap:12,flexWrap:"wrap"}}>
                          <span>📅 {new Date(sm._dateStr+"T12:00:00").toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</span>
                          {sm.startTime&&<span>⏰ {sm.startTime}</span>}
                          {sm.client&&<span>🏢 {sm.client}</span>}
                          {(sm.attendees?.length||0)>0&&<span>👥 {sm.attendees.length} attendees</span>}
                        </div>

                        {/* ── Pre-Meeting Form — 3-column card grid at TOP ── */}
                        {meetingNotesPrepResponse?.submittedAt?(()=>{
                          const resp=meetingNotesPrepResponse;
                          const questions=(resp.questions||[]).filter(q=>{const a=resp.answers?.[q.id];return a!==undefined&&a!==null&&a!=="";});
                          // If submitted but every answer is empty, show a clear "submitted empty"
                          // banner instead of an empty-looking grid. This is what happens when a
                          // client opens the form but submits without filling anything in.
                          if(questions.length===0){
                            return(
                              <div style={{background:"rgba(230,126,34,0.06)",border:"1px solid rgba(230,126,34,0.2)",borderRadius:8,padding:"12px 14px",marginBottom:14,fontSize:11,color:"var(--text2)",lineHeight:1.5}}>
                                <div style={{fontWeight:600,color:"var(--cal)",marginBottom:4}}>⚠ Pre-meeting form submitted empty</div>
                                The client opened the form and submitted on <strong>{new Date(resp.submittedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</strong>, but no answers were filled in. Consider following up before the meeting if needed.
                              </div>
                            );
                          }
                          // Group into sections by question.section or split into thirds
                          const grouped={};
                          questions.forEach(q=>{const sec=q.section||q.category||"General";if(!grouped[sec])grouped[sec]=[];grouped[sec].push(q);});
                          const sections=Object.entries(grouped);
                          return(
                            <div style={{marginBottom:16}}>
                              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--strat)",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                                ✅ Pre-Meeting Form
                                <span style={{fontSize:9,fontWeight:400,color:"var(--text3)",letterSpacing:0,textTransform:"none"}}>Submitted {new Date(resp.submittedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                                {sections.map(([sec,qs])=>(
                                  <div key={sec} style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border)"}}>
                                    <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--strat)",marginBottom:7,borderBottom:"1px solid var(--border)",paddingBottom:5}}>{sec}</div>
                                    {qs.map(q=>{
                                      const ans=resp.answers[q.id];
                                      const isRating=q.type==="rating";
                                      const color=isRating?(ans>=4?"var(--ops)":ans>=3?"var(--cal)":"var(--danger)"):ans==="yes"?"var(--ops)":ans==="no"?"var(--danger)":"var(--text)";
                                      return(
                                        <div key={q.id} style={{marginBottom:6}}>
                                          <div style={{fontSize:9,color:"var(--text3)",lineHeight:1.3,marginBottom:1}}>{q.label}</div>
                                          <div style={{fontSize:12,fontWeight:600,color}}>{isRating?`${ans}/${q.scale||5}`:ans==="yes"?"✓ Yes":ans==="no"?"✗ No":String(ans)}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })():(
                          // Distinguish three "no answers visible" states so we
                          // can tell whether to chase the client or send the form.
                          (()=>{
                            const wasFormSent=!!meetingNotesLinkInfo?.sentAt;
                            const recipient=meetingNotesLinkInfo?.contactEmail||"";
                            const sentAtDisplay=wasFormSent
                              ?new Date(meetingNotesLinkInfo.sentAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})
                              :"";
                            if(wasFormSent){
                              return(
                                <div style={{background:"rgba(230,126,34,0.06)",border:"1px solid rgba(230,126,34,0.2)",borderRadius:8,padding:"12px 14px",marginBottom:14,fontSize:11,color:"var(--text2)",lineHeight:1.5}}>
                                  <div style={{fontWeight:600,color:"var(--cal)",marginBottom:4}}>📨 Form sent · awaiting response</div>
                                  Sent to <strong>{recipient||"the client"}</strong> on <strong>{sentAtDisplay}</strong> but no response received yet. Consider following up before the meeting.
                                </div>
                              );
                            }
                            return(
                              <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 14px",marginBottom:14,border:"1px solid var(--border)",fontSize:11,color:"var(--text3)",display:"flex",alignItems:"center",gap:10}}>
                                <span>📋</span>
                                <span>No pre-meeting form has been sent for this meeting.</span>
                              </div>
                            );
                          })()
                        )}

                        {/* ── Meeting page link ── */}
                        {meetingNotesPageUrl&&(
                          <div className="mip-meeting-url" style={{marginBottom:14}}>
                            <span style={{fontSize:11,fontWeight:700,color:"var(--tact)"}}>✓ Meeting page committed</span>
                            <a href={meetingNotesPageUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"var(--strat)",fontWeight:600,textDecoration:"none",marginLeft:4}}>Open Meeting Page →</a>
                            <button className="btn" style={{fontSize:9,padding:"2px 7px",marginLeft:"auto"}} onClick={()=>navigator.clipboard.writeText(meetingNotesPageUrl)}>📋 Copy</button>
                          </div>
                        )}

                        {/* ── MIP ── */}
                        {!brief&&(
                          <div style={{background:"var(--s2)",borderRadius:8,padding:"16px",marginBottom:14,border:"1px solid var(--border)"}}>
                            <div style={{fontWeight:600,fontSize:12,color:"var(--text)",marginBottom:6}}>No Meeting Intelligence Pack found.</div>
                            {meetingNotesGenerating?(
                              <div style={{display:"flex",gap:8,alignItems:"center",color:"var(--tact)",fontSize:11,padding:"8px 0"}}>
                                <div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/> Generating MIP…
                              </div>
                            ):(
                              <>
                                <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>Generate one now using the pre-meeting form data and available client context.</div>
                                <button className="btn btn-T" style={{fontSize:11,padding:"6px 14px"}}
                                  onClick={async()=>{
                                    // Find company — try client name first, then attendee email domains
                                    let co=atCompanies.find(c=>normCo(c.companyName)===normCo(sm.client||"")||c.companyName.toLowerCase()===sm.client?.toLowerCase());
                                    if(!co&&sm.attendees?.length){
                                      for(const att of sm.attendees){
                                        const email=att.emailAddress?.address||att.email||"";
                                        const domain=email.split("@")[1]||"";
                                        if(!domain||domain==="datatech.net.au")continue;
                                        const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                                        if(entry){const found=atCompanies.find(c=>String(c.id)===entry[0]);if(found){co=found;break;}}
                                      }
                                    }
                                    if(!co){alert("Cannot find company for this meeting.\nClient name: '"+(sm.client||"(empty)")+"'\nPlease set the domain mapping in Settings → Client Register.");return;}
                                    setMeetingNotesGenerating(true);
                                    const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                                    // Use iCalUId (Graph API field) or iCalUID or date as meeting key
                                    const meetingKey=sm.iCalUID||sm.iCalUId||sm._dateStr||"";
                                    // Build context from available data
                                    const _sites=Array.isArray(dattoData?.sites)?dattoData.sites:[];
                                    const _alerts=Array.isArray(dattoData?.alerts)?dattoData.alerts:[];
                                    const coWords=co.companyName.toLowerCase().split(" ").filter(w=>w.length>3);
                                    const clientSites=_sites.filter(s=>coWords.some(w=>(s.name||"").toLowerCase().includes(w)));
                                    const clientAlerts=_alerts.filter(a=>clientSites.some(s=>a.alertSourceInfo?.siteUid===s.uid));
                                    const dattoSummary=clientSites.length>0?{siteCount:clientSites.length,deviceCount:clientSites.reduce((n,s)=>n+(s.deviceCount||0),0),offlineCount:clientSites.reduce((n,s)=>n+(s.offlineCount||0),0),alertCount:clientAlerts.length}:null;
                                    const prepFormData=meetingNotesPrepResponse?{questions:meetingNotesPrepResponse.questions||[],answers:meetingNotesPrepResponse.answers||{},submittedAt:meetingNotesPrepResponse.submittedAt}:null;
                                    const clientSentiment=(preloadedSentiment?.results||[]).find(r=>String(r.companyId)===String(co.id));
                                    const sentimentData=clientSentiment?{score:clientSentiment.score,sentiment:clientSentiment.sentiment,summary:clientSentiment.summary}:null;
                                    // Carry-forward of action items from prior meetings (last 90 days)
                                    // plus the previous meeting date — drives the Completed/Outstanding cards.
                                    const priorActionItems=gatherPriorActionItems(co.id,co.companyName);
                                    const previousMeetingDate=findPreviousMeetingDate(co.companyName);
                                    try{
                                      const r=await fetch(`${_ab}/api/ai/meeting-brief`,{
                                        method:"POST",
                                        headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                        body:JSON.stringify({
                                          companyId:String(co.id),companyName:co.companyName,
                                          meetingDate:meetingKey,
                                          meetingTitle:sm.title,
                                          prepFormData,dattoSummary,sentimentData,
                                          priorActionItems,previousMeetingDate,
                                        })
                                      });
                                      const d=await r.json();
                                      if(r.ok&&d.snapshot){
                                        const briefWithMeta={...d,companyName:co.companyName,meetingTitle:sm.title,meetingDate:sm._dateStr||"",generatedAt:new Date().toISOString()};
                                        // Save to blob first
                                        const mk=encodeURIComponent(meetingKey);
                                        if(mk){
                                          const saveR=await fetch(`${_ab}/api/ai/meeting-brief/${co.id}?meeting=${mk}`,{
                                            method:"PUT",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                            body:JSON.stringify(briefWithMeta)
                                          });
                                          if(!saveR.ok){console.warn("MIP save failed:",saveR.status);}
                                        }
                                        // Now set in state — persisted to blob above
                                        setMeetingNotesBrief(briefWithMeta);
                                      } else {
                                        alert("MIP generation failed: "+(d.error||"Unknown error"));
                                      }
                                    }catch(e){alert("MIP generation error: "+e.message);}
                                    finally{setMeetingNotesGenerating(false);}
                                  }}>
                                  ◆ Generate MIP
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {brief&&(
                          <>
                            {/* Action row: timestamp + Re-Run MIP button */}
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:10,flexWrap:"wrap"}}>
                              <div style={{fontSize:10,color:"var(--text3)"}}>
                                {brief.generatedAt?`Last generated ${new Date(brief.generatedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}`:""}
                                {brief.committedAt?` · committed ${new Date(brief.committedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}`:""}
                              </div>
                              <button className="btn" style={{fontSize:10,padding:"5px 12px"}}
                                disabled={meetingNotesGenerating}
                                onClick={async()=>{
                                  if(meetingNotesGenerating)return;
                                  if(!window.confirm("Regenerate the MIP for this meeting?\n\nThe existing brief will be replaced with a fresh one built from the latest data — including any new outstanding action items, completed tickets, and Datto/sentiment changes."))return;
                                  // Reuse the same generation pipeline by calling the existing Generate button's handler.
                                  // We resolve the company the same way and POST with the same body shape (now including
                                  // priorActionItems and previousMeetingDate so the carry-forward cards populate).
                                  let co=atCompanies.find(c=>normCo(c.companyName)===normCo(sm.client||"")||c.companyName.toLowerCase()===sm.client?.toLowerCase());
                                  if(!co&&sm.attendees?.length){
                                    for(const att of sm.attendees){
                                      const email=att.emailAddress?.address||att.email||"";
                                      const domain=email.split("@")[1]||"";
                                      if(!domain||domain==="datatech.net.au")continue;
                                      const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                                      if(entry){const found=atCompanies.find(c=>String(c.id)===entry[0]);if(found){co=found;break;}}
                                    }
                                  }
                                  if(!co){alert("Cannot find company for this meeting.");return;}
                                  setMeetingNotesGenerating(true);
                                  const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                                  const meetingKey=sm.iCalUID||sm.iCalUId||sm._dateStr||"";
                                  const _sites=Array.isArray(dattoData?.sites)?dattoData.sites:[];
                                  const _alerts=Array.isArray(dattoData?.alerts)?dattoData.alerts:[];
                                  const coWords=co.companyName.toLowerCase().split(" ").filter(w=>w.length>3);
                                  const clientSites=_sites.filter(s=>coWords.some(w=>(s.name||"").toLowerCase().includes(w)));
                                  const clientAlerts=_alerts.filter(a=>clientSites.some(s=>a.alertSourceInfo?.siteUid===s.uid));
                                  const dattoSummary=clientSites.length>0?{siteCount:clientSites.length,deviceCount:clientSites.reduce((n,s)=>n+(s.deviceCount||0),0),offlineCount:clientSites.reduce((n,s)=>n+(s.offlineCount||0),0),alertCount:clientAlerts.length}:null;
                                  const prepFormData=meetingNotesPrepResponse?{questions:meetingNotesPrepResponse.questions||[],answers:meetingNotesPrepResponse.answers||{},submittedAt:meetingNotesPrepResponse.submittedAt}:null;
                                  const clientSentiment=(preloadedSentiment?.results||[]).find(r=>String(r.companyId)===String(co.id));
                                  const sentimentData=clientSentiment?{score:clientSentiment.score,sentiment:clientSentiment.sentiment,summary:clientSentiment.summary}:null;
                                  const priorActionItems=gatherPriorActionItems(co.id,co.companyName);
                                  const previousMeetingDate=findPreviousMeetingDate(co.companyName);
                                  try{
                                    const r=await fetch(`${_ab}/api/ai/meeting-brief`,{
                                      method:"POST",
                                      headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                      body:JSON.stringify({
                                        companyId:String(co.id),companyName:co.companyName,
                                        meetingDate:meetingKey,meetingTitle:sm.title,
                                        prepFormData,dattoSummary,sentimentData,
                                        priorActionItems,previousMeetingDate,
                                      })
                                    });
                                    const d=await r.json();
                                    if(r.ok&&d.snapshot){
                                      // Preserve any existing committed URL / committedAt across the regenerate
                                      const briefWithMeta={...d,companyName:co.companyName,meetingTitle:sm.title,meetingDate:sm._dateStr||"",generatedAt:new Date().toISOString(),
                                        ...(brief.committedMeetingUrl?{committedMeetingUrl:brief.committedMeetingUrl}:{}),
                                        ...(brief.committedAt?{committedAt:brief.committedAt}:{})};
                                      const mk=encodeURIComponent(meetingKey);
                                      if(mk){
                                        await fetch(`${_ab}/api/ai/meeting-brief/${co.id}?meeting=${mk}`,{
                                          method:"PUT",headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                          body:JSON.stringify(briefWithMeta)
                                        }).catch(()=>{});
                                      }
                                      setMeetingNotesBrief(briefWithMeta);
                                    } else {
                                      alert("MIP regenerate failed: "+(d.error||"Unknown error"));
                                    }
                                  }catch(e){alert("MIP regenerate error: "+e.message);}
                                  finally{setMeetingNotesGenerating(false);}
                                }}>
                                {meetingNotesGenerating?"Regenerating…":"↻ Re-Run MIP"}
                              </button>
                            </div>
                            {/* ── Commit / Open Meeting Page row (same flow as Focus tab) ── */}
                            {(()=>{
                              // Resolve company (same fallback chain as Re-Run)
                              let co=atCompanies.find(c=>normCo(c.companyName)===normCo(sm.client||"")||c.companyName.toLowerCase()===sm.client?.toLowerCase());
                              if(!co&&sm.attendees?.length){
                                for(const att of sm.attendees){
                                  const email=att.emailAddress?.address||att.email||"";
                                  const domain=email.split("@")[1]||"";
                                  if(!domain||domain==="datatech.net.au")continue;
                                  const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                                  if(entry){const found=atCompanies.find(c=>String(c.id)===entry[0]);if(found){co=found;break;}}
                                }
                              }
                              if(!co)return null;
                              const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                              const commitNow=async()=>{
                                if(meetingNotesCommitting)return;
                                // Resolve the meeting key (event id, iCalUID, or date — same union as the loader uses).
                                // Commit always picks the FIRST plausible key so the write hits the same record the brief was loaded from.
                                const resolvedKey=sm.iCalUID||sm.id||sm._dateStr||sm.localDateStr||"";
                                if(!resolvedKey){alert("Cannot commit — meeting key not resolved.");return;}
                                setMeetingNotesCommitting(true);
                                let briefBody;
                                try{briefBody=JSON.stringify({brief});}
                                catch{
                                  // Strip non-serialisable fields if any sneaked in
                                  const safeBrief={
                                    snapshot:brief?.snapshot||"",
                                    openIssues:brief?.openIssues||[],
                                    clientFeedback:brief?.clientFeedback||[],
                                    infrastructure:brief?.infrastructure||[],
                                    outstandingActions:brief?.outstandingActions||[],
                                    sentiment:brief?.sentiment||null,
                                    talkingPoints:brief?.talkingPoints||[],
                                    generatedAt:brief?.generatedAt||"",
                                    _factualCompletedSinceLastMeeting:brief?._factualCompletedSinceLastMeeting||[],
                                    _factualOutstanding:brief?._factualOutstanding||[],
                                    _factualClientDiscuss:brief?._factualClientDiscuss||null,
                                  };
                                  briefBody=JSON.stringify({brief:safeBrief});
                                }
                                try{
                                  const r=await fetch(`${_ab}/api/ai/meeting-brief/${co.id}/commit?meeting=${encodeURIComponent(resolvedKey)}`,{
                                    method:"POST",
                                    headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                    body:briefBody,
                                  });
                                  const raw=await r.text();let d={};try{d=JSON.parse(raw);}catch{}
                                  const safeUrl=typeof d.meetingUrl==="string"&&d.meetingUrl.startsWith("http")?d.meetingUrl:null;
                                  if(r.ok&&safeUrl){
                                    setMeetingNotesPageUrl(safeUrl);
                                    // Persist committedMeetingUrl + committedAt to the saved brief so the loader will surface them next time
                                    const updatedBrief={...brief,committedMeetingUrl:safeUrl,committedAt:d.committedAt||new Date().toISOString()};
                                    setMeetingNotesBrief(updatedBrief);
                                    await fetch(`${_ab}/api/ai/meeting-brief/${co.id}?meeting=${encodeURIComponent(resolvedKey)}`,{
                                      method:"PUT",
                                      headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                                      body:JSON.stringify(updatedBrief),
                                    }).catch(()=>{});
                                  } else {
                                    alert("Commit failed: "+(d.error||raw.slice(0,150)));
                                  }
                                }catch(e){alert("Commit failed: "+e.message);}
                                finally{setMeetingNotesCommitting(false);}
                              };
                              return(
                                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14,padding:"10px 0",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)"}}>
                                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                                    {meetingNotesPageUrl?(
                                      <button className="mip-commit-btn" style={{background:"linear-gradient(135deg,var(--ops),#219653)",cursor:"default",opacity:0.9}} disabled>
                                        ✓ Committed to Meeting
                                      </button>
                                    ):(
                                      <button className="mip-commit-btn" disabled={meetingNotesCommitting} onClick={commitNow}>
                                        {meetingNotesCommitting?"Committing…":"🚀 Commit to Meeting Page"}
                                      </button>
                                    )}
                                    {meetingNotesPageUrl&&!meetingNotesCommitting&&(
                                      <button className="btn" style={{fontSize:9,padding:"3px 9px"}} onClick={commitNow}>↻ Re-commit</button>
                                    )}
                                  </div>
                                  {meetingNotesPageUrl&&(
                                    <div className="mip-meeting-url">
                                      <span style={{fontSize:10,fontWeight:600,color:"var(--ops)"}}>✓ Meeting page ready</span>
                                      <a href={meetingNotesPageUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"var(--strat)",fontWeight:600,textDecoration:"none",marginLeft:4}}>Open Meeting Page →</a>
                                      <button className="btn" style={{fontSize:9,padding:"2px 7px",marginLeft:"auto"}} onClick={()=>navigator.clipboard.writeText(meetingNotesPageUrl)}>📋 Copy</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {brief.snapshot&&(
                              <div style={{marginBottom:14}}>
                                <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--tact)",marginBottom:6}}>◆ Meeting Brief</div>
                                <div className="mip-snapshot">{brief.snapshot}</div>
                              </div>
                            )}
                            <div className="mip-grid" style={{marginBottom:14}}>
                              {[{key:"openIssues",hdr:"🎫 Open Issues (Autotask)"},{key:"clientFeedback",hdr:"📋 Client Feedback (Form)"},{key:"infrastructure",hdr:"🖥️ Infrastructure (Datto)"}].map(({key,hdr})=>(
                                (brief[key]||[]).length>0&&(
                                  <div key={key} className="mip-card">
                                    <div className="mip-card-hdr">{hdr}</div>
                                    <div className="mip-card-body">{renderBullets(brief[key]||[])}</div>
                                  </div>
                                )
                              ))}
                              {/* What the CLIENT ticked on their monthly report. FIRST, deliberately:
                                  these are the only items on this brief the client themselves raised,
                                  and they are the ones they will notice if the meeting misses them.
                                  Rendered from _factualClientDiscuss — their own words, never the AI's
                                  paraphrase of them. */}
                              {(()=>{const cd=brief._factualClientDiscuss;
                                if(!cd||!(cd.items||[]).length)return null;
                                const ck=`${cd.companyId}-${cd.period}`;
                                if(discussCovered[ck])return null;
                                const monthLabel=(()=>{const[y,mo]=String(cd.period||"").split("-");
                                  return y&&mo?new Date(Number(y),Number(mo)-1,1).toLocaleDateString("en-AU",{month:"long",year:"numeric"}):cd.period;})();
                                return(
                                <div className="mip-card" style={{borderLeft:"3px solid #16b0e8"}}>
                                  <div className="mip-card-hdr" style={{display:"flex",alignItems:"center",gap:8}}>
                                    <span>🗣 Client asked to discuss</span>
                                    <span style={{fontSize:9,fontWeight:600,color:"#52514e"}}>from the {monthLabel} report</span>
                                    <button
                                      style={{marginLeft:"auto",fontSize:9.5,fontWeight:600,padding:"2px 8px",borderRadius:5,
                                        border:"1px solid var(--border,#d0d0d0)",background:"var(--card,#fff)",cursor:"pointer"}}
                                      title="Stop these appearing on future preps. Reversible — it does not delete what they asked."
                                      onClick={()=>{
                                        markDiscussAddressed(cd.companyId,cd.period,true)
                                          .then(()=>setDiscussCovered(prev=>({...prev,[ck]:true})))
                                          .catch(e=>alert(e?.response?.data?.error||"Could not mark as covered"));
                                      }}>✓ covered</button>
                                  </div>
                                  <div className="mip-card-body">
                                    {cd.items.map((it,i)=>(
                                      <div key={i} className="mip-bullet" style={{lineHeight:1.45,marginBottom:6}}>
                                        <span style={{color:"#0b80a5",marginRight:6}}>▸</span>
                                        <strong>{it.sectionTitle}</strong>
                                        {it.note
                                          ? <span style={{display:"block",marginLeft:18,color:"var(--text2,#444)",fontStyle:"italic"}}>“{it.note}”</span>
                                          : <span style={{marginLeft:6,fontSize:10,color:"var(--text3,#999)"}}>(ticked, no note)</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>);})()}
                              {/* Completed Since Last Meeting — factual when present, fall back to legacy outstandingActions */}
                              {(brief._factualCompletedSinceLastMeeting&&brief._factualCompletedSinceLastMeeting.length>0)&&(
                                <div className="mip-card">
                                  <div className="mip-card-hdr">✅ Completed Since Last Meeting</div>
                                  <div className="mip-card-body">
                                    {brief._factualCompletedSinceLastMeeting.map((e,i)=>(
                                      <div key={i} className="mip-bullet" style={{display:"flex",alignItems:"flex-start",gap:0,flexWrap:"wrap",lineHeight:1.4}}>
                                        <span style={{color:"#276749",marginRight:6,flexShrink:0}}>✓</span>
                                        <span style={{flex:"1 1 auto",minWidth:0}}>{e.title}</span>
                                        {e.ticket&&<span style={{fontSize:9,fontFamily:"var(--ff-mono, ui-monospace, monospace)",background:"#c6f6d5",color:"#276749",padding:"1px 6px",borderRadius:3,marginLeft:6,whiteSpace:"nowrap"}}>{e.ticket}</span>}
                                        <span style={{fontSize:9,color:"#276749",fontWeight:600,marginLeft:6}}>· {e.status}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Outstanding from Prior Meetings — factual if present, else legacy AI list */}
                              {((brief._factualOutstanding&&brief._factualOutstanding.length>0)||(brief.outstandingActions||[]).length>0)&&(
                                <div className="mip-card">
                                  <div className="mip-card-hdr">⏳ Outstanding from Prior Meetings</div>
                                  <div className="mip-card-body">
                                    {brief._factualOutstanding&&brief._factualOutstanding.length>0
                                      ? brief._factualOutstanding.map((e,i)=>{
                                          const isOpen=/^Open$/i.test(e.status);
                                          const statusBg=isOpen?"#fed7d7":"#fef3c7";
                                          const statusFg=isOpen?"#9b2c2c":"#78350f";
                                          return(
                                            <div key={i} className="mip-bullet" style={{display:"flex",alignItems:"flex-start",gap:0,flexWrap:"wrap",lineHeight:1.4}}>
                                              <span style={{color:"#d97706",marginRight:6,flexShrink:0}}>⏳</span>
                                              <span style={{flex:"1 1 auto",minWidth:0}}>{e.title}</span>
                                              {e.ticket&&<span style={{fontSize:9,fontFamily:"var(--ff-mono, ui-monospace, monospace)",background:"#fef3c7",color:"#78350f",padding:"1px 6px",borderRadius:3,marginLeft:6,whiteSpace:"nowrap"}}>{e.ticket}</span>}
                                              <span style={{fontSize:9,background:statusBg,color:statusFg,padding:"1px 6px",borderRadius:3,marginLeft:6,fontWeight:600,whiteSpace:"nowrap"}}>{e.status}</span>
                                            </div>
                                          );
                                        })
                                      : renderBullets(brief.outstandingActions||[])}
                                  </div>
                                </div>
                              )}
                              {brief.sentiment&&(
                                <div className="mip-card" style={{gridColumn:"1/-1"}}>
                                  <div className="mip-card-hdr">😊 Customer Sentiment
                                    {brief.sentiment.score!=null&&<span style={{marginLeft:"auto",fontSize:13,fontWeight:800,color:brief.sentiment.score>=7?"var(--ops)":brief.sentiment.score>=5?"var(--cal)":"var(--danger)"}}>{brief.sentiment.score}<span style={{fontSize:9,fontWeight:400,color:"var(--text3)"}}>/10</span></span>}
                                  </div>
                                  <div className="mip-card-body">
                                    {brief.sentiment.summary&&<div className="mip-bullet" style={{paddingLeft:0}}>{brief.sentiment.summary}</div>}
                                    {renderBullets(brief.sentiment.bullets||[])}
                                  </div>
                                </div>
                              )}
                            </div>
                            {(brief.talkingPoints||[]).length>0&&(
                              <div className="mip-tp-section" style={{marginBottom:14}}>
                                <div className="mip-tp-hdr">🎯 Talking Points</div>
                                <div className="mip-tp-body">
                                  {(brief.talkingPoints||[]).map((tp,i)=>(
                                    <div key={i} className="mip-tp-item">
                                      <div className="mip-tp-num">{i+1}.</div>
                                      <div>{tp.replace(/^\d+\.\s*/,"")}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {/* ── Outstanding Actions ── */}
                        {(()=>{
                          const allMtgTodos=todos.filter(t=>t.meetingRef===sm.id||(sm.iCalUID&&t.meetingRef===sm.iCalUID));
                          if(!allMtgTodos.length)return null;
                          const open=allMtgTodos.filter(t=>!t.done);
                          const completedRecently=allMtgTodos.filter(t=>t.done&&new Date(t.completedAt||t.doneAt||0)>=new Date(Date.now()-30*24*60*60*1000));
                          const hasItems=open.length||completedRecently.length;
                          if(!hasItems)return null;
                          return(
                            <div style={{background:"white",borderRadius:8,padding:"12px 16px",marginTop:14,border:"1px solid var(--border)"}}>
                              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--tact)",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                                ✅ Outstanding Actions
                                <span style={{fontSize:10,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none"}}>{open.length} open{completedRecently.length>0?` · ${completedRecently.length} completed`:""}</span>
                              </div>
                              {open.length>0&&(
                                <div style={{marginBottom:completedRecently.length>0?10:0}}>
                                  {open.map(t=>(
                                    <div key={t.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
                                      <div onClick={()=>{toggleTodo(t.id);}} style={{width:14,height:14,borderRadius:3,border:"2px solid var(--border2)",background:"transparent",flexShrink:0,marginTop:2,cursor:"pointer"}}/>
                                      <div style={{flex:1}}>
                                        <div style={{fontSize:11,color:"var(--text)",fontWeight:500,lineHeight:1.3}}>{t.title}</div>
                                        {t.dueDate&&<div style={{fontSize:9,color:"var(--text3)",marginTop:2}}>Due {new Date(t.dueDate+"T12:00:00").toLocaleDateString("en-AU",{day:"numeric",month:"short"})}</div>}
                                      </div>
                                      <span className={`pri pri-${t.priority}`} style={{flexShrink:0,fontSize:9}}>{t.priority}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {completedRecently.length>0&&(
                                <div>
                                  <div style={{fontSize:9,color:"var(--text3)",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:5}}>Completed this month</div>
                                  {completedRecently.map(t=>(
                                    <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",opacity:0.7}}>
                                      <div style={{width:14,height:14,borderRadius:3,border:"2px solid var(--ops)",background:"var(--ops)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:9}}>✓</div>
                                      <div style={{fontSize:11,color:"var(--text3)",textDecoration:"line-through",flex:1}}>{t.title}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* ── Raw Notes from Fellow Import ── */}
                        {(()=>{
                          /* Exact keys only — same cross-client trap as the Post
                             Meeting Report's rawNotes lookup (see there): the
                             title+date leg showed another client's notes under
                             this meeting whenever this one had none. */
                          const rn=(()=>{
                            if(meetingRawNotes[sm.id])return meetingRawNotes[sm.id];
                            if(sm.iCalUID&&meetingRawNotes[sm.iCalUID])return meetingRawNotes[sm.iCalUID];
                            return null;
                          })();
                          if(!rn)return null;
                          return(
                            <div style={{background:"var(--s2)",borderRadius:8,padding:"12px 16px",marginTop:14,border:"1px solid var(--border)"}}>
                              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--fellow)",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                                📋 Fellow Meeting Notes
                                <span style={{fontSize:9,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none"}}>
                                  {rn.savedAt&&"Saved "+new Date(rn.savedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                                  {rn.actionItemCount>0&&` · ${rn.actionItemCount} items imported`}
                                </span>
                              </div>
                              {/* Horizontal + vertical scroll container */}
                              <div style={{overflowX:"auto",overflowY:"auto",maxHeight:300,borderRadius:4,background:"white",border:"1px solid var(--border)",padding:"10px 12px"}}>
                                <pre style={{fontSize:11,color:"var(--text)",lineHeight:1.65,margin:0,fontFamily:"var(--ff-head)",whiteSpace:"pre-wrap",wordBreak:"break-word",minWidth:0}}>
                                  {rn.text}
                                </pre>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    </PanelErrorBoundary>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── FELLOW NOTES IMPORT TAB ── */}
          {tab==="notes"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              {/* Header */}
              <div style={{padding:"13px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)",letterSpacing:"-0.3px",marginBottom:3}}>Fellow Notes Import</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>Review Fellow meetings · Extract action items · Add to your to-do list</div>
                </div>
                <button className="btn btn-fellow" onClick={()=>{setSelectedFellowMeeting(null);setNotesMode("manual");setNotesText("");setNotesMeeting("");setNotesClient("");setExtracted([]);}}
                  style={{fontSize:11}}>+ Add manual notes</button>
              </div>

              <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                {/* LEFT: Fellow meeting list */}
                <div style={{width:320,borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden",background:"white",flexShrink:0}}>
                  <div style={{padding:"10px 14px 8px",borderBottom:"1px solid var(--border)",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                    <span>Fellow — Last 14 Days</span>
                    {fellowConn&&<button className="btn" style={{fontSize:9,padding:"2px 8px"}} onClick={()=>loadFellowMeetings(true)}>{fellowMeetingsLoading?"Loading...":"↻ Refresh"}</button>}
                  </div>
                  <div className="scroll">
                    {!fellowConn&&(
                      <div style={{padding:20,textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.7}}>
                        <div style={{fontSize:24,marginBottom:8}}>🤝</div>
                        Fellow not connected.<br/>
                        Go to <strong>Settings → Fellow.app</strong> and paste your<br/>
                        Fellow OAuth token to enable meeting notes.<br/>
                        <button className="btn btn-S" style={{marginTop:10,fontSize:10}}
                          onClick={()=>setTab("settings")}>
                          Open Settings →
                        </button>
                      </div>
                    )}
                    {fellowConn&&fellowMeetings.length===0&&!fellowMeetingsLoading&&(
                      <div style={{padding:"16px 14px",textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.7}}>
                        <button className="btn btn-fellow" style={{marginBottom:10}} onClick={loadFellowMeetings}>Load Fellow meetings</button><br/>
                        Pull meetings from the last 14 days
                      </div>
                    )}
                    {fellowMeetingsLoading&&(
                      <div style={{padding:"20px 14px",display:"flex",alignItems:"center",gap:8,color:"var(--fellow)",fontSize:11}}>
                        <div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/>
                        Loading Fellow meetings...
                      </div>
                    )}
                    {[...fellowMeetings].sort((a,b)=>{
                      // Most recent meeting first. Meetings without a date sink to the bottom.
                      const ta=a.date?new Date(a.date).getTime():0;
                      const tb=b.date?new Date(b.date).getTime():0;
                      return tb-ta;
                    }).map(m=>{
                      const isSelected=selectedFellowMeeting?.id===m.id;
                      // isImported: either in local state set OR annotations.todosExtracted from blob (survives browser refresh)
                      const isImported=importedFellowMeetings.has(m.id)||!!m.annotations?.todosExtracted;
                      const resetImport=(e)=>{
                        e.stopPropagation();
                        // Remove from importedFellowMeetings state + blob
                        setImportedFellowMeetings(s=>{const next=new Set([...s]);next.delete(m.id);saveSnapshot({importedFellowMeetings:[...next]});return next;});
                        // Clear annotation on the Fellow meeting record
                        if(onAnnotateFellowMeeting)onAnnotateFellowMeeting(m.id,{todosExtracted:false,todosExtractedAt:null});
                        setFellowMeetings(ms=>ms.map(fm=>fm.id===m.id?{...fm,annotations:{...(fm.annotations||{}),todosExtracted:false}}:fm));
                      };
                      return(
                        <div key={m.id}
                          onClick={()=>{if(!fellowNotesLoading||!isSelected)selectFellowMeeting(m);}}
                          style={{padding:"11px 14px",borderBottom:"1px solid var(--border)",
                            cursor:fellowNotesLoading&&isSelected?"wait":"pointer",
                            background:isSelected?"#eef6fa":isImported?"#f8fdf8":"white",
                            borderLeft:isSelected?"3px solid var(--strat)":isImported?"3px solid var(--ops)":"3px solid transparent",
                            opacity:isImported?0.75:1,transition:"background 0.1s"}}
                          onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background=isImported?"#f0fbf0":"#f5f9fb";}}
                          onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background=isImported?"#f8fdf8":"white";}}>
                          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:4}}>
                            <div style={{fontSize:12,color:isImported?"var(--text3)":"var(--text)",fontWeight:500,lineHeight:1.3,flex:1,display:"flex",alignItems:"center",gap:6}}>
                              {/* Inline spinner on the currently-loading card */}
                              {isSelected&&fellowNotesLoading&&(
                                <span style={{
                                  display:"inline-block",
                                  width:11,height:11,
                                  border:"2px solid var(--border)",
                                  borderTopColor:"var(--strat)",
                                  borderRadius:"50%",
                                  flexShrink:0,
                                  animation:"fellow-spin 0.8s linear infinite",
                                }}/>
                              )}
                              <span>{m.title}</span>
                            </div>
                            <div style={{display:"flex",gap:4,flexShrink:0,alignItems:"center"}}>
                              {isImported&&<span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:"var(--ops-d)",color:"var(--ops)",fontWeight:600}}>✓ Todos added</span>}
                              {m.annotations?.reviewed&&!isImported&&<span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:"var(--s2)",color:"var(--text3)",fontWeight:600}}>Reviewed</span>}
                              {m._cachedAt&&!isImported&&<span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:"var(--strat-d)",color:"var(--strat)",fontWeight:600}}>💾 Cached</span>}
                              {/* Reset button — always visible on imported cards */}
                              {isImported&&(
                                <button
                                  onClick={resetImport}
                                  title="Reset — remove Todos added status so you can re-import"
                                  style={{fontSize:9,padding:"2px 7px",borderRadius:3,border:"1px solid var(--border)",background:"white",color:"var(--text3)",cursor:"pointer",fontWeight:600,lineHeight:1.4}}
                                  onMouseEnter={e=>{e.target.style.borderColor="var(--danger)";e.target.style.color="var(--danger)";}}
                                  onMouseLeave={e=>{e.target.style.borderColor="var(--border)";e.target.style.color="var(--text3)";}}>
                                  ↺ Reset
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{fontSize:10,color:"var(--text3)",marginBottom:isImported?0:6}}>
                            {m.date?(()=>{
                              // Fellow returns ISO UTC timestamps. new Date() + toLocale*
                              // already converts to the browser's local timezone (Perth
                              // for Craig), so no manual +8h offset is needed — that was
                              // double-shifting the display 8h forward.
                              const p=new Date(m.date);
                              return p.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})+" · "+p.toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit",hour12:true});
                            })():""}
                            {m.attendeeCount>0&&` · ${m.attendeeCount} attendees`}
                          </div>
                          {m.preview&&!isImported&&(
                            <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.5,fontStyle:"italic",overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>
                              {m.preview}
                            </div>
                          )}
                          {!isImported&&!isSelected&&(
                            <div style={{marginTop:6}}>
                              <span style={{fontSize:9,color:"var(--strat)",fontWeight:600,letterSpacing:"0.04em"}}>→ Click to review</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT: Review / manual input pane */}
                <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                  {!selectedFellowMeeting&&notesMode!=="manual"&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:"var(--text3)",fontSize:12,lineHeight:1.8,padding:40,textAlign:"center"}}>
                      <div style={{fontSize:36,marginBottom:16}}>📋</div>
                      <div style={{fontFamily:"var(--ff-head)",fontWeight:600,fontSize:14,color:"var(--text)",marginBottom:8}}>Select a meeting to review</div>
                      Click a Fellow meeting on the left to extract its action items,<br/>
                      or use <strong>+ Add manual notes</strong> to paste in notes from any source.
                    </div>
                  )}

                  {(selectedFellowMeeting||notesMode==="manual")&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                      {/* Pane header */}
                      <div style={{padding:"11px 16px",borderBottom:"1px solid var(--border)",background:"white",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>
                            {selectedFellowMeeting?selectedFellowMeeting.title:notesMeeting||"Manual notes"}
                          </div>
                          {selectedFellowMeeting?.date&&(
                            <div style={{fontSize:10,color:"var(--text3)"}}>
                              {new Date(new Date(selectedFellowMeeting.date).getTime()+(8*60*60*1000)).toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit",hour12:true})}
                            </div>
                          )}
                        </div>
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          {selectedFellowMeeting&&<button className="btn" style={{fontSize:10}}
                            onClick={()=>selectFellowMeeting(selectedFellowMeeting,true)}>↻ Refresh notes</button>}
                          <button className="btn" style={{fontSize:10}} onClick={()=>{setSelectedFellowMeeting(null);setNotesMode(null);setNotesClient("");setExtracted([]);}}>✕ Close</button>
                        </div>
                      </div>

                      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                        {/* Notes area */}
                        <div style={{width:"45%",borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
                          <div style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",fontWeight:600,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                            <span>Raw Notes</span>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              {notesMode==="manual"&&<input className="inp" style={{marginBottom:0,width:120,fontSize:10}} placeholder="Meeting name..." value={notesMeeting} onChange={e=>setNotesMeeting(e.target.value)}/>}
                              {notesText.split("\n").filter(l=>l.trim()).length>0&&(
                                <button className="btn" style={{fontSize:9,padding:"1px 7px"}}
                                  onClick={()=>{
                                    const lines=notesText.split("\n");
                                    const nonEmpty=new Set(lines.map((l,i)=>l.trim()?i:-1).filter(i=>i>=0));
                                    setSelectedNoteLines(selectedNoteLines.size===nonEmpty.size?new Set():nonEmpty);
                                  }}>
                                  {selectedNoteLines.size>0?"Deselect all":"Select all"}
                                </button>
                              )}
                            </div>
                          </div>
                          {notesMode==="manual"&&notesText===""?(
                            <textarea className="notes-textarea" placeholder={"Paste your meeting notes here...\nEach line will become selectable.\nThen click Extract to find action items."} value={notesText} onChange={e=>{setNotesText(e.target.value);setSelectedNoteLines(new Set());}} style={{flex:1,padding:14,fontSize:11}}/>
                          ):(
                            <div className="scroll" style={{flex:1}}>
                              {/* Loading spinner — visible while Fellow notes are being fetched
                                  (initial fetch or cache auto-heal). Replaces the previous
                                  static "Loading notes..." placeholder which showed even
                                  after failed loads or before any meeting was selected. */}
                              {fellowNotesLoading&&notesText===""&&(
                                <div style={{padding:"40px 14px",color:"var(--text3)",fontSize:11,textAlign:"center",lineHeight:1.8,display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
                                  <div style={{
                                    width:32,height:32,
                                    border:"3px solid var(--border)",
                                    borderTopColor:"var(--strat)",
                                    borderRadius:"50%",
                                    animation:"fellow-spin 0.8s linear infinite",
                                  }}/>
                                  <div>
                                    <div style={{fontWeight:600,color:"var(--text2)",marginBottom:4}}>Loading meeting notes…</div>
                                    <div style={{fontSize:10}}>First load can take a few seconds while we pull AI sections from Fellow.</div>
                                  </div>
                                  {/* Inject keyframe locally — avoids changing global CSS */}
                                  <style>{`@keyframes fellow-spin{to{transform:rotate(360deg)}}`}</style>
                                </div>
                              )}
                              {/* Empty state — no meeting selected yet, or load completed with no notes */}
                              {!fellowNotesLoading&&notesText===""&&(
                                <div style={{padding:"20px 14px",color:"var(--text3)",fontSize:11,textAlign:"center",lineHeight:1.8}}>
                                  <div style={{fontSize:20,marginBottom:6}}>📄</div>
                                  {selectedFellowMeeting?"This meeting has no notes available.":"Select a meeting on the left to view its notes."}
                                </div>
                              )}
                              {notesText.split("\n").map((line,i)=>{
                                if(!line.trim())return null;
                                if(deletedNoteLines.has(i))return null; // hidden — deleted for privacy
                                const isSelected=selectedNoteLines.has(i);
                                const isEditing=editingNoteLine===i;
                                const displayText=editedNoteLines[i]??line;
                                const isEdited=editedNoteLines[i]!==undefined&&editedNoteLines[i]!==line;
                                const isHeading=line.startsWith("#")||(/^[A-Z][^a-z]{2,}/.test(line)&&line.length<60);
                                return(
                                  <div key={i}
                                    style={{padding:"5px 8px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"flex-start",gap:6,
                                      background:isSelected?"rgba(11,128,165,0.06)":isEdited?"rgba(255,193,7,0.06)":"white",
                                      transition:"background 0.1s"}}>
                                    {/* Checkbox — click text area to toggle, not buttons */}
                                    <div onClick={()=>!isEditing&&setSelectedNoteLines(prev=>{const next=new Set(prev);next.has(i)?next.delete(i):next.add(i);return next;})}
                                      style={{width:14,height:14,borderRadius:3,border:`1.5px solid ${isSelected?"var(--strat)":"var(--border2)"}`,
                                        background:isSelected?"var(--strat)":"transparent",flexShrink:0,marginTop:3,cursor:"pointer",
                                        display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:9}}>
                                      {isSelected&&"✓"}
                                    </div>
                                    {/* Line text — editable or display */}
                                    {isEditing?(
                                      <input
                                        autoFocus
                                        defaultValue={displayText}
                                        onBlur={e=>{
                                          const val=e.target.value.trim();
                                          if(val&&val!==line){
  const newEdited={...editedNoteLines,[i]:val};
  setEditedNoteLines(newEdited);
  // Save immediately — rebuild text with this line updated
  if(selectedFellowMeeting?.id){
    const rawLines=notesText.split("\n");
    const saved=rawLines.map((l,idx)=>deletedNoteLines.has(idx)?null:(newEdited[idx]??l)).filter(l=>l!==null).join("\n").trim();
    const key="curated_"+selectedFellowMeeting.id;
    setMeetingRawNotes(prev=>{
      const next={...prev,[key]:{text:saved,savedAt:new Date().toISOString()}};
      saveSnapshot({meetingRawNotes:next});
      return next;
    });
  }
}
                                          else if(!val){setDeletedNoteLines(prev=>new Set([...prev,i]));}
                                          else{setEditedNoteLines(prev=>{const n={...prev};delete n[i];return n;});}
                                          setEditingNoteLine(null);
                                        }}
                                        onKeyDown={e=>{
                                          if(e.key==="Enter"){e.target.blur();}
                                          if(e.key==="Escape"){setEditingNoteLine(null);}
                                        }}
                                        style={{flex:1,fontSize:isHeading?11:10,fontWeight:isHeading?700:400,background:"var(--s2)",border:"1px solid var(--strat)",borderRadius:3,padding:"1px 5px",outline:"none",fontFamily:"var(--ff-head)"}}
                                      />
                                    ):(
                                      <div onClick={()=>!isEditing&&setSelectedNoteLines(prev=>{const next=new Set(prev);next.has(i)?next.delete(i):next.add(i);return next;})}
                                        style={{fontSize:isHeading?11:10,fontWeight:isHeading?700:400,color:isHeading?"var(--text)":"var(--text2)",lineHeight:1.5,flex:1,cursor:"pointer",userSelect:"none"}}>
                                        {displayText.replace(/^#+\s*/,"")}
                                        {isEdited&&<span style={{fontSize:8,color:"var(--cal)",marginLeft:5,fontStyle:"italic"}}>edited</span>}
                                      </div>
                                    )}
                                    {/* Edit button */}
                                    {!isEditing&&(
                                      <button onClick={e=>{e.stopPropagation();setEditingNoteLine(i);}}
                                        title="Edit line"
                                        style={{flexShrink:0,background:"none",border:"none",cursor:"pointer",padding:"1px 3px",fontSize:10,color:"var(--text3)",lineHeight:1,marginTop:1,opacity:0.6}}
                                        onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                                        onMouseLeave={e=>e.currentTarget.style.opacity="0.6"}>
                                        ✏️
                                      </button>
                                    )}
                                    {/* Delete button */}
                                    <button onClick={e=>{
  e.stopPropagation();
  const newDeleted=new Set([...deletedNoteLines,i]);
  setDeletedNoteLines(newDeleted);
  setSelectedNoteLines(prev=>{const n=new Set(prev);n.delete(i);return n;});
  setEditingNoteLine(null);
  // Save immediately — rebuild text with this line removed
  if(selectedFellowMeeting?.id){
    const rawLines=notesText.split("\n");
    const saved=rawLines.map((l,idx)=>newDeleted.has(idx)?null:(editedNoteLines[idx]??l)).filter(l=>l!==null).join("\n").trim();
    const key="curated_"+selectedFellowMeeting.id;
    setMeetingRawNotes(prev=>{
      const next={...prev,[key]:{text:saved,savedAt:new Date().toISOString()}};
      saveSnapshot({meetingRawNotes:next});
      return next;
    });
  }
}}
                                      title="Remove this line (privacy)"
                                      style={{flexShrink:0,background:"none",border:"none",cursor:"pointer",padding:"1px 3px",fontSize:11,color:"var(--danger)",lineHeight:1,marginTop:1,opacity:0.5}}
                                      onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                                      onMouseLeave={e=>e.currentTarget.style.opacity="0.5"}>
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div style={{padding:"10px 14px",borderTop:"1px solid var(--border)",display:"flex",gap:8,flexShrink:0,background:"var(--s2)",alignItems:"center",flexWrap:"wrap"}}>
                            {deletedNoteLines.size>0&&(
                              <span style={{fontSize:10,color:"var(--danger)",fontWeight:600}}>
                                🔒 {deletedNoteLines.size} line{deletedNoteLines.size!==1?"s":""} removed
                                <button className="btn" style={{fontSize:9,padding:"1px 6px",marginLeft:5}}
                                  onClick={()=>{
  setDeletedNoteLines(new Set());
  setEditedNoteLines({});
  setNotesText(notesText); // keep current raw for display
  // Clear curated so fresh Fellow data loads next time
  if(selectedFellowMeeting?.id){
    const key="curated_"+selectedFellowMeeting.id;
    setMeetingRawNotes(prev=>{
      const next={...prev};
      delete next[key];
      saveSnapshot({meetingRawNotes:next});
      return next;
    });
  }
}}>Restore all</button>
                              </span>
                            )}
                            {selectedNoteLines.size>0&&(
                              <span style={{fontSize:10,color:"var(--tact)",fontWeight:600}}>{selectedNoteLines.size} line{selectedNoteLines.size!==1?"s":""} selected</span>
                            )}
                            {selectedNoteLines.size===0&&!deletedNoteLines.size&&notesText&&(
                              <span style={{fontSize:10,color:"var(--text3)"}}>Tick lines to move to Action Items, or run AI detect</span>
                            )}
                            <button className="btn btn-fellow" onClick={extractTodosFromNotes} disabled={!notesText.trim()||extracting} style={{justifyContent:"center",marginLeft:"auto"}}>
                              {extracting?<><div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/><div className="ai-dot" style={{background:"var(--fellow)"}}/>Extracting...</>:selectedNoteLines.size>0?`▶ Move ${selectedNoteLines.size} ticked to Action Items`:"◆ Detect action items (AI)"}
                            </button>
                          </div>
                        </div>

                        {/* Extracted items */}
                        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                          <div style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",fontWeight:600,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                            <span>Action Items {extracted.length>0&&`(${extracted.filter(e=>e.selected!==false).length} selected)`}</span>
                            {extracted.length>1&&<button className="btn" style={{fontSize:9,padding:"2px 8px"}} onClick={()=>setExtracted(ex=>ex.map(e=>({...e,selected:true})))}>Select all</button>}
                          </div>
                          <div className="scroll">
                            {extracted.length===0&&(
                              <div style={{padding:"30px 16px",textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.7}}>
                                Action items will appear here after extraction.
                              </div>
                            )}
                            {extracted.map((item,idx)=>(
                              <div key={item.id} style={{padding:"11px 14px",borderBottom:"1px solid var(--border)",background:item.selected===false?"var(--s2)":"white",opacity:item.selected===false?0.5:1,transition:"all 0.15s"}}>
                                <div style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:7}}>
                                  {/* Checkbox */}
                                  <div
                                    onClick={()=>setExtracted(ex=>ex.map((e,i)=>i===idx?{...e,selected:e.selected===false?true:false}:e))}
                                    style={{width:16,height:16,borderRadius:4,border:`2px solid ${item.selected===false?"var(--border2)":"var(--strat)"}`,
                                      background:item.selected===false?"transparent":"var(--strat)",
                                      display:"flex",alignItems:"center",justifyContent:"center",
                                      cursor:"pointer",flexShrink:0,marginTop:1,color:"white",fontSize:10,transition:"all 0.15s"}}>
                                    {item.selected!==false&&"✓"}
                                  </div>
                                  {/* Editable title */}
                                  <input
                                    value={item.title}
                                    onChange={e=>setExtracted(ex=>ex.map((x,i)=>i===idx?{...x,title:e.target.value}:x))}
                                    style={{flex:1,background:"transparent",border:"none",outline:"none",fontSize:12,color:"var(--text)",fontWeight:500,fontFamily:"var(--ff-head)",padding:0}}
                                  />
                                  {/* Delete */}
                                  <button className="ico-btn" onClick={()=>setExtracted(ex=>ex.filter((_,i)=>i!==idx))} style={{flexShrink:0}}>×</button>
                                </div>
                                {/* Owner + controls row */}
                                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",paddingLeft:25}}>
                                  {item.owner&&item.owner!=="unknown"&&(
                                    <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:"var(--tact-d)",color:"var(--tact)",fontWeight:600}}>👤 {item.owner}</span>
                                  )}
                                  <span className={`pri pri-${item.priority==="high"?"high":item.priority==="med"?"med":"low"}`} style={{cursor:"pointer"}}
                                    onClick={()=>setExtracted(ex=>ex.map((x,i)=>i===idx?{...x,priority:x.priority==="high"?"med":x.priority==="med"?"low":"high"}:x))}>
                                    {item.priority}
                                  </span>
                                  <span className={`lb lb-${item.layer}`} style={{cursor:"pointer"}}
                                    onClick={()=>setExtracted(ex=>ex.map((x,i)=>i===idx?{...x,layer:x.layer==="S"?"T":x.layer==="T"?"O":"S"}:x))}>
                                    {LAYERS[item.layer]?.label}
                                  </span>
                                  <input type="date" value={item.editedDate||item.suggestedDate||""} onChange={e=>setExtracted(ex=>ex.map((x,i)=>i===idx?{...x,editedDate:e.target.value}:x))}
                                    style={{fontSize:10,background:"#f8f9fa",border:"1px solid #ddd",borderRadius:5,padding:"2px 7px",color:"var(--text)",fontFamily:"var(--ff-head)",outline:"none"}}/>
                                </div>
                                {item.notes&&(
                                  <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.5,marginTop:5,paddingLeft:25,fontStyle:"italic"}}>{item.notes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Confirm footer — assign to meeting + commit to To-Do */}
                          {extracted.length>0&&(
                            <div style={{padding:"10px 14px",borderTop:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:8,flexShrink:0,background:"var(--s2)"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                {/* Label turns red when the user attempted to commit without picking a meeting */}
                                <span style={{
                                  fontSize:10,
                                  color:meetingAssignError?"var(--danger)":"var(--text3)",
                                  fontWeight:meetingAssignError?700:600,
                                  flexShrink:0,
                                  transition:"color .15s",
                                }}>📅 Assign to meeting:</span>
                                <select
                                  id="fellow-assign-to-meeting"
                                  value={assignToMeeting}
                                  onChange={e=>{
                                    setAssignToMeeting(e.target.value);
                                    // Clear validation error as soon as user picks something
                                    if(e.target.value) setMeetingAssignError(false);
                                  }}
                                  style={{
                                    flex:1,fontSize:10,padding:"3px 6px",borderRadius:4,
                                    // Red 2px border + light pink background when validation error,
                                    // otherwise normal styling
                                    border:meetingAssignError?"2px solid var(--danger)":"1px solid var(--border)",
                                    background:meetingAssignError?"rgba(231,76,60,0.06)":"white",
                                    color:assignToMeeting?"var(--text)":(meetingAssignError?"var(--danger)":"var(--text3)"),
                                    fontFamily:"var(--ff-head)",outline:"none",
                                    fontWeight:meetingAssignError&&!assignToMeeting?600:400,
                                    transition:"border-color .15s, background .15s",
                                  }}>
                                  <option value="">— No meeting assigned —</option>
                                  {[...meetings]
                                    // 6 months, matching the three past lists — a note often needs
                                    // assigning to an older meeting. The .sort below is newest-first,
                                    // so the likely pick stays at the top of a longer list.
                                    .filter(m=>{const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);if(!ds)return false;return ds<=todayISO()&&ds>=pastWindowStartISO();})
                                    .map(m=>{
                                      const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                                      let client=m.client||"";
                                      if(!client){const tm=atCompanies.find(c=>m.title?.toLowerCase().includes(c.companyName.toLowerCase()));if(tm)client=tm.companyName;}
                                      if(!client&&m.attendees?.length){for(const att of m.attendees){const email=att.emailAddress?.address||att.email||"";const domain=email.split("@")[1]||"";if(!domain||domain==="datatech.net.au")continue;const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){client=co.companyName;break;}}}}
                                      const dateLabel=new Date(ds+"T12:00:00").toLocaleDateString("en-AU",{day:"numeric",month:"short"});
                                      return{id:m.id,label:[client,m.title].filter(Boolean).join(" — "),dateLabel,ds};
                                    })
                                    .sort((a,b)=>b.ds.localeCompare(a.ds))
                                    .map(({id,label,dateLabel})=><option key={id} value={id}>{dateLabel} · {label}</option>)
                                  }
                                </select>
                              </div>
                              {/* Inline error message below the field when validation fails */}
                              {meetingAssignError&&!assignToMeeting&&(
                                <div style={{fontSize:10,color:"var(--danger)",fontWeight:600,marginTop:-2,paddingLeft:2}}>
                                  ⚠ Please pick a meeting before committing — these to-dos need to be tied to one of your recent meetings.
                                </div>
                              )}
                              {/* AT ticket creation status toast */}
                              {fellowTicketStatus&&(
                                <div style={{
                                  padding:"8px 12px",borderRadius:6,fontSize:10,lineHeight:1.5,
                                  background:fellowTicketStatus==="creating"?"rgba(52,152,219,0.08)":fellowTicketStatus?.error?"rgba(231,76,60,0.08)":"rgba(39,174,96,0.08)",
                                  border:`1px solid ${fellowTicketStatus==="creating"?"rgba(52,152,219,0.25)":fellowTicketStatus?.error?"rgba(231,76,60,0.25)":"rgba(39,174,96,0.25)"}`,
                                  color:fellowTicketStatus==="creating"?"var(--strat)":fellowTicketStatus?.error?"var(--danger)":"var(--ops)",
                                  fontWeight:600,
                                }}>
                                  {fellowTicketStatus==="creating"&&<>⏳ Creating Autotask ticket…</>}
                                  {fellowTicketStatus?.ticketNumber&&<>🎫 Autotask ticket {fellowTicketStatus.ticketNumber} created · {fellowTicketStatus.hoursWorked}h logged</>}
                                  {fellowTicketStatus?.error&&<>⚠ AT ticket failed: {fellowTicketStatus.error}</>}
                                </div>
                              )}
                              <div style={{display:"flex",gap:8}}>
                                <button className="btn btn-S" style={{flex:1,justifyContent:"center",background:"var(--strat)",color:"white",borderColor:"var(--strat)"}}
                                  onClick={async()=>{
                                    const toImport=extracted.filter(e=>e.selected!==false);
                                    if(!toImport.length)return;
                                    if(!assignToMeeting){
                                      setMeetingAssignError(true);
                                      try{
                                        const sel=document.getElementById("fellow-assign-to-meeting");
                                        if(sel){
                                          if(typeof sel.scrollIntoView==="function") sel.scrollIntoView({block:"nearest",behavior:"smooth"});
                                          if(typeof sel.focus==="function") sel.focus();
                                        }
                                      }catch{/* noop */}
                                      return;
                                    }
                                    setMeetingAssignError(false);
                                    // Snapshot values we need before clearing state below
                                    const _selectedMeeting=selectedFellowMeeting;
                                    const _notesText=notesText;
                                    const _notesClient=notesClient;
                                    const _assignTo=assignToMeeting;
                                    // Add to To-Do list
                                    toImport.forEach(item=>acceptExtracted(item));
                                    // Save raw notes + action items against the meeting
                                    if(_assignTo&&_notesText.trim()){
                                      const rawLines=_notesText.split("\n");
                                      const cleanedText=rawLines.map((l,i)=>deletedNoteLines.has(i)?null:(editedNoteLines[i]??l)).filter(l=>l!==null).join("\n").trim();
                                      if(cleanedText){
                                        const assignedMeetingEvent=meetings.find(m=>m.id===_assignTo);
                                        const meetingLocalDate=assignedMeetingEvent?._dateStr||assignedMeetingEvent?.localDateStr||null;
                                        const newRawNotes={...meetingRawNotes,[_assignTo]:{
                                          text:cleanedText,
                                          title:notesMeeting||_selectedMeeting?.title||"",
                                          savedAt:new Date().toISOString(),
                                          meetingDate:meetingLocalDate,
                                          actionItemCount:toImport.length,
                                          actionItems:toImport.map(i=>({title:i.title,priority:i.priority,owner:i.owner,layer:i.layer})),
                                          deletedCount:deletedNoteLines.size,
                                        }};
                                        setMeetingRawNotes(newRawNotes);
                                        saveSnapshot({meetingRawNotes:newRawNotes});
                                      }
                                    }
                                    // Mark Fellow meeting as imported
                                    if(_selectedMeeting){
                                      setImportedFellowMeetings(s=>{const next=new Set([...s,_selectedMeeting.id]);saveSnapshot({importedFellowMeetings:[...next]});return next;});
                                      if(onAnnotateFellowMeeting&&_selectedMeeting?.id){
                                        onAnnotateFellowMeeting(_selectedMeeting.id,{todosExtracted:true,todosExtractedAt:new Date().toISOString()});
                                        setFellowMeetings(ms=>ms.map(m=>m.id===_selectedMeeting.id?{...m,annotations:{...(m.annotations||{}),todosExtracted:true}}:m));
                                      }
                                    } else {
                                      setNotesText("");setNotesMode(null);setNotesClient("");
                                    }
                                    setSelectedFellowMeeting(null);
                                    setExtracted([]);setAssignToMeeting("");

                                    // ── Autotask ticket + time entry ───────────────────────────────
                                    // Fire after UI reset so the panel clears immediately.
                                    // Resolve companyId from the assigned calendar meeting.
                                    const assignedMtg=meetings.find(m=>m.id===_assignTo);
                                    const coId=assignedMtg?.companyId||assignedMtg?.companyID||(()=>{
                                      if(!_notesClient) return null;
                                      const co=atCompanies.find(c=>c.companyName===_notesClient||c.companyName?.toLowerCase()===_notesClient?.toLowerCase());
                                      return co?.id||null;
                                    })();

                                    if(!coId){
                                      console.warn("Fellow commit: could not resolve companyId — skipping AT ticket creation");
                                    } else {
                                      // Extract the AI Summary from the notes text
                                      const aiSummary=(()=>{
                                        const lines=(_notesText||"").split("\n");
                                        let inSummary=false,buf=[];
                                        for(const l of lines){
                                          if(/^#{1,3}\s+Summary/i.test(l)){inSummary=true;continue;}
                                          if(inSummary&&/^#{1,3}\s/.test(l)) break;
                                          if(inSummary&&l.trim()) buf.push(l.trim());
                                        }
                                        return buf.join(" ").slice(0,2000)||_notesText.split("\n").find(l=>l.trim()&&!l.startsWith("#"))||"Monthly Tech Catchup meeting notes.";
                                      })();
                                      // Default Gold Fortress contracts for all managed clients
                                      const MANAGED_CONTRACTS={373:29683497,383:29683500,386:29683501,391:29683494,409:29683499,455:29683498,457:29683495,461:29683496,513:29683665};
                                      const contractId=MANAGED_CONTRACTS[Number(coId)]||null;
                                      // Best-effort primary contact from atCompanies
                                      const atContacts=(atCompanies.find(c=>String(c.id)===String(coId))||{}).contacts||[];
                                      const primaryContact=atContacts.find(c=>c.primaryContact&&c.isActive!==0)||atContacts.find(c=>c.isActive!==0)||null;
                                      const ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                                      const tok=window.__mspToken;
                                      setFellowTicketStatus("creating");
                                      try{
                                        const r=await fetch(`${ab}/api/tickets/fellow-meeting`,{
                                          method:"POST",
                                          headers:{Authorization:`Bearer ${tok}`,"Content-Type":"application/json"},
                                          body:JSON.stringify({
                                            companyId:   Number(coId),
                                            contactId:   primaryContact?.id||null,
                                            contractId,
                                            summary:     aiSummary,
                                            meetingDate: assignedMtg?.localDateStr||assignedMtg?._dateStr||new Date().toISOString().slice(0,10),
                                            startTime:   assignedMtg?.startTime||null,
                                            durationMins:_selectedMeeting?._duration||_selectedMeeting?.duration||60,
                                            issueType:    fellowTicketSettings.issueType,
                                            subIssueType: fellowTicketSettings.subIssueType,
                                            source:       fellowTicketSettings.source,
                                            queueId:      fellowTicketSettings.queueId,
                                            workTypeId:   fellowTicketSettings.workTypeId||null,
                                          }),
                                        });
                                        const d=await r.json();
                                        if(d.success){
                                          setFellowTicketStatus({ticketNumber:d.ticketNumber||`#${d.ticketId}`,hoursWorked:d.hoursWorked});
                                        } else {
                                          setFellowTicketStatus({error:d.message||d.error||"Unknown error"});
                                        }
                                      }catch(atErr){
                                        setFellowTicketStatus({error:atErr.message});
                                        console.error("Fellow AT ticket creation failed",atErr);
                                      }
                                      // Auto-dismiss status toast after 8 seconds
                                      setTimeout(()=>setFellowTicketStatus(null),8000);
                                    }
                                  }}>
                                  ✓ Commit {extracted.filter(e=>e.selected!==false).length} to To-Do · Save to meeting
                                </button>
                                <button className="btn" onClick={()=>{setExtracted([]);setSelectedFellowMeeting(null);setNotesMode(null);setNotesClient("");setAssignToMeeting("");setMeetingAssignError(false);}}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── POST MEETING REPORTING TAB ── */}
          {tab==="postreport"&&(()=>{
            const todayStr=todayISO();
            // Same 6-month window as Prep Notes and Meeting Notes — one helper,
            // so the three past lists cannot drift apart again.
            const cutoff=pastWindowStartISO();

            // Same data-ready gate as Prep/Meeting Notes — prevents the
            // attendee-name → hotel-name flicker by waiting for both
            // calendar and AT companies before rendering.
            const dataReady=meetings.length>0&&atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>
                  {meetings.length===0?"Loading calendar data…":"Loading company data…"}
                  <br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span>
                </div>
              </div>
            );

            // Monthly Tech Catchups only, last 6 months
            const mtcMeetings=[...meetings]
              .filter(m=>{
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                return ds&&ds<todayStr&&ds>=cutoff&&m.title?.toLowerCase().includes("monthly tech catchup");
              })
              .map(m=>{
                const ds=m.localDateStr||(typeof m.date==="string"?m.date.slice(0,10):null);
                let client=m.client||"";
                if(!client){const tm=atCompanies.find(c=>m.title?.toLowerCase().includes(c.companyName.toLowerCase()));if(tm)client=tm.companyName;}
                if(!client&&m.attendees?.length){
                  for(const att of m.attendees){
                    const email=att.emailAddress?.address||att.email||"";const domain=email.split("@")[1]||"";
                    if(!domain||domain==="datatech.net.au")continue;
                    const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                    if(entry){const co=atCompanies.find(c=>String(c.id)===entry[0]);if(co){client=co.companyName;break;}}
                  }
                }
                const daysAgo=Math.ceil((new Date(todayStr+"T00:00:00")-new Date(ds+"T12:00:00"))/(1000*60*60*24));
                return{...m,_dateStr:ds,_daysAgo:daysAgo,client};
              })
              .sort((a,b)=>b._dateStr.localeCompare(a._dateStr));

            const sm=postReportMeeting;
            /* ⛔ EXACT KEYS ONLY — NO TITLE+DATE FALLBACK. meetingRawNotes is one
               global map for every client, and the old fuzzy leg (title
               contains / date ±1 day) matched ANOTHER client's "Monthly Tech
               Catchup" held the same week whenever this client's meeting had no
               imported notes — so a client who missed their meeting got a Post
               Meeting Report built from someone else's discussion. The Fellow
               import saves under the CALENDAR event id (`_assignTo`), so the
               direct id / iCalUID matches are the ones that ever legitimately
               hit; the entries carry no companyId to scope a fuzzy match by.
               No notes ⇒ null ⇒ the prompts below say the meeting did not
               proceed, rather than inventing or borrowing content. */
            const rawNotes=sm?(()=>{
              if(meetingRawNotes[sm.id])return meetingRawNotes[sm.id];
              if(sm.iCalUID&&meetingRawNotes[sm.iCalUID])return meetingRawNotes[sm.iCalUID];
              return null;
            })():null;
            const savedReport=sm?(meetingReportUrls[sm.id]||meetingReportUrls[sm.iCalUID]||null):null;

            const loadPostReportData=async(m)=>{
              setPostReportMeeting(m);
              setPostReportBrief(null);setPostReportPrep(null);
              if(!m)return;
              setPostReportLoading(true);
              const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
              const tok=window.__mspToken||"";
              let co=atCompanies.find(c=>normCo(c.companyName)===normCo(m.client||"")||c.companyName.toLowerCase()===m.client?.toLowerCase());
              if(!co&&m.attendees?.length){
                for(const att of m.attendees){
                  const email=att.emailAddress?.address||att.email||"";const domain=email.split("@")[1]||"";
                  if(!domain||domain==="datatech.net.au")continue;
                  // 1. clientDomainOverrides exact match
                  const entry=Object.entries(clientDomainOverrides||{}).find(([,d])=>d===domain||domain.endsWith("."+d)||d.endsWith("."+domain));
                  if(entry){const found=atCompanies.find(c=>String(c.id)===entry[0]);if(found){co=found;break;}}
                  // 2. clientDomainOverrides by id (even if atCompanies empty)
                  if(!co&&entry){co={id:entry[0],companyName:m.client||domain};break;}
                  // 3. Fuzzy domain name → atCompanies
                  if(!co&&atCompanies.length){
                    const domainBase=domain.replace(/\.(com\.au|com|net\.au|net|org\.au|org|au)$/,"").replace(/[.-]/g," ").toLowerCase();
                    const fuzzy=atCompanies.find(c=>c.companyName.toLowerCase().replace(/[^a-z0-9]/g,"").includes(domainBase.replace(/\s/g,""))||domainBase.replace(/\s/g,"").includes(c.companyName.toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,8)));
                    if(fuzzy){co=fuzzy;break;}
                  }
                }
              }
              try{
                // Build all possible meeting keys — data may be stored under
                // any of these. Includes "prep-" prefixed variants because some
                // earlier code paths sent the prep card's React id (which has
                // the "prep-" prefix) to send-link, storing under that key.
                const meetingKeys=[
                  m.id, m.iCalUID, m.iCalUId, m._dateStr,
                  m.id?`prep-${m.id}`:null,
                  m.iCalUID?`prep-${m.iCalUID}`:null,
                ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
                // Scan all managed company IDs × all key variants to find wherever data is stored
                // Last resort before scanning EVERY managed client: match the
                // meeting's client name against Autotask.
                //
                // ⚠ STRICT matching only — exact, or bidirectional substring
                // with BOTH sides >= 8 chars, the same rule as line ~2959 and
                // gatherPriorActionItems. Unbounded substring is what made
                // every hotel match "Hotel Rottnest", and here it would be
                // worse than the bug it fixes: a wrong `co` pins the whole
                // report to one confidently-wrong client, where the fallback
                // below at least keeps scanning.
                if(!co&&m.client&&atCompanies?.length){
                  const t=m.client.toLowerCase().trim();
                  co=atCompanies.find(c=>{
                    const n=(c.companyName||"").toLowerCase().trim();
                    if(!n)return false;
                    if(n===t||normCo(n)===normCo(t))return true;
                    return n.length>=8&&t.length>=8&&(n.includes(t)||t.includes(n));
                  })||null;
                }
                const candidateIds=co?[String(co.id)]:[...new Set([...(managedCompanyIds||[]),...Object.keys(clientDomainOverrides||{})])];
                let briefResult=null,prepResult=null;
                if(candidateIds.length>0&&meetingKeys.length>0){
                  // Try all combinations in parallel
                  const combos=candidateIds.flatMap(id=>meetingKeys.map(mk=>({id,mk})));
                  const results=await Promise.all(combos.map(async({id,mk})=>{
                    const enc=encodeURIComponent(mk);
                    const [br,pr]=await Promise.all([
                      fetch(`${_ab}/api/ai/meeting-brief/${id}?meeting=${enc}`,{headers:{Authorization:`Bearer ${tok}`}}).then(r=>r.json()).catch(()=>null),
                      fetch(`${_ab}/api/meeting-prep/${id}?meeting=${enc}`,{headers:{Authorization:`Bearer ${tok}`}}).then(r=>r.json()).catch(()=>null),
                    ]);
                    return{id,mk,brief:br?.brief||null,prep:pr?.response?.submittedAt?{...pr.response,questions:pr.response.questions||pr.questions||[]}:null};
                  }));
                  // Pick brief and prep INDEPENDENTLY. The previous logic
                  // (results.find(r=>r.brief&&r.prep)||...) required both from
                  // the SAME combo, which broke when (as is normal here) the
                  // brief is stored under the event id while the prep response
                  // sits at "prep-"+eventId. The first-prep-only result was
                  // selected and the brief was silently dropped — Post Meeting
                  // Reporting showed "No MIP found" even when both pieces of
                  // data exist, just at different storage keys.
                  // Brief and prep must come from the SAME COMPANY, or the
                  // report mixes two clients' data — one client's narrative
                  // above another's form answers, which reads as a single
                  // coherent document and is the worst possible failure here.
                  //
                  // COMPANY FIRST, then key. The two legitimately live under
                  // DIFFERENT meeting keys (the brief under the event id, the
                  // prep under "prep-"+eventId), which is why requiring one
                  // combo to hold both silently dropped the brief before. So
                  // pick the company that has a brief, then take the prep from
                  // that company under whichever key it happens to sit.
                  //
                  // NOT `results.find(r=>r.brief&&r.prep)` as a first pass:
                  // that scans every candidate company, so a WRONG client whose
                  // brief and prep happen to share a key would outrank the
                  // right client whose two pieces are split across keys.
                  const briefHit = results.find(r=>r.brief);
                  briefResult = briefHit?.brief || null;
                  prepResult  = briefHit
                    ? (results.find(r=>r.prep&&String(r.id)===String(briefHit.id))?.prep || null)
                    : (results.find(r=>r.prep)?.prep || null);
                }
                setPostReportBrief(briefResult);
                setPostReportPrep(prepResult);
              }catch(e){console.warn("Post report load error",e);}
              finally{setPostReportLoading(false);}
            };

            const generateAndPublish=async()=>{
              if(!sm)return;
              setPostReportGenerating(true);
              const _ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
              const tok=window.__mspToken||"";
              // Build context for AI
              const prepSummary=postReportPrep?Object.entries(postReportPrep.answers||{}).map(([qid,ans])=>{const q=(postReportPrep.questions||[]).find(x=>x.id===qid);if(!q)return null;const suffix=q.type==="rating"?` (out of ${q.scale||5})`:"";return`${q.label}: ${ans}${suffix}`;}).filter(Boolean).join("\n"):null;
              const mipData=postReportBrief?`Snapshot: ${postReportBrief.snapshot||""}\nSentiment: ${postReportBrief.sentiment?.score||"N/A"}/10 — ${postReportBrief.sentiment?.summary||""}\nTalking points: ${(postReportBrief.talkingPoints||[]).join("; ")}\nOpen issues: ${(postReportBrief.openIssues||[]).join("; ")}\nInfrastructure: ${(postReportBrief.infrastructure||[]).join("; ")}\nOutstanding actions: ${(postReportBrief.outstandingActions||[]).join("; ")}`:null;
              const actionItems=(rawNotes?.actionItems||[]).map(a=>a.title||String(a)).join("\n");
              // No imported notes for THIS meeting (exact-key lookup above) means
              // the client did not attend or nothing was captured. The prompts
              // must then say so rather than fabricate a discussion — and must
              // never be handed another meeting's notes, which the old fuzzy
              // fallback used to do.
              const meetingOccurred=!!(rawNotes&&((rawNotes.text||"").trim()||rawNotes.actionItems?.length));
              const noMeetingGuidance=(section)=>meetingOccurred?"":`
IMPORTANT: No meeting took place or no notes were captured for this session.
- Do NOT invent or fabricate any meeting discussion content.
- Do NOT generate a "${section}" section with invented content.
- For the "${section}" section, write a single sentence: "The scheduled meeting did not proceed this month."
- Build all other sections (Executive Summary, Action Items, MIP summary, etc.) from the pre-meeting form data, roadmap, and action items only.
- The Executive Summary may briefly note that the monthly meeting did not take place this period.
`;

              try{
                // Two separate calls via backend to stay within token limits
                // (Anthropic API is server-side only — cannot call from browser due to CORS)
                const baseContext=`Meeting: ${sm.title} with ${sm.client||"client"}
Date: ${sm._dateStr}
Attendees: ${(sm.attendees||[]).map(a=>a.emailAddress?.name||a.emailAddress?.address||"").filter(Boolean).join(", ")||"N/A"}

PRE-MEETING FORM:
${prepSummary||"Not available"}

MEETING INTELLIGENCE:
${mipData||"Not available"}

MEETING NOTES:
${meetingOccurred
  ?(rawNotes.text||"").slice(0,2000)
  :"⚠ NO MEETING NOTES AVAILABLE — The client did not attend this session, or no Fellow Notes were captured for this meeting."}

ACTION ITEMS:
${actionItems||"None recorded"}`;

                const clientPrompt=`You are writing a post-meeting executive summary for Datatech Solutions, a managed IT service provider. This report is sent to the client's executive team and general manager as a record of the meeting.

${baseContext}

TONE AND FRAMING GUIDELINES — READ CAREFULLY:
- This report represents Datatech Solutions professionally. Always frame Datatech as a proactive, capable, and responsive partner.
- Lead every section with positive outcomes, completed work, and progress made — even when challenges exist.
- Any open issues or unresolved items must be framed as "areas we are actively working on together" or "items on our shared action plan" — never as failures or ongoing problems.
- If infrastructure issues or tickets are mentioned, frame them as "Datatech's monitoring systems identified..." or "we proactively flagged..." to emphasise our vigilance.
- Use warm, collaborative language throughout: "our partnership", "together", "we've achieved", "Datatech is committed to".
- The client should finish reading this feeling confident, valued, and well-supported — not concerned.
- Do NOT list problems without immediately following them with what is being done or has been done.
- Sentiment scores below 7 should not be mentioned; focus on the qualitative relationship instead.
- Keep an optimistic, forward-looking close that reinforces the value of the partnership.
${noMeetingGuidance("Key Topics Discussed")}
Write the report in professional markdown. Include these sections:
## Executive Summary (2 short paragraphs maximum — keep it tight and confident; no more than 4 sentences per paragraph)
## Key Topics Discussed
## Action Items (frame as "Next Steps" — shared responsibilities, not Datatech shortcomings)
## Infrastructure & Systems Update (lead with "systems are being actively monitored and maintained")

Return only the markdown report, no preamble, no JSON.`;

                const mgmtPrompt=`You are writing a post-meeting executive report for Datatech Solutions, a managed IT service provider. This report is shared with the client's head office executive team and general manager following their monthly technology catchup.

${baseContext}

TONE AND FRAMING — THIS IS CRITICAL:
- Datatech Solutions is the trusted IT partner. This report must reinforce that trust.
- Always lead with what has gone well, what has been delivered, and what value Datatech is adding.
- Open tickets, alerts, or unresolved items must be framed as evidence of Datatech's proactive monitoring — NOT as service failures. e.g. "Our monitoring identified X and we are actively resolving it" rather than "X is broken".
- Outstanding action items should be presented as a shared forward plan — not a backlog of failures.
- If the pre-meeting form shows any "No" answers, do NOT highlight them bluntly. Instead, acknowledge "we are working closely with the team to address [area]" and move on.
- Infrastructure findings should be framed as "Datatech's systems are actively monitored and maintained; we have identified the following areas for attention" — never as failures or problems.
- Avoid clinical or harsh language. Replace "issues", "problems", and "failures" with "areas of attention", "items we are actively progressing", and "opportunities for improvement".
- The executive team reading this should feel confident that their IT is in expert hands and that Datatech is a proactive, communicative partner — not a reactive one cleaning up problems.
- Close on a forward-looking, positive note about the partnership.
${noMeetingGuidance("Key Outcomes & Discussions")}
Write the report in professional markdown. Include these sections:
## Executive Summary (2 short paragraphs maximum — lead with achievements, close with confidence; no more than 4 sentences per paragraph)
## Partnership Health (emphasise collaboration, communication quality, and trust)
## Key Outcomes & Discussions (what was covered and agreed upon)
## Infrastructure & Systems Update (frame as active monitoring and maintenance, not problem-listing)
## Shared Action Plan (collaborative language; present as a joint plan, not a list of failures)

IMPORTANT: After the visible client-facing sections above, append an internal section separated by ---:
## [INTERNAL] Commercial Opportunities
## [INTERNAL] Recommended Follow-ups
This internal section is stripped out before the report reaches the client. Return only the markdown report, no JSON.`;

                const [clientRes, mgmtRes] = await Promise.all([
                  fetch(`${_ab}/api/ai/brief`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},body:JSON.stringify({rawPrompt:true,customPrompt:clientPrompt,voice:"client"})}),
                  fetch(`${_ab}/api/ai/brief`,{method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},body:JSON.stringify({rawPrompt:true,customPrompt:mgmtPrompt,voice:"client"})})
                ]);

                if(!clientRes.ok||!mgmtRes.ok)throw new Error(`AI request failed: ${!clientRes.ok?clientRes.status:mgmtRes.status}`);

                const [cd, md] = await Promise.all([clientRes.json(), mgmtRes.json()]);
                const clientReport = cd.text||cd.content||"";
                const managementReport = md.text||md.content||"";

                // Publish to blob and get shareable URL
                const pubRes=await fetch(`${_ab}/api/report/publish`,{
                  method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},
                  body:JSON.stringify({
                    meetingTitle:sm.title,clientName:sm.client||sm.title,meetingDate:sm._dateStr,
                    clientReport,managementReport,
                    prepForm:postReportPrep,mip:postReportBrief,
                    actionItems:rawNotes?.actionItems||[],
                    fellowNotes:rawNotes?.text||null, // ⚠️ DO NOT REMOVE — needed for Fellow Notes accordion in meeting-report.html
                  })
                });
                const pubData=await pubRes.json();
                if(pubRes.ok&&pubData.token){
                  const url=`${window.location.origin}/meeting-report.html?token=${pubData.token}`;
                  const newUrls={...stateRef.current.meetingReportUrls,[sm.id]:{url,token:pubData.token,generatedAt:new Date().toISOString(),clientName:sm.client||sm.title,meetingDate:sm._dateStr}};
                  if(sm.iCalUID)newUrls[sm.iCalUID]=newUrls[sm.id];
                  setMeetingReportUrls(newUrls);
                  saveSnapshot({meetingReportUrls:newUrls});
                  navigator.clipboard.writeText(url).catch(()=>{});
                } else {
                  // Publish failed — show specific error
                  alert("Report was generated but could not be published.\nError: "+(pubData.message||pubData.error||`Server returned ${pubRes.status}`)+"\n\nPlease check the backend logs.");
                }
              }catch(e){
                // Show the actual error so it's not silent
                alert("Report generation failed.\n\nError: "+e.message+"\n\nIf this keeps happening, check the browser console for details.");
              }
              finally{setPostReportGenerating(false);}
            };

            return(
              <div className="focus-area" style={{display:"flex",flexDirection:"column",overflow:"hidden"}}>
                {/* Header */}
                <div style={{padding:"13px 18px 10px",borderBottom:"1px solid var(--border)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:18,color:"var(--text)",marginBottom:2}}>Post Meeting Reporting</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>Monthly Tech Catchup reports · last 6 months</div>
                  </div>
                </div>

                <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                  {/* Left: Meeting list */}
                  <div style={{width:260,flexShrink:0,borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
                    <div style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)"}}>
                      MONTHLY TECH CATCHUPS
                    </div>
                    <div className="scroll">
                      {meetings.length===0&&(
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,padding:30,textAlign:"center"}}>
                          <div style={{width:24,height:24,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                          <div style={{fontSize:11,color:"var(--text3)"}}>Loading calendar…</div>
                        </div>
                      )}
                      {meetings.length>0&&mtcMeetings.length===0&&(
                        <div style={{padding:20,fontSize:11,color:"var(--text3)",textAlign:"center"}}>No Monthly Tech Catchups in the last 6 months.</div>
                      )}
                      {mtcMeetings.map(m=>{
                        const isSelected=postReportMeeting?.id===m.id;
                        const hasReport=meetingReportUrls[m.id]||meetingReportUrls[m.iCalUID];
                        const hasNotes=meetingRawNotes[m.id]||meetingRawNotes[m.iCalUID];
                        return(
                          <div key={m.id} onClick={()=>loadPostReportData(m)}
                            style={{padding:"11px 14px",borderBottom:"1px solid var(--border)",cursor:"pointer",background:isSelected?"var(--strat-d)":"white",borderLeft:isSelected?"3px solid var(--strat)":"3px solid transparent",transition:"all 0.15s"}}
                            onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background="var(--s2)";}}
                            onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background="white";}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:3}}>
                              <div style={{fontWeight:600,fontSize:12,color:isSelected?"var(--strat)":"var(--text)",lineHeight:1.3}}>{m.client||m.title}</div>
                              <span style={{fontSize:9,color:m._daysAgo<=3?"var(--ops)":"var(--text3)",fontWeight:600,flexShrink:0}}>{m._daysAgo}d ago</span>
                            </div>
                            <div style={{fontSize:10,color:"var(--text3)",marginBottom:2}}>{new Date(m._dateStr+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"})}{m.startTime&&` · ${m.startTime}`}</div>
                            <div style={{display:"flex",gap:6,marginTop:3,flexWrap:"wrap"}}>
                              {hasNotes&&<span style={{fontSize:8,color:"var(--fellow)",fontWeight:700}}>📝 Notes</span>}
                              {hasReport&&<span style={{fontSize:8,color:"var(--ops)",fontWeight:700}}>📊 Report</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right: Full meeting data + generate button */}
                  {!sm&&(
                    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,color:"var(--text3)"}}>
                      <div style={{fontSize:32}}>📊</div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text2)"}}>Select a meeting to view its data</div>
                      <div style={{fontSize:11}}>Pre-meeting form · MIP · Fellow notes · Action items</div>
                    </div>
                  )}

                  {sm&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

                      {/* Meeting header — info only, no button here */}
                      <div style={{padding:"10px 18px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"var(--s2)"}}>
                        <div style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>{sm.client||sm.title}</div>
                        <div style={{fontSize:11,color:"var(--text3)",display:"flex",gap:10,marginTop:1,flexWrap:"wrap"}}>
                          <span>{sm.title}</span>
                          <span>📅 {new Date(sm._dateStr+"T12:00:00").toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</span>
                          {sm.startTime&&<span>⏰ {sm.startTime}</span>}
                        </div>
                      </div>

                      {/* Scrollable data panel */}
                      <div className="scroll" style={{padding:"18px 22px",display:"flex",flexDirection:"column",gap:20}}>

                        {postReportLoading&&(
                          <div style={{display:"flex",gap:8,alignItems:"center",fontSize:11,color:"var(--text3)"}}>
                            <div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/>Loading meeting data…
                          </div>
                        )}

                        {/* ── Generate Report section — always visible ── */}
                        <div style={{background:"var(--s2)",borderRadius:10,border:"2px solid var(--border)",padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:700,fontSize:13,color:"var(--text)",marginBottom:4}}>📊 Post Meeting Report</div>
                            <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
                              {postReportGenerating
                                ?"Generating client report and management report using all meeting data. This may take 30–60 seconds…"
                                :savedReport
                                  ?"Report generated — open or share the link below. Click Regenerate to create a new version."
                                  :"Click Generate Report to create a client-facing report and an internal management report using all available meeting data."}
                            </div>
                            {savedReport&&!postReportGenerating&&(
                              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,flexWrap:"wrap"}}>
                                <span style={{fontSize:10,color:"var(--ops)",fontWeight:700}}>✓ Report ready</span>
                                <a href={savedReport.url} target="_blank" rel="noopener noreferrer"
                                  style={{fontSize:11,color:"white",fontWeight:600,textDecoration:"none",background:"var(--strat)",padding:"4px 12px",borderRadius:5,display:"inline-flex",alignItems:"center",gap:4}}>
                                  🔗 Open Report →
                                </a>
                                <button className="btn" style={{fontSize:10,padding:"4px 10px"}}
                                  onClick={(e)=>{navigator.clipboard.writeText(savedReport.url);const btn=e.target;const orig=btn.textContent;btn.textContent="✓ Copied!";btn.style.color="var(--ops)";setTimeout(()=>{btn.textContent=orig;btn.style.color="";},2000);}}>
                                  Copy link
                                </button>
                                <span style={{fontSize:10,color:"var(--text3)"}}>
                                  Generated {new Date(savedReport.generatedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                                </span>
                              </div>
                            )}
                          </div>
                          <button className="btn btn-S"
                            style={{background:postReportGenerating?"var(--s3)":"var(--strat)",color:postReportGenerating?"var(--text3)":"white",borderColor:postReportGenerating?"var(--border)":"var(--strat)",fontSize:11,padding:"9px 20px",gap:6,flexShrink:0,minWidth:160,justifyContent:"center",opacity:postReportGenerating?0.7:1}}
                            disabled={postReportGenerating}
                            onClick={generateAndPublish}>
                            {postReportGenerating
                              ?<><div className="ai-dot" style={{background:"var(--text3)"}}/><div className="ai-dot" style={{background:"var(--text3)"}}/><div className="ai-dot" style={{background:"var(--text3)"}}/>Generating…</>
                              :savedReport?"↻ Regenerate Report":"◆ Generate Report"}
                          </button>
                        </div>

                        {/* ── Pre-meeting form ── */}
                        <div>
                          <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--strat)",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                            ✅ Pre-Meeting Form
                            {postReportPrep?.submittedAt&&<span style={{fontSize:9,fontWeight:400,color:"var(--text3)",textTransform:"none",letterSpacing:0}}>Submitted {new Date(postReportPrep.submittedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>}
                          </div>
                          {postReportPrep?(()=>{
                            const qs=(postReportPrep.questions||[]).filter(q=>{const a=postReportPrep.answers?.[q.id];return a!==undefined&&a!==null&&a!=="";});
                            const grouped={};qs.forEach(q=>{const s=q.section||"General";if(!grouped[s])grouped[s]=[];grouped[s].push(q);});
                            return(
                              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                                {Object.entries(grouped).map(([sec,items])=>(
                                  <div key={sec} style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border)"}}>
                                    <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--strat)",marginBottom:7,borderBottom:"1px solid var(--border)",paddingBottom:5}}>{sec}</div>
                                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                                      {items.map(q=>{const ans=postReportPrep.answers[q.id];const isText=q.type==="textarea"||q.type==="text";const color=ans==="yes"?"var(--ops)":ans==="no"?"var(--danger)":"var(--text)";
                                        return isText?(
                                          <div key={q.id} style={{marginTop:4,paddingTop:5,borderTop:"1px solid var(--border)"}}>
                                            <div style={{fontSize:9,color:"var(--text3)",marginBottom:2}}>{q.label}</div>
                                            <div style={{fontSize:11,color:"var(--text)",lineHeight:1.5,whiteSpace:"pre-wrap"}}>{String(ans)}</div>
                                          </div>
                                        ):(
                                          <div key={q.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6}}>
                                            <div style={{fontSize:10,color:"var(--text2)",flex:1,lineHeight:1.3}}>{q.label}</div>
                                            <div style={{flexShrink:0,fontWeight:700,color,fontSize:11}}>{ans==="yes"?"✓ Yes":ans==="no"?"✗ No":String(ans)}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })():(
                            !postReportLoading&&<div style={{padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--border)",fontSize:11,color:"var(--text3)"}}>No pre-meeting form response found for this meeting.</div>
                          )}
                        </div>

                        {/* ── MIP ── */}
                        <div>
                          <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--tact)",marginBottom:8}}>◆ Meeting Intelligence Pack</div>
                          {postReportBrief?(
                            <div style={{display:"flex",flexDirection:"column",gap:10}}>
                              {postReportBrief.snapshot&&(
                                <div style={{background:"var(--tact-d,#e8f4fb)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--tact)",fontSize:12,color:"var(--text)",lineHeight:1.7}}>{postReportBrief.snapshot}</div>
                              )}
                              {postReportBrief.sentiment&&(
                                <div style={{display:"flex",alignItems:"center",gap:12,background:"var(--s2)",borderRadius:8,padding:"10px 14px",border:"1px solid var(--border)"}}>
                                  <span style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--text3)"}}>SENTIMENT</span>
                                  <span style={{fontWeight:800,fontSize:20,color:postReportBrief.sentiment.score>=7?"var(--ops)":postReportBrief.sentiment.score>=5?"var(--cal)":"var(--danger)"}}>{postReportBrief.sentiment.score}<span style={{fontSize:10,fontWeight:400,color:"var(--text3)"}}>/10</span></span>
                                  <span style={{fontSize:11,color:"var(--text2)"}}>{postReportBrief.sentiment.summary}</span>
                                </div>
                              )}
                              <div className="mip-grid">
                                {[{key:"openIssues",hdr:"🎫 Open Issues"},{key:"clientFeedback",hdr:"📋 Client Feedback"},{key:"infrastructure",hdr:"🖥️ Infrastructure"},{key:"outstandingActions",hdr:"✅ Outstanding Actions"}].map(({key,hdr})=>(
                                  (postReportBrief[key]||[]).length>0&&(
                                    <div key={key} className="mip-card">
                                      <div className="mip-card-hdr">{hdr}</div>
                                      <div className="mip-card-body">{(postReportBrief[key]||[]).map((item,i)=><div key={i} className="mip-bullet">• {String(item).replace(/^[-•*]\s*/,"")}</div>)}</div>
                                    </div>
                                  )
                                ))}
                              </div>
                              {(postReportBrief.talkingPoints||[]).length>0&&(
                                <div className="mip-tp-section">
                                  <div className="mip-tp-hdr">🎯 Talking Points</div>
                                  <div className="mip-tp-body">
                                    {postReportBrief.talkingPoints.map((tp,i)=>(
                                      <div key={i} className="mip-tp-item"><div className="mip-tp-num">{i+1}.</div><div>{tp.replace(/^\d+\.\s*/,"")}</div></div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ):(
                            !postReportLoading&&<div style={{padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--border)",fontSize:11,color:"var(--text3)"}}>No MIP found. Generate one in Meeting Notes first.</div>
                          )}
                        </div>

                        {/* ── Action Items ── */}
                        <div>
                          <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--ops)",marginBottom:8}}>✅ Action Items from Meeting</div>
                          {rawNotes?.actionItems?.length?(
                            <div style={{display:"flex",flexDirection:"column",gap:6}}>
                              {rawNotes.actionItems.map((a,i)=>(
                                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,background:"var(--s2)",borderRadius:6,padding:"8px 12px",border:"1px solid var(--border)"}}>
                                  <span style={{fontSize:12,marginTop:1}}>•</span>
                                  <div>
                                    <div style={{fontSize:12,fontWeight:500,color:"var(--text)"}}>{a.title||String(a)}</div>
                                    {(a.priority||a.owner)&&<div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{[a.priority&&`Priority: ${a.priority}`,a.owner&&a.owner!=="unknown"&&`Owner: ${a.owner}`].filter(Boolean).join(" · ")}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ):(
                            !postReportLoading&&<div style={{padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--border)",fontSize:11,color:"var(--text3)"}}>No action items saved for this meeting. Import from Fellow Notes first.</div>
                          )}
                        </div>

                        {/* ── Fellow raw notes ── */}
                        <div>
                          <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--fellow)",marginBottom:8}}>
                            📝 Fellow Meeting Notes
                            {rawNotes?.savedAt&&<span style={{fontSize:9,fontWeight:400,color:"var(--text3)",marginLeft:6,textTransform:"none",letterSpacing:0}}>Saved {new Date(rawNotes.savedAt).toLocaleDateString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>}
                          </div>
                          {rawNotes?.text?(
                            <div style={{background:"var(--s2)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--border)",fontSize:11,color:"var(--text2)",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{rawNotes.text}</div>
                          ):(
                            !postReportLoading&&<div style={{padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--border)",fontSize:11,color:"var(--text3)",lineHeight:1.7}}>No Fellow meeting notes saved for this meeting. <strong style={{color:"var(--fellow)"}}>To fix:</strong> Go to Fellow Notes Import, click <strong>↺ Reset</strong> on this meeting card, re-import the notes, assign to this calendar event in the dropdown, then click Commit. The notes will then appear here.<button className="btn" style={{fontSize:10,padding:"2px 10px",marginLeft:8}} onClick={()=>setTab("notes")}>Go to Fellow Notes Import →</button></div>
                          )}
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── TO-DO TAB ── */}
          {tab==="fellowticketconfig"&&(
            <div className="focus-area">
              <div style={{padding:"13px 18px 12px",borderBottom:"1px solid var(--border)",flexShrink:0}}>
                <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)",letterSpacing:"-0.3px",marginBottom:3}}>Meetings & Notes Settings</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>Default Autotask ticket fields used when you click Commit in Fellow Notes Import. These apply to every meeting ticket — customer, contact, contract, and summary are resolved automatically per meeting.</div>
              </div>
              <div className="scroll" style={{flex:1,padding:"18px 20px",display:"flex",flexDirection:"column",gap:18}}>
                {/* Info card */}
                <div style={{background:"rgba(52,152,219,0.06)",border:"1px solid rgba(52,152,219,0.2)",borderRadius:8,padding:"10px 14px",fontSize:11,color:"var(--text2)",lineHeight:1.7}}>
                  <strong>How this works:</strong> When you click <strong>✓ Commit … to To-Do · Save to meeting</strong>, MSP OS automatically creates a completed Autotask ticket and time entry using the settings below. The ticket title is always <em>Monthly Tech Catchup</em>, priority is <em>P3 Medium</em>, status is <em>Complete</em>, and the primary resource is the logged-in user. Duration comes from the Fellow meeting length.
                </div>

                {/* Settings form */}
                <div style={{borderBottom:"1px solid var(--border)",paddingBottom:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:12,letterSpacing:"-0.2px"}}>Ticket Default Settings</div>
                  {(()=>{
                  const fts=fellowTicketSettings;
                  const pl=ticketPicklists;
                  const mkRow=(label,hint,children)=>(
                    <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:"0 16px",alignItems:"start"}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)",marginBottom:2}}>{label}</div>
                        <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.4}}>{hint}</div>
                      </div>
                      <div>{children}</div>
                    </div>
                  );
                  const mkSelect=(field,options,loading)=>(
                    <select
                      value={fts[field]||""}
                      onChange={e=>{
                        const next={...fts,[field]:e.target.value};
                        setFellowTicketSettings(next);
                        saveSnapshot({fellowTicketSettings:next});
                      }}
                      style={{width:"100%",fontSize:11,padding:"5px 8px",borderRadius:5,border:"1px solid var(--border)",background:"white",fontFamily:"var(--ff-head)",color:"var(--text)"}}>
                      {loading&&<option value="">Loading…</option>}
                      {!loading&&options.map(o=>(
                        <option key={o.value} value={String(o.value)}>{o.label}</option>
                      ))}
                    </select>
                  );

                  const issueOpts=pl?.issueTypes||[];
                  const subIssueOpts=pl?.subIssueTypes||[];
                  // Filter sub-issues to only those whose parentValue matches the selected issueType.
                  // When the user changes issueType, sub-issue resets to the first valid option
                  // so AT never gets a mismatched pair.
                  const filteredSubIssueOpts=fts.issueType
                    ? subIssueOpts.filter(s=>String(s.parentValue)===String(fts.issueType))
                    : subIssueOpts;
                  const sourceOpts=pl?.sources||[];
                  const queueOpts=pl?.queues||[];
                  const loading=!pl;

                  return(
                    <>
                      {mkRow("Issue Type","Category for the Autotask ticket.",
                        <select
                          value={fts.issueType||""}
                          onChange={e=>{
                            const newIssue=e.target.value;
                            // Auto-reset sub-issue to the first valid option for the new issue type
                            const newSubOpts=(pl?.subIssueTypes||[]).filter(s=>String(s.parentValue)===String(newIssue));
                            const newSubIssue=newSubOpts.length>0?String(newSubOpts[0].value):"";
                            const next={...fts,issueType:newIssue,subIssueType:newSubIssue};
                            setFellowTicketSettings(next);
                            saveSnapshot({fellowTicketSettings:next});
                          }}
                          style={{width:"100%",fontSize:11,padding:"5px 8px",borderRadius:5,border:"1px solid var(--border)",background:"white",fontFamily:"var(--ff-head)",color:"var(--text)"}}>
                          {loading&&<option value="">Loading…</option>}
                          {!loading&&issueOpts.map(o=>(
                            <option key={o.value} value={String(o.value)}>{o.label}</option>
                          ))}
                        </select>
                      )}
                      {mkRow("Sub-Issue Type",`Subcategory — showing ${filteredSubIssueOpts.length} option${filteredSubIssueOpts.length===1?"":"s"} for the selected Issue Type.`,
                        <select
                          value={fts.subIssueType||""}
                          onChange={e=>{
                            const next={...fts,subIssueType:e.target.value};
                            setFellowTicketSettings(next);
                            saveSnapshot({fellowTicketSettings:next});
                          }}
                          style={{width:"100%",fontSize:11,padding:"5px 8px",borderRadius:5,border:"1px solid var(--border)",background:"white",fontFamily:"var(--ff-head)",color:"var(--text)"}}>
                          {loading&&<option value="">Loading…</option>}
                          {!loading&&filteredSubIssueOpts.length===0&&<option value="">— No sub-issues for this type —</option>}
                          {!loading&&filteredSubIssueOpts.map(o=>(
                            <option key={o.value} value={String(o.value)}>{o.label}</option>
                          ))}
                        </select>
                      )}
                      {mkRow("Source","How the ticket was initiated (default: Email).",mkSelect("source",sourceOpts.map(o=>({value:String(o.value),label:o.label})),loading))}
                      {mkRow("Queue","Which AT queue the ticket is created in.",mkSelect("queueId",queueOpts.map(o=>({value:String(o.value),label:o.label})),loading))}
                      {mkRow("Work Type (Billing Code)","Billing code for the time entry. Leave blank to omit.",
                        <div>
                          <select
                            value={fts.workTypeId||""}
                            onChange={e=>{
                              const next={...fts,workTypeId:e.target.value};
                              setFellowTicketSettings(next);
                              saveSnapshot({fellowTicketSettings:next});
                            }}
                            style={{width:"100%",fontSize:11,padding:"5px 8px",borderRadius:5,border:"1px solid var(--border)",background:"white",fontFamily:"var(--ff-head)",color:"var(--text)"}}>
                            <option value="">— No billing code —</option>
                            {/* Work types come from the tickets/reference endpoint which is loaded
                                separately. For now we show the known Remote Support option from the
                                AT instance + any others if ticketPicklists exposes them. */}
                            {(pl?.workTypes||[]).map(o=>(
                              <option key={o.id||o.value} value={String(o.id||o.value)}>{o.name||o.label}</option>
                            ))}
                          </select>
                          <div style={{fontSize:10,color:"var(--text3)",marginTop:3}}>Work types are fetched from the AT billing codes list. If this dropdown is empty, the billing code will be omitted from time entries.</div>
                        </div>
                      )}
                    </>
                  );
                })()}
                </div>

                {/* Fixed fields — shown for info only */}
                <div style={{borderTop:"1px solid var(--border)",paddingTop:14}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:10}}>Fixed fields (not configurable)</div>
                  <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:"6px 16px",fontSize:11,color:"var(--text3)"}}>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Title</span><span>Monthly Tech Catchup</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Priority</span><span>P3 | Moderate/Routine</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Status</span><span>Complete</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Primary Resource</span><span>Logged-in user (resolved from your account email)</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Role</span><span>Resource's default AT role</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Customer</span><span>Resolved from calendar meeting attendees</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Contact</span><span>Company's primary active AT contact</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Contract</span><span>Company's active Gold Fortress (type 7) contract</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Ticket Description</span><span>AI Summary from Fellow meeting notes</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Time Entry Notes</span><span>AI Summary from Fellow meeting notes</span>
                    <span style={{fontWeight:600,color:"var(--text)"}}>Duration</span><span>From Fellow meeting length (start → end time)</span>
                  </div>
                </div>

                {/* Test/reset */}
                <div style={{display:"flex",gap:8,paddingTop:4}}>
                  {!ticketPicklists&&(
                    <button className="btn" style={{fontSize:11}}
                      onClick={()=>{
                        const tok=window.__mspToken;
                        const ab=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                        fetch(`${ab}/api/tickets/picklists`,{headers:{Authorization:`Bearer ${tok}`}})
                          .then(r=>r.json()).then(d=>{if(d&&!d.error)setTicketPicklists(d);else alert("Could not load picklists: "+JSON.stringify(d));})
                          .catch(e=>alert("Picklist fetch failed: "+e.message));
                      }}>↺ Load picklists</button>
                  )}
                  <button className="btn" style={{fontSize:11}}
                    onClick={()=>{
                      const defaults={issueType:"17",subIssueType:"240",source:"4",queueId:"29683378",workTypeId:""};
                      setFellowTicketSettings(defaults);
                      saveSnapshot({fellowTicketSettings:defaults});
                    }}>Reset to defaults</button>
                </div>
              </div>
            </div>
          )}

          {/* ── TO-DO TAB ── */}
          {tab==="todos"&&(
            <div className="focus-area">
              <div style={{padding:"13px 18px 12px",borderBottom:"1px solid var(--border)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:18,letterSpacing:"-0.4px",color:"var(--text)",marginBottom:4}}>Action Items</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>
                    {todos.filter(t=>!t.done).length} open · {overdueTodosCount>0&&<span style={{color:"var(--danger)"}}>{overdueTodosCount} overdue · </span>}{todayTodosCount} due today
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-S" style={{fontSize:11,padding:"6px 14px",background:"var(--strat)",color:"white",borderColor:"var(--strat)"}}
                    onClick={()=>setAddTodoModal(true)}>+ Add Action Item</button>
                  <button className="btn btn-fellow" style={{fontSize:10}} onClick={()=>setTab("notes")}>+ From meeting notes</button>
                </div>
              </div>

              <div className="scroll">
                {/* Overdue section */}
                {todos.filter(t=>!t.done&&t.dueDate<todayISO()).length>0&&(
                  <>
                    <div style={{padding:"8px 14px 4px",fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--danger)",display:"flex",alignItems:"center",gap:6,fontWeight:600}}>
                      ⚠ Overdue
                    </div>
                    {todos.filter(t=>!t.done&&t.dueDate<todayISO()).map(t=>(<TodoCard key={t.id} t={t} expanded={expandedTodo===t.id} onToggleExpand={()=>setExpandedTodo(expandedTodo===t.id?null:t.id)} onToggle={toggleTodo} onUpdateNotes={updateTodoNotes} onUpdatePriority={updateTodoPriority} onUpdateDate={updateTodoDate} onReschedule={openReschedule} onDelete={deleteTodo} onCreateTicket={openTicketFromTodo} atConnected={atConnected} priClass={priClass} todoDateClass={todoDateClass} todoItemClass={todoItemClass}/>))}
                  </>
                )}
                {/* Today */}
                {todos.filter(t=>!t.done&&t.dueDate===todayISO()).length>0&&(
                  <>
                    <div style={{padding:"8px 14px 4px",fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--strat)"}}>Today</div>
                    {todos.filter(t=>!t.done&&t.dueDate===todayISO()).map(t=>(<TodoCard key={t.id} t={t} expanded={expandedTodo===t.id} onToggleExpand={()=>setExpandedTodo(expandedTodo===t.id?null:t.id)} onToggle={toggleTodo} onUpdateNotes={updateTodoNotes} onUpdatePriority={updateTodoPriority} onUpdateDate={updateTodoDate} onReschedule={openReschedule} onDelete={deleteTodo} onCreateTicket={openTicketFromTodo} atConnected={atConnected} priClass={priClass} todoDateClass={todoDateClass} todoItemClass={todoItemClass}/>))}
                  </>
                )}
                {/* Upcoming */}
                {todos.filter(t=>!t.done&&t.dueDate>todayISO()).length>0&&(
                  <>
                    <div style={{padding:"8px 14px 4px",fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--text3)"}}>Upcoming</div>
                    {todos.filter(t=>!t.done&&t.dueDate>todayISO()).sort((a,b)=>a.dueDate.localeCompare(b.dueDate)).map(t=>(<TodoCard key={t.id} t={t} expanded={expandedTodo===t.id} onToggleExpand={()=>setExpandedTodo(expandedTodo===t.id?null:t.id)} onToggle={toggleTodo} onUpdateNotes={updateTodoNotes} onUpdatePriority={updateTodoPriority} onUpdateDate={updateTodoDate} onReschedule={openReschedule} onDelete={deleteTodo} onCreateTicket={openTicketFromTodo} atConnected={atConnected} priClass={priClass} todoDateClass={todoDateClass} todoItemClass={todoItemClass}/>))}
                  </>
                )}
                {/* Done */}
                {todos.filter(t=>t.done).length>0&&(
                  <>
                    <div style={{padding:"8px 14px 4px",fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--text3)"}}>Completed</div>
                    {todos.filter(t=>t.done).map(t=>(<TodoCard key={t.id} t={t} expanded={expandedTodo===t.id} onToggleExpand={()=>setExpandedTodo(expandedTodo===t.id?null:t.id)} onToggle={toggleTodo} onUpdateNotes={updateTodoNotes} onUpdatePriority={updateTodoPriority} onUpdateDate={updateTodoDate} onReschedule={openReschedule} onDelete={deleteTodo} onCreateTicket={openTicketFromTodo} atConnected={atConnected} priClass={priClass} todoDateClass={todoDateClass} todoItemClass={todoItemClass}/>))}
                  </>
                )}
                {todos.length===0&&(
                  <div style={{padding:"40px 20px",textAlign:"center",color:"var(--text3)",fontSize:11,lineHeight:1.7}}>
                    No action items yet.<br/>Click <strong>+ Add Action Item</strong> to add one manually,<br/>or go to <span style={{color:"var(--fellow)",cursor:"pointer"}} onClick={()=>setTab("notes")}>Fellow Notes Import</span> to extract from meeting notes.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ADD ACTION ITEM MODAL ── */}
          {addTodoModal&&(
            <div className="todo-modal-overlay" onClick={e=>{if(e.target.className==="todo-modal-overlay")setAddTodoModal(false);}}>
              <div className="todo-modal">
                <div className="todo-modal-hdr">
                  <div>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)"}}>
                      {newTodo._sourceEmail ? "📧 Create To-Do from Email" : "Add Action Item"}
                    </div>
                    {newTodo._sourceEmail&&<div style={{fontSize:10,color:"var(--ops)",marginTop:2}}>Pre-filled from email — edit as needed before saving</div>}
                  </div>
                  <button className="ico-btn" onClick={()=>{setAddTodoModal(false);setNewTodo({title:"",notes:"",dueDate:"",priority:"med",layer:"O",client:""});}}>×</button>
                </div>
                <div className="todo-modal-body">
                  <div className="todo-field">
                    <label>Title *</label>
                    <input className="inp" placeholder="What needs to be done?"
                      value={newTodo.title}
                      onChange={e=>setNewTodo(n=>({...n,title:e.target.value}))}
                      autoFocus
                      onKeyDown={e=>e.key==="Enter"&&newTodo.title.trim()&&addManualTodo()}
                    />
                  </div>
                  <div className="todo-field">
                    <label>Description / Notes</label>
                    <textarea className="inp" rows={3} placeholder="Additional context, links, or details..."
                      value={newTodo.notes}
                      onChange={e=>setNewTodo(n=>({...n,notes:e.target.value}))}
                      style={{resize:"vertical",minHeight:72}}
                    />
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div className="todo-field">
                      <label>Due Date</label>
                      <input className="inp" type="date"
                        value={newTodo.dueDate}
                        onChange={e=>setNewTodo(n=>({...n,dueDate:e.target.value}))}
                      />
                    </div>
                    <div className="todo-field">
                      <label>Priority</label>
                      <select className="inp" value={newTodo.priority}
                        onChange={e=>setNewTodo(n=>({...n,priority:e.target.value}))}>
                        <option value="high">🔴 High</option>
                        <option value="med">🟡 Medium</option>
                        <option value="low">🟢 Low</option>
                      </select>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div className="todo-field">
                      <label>Layer</label>
                      <select className="inp" value={newTodo.layer}
                        onChange={e=>setNewTodo(n=>({...n,layer:e.target.value}))}>
                        <option value="S">Strategic</option>
                        <option value="T">Tactical</option>
                        <option value="O">Operational</option>
                      </select>
                    </div>
                    <div className="todo-field">
                      <label>Client</label>
                      <ClientTypeahead
                        value={newTodo.client}
                        onChange={v=>setNewTodo(n=>({...n,client:v}))}
                        companies={atCompanies}
                        placeholder="Search client..."
                      />
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr",gap:12}}>
                    <div className="todo-field">
                      <label>Date Added</label>
                      <input className="inp" value={new Date().toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",year:"numeric"})} disabled
                        style={{background:"var(--s2)",color:"var(--text3)",cursor:"default"}}/>
                    </div>
                  </div>
                </div>
                <div className="todo-modal-footer">
                  <button className="btn" onClick={()=>setAddTodoModal(false)}>Cancel</button>
                  <button className="btn btn-S"
                    style={{background:"var(--strat)",color:"white",borderColor:"var(--strat)",opacity:newTodo.title.trim()?1:0.5}}
                    disabled={!newTodo.title.trim()}
                    onClick={addManualTodo}>
                    Add Action Item
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── CALENDAR TAB — full width, 3-col grid, no email panel ── */}
          {tab==="calendar"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)",gridColumn:"1/-1"}}>
              {/* Header */}
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,letterSpacing:"-0.4px",color:"var(--text)",marginBottom:3}}>Client Meeting Calendar</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>Next 7 days · Prep blocks auto-suggested · <span style={{color:"var(--strat)"}}>QBR=5d</span> · <span style={{color:"var(--tact)"}}>Monthly=3d</span> · <span style={{color:"var(--ops)"}}>Ad-hoc=1d</span></div>
                </div>
                <div style={{fontSize:11,color:"var(--text3)",background:"var(--s2)",padding:"4px 10px",borderRadius:6,border:"1px solid var(--border)"}}>
                  {sortedMeetings.length} meeting{sortedMeetings.length!==1?"s":""} this week
                </div>
              </div>
              {!calConn?(
                <div style={{padding:20}}>
                  <div className="connect-box" style={{margin:0}}>
                    <div className="c-icon">📅</div>
                    <div className="c-title">Connect M365 Calendar</div>
                    <div className="c-desc">Detects client meetings and auto-schedules prep work.</div>
                    <button className="btn btn-C" onClick={connectCal}>Connect Calendar →</button>
                  </div>
                </div>
              ):(
                <div className="scroll" style={{flex:1,padding:"16px 20px"}}>
                  {sortedMeetings.length===0&&(
                    <div style={{textAlign:"center",color:"var(--text3)",fontSize:12,padding:"60px 20px",lineHeight:1.8}}>
                      <div style={{fontSize:28,marginBottom:12}}>📅</div>
                      No client meetings in the next 7 days.<br/>
                      <span style={{fontSize:11}}>Enjoy the clear week!</span>
                    </div>
                  )}
                  {/* 3-column grid */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,alignItems:"start"}}>
                    {sortedMeetings.map(m=>{
                      const d=daysUntil(m.date);
                      const cfg=PREP_CFG[m.type];
                      const prepStart=subtractWD(m.date,cfg.days);
                      const prepAdded=blocks.some(b=>b.prepFor===m.id);
                      const isPrepDue=d<=cfg.days;
                      const urgencyBg=d===0?"var(--danger-d)":d===1?"rgba(230,126,34,0.1)":d<=3?"var(--warn-d)":"var(--ops-d)";
                      const urgencyColor=d===0?"var(--danger)":d===1?"var(--cal)":d<=3?"var(--warn)":"var(--ops)";
                      const urgencyLabel=d===0?"TODAY":d===1?"Tomorrow":`${d} days`;
                      return(
                        <div key={m.id} style={{border:"1px solid var(--border)",borderRadius:10,overflow:"hidden",borderTop:`3px solid ${m.tier==="vcio"?"var(--strat)":"var(--tact)"}`,background:"white",boxShadow:"var(--shadow-sm)",display:"flex",flexDirection:"column"}}>
                          {/* Card header */}
                          <div style={{padding:"12px 14px",borderBottom:"1px solid var(--border)"}}>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                              <div style={{fontSize:10,color:"var(--text3)",fontWeight:500}}>{fmtD(m.date)}</div>
                              <div style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:4,background:urgencyBg,color:urgencyColor}}>{urgencyLabel}</div>
                            </div>
                            <div style={{fontSize:13,color:"var(--text)",fontWeight:600,lineHeight:1.3,marginBottom:8}}>{m.title||"Untitled Meeting"}</div>
                            <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                              <span className={`lb lb-${m.tier==="vcio"?"S":"T"}`}>{m.tier==="vcio"?"vCIO":"Mgd"}</span>
                              <span className={`lb lb-${cfg.layer}`}>{cfg.label}</span>
                              {m.client&&m.client!=="Internal"&&m.client!=="Datatech"&&(
                                <span style={{fontSize:9,color:"var(--text3)"}}>{m.client}</span>
                              )}
                            </div>
                          </div>
                          {/* Prep checklist */}
                          <div style={{padding:"10px 14px",background:"var(--bg)",flex:1}}>
                            <div style={{fontSize:9,color:"var(--dt-secondary)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8,fontWeight:600}}>
                              📋 {cfg.days}d prep
                              {isPrepDue&&!prepAdded&&<span style={{marginLeft:6,color:"var(--warn)",fontSize:9}}>⚠ open</span>}
                              {!isPrepDue&&<span style={{marginLeft:6,color:"var(--text3)",fontSize:9,fontWeight:400,textTransform:"none"}}>opens {fmtD(prepStart)}</span>}
                            </div>
                            {cfg.tasks.map((task,i)=>(
                              <div key={i} style={{display:"flex",alignItems:"flex-start",gap:7,padding:"5px 0",fontSize:11,color:"var(--text2)",borderBottom:i<cfg.tasks.length-1?"1px solid var(--border)":"none"}}>
                                <span className={`lb lb-${cfg.layer}`} style={{fontSize:8,padding:"1px 5px",flexShrink:0,marginTop:1}}>{LAYERS[cfg.layer].label.slice(0,3)}</span>
                                <span style={{flex:1,lineHeight:1.4}}>{task}</span>
                              </div>
                            ))}
                            <button
                              className={prepAdded?"btn btn-O":"btn btn-S"}
                              style={{fontSize:10,width:"100%",justifyContent:"center",marginTop:10,padding:"6px"}}
                              onClick={()=>{
                                if(prepAdded) return;
                                const pb=cfg.tasks.map((task,i)=>({
                                  id:`prep-${m.id}-${i}`,
                                  title:`${task}${m.client&&m.client!=="Internal"?" — "+m.client:""}`,
                                  layer:cfg.layer,start:`0${9+i}:00`,
                                  dur:m.type==="qbr"?60:45,
                                  done:false,prepFor:m.id,todoId:null
                                }));
                                setBlocks(bs=>{const next=[...bs,...pb];saveSnapshot({blocks:next});return next;});
                              }}
                            >
                              {prepAdded?"✓ Added to plan":"＋ Add to plan"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── CADENCE TAB ── */}
          {tab==="cadence"&&(()=>{
            // Cadence resolves managed clients from atCompanies — same flicker
            // risk if rendered before that cache loads. Doesn't need meetings.
            const dataReady=atCompanies.length>0;
            if(!dataReady)return(
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:40,textAlign:"center"}}>
                <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid var(--border)",borderTopColor:"var(--strat)",animation:"lspin 0.7s linear infinite"}}/>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.8}}>Loading company data…<br/><span style={{fontSize:11}}>This takes a few seconds on first load.</span></div>
              </div>
            );
            return(
            <div className="focus-area">
              <div className="cadence-full">
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                  <div>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:900,fontSize:16,letterSpacing:"-0.5px",color:"var(--text)",marginBottom:3}}>Cadence Framework</div>
                    <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.6}}>Strategic drives retention. Tactical shows ownership. Operational is execution.</div>
                  </div>
                  <div style={{display:"flex",gap:8,flexShrink:0}}>
                    <button className="btn" style={{fontSize:10,padding:"4px 12px",whiteSpace:"nowrap",background:"var(--strat-d)",color:"var(--strat)",border:"1px solid var(--strat)"}}
                      onClick={resetStrategicItems}
                      title="Clear Strategic layer and regenerate from managed clients only">
                      ↺ Reset Strategic
                    </button>
                    <button className="btn" style={{fontSize:10,padding:"4px 12px",whiteSpace:"nowrap",background:"rgba(230,126,34,0.1)",color:"var(--ops)",border:"1px solid var(--ops)"}}
                      onClick={consolidateOperationalItems}
                      title="Replace per-client Operational items with consolidated weekly entries spread Mon–Thu">
                      ↺ Consolidate Ops
                    </button>
                    <button className="btn btn-S" style={{fontSize:10,padding:"4px 12px",whiteSpace:"nowrap"}}
                      onClick={generateCadenceFromClients}
                      title="Rebuild all cadence items from managed clients">
                      ⚡ Generate from Clients
                    </button>
                  </div>
                </div>
                {/* Managed clients notice */}
                <div style={{fontSize:10,color:"var(--text3)",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                  {managedCompanyIds.size>0
                    ?<><span style={{background:"rgba(11,128,165,0.1)",color:"var(--strat)",fontWeight:600,padding:"1px 6px",borderRadius:3,fontSize:9}}>{managedCompanyIds.size} managed</span>
                      <span>Showing managed clients only — tick Managed in Settings → Client Register to include clients</span></>
                    :<><span style={{background:"#fef3c7",color:"#92400e",fontWeight:600,padding:"1px 6px",borderRadius:3,fontSize:9}}>⚠ No clients selected</span>
                      <span>No managed clients selected — go to Settings → Client Register and tick the Managed checkbox</span></>
                  }
                </div>

                {/* Three-column cadence layout */}
                <div className="cadence-cols">
                  {Object.entries(cadence).map(([key,cat])=>{
                    // Filter items to managed clients only (from Client Register Managed checkbox)
                    const managedNames=new Set(atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).map(c=>c.companyName));
                    const managedItems=cat.items.filter(i=>i.client==="All"||managedNames.has(i.client)||i.client==="vCIO"||managedNames.size===0);
                    // Fuzzy-match so slight name discrepancies don't silently drop calendar meetings.
                    const clientMatchesManagedName=(clientStr)=>{
                      if(managedNames.size===0) return true;
                      if(managedNames.has(clientStr)) return true;
                      const cl=clientStr.toLowerCase();
                      for(const n of managedNames){const nl=n.toLowerCase();if(nl.includes(cl)||cl.includes(nl)) return true;}
                      return false;
                    };
                    const calItems=key==="T"?upcomingMeetingPreps.filter(p=>clientMatchesManagedName(p.client)):[];
                    const doneCount=managedItems.filter(i=>i.done).length+(key==="T"?calItems.filter(p=>cadence.T?.items?.find(i=>i.id===`cal-mtg-${p.id}`)?.done).length:0);
                    const totalCount=managedItems.length+(key==="T"?calItems.length:0);
                    return(
                      <div key={key} className="cadence-col">
                        {/* Column header */}
                        <div className={`cad-sh h${key}`} style={{borderRadius:"9px 9px 0 0",flexShrink:0}}>
                          <div>
                            <div className={`cad-st t${key}`}>{cat.label}</div>
                            <div className="cad-sub">{cat.freq} · {cat.desc.split("·")[0].trim()}</div>
                          </div>
                          <div style={{fontSize:10,color:"rgba(255,255,255,0.85)",fontWeight:600}}>{doneCount}/{totalCount}</div>
                        </div>
                        {/* Scrollable body */}
                        <div className="cadence-col-body">
                          {managedItems.length===0&&calItems.length===0&&(
                            <div style={{padding:"16px",fontSize:11,color:"var(--text3)",textAlign:"center",fontStyle:"italic"}}>
                              {managedCompanyIds.size===0?"No managed clients selected. Go to Settings → Client Register.":"No items yet. Click ⚡ Generate from Clients."}
                            </div>
                          )}
                          {(()=>{
                            const today=new Date();today.setHours(0,0,0,0);

                            // Build synthetic calendar entries with dueDate for sorting
                            const calEntries=key==="T"?calItems.flatMap(p=>{
                              const mtgId=`cal-mtg-${p.id}`;
                              const prepId=`cal-prep-${p.id}`;
                              const mtgDone=cadence.T?.items?.find(i=>i.id===mtgId);
                              const prepDone=cadence.T?.items?.find(i=>i.id===prepId);
                              const mtgFmt=new Date(p.meetingDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",year:"numeric"});
                              const prepFmt=new Date(p.prepDate+"T12:00:00").toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short",year:"numeric"});
                              const out=[];
                              // Prep items are surfaced in Plan My Day, not here — only show the meeting itself
                              if(!mtgDone) out.push({_cal:true,_type:"mtg",id:mtgId,dueDate:p.meetingDate,client:p.client,meetingFmt:mtgFmt,p});
                              return out;
                            }):[];

                            // Merge regular + calendar, sort by dueDate ascending (no date → bottom)
                            const allItems=[...managedItems.map(i=>({...i,_cal:false})),...calEntries]
                              .sort((a,b)=>{
                                const da=a.dueDate||"9999";
                                const db=b.dueDate||"9999";
                                return da<db?-1:da>db?1:0;
                              });

                            if(allItems.length===0) return null;

                            // Render-time fallback: consolidated O items always show next Friday even if state hasn't updated yet
                            const calcNextFriday=(()=>{
                              const d=new Date();d.setHours(0,0,0,0);
                              const diff=(5-d.getDay()+7)%7||7;
                              d.setDate(d.getDate()+diff);
                              return d.toISOString().slice(0,10);
                            })();
                            const ROLLING_TITLES_R=/(health check|ticket review|patch status)/i;

                            return allItems.map(i=>{
                              const todayIso=today.toISOString().slice(0,10);
                              const isRolling=i.client==="All"&&ROLLING_TITLES_R.test(i.title);
                              const effectiveDueDate=isRolling&&(!i.dueDate||i.dueDate<todayIso)?calcNextFriday:i.dueDate;
                              const due=effectiveDueDate?new Date(effectiveDueDate+"T12:00:00"):null;
                              const daysUntil=due?Math.ceil((due-today)/(1000*60*60*24)):null;
                              const isOverdue=daysUntil!==null&&daysUntil<0;
                              const isDueSoon=daysUntil!==null&&daysUntil>=0&&daysUntil<=3;

                              // Calendar entries
                              if(i._cal && i._type==="prep"){
                                const {p,prepFmt,meetingFmt}=i;
                                return(
                                  <div key={i.id} className="cad-item"
                                    style={isOverdue?{borderLeft:"3px solid var(--danger)",background:"rgba(231,76,60,0.04)"}:isDueSoon?{borderLeft:"3px solid var(--ops)",background:"rgba(230,126,34,0.03)"}:{}}>
                                    <div className="cad-cb" onClick={()=>{
                                      const ni={id:i.id,title:`Pre-Meeting Prep — Monthly Tech Catchup — ${p.client}`,desc:`Prepare for meeting on ${meetingFmt}`,client:p.client,dueDate:p.prepDate,done:false};
                                      const upd={...cadence,T:{...cadence.T,items:[...cadence.T.items,ni]}};
                                      setCadence(upd);saveSnapshot({cadence:upd});
                                    }}/>
                                    <div style={{flex:1}}>
                                      <div className="ci-title" style={{color:isOverdue?"var(--danger)":isDueSoon?"var(--ops)":undefined}}>
                                        {isOverdue?"⚠ ":""}Pre-Meeting Prep — {p.client}
                                      </div>
                                      <div className="ci-desc">Prepare for Monthly Tech Catchup · {meetingFmt}</div>
                                      <div style={{fontSize:9,marginTop:3,display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                                        <span style={{color:isOverdue?"var(--danger)":isDueSoon?"var(--ops)":"var(--text3)",fontWeight:isOverdue||isDueSoon?600:400}}>
                                          📅 {prepFmt}{isOverdue?` — overdue ${Math.abs(daysUntil)}d`:isDueSoon?` — in ${daysUntil}d`:""}
                                        </span>
                                        <span style={{background:"rgba(52,152,219,0.08)",color:"var(--tact)",padding:"1px 5px",borderRadius:3,fontSize:8,border:"1px solid rgba(52,152,219,0.15)"}}>📅 From calendar</span>
                                      </div>
                                      <div style={{marginTop:3,display:"flex",flexWrap:"wrap",gap:3}}>
                                        {["Open tickets","Recurring issues","Action register","Health summary","Draft agenda"].map(t2=>(
                                          <span key={t2} style={{fontSize:8,background:"rgba(52,152,219,0.06)",color:"var(--tact)",padding:"1px 5px",borderRadius:3,border:"1px solid rgba(52,152,219,0.12)"}}>{t2}</span>
                                        ))}
                                      </div>
                                    </div>
                                    <span className="lb lb-T" style={{fontSize:8}}>{p.client}</span>
                                  </div>
                                );
                              }

                              if(i._cal && i._type==="mtg"){
                                const {p,meetingFmt}=i;
                                const dMtg=Math.ceil((new Date(p.meetingDate+"T12:00:00")-today)/(1000*60*60*24));
                                return(
                                  <div key={i.id} className="cad-item" style={{background:"rgba(52,152,219,0.025)"}}>
                                    <div className="cad-cb" onClick={()=>{
                                      const ni={id:i.id,title:`Monthly Tech Catchup — ${p.client}`,desc:`Monthly meeting`,client:p.client,dueDate:p.meetingDate,done:false};
                                      const upd={...cadence,T:{...cadence.T,items:[...cadence.T.items,ni]}};
                                      setCadence(upd);saveSnapshot({cadence:upd});
                                    }}/>
                                    <div style={{flex:1}}>
                                      <div className="ci-title">Monthly Tech Catchup — {p.client}</div>
                                      <div className="ci-desc">Monthly meeting with client</div>
                                      <div style={{fontSize:9,marginTop:3,display:"flex",gap:5,alignItems:"center"}}>
                                        <span style={{color:"var(--tact)",fontWeight:500}}>📅 {meetingFmt}</span>
                                        <span style={{color:"var(--text3)"}}>
                                          {dMtg===0?"Today":dMtg===1?"Tomorrow":`${dMtg}d away`}
                                        </span>
                                        <span style={{background:"rgba(52,152,219,0.08)",color:"var(--tact)",padding:"1px 5px",borderRadius:3,fontSize:8,border:"1px solid rgba(52,152,219,0.15)"}}>📅 From calendar</span>
                                      </div>
                                    </div>
                                    <span className="lb lb-T" style={{fontSize:8}}>{p.client}</span>
                                  </div>
                                );
                              }

                              // Regular cadence item
                              const dueFmt=due?due.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}):null;
                              return(
                                <div key={i.id} className={`cad-item${i.done?" ci-done":""}`}
                                  style={i.done?{opacity:0.5}:isOverdue?{borderLeft:"3px solid var(--danger)",background:"rgba(231,76,60,0.03)"}:isDueSoon?{borderLeft:"3px solid var(--ops)",background:"rgba(230,126,34,0.03)"}:{}}>
                                  <div className={`cad-cb${i.done?` ck-${key}`:""}`}
                                    onClick={()=>i.done?unCad(key,i.id):toggleCad(key,i.id)}
                                    title={i.done?"Click to reset":"Click to mark done"}
                                    style={{cursor:"pointer"}}>
                                    {i.done?"✓":""}
                                  </div>
                                  <div style={{flex:1}}>
                                    <div className="ci-title" style={{color:isOverdue?"var(--danger)":undefined}}>{i.title}</div>
                                    <div className="ci-desc">{i.desc}</div>
                                    <div style={{fontSize:9,marginTop:3,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                                      {dueFmt&&<span style={{color:isOverdue?"var(--danger)":isDueSoon?"var(--ops)":"var(--text3)",fontWeight:isOverdue||isDueSoon?600:400}}>
                                        📅 {dueFmt}{isOverdue?` — overdue ${Math.abs(daysUntil)}d`:isDueSoon?` — in ${daysUntil}d`:""}
                                      </span>}
                                      {!dueFmt&&<span style={{color:"var(--text3)",fontStyle:"italic",fontSize:8}}>No date set</span>}
                                      <button
                                        className="ico-btn"
                                        style={{fontSize:9,padding:"0 4px",height:16,color:"var(--text3)",flexShrink:0}}
                                        title="Set due date"
                                        onClick={e=>{e.stopPropagation();setCadDueDateModal({layer:key,id:i.id,title:i.title,dueDate:i.dueDate||""});}}
                                      >✏️</button>
                                    </div>
                                  </div>
                                  <span className={`lb lb-${key}`} style={{fontSize:8}}>{i.client}</span>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            );
          })()}


          {/* ── MONTHLY REPORTS TAB ── */}
          {tab==="monthlyreports"&&<MonthlyReports companies={atCompanies} managedCompanyIds={managedCompanyIds} onCreateQuote={createQuoteFromRoadmapItem}/>}

          {/* ── TEAM REPORTING TAB ── INTERNAL ONLY: per-tech 1:1 metrics.
              Never client-facing, never on a share link, never in a report pack.
              Takes no props — it owns its own fetches against /api/team-reporting. */}
          {/* Wrapped after a render exception in the open-tickets card blanked
              the entire app. A section that fails to render should cost its own
              panel, not the page around it. */}
          {tab==="teamreporting"&&<PanelErrorBoundary><TeamReporting/></PanelErrorBoundary>}

          {/* ── PROPOSALS TAB ── */}
          {tab==="proposals"&&(
            <ProposalsTab
              proposals={proposals}
              setProposals={setProposals}
              saveSnapshot={saveSnapshot}
              quotes={quotes}
              setQuotes={setQuotes}
              userEmail={userInfo?.email}
              userName={userInfo?.name||userInfo?.email||""}
              proposalTemplates={proposalTemplates}
              setProposalTemplates={setProposalTemplates}
              defaultMarkupPct={defaultMarkupPct}
              onCatalogSearch={handleFoundationalSearch}
              opportunities={opportunities}
              atCompanies={atCompanies}
              requestOpenId={pendingProposalOpenId}
              onConsumedOpenRequest={clearPendingProposalOpen}
              contractTemplates={contractTemplates}
              contractInstances={contractInstances}
              setContractInstances={setContractInstances}
            />
          )}

          {/* ── PROPOSAL TEMPLATES TAB ── */}
          {tab==="proposaltemplates"&&(
            <TemplatesTab
              proposalTemplates={proposalTemplates}
              setProposalTemplates={setProposalTemplates}
              saveSnapshot={saveSnapshot}
              defaultMarkupPct={defaultMarkupPct}
              onCatalogSearch={handleFoundationalSearch}
            />
          )}

          {/* ── CONTRACT TEMPLATES TAB ── */}
          {tab==="contracttemplates"&&(
            <ContractTemplatesTab
              contractTemplates={contractTemplates}
              setContractTemplates={setContractTemplates}
              contractInstances={contractInstances}
              proposalTemplates={proposalTemplates}
              saveSnapshot={saveSnapshot}
            />
          )}

          {/* ── AUTOTASK TAB ── */}
          {tab==="autotask"&&(
            <AutotaskDashboard
              onGetData={onGetAutotaskData}
              onRefreshAtSync={onRefreshAtSync}
              atSyncing={atSyncing}
              atCompanies={atCompanies}
              onRunSentiment={onRunSentiment}
              preloadedSentiment={preloadedSentiment}
              managedCompanyIds={managedCompanyIds}
              preloadedData={atDashData}
              dattoData={dattoData}
              onTriggerDattoSync={(names) => {
                // Pass managed company names to filter Datto sync
                const managedNames = names || atCompanies
                  .filter(c => managedCompanyIds.has(String(c.id)))
                  .map(c => c.companyName);
                return onTriggerDattoSync?.(managedNames);
              }}
              onRefreshDattoData={onRefreshDattoData}
            />
          )}

          {/* ── IT GLUE UPLOAD TAB ──
               Runbook import for onboarding clients off an incumbent MSP.
               Self-contained in components/itglue/ItGlueUpload.jsx: it writes a
               whole client's documentation into IT Glue and NOTHING IT CREATES
               CAN BE DELETED through the API, so it wants to stay reviewable on
               its own rather than buried in this file. ── */}
          {tab==="itglueupload"&&<ItGlueUpload/>}

          {/* ── MISSING TIME TAB ── calendar meetings vs Autotask time entries.
               Self-contained in components/timeReconcile/. No side panels. The
               "New ticket + time" action reuses the To-Do ticket modal above via
               openTicketFromTodo(prefill) and hears back through reconcileTicketDoneRef. ── */}
          {tab==="timereconcile"&&<PanelErrorBoundary><TimeReconcileTab atCompanies={atCompanies} atReference={atReference} onNewTicket={(prefill,onDone)=>{reconcileTicketDoneRef.current=onDone;openTicketFromTodo({id:null,title:prefill.title,_source:"reconcile"},prefill);}}/></PanelErrorBoundary>}

          {/* ── myITPROCESS TAB ── */}
          {tab==="myitprocess"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              {/* Header */}
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px"}}>📋 myITprocess Strategy</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                    {myItProcessData?.syncedAt
                      ?`Last synced ${new Date(myItProcessData.syncedAt).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}`
                      :"Not yet synced"}
                  </div>
                </div>
                <button className="btn btn-S" style={{fontSize:11}}
                  onClick={async()=>{
                    setMitpSyncing(true);
                    try{
                      await onTriggerMyItProcessSync?.();
                      setTimeout(async()=>{await onRefreshMyItProcessData?.();setMitpSyncing(false);},25000);
                    }catch{setMitpSyncing(false);}
                  }}
                  disabled={mitpSyncing}>
                  {mitpSyncing?"⏳ Syncing…":"🔄 Sync myITprocess"}
                </button>
              </div>

              {/* Body */}
              <div style={{flex:1,overflow:"auto",padding:20,display:"flex",flexDirection:"column",gap:16}}>

                {/* Empty state */}
                {!myItProcessData?.summaries?.length&&(
                  <div style={{padding:"16px 20px",background:"rgba(11,128,165,0.04)",border:"1px dashed rgba(11,128,165,0.25)",borderRadius:8}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:6}}>📋 No myITprocess data yet</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>Click <strong>Sync myITprocess</strong> above to pull clients, reviews, recommendations, and initiatives. Sync takes about 25 seconds.</div>
                  </div>
                )}

                {myItProcessData?.summaries?.length>0&&(<>

                  {/* Snapshot strip */}
                  {(()=>{
                    const s=myItProcessData;
                    const totalOpenRecs=(s.recommendations||[]).filter(r=>!r.isCompleted&&!r.completed).length;
                    const stats=[
                      {label:"Clients",value:s.clients?.length||0,color:"var(--strat)"},
                      {label:"Overdue",value:s.overdue?.length||0,color:"var(--danger)"},
                      {label:"Findings",value:s.findings?.length||0,color:"var(--warn)"},
                      {label:"Open Recommendations",value:totalOpenRecs,color:"var(--strat)"},
                      {label:"Initiatives",value:s.initiatives?.length||0,color:"var(--ops)"},
                    ];
                    return(
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10}}>
                        {stats.map(stat=>(
                          <div key={stat.label} style={{background:"white",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px"}}>
                            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600,marginBottom:4}}>{stat.label}</div>
                            <div style={{fontSize:22,fontWeight:700,color:stat.color,letterSpacing:"-0.5px"}}>{stat.value}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Overdue Review Categories */}
                  {(myItProcessData.overdue||[]).length>0&&(
                    <div style={{background:"white",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                      <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"linear-gradient(135deg,rgba(231,76,60,0.06),rgba(230,126,34,0.04))",display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:14}}>🔥</span>
                        <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>Overdue Review Categories</span>
                        <span style={{fontSize:10,color:"var(--text3)"}}>{myItProcessData.overdue.length} item{myItProcessData.overdue.length!==1?"s":""}</span>
                      </div>
                      <div>
                        {myItProcessData.overdue.map((o,i)=>{
                          const cid=o.clientId??o.client_id??o.client?.id;
                          const summary=myItProcessData.summaries.find(ss=>String(ss.id)===String(cid));
                          return(
                            <div key={i} style={{padding:"8px 16px",borderBottom:i<myItProcessData.overdue.length-1?"1px solid var(--border)":"none",display:"flex",alignItems:"center",gap:10,fontSize:11}}>
                              <span style={{flex:1,fontWeight:600,color:"var(--text)"}}>{summary?.name||"Unknown client"}</span>
                              <span style={{flex:2,color:"var(--text2)"}}>{o.name||o.categoryName||o.title||"Review category"}</span>
                              {o.daysOverdue!=null&&<span style={{background:"rgba(231,76,60,0.1)",color:"var(--danger)",padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:600}}>{o.daysOverdue}d overdue</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Client Overview */}
                  <div style={{background:"white",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                    <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"linear-gradient(135deg,rgba(11,128,165,0.06),rgba(42,67,101,0.04))",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:14}}>🏢</span>
                      <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>Client Overview</span>
                      <span style={{fontSize:10,color:"var(--text3)"}}>{myItProcessData.summaries.length} client{myItProcessData.summaries.length!==1?"s":""}</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                        <thead>
                          <tr style={{background:"var(--s2)"}}>
                            {["Client","Reviews","Overdue","Findings","Open Recs","Initiatives","Autotask Match"].map((h,i)=>(
                              <th key={h} style={{padding:"8px 12px",textAlign:i===0||i===6?"left":"center",fontSize:10,fontWeight:600,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.04em"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {myItProcessData.summaries.map(s=>{
                            const conf=s.autotaskMatch?.confidence;
                            const matchColor=conf==="exact"?"var(--ops)":conf==="manual"?"var(--strat)":conf==="fuzzy"?"var(--warn)":"var(--danger)";
                            const matchLabel=conf==="exact"?"✓ Exact":conf==="manual"?"🔗 Manual":conf==="fuzzy"?"≈ Fuzzy":"✗ No match";
                            return(
                              <tr key={s.id} style={{borderTop:"1px solid var(--border)"}}>
                                <td style={{padding:"10px 12px",fontWeight:600,color:"var(--text)"}}>{s.name}</td>
                                <td style={{padding:"10px 12px",textAlign:"center",color:"var(--text2)"}}>{s.reviewCount||"—"}</td>
                                <td style={{padding:"10px 12px",textAlign:"center",color:s.overdueCount>0?"var(--danger)":"var(--text3)",fontWeight:s.overdueCount>0?700:400}}>{s.overdueCount||"—"}</td>
                                <td style={{padding:"10px 12px",textAlign:"center",color:s.findingCount>0?"var(--warn)":"var(--text3)",fontWeight:s.findingCount>0?700:400}}>{s.findingCount||"—"}</td>
                                <td style={{padding:"10px 12px",textAlign:"center",color:"var(--text2)"}}>{s.openRecommendationCount||"—"}</td>
                                <td style={{padding:"10px 12px",textAlign:"center",color:"var(--text2)"}}>{s.initiativeCount||"—"}</td>
                                <td style={{padding:"10px 12px"}}>
                                  <div style={{display:"flex",alignItems:"center",gap:8,minWidth:200}}>
                                    <span style={{fontSize:10,fontWeight:600,color:matchColor,padding:"2px 8px",borderRadius:10,background:`${matchColor}15`,border:`1px solid ${matchColor}40`,whiteSpace:"nowrap"}}>{matchLabel}</span>
                                    {s.autotaskMatch?.companyName&&<span style={{fontSize:11,color:"var(--text2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={s.autotaskMatch.companyName}>{s.autotaskMatch.companyName}</span>}
                                    <button className="btn btn-S" style={{fontSize:9,padding:"2px 8px",marginLeft:"auto",flexShrink:0}}
                                      onClick={()=>setMitpMappingClient(s)}>Change</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Active Initiatives */}
                  {(myItProcessData.initiatives||[]).length>0&&(
                    <div style={{background:"white",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                      <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",background:"linear-gradient(135deg,rgba(39,174,96,0.06),rgba(11,128,165,0.04))",display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:14}}>🚀</span>
                        <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>Active Initiatives</span>
                        <span style={{fontSize:10,color:"var(--text3)"}}>{myItProcessData.initiatives.length}</span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:1,background:"var(--border)"}}>
                        {myItProcessData.initiatives.slice(0,12).map((init,i)=>{
                          const cid=init.clientId??init.client_id??init.client?.id;
                          const summary=myItProcessData.summaries.find(ss=>String(ss.id)===String(cid));
                          return(
                            <div key={i} style={{background:"white",padding:"10px 14px"}}>
                              <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,marginBottom:2}}>{summary?.name||"—"}</div>
                              <div style={{fontSize:12,fontWeight:600,color:"var(--text)",lineHeight:1.3}}>{init.name||init.title||"Initiative"}</div>
                              {init.status&&<div style={{fontSize:10,color:"var(--text2)",marginTop:4}}>{init.status}</div>}
                            </div>
                          );
                        })}
                      </div>
                      {myItProcessData.initiatives.length>12&&<div style={{padding:"8px 16px",fontSize:10,color:"var(--text3)",textAlign:"center"}}>+ {myItProcessData.initiatives.length-12} more</div>}
                    </div>
                  )}

                </>)}
              </div>

              {/* Mapping override modal */}
              {mitpMappingClient&&(
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
                  onClick={e=>{if(e.target===e.currentTarget){setMitpMappingClient(null);setMitpMappingSearch("");}}}>
                  <div style={{background:"white",borderRadius:12,width:480,maxWidth:"95vw",maxHeight:"85vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,0.25)"}}>
                    <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",background:"linear-gradient(135deg,#f0f4f7,#e8eef2)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.05em",textTransform:"uppercase"}}>Map myITprocess Client</div>
                        <div style={{fontSize:15,fontWeight:700,color:"var(--text)",marginTop:2}}>{mitpMappingClient.name}</div>
                      </div>
                      <button onClick={()=>{setMitpMappingClient(null);setMitpMappingSearch("");}} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--text3)",padding:"0 4px"}}>×</button>
                    </div>
                    <div style={{padding:"14px 20px",overflowY:"auto"}}>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>
                        Currently: {mitpMappingClient.autotaskMatch
                          ?<span style={{fontWeight:600,color:"var(--text2)"}}>{mitpMappingClient.autotaskMatch.companyName} <span style={{color:"var(--text3)"}}>({mitpMappingClient.autotaskMatch.confidence})</span></span>
                          :<span style={{color:"var(--danger)",fontWeight:600}}>No match</span>}
                      </div>
                      <input type="text" placeholder="Search Autotask companies…" value={mitpMappingSearch||""}
                        onChange={e=>setMitpMappingSearch(e.target.value)}
                        style={{width:"100%",padding:"7px 10px",fontSize:11,border:"1px solid var(--border)",borderRadius:6,outline:"none",marginBottom:8}}/>
                      <div style={{maxHeight:240,overflowY:"auto",border:"1px solid var(--border)",borderRadius:6}}>
                        {(atCompanies||[])
                          .filter(c=>!mitpMappingSearch||(c.companyName||"").toLowerCase().includes(mitpMappingSearch.toLowerCase()))
                          .slice(0,30)
                          .map(c=>(
                            <div key={c.id}
                              onClick={async()=>{await onSetMyItProcessMapping?.(mitpMappingClient.id,c.id);setMitpMappingClient(null);setMitpMappingSearch("");}}
                              style={{padding:"7px 10px",fontSize:11,cursor:"pointer",borderBottom:"1px solid var(--border)"}}
                              onMouseEnter={e=>{e.currentTarget.style.background="var(--s2)";}}
                              onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                              {c.companyName}
                            </div>
                          ))}
                      </div>
                    </div>
                    <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)",display:"flex",gap:8,background:"var(--s2)"}}>
                      <button className="btn btn-S" style={{fontSize:11,background:"rgba(231,76,60,0.08)",color:"var(--danger)",borderColor:"rgba(231,76,60,0.25)"}}
                        onClick={async()=>{await onSetMyItProcessMapping?.(mitpMappingClient.id,"");setMitpMappingClient(null);setMitpMappingSearch("");}}>
                        ✗ No match
                      </button>
                      <button className="btn btn-S" style={{fontSize:11}}
                        onClick={async()=>{await onSetMyItProcessMapping?.(mitpMappingClient.id,null);setMitpMappingClient(null);setMitpMappingSearch("");}}>
                        ↺ Reset to auto
                      </button>
                      <div style={{flex:1}}/>
                      <button className="btn btn-S" style={{fontSize:11}}
                        onClick={()=>{setMitpMappingClient(null);setMitpMappingSearch("");}}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}



          {/* ── QUOTING TAB ── */}
          {tab==="quoting"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              {/* Header */}
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px",marginRight:6}}>Quoting</div>
                  {/* Per-supplier freshness chips */}
                  {suppliers.filter(s => s.enabled).map(s => {
                    const stale = isSupplierStale(s);
                    const ageD  = supplierAgeDays(s);
                    if (!s.catalogMeta) {
                      return (
                        <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 9px",background:"var(--s2)",border:"1px dashed var(--border2)",borderRadius:14,fontSize:10,cursor:"pointer"}}
                          onClick={()=>setSupUiState(s.id,{modalOpen:true})}
                          title={`No catalog uploaded yet for ${s.name} — click to upload`}>
                          <span style={{fontSize:11,opacity:0.4}}>📦</span>
                          <span style={{color:"var(--text3)",fontStyle:"italic"}}>{s.name}: not yet uploaded</span>
                        </div>
                      );
                    }
                    return (
                      <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 9px",background:stale?"var(--warn-d)":"var(--ops-d)",border:`1px solid ${stale?"rgba(230,126,34,0.35)":"rgba(39,174,96,0.3)"}`,borderRadius:14,fontSize:10,cursor:"pointer"}}
                        onClick={()=>setSupUiState(s.id,{modalOpen:true})}
                        title={`${s.name} — click to upload a fresh catalog`}>
                        <span style={{fontSize:11}}>📦</span>
                        <span style={{color:"var(--text2)"}}>
                          <strong>{s.name}</strong> · <strong style={{color:stale?"var(--warn)":"var(--ops)"}}>{ageD<1?"today":`${Math.floor(ageD)}d`}</strong> · {s.catalogMeta.priorityItems?.toLocaleString()||0} priority{s.id==="leader" && s.catalogMeta.perthStock>0 ? ` · ${s.catalogMeta.perthStock.toLocaleString()} Perth` : ""}
                        </span>
                        {stale && <span style={{fontSize:9,color:"var(--warn)",fontWeight:700,textTransform:"uppercase"}}>Stale</span>}
                      </div>
                    );
                  })}
                  {suppliers.filter(s=>s.enabled).length === 0 && (
                    <span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>No suppliers enabled — configure in Settings</span>
                  )}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <div style={{fontSize:11,color:"var(--text3)"}}>
                    <span style={{color:"var(--text2)",fontWeight:600}}>{quoteBacklog.length}</span> in backlog · <span style={{color:"var(--text2)",fontWeight:600}}>{quotes.filter(q=>q.status==="draft").length}</span> drafting · <span style={{color:"var(--warn)",fontWeight:600}}>{quotes.filter(q=>q.status==="sent").length}</span> sent · <span style={{color:"var(--ops)",fontWeight:600}}>{quotes.filter(q=>q.status==="won").length}</span> won
                  </div>
                  {/* Upload dropdown — one entry per enabled supplier */}
                  {suppliers.filter(s=>s.enabled).length > 0 && (
                    <select className="inp" style={{fontSize:11,padding:"5px 10px",marginBottom:0,minWidth:160}}
                      value=""
                      onChange={e=>{ if(e.target.value){ setSupUiState(e.target.value, {modalOpen: true}); e.target.value=""; } }}>
                      <option value="">📦 Upload catalog…</option>
                      {suppliers.filter(s=>s.enabled).map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.uploadFormat.toUpperCase()}){s.catalogMeta?" — refresh":" — first time"}
                        </option>
                      ))}
                    </select>
                  )}
                  <button className="btn btn-strat" style={{fontSize:11}} onClick={addQuoteRequestManually}>+ New quote request</button>
                </div>
              </div>

              {/* Stale catalog prompt — shows once per session if a supplier with a source URL hasn't been refreshed in 24h */}
              {(()=>{
                const staleAutoSuppliers = suppliers.filter(s =>
                  s.enabled
                  && s.hasAutoRefreshSource
                  && !sessionStorage.getItem(`stalePromptDismissed:${s.id}`)
                  && (
                    !s.catalogMeta
                    || !s.catalogMeta.syncedAt
                    || (Date.now() - s.catalogMeta.syncedAt) > 24*60*60*1000
                  )
                );
                if (staleAutoSuppliers.length === 0) return null;
                return (
                  <div style={{flexShrink:0,padding:"10px 20px",background:"var(--warn-d)",borderBottom:"1px solid rgba(230,126,34,0.35)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                    <div style={{fontSize:12,color:"var(--text)",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>⚠️</span>
                      <div>
                        <strong>Catalog data may be stale.</strong> {staleAutoSuppliers.map(s=>s.name).join(" and ")} {staleAutoSuppliers.length===1?"hasn't":"haven't"} been refreshed in over 24 hours. Pull fresh now to make sure AI suggestions reflect current stock and pricing.
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                      {staleAutoSuppliers.map(s=>{
                        const pulling = !!supplierUploadState[s.id]?.pullingFromSource;
                        return (
                          <button key={s.id} className="btn btn-warn" style={{fontSize:11,padding:"5px 12px"}}
                            disabled={pulling}
                            onClick={()=>refreshSupplierFromSource(s)}>
                            {pulling?`⏳ Pulling ${s.name}…`:`⚡ Pull ${s.name} now`}
                          </button>
                        );
                      })}
                      <button className="ico-btn" style={{color:"var(--text3)"}} title="Dismiss for this session"
                        onClick={()=>{
                          staleAutoSuppliers.forEach(s=>sessionStorage.setItem(`stalePromptDismissed:${s.id}`,"1"));
                          // Force re-render
                          setSupUiState("__dismiss",{ts:Date.now()});
                        }}>×</button>
                    </div>
                  </div>
                );
              })()}

              {/* The list, and the builder, are the SAME PAGE — one replaces the
                  other. The builder used to be a modal over this list; as a full
                  page it needs the width, so the list steps aside rather than
                  sitting behind it. */}
              {!editingQuoteId && (<>
              {/* Body: backlog | pipeline */}
              <div style={{flex:1,display:"flex",overflow:"hidden"}}>

                {/* LEFT: BACKLOG (drop target for emails) */}
                <div style={{width:340,flexShrink:0,borderRight:"1px solid var(--border)",background:"white",display:"flex",flexDirection:"column",overflow:"hidden"}}>
                  <div style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",background:"var(--s2)",fontSize:11,fontWeight:700,color:"var(--text)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span>📥 Backlog ({quoteBacklog.length})</span>
                    <span style={{fontSize:9,color:"var(--text3)",fontWeight:500}}>Drag emails →</span>
                  </div>
                  <div className="scroll" style={{flex:1,padding:10,transition:"background 0.15s"}}
                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="var(--strat-d)";e.dataTransfer.dropEffect="copy";}}
                    onDragLeave={e=>{e.currentTarget.style.background="";}}
                    onDrop={e=>{
                      e.preventDefault();
                      e.currentTarget.style.background="";
                      const data=e.dataTransfer.getData("emailItem");
                      if(data){
                        try{
                          const em=JSON.parse(data);
                          addQuoteRequestFromEmail(em);
                        }catch(err){console.warn("Bad email drop",err);}
                      }
                    }}
                  >
                    {quoteBacklog.length===0 ? (
                      <div style={{textAlign:"center",padding:"40px 16px",color:"var(--text3)",fontSize:11,border:"2px dashed var(--border2)",borderRadius:8}}>
                        <div style={{fontSize:32,marginBottom:8,opacity:0.4}}>💰</div>
                        <div style={{marginBottom:6,fontWeight:600,color:"var(--text2)"}}>No quote requests</div>
                        <div style={{lineHeight:1.5}}>Drag an email from the inbox →<br/>or click <strong>+ New quote request</strong></div>
                      </div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {quoteBacklog.map(b=>(
                          <div key={b.id} style={{padding:"10px 12px",background:"var(--s2)",borderRadius:6,border:"1px solid var(--border)",cursor:"pointer",transition:"all 0.15s"}}
                            onClick={()=>startQuoteFromBacklog(b)}
                            onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--strat)";e.currentTarget.style.background="white";}}
                            onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.background="var(--s2)";}}
                            title="Click to start building this quote">
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6,marginBottom:4}}>
                              <div style={{fontSize:11,fontWeight:600,color:"var(--text)",flex:1,lineHeight:1.3}}>{b.subject||"(no subject)"}</div>
                              <button className="ico-btn" style={{flexShrink:0,fontSize:14,padding:"0 4px",lineHeight:1}} onClick={ev=>{ev.stopPropagation();deleteBacklogItem(b.id);}} title="Remove from backlog">×</button>
                            </div>
                            {b.from && <div style={{fontSize:10,color:"var(--text2)",marginBottom:3}}>From: {b.from}</div>}
                            {b.companyName && <div style={{fontSize:10,color:"var(--strat)",marginBottom:3,fontWeight:600}}>↳ {b.companyName}</div>}
                            {b.body && <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.4,maxHeight:42,overflow:"hidden"}}>{b.body.slice(0,140)}{b.body.length>140?"…":""}</div>}
                            <div style={{display:"flex",gap:6,marginTop:6,paddingTop:6,borderTop:"1px solid var(--border)",alignItems:"center"}}>
                              <span style={{fontSize:9,color:"var(--text3)"}}>{new Date(b.createdAt).toLocaleDateString()}</span>
                              {b.source==="email" && <span style={{fontSize:9,color:"var(--text3)"}}>· via email</span>}
                              <span style={{flex:1}}/>
                              <span style={{fontSize:9,color:"var(--strat)",fontWeight:600}}>Build quote ›</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT: PIPELINE */}
                <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",background:"white",display:"flex",gap:6,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
                    {/* Filter chips belong to the List — the board's columns
                        are its filter. Lost is the one off-board status; this
                        is where it stays reachable. */}
                    {quoteView==="list" && [
                      {id:"all",label:"All",count:quotes.length},
                      {id:"draft",label:"Drafting",count:quotes.filter(q=>q.status==="draft").length},
                      {id:"sent",label:"Sent",count:quotes.filter(q=>q.status==="sent").length},
                      {id:"won",label:"Won",count:quotes.filter(q=>q.status==="won").length},
                      {id:"lost",label:"Lost",count:quotes.filter(q=>q.status==="lost").length},
                    ].map(f=>(
                      <button key={f.id} className={`btn ${quoteFilter===f.id?"btn-strat":""}`} style={{fontSize:10,padding:"4px 10px"}} onClick={()=>setQuoteFilter(f.id)}>
                        {f.label}{f.count>0 && <span style={{opacity:0.7,marginLeft:4}}>({f.count})</span>}
                      </button>
                    ))}
                    {/* Searching by number IGNORES the status tabs — see the
                        filter below. Someone typing a Q number wants ONE known
                        quote, and it should not matter which tab is open. */}
                    <input className="inp" style={{width:130,marginBottom:0,fontSize:10,padding:"4px 8px"}}
                           value={quoteNumberSearch}
                           onChange={e=>setQuoteNumberSearch(e.target.value)}
                           placeholder="Q number…"
                           title="Find a quote by its number. Searches every quote, whichever tab is selected."/>
                    <div data-testid="quote-view-toggle" style={{display:"flex",border:"1px solid var(--border)",borderRadius:6,overflow:"hidden",marginLeft:4}}>
                      {[["board","▦ Board"],["list","☰ List"]].map(([id,label])=>(
                        <button key={id} className={`btn ${quoteView===id?"btn-strat":""}`} style={{fontSize:10,padding:"4px 10px",borderRadius:0,border:"none"}}
                          aria-pressed={quoteView===id} onClick={()=>setQuoteView(id)}>{label}</button>
                      ))}
                    </div>
                    <div style={{flex:1}}/>
                    {quoteTemplates.length>0 && (
                      <span style={{fontSize:10,color:"var(--text3)"}}>📚 {quoteTemplates.length} template{quoteTemplates.length!==1?"s":""} saved</span>
                    )}
                  </div>

                  {quoteView==="board" && !quoteNumberSearch.trim() ? (
                    /* A chip click or a drop sets ONE fulfilment step through
                       updateQuote, computed from the migrated record. Nothing
                       else in the quote is touched — quoteBoard.test.js pins it. */
                    <QuoteKanban quotes={quotes}
                      computeQuoteTotals={computeQuoteTotals} countAllLineItems={countAllLineItems}
                      sumOptions={sumOptions} ensureMigrated={ensureMigrated}
                      onOpen={setEditingQuoteId} onDuplicate={duplicateQuoteById}
                      onToggleStep={(id,step)=>{ const q=quotes.find(x=>x.id===id); if(q) updateQuote(id, toggleStep(ensureMigrated(q), step)); }}
                      onDropStep={(id,step)=>{ const q=quotes.find(x=>x.id===id); const patch=q?setStepDone(ensureMigrated(q), step):null; if(patch) updateQuote(id, patch); }}/>
                  ) : (
                  <div className="scroll" style={{flex:1,padding:14}}>
                    {(()=>{
                      /* ⚠ The number search overrides the status tabs entirely.
                         A Q number identifies ONE document, and making the
                         lookup depend on which tab happens to be selected
                         would hide the answer exactly when somebody already
                         knows what they are looking for. */
                      const filtered = quoteNumberSearch.trim()
                        ? quotes.filter(q=>matchesQuoteNumberSearch(q,quoteNumberSearch))
                        : (quoteFilter==="all" ? quotes : quotes.filter(q=>q.status===quoteFilter));
                      if(filtered.length===0){
                        return (
                          <div style={{textAlign:"center",padding:"60px 20px",color:"var(--text3)",fontSize:12}}>
                            <div style={{fontSize:36,marginBottom:10,opacity:0.4}}>📋</div>
                            <div style={{marginBottom:4,fontWeight:600,color:"var(--text2)"}}>No quotes {quoteFilter!=="all"?`in "${quoteFilter}"`:"yet"}</div>
                            <div style={{fontSize:11}}>Convert a backlog item or click <strong>+ New quote request</strong></div>
                          </div>
                        );
                      }
                      const order = {draft:0, sent:1, won:2, lost:3};
                      const sorted = [...filtered].sort((a,b)=>{
                        const so = (order[a.status]??9) - (order[b.status]??9);
                        if(so !== 0) return so;
                        return (b.updatedAt||0) - (a.updatedAt||0);
                      });
                      return (
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))",gap:12}}>
                          {/* THE card is components/quotes/QuoteCard.jsx — the same one the
                              board draws, so the two cannot drift. Chips only show on
                              won-or-later cards; a click toggles one step. */}
                          {sorted.map(q=>(
                            <QuoteCard key={q.id} quote={q}
                              computeQuoteTotals={computeQuoteTotals} countAllLineItems={countAllLineItems}
                              sumOptions={sumOptions} ensureMigrated={ensureMigrated}
                              onOpen={setEditingQuoteId} onDuplicate={duplicateQuoteById}
                              onToggleStep={(step)=>updateQuote(q.id, toggleStep(ensureMigrated(q), step))}/>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                </div>
              </div>
              </>)}

              {/* QUOTE BUILDER — the markup lives in components/quotes/QuoteBuilderPage.jsx.
                  Moved out of this file verbatim; the long prop list is the surface it
                  still reaches back into MspOsApp for, kept explicit so it can be
                  reduced deliberately rather than forgotten. */}
              {editingQuoteId && (()=>{
                const q = quotes.find(x=>x.id===editingQuoteId);
                if(!q) return null;
                return (
                  /* The tab root is overflow:hidden (the app shell is, all the way
                     up), so the builder needs its own scrolling box or its footer
                     actions are simply unreachable. */
                  <div style={{flex:1,minWidth:0,overflow:"auto",padding:"14px 20px",display:"flex"}}>
                  <QuoteBuilderPage
                    quote={q}
                    atCompanies={atCompanies} opportunities={opportunities} proposals={proposals}
                    quoteTemplates={quoteTemplates} defaultMarkupPct={defaultMarkupPct}
                    computeQuoteTotals={computeQuoteTotals} ensureMigrated={ensureMigrated}
                    countAllLineItems={countAllLineItems} sumOptions={sumOptions}
                    isSameLocation={isSameLocation} isSectionTemplate={isSectionTemplate}
                    templateItemCount={templateItemCount}
                    updateQuote={updateQuote} deleteQuote={deleteQuote} setEditingQuoteId={setEditingQuoteId}
                    addSection={addSection} updateSection={updateSection}
                    confirmAndDeleteSection={confirmAndDeleteSection} moveSection={moveSection}
                    reorderSections={reorderSections}
                    addOption={addOption} updateOption={updateOption}
                    confirmAndDeleteOption={confirmAndDeleteOption} selectOption={selectOption}
                    addLineItem={addLineItem} updateLineItem={updateLineItem} deleteLineItem={deleteLineItem}
                    focusedLocation={focusedLocation} setFocusedLocation={setFocusedLocation}
                    dragSectionIndexRef={dragSectionIndexRef}
                    setFreightVisibility={setFreightVisibility} setManualFreight={setManualFreight}
                    switchToManualFreight={switchToManualFreight} switchToManagedFreight={switchToManagedFreight}
                    applyTemplate={applyTemplate} saveCurrentAsTemplate={saveCurrentAsTemplate}
                    openAiChat={openAiChat} setLightboxImage={setLightboxImage}
                    addQuoteClause={addQuoteClause} updateQuoteClause={updateQuoteClause}
                    removeQuoteClause={removeQuoteClause} moveQuoteClause={moveQuoteClause}
                    restoreQuoteClause={restoreQuoteClause}
                    generateQuotePdf={generateQuotePdf} sendQuoteToClient={sendQuoteToClient}
                    pushQuoteToAutotask={pushQuoteToAutotask}
                  />
                  </div>
                );
              })()}

              {/* AI CHAT ASSISTANT MODAL */}
              {aiChatOpen && (()=>{
                const q = quotes.find(x=>x.id===aiChatQuoteId);
                if(!q) return null;
                return (
                  <div className="pe-overlay" onClick={e=>{if(e.target.className==="pe-overlay")closeAiChat();}}>
                    <div className="pe-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:760,width:"95vw",height:"85vh",display:"flex",flexDirection:"column"}}>
                      {/* Header */}
                      <div className="pe-hdr" style={{background:"var(--strat)",flexShrink:0}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:700,fontSize:15,color:"white",marginBottom:3}}>🤖 AI Quote Assistant</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.75)"}}>
                              Quote: <strong>{q.title||"(untitled)"}</strong>
                              {q.companyName && <> · {q.companyName}</>}
                              {(()=>{ const agg=aggregateSupplierStats(); return agg.priorityItems>0 ? <> · {agg.priorityItems.toLocaleString()} priority items across {agg.suppliersWithCatalog} supplier{agg.suppliersWithCatalog===1?"":"s"}</> : null; })()}
                            </div>
                          </div>
                          <button className="ico-btn" style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"white",flexShrink:0}} onClick={closeAiChat} title="Close (line items already added stay)">×</button>
                        </div>
                      </div>

                      {/* Chat history */}
                      <div className="scroll" style={{flex:1,padding:16,background:"var(--bg)",display:"flex",flexDirection:"column",gap:12}}
                        ref={el=>{if(el){el.scrollTop=el.scrollHeight;}}}>

                        {aiChatMessages.length===0 && (
                          <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text3)",fontSize:12}}>
                            <div style={{fontSize:36,marginBottom:10,opacity:0.4}}>💬</div>
                            <div style={{fontWeight:600,color:"var(--text2)",marginBottom:6}}>Tell me what you need</div>
                            <div style={{lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>
                              Describe the items, qty, budget, use case. I'll search your enabled suppliers' priority catalogs ({aggregatePriorityBrands().join(", ")||"none configured"}) and suggest matching SKUs. You can refine across turns — "swap those laptops for ones with 32GB RAM", "now add 5 docking stations", etc.
                            </div>
                          </div>
                        )}

                        {aiChatMessages.map((msg,mi)=>(
                          <div key={mi} style={{display:"flex",flexDirection:"column",gap:8,alignItems: msg.role==="user"?"flex-end":"flex-start"}}>
                            {/* Message bubble */}
                            {msg.content && (
                              <div style={{
                                maxWidth:"85%",
                                padding:"10px 14px",
                                background: msg.role==="user" ? "var(--strat)" : msg.isError?"var(--danger-d)":"white",
                                color: msg.role==="user" ? "white" : msg.isError?"var(--danger)":"var(--text)",
                                borderRadius:12,
                                border: msg.role==="user"?"none":`1px solid ${msg.isError?"rgba(231,76,60,0.3)":"var(--border)"}`,
                                fontSize:12,
                                lineHeight:1.5,
                                whiteSpace:"pre-wrap",
                                wordWrap:"break-word",
                              }}>
                                {msg.content}
                              </div>
                            )}

                            {/* Suggestion cards (only on assistant turns) */}
                            {msg.role==="assistant" && msg.suggestions && msg.suggestions.length>0 && (
                              <div style={{display:"flex",flexDirection:"column",gap:8,width:"100%",maxWidth:"100%"}}>
                                {/* Bulk accept — only worth showing for 2+ outstanding */}
                                {(()=>{
                                  const pend = msg.suggestions.filter(s=>s.status==="pending");
                                  if(pend.length < 2) return null;
                                  const newSecs = [...new Set(pend
                                    .filter(s=>!s.targetSectionId && s.targetSectionLabel)
                                    .map(s=>s.targetSectionLabel))];
                                  return (
                                    <div style={{
                                      display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,
                                      padding:"8px 12px",background:"var(--strat-d)",
                                      border:"1px solid rgba(52,152,219,0.3)",borderRadius:8,
                                    }}>
                                      <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.4}}>
                                        <strong style={{color:"var(--text)"}}>{pend.length} suggestions</strong> pending
                                        {newSecs.length>0 && <> · creates {newSecs.length} new section{newSecs.length===1?"":"s"}: <span style={{color:"var(--strat)",fontWeight:600}}>{newSecs.join(", ")}</span></>}
                                      </div>
                                      <button className="btn btn-strat" style={{fontSize:11,whiteSpace:"nowrap",flexShrink:0}}
                                        onClick={()=>acceptAllAiSuggestions(mi)}>
                                        ✓ Add all {pend.length}
                                      </button>
                                    </div>
                                  );
                                })()}
                                {msg.suggestions.map((s)=>{
                                  const accepted = s.status==="accepted";
                                  const skipped = s.status==="skipped";
                                  const pending = s.status==="pending";
                                  const subtotal = (Number(s.quantity)||0) * (Number(s.unitPrice)||0);
                                  const margin = (Number(s.unitCost)||0) > 0 ? (((Number(s.unitPrice)||0) - (Number(s.unitCost)||0)) / (Number(s.unitPrice)||0)) * 100 : 0;
                                  return (
                                    <div key={s.chatItemId} style={{
                                      padding:12,
                                      background:"white",
                                      border:`1px solid ${accepted?"rgba(39,174,96,0.4)":skipped?"var(--border)":"var(--border)"}`,
                                      borderLeft:`3px solid ${accepted?"var(--ops)":skipped?"var(--text3)":"var(--strat)"}`,
                                      borderRadius:8,
                                      opacity: skipped?0.55:1,
                                      transition:"all 0.15s",
                                    }}>
                                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
                                        <div style={{flex:1,minWidth:0}}>
                                          <div style={{fontSize:12,fontWeight:600,color:"var(--text)",lineHeight:1.4,marginBottom:3}}>{s.description||"(no description)"}</div>
                                          <div style={{fontSize:10,color:"var(--text3)",display:"flex",gap:6,flexWrap:"wrap"}}>
                                            {s.sku && <span style={{color:"var(--strat)",fontWeight:600}}>{s.sku}</span>}
                                            {s.manufacturer && <span>· {s.manufacturer}</span>}
                                            {s.stockNote && <span>· {s.stockNote}</span>}
                                          </div>
                                        </div>
                                        {/* Status badge */}
                                        {accepted && <span style={{fontSize:9,fontWeight:700,color:"var(--ops)",background:"var(--ops-d)",padding:"2px 8px",borderRadius:10,textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0}}>{s.replacedLineId?"Replaced":"Added"}</span>}
                                        {skipped && <span style={{fontSize:9,fontWeight:700,color:"var(--text3)",background:"var(--s2)",padding:"2px 8px",borderRadius:10,textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0}}>Skipped</span>}
                                      </div>

                                      <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:8,padding:"6px 0",borderTop:"1px solid var(--border)",borderBottom:pending?"1px solid var(--border)":"none",fontSize:10}}>
                                        <div><div style={{color:"var(--text3)"}}>Qty</div><div style={{fontWeight:600,color:"var(--text)"}}>{s.quantity||0}</div></div>
                                        <div><div style={{color:"var(--text3)"}}>Cost</div><div style={{fontWeight:600,color:"var(--text)"}}>${(Number(s.unitCost)||0).toFixed(2)}</div></div>
                                        <div><div style={{color:"var(--text3)"}}>Price</div><div style={{fontWeight:600,color:"var(--text)"}}>${(Number(s.unitPrice)||0).toFixed(2)}</div></div>
                                        <div><div style={{color:"var(--text3)"}}>Subtotal</div><div style={{fontWeight:700,color:"var(--text)"}}>${subtotal.toFixed(2)} <span style={{fontWeight:500,color:margin>=20?"var(--ops)":margin>=10?"var(--warn)":"var(--danger)",fontSize:9}}>({margin.toFixed(0)}%)</span></div></div>
                                      </div>

                                      {/* Action buttons (only when pending) */}
                                      {pending && (
                                        <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
                                          <button className="btn btn-O" style={{fontSize:10,padding:"4px 10px"}} onClick={()=>acceptAiSuggestion(mi,s.chatItemId)} title="Add this item as a new line on the quote">✓ Add to quote</button>

                                          {/* Replace dropdown — only show if quote has existing line items */}
                                          {countAllLineItems(q)>0 && (
                                            <div style={{position:"relative",display:"inline-block"}}>
                                              <select className="inp" style={{marginBottom:0,fontSize:10,padding:"4px 8px",cursor:"pointer"}}
                                                value=""
                                                onChange={e=>{ if(e.target.value){ replaceWithAiSuggestion(mi, s.chatItemId, e.target.value); e.target.value=""; } }}>
                                                <option value="">↻ Replace existing line…</option>
                                                {getAllLineItemRefs(q).map((ref,idx)=>(
                                                  <option key={ref.lineItem.id} value={ref.lineItem.id}>
                                                    [{ref.locationLabel}] {(ref.lineItem.description||"(no desc)").slice(0,40)}{(ref.lineItem.description||"").length>40?"…":""}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          )}

                                          <button className="btn" style={{fontSize:10,padding:"4px 10px",color:"var(--text3)"}} onClick={()=>skipAiSuggestion(mi,s.chatItemId)} title="Don't add this item">✗ Skip</button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Loading indicator */}
                        {aiChatLoading && (
                          <div style={{display:"flex",alignItems:"center",gap:8,color:"var(--text3)",fontSize:11,fontStyle:"italic",padding:"4px 0"}}>
                            <div style={{display:"inline-flex",gap:4}}>
                              <span style={{width:6,height:6,borderRadius:"50%",background:"var(--strat)",animation:"aiD 1.4s ease-in-out infinite"}}/>
                              <span style={{width:6,height:6,borderRadius:"50%",background:"var(--strat)",animation:"aiD 1.4s ease-in-out 0.2s infinite"}}/>
                              <span style={{width:6,height:6,borderRadius:"50%",background:"var(--strat)",animation:"aiD 1.4s ease-in-out 0.4s infinite"}}/>
                            </div>
                            Searching catalog…
                          </div>
                        )}
                      </div>

                      {/* Composer */}
                      <div style={{padding:"12px 16px",borderTop:"1px solid var(--border)",background:"white",flexShrink:0}}>
                        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                          <textarea className="inp" style={{flex:1,marginBottom:0,fontSize:12,resize:"none",minHeight:50,maxHeight:120,fontFamily:"inherit",lineHeight:1.5}}
                            value={aiChatInput}
                            placeholder={aiChatMessages.length===0
                              ? "e.g. 5 HP laptops for new hires, ~$1500 each, 16GB RAM, in Perth stock"
                              : "Refine: 'swap those for 32GB models' or 'now add 5 docking stations'"}
                            disabled={aiChatLoading}
                            onChange={e=>setAiChatInput(e.target.value)}
                            onKeyDown={e=>{
                              if(e.key==="Enter" && !e.shiftKey){
                                e.preventDefault();
                                sendAiChatMessage();
                              }
                            }}/>
                          <button className="btn btn-strat" style={{fontSize:11,padding:"8px 16px",flexShrink:0}}
                            onClick={sendAiChatMessage}
                            disabled={aiChatLoading||!aiChatInput.trim()}>
                            {aiChatLoading?"⏳":"Send"}
                          </button>
                        </div>
                        <div style={{fontSize:9,color:"var(--text3)",marginTop:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span>Enter to send · Shift+Enter for new line</span>
                          <button className="btn" style={{fontSize:10,padding:"3px 10px"}} onClick={closeAiChat}>Done</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* CATALOG UPLOAD MODAL (renders for whichever supplier has modalOpen=true) */}
              {(()=>{
                const openSup = suppliers.find(s => supplierUploadState[s.id]?.modalOpen);
                if (!openSup) return null;
                const uploading = !!supplierUploadState[openSup.id]?.uploading;
                const acceptExt = openSup.uploadFormat === "zip" ? ".zip,application/zip" : ".csv,text/csv";
                const acceptLabel = openSup.uploadFormat === "zip" ? "ZIP file" : "CSV file";
                const close = ()=>{ if(!uploading) setSupUiState(openSup.id, {modalOpen: false}); };
                // Per-supplier guidance text
                const guidance = openSup.id === "leader"
                  ? <>Log into the <strong>Leader Computers</strong> reseller portal, navigate to the stock/pricing export page, download the latest CSV. The backend parses ~19,500 items and extracts your priority brands ({(openSup.priorityBrands||[]).join(", ")||"none"}) including Perth stock info.</>
                  : openSup.id === "dicker"
                    ? <>Log into the <strong>Dicker Data</strong> portal, download the datafeed ZIP (contains <code>datafeed.csv</code>). The backend extracts and parses it, keeping only Status=A items and filtering to your priority brands ({(openSup.priorityBrands||[]).join(", ")||"none"}).</>
                    : <>Upload the latest catalog file for <strong>{openSup.name}</strong>. The backend parses it and filters to your priority brands ({(openSup.priorityBrands||[]).join(", ")||"none"}).</>;
                return (
                  <div className="pe-overlay" onClick={e=>{if(e.target.className==="pe-overlay")close();}}>
                    <div className="pe-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:580,width:"95vw"}}>
                      <div className="pe-hdr" style={{background:"var(--strat)"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                          <div>
                            <div style={{fontWeight:700,fontSize:15,color:"white",marginBottom:3}}>📦 Upload {openSup.name} Catalog</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Upload the latest stock {acceptLabel} from your supplier portal</div>
                          </div>
                          <button className="ico-btn" style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"white",flexShrink:0}} onClick={close}>×</button>
                        </div>
                      </div>
                      <div className="pe-body" style={{padding:20}}>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>
                          {guidance}
                        </div>

                        {/* Quick pull from source URL — only for suppliers with a configured Key Vault URL */}
                        {openSup.hasAutoRefreshSource && (()=>{
                          const pulling = !!supplierUploadState[openSup.id]?.pullingFromSource;
                          return (
                            <div style={{marginBottom:14,padding:12,background:"var(--ops-d)",border:"1px solid rgba(39,174,96,0.3)",borderRadius:6}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                                <div style={{flex:1}}>
                                  <div style={{fontSize:12,fontWeight:600,color:"var(--text)",marginBottom:3}}>⚡ Pull fresh from {openSup.name} now</div>
                                  <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.5}}>
                                    Skip the manual download — pull the latest CSV directly from {openSup.name}'s API. Auto-runs hourly 8am-5pm Perth time.
                                  </div>
                                </div>
                                <button className="btn btn-ops" style={{fontSize:11,padding:"7px 14px",flexShrink:0,whiteSpace:"nowrap"}}
                                  disabled={pulling || uploading}
                                  onClick={()=>refreshSupplierFromSource(openSup)}>
                                  {pulling?"⏳ Pulling…":"⚡ Pull now"}
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Drop zone / file picker */}
                        <label
                          style={{display:"block",border:"2px dashed var(--border2)",borderRadius:8,padding:"32px 20px",textAlign:"center",cursor:uploading?"wait":"pointer",background:"var(--s2)",transition:"all 0.15s"}}
                          onDragOver={e=>{if(!uploading){e.preventDefault();e.currentTarget.style.borderColor="var(--strat)";e.currentTarget.style.background="var(--strat-d)";}}}
                          onDragLeave={e=>{e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.background="var(--s2)";}}
                          onDrop={e=>{
                            if(uploading) return;
                            e.preventDefault();
                            e.currentTarget.style.borderColor="var(--border2)";
                            e.currentTarget.style.background="var(--s2)";
                            const file=e.dataTransfer.files?.[0];
                            if(file) uploadSupplierCatalog(openSup, file);
                          }}
                        >
                          <div style={{fontSize:36,marginBottom:8,opacity:uploading?0.4:0.6}}>{uploading?"⏳":"📦"}</div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:4}}>
                            {uploading?"Uploading and parsing…":`Drop ${acceptLabel.toLowerCase()} here or click to browse`}
                          </div>
                          <div style={{fontSize:10,color:"var(--text3)"}}>
                            {uploading?"This can take 10-30 seconds for a full catalog":`Accepts ${acceptExt} files`}
                          </div>
                          <input
                            type="file"
                            accept={acceptExt}
                            style={{display:"none"}}
                            disabled={uploading}
                            onChange={e=>{
                              const file=e.target.files?.[0];
                              if(file) uploadSupplierCatalog(openSup, file);
                              e.target.value=""; // allow re-uploading same file
                            }}/>
                        </label>

                        {openSup.catalogMeta && !uploading && (
                          <div style={{marginTop:14,padding:10,background:"var(--s2)",borderRadius:6,border:"1px solid var(--border)",fontSize:10,color:"var(--text3)"}}>
                            <div style={{fontWeight:600,color:"var(--text2)",marginBottom:4}}>Current catalog</div>
                            {openSup.catalogMeta.fileName && <div>File: {openSup.catalogMeta.fileName}</div>}
                            <div>Synced: {new Date(openSup.catalogMeta.syncedAt).toLocaleString("en-AU")}</div>
                            <div>Total items: {openSup.catalogMeta.totalItems?.toLocaleString()||0}</div>
                            <div>Priority items ({(openSup.priorityBrands||[]).join("/")||"—"}): {openSup.catalogMeta.priorityItems?.toLocaleString()||0}</div>
                            {openSup.id==="leader" && openSup.catalogMeta.perthStock>0 && (
                              <div>In Perth stock: {openSup.catalogMeta.perthStock.toLocaleString()}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{padding:"12px 16px",borderTop:"1px solid var(--border)",background:"var(--s2)",display:"flex",justifyContent:"flex-end"}}>
                        <button className="btn" style={{fontSize:11}} onClick={close} disabled={uploading}>Close</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* PUSH TO AUTOTASK — the review modal */}
              {quotePushModal && (
                <QuotePushModal
                  quote={quotes.find(q => q.id===quotePushModal.quoteId) || null}
                  preview={quotePushModal.preview}
                  busy={quotePushModal.busy}
                  result={quotePushModal.result}
                  onCancel={()=>{ if(!quotePushModal.busy) setQuotePushModal(null); }}
                  onPush={confirmPushQuote}/>
              )}
              {quoteTemplateModal && (
                <QuoteTemplateSaveModal choices={quoteTemplateModal.choices}
                  onCancel={()=>setQuoteTemplateModal(null)}
                  onSave={({mode,name})=>saveQuoteTemplate(quoteTemplateModal.quoteId, mode, name)}/>
              )}
              {quoteStartModal && (
                <QuoteStartModal backlogItem={quoteStartModal.backlogItem} templates={quoteTemplates} quotes={quotes}
                  onCancel={()=>setQuoteStartModal(null)}
                  onBlank={()=>startQuoteChoice("blank")}
                  onTemplate={(tId)=>startQuoteChoice("template", tId)}
                  onCopy={(qId)=>startQuoteChoice("copy", qId)}/>
              )}

              {/* SEND MODAL — the covering message before the quote goes out */}
              {quoteSendModal && (
                <QuoteSendModal
                  quote={quotes.find(q => q.id===quoteSendModal.quoteId) || null}
                  busy={quoteSendModal.busy}
                  onCancel={()=>{ if(!quoteSendModal.busy) setQuoteSendModal(null); }}
                  onSend={confirmSendQuote}/>
              )}

              {/* IMAGE LIGHTBOX */}
              {lightboxImage && (
                <div onClick={()=>setLightboxImage(null)}
                  style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:40,cursor:"zoom-out"}}>
                  <img src={lightboxImage} alt="Line item" style={{maxWidth:"100%",maxHeight:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.5)",borderRadius:4,background:"white"}}
                    onClick={e=>e.stopPropagation()}/>
                  <button className="ico-btn" style={{position:"absolute",top:16,right:16,fontSize:20,background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",color:"white",borderRadius:24,width:40,height:40}}
                    onClick={()=>setLightboxImage(null)}>×</button>
                </div>
              )}

              {/* Toast */}
              {quoteToast && (
                <div style={{position:"fixed",bottom:24,right:24,background:quoteToast.kind==="ok"?"var(--ops)":"var(--danger)",color:"white",padding:"12px 18px",borderRadius:8,boxShadow:"var(--shadow-lg)",fontSize:12,fontWeight:600,zIndex:9999,maxWidth:380}}>
                  {quoteToast.msg}
                </div>
              )}
            </div>
          )}

          {/* ── OPPORTUNITIES TAB ── */}
          {tab==="opportunities"&&(
            <OpportunitiesTab
              opportunities={opportunities}
              proposals={proposals}
              quotes={quotes}
              atCompanies={atCompanies}
              selectedOpportunityId={selectedOpportunityId}
              setSelectedOpportunityId={setSelectedOpportunityId}
              createOpportunityManually={createOpportunityManually}
              createOpportunityFromEmail={createOpportunityFromEmail}
              addEmailToOpportunity={addEmailToOpportunity}
              removeEmailFromOpportunity={removeEmailFromOpportunity}
              updateOpportunity={updateOpportunity}
              deleteOpportunity={deleteOpportunity}
              onCreateProposal={createProposalFromOpportunity}
              onCreateQuote={createQuoteFromOpportunity}
              onOpenProposal={openProposal}
              onOpenQuote={openQuote}
            />
          )}

          {/* ── PRE-MEETING FORM TEMPLATES TAB ── */}
          {tab==="prepformtemplates"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px"}}>Pre-Meeting Form Templates</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Reusable question sets for different client industries · Assign customers on the next page</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-T" style={{fontSize:11,padding:"7px 14px"}}
                    onClick={()=>{
                      const tplId=`tpl-${Date.now()}`;
                      const newTpl={id:tplId,name:"New Template",description:"",questions:[]};
                      setPrepFormTemplates(prev=>{
                        const next=[...prev,newTpl];
                        saveSnapshot({prepFormTemplates:next});
                        return next;
                      });
                      setPrepTemplateModal({
                        templateId:tplId,
                        name:newTpl.name,
                        description:newTpl.description,
                        questions:[],
                        newQLabels:{},
                        newSecName:"",
                        isNew:true,
                      });
                    }}>+ New Blank Template</button>
                  <button className="btn btn-S" style={{fontSize:11,padding:"7px 14px",background:"var(--strat)",color:"white",borderColor:"var(--strat)"}}
                    onClick={async()=>{
                      try{
                        const defaults=await fetchDefaultPrepQuestions();
                        const tplId=`tpl-${Date.now()}`;
                        const newTpl={id:tplId,name:"From Defaults",description:"Seeded from system default questions",questions:JSON.parse(JSON.stringify(defaults))};
                        setPrepFormTemplates(prev=>{
                          const next=[...prev,newTpl];
                          saveSnapshot({prepFormTemplates:next});
                          return next;
                        });
                        setPrepTemplateModal({
                          templateId:tplId,
                          name:newTpl.name,
                          description:newTpl.description,
                          questions:newTpl.questions,
                          newQLabels:{},
                          newSecName:"",
                          isNew:true,
                        });
                      }catch(e){alert("Could not load default questions: "+e.message);}
                    }}>+ New from Defaults</button>
                  <button className="btn" style={{fontSize:11,padding:"7px 14px",background:"#0b2240",color:"white",borderColor:"#0b2240"}}
                    onClick={()=>{
                      // Hotel — By Department: pre-built question set organised by hotel department.
                      // Each question gets a fresh unique id so it can be edited independently of any
                      // other template that may have been seeded from this constant earlier.
                      const tplId=`tpl-${Date.now()}`;
                      const seedQs=HOTEL_BY_DEPARTMENT_QUESTIONS.map(q=>({
                        ...q,
                        id:`cq-${Math.random().toString(36).slice(2,9)}`,
                      }));
                      const newTpl={
                        id:tplId,
                        name:"Hotel — By Department",
                        description:"Hotel pre-meeting form organised by department (Front Office, F&B, Housekeeping, Maintenance, Sales) plus an Overall Feedback section.",
                        questions:seedQs,
                      };
                      setPrepFormTemplates(prev=>{
                        const next=[...prev,newTpl];
                        saveSnapshot({prepFormTemplates:next});
                        return next;
                      });
                      setPrepTemplateModal({
                        templateId:tplId,
                        name:newTpl.name,
                        description:newTpl.description,
                        questions:newTpl.questions,
                        newQLabels:{},
                        newSecName:"",
                        isNew:true,
                      });
                    }}>🏨 + Hotel — By Department</button>
                </div>
              </div>
              <div className="scroll" style={{padding:"20px",display:"flex",flexDirection:"column",gap:16}}>
                {(prepFormTemplates||[]).length===0?(
                  <div style={{background:"white",border:"1px dashed var(--border2)",borderRadius:8,padding:"40px 20px",textAlign:"center"}}>
                    <div style={{fontSize:32,marginBottom:10}}>📝</div>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:6}}>No templates yet</div>
                    <div style={{fontSize:12,color:"var(--text3)",maxWidth:480,margin:"0 auto",lineHeight:1.55}}>
                      Create your first template to start customising the Pre-Meeting Form questions per industry. Use <strong>🏨 + Hotel — By Department</strong> for a ready-built hotel template organised by department, <strong>+ New from Defaults</strong> to start from the standard hospitality question set, or <strong>+ New Blank Template</strong> to build from scratch.
                    </div>
                  </div>
                ):(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
                    {(prepFormTemplates||[]).map(tpl=>{
                      const qs=tpl.questions||[];
                      const secCount=new Set(qs.map(q=>q.section||"General")).size;
                      const assignedCount=Object.values(companyPrepTemplates||{}).filter(tid=>tid===tpl.id).length;
                      return(
                        <div key={tpl.id} className="sg" style={{background:"white"}}>
                          <div className="sg-h" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                            <span style={{textTransform:"none",letterSpacing:0,fontSize:13,fontWeight:700,color:"white"}}>{tpl.name||"Untitled template"}</span>
                            <span style={{fontSize:10,color:"rgba(255,255,255,0.65)"}}>{assignedCount} customer{assignedCount===1?"":"s"}</span>
                          </div>
                          <div className="sg-b">
                            {tpl.description&&(
                              <div style={{fontSize:11,color:"var(--text2)",marginBottom:10,lineHeight:1.55,fontStyle:"italic"}}>{tpl.description}</div>
                            )}
                            <div style={{fontSize:11,color:"var(--text3)",marginBottom:14}}>
                              <strong style={{color:"var(--text2)"}}>{qs.length}</strong> question{qs.length===1?"":"s"} across <strong style={{color:"var(--text2)"}}>{secCount}</strong> section{secCount===1?"":"s"}
                            </div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              <button className="btn btn-T" style={{fontSize:11,padding:"5px 12px"}}
                                onClick={()=>setPrepTemplateModal({
                                  templateId:tpl.id,
                                  name:tpl.name||"",
                                  description:tpl.description||"",
                                  questions:JSON.parse(JSON.stringify(tpl.questions||[])),
                                  newQLabels:{},
                                  newSecName:"",
                                })}>✏️ Edit</button>
                              <button className="btn" style={{fontSize:11,padding:"5px 12px"}}
                                onClick={()=>{
                                  const newId=`tpl-${Date.now()}`;
                                  const copy={
                                    id:newId,
                                    name:`Copy of ${tpl.name||"Untitled"}`,
                                    description:tpl.description||"",
                                    questions:JSON.parse(JSON.stringify(tpl.questions||[])).map(q=>({...q,id:`cq-${Math.random().toString(36).slice(2,9)}`})),
                                  };
                                  setPrepFormTemplates(prev=>{
                                    const next=[...prev,copy];
                                    saveSnapshot({prepFormTemplates:next});
                                    return next;
                                  });
                                  // Open the new copy for editing immediately
                                  setPrepTemplateModal({
                                    templateId:newId,
                                    name:copy.name,
                                    description:copy.description,
                                    questions:copy.questions,
                                    newQLabels:{},
                                    newSecName:"",
                                    isNew:true,
                                  });
                                }}>📋 Duplicate</button>
                              <button className="btn btn-danger" style={{fontSize:11,padding:"5px 12px",marginLeft:"auto"}}
                                onClick={()=>{
                                  const usedBy=Object.entries(companyPrepTemplates||{}).filter(([,tid])=>tid===tpl.id).map(([cid])=>cid);
                                  const msg=usedBy.length>0
                                    ?`Delete template "${tpl.name}"? ${usedBy.length} customer${usedBy.length===1?" is":"s are"} assigned and will revert to system defaults.`
                                    :`Delete template "${tpl.name}"?`;
                                  if(!window.confirm(msg)) return;
                                  // Unassign customers (best-effort)
                                  usedBy.forEach(cid=>{ assignCompanyToTemplate(cid,null); });
                                  setPrepFormTemplates(prev=>{
                                    const next=prev.filter(t=>t.id!==tpl.id);
                                    saveSnapshot({prepFormTemplates:next});
                                    return next;
                                  });
                                }}>🗑 Delete</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PRE-MEETING FORM ASSIGN CUSTOMERS TAB ── */}
          {tab==="prepformassign"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px"}}>Assign Customers to Templates</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Pick a Pre-Meeting Form template for each managed customer · Unassigned customers receive the system defaults</div>
                </div>
                <button className="btn" style={{fontSize:11,padding:"7px 14px"}} onClick={()=>goTab("prepformtemplates")}>📝 Manage Templates</button>
              </div>
              <div className="scroll" style={{padding:"20px",display:"flex",flexDirection:"column",gap:14}}>
                {atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).length===0?(
                  <div style={{background:"white",border:"1px dashed var(--border2)",borderRadius:8,padding:"30px 20px",textAlign:"center",fontSize:12,color:"var(--text3)"}}>
                    No managed customers. Tick customers as Managed on the <button className="btn" style={{fontSize:11,padding:"3px 10px",margin:"0 4px"}} onClick={()=>goTab("settings")}>Settings</button> page first.
                  </div>
                ):(prepFormTemplates||[]).length===0?(
                  <div style={{background:"white",border:"1px dashed var(--border2)",borderRadius:8,padding:"30px 20px",textAlign:"center",fontSize:12,color:"var(--text3)"}}>
                    No templates yet. <button className="btn btn-T" style={{fontSize:11,padding:"3px 10px",margin:"0 4px"}} onClick={()=>goTab("prepformtemplates")}>Create one</button> first, then come back to assign customers.
                  </div>
                ):(
                  <div className="sg" style={{background:"white"}}>
                    <div className="sg-h">Managed Customers</div>
                    <div className="sg-b" style={{padding:0}}>
                      <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead>
                          <tr style={{borderBottom:"1px solid var(--border)",background:"var(--s2)"}}>
                            <th style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:600,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.06em"}}>Customer</th>
                            <th style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:600,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.06em"}}>Template</th>
                            <th style={{textAlign:"right",padding:"10px 14px",fontSize:10,fontWeight:600,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.06em",width:90}}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).sort((a,b)=>a.companyName.localeCompare(b.companyName)).map(c=>{
                            const cid=String(c.id);
                            const currentTplId=companyPrepTemplates?.[cid]||"";
                            const currentTpl=(prepFormTemplates||[]).find(t=>t.id===currentTplId);
                            return(
                              <tr key={cid} style={{borderBottom:"1px solid var(--border)"}}>
                                <td style={{padding:"10px 14px",fontSize:12,color:"var(--text)",fontWeight:500}}>{c.companyName}</td>
                                <td style={{padding:"10px 14px"}}>
                                  <select className="inp" style={{marginBottom:0,fontSize:12,padding:"5px 8px",minWidth:240}}
                                    value={currentTplId}
                                    onChange={async(e)=>{
                                      const newTplId=e.target.value||null;
                                      await assignCompanyToTemplate(cid,newTplId);
                                    }}>
                                    <option value="">— None (system defaults) —</option>
                                    {(prepFormTemplates||[]).map(t=>(
                                      <option key={t.id} value={t.id}>{t.name||"Untitled"}</option>
                                    ))}
                                  </select>
                                </td>
                                <td style={{padding:"10px 14px",textAlign:"right",fontSize:10}}>
                                  {currentTpl?(
                                    <span style={{color:"var(--strat)",fontWeight:600}}>✓ Assigned</span>
                                  ):(
                                    <span style={{color:"var(--text3)"}}>Defaults</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* ── BUDGETING TAB ── */}
          {tab==="budgeting"&&(()=>{
            const MONTHS_B=["Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr","May","Jun"];
            const SECTION_DEFS=[
              {type:"service",        name:"Service, Maintenance, Support",   mode:"recurring"},
              {type:"renewals",       name:"Services, Subscriptions, Renewals",mode:"recurring"},
              {type:"infrastructure", name:"Infrastructure",                  mode:"oneoff"},
              {type:"workstations",   name:"Workstations, Peripherals, Hardware",mode:"oneoff"},
              {type:"projects",       name:"Projects & Consulting",           mode:"oneoff"},
            ];
            const bAuth={"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`};
            const uid=(p)=>p+Math.random().toString(36).slice(2,10);
            const money=(n)=>"$"+(Number(n)||0).toLocaleString("en-AU",{minimumFractionDigits:2,maximumFractionDigits:2});
            // managed companies only, sorted by name
            const mgList=atCompanies.filter(c=>managedCompanyIds.has(String(c.id))).sort((a,b)=>a.companyName.localeCompare(b.companyName));

            const blankBudget=(companyId,fy)=>({
              companyId,financialYear:fy,clientCode:null,currency:"AUD",
              createdFromBudgetYear:null,createdAt:new Date().toISOString(),updatedAt:new Date().toISOString(),
              sections:SECTION_DEFS.map((d,i)=>({id:uid("sec_"),type:d.type,name:d.name,order:i,lineMode:d.mode,lines:[]})),
              meta:{notes:null,monthOrder:MONTHS_B},
            });

            const loadFyList=async(companyId)=>{
              setBudFyList([]);setBudFyLoading(true);
              try{
                const r=await fetch(`${API_BASE}/api/budgets/${companyId}`,{headers:bAuth});
                if(r.ok){const j=await r.json();setBudFyList(Array.isArray(j)?j:(j.budgets||[]));}
                else setBudFyList([]);
              }catch(e){setBudFyList([]);}
              setBudFyLoading(false);
            };
            const loadBudget=async(companyId,fy)=>{
              setBudLoading(true);setBudData(null);
              try{
                const r=await fetch(`${API_BASE}/api/budgets/${companyId}/${fy}`,{headers:bAuth});
                if(r.ok){const j=await r.json();setBudData(j);setBudDirty(false);}
                else if(r.status===404){setBudData(blankBudget(companyId,fy));setBudDirty(true);}
              }catch(e){setBudToast({kind:"err",msg:"Failed to load budget"});}
              setBudLoading(false);
            };
            const saveBudget=async()=>{
              if(!budData)return;setBudSaving(true);
              try{
                const body={...budData,updatedAt:new Date().toISOString()};
                const r=await fetch(`${API_BASE}/api/budgets/${budData.companyId}/${budData.financialYear}`,
                  {method:"PUT",headers:bAuth,body:JSON.stringify(body)});
                if(r.ok){setBudDirty(false);setBudToast({kind:"ok",msg:"Budget saved"});
                  if(!budFyList.some(f=>(f.financialYear||f)===budData.financialYear))loadFyList(budData.companyId);}
                else setBudToast({kind:"err",msg:`Save failed (${r.status})`});
              }catch(e){setBudToast({kind:"err",msg:"Save failed"});}
              setBudSaving(false);
              setTimeout(()=>setBudToast(null),3000);
            };
            const exportXlsx=()=>{
              if(!budData)return;
              const url=`${API_BASE}/api/budgets/${budData.companyId}/${budData.financialYear}/export-xlsx?token=${encodeURIComponent(window.__mspToken||"")}`;
              window.open(url,"_blank");
            };
            const rollForward=async()=>{
              if(!budSelCompany||!budRollFrom||!budRollTo){setBudToast({kind:"err",msg:"Pick source and target FY"});setTimeout(()=>setBudToast(null),3000);return;}
              setBudSaving(true);
              try{
                const r=await fetch(`${API_BASE}/api/budgets/${budSelCompany}/${budRollFrom}/roll-forward`,
                  {method:"POST",headers:bAuth,body:JSON.stringify({targetFy:budRollTo})});
                if(r.ok){setBudToast({kind:"ok",msg:`Rolled ${budRollFrom} → ${budRollTo}`});await loadFyList(budSelCompany);setBudSelFy(budRollTo);loadBudget(budSelCompany,budRollTo);setBudRollOpen(false);}
                else setBudToast({kind:"err",msg:`Roll-forward failed (${r.status})`});
              }catch(e){setBudToast({kind:"err",msg:"Roll-forward failed"});}
              setBudSaving(false);setTimeout(()=>setBudToast(null),3500);
            };

            // ── line mutators (operate on budData copy) ──
            const mutate=(fn)=>{setBudData(prev=>{const next=JSON.parse(JSON.stringify(prev));fn(next);return next;});setBudDirty(true);};
            const addLine=(secId)=>mutate(b=>{const s=b.sections.find(x=>x.id===secId);if(s)s.lines.push({id:uid("line_"),label:"New line",sourceType:"manual",monthlyAmounts:Array(12).fill(0),qty:1,notes:null,status:"planned",linkedQuoteId:null,deviceRef:null,deviceMeta:null});});
            const delLine=(secId,lineId)=>mutate(b=>{const s=b.sections.find(x=>x.id===secId);if(s)s.lines=s.lines.filter(l=>l.id!==lineId);});
            const setLabel=(secId,lineId,v)=>mutate(b=>{const l=b.sections.find(x=>x.id===secId)?.lines.find(y=>y.id===lineId);if(l)l.label=v;});
            const setNotes=(secId,lineId,v)=>mutate(b=>{const l=b.sections.find(x=>x.id===secId)?.lines.find(y=>y.id===lineId);if(l)l.notes=v||null;});
            const setRecurring=(secId,lineId,v)=>mutate(b=>{const l=b.sections.find(x=>x.id===secId)?.lines.find(y=>y.id===lineId);if(l){const n=Number(v)||0;l.monthlyAmounts=Array(12).fill(n);}});
            const setOneoff=(secId,lineId,monthIdx,v)=>mutate(b=>{const l=b.sections.find(x=>x.id===secId)?.lines.find(y=>y.id===lineId);if(l){const a=Array.isArray(l.monthlyAmounts)?[...l.monthlyAmounts]:Array(12).fill(0);a[monthIdx]=Number(v)||0;l.monthlyAmounts=a;}});

            const lineTotal=(l)=>(l.monthlyAmounts||[]).reduce((a,b)=>a+(Number(b)||0),0);
            const sectionTotal=(s)=>(s.lines||[]).reduce((a,l)=>a+lineTotal(l),0);
            const grand=budData?budData.sections.reduce((a,s)=>a+sectionTotal(s),0):0;

            const companyName=(id)=>atCompanies.find(c=>c.id===id)?.companyName||`#${id}`;

            return(
              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
                {/* Header */}
                <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px",marginRight:6}}>📊 Budgeting</div>
                    {/* Client selector */}
                    <select value={budSelCompany||""} onChange={e=>{const id=Number(e.target.value)||null;setBudSelCompany(id);setBudSelFy("");setBudData(null);if(id)loadFyList(id);}}
                      style={{fontSize:12,padding:"6px 10px",borderRadius:6,border:"1px solid var(--border2)",background:"white",color:"var(--text)",minWidth:200}}>
                      <option value="">Select client…</option>
                      {mgList.map(c=><option key={c.id} value={c.id}>{c.companyName}</option>)}
                    </select>
                    {/* FY selector */}
                    {budSelCompany&&(
                      <select value={budSelFy||""} onChange={e=>{const fy=e.target.value;setBudSelFy(fy);if(fy)loadBudget(budSelCompany,fy);else setBudData(null);}}
                        style={{fontSize:12,padding:"6px 10px",borderRadius:6,border:"1px solid var(--border2)",background:"white",color:"var(--text)",minWidth:130}}>
                        <option value="">{budFyLoading?"Loading…":"Select FY…"}</option>
                        {budFyList.map(f=>{const fy=f.financialYear||f;return <option key={fy} value={fy}>{fy}{f.archived?" (archived)":""}</option>;})}
                      </select>
                    )}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    {budSelCompany&&(
                      <>
                        <button className="btn" onClick={()=>setBudNewOpen(true)} title="Create a new blank budget for a financial year">+ New FY</button>
                        <button className="btn" onClick={()=>setBudRollOpen(true)} title="Clone a prior FY into a new year">⟳ Roll forward</button>
                      </>
                    )}
                    {budData&&(
                      <>
                        <button className="btn" onClick={exportXlsx} title="Download a client-ready Excel spreadsheet">⬇ Export Excel</button>
                        <button className={"btn "+(budDirty?"btn-O":"")} disabled={!budDirty||budSaving} onClick={saveBudget}>
                          {budSaving?"Saving…":budDirty?"● Save changes":"Saved"}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div style={{flex:1,overflow:"auto",padding:"18px 22px"}}>
                  {!budSelCompany&&(
                    <div style={{textAlign:"center",color:"var(--text3)",fontSize:13,marginTop:60}}>
                      Select a managed client to view or build their IT budget.
                    </div>
                  )}
                  {budSelCompany&&!budSelFy&&!budData&&(
                    <div style={{textAlign:"center",color:"var(--text3)",fontSize:13,marginTop:60}}>
                      {budFyLoading?"Loading financial years…":budFyList.length?"Select a financial year, or create a new one.":"No budgets yet for this client. Create a new FY to begin."}
                    </div>
                  )}
                  {budLoading&&(<div style={{textAlign:"center",color:"var(--text3)",fontSize:13,marginTop:60}}>Loading budget…</div>)}

                  {budData&&!budLoading&&(
                    <div style={{maxWidth:1280,margin:"0 auto"}}>
                      {/* Title strip */}
                      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
                        <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:16,color:"var(--text)"}}>
                          {companyName(budData.companyId)} — {budData.financialYear}
                          {budData.createdFromBudgetYear&&<span style={{fontSize:11,color:"var(--text3)",fontWeight:400,marginLeft:8}}>(rolled from {budData.createdFromBudgetYear})</span>}
                        </div>
                        <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:15,color:"var(--strat)"}}>Total: {money(grand)}</div>
                      </div>

                      {/* Sections */}
                      {budData.sections.slice().sort((a,b)=>a.order-b.order).map(sec=>{
                        const recurring=sec.lineMode==="recurring";
                        return(
                          <div key={sec.id} style={{background:"white",border:"1px solid var(--border)",borderRadius:8,marginBottom:14,overflow:"hidden"}}>
                            {/* Section head */}
                            <div style={{padding:"10px 14px",background:"var(--s2)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                              <div style={{fontFamily:"var(--ff-head)",fontWeight:600,fontSize:13,color:"var(--text)"}}>{sec.name}
                                <span style={{fontSize:10,color:"var(--text3)",fontWeight:400,marginLeft:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>{recurring?"recurring":"one-off"}</span>
                              </div>
                              <div style={{fontWeight:600,fontSize:12,color:"var(--text2)"}}>{money(sectionTotal(sec))}</div>
                            </div>
                            {/* Lines */}
                            <div style={{padding:"6px 0"}}>
                              {sec.lines.length===0&&(<div style={{padding:"10px 14px",color:"var(--text3)",fontSize:12,fontStyle:"italic"}}>No lines yet.</div>)}
                              {sec.lines.map(l=>(
                                <div key={l.id} style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:6}}>
                                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                                    <input value={l.label} onChange={e=>setLabel(sec.id,l.id,e.target.value)}
                                      style={{flex:1,fontSize:12,fontWeight:500,padding:"5px 8px",border:"1px solid var(--border)",borderRadius:5,color:"var(--text)"}}/>
                                    {recurring?(
                                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                                        <span style={{fontSize:11,color:"var(--text3)"}}>$/mo</span>
                                        <input type="number" value={(l.monthlyAmounts&&l.monthlyAmounts[0])||0} onChange={e=>setRecurring(sec.id,l.id,e.target.value)}
                                          style={{width:90,fontSize:12,padding:"5px 8px",border:"1px solid var(--border)",borderRadius:5,textAlign:"right"}}/>
                                        <span style={{fontSize:11,color:"var(--text2)",minWidth:90,textAlign:"right"}}>{money(lineTotal(l))}/yr</span>
                                      </div>
                                    ):(
                                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                                        <select value={(l.monthlyAmounts||[]).findIndex(v=>Number(v)>0)===-1?0:(l.monthlyAmounts||[]).findIndex(v=>Number(v)>0)}
                                          onChange={e=>{const idx=Number(e.target.value);const amt=lineTotal(l);mutate(b=>{const ln=b.sections.find(x=>x.id===sec.id)?.lines.find(y=>y.id===l.id);if(ln){const a=Array(12).fill(0);a[idx]=amt;ln.monthlyAmounts=a;}});}}
                                          style={{fontSize:11,padding:"5px 6px",border:"1px solid var(--border)",borderRadius:5}} title="Month this one-off falls in">
                                          {MONTHS_B.map((m,i)=><option key={m} value={i}>{m}</option>)}
                                        </select>
                                        <span style={{fontSize:11,color:"var(--text3)"}}>$</span>
                                        <input type="number" value={lineTotal(l)} onChange={e=>{const idx=(l.monthlyAmounts||[]).findIndex(v=>Number(v)>0);setOneoff(sec.id,l.id,idx===-1?0:idx,e.target.value);}}
                                          style={{width:100,fontSize:12,padding:"5px 8px",border:"1px solid var(--border)",borderRadius:5,textAlign:"right"}}/>
                                      </div>
                                    )}
                                    <button className="btn btn-danger" style={{padding:"4px 8px",fontSize:11}} onClick={()=>delLine(sec.id,l.id)} title="Delete line">✕</button>
                                  </div>
                                  {/* device meta (read-only) */}
                                  {l.deviceMeta&&(
                                    <div style={{fontSize:10,color:"var(--text3)",display:"flex",gap:12,flexWrap:"wrap",paddingLeft:4}}>
                                      {l.deviceMeta.model&&<span>📦 {l.deviceMeta.model}</span>}
                                      {l.deviceMeta.serial&&<span>S/N {l.deviceMeta.serial}</span>}
                                      {l.deviceMeta.usedBy&&<span>👤 {l.deviceMeta.usedBy}</span>}
                                      {l.deviceMeta.purchaseDate&&<span>🛒 {l.deviceMeta.purchaseDate}</span>}
                                      <span>🛡 {l.deviceMeta.warrantyExpiry?("Warranty "+l.deviceMeta.warrantyExpiry):"Warranty unknown"}</span>
                                    </div>
                                  )}
                                  {/* notes */}
                                  <input value={l.notes||""} onChange={e=>setNotes(sec.id,l.id,e.target.value)} placeholder="Notes…"
                                    style={{fontSize:11,padding:"4px 8px",border:"1px dashed var(--border)",borderRadius:5,color:"var(--text2)"}}/>
                                </div>
                              ))}
                            </div>
                            <div style={{padding:"8px 14px"}}>
                              <button className="btn btn-S" style={{fontSize:11,padding:"5px 10px"}} onClick={()=>addLine(sec.id)}>+ Add line</button>
                            </div>
                          </div>
                        );
                      })}

                      {/* 12-month grid */}
                      <div style={{background:"white",border:"1px solid var(--border)",borderRadius:8,overflow:"auto",marginTop:6}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                          <thead>
                            <tr style={{background:"var(--s2)"}}>
                              <th style={{textAlign:"left",padding:"8px 10px",position:"sticky",left:0,background:"var(--s2)",minWidth:180}}>Section</th>
                              {MONTHS_B.map(m=><th key={m} style={{textAlign:"right",padding:"8px 8px",fontWeight:600,color:"var(--text2)"}}>{m}</th>)}
                              <th style={{textAlign:"right",padding:"8px 10px",fontWeight:700}}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {budData.sections.slice().sort((a,b)=>a.order-b.order).map(sec=>{
                              const colSums=MONTHS_B.map((_,i)=>sec.lines.reduce((a,l)=>a+(Number((l.monthlyAmounts||[])[i])||0),0));
                              return(
                                <tr key={sec.id} style={{borderTop:"1px solid var(--border)"}}>
                                  <td style={{padding:"6px 10px",position:"sticky",left:0,background:"white",fontWeight:500}}>{sec.name}</td>
                                  {colSums.map((v,i)=><td key={i} style={{textAlign:"right",padding:"6px 8px",color:v?"var(--text)":"var(--text3)"}}>{v?money(v):"—"}</td>)}
                                  <td style={{textAlign:"right",padding:"6px 10px",fontWeight:600}}>{money(sectionTotal(sec))}</td>
                                </tr>
                              );
                            })}
                            <tr style={{borderTop:"2px solid var(--border2)",background:"var(--s2)"}}>
                              <td style={{padding:"8px 10px",position:"sticky",left:0,background:"var(--s2)",fontWeight:700}}>Total</td>
                              {MONTHS_B.map((_,i)=>{const v=budData.sections.reduce((a,s)=>a+s.lines.reduce((aa,l)=>aa+(Number((l.monthlyAmounts||[])[i])||0),0),0);return <td key={i} style={{textAlign:"right",padding:"8px 8px",fontWeight:600}}>{v?money(v):"—"}</td>;})}
                              <td style={{textAlign:"right",padding:"8px 10px",fontWeight:700,color:"var(--strat)"}}>{money(grand)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* New FY modal */}
                {budNewOpen&&(
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setBudNewOpen(false)}>
                    <div style={{background:"white",borderRadius:10,padding:22,width:360,boxShadow:"var(--shadow-lg)"}} onClick={e=>e.stopPropagation()}>
                      <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:15,marginBottom:12}}>New budget — {companyName(budSelCompany)}</div>
                      <label style={{fontSize:11,color:"var(--text3)",display:"block",marginBottom:4}}>Financial year (e.g. FY27-28)</label>
                      <input value={budNewFy} onChange={e=>setBudNewFy(e.target.value)} placeholder="FY27-28"
                        style={{width:"100%",fontSize:13,padding:"8px 10px",border:"1px solid var(--border2)",borderRadius:6,marginBottom:16}}/>
                      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                        <button className="btn" onClick={()=>setBudNewOpen(false)}>Cancel</button>
                        <button className="btn btn-O" disabled={!/^FY\d{2}-\d{2}$/.test(budNewFy.trim())} onClick={()=>{const fy=budNewFy.trim();setBudData(blankBudget(budSelCompany,fy));setBudSelFy(fy);setBudDirty(true);setBudNewOpen(false);setBudNewFy("");}}>Create</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Roll-forward modal */}
                {budRollOpen&&(
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setBudRollOpen(false)}>
                    <div style={{background:"white",borderRadius:10,padding:22,width:380,boxShadow:"var(--shadow-lg)"}} onClick={e=>e.stopPropagation()}>
                      <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:15,marginBottom:12}}>Roll forward — {companyName(budSelCompany)}</div>
                      <label style={{fontSize:11,color:"var(--text3)",display:"block",marginBottom:4}}>Source FY (clone from)</label>
                      <select value={budRollFrom} onChange={e=>setBudRollFrom(e.target.value)} style={{width:"100%",fontSize:13,padding:"8px 10px",border:"1px solid var(--border2)",borderRadius:6,marginBottom:12}}>
                        <option value="">Select…</option>
                        {budFyList.map(f=>{const fy=f.financialYear||f;return <option key={fy} value={fy}>{fy}</option>;})}
                      </select>
                      <label style={{fontSize:11,color:"var(--text3)",display:"block",marginBottom:4}}>Target FY (new)</label>
                      <input value={budRollTo} onChange={e=>setBudRollTo(e.target.value)} placeholder="FY27-28"
                        style={{width:"100%",fontSize:13,padding:"8px 10px",border:"1px solid var(--border2)",borderRadius:6,marginBottom:8}}/>
                      <div style={{fontSize:10,color:"var(--text3)",marginBottom:14}}>Recurring lines carry over; completed one-offs are dropped; all statuses reset to planned.</div>
                      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                        <button className="btn" onClick={()=>setBudRollOpen(false)}>Cancel</button>
                        <button className="btn btn-O" disabled={!budRollFrom||!/^FY\d{2}-\d{2}$/.test(budRollTo.trim())||budSaving} onClick={rollForward}>{budSaving?"Working…":"Roll forward"}</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Toast */}
                {budToast&&(
                  <div style={{position:"fixed",bottom:24,right:24,background:budToast.kind==="ok"?"var(--ops)":"var(--danger)",color:"white",padding:"12px 18px",borderRadius:8,boxShadow:"var(--shadow-lg)",fontSize:12,fontWeight:600,zIndex:9999,maxWidth:380}}>
                    {budToast.msg}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── SETTINGS TAB ── */}
          {tab==="settings"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",flexShrink:0,background:"white",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontFamily:"var(--ff-head)",fontWeight:700,fontSize:17,color:"var(--text)",letterSpacing:"-0.3px"}}>Settings</div>
                {onSignOut&&<button className="btn btn-danger" style={{fontSize:11}} onClick={onSignOut}>Sign out</button>}
              </div>
              <div className="scroll" style={{padding:"20px",display:"flex",flexDirection:"column",gap:16}}>

                {/* Team Reporting roster + targets. Self-contained component:
                    it owns its own fetches and this file stays one line lighter. */}
                <TeamReportingSettings/>

                {/* Row 1: Autotask PSA + Work Hours + Email Filtering */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                <div className="sg">
                  <div className="sg-h">Autotask PSA</div>
                  <div className="sg-b">
                    {!atConnected?(
                      <div>
                        <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.65}}>
                          Enter your Autotask API credentials. Find them in Autotask → Admin → API Users.<br/>
                          <span style={{color:"var(--text3)",fontSize:10}}>Zone is your AT subdomain e.g. <code>webservices2</code></span>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          <input className="inp" style={{marginBottom:0}} placeholder="API Username (email)" value={atCreds.username} onChange={e=>setAtCreds(c=>({...c,username:e.target.value}))}/>
                          <input className="inp" style={{marginBottom:0}} type="password" placeholder="API Secret Key" value={atCreds.apiKey} onChange={e=>setAtCreds(c=>({...c,apiKey:e.target.value}))}/>
                          <input className="inp" style={{marginBottom:0}} placeholder="Zone (e.g. webservices2)" value={atCreds.zone} onChange={e=>setAtCreds(c=>({...c,zone:e.target.value}))}/>
                        </div>
                        <button className="btn btn-S" style={{marginTop:10}} disabled={atConnecting} onClick={connectAutotask}>
                          {atConnecting?<><div className="at-lookup-spin" style={{borderTopColor:"var(--strat)"}}/>Connecting...</>:"Connect Autotask →"}
                        </button>
                      </div>
                    ):(
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        <div style={{fontSize:11,color:"var(--ops)"}}>✓ Autotask connected</div>
                        <div style={{fontSize:10,color:"var(--text3)"}}>🎫 Ticket button now appears on each email. Contact lookup runs automatically when you open a ticket.</div>
                        <button className="btn btn-danger" style={{fontSize:10,padding:"4px 10px",alignSelf:"flex-start"}} onClick={()=>setAtConnected(false)}>Disconnect</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Work Hours */}
                <div className="sg">
                  <div className="sg-h">Work Hours</div>
                  <div className="sg-b">
                    <div style={{display:"flex",gap:12,alignItems:"center"}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:5,fontWeight:500}}>Day starts</div>
                        <input type="time" className="inp" style={{marginBottom:0}} value={workStart}
                          onChange={e=>{setWorkStart(e.target.value);localStorage.setItem("msp-os-work-start",e.target.value);}}/>
                      </div>
                      <div style={{fontSize:16,color:"var(--text3)",marginTop:16}}>→</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:5,fontWeight:500}}>Day ends</div>
                        <input type="time" className="inp" style={{marginBottom:0}} value={workEnd}
                          onChange={e=>{setWorkEnd(e.target.value);localStorage.setItem("msp-os-work-end",e.target.value);}}/>
                      </div>
                    </div>
                    <div style={{fontSize:10,color:"var(--text3)",marginTop:8}}>{Math.floor((timeToMins(workEnd)-timeToMins(workStart))/60)} hours available per day · used by Plan My Day</div>
                  </div>
                </div>
                {/* Email Filtering */}
                <div className="sg">
                  <div className="sg-h">Email Filtering</div>
                  <div style={{padding:0}}>
                    {[{key:"blockNoise",label:"Block newsletters & notifications"},{key:"allowUrgent",label:"Always show urgent emails"},{key:"allowVip",label:"Always show VIP contact emails"},{key:"allowClient",label:"Always show vCIO client emails"}].map(f=>(
                      <div key={f.key} className="t-row"><span className="t-lbl">{f.label}</span><label className="toggle"><input type="checkbox" checked={filters[f.key]} onChange={e=>setFilters(x=>({...x,[f.key]:e.target.checked}))}/><div className="t-tr"/><div className="t-th"/></label></div>
                    ))}
                  </div>
                </div>
                </div>{/* end row 1 */}

                {/* Autotask Data Sync */}
                <div className="sg">
                  <div className="sg-h">Autotask Data Sync</div>
                  <div className="sg-b">
                    <div style={{fontSize:12,color:"var(--text2)",marginBottom:12,lineHeight:1.65}}>
                      Autotask data syncs automatically on login and every 10 minutes in the background.
                      Use the button below to force an immediate sync.
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
                      {[
                        {label:"Tickets",           key:"tickets",          icon:"🎫"},
                        {label:"Companies",          key:"companies",        icon:"🏢"},
                        {label:"Time Entries",       key:"timeEntries",      icon:"⏱"},
                        {label:"Resources",          key:"resources",        icon:"👤"},
                        {label:"Contracts",          key:"contracts",        icon:"📄"},
                        {label:"Contract Services",  key:"contractServices", icon:"📦"},
                        {label:"Services",           key:"services",         icon:"🔧"},
                        {label:"Service Bundles",    key:"serviceBundles",   icon:"🎁"},
                        {label:"Projects",           key:"projects",         icon:"📋"},
                        {label:"Tasks",              key:"tasks",            icon:"✅"},
                        {label:"Ticket Notes",       key:"ticketNotes",      icon:"💬"},
                        {label:"SLA Results",        key:"slaResults",       icon:"📊"},
                        {label:"Statuses",           key:"ticketStatuses",   icon:"🏷"},
                        {label:"Priorities",         key:"priorities",       icon:"⚡"},
                        {label:"Queues",             key:"queues",           icon:"📬"},
                      ].map(({label,key,icon})=>(
                        <div key={key} style={{padding:"12px",border:"1px solid var(--border)",borderRadius:8,background:"var(--bg)",textAlign:"center"}}>
                          <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text)",marginBottom:4}}>{label}</div>
                          <div style={{fontSize:10,color:"var(--text3)",marginBottom:8}}>
                            {syncStatus?.[key]
                              ?`Synced ${new Date(syncStatus[key]).toLocaleDateString("en-AU")}`
                              :"Not synced yet"}
                          </div>
                          <button className="btn" style={{fontSize:10,padding:"4px 8px",width:"100%",justifyContent:"center"}}
                            onClick={async()=>{if(onExportCsv)await onExportCsv(key);}}>
                            ↓ Export CSV
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                      <button className="btn btn-S" style={{fontSize:12,padding:"8px 16px"}}
                        disabled={syncLoading}
                        onClick={async()=>{
                          setSyncLoading(true);setSyncResults(null);
                          try{
                            const result=await onTriggerSync("all");
                            setSyncResults(result);
                            if(onGetSyncStatus)await onGetSyncStatus();
                          }catch(e){setSyncResults({error:e.message});}
                          finally{setSyncLoading(false);}
                        }}>
                        {syncLoading?"⟳ Syncing all data...":"⟳ Sync all data from Autotask"}
                      </button>
                      <button className="btn" style={{fontSize:11}}
                        onClick={async()=>{if(onGetSyncStatus)await onGetSyncStatus();}}>
                        Refresh status
                      </button>
                      {syncResults&&(
                        <div style={{fontSize:11,color:syncResults.error?"var(--danger)":"var(--ops)",fontWeight:500}}>
                          {syncResults.error
                            ?`✗ Error: ${syncResults.error}`
                            :`✓ Sync complete — ${Object.entries(syncResults.results||{}).map(([k,v])=>`${k}: ${v.count||0}`).join(" · ")}`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row: M365 Email + M365 Calendar + Fellow — 3 columns */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                <div className="sg">
                  <div className="sg-h">M365 — Email</div>
                  <div className="sg-b">
                    {!m365?(<div className="connect-box" style={{margin:0}}><div className="c-icon">📬</div><div className="c-title">Connect Microsoft 365</div><div className="c-desc">Live Outlook inbox. Requires <code>Mail.Read</code>.</div><button className="btn btn-S" onClick={()=>setM365(true)}>Connect →</button></div>)
                    :(<div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}><span style={{fontSize:11,color:"var(--ops)"}}>✓ M365 connected</span><button className="btn btn-danger" style={{fontSize:10,padding:"4px 10px"}} onClick={()=>setM365(false)}>Disconnect</button></div>)}
                  </div>
                </div>
                <div className="sg">
                  <div className="sg-h">M365 — Calendar</div>
                  <div className="sg-b">
                    {!calConn?(<div className="connect-box" style={{margin:0}}><div className="c-icon">📅</div><div className="c-title">Connect Outlook Calendar</div><div className="c-desc">30-day scan, prep scheduling. Requires <code>Calendars.Read</code>.</div><button className="btn btn-C" onClick={connectCal}>Connect →</button></div>)
                    :(<div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}><span style={{fontSize:11,color:"var(--ops)"}}>✓ Calendar connected</span><button className="btn btn-danger" style={{fontSize:10,padding:"4px 10px"}} onClick={()=>{setCalConn(false);setMeetings([]);}}>Disconnect</button></div>)}
                  </div>
                </div>
                <div className="sg">
                  <div className="sg-h">Fellow.app</div>
                  <div className="sg-b">
                    {!fellowConn?(
                      <div className="connect-box" style={{margin:0}}>
                        <div className="c-icon">🤝</div>
                        <div className="c-title">Connect Fellow via MCP</div>
                        <div className="c-desc" style={{lineHeight:1.7}}>
                          <strong>Recommended — set once via Azure:</strong><br/>
                          Azure Portal → App Services → msp-os-api → Environment variables<br/>
                          Add: <code>FELLOW_OAUTH_TOKEN</code> = your Fellow OAuth token<br/>
                          Save → app restarts → Fellow connects automatically.<br/><br/>
                          <strong>To get your token:</strong> Fellow → User Settings → API &amp; MCP Connections → Create new key.<br/><br/>
                          <em style={{fontSize:10,color:"var(--text3)"}}>Or paste below for this session only (resets on page refresh):</em>
                        </div>
                        <div style={{display:"flex",gap:6,marginTop:8}}>
                          <input className="inp" style={{marginBottom:0,flex:1}} type="password"
                            placeholder="Fellow OAuth token (session only)..."
                            value={fellowKey} onChange={e=>setFellowKey(e.target.value)}/>
                          <button className="btn btn-fellow"
                            onClick={async()=>{
                              if(!fellowKey.trim()) return;
                              try{
                                if(onSaveFellowToken) await onSaveFellowToken(fellowKey.trim());
                                setFellowConn(true);
                                setFellowKey("");
                              }catch(e){
                                alert("Failed to save token: "+e.message);
                              }
                            }}>Use</button>
                        </div>
                      </div>
                    ):(
                      <div>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                          <span style={{fontSize:11,color:"var(--fellow)"}}>✓ Fellow connected via MCP</span>
                          <button className="btn btn-danger" style={{fontSize:10,padding:"4px 10px"}} onClick={()=>setFellowConn(false)}>Disconnect</button>
                        </div>
                        <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.6}}>
                          Meeting summaries and action items from Fellow available in:<br/>
                          • Meeting Notes tab — browse and extract todos<br/>
                          • AI Brief — prep blocks include recent meeting context<br/>
                          • Plan My Day AI — aware of recent meeting outcomes
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>{/* end M365 + Fellow 3-col */}
                {m365Pick&&(
                  <div onClick={()=>setM365Pick(null)}
                    style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                    <div onClick={e=>e.stopPropagation()} style={{background:"var(--card,#fff)",borderRadius:12,padding:18,width:460,maxWidth:"100%",maxHeight:"80vh",overflow:"auto"}}>
                      <div style={{fontWeight:700,fontSize:14,marginBottom:2}}>Which domains belong to {m365Pick.companyName}?</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>
                        Tenant <strong>{m365Pick.tenantName||"(unnamed)"}</strong> hosts {m365Pick.domains.length} domain{m365Pick.domains.length===1?"":"s"}.
                        {m365Pick.domains.length>1&&" Several clients can share one tenant — tick only this client's."}
                      </div>
                      {m365Pick.domains.length===0&&(
                        <div style={{fontSize:12,color:"var(--warn)"}}>No verified custom domains on this tenant (only *.onmicrosoft.com, which is never a client mail domain).</div>
                      )}
                      {m365Pick.domains.map(d=>(
                        <label key={d.name} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 2px",fontSize:12.5,cursor:"pointer"}}>
                          <input type="checkbox" checked={m365Pick.chosen.includes(d.name)}
                            onChange={e=>setM365Pick(p=>({...p,chosen:e.target.checked?[...p.chosen,d.name]:p.chosen.filter(x=>x!==d.name)}))}/>
                          <span>{d.name}</span>
                          {d.isDefault&&<span style={{fontSize:9.5,color:"var(--text3)"}}>(tenant default)</span>}
                        </label>
                      ))}
                      <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
                        <button className="btn btn-S" onClick={()=>setM365Pick(null)}>Cancel</button>
                        <button className="btn btn-O" onClick={()=>{
                          const {companyId,chosen}=m365Pick;
                          saveM365Domains(companyId,chosen).then(()=>{setM365Pick(null);loadM365();})
                            .catch(e=>setUnifiErr(e?.response?.data?.error||e.message||"Could not save domains"));
                        }}>Save domains</button>
                      </div>
                    </div>
                  </div>
                )}
                {/* VIP Contacts on its own row so the Client Register below can
                    span the full page width — it now carries Domain, Prep Email,
                    UniFi Site, Industry, FortiGate SN and WAN. */}
                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:16}}>
                <div className="sg">
                  <div className="sg-h">VIP Contacts</div>
                  <div className="sg-b">
                    <div style={{display:"flex",gap:6}}><input className="inp" style={{marginBottom:0,flex:1}} placeholder="Name or email..." value={newVip} onChange={e=>setNewVip(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addVip()}/><button className="btn btn-O" onClick={addVip}>Add</button></div>
                  </div>
                  <div style={{padding:"0 13px 10px"}}>
                    {vips.map(v=>(<div key={v} className="vip-r"><div style={{fontSize:11,color:"var(--text2)",display:"flex",alignItems:"center",gap:7}}><span style={{color:"var(--strat)"}}>★</span>{v}</div><button className="ico-btn" onClick={()=>setVips(vs=>{const next=vs.filter(x=>x!==v);saveSnapshot({vips:next});return next;})}>×</button></div>))}
                  </div>
                </div>
                </div>
                {/* ⚠ minmax(0,1fr), NOT 1fr — THIS IS THE ROOT CAUSE OF THE
                    PAGE-LEVEL SCROLLBAR. A `1fr` track is `minmax(auto,1fr)`,
                    and `auto` there means min-content: the track REFUSES to
                    shrink below the register's ~1860px of columns, so the grid
                    grew, its flex ancestors grew, the page grew, and the
                    horizontal scrollbar appeared at the bottom of the WINDOW.
                    The register's own overflow-x:auto never engaged, because
                    there was nothing left to overflow.
                    Same reason `.sg` below needs minWidth:0 — a GRID ITEM has
                    the same automatic minimum unless its overflow is not
                    visible, and .sg is overflow:visible. Both are needed; either
                    one alone still lets the page stretch. */}
                <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr)",gap:16}}>
                <div className="sg" style={{minWidth:0}}>
                  <div className="sg-h">Client Register</div>
                  <div className="sg-b">
                    {atCompanies.length===0?(
                      <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",padding:"12px 0"}}>
                        No companies loaded.<br/>
                        <button className="btn btn-S" style={{fontSize:10,marginTop:8}} onClick={()=>setTab("settings")}>Run Autotask Data Sync →</button>
                      </div>
                    ):(
                      <>
                        <div style={{fontSize:10,color:"var(--text3)",marginBottom:8}}>{atCompanies.length} companies · {managedCompanyIds.size} managed · tick <strong>Managed</strong> to include in Customer Sentiment Analysis</div>
                        {/* UniFi mapping controls — the column below writes straight to the backend config */}
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:8,fontSize:10,color:"var(--text3)"}}>
                          <span><strong>{Object.keys(unifiByCid).length}</strong> mapped to UniFi</span>
                          {unifiMeta.lastSyncAt&&<span>· last sync {new Date(unifiMeta.lastSyncAt).toLocaleString()}</span>}
                          {!unifiMeta.selfHostedConfigured&&<span style={{color:"var(--warn)"}}>· self-hosted controller not configured</span>}
                          <button className="btn btn-S" style={{fontSize:10}} disabled={unifiSyncing} onClick={runUnifiSyncNow}>
                            {unifiSyncing?"Syncing…":"↻ Sync UniFi"}
                          </button>
                          {unifiErr&&<span style={{color:"var(--warn)"}}>⚠ {unifiErr}</span>}
                        </div>
                        {/* Filter row — directly above the register. Tier (the canonical
                            enum, matching what each row DISPLAYS) and/or Company Name
                            (case-insensitive substring), AND-combined so either works
                            alone. Sits OUTSIDE the scroll wrapper below so it never joins
                            the horizontal scroll and never touches the column layout. */}
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:8,fontSize:10,color:"var(--text3)"}}>
                          <label style={{display:"inline-flex",alignItems:"center",gap:4}}>
                            <span>Tier</span>
                            <select className="inp" aria-label="Filter by tier" value={registerTierFilter} onChange={e=>setRegisterTierFilter(e.target.value)}
                              style={{width:110,marginBottom:0,fontSize:10,padding:"3px 6px"}}>
                              <option value="">All</option>
                              {CLIENT_TIERS.map(tv=><option key={tv} value={tv}>{tv}</option>)}
                            </select>
                          </label>
                          <label style={{display:"inline-flex",alignItems:"center",gap:4}}>
                            <span>Company</span>
                            <input className="inp" type="text" aria-label="Filter by company name" value={registerNameFilter} placeholder="Name contains…"
                              onChange={e=>setRegisterNameFilter(e.target.value)} style={{width:180,marginBottom:0,fontSize:10,padding:"3px 6px"}}/>
                          </label>
                          <span data-testid="register-count">showing {registerRows.length} of {atCompanies.length}</span>
                          {isRegisterFilterActive({tier:registerTierFilter,name:registerNameFilter})&&(
                            <button className="btn btn-S" style={{fontSize:10}} onClick={()=>{setRegisterTierFilter("");setRegisterNameFilter("");}}>Clear filters</button>
                          )}
                        </div>
                        {/* ⚠ THE ONLY HORIZONTAL SCROLLER, and it always was —
                            overflowX:auto and minWidth:0 were already right here.
                            It never engaged because the grid track and the .sg
                            item above both refused to shrink below their
                            min-content, so the register pushed the whole PAGE
                            wider instead and the browser put the scrollbar under
                            the window. Fixing those two lets this one do its job.
                            maxWidth:100% so it can never be the thing that grows. */}
                        <div style={{overflowX:"auto",overflowY:"auto",maxHeight:480,margin:"0 -16px",padding:"0 16px 8px",minWidth:0,maxWidth:"100%",WebkitOverflowScrolling:"touch"}}>
                        <datalist id="dt-industries">{industrySuggestions.map(x=><option key={x} value={x}/>)}</datalist>
                        {/* ⚠ THIS LIST NO LONGER SCROLLS — the wrapper above owns
                            BOTH axes now, and that is what removes the second
                            scrollbar. While this had its own overflowY:auto, the
                            other axis computed from `visible` to `auto` with it,
                            so once the window was narrow enough to need
                            horizontal scrolling the rows grew a 15px h-scrollbar
                            of their own INSIDE the section's — two scrollbars for
                            one axis, moving independently. Measured, not guessed.
                            It keeps minWidth so the rows never render narrower
                            than the header above them. */}
                        <div style={{minWidth:REGISTER_MIN_W}}>
                        {/* ⚠ THE HEADER SITS IN THE SAME WIDTH CONTEXT AS THE ROWS,
                            which is what keeps the thirteen columns lined up. When it
                            sat outside the scrolling list, that list's vertical
                            scrollbar made the rows ~15px narrower than the header and
                            every column drifted a little further out of line across the
                            row. Measured at 3318 vs 3303 before, 3303 vs 3303 after.

                            position:sticky keeps the column names visible while
                            scrolling down 60-odd clients — the wrapper above is the
                            scrollport for both axes. Deliberately NOT sticky
                            horizontally: header and rows must travel sideways together
                            or they stop lining up. */}
                        <div style={{display:"grid",gridTemplateColumns:REGISTER_COLS,minWidth:REGISTER_MIN_W,gap:8,marginBottom:0,padding:"5px 0",borderBottom:"2px solid var(--border)",alignItems:"center",background:"var(--s1)",position:"sticky",top:0,zIndex:2,whiteSpace:"nowrap"}}>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>ID</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Company</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Domain</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Prep Form Email</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>UniFi Site</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Industry</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>FortiGate</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>WAN</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>M365</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>IT Glue</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}} title="The exact client name each vendor prints in its report PDF, when it differs from the Autotask name">Report ID</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Tier</div>
                          <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",letterSpacing:"0.08em",textTransform:"uppercase",textAlign:"center"}}>Mgd</div>
                        </div>
                          {registerRows.length===0&&(
                            <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",padding:"18px 0"}}>
                              No clients match — adjust the Tier or Company filter above.
                            </div>
                          )}
                          {registerRows.map(c=>{
                            const cid=String(c.id);
                            const override=clientDomainOverrides[cid];
                            const derived=c.webAddress
                              ?c.webAddress.replace(/^https?:\/\//,"").replace(/^www\./,"").split("/")[0].toLowerCase()
                              :"";
                            const displayDomain=override||derived;
                            const tier=c._tier||"Adhoc";
                            const tierColor=tier==="Gold"?"#f59e0b":tier==="Silver"?"#6b7280":tier==="Bronze"?"#92400e":tier==="Copper"?"#b45309":"var(--text3)";
                            const isTierManual=c._tierSource==="manual";
                            const isMgd=managedCompanyIds.has(cid);
                            const isOverridden=!!override;
                            const savedEmail=clientEmailOverrides[cid]||"";
                            const hasEmail=!!savedEmail;
                            // Domain-mismatch warning: the saved email's domain vs the
                            // company's known domain. Gated on blur (focusedEmailCid!==cid)
                            // so it doesn't flash mid-typing; persists until corrected.
                            const _emailDom=emailDomainOf(savedEmail);
                            const _coDom=(displayDomain||"").toLowerCase().trim();
                            const domainWarn=!!(savedEmail&&_coDom&&_emailDom&&_emailDom!==_coDom&&focusedEmailCid!==cid);
                            // UniFi mapping for this row + how the last sync went.
                            const unifiRow=unifiByCid[cid]||null;
                            const unifiBusy=unifiSavingCid===cid;
                            const unifiState=
                              !unifiRow                    ?{text:"",color:"var(--text3)"}
                              :unifiRow.state==="syncing"  ?{text:`✓ syncing${unifiRow.clientsOnline!=null?` · ${unifiRow.clientsOnline} clients`:""}`,color:"var(--tact)"}
                              :unifiRow.state==="error"    ?{text:"✕ sync error",color:"var(--warn)"}
                              :unifiRow.state==="pending"  ?{text:"⚠ not yet reporting",color:"var(--warn)"}
                                                           :{text:"· awaiting first sync",color:"var(--text3)"};
                            return(
                              <div key={c.id} style={{display:"grid",gridTemplateColumns:REGISTER_COLS,minWidth:REGISTER_MIN_W,gap:8,padding:"5px 0",borderBottom:"1px solid var(--border)",alignItems:"center",whiteSpace:"nowrap",background:isMgd?"rgba(11,128,165,0.04)":"transparent"}}>
                                <div style={{fontSize:12,fontFamily:"monospace",color:"var(--text3)",userSelect:"all",cursor:"text",whiteSpace:"nowrap"}} title={`Autotask companyId: ${cid} (read-only — click to select)`}>{cid}</div>
                                <div style={{fontSize:13,color:"var(--text)",fontWeight:500,whiteSpace:"nowrap"}} title={c.companyName}>{c.companyName}</div>
                                {/* Editable domain */}
                                <input className="inp"
                                  style={{fontSize:12,fontFamily:"monospace",padding:"3px 6px",height:28,width:"100%",borderColor:isOverridden?"var(--strat)":"var(--border)",color:isOverridden?"var(--strat)":"var(--text3)"}}
                                  value={displayDomain}
                                  placeholder="domain"
                                  title={isOverridden?"Custom domain":"From AT webAddress"}
                                  onChange={e=>{
                                    const val=e.target.value.toLowerCase().trim();
                                    setClientDomainOverrides(prev=>{const next={...prev,[cid]:val};saveSnapshot({clientDomainOverrides:next});return next;});
                                  }}/>
                                {/* Editable prep form email */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  <input className="inp"
                                    type="email"
                                    style={{fontSize:12,padding:"3px 6px",height:28,width:"100%",borderColor:domainWarn?"var(--warn)":hasEmail?"var(--tact)":"var(--border)",color:hasEmail?"var(--tact)":"var(--text3)"}}
                                    value={savedEmail}
                                    placeholder="prep form email…"
                                    title={hasEmail?"Pre-meeting form will be sent here":"Add email to auto-send prep form"}
                                    onFocus={()=>setFocusedEmailCid(cid)}
                                    onBlur={()=>setFocusedEmailCid(prev=>prev===cid?null:prev)}
                                    onChange={e=>{
                                      const val=e.target.value.trim();
                                      setClientEmailOverrides(prev=>{const next={...prev,[cid]:val};saveSnapshot({clientEmailOverrides:next});return next;});
                                    }}/>
                                  {domainWarn&&<span style={{fontSize:10,color:"var(--warn)",lineHeight:1.2}}>⚠ Domain doesn't match (expected @{displayDomain})</span>}
                                </div>
                                {/* UniFi site — picker + report type + last-sync state */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  <select className="inp"
                                    style={{fontSize:12,padding:"3px 5px",height:28,borderColor:unifiRow?"var(--tact)":"var(--border)",color:unifiRow?"var(--tact)":"var(--text3)",opacity:unifiBusy?0.55:1}}
                                    value={unifiRow?.siteId||""}
                                    disabled={unifiBusy||unifiSites.length===0}
                                    title={unifiRow?`${unifiRow.siteName} · ${unifiRow.accessMode}`:"Not mapped to a UniFi site"}
                                    onChange={e=>saveUnifiSite(cid,c.companyName,e.target.value)}>
                                    <option value="">{unifiSites.length===0?"loading sites…":"— not mapped —"}</option>
                                    {unifiSites.map(s=>(
                                      <option key={s.siteId} value={s.siteId}>{s.label||s.siteName}{s.isOld?" (old)":""}</option>
                                    ))}
                                  </select>
                                  {unifiRow&&(
                                    <div style={{display:"flex",gap:4,alignItems:"center",minWidth:0}}>
                                      <select className="inp"
                                        style={{fontSize:9,padding:"1px 3px",height:19,flexShrink:0,color:"var(--text3)"}}
                                        value={unifiRow.reportType||"guestwifi"}
                                        disabled={unifiBusy}
                                        title="Which WiFi section the monthly report renders"
                                        onChange={e=>saveUnifiReportType(cid,c.companyName,e.target.value)}>
                                        <option value="guestwifi">guest</option>
                                        <option value="standard">standard</option>
                                      </select>
                                      <span style={{fontSize:9,lineHeight:1.15,color:unifiState.color,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                                        title={unifiRow.reason||(unifiRow.syncedAt?`Last synced ${new Date(unifiRow.syncedAt).toLocaleString()}`:"")}>
                                        {unifiState.text}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {/* Industry — themes the report's "Did You Know?" panel.
                                    Saved server-side (not saveSnapshot) so the report
                                    generator can read it at generate time. */}
                                <input className="inp" list="dt-industries"
                                  style={{fontSize:12,padding:"3px 6px",height:28,borderColor:industryByCid[cid]?"var(--tact)":"var(--border)",color:industryByCid[cid]?"var(--tact)":"var(--text3)"}}
                                  defaultValue={industryByCid[cid]||""}
                                  placeholder="industry…"
                                  title="Used to theme the monthly report's Did You Know panel"
                                  onBlur={e=>saveIndustry(cid,e.target.value)}/>
                                {/* FortiGate serial — the device key for the report's
                                    Network & Internet section. Saved server-side. */}
                                <select className="inp"
                                  style={{fontSize:12,padding:"3px 5px",height:28,borderColor:fgByCid[cid]?.sn?"var(--tact)":"var(--border)",color:fgByCid[cid]?.sn?"var(--tact)":"var(--text3)"}}
                                  value={fgByCid[cid]?.sn||""}
                                  title={fgByCid[cid]?.sn?`Reporting from ${fgByCid[cid].sn}`:"No FortiGate mapped — the Network section is omitted"}
                                  onChange={e=>saveFortigate(cid,e.target.value,fgByCid[cid]?.wan)}>
                                  <option value="">{fgDevices.length?"— none —":"loading…"}</option>
                                  {fgDevices.map(dv=>(
                                    <option key={dv.sn} value={dv.sn}>{dv.name}{dv.logging?"":" (no logs)"}{dv.suggested&&String(dv.suggested.id)===cid?" ★":""}</option>
                                  ))}
                                </select>
                                <input className="inp"
                                  style={{fontSize:12,padding:"3px 5px",height:28,color:"var(--text3)"}}
                                  defaultValue={fgByCid[cid]?.wan||""}
                                  placeholder="wan1"
                                  title="Optional WAN interface; blank = auto-detect"
                                  onBlur={e=>{const v=e.target.value.trim();if(v!==(fgByCid[cid]?.wan||""))saveFortigate(cid,fgByCid[cid]?.sn||"",v);}}/>
                                {/* M365 — admin-consent the read-only app into the
                                    client's tenant. No tenant ID is ever typed. */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  {(()=>{ const m=m365ByCid[cid]; const busy=m365Busy===cid;
                                    const ok=m&&m.tenantId&&m.lastCheck&&m.lastCheck.ok;
                                    const bad=m&&m.tenantId&&m.lastCheck&&!m.lastCheck.ok;
                                    const doms=(m&&m.ownedDomains)||[];
                                    if(!m||!m.tenantId) return (<>
                                      <button className="btn btn-S" style={{fontSize:9.5,padding:"2px 6px"}} disabled={busy}
                                        title="Admin-consent the read-only reporting app into this client's Microsoft 365 tenant"
                                        onClick={()=>connectM365(cid,c.companyName)}>{busy?"…":"Connect M365"}</button>
                                      {m365Tenants.length>0&&(
                                        <select className="inp" style={{fontSize:9,padding:"1px 3px",height:19,marginTop:2,color:"var(--text3)"}}
                                          value="" disabled={busy}
                                          title="This client shares a tenant that is already connected — reuse it instead of consenting again"
                                          onChange={e=>{const t=e.target.value;if(t)reuseM365Tenant(cid,c.companyName,t);}}>
                                          <option value="">or reuse a connected tenant…</option>
                                          {m365Tenants.map(t=>(
                                            <option key={t.tenantId} value={t.tenantId}>{t.tenantName||t.tenantId.slice(0,8)}{t.domains.length?" ("+t.domains.join(", ")+")":""}</option>
                                          ))}
                                        </select>
                                      )}
                                    </>);
                                    return (<>
                                      <div style={{fontSize:9.5,fontWeight:700,color:ok?"var(--tact)":"var(--warn)"}}
                                        title={(m.tenantName||m.tenantId)+(bad?("  —  "+((m.lastCheck||{}).error||"")):"")}>
                                        {ok?"✓ Connected":"⚠ No access"}
                                      </div>
                                      <div style={{fontSize:9,color:"var(--text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                        {doms.length?doms.join(", "):"no domain set"}
                                      </div>
                                      <div style={{display:"flex",gap:3}}>
                                        <button className="btn btn-S" style={{fontSize:9,padding:"1px 5px"}} disabled={busy}
                                          onClick={()=>openDomainPicker(cid,c.companyName)}>domains</button>
                                        {bad&&<button className="btn btn-S" style={{fontSize:9,padding:"1px 5px"}} disabled={busy}
                                          onClick={()=>recheckM365Conn(cid)}>re-check</button>}
                                        <button className="btn btn-S" style={{fontSize:9,padding:"1px 5px"}} disabled={busy}
                                          title="Re-run consent" onClick={()=>connectM365(cid,c.companyName)}>↻</button>
                                      </div>
                                    </>);
                                  })()}
                                </div>
                                {/* IT Glue org id — source of the documented
                                    passwords the credential check rates. */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  <input className="inp"
                                    style={{fontSize:12,padding:"3px 5px",height:28,borderColor:(m365ByCid[cid]||{}).itGlueOrgId?"var(--tact)":"var(--border)",color:(m365ByCid[cid]||{}).itGlueOrgId?"var(--tact)":"var(--text3)"}}
                                    defaultValue={(m365ByCid[cid]||{}).itGlueOrgId||""}
                                    key={"itg-"+cid+"-"+((m365ByCid[cid]||{}).itGlueOrgId||"")}
                                    placeholder="org id"
                                    title="IT Glue organisation id — used to match documented passwords to mailboxes"
                                    onBlur={e=>{const v=e.target.value.trim();if(v!==(((m365ByCid[cid]||{}).itGlueOrgId)||""))saveItGlue(cid,v);}}/>
                                  <button className="btn btn-S" style={{fontSize:9,padding:"1px 5px"}} disabled={m365Busy===cid}
                                    title="Find the IT Glue organisation by this client's name"
                                    onClick={()=>findItGlue(cid)}>{m365Busy===cid?"…":"find"}</button>
                                </div>
                                {/* Vendor report identity — only needed when the
                                    vendor's name for the client differs from
                                    Autotask's. Blank = match on the company name. */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  <input className="inp"
                                    style={{fontSize:11,padding:"2px 5px",height:24,borderColor:(m365ByCid[cid]||{}).reportIdentityCyber?"var(--tact)":"var(--border)",color:(m365ByCid[cid]||{}).reportIdentityCyber?"var(--tact)":"var(--text3)"}}
                                    defaultValue={(m365ByCid[cid]||{}).reportIdentityCyber||""}
                                    key={"ric-"+cid+"-"+((m365ByCid[cid]||{}).reportIdentityCyber||"")}
                                    placeholder="cyber name"
                                    title={"Slipstream's exact name for "+c.companyName+" — leave blank if it matches the Autotask name"}
                                    onBlur={e=>{const v=e.target.value.trim();if(v!==(((m365ByCid[cid]||{}).reportIdentityCyber)||""))saveReportIdentity(cid,"reportIdentityCyber",v);}}/>
                                  <input className="inp"
                                    style={{fontSize:11,padding:"2px 5px",height:24,borderColor:(m365ByCid[cid]||{}).reportIdentityBackup?"var(--tact)":"var(--border)",color:(m365ByCid[cid]||{}).reportIdentityBackup?"var(--tact)":"var(--text3)"}}
                                    defaultValue={(m365ByCid[cid]||{}).reportIdentityBackup||""}
                                    key={"rib-"+cid+"-"+((m365ByCid[cid]||{}).reportIdentityBackup||"")}
                                    placeholder="backup name"
                                    title={"Datto BCDR's exact name for "+c.companyName+" — leave blank if it matches the Autotask name"}
                                    onBlur={e=>{const v=e.target.value.trim();if(v!==(((m365ByCid[cid]||{}).reportIdentityBackup)||""))saveReportIdentity(cid,"reportIdentityBackup",v);}}/>
                                  <input className="inp"
                                    style={{fontSize:11,padding:"2px 5px",height:24,borderColor:(m365ByCid[cid]||{}).reportIdentityDarkWeb?"var(--tact)":"var(--border)",color:(m365ByCid[cid]||{}).reportIdentityDarkWeb?"var(--tact)":"var(--text3)"}}
                                    defaultValue={(m365ByCid[cid]||{}).reportIdentityDarkWeb||""}
                                    key={"rid-"+cid+"-"+((m365ByCid[cid]||{}).reportIdentityDarkWeb||"")}
                                    placeholder="dark web name"
                                    title={"The dark-web report's exact \"Prepared for\" name for "+c.companyName+" — leave blank if it matches the Autotask name"}
                                    onBlur={e=>{const v=e.target.value.trim();if(v!==(((m365ByCid[cid]||{}).reportIdentityDarkWeb)||""))saveReportIdentity(cid,"reportIdentityDarkWeb",v);}}/>
                                  {/* 3CX after-hours divert number. Matched EXACTLY
                                      against the CSV's `To` column, so it is stored
                                      digits-only — normalised here as well as on the
                                      server, or the optimistic local value and the
                                      saved one would disagree until a reload. */}
                                  <input className="inp"
                                    style={{fontSize:11,padding:"2px 5px",height:24,borderColor:(m365ByCid[cid]||{}).phoneNitelNumber?"var(--tact)":"var(--border)",color:(m365ByCid[cid]||{}).phoneNitelNumber?"var(--tact)":"var(--text3)"}}
                                    defaultValue={(m365ByCid[cid]||{}).phoneNitelNumber||""}
                                    key={"pnn-"+cid+"-"+((m365ByCid[cid]||{}).phoneNitelNumber||"")}
                                    placeholder="after-hrs divert no."
                                    title={"The number "+c.companyName+"'s phone system diverts to after hours (the Nitel number). Used to count after-hours calls on the Phone System report — leave blank if this site has no divert."}
                                    onBlur={e=>{const v=e.target.value.replace(/[^\d]/g,"");if(v!==(((m365ByCid[cid]||{}).phoneNitelNumber)||""))saveReportIdentity(cid,"phoneNitelNumber",v);}}/>
                                </div>
                                {/* ⛔ TIER IS A MANUAL OVERRIDE THAT THE SYNC NEVER CLOBBERS.
                                    It used to be derived from the Autotask CONTRACT NAME on every
                                    read, which is why several were wrong — and re-syncing brought
                                    the wrong value straight back. Setting it here writes a
                                    server-side override in its own blob, merged over the derivation
                                    at read time.
                                    ⚠ A DROPDOWN, never free text: these five strings are the keys
                                    CADENCE_TEMPLATES is looked up by, so a typo would not fail
                                    loudly — it would silently fall through to the Adhoc cadence. */}
                                <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0}}>
                                  <select className="inp"
                                    style={{fontSize:12,padding:"3px 6px",height:28,width:"100%",
                                      fontWeight:600,color:tierColor,
                                      borderColor:isTierManual?"var(--tact)":"var(--border)"}}
                                    value={tier}
                                    disabled={tierSaving===cid}
                                    title={isTierManual
                                      ? "Set by you — the Autotask sync will not change it"
                                      : "Derived from the Autotask contract name. Choose a value to lock it."}
                                    onChange={async e=>{
                                      const val=e.target.value;
                                      setTierSaving(cid);
                                      try{
                                        await setClientTier(cid,val);
                                        /* Patch in place so the row reflects the new value and its
                                           provenance without a full reload. */
                                        setAtCompanies(prev=>prev.map(x=>String(x.id)===cid
                                          ?{...x,_tier:val,_tierSource:val?"manual":"derived"}:x));
                                      }catch(err){
                                        alert("Could not save the tier: "+(err?.response?.data?.error||err.message));
                                      }finally{ setTierSaving(null); }
                                    }}>
                                    {CLIENT_TIERS.map(tv=><option key={tv} value={tv}>{tv}</option>)}
                                  </select>
                                  {/* Light provenance marker — which tiers are explicit, and which
                                      are still guessing from a contract name. */}
                                  <span style={{fontSize:9,lineHeight:1.2,color:isTierManual?"var(--tact)":"var(--text3)"}}>
                                    {tierSaving===cid?"saving…":isTierManual?"● set by you":"auto (from contract)"}
                                  </span>
                                </div>
                                <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>
                                  <input type="checkbox" checked={isMgd}
                                    style={{width:15,height:15,accentColor:"var(--strat)",cursor:"pointer"}}
                                    onChange={e=>{
                                      setManagedCompanyIds(prev=>{
                                        const next=new Set(prev);
                                        if(e.target.checked) next.add(cid); else next.delete(cid);
                                        saveSnapshot({managedCompanyIds:[...next]});
                                        return next;
                                      });
                                    }}/>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        </div>{/* end horizontal scroll wrapper */}
                      </>
                    )}
                  </div>
                </div>
                </div>{/* end VIP + Client Register 2-col */}

                {/* Pre-Meeting Form — moved to its own pages */}
                <div className="sg">
                  <div className="sg-h">Pre-Meeting Form</div>
                  <div className="sg-b">
                    <div style={{fontSize:12,color:"var(--text2)",marginBottom:14,lineHeight:1.65}}>
                      The Pre-Meeting Form has its own pages now. Create reusable question <strong>templates</strong> for different industries, then <strong>assign each managed customer</strong> to a template.
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <button className="btn btn-T" style={{fontSize:11,padding:"6px 14px"}}
                        onClick={()=>goTab("prepformtemplates")}>
                        📝 Manage Templates
                      </button>
                      <button className="btn" style={{fontSize:11,padding:"6px 14px"}}
                        onClick={()=>goTab("prepformassign")}>
                        🔗 Assign Customers to Templates
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quoting Configuration */}
                <div className="sg">
                  <div className="sg-h">Quoting Configuration</div>
                  <div className="sg-b">
                    <div style={{fontSize:12,color:"var(--text2)",marginBottom:14,lineHeight:1.65}}>
                      AI quote-suggest searches all enabled suppliers' catalogs for matching items, restricted to each supplier's priority brands. Default markup is applied to suggested line items but each line stays editable.
                    </div>

                    {/* Global: default markup */}
                    <div style={{marginBottom:18,padding:"12px 14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:6}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Default markup (applies to all suppliers)</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,lineHeight:1.5}}>
                        Applied to AI-suggested line items: <strong>price = cost × (1 + markup%)</strong>. Each line stays editable in the quote builder.
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <input type="number" className="inp" style={{width:80,marginBottom:0,fontSize:13,textAlign:"right",fontWeight:600}}
                          value={defaultMarkupPct}
                          min="0" max="500" step="1"
                          onChange={e=>{
                            const v=e.target.value===""?0:Number(e.target.value);
                            setDefaultMarkupPct(v);
                            saveSnapshot({defaultMarkupPct:v});
                          }}/>
                        <span style={{fontSize:13,color:"var(--text2)"}}>%</span>
                        <span style={{fontSize:11,color:"var(--text3)",marginLeft:8}}>e.g. cost $1,000 → price ${(1000*(1+(Number(defaultMarkupPct)||0)/100)).toFixed(0)}</span>
                      </div>
                    </div>

                    {/* Global: default terms & conditions ─────────────────────
                        ⚠ THIS IS THE DEFAULT, NOT A LIVE GLOBAL. Each quote
                        snapshots it at creation and keeps that copy for good.
                        Rendering every quote from this box instead would be the
                        easier build and would rewrite history: acceptance is
                        about to raise invoices, and the terms behind an accepted
                        offer are what the customer agreed to. See quoteTerms.js.

                        The line under the box is load-bearing UI, not a caption.
                        Without it, the first edit that visibly fails to reach an
                        open draft reads as a bug. */}
                    <div style={{marginBottom:18,padding:"12px 14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:6}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Default terms &amp; conditions</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,lineHeight:1.5}}>
                        Printed at the foot of every quote, after the items and totals. Leave a
                        blank line between paragraphs. Craig can edit or add further clauses on
                        an individual quote in the builder.
                      </div>
                      <textarea className="inp" rows={9}
                        style={{marginBottom:0,fontSize:12,lineHeight:1.55,resize:"vertical"}}
                        value={quoteTermsDefault}
                        onChange={e=>{
                          setQuoteTermsDefault(e.target.value);
                          saveSnapshot({quoteTermsDefault:e.target.value});
                        }}/>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:10,color:"var(--warn)",fontWeight:600}}>
                          ⚠ Applies to NEW quotes only — existing quotes keep the wording they were created with.
                        </span>
                        <div style={{flex:1}}/>
                        <button className="btn" style={{fontSize:10,padding:"3px 8px"}}
                          disabled={quoteTermsDefault===DEFAULT_QUOTE_TERMS_TEXT}
                          onClick={()=>{
                            setQuoteTermsDefault(DEFAULT_QUOTE_TERMS_TEXT);
                            saveSnapshot({quoteTermsDefault:DEFAULT_QUOTE_TERMS_TEXT});
                          }}>↻ Restore Datatech standard</button>
                      </div>
                    </div>

                    {/* Quote templates — the only place they can be SEEN, renamed
                        or deleted; before this the list was two dropdowns and a
                        typo lived in them forever. Templates hold cost and margin
                        (fine — internal), so nothing here reaches a client. */}
                    <div data-testid="settings-quote-templates" style={{marginBottom:18,padding:"12px 14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:6}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Quote templates ({quoteTemplates.length})</div>
                      {quoteTemplates.length===0 ? (
                        <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>None yet — open a quote and use “📚 Save as template”.</div>
                      ) : (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {quoteTemplates.map(t=>(
                            <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"white",border:"1px solid var(--border)",borderRadius:6,fontSize:11}}>
                              <span style={{fontWeight:600,color:"var(--text)",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</span>
                              <span style={{color:"var(--text3)",flexShrink:0}}>
                                {isSectionTemplate(t)?`${t.sections.length} section${t.sections.length===1?"":"s"} · `:""}{templateItemCount(t)} item{templateItemCount(t)===1?"":"s"}{t.createdAt?` · ${new Date(t.createdAt).toLocaleDateString()}`:""}
                              </span>
                              <button className="btn" style={{fontSize:10,padding:"2px 8px"}} onClick={()=>renameTemplate(t.id)}>✎ Rename</button>
                              <button className="btn btn-danger" style={{fontSize:10,padding:"2px 8px"}} onClick={()=>deleteTemplate(t.id)} title="Delete this template">🗑</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Per-supplier configuration */}
                    <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>Suppliers ({suppliers.length})</div>
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {suppliers.map(sup => {
                        const stale = isSupplierStale(sup);
                        const ageD = supplierAgeDays(sup);
                        return (
                          <div key={sup.id} style={{padding:14,background:"white",border:`1px solid ${sup.enabled?"var(--border)":"var(--border2)"}`,borderRadius:6,opacity:sup.enabled?1:0.65}}>
                            {/* Header row */}
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:10}}>
                              <div style={{display:"flex",alignItems:"center",gap:10,flex:1}}>
                                <span style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>📦 {sup.name}</span>
                                <span style={{fontSize:9,color:"var(--text3)",background:"var(--s2)",padding:"2px 7px",borderRadius:10,textTransform:"uppercase",letterSpacing:"0.5px",fontWeight:600}}>{sup.uploadFormat}</span>
                                {sup.catalogMeta ? (
                                  <span style={{fontSize:10,color:stale?"var(--warn)":"var(--ops)",fontWeight:600}}>
                                    {ageD<1?"Synced today":`Synced ${Math.floor(ageD)}d ago`}{stale?" · STALE":""}
                                  </span>
                                ) : (
                                  <span style={{fontSize:10,color:"var(--text3)",fontStyle:"italic"}}>Not yet uploaded</span>
                                )}
                              </div>
                              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:"var(--text2)",cursor:"pointer"}}>
                                <input type="checkbox" checked={sup.enabled}
                                  onChange={e=>updateSupplier(sup.id,{enabled:e.target.checked})}/>
                                Enabled
                              </label>
                            </div>

                            {/* Two-column layout: priority brands | catalog status */}
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                              {/* Priority brands editor */}
                              <div>
                                <div style={{fontSize:10,fontWeight:600,color:"var(--text2)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Priority brands</div>
                                <div style={{fontSize:10,color:"var(--text3)",marginBottom:6,lineHeight:1.4}}>
                                  AI will pick from these manufacturers when searching {sup.name}.
                                </div>
                                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                  {(sup.priorityBrands||[]).map((b,i)=>(
                                    <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
                                      <input type="text" className="inp" style={{flex:1,marginBottom:0,fontSize:11}}
                                        value={b}
                                        placeholder="Brand name"
                                        onChange={e=>{
                                          const next=[...(sup.priorityBrands||[])];
                                          next[i]=e.target.value;
                                          updateSupplier(sup.id,{priorityBrands:next});
                                        }}/>
                                      <button className="ico-btn" style={{flexShrink:0,color:"var(--danger)"}} title="Remove brand"
                                        onClick={()=>{
                                          const next=(sup.priorityBrands||[]).filter((_,j)=>j!==i);
                                          updateSupplier(sup.id,{priorityBrands:next});
                                        }}>×</button>
                                    </div>
                                  ))}
                                  <button className="btn" style={{fontSize:10,padding:"4px 10px",alignSelf:"flex-start",marginTop:2}}
                                    onClick={()=>updateSupplier(sup.id,{priorityBrands:[...(sup.priorityBrands||[]),""]})}>+ Add brand</button>
                                </div>
                              </div>

                              {/* Freight & Delivery — collapsed by default; this is
                                  a lot of config and most of it is set-and-forget. */}
                              {(()=>{
                                const fc = supFreight(sup);
                                const open = !!supplierUploadState[sup.id]?.freightOpen;
                                return (
                                  <div style={{borderTop:"1px solid var(--border)",paddingTop:10}}>
                                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}
                                      onClick={()=>setSupUiState(sup.id,{freightOpen:!open})}>
                                      <div style={{fontSize:10,fontWeight:600,color:"var(--text2)",textTransform:"uppercase",letterSpacing:"0.5px"}}>
                                        Freight &amp; Delivery
                                        {fc.enabled === false && <span style={{color:"var(--text3)",fontWeight:400,textTransform:"none",letterSpacing:0,marginLeft:6}}>(disabled)</span>}
                                      </div>
                                      <span style={{fontSize:10,color:"var(--text3)"}}>{open?"▼":"▶"}</span>
                                    </div>

                                    {open && (
                                      <div style={{marginTop:10,padding:10,background:"var(--s2)",borderRadius:6}}>
                                        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,marginBottom:12}}>
                                          <input type="checkbox" checked={fc.enabled !== false}
                                            onChange={e=>updateSupplierFreight(sup.id,{enabled:e.target.checked})}/>
                                          Apply this supplier's freight rules
                                        </label>

                                        <div style={{fontSize:10,fontWeight:600,marginBottom:6,color:"var(--text2)"}}>Rate card</div>
                                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:12}}>
                                          <label style={{fontSize:10,color:"var(--text3)"}}>Free above ($ ex GST)
                                            <input type="number" className="inp" style={{width:"100%",fontSize:10}}
                                              value={fc.metroThresholdExGst}
                                              onChange={e=>updateSupplierFreight(sup.id,{metroThresholdExGst:Number(e.target.value)||0})}/>
                                          </label>
                                          <label style={{fontSize:10,color:"var(--text3)"}}>Flat rate below ($)
                                            <input type="number" className="inp" style={{width:"100%",fontSize:10}}
                                              value={fc.metroFlatRateExGst}
                                              onChange={e=>updateSupplierFreight(sup.id,{metroFlatRateExGst:Number(e.target.value)||0})}/>
                                          </label>
                                        </div>

                                        <div style={{fontSize:10,fontWeight:600,marginBottom:4,color:"var(--text2)"}}>Always charged freight</div>
                                        <input type="text" className="inp" style={{width:"100%",fontSize:10,marginBottom:12}}
                                          placeholder="printer, ups, cabinet, rack…"
                                          value={(fc.excludeKeywords||[]).join(", ")}
                                          onChange={e=>updateSupplierFreight(sup.id,{
                                            excludeKeywords:e.target.value.split(",").map(x=>x.trim()).filter(Boolean),
                                          })}/>

                                        <div style={{fontSize:10,fontWeight:600,marginBottom:4,color:"var(--text2)"}}>
                                          Weight lookup <span style={{fontWeight:400,color:"var(--text3)"}}>— used when the catalog has no weight</span>
                                        </div>
                                        <div style={{maxHeight:180,overflowY:"auto",marginBottom:6}}>
                                          {(fc.weightLookup||[]).map((entry,i)=>(
                                            <div key={i} style={{display:"flex",gap:5,marginBottom:3,alignItems:"center"}}>
                                              <input type="text" className="inp" style={{flex:1,fontSize:10,marginBottom:0}}
                                                placeholder="pattern e.g. laptop" value={entry.pattern}
                                                onChange={e=>updateWeightLookup(sup.id,i,{pattern:e.target.value})}/>
                                              <input type="number" className="inp" style={{width:56,fontSize:10,marginBottom:0}}
                                                value={entry.weightKg}
                                                onChange={e=>updateWeightLookup(sup.id,i,{weightKg:Number(e.target.value)||0})}/>
                                              <span style={{fontSize:10,color:"var(--text3)"}}>kg</span>
                                              <button className="ico-btn" style={{color:"var(--danger)",flexShrink:0}}
                                                onClick={()=>removeWeightLookup(sup.id,i)}>×</button>
                                            </div>
                                          ))}
                                        </div>
                                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                          <button className="btn" style={{fontSize:10}} onClick={()=>addWeightLookup(sup.id)}>+ Add pattern</button>
                                          <label style={{fontSize:10,color:"var(--text3)",marginLeft:"auto"}}>Fallback
                                            <input type="number" className="inp" style={{width:56,fontSize:10,marginLeft:5,marginBottom:0}}
                                              value={fc.defaultWeightKg}
                                              onChange={e=>updateSupplierFreight(sup.id,{defaultWeightKg:Number(e.target.value)||2})}/>
                                            <span style={{marginLeft:3}}>kg</span>
                                          </label>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Catalog status + upload */}
                              <div>
                                <div style={{fontSize:10,fontWeight:600,color:"var(--text2)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Catalog status</div>
                                {sup.catalogMeta ? (
                                  <div style={{padding:10,background:"var(--s2)",borderRadius:6,border:`1px solid ${stale?"rgba(230,126,34,0.35)":"var(--border)"}`,fontSize:11,color:"var(--text2)",lineHeight:1.5}}>
                                    {sup.catalogMeta.fileName && <div style={{color:"var(--text3)",fontSize:10}}>File: {sup.catalogMeta.fileName}</div>}
                                    <div>Synced: <strong>{new Date(sup.catalogMeta.syncedAt).toLocaleString("en-AU")}</strong></div>
                                    <div>{sup.catalogMeta.totalItems?.toLocaleString()||0} total · {sup.catalogMeta.priorityItems?.toLocaleString()||0} priority{sup.id==="leader" && sup.catalogMeta.perthStock>0 ? <> · <strong>{sup.catalogMeta.perthStock.toLocaleString()}</strong> in Perth</> : null}</div>
                                    <button className="btn" style={{fontSize:10,padding:"4px 10px",marginTop:8}} onClick={()=>setSupUiState(sup.id,{modalOpen:true})}>📦 Upload new {sup.uploadFormat.toUpperCase()}</button>
                                  </div>
                                ) : (
                                  <div style={{padding:10,background:"var(--s2)",borderRadius:6,border:"1px dashed var(--border2)",fontSize:11,color:"var(--text3)"}}>
                                    No catalog uploaded yet.
                                    <div style={{marginTop:6}}>
                                      <button className="btn btn-strat" style={{fontSize:10,padding:"4px 10px"}} onClick={()=>{setTab("quoting");setTimeout(()=>setSupUiState(sup.id,{modalOpen:true}),50);}}>Upload now →</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* AI Privacy — Data Loss Prevention */}
                <div className="sg">
                  <div className="sg-h">AI Privacy — Data Loss Prevention</div>
                  <div className="sg-b">
                    <div style={{fontSize:12,color:"var(--text2)",marginBottom:14,lineHeight:1.65}}>
                      Before any prompt leaves the backend, sensitive data is replaced with tokens — client names, staff, contacts, emails, phones, IPs, ABNs, credentials and your custom rules. The AI never sees real values. Tokens are inflated back to real names in the response, so your brief still reads naturally. Add custom rules below for project codenames, internal IDs or anything else specific to Datatech.
                    </div>

                    {/* Mode selector */}
                    <div style={{marginBottom:18}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Mode</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                        {[
                          {v:"tokenised",l:"Tokenised",d:"Reversible tokens. Real names appear in responses. Recommended."},
                          {v:"strict",   l:"Strict",   d:"Plain [LABEL] placeholders. Not reversible — AI output stays generic."},
                          {v:"off",      l:"Off",      d:"Send raw text. Only for debugging."},
                        ].map(o=>(
                          <label key={o.v} style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer",padding:10,border:`1px solid ${dlpMode===o.v?"var(--strat)":"var(--border)"}`,borderRadius:6,background:dlpMode===o.v?"rgba(11,128,165,0.06)":"transparent"}}>
                            <input type="radio" checked={dlpMode===o.v} onChange={()=>setDlpMode(o.v)} style={{marginTop:2,flexShrink:0}}/>
                            <div>
                              <div style={{fontWeight:600,fontSize:12,color:dlpMode===o.v?"var(--strat)":"var(--text)"}}>{o.l}</div>
                              <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.4,marginTop:2}}>{o.d}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Custom rules */}
                    <div style={{marginBottom:18}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                        <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",textTransform:"uppercase",letterSpacing:"0.5px"}}>Custom rules</div>
                        <span style={{fontSize:10,color:"var(--text3)"}}>{dlpRules.length} rule{dlpRules.length===1?"":"s"}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,lineHeight:1.5}}>
                        Each rule replaces a string or regex pattern with a labelled token (e.g. <code>⟦PROJECT001⟧</code>) which gets restored to the original in the AI's response. Tick <em>Regex</em> for pattern matching (e.g. <code>EMP-\d{"{5}"}</code>), leave it off for exact-text matching.
                      </div>

                      {/* Header row */}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 2fr 60px 60px 30px",gap:6,fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.5px",fontWeight:600,paddingBottom:4,borderBottom:"1px solid var(--border)",marginBottom:4}}>
                        <div>Label</div>
                        <div>Pattern</div>
                        <div style={{textAlign:"center"}}>Regex</div>
                        <div style={{textAlign:"center"}}>On</div>
                        <div></div>
                      </div>

                      {/* Rule rows */}
                      <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:240,overflowY:"auto"}}>
                        {dlpRules.map((r,i)=>(
                          <div key={r.id||i} style={{display:"grid",gridTemplateColumns:"1fr 2fr 60px 60px 30px",gap:6,alignItems:"center"}}>
                            <input className="inp" style={{marginBottom:0,fontSize:11,height:26,padding:"2px 6px"}}
                              value={r.label||""}
                              placeholder="LABEL"
                              onChange={e=>setDlpRules(rs=>rs.map((x,j)=>j===i?{...x,label:e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"")}:x))}/>
                            <input className="inp" style={{marginBottom:0,fontSize:11,fontFamily:r.isRegex?"monospace":"inherit",height:26,padding:"2px 6px"}}
                              value={r.pattern||""}
                              placeholder={r.isRegex?"e.g. EMP-\\d{5}":"Exact text to redact"}
                              onChange={e=>setDlpRules(rs=>rs.map((x,j)=>j===i?{...x,pattern:e.target.value}:x))}/>
                            <div style={{textAlign:"center"}}>
                              <input type="checkbox" checked={!!r.isRegex}
                                onChange={e=>setDlpRules(rs=>rs.map((x,j)=>j===i?{...x,isRegex:e.target.checked}:x))}/>
                            </div>
                            <div style={{textAlign:"center"}}>
                              <input type="checkbox" checked={r.enabled!==false}
                                onChange={e=>setDlpRules(rs=>rs.map((x,j)=>j===i?{...x,enabled:e.target.checked}:x))}/>
                            </div>
                            <button className="ico-btn" style={{color:"var(--danger)",fontSize:14}} title="Delete rule"
                              onClick={()=>setDlpRules(rs=>rs.filter((_,j)=>j!==i))}>×</button>
                          </div>
                        ))}
                        {dlpRules.length===0&&(
                          <div style={{fontSize:11,color:"var(--text3)",fontStyle:"italic",padding:"8px 0"}}>
                            No custom rules yet — built-in protection (clients, staff, emails, phones, secrets) is still active.
                          </div>
                        )}
                      </div>

                      <button className="btn" style={{fontSize:10,padding:"5px 10px",marginTop:8}}
                        onClick={()=>setDlpRules(rs=>[...rs,{
                          id:`dlp-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
                          label:"CUSTOM",
                          pattern:"",
                          isRegex:false,
                          enabled:true,
                        }])}>+ Add rule</button>
                    </div>

                    {/* Live preview */}
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Test what gets scrubbed</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,lineHeight:1.5}}>
                        Paste any text — a ticket note, an email body, a calendar block title — to see exactly what would be sent to the AI. Uses the rules currently saved on the server, so click <strong>Save</strong> first if you've edited anything above.
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        <textarea className="inp" style={{height:120,fontSize:11,fontFamily:"monospace",resize:"vertical",marginBottom:0,padding:8}}
                          placeholder="Paste sample text here..."
                          value={dlpPreviewIn} onChange={e=>setDlpPreviewIn(e.target.value)}/>
                        <div style={{padding:"8px 10px",background:"var(--s2)",borderRadius:6,border:"1px solid var(--border)",fontSize:11,fontFamily:"monospace",overflowY:"auto",height:120,whiteSpace:"pre-wrap",lineHeight:1.5,color:"var(--text)"}}>
                          {dlpPreviewOut || <span style={{color:"var(--text3)",fontStyle:"italic"}}>Scrubbed output will appear here…</span>}
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8}}>
                        <button className="btn btn-T" style={{fontSize:10,padding:"5px 12px"}}
                          disabled={!dlpPreviewIn||dlpPreviewing||!onDlpPreview}
                          onClick={async()=>{
                            if(!onDlpPreview)return;
                            setDlpPreviewing(true);
                            try{
                              const r=await onDlpPreview(dlpPreviewIn);
                              setDlpPreviewOut(r?.scrubbed||"");
                              setDlpPreviewStats(r?.stats||null);
                            }catch(e){
                              console.warn("DLP preview failed",e);
                              setDlpPreviewOut("Error: "+(e?.message||"unknown"));
                              setDlpPreviewStats(null);
                            }finally{setDlpPreviewing(false);}
                          }}>{dlpPreviewing?"Testing…":"Test"}</button>
                        {dlpPreviewStats&&(
                          <div style={{fontSize:11,color:"var(--text3)"}}>
                            {dlpPreviewStats.totalRedactions} redaction{dlpPreviewStats.totalRedactions===1?"":"s"}
                            {Object.keys(dlpPreviewStats.byLabel||{}).length>0&&" — "+Object.entries(dlpPreviewStats.byLabel).map(([k,v])=>`${v} ${k}`).join(", ")}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Save bar */}
                    <div style={{display:"flex",alignItems:"center",gap:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                      <button className="btn btn-S" style={{fontSize:11}}
                        disabled={dlpSaving||!onDlpSave}
                        onClick={async()=>{
                          if(!onDlpSave)return;
                          // Drop any blank rules before saving
                          const clean=dlpRules.filter(r=>r.pattern&&r.pattern.trim());
                          setDlpSaving(true);
                          try{
                            const r=await onDlpSave({mode:dlpMode,rules:clean});
                            if(r?.saved){
                              setDlpSavedAt(new Date());
                              if(Array.isArray(r.rules))setDlpRules(r.rules);
                            }
                          }catch(e){alert("Save failed: "+(e?.message||"unknown"));}
                          finally{setDlpSaving(false);}
                        }}>{dlpSaving?"Saving…":"Save DLP settings"}</button>
                      {dlpSavedAt&&<span style={{fontSize:11,color:"var(--ops)"}}>✓ Saved at {dlpSavedAt.toLocaleTimeString("en-AU")}</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Resize handle: Focus | Inbox */}
          <div className="resize-handle" style={{display:showInboxPanel?"flex":"none"}} onMouseDown={(e)=>startResize("inbox",e)} title="Drag to resize inbox width"/>
          <div className="panel email-panel" style={{display:showInboxPanel?"flex":"none",width:inboxWidth,flexShrink:0,borderLeft:"3px solid var(--border2)",background:"var(--s2)"}}>
            {/* Email panel header with refresh */}
            <div className="ph" style={{gap:6,background:"linear-gradient(135deg,var(--dt-navy),var(--dt-secondary))",borderBottom:"2px solid var(--dt-secondary-dark)"}}>
              <div className="ph-title" style={{color:"rgba(255,255,255,0.85)",letterSpacing:"0.12em"}}>Inbox</div>
              <div className="ph-badge" style={{background:"rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.8)",borderColor:"rgba(255,255,255,0.2)"}}>{focusing?`${visibleEmails.filter(e=>e.priority!=="noise").length} vis`:`${emailSource.length}`}</div>
              <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
                {lastEmailRefresh&&(
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.6)"}}>
                    {lastEmailRefresh.toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"})}
                  </div>
                )}
                <button className="ico-btn" title="Refresh inbox"
                  style={{opacity:emailRefreshing?0.5:1,background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",color:"white"}}
                  onClick={()=>onRefreshEmails&&onRefreshEmails()}
                  disabled={emailRefreshing}>
                  {emailRefreshing?"⟳":"↻"}
                </button>
              </div>
            </div>
            {!m365&&(<div><div className="connect-box"><div className="c-icon">📬</div><div className="c-title">M365 not connected</div><div className="c-desc">Connect in Settings for live triage.</div><button className="btn btn-S" style={{fontSize:10}} onClick={()=>setTab("settings")}>Settings →</button></div><div style={{padding:"5px 13px 3px",fontSize:9,letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--text3)"}}>Preview (mock)</div></div>)}
            {m365&&focusing&&(<div className="filter-bar"><span className="filt-on">🔒 Focus on</span><span style={{marginLeft:"auto"}}>{visibleEmails.filter(e=>e.vip||e.priority==="urgent").length} priority</span></div>)}
            <div className="scroll">
              {emailSource.filter(e=>!deletedEmails.has(e.id)).map(e=>{
                const show=!focusing||(e.priority==="urgent"&&filters.allowUrgent)||(e.vip&&filters.allowVip)||(e.priority==="client"&&filters.allowClient)||!filters.blockNoise;
                const cls=e.priority==="urgent"?"em-urgent":e.vip?"em-vip":e.priority==="client"?"em-client":"";
                const isTicketed=ticketedEmails.has(e.id);
                return(
                  <div key={e.id} className={`em-item ${cls} ${!show?"em-blocked":""}`}
                    draggable
                    onDragStart={ev=>{
                      ev.dataTransfer.setData("emailItem",JSON.stringify({
                        id:e.id,
                        from:e.from,
                        fromEmail:e.fromEmail||e.from_address||e.address||"",
                        subj:e.subj,
                        body:e.body||e.bodyPreview||e.snippet||e.aiSum||"",
                        time:e.time,
                      }));
                      ev.dataTransfer.effectAllowed="copy";
                    }}
                    onClick={()=>setEmailDetailModal({email:e})}
                    style={{cursor:"pointer"}}
                    title="Click to open · drag to Quoting → Backlog for a quote request"
                  >
                    <div className="e-from">{e.from}{e.vip&&<span className="eb eb-vip">VIP</span>}{e.priority==="urgent"&&<span className="eb eb-urg">URGENT</span>}{e.priority==="client"&&<span className="eb eb-cli">CLIENT</span>}{!show&&<span className="eb eb-blk">BLOCKED</span>}{isTicketed&&<span className="eb" style={{background:"rgba(39,174,96,0.1)",color:"var(--ops)",border:"1px solid rgba(39,174,96,0.3)"}}>TICKETED</span>}{todoedEmails.has(e.id)&&<span className="eb" style={{background:"rgba(39,174,96,0.1)",color:"var(--ops)",border:"1px solid rgba(39,174,96,0.3)"}}>TO-DO</span>}</div>
                    <div className="e-subj">{e.subj}</div>
                    {e.aiSum&&show&&<div className="e-ai"><span style={{color:"var(--strat)",marginRight:4}}>◆</span>{e.aiSum}</div>}
                    <div className="e-meta">
                      <span>{e.time}</span>
                      {e.aiSum&&show&&<span className="eb eb-ai">AI</span>}
                      <span style={{marginLeft:"auto",display:"flex",gap:4}}>
                        <button className="em-ticket-btn"
                          style={{borderColor:"rgba(231,76,60,0.3)",background:"rgba(231,76,60,0.06)",color:"var(--danger)",fontWeight:600}}
                          onClick={ev=>{ev.stopPropagation();handleDeleteEmail(e);}}
                          title="Move to Deleted Items">🗑 delete</button>
                        {atConnected&&!isTicketed&&(
                          <button className="em-ticket-btn"
                            style={{borderColor:"rgba(11,128,165,0.3)",background:"rgba(11,128,165,0.06)",color:"var(--strat)",fontWeight:600}}
                            onClick={ev=>{ev.stopPropagation();openTicketModal(e,{quickTicket:true});}}
                            title="Quick ticket — create Autotask ticket only, no reply, email stays in inbox">🎫 ticket</button>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── PRE-MEETING FORM EDITOR MODAL ── */}
      {prepSendModal&&(
        <div className="pe-overlay" onClick={e=>{if(e.target.className==="pe-overlay")setPrepSendModal(null);}}>
          <div className="pe-modal" onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div className="pe-hdr">
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15,color:"white",marginBottom:3}}>✏️ Pre-Meeting Form Questions</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>{prepSendModal.companyName}{prepSendModal.isCustom?" · Custom questions":" · Default questions (unsaved changes apply to this client only)"}</div>
                </div>
                <button className="ico-btn" style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"white",flexShrink:0}} onClick={()=>setPrepSendModal(null)}>×</button>
              </div>
            </div>

            {/* Question editor body */}
            <div className="pe-body">

              {(()=>{
                // Group questions by section
                const sections={};
                (prepSendModal.questions||[]).forEach(q=>{
                  const s=q.section||"General";
                  if(!sections[s]) sections[s]=[];
                  sections[s].push(q);
                });

                const updateQ=(id,patch)=>setPrepSendModal(m=>({...m,questions:m.questions.map(q=>q.id===id?{...q,...patch}:q)}));
                const deleteQ=(id)=>setPrepSendModal(m=>({...m,questions:m.questions.filter(q=>q.id!==id)}));
                const deleteSection=(secName)=>setPrepSendModal(m=>({...m,questions:m.questions.filter(q=>q.section!==secName)}));
                const renameSection=(oldName,newName)=>{
                  if(!newName.trim()||newName===oldName)return;
                  setPrepSendModal(m=>({...m,questions:m.questions.map(q=>q.section===oldName?{...q,section:newName}:q)}));
                };
                const addQuestion=(secName)=>{
                  const label=(prepSendModal.newQLabels?.[secName]||"").trim();
                  if(!label)return;
                  const newQ={id:`cq-${Date.now()}`,section:secName,type:"yesno",label,optional:false};
                  setPrepSendModal(m=>({...m,questions:[...m.questions,newQ],newQLabels:{...m.newQLabels,[secName]:""}}));
                };
                const addSection=()=>{
                  const name=(prepSendModal.newSecName||"").trim();
                  if(!name)return;
                  const newQ={id:`cq-${Date.now()}`,section:name,type:"yesno",label:"New question",optional:false};
                  setPrepSendModal(m=>({...m,questions:[...m.questions,newQ],newSecName:""}));
                };

                // Move a section up or down by reordering questions in the array
                const moveSection=(secName,dir)=>{
                  setPrepSendModal(m=>{
                    const secOrder=[...new Set((m.questions||[]).map(q=>q.section||"General"))];
                    const idx=secOrder.indexOf(secName);
                    const swapIdx=idx+dir;
                    if(swapIdx<0||swapIdx>=secOrder.length)return m;
                    [secOrder[idx],secOrder[swapIdx]]=[secOrder[swapIdx],secOrder[idx]];
                    const reordered=secOrder.flatMap(s=>(m.questions||[]).filter(q=>(q.section||"General")===s));
                    return{...m,questions:reordered};
                  });
                };

                // Section order from questions array insertion order
                const sectionOrder=[...new Set((prepSendModal.questions||[]).map(q=>q.section||"General"))];

                return(
                  <>
                    {sectionOrder.map((secName,secIdx)=>{
                      const qs=sections[secName]||[];
                      return(
                      <div key={secName} className="pe-sec">
                        <div className="pe-sec-hdr">
                          <div style={{display:"flex",flexDirection:"column",gap:2,flexShrink:0}}>
                            <button className="pe-move-btn" disabled={secIdx===0} onClick={()=>moveSection(secName,-1)} title="Move section up">▲</button>
                            <button className="pe-move-btn" disabled={secIdx===sectionOrder.length-1} onClick={()=>moveSection(secName,1)} title="Move section down">▼</button>
                          </div>
                          <input className="pe-sec-name" defaultValue={secName}
                            onBlur={e=>renameSection(secName,e.target.value)}
                            onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                            title="Click to rename section"/>
                          <span style={{fontSize:10,color:"var(--text3)"}}>{qs.length} question{qs.length!==1?"s":""}</span>
                          <button className="ico-btn" title="Delete section" style={{flexShrink:0}}
                            onClick={()=>{if(window.confirm(`Delete section "${secName}" and all its questions?`))deleteSection(secName);}}>🗑</button>
                        </div>

                        {qs.map(q=>(
                          <div key={q.id} className="pe-q">
                            <span className={`lb lb-${q.type==="yesno"||q.type==="yesnoNA"?"T":q.type==="rating"?"S":"O"}`}
                              style={{fontSize:9,flexShrink:0,cursor:"pointer",title:"Click to cycle type"}}
                              title="Click to change type"
                              onClick={()=>updateQ(q.id,{type:q.type==="yesno"?"yesnoNA":q.type==="yesnoNA"?"rating":q.type==="rating"?"textarea":"yesno"})}>
                              {q.type==="yesno"?"Y/N":q.type==="yesnoNA"?"Y/N/NA":q.type==="rating"?"★":"Text"}
                            </span>
                            <input className="pe-q-label" value={q.label}
                              onChange={e=>updateQ(q.id,{label:e.target.value})}
                              placeholder="Question label…"/>
                            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--text3)",flexShrink:0,cursor:"pointer"}}>
                              <input type="checkbox" checked={!!q.optional} onChange={e=>updateQ(q.id,{optional:e.target.checked})} style={{accentColor:"var(--strat)"}}/>
                              opt
                            </label>
                            <button className="ico-btn" style={{flexShrink:0}} onClick={()=>deleteQ(q.id)}>×</button>
                          </div>
                        ))}

                        {/* Add question to this section */}
                        <div className="pe-add-q">
                          <input className="inp" style={{flex:1,marginBottom:0,fontSize:11,padding:"5px 9px"}}
                            placeholder="+ Add yes/no question…"
                            value={prepSendModal.newQLabels?.[secName]||""}
                            onChange={e=>setPrepSendModal(m=>({...m,newQLabels:{...m.newQLabels,[secName]:e.target.value}}))}
                            onKeyDown={e=>e.key==="Enter"&&addQuestion(secName)}/>
                          <button className="btn btn-T" style={{fontSize:10,padding:"5px 10px",flexShrink:0}}
                            onClick={()=>addQuestion(secName)}>Add</button>
                        </div>
                      </div>
                      );
                    })}

                    {/* Add new section */}
                    <div className="pe-new-sec">
                      <input className="inp" style={{flex:1,marginBottom:0,fontSize:11}}
                        placeholder="New section name…"
                        value={prepSendModal.newSecName||""}
                        onChange={e=>setPrepSendModal(m=>({...m,newSecName:e.target.value}))}
                        onKeyDown={e=>e.key==="Enter"&&addSection()}/>
                      <button className="btn" style={{fontSize:10,flexShrink:0}} onClick={addSection}>+ Add Section</button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="pe-footer">
              <div style={{fontSize:10,color:"var(--text3)",flex:1}}>
                {(prepSendModal.questions||[]).length} questions across {[...new Set((prepSendModal.questions||[]).map(q=>q.section||"General"))].length} sections
              </div>
              <button className="btn" onClick={()=>setPrepSendModal(null)}>Cancel</button>
              {prepSendModal.companyId!=="default"&&(
                <button className="btn btn-danger" style={{fontSize:11}}
                  onClick={async()=>{
                    if(!window.confirm("Reset to default questions for "+prepSendModal.companyName+"?"))return;
                    const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                    try{
                      await fetch(`${_apiBase}/api/meeting-prep/${prepSendModal.companyId}/questions`,{
                        method:"DELETE",
                        headers:{Authorization:`Bearer ${window.__mspToken||""}`}
                      });
                      setPrepSendModal(null);
                    }catch(e){alert("Reset failed: "+e.message);}
                  }}>Reset to defaults</button>
              )}
              <button className="btn btn-S"
                style={{background:"var(--strat)",color:"white",borderColor:"var(--strat)",fontSize:11}}
                disabled={prepSendModalSending}
                onClick={async()=>{
                  if(prepSendModal.companyId==="default"){setPrepSendModal(null);return;}
                  setPrepSendModalSending(true);
                  const _apiBase=window.location.hostname==="localhost"?"http://localhost:3001":"https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";
                  try{
                    const r=await fetch(`${_apiBase}/api/meeting-prep/${prepSendModal.companyId}/questions`,{
                      method:"PUT",
                      headers:{"Content-Type":"application/json",Authorization:`Bearer ${window.__mspToken||""}`},
                      body:JSON.stringify({questions:prepSendModal.questions})
                    });
                    const raw=await r.text();
                    let d={};try{d=JSON.parse(raw);}catch{}
                    if(r.ok&&(d.saved||d.count!=null)){
                      setPrepSendModal(null);
                    }else{
                      alert("Save failed (HTTP "+r.status+"): "+(d.error||raw.slice(0,200)||"No response"));
                    }
                  }catch(e){alert("Save failed: "+e.message);}
                  finally{setPrepSendModalSending(false);}
                }}>
                {prepSendModalSending?"Saving…":"✓ Save Questions"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── PRE-MEETING FORM TEMPLATE EDIT MODAL ── */}
      {prepTemplateModal&&(
        <div className="pe-overlay" onClick={e=>{if(e.target.className==="pe-overlay")setPrepTemplateModal(null);}}>
          <div className="pe-modal" onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div className="pe-hdr">
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:15,color:"white"}}>📝 Edit Pre-Meeting Form Template</div>
                <button className="ico-btn" style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"white",flexShrink:0}} onClick={()=>setPrepTemplateModal(null)}>×</button>
              </div>
              <div style={{display:"flex",gap:10}}>
                <input className="inp" style={{flex:"0 0 36%",marginBottom:0,fontSize:13,fontWeight:600,padding:"7px 10px",background:"rgba(255,255,255,0.95)"}}
                  placeholder="Template name…"
                  value={prepTemplateModal.name||""}
                  onChange={e=>setPrepTemplateModal(m=>({...m,name:e.target.value}))}/>
                <input className="inp" style={{flex:1,marginBottom:0,fontSize:12,padding:"7px 10px",background:"rgba(255,255,255,0.95)"}}
                  placeholder="Description (e.g. Hospitality — guest Wi-Fi, PMS, POS)"
                  value={prepTemplateModal.description||""}
                  onChange={e=>setPrepTemplateModal(m=>({...m,description:e.target.value}))}/>
              </div>
            </div>

            {/* Question editor body */}
            <div className="pe-body">
              {(()=>{
                // Group questions by section
                const sections={};
                (prepTemplateModal.questions||[]).forEach(q=>{
                  const s=q.section||"General";
                  if(!sections[s]) sections[s]=[];
                  sections[s].push(q);
                });

                const updateQ=(id,patch)=>setPrepTemplateModal(m=>({...m,questions:m.questions.map(q=>q.id===id?{...q,...patch}:q)}));
                const deleteQ=(id)=>setPrepTemplateModal(m=>({...m,questions:m.questions.filter(q=>q.id!==id)}));
                const deleteSection=(secName)=>setPrepTemplateModal(m=>({...m,questions:m.questions.filter(q=>q.section!==secName)}));
                const renameSection=(oldName,newName)=>{
                  if(!newName.trim()||newName===oldName)return;
                  setPrepTemplateModal(m=>({...m,questions:m.questions.map(q=>q.section===oldName?{...q,section:newName}:q)}));
                };
                const addQuestion=(secName)=>{
                  const label=(prepTemplateModal.newQLabels?.[secName]||"").trim();
                  if(!label)return;
                  const newQ={id:`cq-${Date.now()}`,section:secName,type:"yesno",label,optional:false};
                  setPrepTemplateModal(m=>({...m,questions:[...m.questions,newQ],newQLabels:{...m.newQLabels,[secName]:""}}));
                };
                const addSection=()=>{
                  const name=(prepTemplateModal.newSecName||"").trim();
                  if(!name)return;
                  const newQ={id:`cq-${Date.now()}`,section:name,type:"yesno",label:"New question",optional:false};
                  setPrepTemplateModal(m=>({...m,questions:[...m.questions,newQ],newSecName:""}));
                };

                const moveSection=(secName,dir)=>{
                  setPrepTemplateModal(m=>{
                    const secOrder=[...new Set((m.questions||[]).map(q=>q.section||"General"))];
                    const idx=secOrder.indexOf(secName);
                    const swapIdx=idx+dir;
                    if(swapIdx<0||swapIdx>=secOrder.length)return m;
                    [secOrder[idx],secOrder[swapIdx]]=[secOrder[swapIdx],secOrder[idx]];
                    const reordered=secOrder.flatMap(s=>(m.questions||[]).filter(q=>(q.section||"General")===s));
                    return{...m,questions:reordered};
                  });
                };

                const sectionOrder=[...new Set((prepTemplateModal.questions||[]).map(q=>q.section||"General"))];

                if((prepTemplateModal.questions||[]).length===0){
                  return(
                    <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text3)"}}>
                      <div style={{fontSize:24,marginBottom:10}}>📋</div>
                      <div style={{fontSize:12,marginBottom:14}}>This template has no questions yet. Add a section to get started.</div>
                      <div style={{display:"flex",gap:6,maxWidth:360,margin:"0 auto"}}>
                        <input className="inp" style={{flex:1,marginBottom:0,fontSize:11}}
                          placeholder="First section name (e.g. Network)"
                          value={prepTemplateModal.newSecName||""}
                          onChange={e=>setPrepTemplateModal(m=>({...m,newSecName:e.target.value}))}
                          onKeyDown={e=>e.key==="Enter"&&addSection()}/>
                        <button className="btn btn-T" style={{fontSize:10,flexShrink:0}} onClick={addSection}>+ Add Section</button>
                      </div>
                    </div>
                  );
                }

                return(
                  <>
                    {sectionOrder.map((secName,secIdx)=>{
                      const qs=sections[secName]||[];
                      return(
                      <div key={secName} className="pe-sec">
                        <div className="pe-sec-hdr">
                          <div style={{display:"flex",flexDirection:"column",gap:2,flexShrink:0}}>
                            <button className="pe-move-btn" disabled={secIdx===0} onClick={()=>moveSection(secName,-1)} title="Move section up">▲</button>
                            <button className="pe-move-btn" disabled={secIdx===sectionOrder.length-1} onClick={()=>moveSection(secName,1)} title="Move section down">▼</button>
                          </div>
                          <input className="pe-sec-name" defaultValue={secName}
                            onBlur={e=>renameSection(secName,e.target.value)}
                            onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                            title="Click to rename section"/>
                          <span style={{fontSize:10,color:"var(--text3)"}}>{qs.length} question{qs.length!==1?"s":""}</span>
                          <button className="ico-btn" title="Delete section" style={{flexShrink:0}}
                            onClick={()=>{if(window.confirm(`Delete section "${secName}" and all its questions?`))deleteSection(secName);}}>🗑</button>
                        </div>

                        {qs.map(q=>(
                          <div key={q.id} className="pe-q">
                            <span className={`lb lb-${q.type==="yesno"||q.type==="yesnoNA"?"T":q.type==="rating"?"S":"O"}`}
                              style={{fontSize:9,flexShrink:0,cursor:"pointer"}}
                              title="Click to change type"
                              onClick={()=>updateQ(q.id,{type:q.type==="yesno"?"yesnoNA":q.type==="yesnoNA"?"rating":q.type==="rating"?"textarea":"yesno"})}>
                              {q.type==="yesno"?"Y/N":q.type==="yesnoNA"?"Y/N/NA":q.type==="rating"?"★":"Text"}
                            </span>
                            <input className="pe-q-label" value={q.label}
                              onChange={e=>updateQ(q.id,{label:e.target.value})}
                              placeholder="Question label…"/>
                            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--text3)",flexShrink:0,cursor:"pointer"}}>
                              <input type="checkbox" checked={!!q.optional} onChange={e=>updateQ(q.id,{optional:e.target.checked})} style={{accentColor:"var(--strat)"}}/>
                              opt
                            </label>
                            <button className="ico-btn" style={{flexShrink:0}} onClick={()=>deleteQ(q.id)}>×</button>
                          </div>
                        ))}

                        {/* Add question to this section */}
                        <div className="pe-add-q">
                          <input className="inp" style={{flex:1,marginBottom:0,fontSize:11,padding:"5px 9px"}}
                            placeholder="+ Add yes/no question…"
                            value={prepTemplateModal.newQLabels?.[secName]||""}
                            onChange={e=>setPrepTemplateModal(m=>({...m,newQLabels:{...m.newQLabels,[secName]:e.target.value}}))}
                            onKeyDown={e=>e.key==="Enter"&&addQuestion(secName)}/>
                          <button className="btn btn-T" style={{fontSize:10,padding:"5px 10px",flexShrink:0}}
                            onClick={()=>addQuestion(secName)}>Add</button>
                        </div>
                      </div>
                      );
                    })}

                    {/* Add new section */}
                    <div className="pe-new-sec">
                      <input className="inp" style={{flex:1,marginBottom:0,fontSize:11}}
                        placeholder="New section name…"
                        value={prepTemplateModal.newSecName||""}
                        onChange={e=>setPrepTemplateModal(m=>({...m,newSecName:e.target.value}))}
                        onKeyDown={e=>e.key==="Enter"&&addSection()}/>
                      <button className="btn" style={{fontSize:10,flexShrink:0}} onClick={addSection}>+ Add Section</button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="pe-footer">
              {(()=>{
                const assignedCount=Object.values(companyPrepTemplates||{}).filter(tid=>tid===prepTemplateModal.templateId).length;
                return(
                  <div style={{fontSize:10,color:"var(--text3)",flex:1,lineHeight:1.5}}>
                    {(prepTemplateModal.questions||[]).length} questions · {[...new Set((prepTemplateModal.questions||[]).map(q=>q.section||"General"))].length} sections
                    {assignedCount>0&&<><br/><span style={{color:"var(--strat)"}}>Saving will sync changes to {assignedCount} assigned customer{assignedCount===1?"":"s"}</span></>}
                  </div>
                );
              })()}
              <button className="btn" onClick={()=>setPrepTemplateModal(null)}>Cancel</button>
              <button className="btn btn-S"
                style={{background:"var(--strat)",color:"white",borderColor:"var(--strat)",fontSize:11}}
                disabled={prepTemplateModalSaving}
                onClick={async()=>{
                  const tplId=prepTemplateModal.templateId;
                  const name=(prepTemplateModal.name||"").trim()||"Untitled template";
                  const description=(prepTemplateModal.description||"").trim();
                  const questions=prepTemplateModal.questions||[];
                  setPrepTemplateModalSaving(true);
                  try {
                    // 1. Update local templates state (and persist)
                    let nextTemplates;
                    setPrepFormTemplates(prev=>{
                      const exists=prev.some(t=>t.id===tplId);
                      nextTemplates=exists
                        ? prev.map(t=>t.id===tplId?{...t,name,description,questions}:t)
                        : [...prev,{id:tplId,name,description,questions}];
                      saveSnapshot({prepFormTemplates:nextTemplates});
                      return nextTemplates;
                    });
                    // 2. Propagate to assigned customers (best-effort)
                    const {ok,fail}=await propagateTemplateToAssignedCompanies(tplId,questions);
                    setPrepTemplateModal(null);
                    if(fail>0) alert(`Template saved, but ${fail} of ${ok+fail} customer pushes failed. They may need a manual re-assignment.`);
                  } catch(e){
                    alert("Save failed: "+e.message);
                  } finally {
                    setPrepTemplateModalSaving(false);
                  }
                }}>
                {prepTemplateModalSaving?"Saving…":"✓ Save Template"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── AUTOTASK TICKET MODAL ── */}
      {/* ── TICKET MODAL (from To-Do) ── */}
      {atTodoModal&&(
        <div className="at-overlay" onClick={e=>{if(e.target.className==="at-overlay")cancelTicket();}}>
          <div className="at-modal">
            <div className="at-modal-hdr">
              <div className="at-modal-title">🎫 New Autotask Ticket</div>
              <div className="at-modal-sub">
                <span>From To-Do: <strong style={{color:"var(--text2)"}}>{atTodoModal.title}</strong></span>
                {atTodoModal.client&&<><span>·</span><span style={{color:"var(--ops)"}}>{atTodoModal.client}</span></>}
                {atLookup&&<span style={{color:"var(--ops)"}}>✓ Company matched</span>}
              </div>
            </div>
            <div className="at-modal-body">
              {/* Contact chip */}
              {atLookup?.company?(
                <div className="at-contact-chip">
                  <span style={{fontSize:16}}>🏢</span>
                  <div>
                    <div className="at-contact-name">{atLookup.company?.companyName||atLookup.company?.name||atTodoModal.client}</div>
                    <div className="at-contact-email">Matched from To-Do client name</div>
                  </div>
                </div>
              ):(
                <div className="at-contact-chip not-found">
                  <span style={{fontSize:16}}>❓</span>
                  <div><div className="at-contact-name not-found">No company auto-matched</div><div className="at-contact-email">Select company manually below</div></div>
                </div>
              )}

              <div className="at-section-label">📋 Ticket Details</div>
              <div className="at-field">
                <div className="at-label">Title <span className="at-label-required">*</span></div>
                <input className="at-input" value={atTicket.title||""} onChange={e=>setAtTicket(t=>({...t,title:e.target.value}))} placeholder="Ticket title..."/>
              </div>
              <div className="at-field">
                <div className="at-label">Description</div>
                <textarea className="at-input" value={atTicket.description||""} onChange={e=>setAtTicket(t=>({...t,description:e.target.value}))} placeholder="Describe the issue..."/>
              </div>
              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Priority <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.priority||3} onChange={e=>setAtTicket(t=>({...t,priority:+e.target.value}))}>
                    {AT_PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.id} — {p.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Status <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.statusId||1} onChange={e=>setAtTicket(t=>({...t,statusId:+e.target.value}))}>
                    {(atReference?.statuses||AT_STATUSES).map(s=><option key={s.id||s.value} value={s.id||s.value}>{s.name||s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Queue <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.queueId||""} onChange={e=>setAtTicket(t=>({...t,queueId:+e.target.value}))}>
                    {(atReference?.queues||AT_QUEUES).filter(q=>(q.isActive!==false&&q.isSystem!==true)).map(q=><option key={q.id||q.value} value={q.id||q.value}>{q.name||q.label}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Due Date</div>
                  <input type="date" className="at-input" value={atTicket.dueDate||""} onChange={e=>setAtTicket(t=>({...t,dueDate:e.target.value}))}/>
                </div>
              </div>
              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Issue Type</div>
                  <select className="at-input" value={atTicket.issueType||""} onChange={e=>setAtTicket(t=>({...t,issueType:e.target.value,subIssueType:""}))}>
                    <option value="">— Select issue type —</option>
                    {(atReference?.issueTypes||[]).filter(i=>i.isActive!==false).map(i=><option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Sub-Issue Type</div>
                  <select className="at-input" value={atTicket.subIssueType||""} onChange={e=>setAtTicket(t=>({...t,subIssueType:e.target.value}))} disabled={!atTicket.issueType}>
                    <option value="">{atTicket.issueType?"— Select sub-issue type —":"— Select issue type first —"}</option>
                    {(atReference?.subIssueTypes||[]).filter(s=>s.isActive!==false&&String(s.parentValue)===String(atTicket.issueType)).map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="at-section-label">🏢 Company & Contact</div>
              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Company <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.companyId||""} onChange={async e=>{
                    const cid=+e.target.value;
                    setAtTicket(t=>({...t,companyId:cid,contactId:"",contractId:"",configurationItemId:""}));
                    if(!cid){
                      setAtModalContacts([]);setAtModalContracts([]);return;
                    }
                    const {contracts:loadedContracts}=await loadCompanyContactsContracts(cid);
                    if(loadedContracts.length){
                      setAtTicket(t=>({...t,contractId:loadedContracts[0].id}));
                    }
                  }}>
                    <option value="">— Select company —</option>
                    {(atCompanies.length>0?atCompanies:atReference?.companies||AT_COMPANIES).map(c=><option key={c.id} value={c.id}>{c.companyName||c.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Contact {atModalLoading&&<span style={{fontSize:9,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none",marginLeft:6}}>loading…</span>}</div>
                  <select className="at-input" value={atTicket.contactId||""} onChange={e=>setAtTicket(t=>({...t,contactId:+e.target.value}))} disabled={atModalLoading||atTicket.companyId===""}>
                    <option value="">{atModalLoading?"Loading contacts…":atTicket.companyId!==""?(atModalContacts.length?"— Select contact —":"— No active contacts —"):"— Select company first —"}</option>
                    {atModalContacts.map(c=>{
                      const fullName=[c.firstName,c.lastName].filter(Boolean).join(" ")||c.emailAddress||`Contact ${c.id}`;
                      const title=c.title?` — ${c.title}`:"";
                      return <option key={c.id} value={c.id}>{fullName}{title}</option>;
                    })}
                  </select>
                </div>
              </div>
              {(()=>{
                if(!atTicket.contactId||atTicket.companyId==="") return null;
                const contact=atModalContacts.find(c=>c.id===atTicket.contactId);
                if(!contact) return null;
                const company=(atCompanies.length>0?atCompanies:atReference?.companies||AT_COMPANIES).find(c=>c.id===atTicket.companyId);
                const companyName=company?.companyName||company?.name||"";
                const match=findDeviceForContact(contact,companyName);
                if(!match?.device) return null;
                const d=match.device;
                const lastSeen=d.lastSeen?new Date(d.lastSeen).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}):"";
                const onlineDot=d.online?<span style={{color:"#10b981",fontSize:8,marginRight:4}}>●</span>:<span style={{color:"#9ca3af",fontSize:8,marginRight:4}}>●</span>;
                const confidence=match.score>=100?"":match.score>=95?" (likely)":" (possible)";
                return(
                  <div style={{margin:"-4px 0 10px",fontSize:10,color:"var(--text3)",background:"rgba(11,128,165,0.04)",border:"1px solid rgba(11,128,165,0.15)",borderRadius:4,padding:"5px 8px",lineHeight:1.5}}>
                    <span style={{color:"var(--strat)",fontWeight:600}}>💻 Likely device:</span>{" "}
                    <span style={{fontFamily:"var(--ff-mono, ui-monospace, monospace)",color:"var(--text)"}}>{d.hostname}</span>
                    {confidence&&<span style={{color:"var(--text3)"}}>{confidence}</span>}
                    <div style={{marginTop:2}}>
                      {onlineDot}{d.online?"Online":"Offline"}{" · "}{d.deviceType?.category||"Device"}
                      {lastSeen?` · last login ${lastSeen}`:""}
                      {d.lastLoggedInUser?` · ${d.lastLoggedInUser}`:""}
                    </div>
                  </div>
                );
              })()}
              <div className="at-section-label">🖥️ Asset (optional)</div>
              <div className="at-field" style={{marginBottom:10}}>
                <select className="at-input" value={atTicket.configurationItemId||""} onChange={e=>setAtTicket(t=>({...t,configurationItemId:e.target.value?+e.target.value:""}))} disabled={atModalLoading||atTicket.companyId===""}>
                  <option value="">{atModalLoading?"Loading assets…":atTicket.companyId!==""?(atModalAssets.length?"— No asset (general ticket) —":"— No assets found for this company —"):"— Select company first —"}</option>
                  {atModalAssets.map(a=>{
                    const name=a.referenceTitle||a.referenceNumber||a.serialNumber||`Asset ${a.id}`;
                    const parts=[];
                    if(a.serialNumber&&a.serialNumber!==name)parts.push(a.serialNumber);
                    if(a.installedProductName)parts.push(a.installedProductName);
                    const suffix=parts.length?` — ${parts.join(" · ")}`:"";
                    return <option key={a.id} value={a.id}>{name}{suffix}</option>;
                  })}
                </select>
              </div>
                            <div className="at-section-label">⚙ Assignment & Billing</div>
              <div className="at-row-3">
                <div className="at-field">
                  <div className="at-label">Assigned To</div>
                  <select className="at-input" value={atTicket.resourceId||""} onChange={e=>setAtTicket(t=>({...t,resourceId:+e.target.value}))}>
                    <option value="">— Unassigned —</option>
                    {(atReference?.resources||AT_RESOURCES).map(r=><option key={r.id} value={r.id}>{r.name||r.firstName+" "+r.lastName}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Work Type</div>
                  <select className="at-input" value={atTicket.workTypeId||""} onChange={e=>setAtTicket(t=>({...t,workTypeId:+e.target.value}))}>
                    <option value="">— Select —</option>
                    {(atReference?.workTypes||AT_WORK_TYPES).map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Contract {atModalLoading&&<span style={{fontSize:9,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none",marginLeft:6}}>loading…</span>}</div>
                  <select className="at-input" value={atTicket.contractId||""} onChange={e=>setAtTicket(t=>({...t,contractId:+e.target.value}))} disabled={atModalLoading||atTicket.companyId===""}>
                    <option value="">{atModalLoading?"Loading contracts…":atTicket.companyId!==""?(atModalContracts.length?"— Select contract —":"— No active contracts —"):"— Select company first —"}</option>
                    {atModalContracts.map(c=>{
                      const name=c.contractName||c.name||`Contract ${c.id}`;
                      let suffix="";
                      if(c.endDate){
                        const d=new Date(c.endDate);
                        if(!isNaN(d))suffix=` — ends ${d.toLocaleDateString("en-AU",{month:"short",year:"numeric"})}`;
                      }
                      return <option key={c.id} value={c.id}>{name}{suffix}</option>;
                    })}
                  </select>
                </div>
              </div>
              {/* Optional time entry — logged against the new ticket via /api/timelog/entry
                  as the signed-in tech. Pre-filled from a Focus batch item's planned
                  minutes; blank = no time entry. */}
              <div className="at-section-label">⏱ Time Entry <span style={{fontWeight:400,opacity:0.65,textTransform:"none",letterSpacing:0}}>optional — leave hours blank to skip · logged as you, against this ticket's contract, with the Work Type above</span></div>
              <div className="at-row-3">
                <div className="at-field">
                  <div className="at-label">Hours Worked</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <input type="number" className="at-input" style={{width:80}} min="0" max="24" step="0.25" placeholder="0.00"
                      value={atTicket.hoursWorked??""}
                      onChange={e=>setAtTicket(t=>({...t,hoursWorked:e.target.value===""?"":+e.target.value}))}/>
                    <span style={{fontSize:11,color:"var(--text3)"}}>hrs</span>
                  </div>
                  {(+atTicket.hoursWorked>0)&&(()=>{
                    // What the entry will carry: now minus the hours → now.
                    const now=new Date();
                    const start=new Date(now.getTime()-(+atTicket.hoursWorked*3600000));
                    const fmt=d=>d.toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"});
                    return <div style={{fontSize:10,color:"var(--text3)",marginTop:3}}>{fmt(start)} → {fmt(now)} today</div>;
                  })()}
                </div>
                <div className="at-field">
                  <div className="at-label">Role</div>
                  <select className="at-input" value={atTicket.roleId||""} onChange={e=>setAtTicket(t=>({...t,roleId:e.target.value}))}>
                    <option value="">{atMyRoles===null?"Loading your roles…":`— Your default role${atMyRoles[0]?.name?` (${atMyRoles[0].name})`:""} —`}</option>
                    {(atMyRoles||[]).map(r=><option key={r.roleID} value={r.roleID}>{r.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Contract</div>
                  <div style={{fontSize:11,color:"var(--text3)",paddingTop:6}}>
                    {atTicket.contractId?(atModalContracts.find(c=>String(c.id)===String(atTicket.contractId))?.contractName||atModalContracts.find(c=>String(c.id)===String(atTicket.contractId))?.name||`Contract ${atTicket.contractId}`):"— as selected above —"}
                  </div>
                </div>
              </div>
              <div className="at-field" style={{marginBottom:10}}>
                <div className="at-label">Summary Notes <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(goes on the time entry)</span></div>
                <textarea className="at-input" rows={3} placeholder="What was done? Blank = the ticket title is used."
                  value={atTicket.timeEntryNotes||""}
                  onChange={e=>setAtTicket(t=>({...t,timeEntryNotes:e.target.value}))}/>
              </div>
              <div className="at-section-label">📞 Ticket Source</div>
              <div className="at-field" style={{marginBottom:10}}>
                <select className="at-input" value={atTicket.source||""} onChange={e=>setAtTicket(t=>({...t,source:e.target.value?+e.target.value:""}))}>
                  <option value="">— Select source —</option>
                  {(atReference?.sources||[]).filter(s=>s.isActive!==false).map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
                            <div className="at-section-label">🔧 Custom Fields <span style={{fontSize:9,fontWeight:400,letterSpacing:0,textTransform:"none",color:"var(--text3)"}}>optional</span></div>
              {atCustomFields.map((cf,i)=>(
                <div key={i} className="at-custom-field">
                  <input className="at-input" placeholder="Field name" value={cf.key} onChange={e=>{const a=[...atCustomFields];a[i]={...a[i],key:e.target.value};setAtCustomFields(a);}} style={{flex:"0 0 35%"}}/>
                  <input className="at-input" placeholder="Value" value={cf.value} onChange={e=>{const a=[...atCustomFields];a[i]={...a[i],value:e.target.value};setAtCustomFields(a);}}/>
                  {atCustomFields.length>1&&<button className="ico-btn" onClick={()=>setAtCustomFields(a=>a.filter((_,j)=>j!==i))}>×</button>}
                </div>
              ))}
              <button className="at-add-custom" onClick={()=>setAtCustomFields(a=>[...a,{key:"",value:""}])}>＋ Add custom field</button>
            </div>
            <div className="at-modal-footer">
              <button
                className="btn btn-O"
                style={{flex:1,justifyContent:"center",fontSize:12,padding:"9px"}}
                disabled={!atTicket.title||atTicket.companyId===""||atSubmitting}
                onClick={submitTodoTicket}
              >
                {atSubmitting
                  ?<><div className="at-lookup-spin" style={{borderTopColor:"var(--ops)"}}/>Creating ticket...</>
                  :"🎫 Create Autotask Ticket"
                }
              </button>
              <button className="btn btn-danger" style={{fontSize:12,padding:"9px 14px"}} onClick={cancelTicket}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TICKET MODAL (from Inbox email) ── */}
      {atModal&&(
        <div className="at-overlay" onClick={e=>{if(e.target.className==="at-overlay")cancelTicket();}}>
          <div className="at-modal">
            <div className="at-modal-hdr">
              <div className="at-modal-title">
                🎫 New Autotask Ticket
                {atModal.quickTicket&&<span style={{fontSize:10,letterSpacing:"0.08em",textTransform:"uppercase",background:"rgba(255,255,255,0.18)",padding:"2px 8px",borderRadius:10,marginLeft:8}}>Quick mode</span>}
              </div>
              <div className="at-modal-sub">
                <span>From: <strong style={{color:"var(--text2)"}}>{atModal.from}</strong></span>
                <span>·</span>
                <span>{atModal.time}</span>
                {atLookingUp&&<span style={{display:"flex",alignItems:"center",gap:5,color:"var(--strat)"}}><div className="at-lookup-spin"/>Looking up contact...</span>}
                {atLookup&&<span style={{color:"var(--ops)"}}>✓ Contact matched</span>}
                {!atLookingUp&&!atLookup&&<span style={{color:"var(--text3)"}}>No contact match found</span>}
              </div>
            </div>
            <div className="at-modal-body">

              {/* Contact/company chip */}
              {atLookup?(
                <div className="at-contact-chip">
                  <span style={{fontSize:16}}>👤</span>
                  <div>
                    <div className="at-contact-name">{atLookup.contact.firstName} {atLookup.contact.lastName}</div>
                    <div className="at-contact-email">{atLookup.company?.name} · {atLookup.contact.email}</div>
                  </div>
                </div>
              ):(
                <div className="at-contact-chip not-found">
                  <span style={{fontSize:16}}>❓</span>
                  <div><div className="at-contact-name not-found">No contact found for {atModal.from}</div><div className="at-contact-email">Select company and contact manually below</div></div>
                </div>
              )}

              {/* Core fields */}
              <div className="at-section-label">📋 Ticket Details</div>

              <div className="at-field">
                <div className="at-label">Title <span className="at-label-required">*</span></div>
                <input className="at-input" value={atTicket.title||""} onChange={e=>setAtTicket(t=>({...t,title:e.target.value}))} placeholder="Ticket title..."/>
              </div>

              <div className="at-field">
                <div className="at-label">Description</div>
                <textarea className="at-input" value={atTicket.description||""} onChange={e=>setAtTicket(t=>({...t,description:e.target.value}))} placeholder="Describe the issue..."/>
              </div>

              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Priority <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.priority||3} onChange={e=>setAtTicket(t=>({...t,priority:+e.target.value}))}>
                    {AT_PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.id} — {p.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Status <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.statusId||1} onChange={e=>setAtTicket(t=>({...t,statusId:+e.target.value}))}>
                    {(atReference?.statuses||AT_STATUSES).map(s=><option key={s.id||s.value} value={s.id||s.value}>{s.name||s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Queue <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.queueId||""} onChange={e=>setAtTicket(t=>({...t,queueId:+e.target.value}))}>
                    {(atReference?.queues||AT_QUEUES).filter(q=>(q.isActive!==false&&q.isSystem!==true)).map(q=><option key={q.id||q.value} value={q.id||q.value}>{q.name||q.label}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Due Date</div>
                  <input type="date" className="at-input" value={atTicket.dueDate||""} onChange={e=>setAtTicket(t=>({...t,dueDate:e.target.value}))}/>
                </div>
              </div>

              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Issue Type</div>
                  <select className="at-input" value={atTicket.issueType||""} onChange={e=>setAtTicket(t=>({...t,issueType:e.target.value,subIssueType:""}))}>
                    <option value="">— Select issue type —</option>
                    {(atReference?.issueTypes||[]).filter(i=>i.isActive!==false).map(i=><option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Sub-Issue Type</div>
                  <select className="at-input" value={atTicket.subIssueType||""} onChange={e=>setAtTicket(t=>({...t,subIssueType:e.target.value}))} disabled={!atTicket.issueType}>
                    <option value="">{atTicket.issueType?"— Select sub-issue type —":"— Select issue type first —"}</option>
                    {(atReference?.subIssueTypes||[]).filter(s=>s.isActive!==false&&String(s.parentValue)===String(atTicket.issueType)).map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Company / Contact */}
              <div className="at-section-label">🏢 Company & Contact</div>

              <div className="at-row">
                <div className="at-field">
                  <div className="at-label">Company <span className="at-label-required">*</span></div>
                  <select className="at-input" value={atTicket.companyId||""} onChange={async e=>{
                    const cid=+e.target.value;
                    setAtTicket(t=>({...t,companyId:cid,contactId:"",contractId:"",configurationItemId:""}));
                    if(!cid){
                      setAtModalContacts([]);setAtModalContracts([]);return;
                    }
                    const {contracts:loadedContracts}=await loadCompanyContactsContracts(cid);
                    if(loadedContracts.length){
                      setAtTicket(t=>({...t,contractId:loadedContracts[0].id}));
                    }
                  }}>
                    <option value="">— Select company —</option>
                    {(atCompanies.length>0?atCompanies:atReference?.companies||AT_COMPANIES).map(c=><option key={c.id} value={c.id}>{c.companyName||c.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Contact {atModalLoading&&<span style={{fontSize:9,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none",marginLeft:6}}>loading…</span>}</div>
                  <select className="at-input" value={atTicket.contactId||""} onChange={e=>setAtTicket(t=>({...t,contactId:+e.target.value}))} disabled={atModalLoading||atTicket.companyId===""}>
                    <option value="">{atModalLoading?"Loading contacts…":atTicket.companyId!==""?(atModalContacts.length?"— Select contact —":"— No active contacts —"):"— Select company first —"}</option>
                    {atModalContacts.map(c=>{
                      const fullName=[c.firstName,c.lastName].filter(Boolean).join(" ")||c.emailAddress||`Contact ${c.id}`;
                      const title=c.title?` — ${c.title}`:"";
                      return <option key={c.id} value={c.id}>{fullName}{title}</option>;
                    })}
                  </select>
                </div>
              </div>
              {(()=>{
                if(!atTicket.contactId||atTicket.companyId==="") return null;
                const contact=atModalContacts.find(c=>c.id===atTicket.contactId);
                if(!contact) return null;
                const company=(atCompanies.length>0?atCompanies:atReference?.companies||AT_COMPANIES).find(c=>c.id===atTicket.companyId);
                const companyName=company?.companyName||company?.name||"";
                const match=findDeviceForContact(contact,companyName);
                if(!match?.device) return null;
                const d=match.device;
                const lastSeen=d.lastSeen?new Date(d.lastSeen).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}):"";
                const onlineDot=d.online?<span style={{color:"#10b981",fontSize:8,marginRight:4}}>●</span>:<span style={{color:"#9ca3af",fontSize:8,marginRight:4}}>●</span>;
                const confidence=match.score>=100?"":match.score>=95?" (likely)":" (possible)";
                return(
                  <div style={{margin:"-4px 0 10px",fontSize:10,color:"var(--text3)",background:"rgba(11,128,165,0.04)",border:"1px solid rgba(11,128,165,0.15)",borderRadius:4,padding:"5px 8px",lineHeight:1.5}}>
                    <span style={{color:"var(--strat)",fontWeight:600}}>💻 Likely device:</span>{" "}
                    <span style={{fontFamily:"var(--ff-mono, ui-monospace, monospace)",color:"var(--text)"}}>{d.hostname}</span>
                    {confidence&&<span style={{color:"var(--text3)"}}>{confidence}</span>}
                    <div style={{marginTop:2}}>
                      {onlineDot}{d.online?"Online":"Offline"}{" · "}{d.deviceType?.category||"Device"}
                      {lastSeen?` · last login ${lastSeen}`:""}
                      {d.lastLoggedInUser?` · ${d.lastLoggedInUser}`:""}
                    </div>
                  </div>
                );
              })()}

              {/* Resource / Work Type / Contract */}
              <div className="at-section-label">🖥️ Asset (optional)</div>
              <div className="at-field" style={{marginBottom:10}}>
                <select className="at-input" value={atTicket.configurationItemId||""} onChange={e=>setAtTicket(t=>({...t,configurationItemId:e.target.value?+e.target.value:""}))} disabled={atModalLoading||atTicket.companyId===""}>
                  <option value="">{atModalLoading?"Loading assets…":atTicket.companyId!==""?(atModalAssets.length?"— No asset (general ticket) —":"— No assets found for this company —"):"— Select company first —"}</option>
                  {atModalAssets.map(a=>{
                    const name=a.referenceTitle||a.referenceNumber||a.serialNumber||`Asset ${a.id}`;
                    const parts=[];
                    if(a.serialNumber&&a.serialNumber!==name)parts.push(a.serialNumber);
                    if(a.installedProductName)parts.push(a.installedProductName);
                    const suffix=parts.length?` — ${parts.join(" · ")}`:"";
                    return <option key={a.id} value={a.id}>{name}{suffix}</option>;
                  })}
                </select>
              </div>
                            <div className="at-section-label">⚙ Assignment & Billing</div>

              <div className="at-row-3">
                <div className="at-field">
                  <div className="at-label">Assigned To</div>
                  <select className="at-input" value={atTicket.resourceId||""} onChange={e=>setAtTicket(t=>({...t,resourceId:+e.target.value}))}>
                    <option value="">— Unassigned —</option>
                    {(atReference?.resources||AT_RESOURCES).map(r=><option key={r.id} value={r.id}>{r.name||r.firstName+" "+r.lastName}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Work Type</div>
                  <select className="at-input" value={atTicket.workTypeId||""} onChange={e=>setAtTicket(t=>({...t,workTypeId:+e.target.value}))}>
                    <option value="">— Select —</option>
                    {(atReference?.workTypes||AT_WORK_TYPES).map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div className="at-field">
                  <div className="at-label">Contract {atModalLoading&&<span style={{fontSize:9,color:"var(--text3)",fontWeight:400,letterSpacing:0,textTransform:"none",marginLeft:6}}>loading…</span>}</div>
                  <select className="at-input" value={atTicket.contractId||""} onChange={e=>setAtTicket(t=>({...t,contractId:+e.target.value}))} disabled={atModalLoading||atTicket.companyId===""}>
                    <option value="">{atModalLoading?"Loading contracts…":atTicket.companyId!==""?(atModalContracts.length?"— Select contract —":"— No active contracts —"):"— Select company first —"}</option>
                    {atModalContracts.map(c=>{
                      const name=c.contractName||c.name||`Contract ${c.id}`;
                      let suffix="";
                      if(c.endDate){
                        const d=new Date(c.endDate);
                        if(!isNaN(d))suffix=` — ends ${d.toLocaleDateString("en-AU",{month:"short",year:"numeric"})}`;
                      }
                      return <option key={c.id} value={c.id}>{name}{suffix}</option>;
                    })}
                  </select>
                </div>
              </div>

              {/* Custom fields */}
              <div className="at-section-label">📞 Ticket Source</div>
              <div className="at-field" style={{marginBottom:10}}>
                <select className="at-input" value={atTicket.source||""} onChange={e=>setAtTicket(t=>({...t,source:e.target.value?+e.target.value:""}))}>
                  <option value="">— Select source —</option>
                  {(atReference?.sources||[]).filter(s=>s.isActive!==false).map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
                            <div className="at-section-label">🔧 Custom Fields <span style={{fontSize:9,fontWeight:400,letterSpacing:0,textTransform:"none",color:"var(--text3)"}}>optional</span></div>
              {atCustomFields.map((cf,i)=>(
                <div key={i} className="at-custom-field">
                  <input className="at-input" placeholder="Field name" value={cf.key} onChange={e=>{const a=[...atCustomFields];a[i]={...a[i],key:e.target.value};setAtCustomFields(a);}} style={{flex:"0 0 35%"}}/>
                  <input className="at-input" placeholder="Value" value={cf.value} onChange={e=>{const a=[...atCustomFields];a[i]={...a[i],value:e.target.value};setAtCustomFields(a);}}/>
                  {atCustomFields.length>1&&<button className="ico-btn" onClick={()=>setAtCustomFields(a=>a.filter((_,j)=>j!==i))}>×</button>}
                </div>
              ))}
              <button className="at-add-custom" onClick={()=>setAtCustomFields(a=>[...a,{key:"",value:""}])}>＋ Add custom field</button>

              {/* Reply to client — added as public ticket note + sent as email reply.
                  Hidden for the quick-ticket flow (from inbox row), where the user
                  just wants to create a ticket without replying. */}
              {!atModal.quickTicket&&<>
                <div className="at-section-label" style={{marginTop:14,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span>↩ Reply to Client</span>
                  <label style={{display:"flex",alignItems:"center",gap:6,fontSize:10,fontWeight:400,letterSpacing:0,textTransform:"none",color:"var(--text3)",cursor:"pointer"}}>
                    <input type="checkbox" checked={ticketReplySend} onChange={e=>setTicketReplySend(e.target.checked)} style={{accentColor:"var(--strat)"}}/>
                    Send as reply email + public note on ticket
                  </label>
                </div>
                <textarea
                  className="at-input"
                  value={ticketReplyBody}
                  onChange={e=>setTicketReplyBody(e.target.value)}
                  placeholder="Reply message to client (will be added as a public note to the ticket and sent as an email reply)…"
                  disabled={!ticketReplySend}
                  style={{minHeight:120,opacity:ticketReplySend?1:0.5,marginBottom:6,fontFamily:"inherit"}}
                />
                <div style={{fontSize:10,color:"var(--text3)",marginBottom:4}}>
                  The reply is added as a <strong style={{color:"var(--strat)"}}>public note</strong> on the ticket (visible in the Autotask client portal) and sent as an email reply via M365. The original email is moved to Deleted Items.
                </div>
              </>}
              {atModal.quickTicket&&(
                <div style={{marginTop:10,padding:"8px 12px",background:"rgba(11,128,165,0.06)",border:"1px solid rgba(11,128,165,0.2)",borderRadius:8,fontSize:10,color:"var(--text3)"}}>
                  <strong style={{color:"var(--strat)"}}>Quick ticket mode</strong> — creates the ticket only. The email stays in your inbox and no reply is sent.
                </div>
              )}

            </div>
            <div className="at-modal-footer">
              <button
                className="btn btn-O"
                style={{flex:1,justifyContent:"center",fontSize:12,padding:"9px"}}
                disabled={!atTicket.title||atTicket.companyId===""||atSubmitting}
                onClick={submitTicket}
              >
                {atSubmitting
                  ?<><div className="at-lookup-spin" style={{borderTopColor:"var(--ops)"}}/>Creating ticket...</>
                  :atModal.quickTicket
                    ?"✓ Create Ticket"
                    :ticketReplySend&&ticketReplyBody.trim()
                      ?"✓ Create Ticket + Reply + Delete Email"
                      :"✓ Create Ticket & Delete Email"
                }
              </button>
              <button className="btn btn-danger" style={{fontSize:12,padding:"9px 14px"}} onClick={cancelTicket}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── AUTOTASK SUCCESS ── */}
      {atSuccess&&(
        <div className="at-success-overlay">
          <div className="at-success-box">
            <div className="at-success-icon">🎉</div>
            <div className="at-success-title">Ticket Created</div>
            <div className="at-success-sub">
              Your ticket has been raised in Autotask.
              {atSuccess.isTodo
                ?<><br/>The To-Do has been marked complete.</>
                :atSuccess.quickTicket
                  ?<><br/>The email remains in your inbox.</>
                  :<><br/>The email is being moved to Deleted Items.</>
              }
              {atSuccess.timeEntry&&(atSuccess.timeEntry.error
                ?<><br/><span style={{color:"var(--danger)"}}>⚠ {atSuccess.timeEntry.hours}h time entry NOT logged — {atSuccess.timeEntry.error}. Add it in Autotask.</span></>
                :<><br/>⏱ {atSuccess.timeEntry.hours}h logged against the ticket.</>
              )}
            </div>
            <div className="at-ticket-num">{atSuccess.ticketNum}</div>
          </div>
        </div>
      )}

      {/* ── EMAIL DETAIL MODAL (opens when user clicks an inbox row) ── */}
      {emailDetailModal&&(
        <div className="em-detail-overlay" onClick={ev=>{if(ev.target.className==="em-detail-overlay")setEmailDetailModal(null);}}>
          <div className="em-detail-modal" onClick={ev=>ev.stopPropagation()}>
            <div className="em-detail-hdr">
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div className="em-detail-from">
                    {emailDetailModal.email.from}
                    {emailDetailModal.email.vip&&<span className="eb eb-vip">VIP</span>}
                    {emailDetailModal.email.priority==="urgent"&&<span className="eb eb-urg">URGENT</span>}
                    {emailDetailModal.email.priority==="client"&&<span className="eb eb-cli">CLIENT</span>}
                    {ticketedEmails.has(emailDetailModal.email.id)&&<span className="eb" style={{background:"rgba(39,174,96,0.2)",color:"#a4e7c5",border:"1px solid rgba(39,174,96,0.4)"}}>TICKETED</span>}
                    {todoedEmails.has(emailDetailModal.email.id)&&<span className="eb" style={{background:"rgba(39,174,96,0.2)",color:"#a4e7c5",border:"1px solid rgba(39,174,96,0.4)"}}>TO-DO</span>}
                  </div>
                  <div className="em-detail-subj">{emailDetailModal.email.subj||"(no subject)"}</div>
                  <div className="em-detail-meta">
                    <span>{emailDetailModal.email.time}</span>
                    {emailDetailModal.email.fromEmail&&<span>· {emailDetailModal.email.fromEmail}</span>}
                  </div>
                </div>
                <button className="em-detail-close" onClick={()=>setEmailDetailModal(null)} title="Close">×</button>
              </div>
            </div>

            {emailDetailModal.email.aiSum&&(
              <div className="em-detail-ai" style={{marginTop:14}}>
                <div className="em-detail-ai-label">◆ AI Summary</div>
                {emailDetailModal.email.aiSum}
              </div>
            )}

            <div className="em-detail-body">
              {emailBodyLoading?(
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 0",color:"var(--text3)",fontSize:12,gap:10}}>
                  <div className="at-lookup-spin" style={{borderTopColor:"var(--strat)"}}/>
                  Loading full email…
                </div>
              ):emailBodyError?(
                <div style={{padding:"20px 0"}}>
                  <div style={{padding:"10px 12px",background:"rgba(231,76,60,0.08)",border:"1px solid rgba(231,76,60,0.3)",borderRadius:8,fontSize:11,color:"var(--danger)",marginBottom:12}}>
                    ⚠ Couldn't load the full email body: {emailBodyError}
                    <div style={{marginTop:4,color:"var(--text3)"}}>Showing the preview instead.</div>
                  </div>
                  {(() => {
                    const fallback=emailDetailModal.email.body||emailDetailModal.email.bodyPreview||emailDetailModal.email.snippet||emailDetailModal.email.aiSum||"(no preview available)";
                    if(/<\/?(html|body|div|p|br|a|span|strong|table|tr|td|img|h[1-6]|ul|ol|li)\b/i.test(fallback)){
                      return <div dangerouslySetInnerHTML={{__html:fallback}}/>;
                    }
                    return <div style={{whiteSpace:"pre-wrap"}}>{fallback}</div>;
                  })()}
                </div>
              ):(() => {
                // Prefer the fetched full body. Fall back to preview only if no fetch happened.
                const fetched=emailBodyContent&&emailBodyContent.fetched;
                const body=fetched
                  ?emailBodyContent.html
                  :(emailDetailModal.email.body||emailDetailModal.email.bodyPreview||emailDetailModal.email.snippet||emailDetailModal.email.aiSum||"(no body content available)");
                const isHtml=fetched
                  ?(emailBodyContent.contentType||"html").toLowerCase()!=="text"
                  :/<\/?(html|body|div|p|br|a|span|strong|table|tr|td|img|h[1-6]|ul|ol|li)\b/i.test(body);
                if(isHtml){
                  return <div dangerouslySetInnerHTML={{__html:body}}/>;
                }
                return <div style={{whiteSpace:"pre-wrap"}}>{body}</div>;
              })()}
            </div>

            <div className="em-detail-actions">
              <button className="btn btn-danger" style={{fontSize:12}}
                onClick={()=>handleDeleteEmail(emailDetailModal.email)}
                title="Move to Deleted Items">🗑 Delete</button>
              <button className="btn" style={{fontSize:12,background:"rgba(52,152,219,0.1)",color:"#2980b9",border:"1px solid rgba(52,152,219,0.3)"}}
                onClick={()=>openReplyModal(emailDetailModal.email)}
                title="Reply (no ticket)">↩ Reply</button>
              {todoedEmails.has(emailDetailModal.email.id)
                ?<button className="btn" style={{fontSize:12,background:"rgba(39,174,96,0.1)",color:"var(--ops)",border:"1px solid rgba(39,174,96,0.3)",opacity:0.7,cursor:"default"}} disabled>✓ Added to To-Do</button>
                :<button className="btn btn-O" style={{fontSize:12}}
                  onClick={()=>{openTodoFromEmail(emailDetailModal.email,false);setEmailDetailModal(null);}}
                  title="Create To-Do from email">📋 To-Do</button>
              }
              {atConnected?(
                ticketedEmails.has(emailDetailModal.email.id)
                  ?<button className="btn" style={{fontSize:12,background:"rgba(39,174,96,0.1)",color:"var(--ops)",border:"1px solid rgba(39,174,96,0.3)",opacity:0.7,cursor:"default"}} disabled>✓ Ticketed</button>
                  :<button className="btn btn-S" style={{fontSize:12}}
                    onClick={()=>openTicketModal(emailDetailModal.email)}
                    title="Create Autotask ticket + reply">🎫 Ticket</button>
              ):(
                <button className="btn" style={{fontSize:12,background:"transparent",color:"var(--text3)",border:"1px solid var(--border2)"}}
                  onClick={()=>{setTab("settings");setEmailDetailModal(null);}}
                  title="Connect Autotask in Settings">🎫 AT (connect)</button>
              )}
              <button className="btn" style={{fontSize:12,background:"transparent",color:"var(--text3)",border:"1px solid var(--border)",marginLeft:"auto"}}
                onClick={()=>setEmailDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PLAIN REPLY MODAL (no ticket) ── */}
      {replyModal&&(
        <div className="at-overlay" onClick={e=>{if(e.target.className==="at-overlay"&&!replySending){setReplyModal(null);setReplyBody("");}}}>
          <div className="at-modal" style={{maxWidth:640}}>
            <div className="at-modal-hdr">
              <div className="at-modal-title">↩ Reply to Email</div>
              <div className="at-modal-sub" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span>To: <strong style={{color:"var(--text2)"}}>{replyModal.email.from}</strong></span>
                <span style={{color:"var(--text3)"}}>·</span>
                <span>Re: {replyModal.email.subj}</span>
              </div>
            </div>
            <div className="at-modal-body">
              {replyModal.email.aiSum&&(
                <div style={{padding:"10px 12px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:8,marginBottom:12,fontSize:11,color:"var(--text2)"}}>
                  <div style={{fontSize:9,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--strat)",marginBottom:4}}>◆ Original (AI summary)</div>
                  {replyModal.email.aiSum}
                </div>
              )}
              <div className="at-section-label">Your Reply</div>
              <textarea
                className="at-input"
                value={replyBody}
                onChange={e=>setReplyBody(e.target.value)}
                placeholder="Type your reply…"
                autoFocus
                style={{minHeight:220,fontFamily:"inherit"}}
              />
              <div style={{fontSize:10,color:"var(--text3)",marginTop:6}}>
                This sends a threaded reply via Microsoft 365. The original email stays in your inbox.
              </div>
            </div>
            <div className="at-modal-footer">
              <button
                className="btn btn-O"
                style={{flex:1,justifyContent:"center",fontSize:12,padding:"9px"}}
                disabled={!replyBody.trim()||replySending}
                onClick={sendReplyEmail}
              >
                {replySending
                  ?<><div className="at-lookup-spin" style={{borderTopColor:"var(--ops)"}}/>Sending reply…</>
                  :"📤 Send Reply"
                }
              </button>
              <button className="btn btn-danger" style={{fontSize:12,padding:"9px 14px"}} disabled={replySending} onClick={()=>{setReplyModal(null);setReplyBody("");}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Todo Card Component ────────────────────────────────────────────────────

function TodoCard({t,expanded,onToggleExpand,onToggle,onUpdateNotes,onUpdatePriority,onUpdateDate,onReschedule,onDelete,onCreateTicket,atConnected,priClass,todoDateClass,todoItemClass}){
  const [editNotes,setEditNotes]=useState(false);
  const [notesVal,setNotesVal]=useState(t.notes||"");

  const fmtAdded=(iso)=>{
    if(!iso) return null;
    try{ return new Date(iso).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}); }
    catch{ return null; }
  };

  return(
    <div className={`todo-item ${todoItemClass(t)} ${expanded?"td-expanded":""}`}>
      <div className="td-top" onClick={onToggleExpand}>
        <div className={`td-cb ${t.done?"checked":""}`} onClick={e=>{e.stopPropagation();onToggle(t.id);}}>{t.done?"✓":""}</div>
        <div className="td-body">
          <div className="td-title">{t.title}</div>
          {!expanded&&t.notes&&(
            <div style={{fontSize:10,color:"var(--text3)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:320}}>{t.notes}</div>
          )}
          <div className="td-meta">
            <span className={`pri ${priClass[t.priority]||"pri-med"}`}>{t.priority}</span>
            <span className={`lb lb-${t.layer}`} style={{fontSize:9}}>{({S:"Strategic",T:"Tactical",O:"Operational"})[t.layer]||t.layer}</span>
            {t.client&&<span style={{fontSize:10,color:"var(--text3)",background:"var(--s2)",padding:"1px 5px",borderRadius:3,border:"1px solid var(--border)"}}>{t.client}</span>}
            <span className={`td-date ${todoDateClass(t)}`}>
              {t.dueDate?t.dueDate<new Date().toISOString().split("T")[0]?`Overdue · ${t.dueDate}`:t.dueDate===new Date().toISOString().split("T")[0]?"Today":t.dueDate:"No due date"}
            </span>
            {t.dueDate&&t.dueDate<new Date().toISOString().split("T")[0]&&<span className="overdue-chip">overdue</span>}
            {(t.atTicketNumber||t.atTicketNum)&&<span title="Autotask ticket created from this action item" style={{fontSize:10,color:"var(--strat)",background:"var(--strat-d)",padding:"1px 6px",borderRadius:3,border:"1px solid rgba(11,128,165,0.25)",fontFamily:"var(--ff-mono, ui-monospace, monospace)"}}>🎫 {t.atTicketNumber||t.atTicketNum}</span>}
          </div>
        </div>
        <div className="td-expand-btn">{expanded?"▲":"▼"}</div>
      </div>

      {expanded&&(
        <div className="td-detail">
          <div style={{display:"flex",gap:12,marginBottom:10,flexWrap:"wrap"}}>
            {t.source&&<div className="td-source"><span style={{color:"var(--fellow)"}}>🤝</span>From: {t.source}</div>}
            {t.createdAt&&<div style={{fontSize:10,color:"var(--text3)"}}>Added: {fmtAdded(t.createdAt)}</div>}
            {t.client&&<div style={{fontSize:10,color:"var(--text3)"}}>Client: <strong style={{color:"var(--text)"}}>{t.client}</strong></div>}
          </div>

          {!editNotes?(
            <div>
              {notesVal?(
                <div style={{background:"var(--s2)",borderRadius:6,padding:"10px 12px",marginBottom:10,fontSize:11,color:"var(--text)",lineHeight:1.6,whiteSpace:"pre-wrap",border:"1px solid var(--border)"}}>{notesVal}</div>
              ):(
                <div style={{fontSize:11,color:"var(--text3)",marginBottom:8,fontStyle:"italic"}}>No description added yet.</div>
              )}
              <button className="btn" style={{fontSize:10,padding:"4px 9px",marginBottom:10}} onClick={()=>setEditNotes(true)}>✎ {notesVal?"Edit":"Add"} description</button>
            </div>
          ):(
            <div style={{marginBottom:10}}>
              <textarea className="inp" style={{marginBottom:6,minHeight:80,resize:"vertical"}} value={notesVal} onChange={e=>setNotesVal(e.target.value)} placeholder="Add notes, context, links, or detail..."/>
              <div style={{display:"flex",gap:6}}>
                <button className="btn btn-O" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{onUpdateNotes(t.id,notesVal);setEditNotes(false);}}>Save</button>
                <button className="btn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>{setNotesVal(t.notes||"");setEditNotes(false);}}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:10,color:"var(--text3)"}}>Priority:</span>
            {["high","med","low"].map(p=>(
              <button key={p} className={`btn ${t.priority===p?`btn-${p==="high"?"danger":p==="med"?"warn":"O"}`:""}`} style={{fontSize:10,padding:"3px 9px"}} onClick={()=>onUpdatePriority(t.id,p)}>{p}</button>
            ))}
          </div>

          <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:10,color:"var(--text3)"}}>Due:</span>
            <input type="date" className="inp" style={{marginBottom:0,width:140,fontSize:10}} value={t.dueDate} onChange={e=>onUpdateDate(t.id,e.target.value)}/>
            {!t.done&&<button className="btn btn-warn" style={{fontSize:10,padding:"4px 9px"}} onClick={()=>onReschedule(t)}>↻ Reschedule</button>}
            {atConnected&&!t.done&&<button className="btn" style={{fontSize:10,padding:"4px 9px",background:"var(--strat-d)",color:"var(--strat)",borderColor:"rgba(11,128,165,0.25)"}} onClick={()=>onCreateTicket(t)}>🎫 Ticket</button>}
            <button className="btn btn-danger" style={{fontSize:10,padding:"4px 9px",marginLeft:"auto"}} onClick={()=>onDelete(t.id)}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}
